import missingLocales from './i18n.missingLocales';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { syncUiLanguage } from './utils/regionalFormat';

const resources = {
  en: {
    translation: {
      brand: {
        tagline: 'Manage. Monitor. Grow.'
      },
      nav: {
        branches: 'Branches',
        levels: 'Levels',
        batches: 'Batches',
        students: 'Students',
        coaches: 'Coaches',
        managers: 'Managers',
        enrollments: 'Enrollments',
        sessions: 'Sessions',
        calendar: 'Calendar',
        reports: 'Reports',
        wallet: 'My Wallet',
        faq: 'FAQ',
        dashboard: 'Dashboard',
        help: 'Help',
        finances: 'Finances',
        manage: 'Manage',
        people: 'People',
        academySetup: 'Academy setup',
        operations: 'Operations',
        system: 'System',
        feesBilling: 'Fees & Billing',
        myAccount: 'My Account',
        settings: 'Settings',
        discounts: 'Discounts',
        sessionsSummary: 'Sessions Summary',
        monthlyStatements: 'Monthly Statements',
        reportSettings: 'Report Settings',
        atRiskStudents: 'At-Risk Students',
        walletTopup: 'Wallet Top-up',
        paymentEnforcement: 'Payment Enforcement',
        paymentMethods: 'Payment Methods',
        regionalSettings: 'Regional Settings',
        productionReadiness: 'EU Production Ready',
        playingPlatform: 'Playing Platform',
        playingHub: 'Play Hub',
        fairPlay: 'Fair play',
        playNow: 'Play vs Computer',
        liveGames: 'Live games',
        myGames: 'My Games',
        challenges: 'Challenges',
        leaderboard: 'Leaderboard',
        classroom: 'Classroom',
        classroomOverview: 'Overview',
        classroomLive: 'Live classes',
        classroomZoom: 'Zoom connection',
        classroomSettings: 'Classroom settings',
        emailSettings: 'Email Settings',
        whatsapp: 'WhatsApp',
        aiCoachSettings: 'Ask AI Coach',
        branding: 'Branding',
        license: 'License',
        holidays: 'Holidays',
        notifications: 'Notifications',
        auditLogs: 'Audit Logs',
        mySessions: 'My Sessions',
        myEnrollments: 'My Enrollments',
        sessionStatement: 'Session Statement',
        parentPortal: 'Parent portal',
        home: 'Home',
        myChild: 'My child',
        overview: 'Overview',
        attendance: 'Attendance',
        more: 'More'
      },
      classroom: {
        hub: {
          title: 'Classroom',
          subtitleStaff: 'Run online classes with Zoom — start and join video in the Zoom app.',
          subtitleLearner: 'Join your academy’s live online classes in Zoom.',
          zoomFirstBanner: 'Classroom uses Zoom. Start / Host / Join opens Zoom for coaches and students.',
          liveTitle: 'Live classes',
          liveDescStaff: 'See today’s online sessions and Start / Host / Join in Zoom.',
          liveDescLearner: 'See upcoming online classes and join in Zoom when your coach starts the meeting.',
          zoomTitle: 'Zoom connection',
          zoomDesc: 'Connect Zoom so staff and students can run class video in Zoom.',
          zoomDescConnected: 'Zoom is connected. Manage credentials or disconnect here.',
          settingsTitle: 'Classroom settings',
          settingsDesc: 'Defaults for waiting room, mute on entry, camera, and start notifications.',
          open: 'Open',
          zoomConnectedBanner: 'Zoom is connected. Open Live classes to start or join video in Zoom.',
          zoomNotConnectedBanner: 'Zoom is not connected yet. Connect your academy Zoom account to enable Start / Host / Join.',
          externalModeBanner: 'Classroom is set to external meeting links. Paste a URL on the session, then use Start / Join from Live or Sessions.',
          statusLoadFailed: 'Could not load classroom status.',
          zoomStatusUnknown: 'Could not verify Zoom connection. Use Refresh or open Live classes.',
        },
        joinAction: {
          stillWaiting: "Still waiting — your coach has not started yet.",
          notStartedYet: 'Not started yet',
          notStartedHint: 'Your coach has not started this online class yet. Tap to check again.',
          checking: 'Checking…',
          checkFailed: 'Could not check classroom status. Try again or open Live classes.',
        },
        live: {
          title: 'Live classes',
          subtitleStaff: 'Start or host Zoom for online sessions — video opens in the Zoom app.',
          subtitleAdmin: 'Start or host Zoom for online sessions across the academy — opens in Zoom.',
          subtitleLearner: 'Join the class in Zoom when your coach starts the session.',
          zoomPendingHint: 'Zoom is not connected yet. You can still open sessions; Start will explain what is missing until an admin connects Zoom.',
          zoomPendingHintLearner: 'Zoom is not connected yet. Join will appear here when your coach starts an online class.',
          connectZoomCta: 'Connect Zoom',
          todayHeading: 'Today & upcoming',
          todayHint: 'Online sessions with a Zoom link will list here.',
          todayHintLive: 'Eligible online sessions in the next 48 hours. Start creates a meeting and opens it in ChessDesk; Host rejoins as host; Join enters as participant.',
          todayHintLearner: 'Eligible online sessions in the next 48 hours. Join opens the classroom inside ChessDesk when your coach has started it.',
          openSessions: 'Open sessions',
          refresh: 'Refresh',
          emptyTitle: 'No live classroom yet',
          emptyStaff: 'No eligible online sessions in the next 48 hours. Use Open sessions to schedule. Start still works from Sessions once Zoom is connected.',
          emptyLearner: 'When your coach starts an online class, Join will appear here. You can also open Sessions for your timetable.',
          emptyStaffConnected: 'No online sessions in this window. Use Open sessions to schedule, then return here to Start or Host.',
          emptyLearnerConnected: 'No live class right now. When your coach starts one, Join will appear here.',
          chipStart: 'Start',
          chipJoin: 'Join',
          chipHost: 'Host',
          statusLoadFailed: 'Could not load classroom status. Try refreshing.',
          zoomConnectedHint: 'Zoom is connected. Start / Host / Join opens Zoom when a meeting exists.',
          start: 'Start',
          startZoom: 'Start Zoom',
          host: 'Host',
          restart: 'Restart',
          join: 'Join',
          joinSession: 'Join Session',
          starting: 'Opening…',
          startFailed: 'Could not start Zoom classroom.',
          openInZoomFailed: 'No Zoom host link is available yet. Try Host, or ask an admin to reconnect Zoom.',
          meetingReady: 'Meeting ready',
          meetingNotCreated: 'Meeting not created yet',
          end: "End class",
          ending: "Ending…",
          endFailed: "Could not end the classroom meeting.",
          meetingStatusCreating: 'Creating…',
          meetingStatusStarted: 'Started',
          meetingStatusFailed: 'Failed — try Start again',
          meetingStatusEnded: 'Ended',
          meetingStatusUnknown: 'Status unavailable',
          unnamedSession: 'Session',
          selectChildTitle: 'Select a child',
          selectChildHint: 'Choose a child in the parent portal header to see their live classes.',
          noChildrenTitle: 'No linked children',
          noChildrenHint: 'No approved child is linked to this parent account yet.',
          childrenLoadFailed: 'Could not load linked children. Refresh the page or try again.',
          externalModeHint: 'Using external meeting links. Start opens the session link; Join uses the participant URL when available.',
        },
        meeting: {
          title: 'Classroom meeting',
          subtitle: 'Video class inside ChessDesk',
          preparing: 'Preparing Zoom classroom…',
          preparingHint: 'Setting up the meeting. This usually takes a few seconds.',
          joinFailed: 'Could not join the embedded classroom.',
          waitingForHost: 'Waiting for your coach to start the class. Stay on this page — you will enter when they join.',
          leave: 'Leave meeting',
          notStarted: 'The host has not started this class yet. Wait for your coach to open the classroom, or ask them to tap Host.',
          back: 'Back',
          backToLive: 'Live classes',
          cancelled: "This session was cancelled.",
          ended: "This classroom meeting has ended.",
          startAsHost: 'Start as host',
          redirectingExternal: 'Opening Zoom…',
          openExternalNow: 'Open meeting now',
          askAdminZoomSetup: 'Ask your academy admin to configure Zoom under Classroom → Zoom.',
          sdkLoadFailed: 'Could not load the video classroom. Try again, or use Open in Zoom app.',
          signatureInvalid: 'Could not start video in the browser. Try Open in Zoom app, or ask an admin to check Classroom → Zoom.',
          embedUnavailable: 'Meeting is not ready yet. Try again in a moment.',
          embedUnavailableRetry: 'Could not start video in the browser. Try again, or use Open in Zoom app.',
          hostCredentialsMissing: 'Could not open the classroom as host. Try again in a moment.',
          hostOpenFailed: 'Could not open the classroom meeting. Try again in a moment.',
          hostTokenInvalid: 'Could not open the classroom meeting. Try again in a moment.',
          hostTokenInvalidStaff: 'Could not open the classroom meeting. Try again in a moment.',
          noExternalLink: 'No meeting link is available for this session yet.',
          invalidSession: 'Invalid session.',
          leave: 'Leave meeting',
          retry: 'Try again',
          openInZoom: 'Open in Zoom app',
          inMeetingHint: 'Video runs inside ChessDesk. Use Leave when you are done.',
          connectingHint: 'Connecting…',
          connectedHint: 'Live in ChessDesk',
          livePill: 'Live',
          joinTimedOut: 'The classroom is taking too long to open. Try again, or use Open in Zoom app.',
          zoomToolbarHint: 'Use the controls on the video to unmute and start your camera.',
          cameraOffTitle: 'Camera is off',
          cameraOffHint: 'Tap the camera icon on the bar below to show yourself to the class.',
          waitingRoomAdmit: 'Admit',
          waitingRoomAdmitAll: 'Admit all',
          waitingRoomView: 'Participants',
          waitingRoomDismiss: 'Dismiss waiting room notice',
          waitingRoomOne: '{{name}} is in the waiting room.',
          waitingRoomMany: '{{count}} participants are in the waiting room: {{names}}.',
          waitingRoomManyMore: '{{count}} participants are in the waiting room ({{names}}, and more).',
          waitingForHostPill: 'Waiting for host',
          learnerWaitingHint: 'You are waiting for the host to let you in. Keep this page open.',
          waitingForHostAutoRetry: 'We will retry automatically when your coach starts the class.',
          leaveHostConfirm: 'Leave without ending the class? The Zoom meeting stays live until you End class.',
          endHostConfirm: 'End class for everyone? Students will be removed from the Zoom meeting.',
          leaving: 'Leaving meeting…',
          hostConflictWarning: 'Another host may already be in this meeting. Only one person should control Admit.',
          fixZoomSetup: 'Open Classroom Zoom settings →',
        },
        settings: {
          title: 'Classroom settings',
          subtitle: 'Default meeting options applied when ChessDesk creates a Zoom classroom.',
          infoBanner: 'These preferences are saved as a local draft until classroom settings are stored on the server.',
          infoBannerLive: 'These defaults apply when ChessDesk creates a Zoom meeting for a session.',
          defaultProvider: 'Default video provider',
          defaultProviderNote: 'Zoom is the primary provider. External link is a fallback for paste-your-own meeting URLs.',
          providerExternalLink: 'External meeting link',
          hostUserEmail: 'Zoom host user email',
          hostUserEmailNote: 'Zoom account email used to create and host meetings. Must be a licensed user on your connected Zoom account. If empty, the session coach email is used.',
          hostEmailInvalid: 'Enter a valid Zoom host email (or leave blank to use the coach email).',
          waitingRoom: 'Enable waiting room',
          waitingRoomNote: 'Participants wait until the host admits them.',
          muteOnEntry: 'Mute participants on entry',
          allowStudentCameraDefault: 'Allow student camera by default',
          allowStudentCameraNote: 'Hosts can still change this in Zoom for each meeting.',
          notifyOnStart: 'Notify students when class starts',
          notifyOnStartNote: 'When enabled, enrolled students and linked parents receive an in-app notification with a Join link when a coach starts the classroom.',
          saveDraft: 'Save draft',
          save: 'Save settings',
          saving: 'Saving…',
          saveSuccess: 'Classroom settings saved.',
          saveFailed: 'Could not save classroom settings.',
          loadFailed: 'Could not load classroom settings.',
          retryLoad: 'Try again',
          draftSaved: 'Draft saved in this browser.',
          draftSaveFailed: 'Could not save draft in this browser.',
          gotoZoom: 'Zoom connection'
        },
        zoom: {
          title: 'Zoom connection',
          subtitle: 'Link your academy Zoom account so ChessDesk can create and open meetings.',
          infoBanner: 'Create a Server-to-Server OAuth app in the Zoom Marketplace. Paste Account ID and Client credentials, then Connect. Required scopes: meeting:write:meeting:admin, meeting:read:meeting:admin, user:read:user:admin.',
          statusConnected: 'Connected',
          statusConnectedHint: 'ChessDesk can create Zoom meetings with this account. Host / Join opens Zoom.',
          statusNotConnected: 'Not connected',
          statusNotConnectedHint: 'Saving a draft does not connect Zoom. OAuth / API wiring is still required on the server.',
          statusNotConnectedHintLive: 'Enter credentials and click Connect with Zoom. ChessDesk will verify them with Zoom and store them securely on the server.',
          serverToServerHeading: 'Server-to-Server OAuth',
          serverToServerHint: 'Recommended for academy-owned Zoom accounts. Keep the client secret out of the browser in production — this draft UI is for setup only.',
          serverToServerHintLive: 'Recommended for academy-owned Zoom accounts. The Client Secret is encrypted on the server and never returned to the browser.',
          accountId: 'Account ID',
          accountIdNote: 'From Zoom Marketplace → your app → App Credentials.',
          clientId: 'Client ID',
          clientSecret: 'Client secret',
          clientSecretNote: 'Not stored in local draft. Enter only when you are ready to connect via the API.',
          clientSecretConfiguredNote: 'A Client Secret is already stored. Leave blank to keep it, or enter a new secret to replace it.',
          clientSecretConnectNote: 'Required to connect. Stored encrypted on the server — never shown again after save.',
          saveDraft: 'Save draft',
          saveCredentials: 'Save credentials',
          clearDraft: 'Clear draft',
          connectOAuth: 'Connect with Zoom',
          connectDisabledTitle: 'Backend connect not available yet',
          connectDisabledHint: 'Connect with Zoom stays disabled until the server can exchange credentials and store tokens securely.',
          connectHintLive: 'Connect exchanges credentials with Zoom and marks this academy as connected only if Zoom accepts them.',
          connectNeedsFields: 'Enter Account ID, Client ID, and Client Secret, then click Connect with Zoom.',
          draftSaved: 'Draft saved in this browser. Zoom is still not connected.',
          draftSavedServer: 'Credentials saved on the server. Zoom is not connected until you click Connect with Zoom.',
          draftSavedConnected: 'Credentials updated. Connection status unchanged until you reconnect.',
          draftSaveFailed: 'Could not save Zoom settings.',
          draftCleared: 'Draft cleared.',
          loadFailed: 'Could not load Zoom settings.',
          validationRequired: 'Account ID and Client ID are required.',
          secretRequired: 'Client Secret is required to connect (or save it first).',
          connectSuccess: 'Connected to Zoom successfully.',
          connectFailed: 'Could not connect to Zoom. Check credentials and that the app is activated.',
          disconnect: 'Disconnect',
          disconnected: 'Zoom disconnected. Credentials are kept so you can reconnect.',
          disconnectFailed: 'Could not disconnect Zoom.',
          webhookHeading: 'Event subscriptions (webhooks)',
          meetingSdkHeading: 'Embedded video (Meeting SDK)',
          meetingSdkHint: 'Optional override for in-app video. Until your Zoom app is published, paste Client ID and Client Secret from App Credentials → Development (Production signatures fail with “invalid signature”). Server-to-Server credentials above can stay on Production.',
          meetingSdkDevRequired: 'Embedded join needs Development Meeting SDK credentials here, or switch your main credentials to Development until the app is published.',
          meetingSdkClientId: 'Meeting SDK Client ID (Development)',
          meetingSdkClientIdNote: 'From the same Zoom app → App Credentials → Development tab. Can differ from the Server-to-Server Client ID above.',
          meetingSdkClientSecret: 'Meeting SDK Client Secret (Development)',
          meetingSdkClientSecretNote: 'Development Client Secret from the same tab. Used only to sign embedded join tokens.',
          meetingSdkClientSecretConfiguredNote: 'A Meeting SDK secret is stored. Leave blank to keep it, or enter a new secret to replace it.',
          webhookHint: 'In Zoom Marketplace → your app → Feature → Event Subscriptions, add the URL below and subscribe to meeting.started, meeting.ended, and meeting.deleted. Paste the same Secret Token here.',
          webhookSecret: 'Webhook secret token',
          webhookSecretNote: 'From Zoom Event Subscriptions. Required to verify meeting lifecycle webhooks.',
          webhookSecretConfiguredNote: 'A webhook secret is stored. Leave blank to keep it, or enter a new secret to replace it.',
          clearCredentials: 'Clear credentials',
          clearConfirm: 'Remove stored Zoom Account ID, Client ID, and Client Secret from this academy?',
          cleared: 'Zoom credentials cleared.',
          clearFailed: 'Could not clear Zoom credentials.',
          clearing: 'Clearing…',
          saving: 'Saving…',
          connecting: 'Connecting…',
          disconnecting: 'Disconnecting…',
          gotoSettings: 'Classroom settings →'
        }
      },
      playing: {
        hubTitle: 'Playing',
        hubSubtitle: 'Play, challenge clubmates, and follow your rating.',
        playTitle: 'Chess board',
        playSubtitle: 'Make your move',
        loadFailed: 'Could not load playing platform data.',
        hubPartialLoad: 'Some play data could not be refreshed. Actions may still work — try again if something looks missing.',
        moveSending: 'Sending move…',
        moveFailed: 'Illegal or rejected move.',
        resignFailed: 'Could not resign.',
        yourRating: 'Your rating',
        record: 'W {{w}} · D {{d}} · L {{l}}',
        leaderboard: 'Leaderboard',
        leaderboardSubtitle: 'Top players in this academy by internal rating.',
        viewLeaderboard: 'View rankings',
        playComputer: 'Play vs computer',
        playComputerDesc: 'Practice against a built-in engine. Higher levels play stronger tactics.',
        computerLevel: 'Difficulty',
        ratedGame: 'Rated game',
        casualGame: 'Casual game',
        playAsWhite: 'Play as White',
        playAsBlack: 'Play as Black',
        challenges: 'Incoming challenges',
        challengePlayer: 'Challenge a player',
        challengeSubtitle: 'Send a game invite to another academy member.',
        noChallenges: 'No pending challenges.',
        accept: 'Accept',
        decline: 'Decline',
        myGames: 'My games',
        myGamesSubtitle: 'Recent games you played on this platform.',
        noGames: 'No games yet — start one from Play Hub.',
        players: 'Players',
        player: 'Player',
        type: 'Type',
        statusLabel: 'Status',
        open: 'Open',
        gameInfo: 'Game',
        check: 'Check!',
        resign: 'Resign',
        backHub: 'Back to Play Hub',
        noMovesYet: 'No moves yet.',
        rating: 'Rating',
        games: 'Games',
        noRatingsYet: 'No rated games yet.',
        create: 'Create',
        join: 'Join',
        score: 'Score',
        searchPlayers: 'Search players',
        challenge: 'Challenge',
        challengeSent: 'Challenge sent.',
        challengeFailed: 'Could not send challenge.',
        reason: 'Result',
        liveGames: 'Live games',
        liveGamesSubtitle: 'Spectate ongoing academy games in real time.',
        viewLive: 'Watch now',
        spectate: 'Spectate',
        spectateTitle: 'Spectating',
        spectatingHint: 'Read-only view — you cannot move pieces.',
        noLiveGames: 'No live games right now.',
        noLiveGamesHint: 'When clubmates start a game, it will show up here.',
        allSpeeds: 'All',
        noLiveGamesForSpeed: 'No live games in this speed class',
        tryAnotherSpeedFilter: 'Try another speed filter or clear to All.',
        noOpenSeeksExpired: 'Open seeks expired — create a new one or send a challenge.',
        noChallengesExpired: 'Incoming challenges expired',
        noChallengesExpiredHint: 'Send a new challenge or wait for the next invite.',
        challengeExpired: 'Challenge expired',
        openSharedTip: 'Open tip',
        boardSettingsLoadFailed: 'Board settings could not be loaded.',
        prefsSaveFailed: 'Could not sync preferences to the server.',
        prevPly: 'Prev ply',
        nextPly: 'Next ply',
        coachPlyOf: 'Ply {{current}} / {{total}}',
        fairPlayShowAllLevels: 'Show all levels',
        expiresIn: 'Expires in {{when}}',
        incomingChallenges: 'Incoming challenges',
        confirmTitle: 'Please confirm',
        sendChallenge: 'Send a challenge',
        inboxPartialLoadFailed: 'Some challenge lists could not be loaded. Try refresh.',
        bye: 'bye',
        format: 'Format',
        customTime: 'Custom',
        minutes: 'Minutes',
        increment: 'Increment (sec)',
        yourColor: 'Your color',
        colorRandom: 'Random',
        colorRandomSide: 'Random side',
        colorWhite: 'White',
        colorBlack: 'Black',
        offerDraw: 'Offer draw',
        acceptDraw: 'Accept draw',
        declineDraw: 'Decline draw',
        opponentOffersDraw: 'Opponent offers a draw',
        youOfferedDraw: 'Draw offered — waiting for opponent',
        drawFailed: 'Could not update draw offer.',
        abort: 'Abort game',
        abortFailed: 'Could not abort this game.',
        abortLeaveFailed: 'Could not cancel your waiting challenge. It may still appear for your opponent until it expires.',
        whiteToMove: 'White to move',
        blackToMove: 'Black to move',
        yourTurn: 'Your turn',
        engineThinking: 'Engine is thinking…',
        moves: 'Moves',
        resignConfirm: 'Resign this game?',
        playNow: 'Play now',
        resume: 'Resume',
        hubRatingHint: 'Climb the academy ladder with rated games.',
        challengeShortcut: 'Invite a clubmate',
        myGamesShortcut: 'Continue or review',
        challengesHint: 'Updates live when someone challenges you.',
        noChallengesHint: 'Challenge a clubmate to start a live game.',
        noGamesHint: 'Play the computer or challenge someone to fill this list.',
        noRatingsHint: 'Play a few rated games to appear on the board.',
        noPlayersFound: 'No players found',
        noPlayersFoundHint: 'Try another name or username.',
        searchPlayersPlaceholder: 'Search by name or username',
        levelEasy: 'Easy',
        levelHard: 'Hard',
        recordShort: 'W/D/L',
        waitingOpponent: 'Waiting for opponent…',
        premoveHint: 'You can premove while waiting.',
        moveDeadlineShort: 'Move by {{when}}',
        waitingToJoinTitle: 'Waiting for opponent to join',
        waitingToJoinHint: 'Waiting for {{name}} to join… Clocks start when they accept.',
        waitingToJoinInviteeHint: 'Accept to start the game against {{name}}.',
        premoveArmed: 'Premove ready: {{from}}→{{to}} — tap Clear or the squares to cancel',
        clearPremove: 'Clear premove',
        sharedAnalysis: 'Shared analysis from {{name}}',
        loading: 'Loading…',
        banBanner: 'You are banned from the Playing Platform.',
        banBannerReason: 'Reason: {{reason}}',
        banBannerUntil: 'Until {{when}}',
        banBannerIndefinite: 'This ban has no end date.',
        timeControl: {
          label: 'Time control',
          daysPerMove: '{{count}}d/move'
        },
        countdown: {
          zeroSeconds: '0s',
          daysHours: '{{d}}d {{h}}h',
          days: '{{d}}d',
          hoursMinutes: '{{h}}h {{m}}m',
          hours: '{{h}}h',
          minutes: '{{m}}m',
          seconds: '{{s}}s'
        },
        banQuickLinkBlocked: 'Unavailable while you are banned',
        banConfirm: 'Ban this player from the Playing Platform?',
        ratedConfirm: 'This is a rated game and will affect your rating. Continue?',
        challengeAcceptFailed: 'Could not accept challenge. Try again.',
        challengeDeclineFailed: 'Could not decline. Try again.',
        shareAnalysisBtn: 'Share analysis',
        shareAnalysisDone: 'Analysis shared with spectators',
        shareAnalysisFailed: 'Could not share analysis.',
        bookMoves: 'Book',
        bookMovesHint: 'Club book suggestions (vs computer only)',
        exploreOpening: 'Explore',
        pickOpening: 'Load club opening',
        pickOpeningPlaceholder: 'Choose an opening…',
        openingLoaded: 'Loaded {{name}}',
        openingLoadFailed: 'Could not load that opening',
        liveNowPeek: 'Live now',
        autoQueen: 'Always promote to queen',
        showBookHints: 'Opening book hints',
        myStudents: 'My students',
        rosterLoadFailed: 'Could not load your students.',
        noRosterStudents: 'No students on your roster yet',
        typeToSearchOrRoster: 'Type a name to search, or pick a student above.',
        drillOn: 'Book drill on',
        drillOff: 'Book drill',
        drillScore: '{{correct}} / {{attempts}} book moves',
        drillOutOfBook: 'Out of book',
        drillCorrect: 'Book move!',
        drillWrong: 'Not in the club book{{hint}}',
        pgnPackBusy: 'Building PGN…',
        pgnPackEmpty: 'No finished games to export yet.',
        pgnPackFailed: 'Could not download PGN pack.',
        accuracyChip: '{{pct}}% accuracy',
        accuracy: 'Accuracy',
        recentAccuracy: '{{pct}}% recent accuracy',
        recentAccuracyGames: '{{n}} games',
        vsYouRecord: 'vs you {{w}}–{{d}}–{{l}}',
        openEvent: 'Open event',
        recentOpponents: 'Recent opponents',
        gameLoadedOnCoachBoard: 'Loaded game #{{id}}',
        gameLoadFailed: 'Could not load that game onto the coach board.',
        noActiveBans: 'No active bans.',
        liftBanConfirm: 'Lift this ban?',
        coachBoard: 'Coach board',
        coachBoardShortcut: 'Teaching board + Classroom',
        hub: 'Back to Play Hub',
        currentRound: 'Current round',
        allowSpectators: 'Allow spectators',
        spectatorsOff: 'Spectators off',
        fairPlayCaseAutoOpened: 'Auto-opened (engine)',
        showCoordinates: 'Coordinates',

        leaderboardGames: 'Games',
        flipBoard: 'Flip board',
        copyPgn: 'Copy PGN',
        downloadPgn: 'Download PGN',
        copyGameLink: 'Copy game link',
        copied: 'Copied!',
        copyFailed: 'Could not copy PGN.',
        rematch: 'Rematch',
        rematchSent: 'Challenge sent',
        rematchSentHint: 'Rematch challenge sent — waiting for your opponent.',
        rematchFailed: 'Could not send rematch challenge.',
        rematchInboxLoadFailed: 'Could not load rematch challenges. Try again.',
        offerTakeback: 'Takeback',
        acceptTakeback: 'Accept takeback',
        declineTakeback: 'Decline takeback',
        takebackFailed: 'Could not update takeback.',
        opponentOffersTakeback: 'Opponent requests a takeback',
        youOfferedTakeback: 'Takeback offered — waiting for opponent',
        typeMove: 'Type a move',
        typeMovePlaceholder: 'e2e4 or Nf3',
        playMove: 'Go',
        chat: 'Chat',
        chatPlaceholder: 'Message opponent…',
        chatSend: 'Send',
        chatEmpty: 'No messages yet.',
        chatReadOnly: 'Spectating',
        chatLoadFailed: 'Could not load chat.',
        chatSendFailed: 'Could not send message.',
        chatOffline: 'You are offline — reconnect to send chat.',
        player: 'Player',
        correspondence: 'Time mode',
        liveClock: 'Live clock',
        correspondenceMode: 'Correspondence ({{n}} days per move)',
        daysPerMove: '{{n}} day(s) per move',
        correspondenceHint: 'Each player has {{n}} days to make a move. No ticking clock.',
        moveDeadline: 'Move by {{when}}',
        teamA: 'Team A name',
        teamB: 'Team B name',
        chooseTeam: 'Join team',
        selectTeam: 'Select a team…',
        team: 'Team',
        fairPlay: 'Fair play',
        fairPlaySubtitle: 'Review finished human games for unusual timing and engine-like play.',
        fairPlayReview: 'Fair-play review',
        fairPlayMinLevel: 'Minimum flag level',
        fairPlayEmpty: 'No flagged games',
        fairPlayEmptyHint: 'Finished human games with enough moves that meet the threshold will appear here.',
        fairPlayMedian: 'Median think',
        fairPlayFastMoves: 'Fast moves',
        fairPlayStreak: 'Fast streak',
        fairPlayCase: 'Case',
        fairPlayCases: 'Cases',
        fairPlayNotes: 'Staff notes',
        fairPlayAddNote: 'Add note',
        fairPlayNotePlaceholder: 'Observation, evidence, follow-up…',
        fairPlaySaveNote: 'Save note',
        fairPlayOpenCase: 'Open / update case',
        fairPlayBanPlayer: 'Ban from Playing Platform',
        fairPlayBanReason: 'Ban reason',
        fairPlayBanDays: 'Days (blank = indefinite)',
        fairPlayLiftBan: 'Lift ban',
        fairPlayActiveBans: 'Active bans on players',
        fairPlayCaseStatus: 'Case status',
        fairPlayResolution: 'Resolution',
        fairPlaySubject: 'Subject',
        fairPlayModeration: 'Moderation',
        fairPlayNoteFailed: 'Could not save note.',
        fairPlayCaseFailed: 'Could not update case.',
        fairPlayBanFailed: 'Could not apply ban.',
        fairPlayReportFailed: 'Could not update this report.',
        fairPlayAppealFailed: 'Could not update this appeal.',
        fairPlayCaseStatusLabel: {
          OPEN: 'Open',
          WATCHING: 'Watching',
          ACTIONED: 'Actioned',
          CLOSED: 'Closed',
          DISMISSED: 'Dismissed'
        },
        fairPlayResolutionLabel: {
          NONE: 'None',
          WARNING: 'Warning',
          BAN: 'Ban',
          DISMISSED: 'Dismissed'
        },
        moveTiming: 'Move timing',
        thinkTime: 'Think time',
        openBoard: 'Open board',
        runEngineReview: 'Re-run engine review',
        engineReviewRunning: 'Running Stockfish…',
        engineReviewFailed: 'Engine review failed.',
        engineReviewAutoPending: 'Automatic anti-cheat is queued for this game…',
        engineReviewAutoRunning: 'Automatic anti-cheat is analyzing every move…',
        engineReviewAutoDone: 'Automatic per-move anti-cheat complete.',
        engineReviewAutoFailed: 'Automatic anti-cheat failed — you can re-run below.',
        engineFlag: 'Flag',
        engineMatchTop1: 'Top-1 match',
        engineMatchTop3: 'Top-3 match',
        avgCpLoss: 'Avg. CPL',
        engineLevel: 'Engine flag',
        engineMatch: 'Match',
        cpLoss: 'CPL',
        jumpToPly: 'Jump',
        thinkSparkTitle: 'Think-time profile',
        thinkSparkHint: 'Amber/red bars are ultra-fast moves after the opening.',
        analysisQuick: 'Quick (d10)',
        analysisDeep: 'Deep (d16)',
        analysisCloud: 'Cloud analyze',
        analysisDepth: 'Analysis depth',
        clubReview: 'Club review',
        playAgain: 'Play again',
        prevMistake: 'Previous mistake',
        nextMistake: 'Next mistake',
        reviewReady: 'Review ready',
        retryClubReview: 'Retry club review',
        clubReviewNoMoves: 'No moves played — club review is not available. Use Analyze for a local engine look.',
        clubReviewTooShort: 'This game is too short for automatic club review. Try Quick or Cloud analyze.',
        clubReviewFailed: 'Club review failed. Retry club review, or run Quick / Cloud analyze.',
        clubReviewPending: 'Club review is preparing move qualities…',
        clubReviewSlow: 'Club review is taking longer than usual. You can Analyze locally meanwhile.',
        clubReviewUnavailable: 'Club review is not available for this game. Use Quick or Cloud analyze.',
        clubReviewRequestFailed: 'Could not queue club review.',
        cancelAnalysis: 'Cancel',
        cloudAnalysisFailed: 'Cloud analysis failed. Is server Stockfish available?',
        fairPlayFastSummary: 'W fast {{white}} · B fast {{black}}',
        moveNumber: '#',
        san: 'SAN',
        noGamesMatchFilter: 'No games match this filter',
        noGamesMatchFilterHint: 'Try another filter, or go back to All.',
        noGamesMatchSearch: 'No games match this search',
        noGamesMatchSearchHint: 'Try another name or clear the search.',
        clearSearch: 'Clear search',
        teamNamesRequired: 'Enter two different team names.',
        filterAll: 'All',
        yourMove: 'Your move',
        correspondenceGames: 'Correspondence',
        fairPlayLevel: {
          LOW: 'Clear',
          WATCH: 'Watch',
          HIGH: 'High'
        },
        quickMatch: 'Quick match',
        quickMatchDesc: 'Post a seek or join an open lobby with the same time control.',
        findOpponent: 'Find opponent',
        seekWaiting: 'Waiting for an opponent…',
        cancelSeek: 'Cancel seek',
        seekFailed: 'Could not update seek.',
        cancelSeekFailed: 'Could not cancel this seek.',
        acceptSeekFailed: 'Could not join this seek.',
        seekMatched: 'Opponent found — opening board…',
        outgoingChallenges: 'Outgoing challenges',
        outgoingChallengesHint: 'Challenges you sent that are still waiting.',
        noOutgoingChallenges: 'No outgoing challenges.',
        noOutgoingChallengesHint: 'When you challenge someone, they appear here until accepted or cancelled.',
        cancelChallenge: 'Cancel',
        cancelChallengeFailed: 'Could not cancel this challenge.',
        challengeAccepted: 'Challenge accepted',
        challengeDeclined: 'Challenge declined',
        challengeCancelled: 'Challenge cancelled',
        accuracy: 'Accuracy',
        moveQuality: {
          best: 'Best',
          brilliant: 'Brilliant',
          good: 'Good',
          inaccuracy: 'Inaccuracy',
          mistake: 'Mistake',
          blunder: 'Blunder'
        },
        continueGame: 'Game in progress',
        activeGames: 'In progress',
        pastGames: 'Finished',
        noPastGames: 'No finished games yet.',
        review: 'Review',
        cancel: 'Cancel',
        noPairings: 'No pairings yet',
        noPairingsHint: 'Pairings appear after the organizer starts a round.',
        choosePromotion: 'Choose promotion',
        challengeSentHint: 'Waiting for <profile>{{name}}</profile> to accept on Play Hub.',
        challengeAnother: 'Challenge someone else',
        typeToSearch: 'Find a player',
        typeToSearchHint: 'Type at least 2 characters to search the academy.',
        levelDepth: 'Engine depth ~{{depth}}',
        abortConfirm: 'Abort this game?',
        soundOn: 'Unmute',
        soundOff: 'Mute',
        reviewingPly: 'Move {{ply}} / {{total}}',
        you: 'You',
        reviewMoves: 'Review moves',
        showResult: 'Result',
        dismissResult: 'Close',
        backToLive: 'Live',
        retry: 'Retry',
        plyStart: 'First move',
        plyPrev: 'Previous move',
        plyNext: 'Next move',
        plyEnd: 'Last move',
        searchFailed: 'Player search failed. Try again.',
        searchFailedHint: 'Check your connection, then type another search.',
        outsideTop: 'Outside the current leaderboard page',
        reconnecting: 'Connection lost — reconnecting and syncing…',
        timeoutFailed: 'Could not claim the timeout. Try refreshing.',
        firstMoveYourTurn: 'Make your first move — {{seconds}}s (then clocks start)',
        firstMoveWaiting: 'Waiting for first move — {{seconds}}s',
        engineFailed: 'Computer move failed. Refresh if the board looks stuck.',
        challengeIncoming: 'New challenge',
        analyze: 'Analyze',
        reanalyze: 'Re-analyze',
        analyzing: 'Analyzing… {{pct}}%',
        hideAnalysis: 'Hide analysis',
        analysis: 'Engine eval',
        bestMove: 'Best move',
        levelBand: {
          beginner: 'Beginner',
          intermediate: 'Intermediate',
          club: 'Club strength',
          strong: 'Strong'
        },
        gameType: {
          VS_COMPUTER: 'vs Computer',
          CASUAL: 'Casual',
          RATED: 'Rated'
        },
        resultReason: {
          CHECKMATE: 'Checkmate',
          RESIGN: 'Resignation',
          STALEMATE: 'Stalemate',
          AGREEMENT: 'Draw by agreement',
          THREEFOLD: 'Threefold repetition',
          FIFTY_MOVE: 'Fifty-move rule',
          INSUFFICIENT: 'Insufficient material',
          TIMEOUT: 'Flag fell',
          ABORT: 'Aborted',
          COMPUTER: 'Computer'
        },
        status: {
          ACTIVE: 'In progress',
          WHITE_WON: 'White won',
          BLACK_WON: 'Black won',
          DRAW: 'Draw',
          ABORTED: 'Aborted',
          PENDING: 'Waiting to start'
        },

        acceptAppeal: 'Accept appeal',
        acceptRematch: 'Accept rematch',
        adjudicateConfirm: 'Set white win for this pairing?',
        adjudicateConfirmBlack: 'Set black win for this pairing?',
        adjudicateConfirmDraw: 'Set draw for this pairing?',
        appealFailed: 'Could not submit appeal.',
        appealPending: 'Appeal pending review',
        appealPlaceholder: 'Explain why the ban should be lifted…',
        appealRejectNotePrompt: 'Staff note for rejection (optional)',
        appealRejectedNote: 'Appeal rejected',
        applyFen: 'Apply FEN',
        applyPgn: 'Load PGN',
        animationSpeed: 'Animation',
        animationSlow: 'Slow',
        animationNormal: 'Normal',
        animationFast: 'Fast',
        banStatusUnknown: 'Ban status unavailable',
        boardSettings: 'Board settings',
        boardSettingsHint: 'Synced preferences used on every game board',
        boardTheme: 'Board theme',
        boardThemes: { classic: 'Classic', green: 'Green', brown: 'Brown', blue: 'Blue' },
        openings: {
          'alekhine_defence': "Alekhine Defence",
          'benoni_benko_path': "Benoni / Benko path",
          'birds_opening': "Bird's Opening",
          'caro_kann_defence': "Caro-Kann Defence",
          'caro_kann_advance': "Caro-Kann — Advance",
          'caro_kann_classical': "Caro-Kann — Classical",
          'dutch_defence': "Dutch Defence",
          'english_opening': "English Opening",
          'english_1_e5': "English — 1…e5",
          'english_1_e6': "English — 1…e6",
          'english_anglo_indian': "English — Anglo-Indian",
          'english_symmetrical': "English — Symmetrical",
          'evans_gambit': "Evans Gambit",
          'french_defence': "French Defence",
          'french_advance': "French — Advance",
          'french_classical': "French — Classical",
          'french_exchange': "French — Exchange",
          'giuoco_piano': "Giuoco Piano",
          'giuoco_piano_open': "Giuoco Piano — Open",
          'giuoco_piano_c3': "Giuoco Piano — c3",
          'giuoco_piano_cxd4': "Giuoco Piano — cxd4",
          'giuoco_piano_d4': "Giuoco Piano — d4",
          'indian_defence': "Indian Defence",
          'indian_3_nf3': "Indian — 3.Nf3",
          'italian_game': "Italian Game",
          'kings_gambit': "King's Gambit",
          'kings_indian_defence': "King's Indian Defence",
          'kings_knight_opening': "King's Knight Opening",
          'kings_knight_two_knights_path': "King's Knight — Two Knights path",
          'kings_pawn': "King's Pawn",
          'london_system': "London System",
          'london_system_nc6': "London System — Nc6",
          'london_system_c5': "London System — c5",
          'london_system_main_line': "London System — main line",
          'modern_defence': "Modern Defence",
          'nimzo_indian_defence': "Nimzo-Indian Defence",
          'nimzo_indian_path': "Nimzo-Indian path",
          'nimzo_larsen_attack': "Nimzo–Larsen Attack",
          'open_game': "Open Game",
          'owens_defence': "Owen's Defence",
          'petrov_defence': "Petrov Defence",
          'philidor_defence': "Philidor Defence",
          'pirc_defence': "Pirc Defence",
          'qgd': "QGD",
          'queens_gambit': "Queen's Gambit",
          'queens_gambit_accepted': "Queen's Gambit Accepted",
          'queens_gambit_declined': "Queen's Gambit Declined",
          'queens_gambit_declined_3_nf3': "Queen's Gambit Declined — 3.Nf3",
          'queens_gambit_declined_bg5': "Queen's Gambit Declined — Bg5",
          'queens_gambit_declined_london_hybrid': "Queen's Gambit Declined — London hybrid",
          'queens_indian_nimzo_path': "Queen's Indian / Nimzo path",
          'queens_pawn': "Queen's Pawn",
          'queens_pawn_game': "Queen's Pawn Game",
          'ruy_lopez': "Ruy Lopez",
          'ruy_lopez_morphy': "Ruy Lopez — Morphy",
          'ruy_lopez_morphy_defence': "Ruy Lopez — Morphy Defence",
          'ruy_lopez_morphy_bb3': "Ruy Lopez — Morphy, Bb3",
          'ruy_lopez_morphy_closed': "Ruy Lopez — Morphy, Closed",
          'ruy_lopez_morphy_re1': "Ruy Lopez — Morphy, Re1",
          'ruy_lopez_morphy_b5': "Ruy Lopez — Morphy, b5",
          'ruy_lopez_morphy_d6': "Ruy Lopez — Morphy, d6",
          'r_ti_opening': "Réti Opening",
          'r_ti_1_nf6': "Réti — 1…Nf6",
          'r_ti_1_c5': "Réti — 1…c5",
          'r_ti_1_d5': "Réti — 1…d5",
          'scandinavian_defence': "Scandinavian Defence",
          'scandinavian_2_nf6': "Scandinavian — 2…Nf6",
          'scandinavian_2_qxd5': "Scandinavian — 2…Qxd5",
          'sicilian_defence': "Sicilian Defence",
          'sicilian_open': "Sicilian Open",
          'sicilian_2_nf3': "Sicilian — 2.Nf3",
          'sicilian_2_nc6': "Sicilian — 2…Nc6",
          'sicilian_2_d6': "Sicilian — 2…d6",
          'sicilian_2_e6': "Sicilian — 2…e6",
          'sicilian_classical_path': "Sicilian — Classical path",
          'sicilian_dragon': "Sicilian — Dragon",
          'sicilian_dragon_classical': "Sicilian — Dragon, Classical",
          'sicilian_dragon_yugoslav': "Sicilian — Dragon, Yugoslav",
          'sicilian_hyperaccelerated': "Sicilian — Hyperaccelerated",
          'sicilian_najdorf': "Sicilian — Najdorf",
          'sicilian_najdorf_english_attack': "Sicilian — Najdorf, English Attack",
          'slav_defence': "Slav Defence",
          'two_knights_defence': "Two Knights Defence",
          'van_geet_opening': "Van Geet Opening"
        },
        defaultTeamA: 'Team A',
        defaultTeamB: 'Team B',
        claimDraw: 'Claim draw',
        claimDrawFailed: 'Could not claim draw.',
        clearHighlights: 'Clear highlights',
        coachBoard: 'Coach board',
        coachBoardArrowHint: 'Right-click a square to highlight; right-click another square to draw an arrow.',
        coachBoardClassroomHint: 'For voice/video with students, use Classroom live. This board is for positions and arrows only.',
        coachBoardSubtitle: 'Shared teaching board — set a position, draw arrows, open Classroom for live A/V',
        confirmMove: 'Confirm move',
        copyFen: 'Copy FEN',
        copyFailed: 'Could not copy',
        dismissReport: 'Dismiss',
        favoriteOpenings: 'Favorite openings',
        fenCopied: 'FEN copied',
        gameChat: 'Game chat',
        highContrast: 'High contrast',
        hubPartialLoad: 'Some hub data failed to load',
        invalidFen: 'Invalid FEN',
        invalidPgn: 'Could not parse PGN',
        loadMoreGames: 'Load more ({{loaded}} of {{total}})',
        moveConfirmation: 'Confirm moves',
        moveSending: 'Sending move…',
        myRatingLoadFailed: 'Could not load your rating',
        noOpenAppeals: 'No open appeals',
        noOpenReports: 'No open reports',
        noOpenSeeks: 'No open seeks',
        noOpeningsYet: 'No openings yet',
        openAppeals: 'Open appeals',
        openClassroomLive: 'Open Classroom live',
        openGame: 'Open game',
        openReports: 'Open reports',
        peakRating: 'Peak',
        pgnLoaded: 'PGN loaded to board tip',
        pgnPaste: 'Paste PGN',
        pgnPasteHint: 'Full PGN or move list…',
        pieceSet: 'Piece set',
        pieceSetStandard: 'Standard',
        pieceSetUnicode: 'Unicode',
        playerNotFound: 'Player not found',
        playerProfile: 'Player profile',
        playerProfileSubtitle: 'Ratings by speed and recent games',
        playingNow: 'Playing now',
        prefsSaved: 'Preferences saved',
        ratingTrend: 'Recent rating trend',
        ratingsBySpeed: 'Ratings by speed',
        recentGames: 'Recent games',
        rejectAppeal: 'Reject appeal',
        rematchIncoming: 'Rematch offer',
        rematchIncomingToast: 'Rematch challenge',
        rematchSending: 'Sending…',
        reportDetails: 'Details (optional)',
        reportFailed: 'Could not submit report.',
        reportPlayer: 'Report player',
        reportPrompt: 'Briefly describe why you are reporting this player (optional):',
        reportReasonPrompt: 'Report reason',
        reportSent: 'Report submitted. Staff will review.',
        reportedBy: 'Reported by',
        reportsAppealsLoadFailed: 'Could not load reports or appeals',
        resetBoard: 'Reset',
        resolveReport: 'Resolve',
        searchGames: 'Search games',
        searchGamesPlaceholder: 'Opponent name or status…',
        shortcutEnds: 'Jump to start / end',
        shortcutHelpTitle: 'Keyboard shortcuts',
        shortcutScrub: 'Step through moves',
        shortcutToggleHelp: 'Show / hide this help',
        shortcuts: 'Shortcuts',
        sound: 'Sound',
        speedClass: 'Speed',
        speedClassLabel: 'Speed class',
        submitAppeal: 'Submit appeal',
        submitReport: 'Submit report',
        submitting: 'Submitting…',
        themesLoadFailed: 'Could not load themes',
        tourneyFilterActive: 'Open & running',
        tourneyFilterDraft: 'Draft',
        tourneyFilterCompleted: 'Completed',
        undoMove: 'Undo move',
        reportReason: {
          assistance: 'Suspected engine / assistance',
          chat: 'Abusive chat',
          stalling: 'Stalling / refusing to play',
          other: 'Other'
        },
},
      auth: {
        welcome: 'Welcome',
        signInTo: 'Sign in to {{name}}',
        login: 'Sign in',
        username: 'Username',
        password: 'Password',
        forgot: 'Forgot password?',
        enterUsername: 'Enter your username',
        enterPassword: 'Enter your password',
        invalid: 'Invalid username or password',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        agreePrefix: 'By signing in you agree to our',
        agreeAnd: 'and',
        privacyPolicy: 'Privacy Policy',
        terms: 'Terms',
        tooManyAttempts: 'Too many attempts. Please try again later.',
        sessionExpired: 'Your session has expired. Please log in again.',
        sessionInvalid: 'Your session is no longer valid. Please log in again.',
        sessionInactive: 'Your session expired due to inactivity. Please log in again.'
      },
      footer: {
        quickLinks: 'Quick Links',
        academyContact: 'Academy Contact',
        licenseInfo: 'License Info',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service',
        terms: 'Terms',
        forgotPassword: 'Forgot Password',
        helpGettingStarted: 'Help & Getting Started',
        helpFaq: 'Help & FAQ',
        allRightsReserved: 'All rights reserved.',
        developedBy: 'Developed & licensed by',
        chessManagementSystem: 'Chess Management System',
        contactDetailsHint: 'Contact details can be added by your academy administrator.',
        licensePending: 'License status will appear here when available.',
        daysRemaining: '{{count}} day remaining',
        daysRemaining_other: '{{count}} days remaining',
        licenseActive: 'License active',
        built: 'Built {{version}} · {{tz}}',
        unknownVersion: 'unknown'
      },
      common: {
        language: 'Language',
        save: 'Save',
        cancel: 'Cancel',
        loading: 'Loading…',
        edit: 'Edit',
        archive: 'Archive',
        add: 'Add',
        all: 'All',
        showing: 'Showing',
        of: 'of',
        to: 'to',
        results: 'results',
        rowsPerPage: 'Rows per page:',
        downloadExcel: 'Download Excel',
        search: 'Search',
        id: 'ID:',
        idColumn: 'ID',
        items: 'items',
        back: 'Back',
        firstPage: 'First page',
        previousPage: 'Previous page',
        nextPage: 'Next page',
        lastPage: 'Last page',
        prev: 'Prev',
        next: 'Next',
        pageOf: 'Page {{current}} of {{total}}',
        showingRange: 'Showing {{start}} to {{end}} of {{total}} results',
        noDataExport: 'No data to export',
        exportFailed: 'Export failed. Please try again.',
        downloadAsExcel: 'Download as Excel (.xlsx)',
        optionsLoadFailed: 'Could not load options. Refresh the page or try again.',
        listLoadFailed: 'Could not load this list. Refresh and try again.',
        unknownError: 'Unknown error',
        retry: 'Try again',
        minutesCount: '{{count}} minutes',
        unknown: 'Unknown',
        userFallback: 'User #{{id}}',
        branchFallback: 'Branch #{{id}}',
        levelFallback: 'Level #{{id}}',
        active: 'Active',
        archived: 'Archived',
        inactive: 'Inactive',
        inactiveSuffix: ' (Inactive)',
        activate: 'Activate',
        actions: 'Actions',
        status: 'Status',
        username: 'Username',
        email: 'Email',
        phone: 'Phone',
        roles: 'Roles',
        fullName: 'Full Name',
        name: 'Name',
        description: 'Description',
        noDataFound: 'No data found',
        emptySearchHint: 'Try adjusting your search terms',
        noMatchSearch: 'No {{entity}} match your search',
        noFound: 'No {{entity}} found',
        exporting: 'Exporting…',
        showDetails: 'Show Details',
        hideDetails: 'Hide Details',
        system: 'System',
        na: 'N/A',
        entries: 'entries',
        apply: 'Apply',
        never: 'Never',
        code: 'Code',
        location: 'Location',
        place: 'Place',
        score: 'Score',
        dismiss: 'Dismiss',
        yes: 'Yes',
        no: 'No',
        done: 'Done.',
        close: 'Close',
        gotIt: 'Got it',
        confirm: 'Confirm',
        popupInfo: 'Information',
        popupSuccess: 'Success',
        popupWarning: 'Please Review',
        popupError: 'Attention Needed',
        pressEscCancel: 'Press Esc to cancel',
        pressEscClose: 'Press Esc to close',
        backendDown: 'Backend server is down — please contact an administrator or try again later.',
        ariaClose: 'Close',
        info: 'Info',
        learnMore: 'Learn more',
        remove: 'Remove',
        assigned: 'Assigned'
      },
      parent: {
        childSwitcher: {
          loadingChildren: 'Loading children…',
          viewingChild: 'Viewing child',
          switchChild: 'Switch child',
          selectChild: 'Select a child…'
        },
        chooseChildTitle: 'Choose a child',
        chooseChildBody: 'Select which linked student you want to view, or go back to the parent home.',
        backToParentHome: 'Back to parent home',
        parentHome: 'Parent home',
        viewingPrefix: 'Viewing',
        readOnly: 'Read-only',
        roleLabel: 'Parent'
      },
      header: {
        accountMenu: 'Account menu',
        openNav: 'Open navigation menu',
        closeNav: 'Close navigation menu',
        closeNavSidebar: 'Close navigation',
        myProfile: 'My Profile',
        switchRole: 'Switch Role',
        logout: 'Logout'
      },
      selectSearch: {
        select: 'Select…',
        search: 'Search…',
        loading: 'Loading…',
        noOptions: 'No options found',
        loadFailed: 'Could not load options — try again',
      },
      datePicker: {
        clearDate: 'Clear date',
        selectTime: 'Select time',
        selectDateTime: 'Select date and time',
        selectTimeIntervals: 'Select Time (15-min intervals)',
        selectTimeWithFormat: 'Select Time ({{format}}, 15-min intervals)',
        dateWithFormat: 'Date ({{format}})',
        timeWithFormat: 'Time ({{format}})',
        noTimeOptions: 'No time options available',
        duration: 'Duration: {{hours}}h {{minutes}}m',
        exceeds3Hours: 'Exceeds 3 hours',
        hour12: '12-hour',
        hour24: '24-hour',
        clear: 'Clear',
        done: 'Done'
      },
      notificationBell: {
        ariaLabel: 'Notifications',
        ariaLabelUnread: 'Notifications ({{count}} unread)',
        title: 'Notifications',
        unread: '{{count}} unread',
        markReadFailed: 'Could not mark as read. Try again.',
        markAllRead: 'Mark all read',
        empty: 'All caught up!',
        viewAll: 'View all notifications',
        unreadHint: '{{count}} need your attention',
        caughtUpHint: 'No unread alerts right now',
        emptyHint: 'Wallet, attendance, and privacy alerts will show up here when action is needed.'
      },
      publicLayout: {
        developedBy: 'Developed & Licensed by',
        licenseSupport: 'For license purchase or support',
        contactSupport: 'Contact Support'
      },
      search: {
        placeholder: 'Search students, coaches, batches…',
        aria: 'Global search',
        searching: 'Searching…',
        noResults: 'No results for “{{q}}”',
        students: 'Students',
        coaches: 'Coaches',
        batches: 'Batches',
        apiUnavailable: 'Search API not available. Restart the backend container.',
        failed: 'Search failed. Please try again.'
      },
      days: {
        short: {
          '1': 'Mon',
          '2': 'Tue',
          '3': 'Wed',
          '4': 'Thu',
          '5': 'Fri',
          '6': 'Sat',
          '7': 'Sun'
        }
      },
      charges: {
        modes: {
          PER_SESSION: 'Per session (flat)',
          PER_SESSION_HOURLY: 'Per session (hourly)',
          PER_PERIOD: 'Period plan',
          RECURRING_SESSIONS: 'Per session (hourly)',
          MONTHLY: 'Period plan',
          YEARLY: 'Period plan'
        },
        intervals: {
          WEEKLY: 'Weekly (7 days)',
          MONTHLY: 'Monthly (30 days)',
          QUARTERLY: 'Quarterly (90 days)',
          SEMI_ANNUAL: 'Semi-annual (180 days)',
          YEARLY: 'Yearly (365 days)'
        },
        status: {
          PAID: 'Paid',
          UNPAID: 'Unpaid',
          ESCALATED: 'Escalated'
        }
      },
      accountAccess: {
        signInLink: {
          button: 'Email sign-in link',
          buttonTitle: 'Send an email with a link to set or reset their password',
          buttonTitleNoEmail: 'Add an email address on the profile to send a sign-in link',
          noEmailAlert: 'This {{role}} has no email address. Add an email before sending a sign-in link.',
          listConfirm: 'Email a sign-in link to this {{role}}? They can use it to set or reset their password.',
          editConfirm: 'Email a new sign-in link to this {{role}}?',
          success: 'Sign-in link emailed successfully.',
          failure: 'Could not email sign-in link.',
          createCheckbox: 'Email sign-in link (they set their own password)',
          createNote: 'A one-time sign-in link will be emailed so they can set their password.',
          bulkImportOption: 'Email sign-in link — students set their own password'
        },
        passwordReset: {
          pageSubtitle: 'We\'ll email a password reset link if an account exists',
          successMessage: 'If an account exists for that email or username, a password reset link has been sent. Please check your inbox.',
          submitButton: 'Email password reset link',
          publicHelp: 'You will receive a password reset link at your registered email address.'
        }
      },
      userActions: {
        edit: 'Edit',
        archive: 'Archive',
        activate: 'Activate',
        removeFromList: 'Remove from list',
        removePermanently: 'Remove permanently',
        editTitle: 'Edit {{label}}',
        archiveTitle: 'Archive {{label}}',
        reactivateTitle: 'Reactivate {{label}}',
        removeFromListTitle: 'Remove from your list without archiving the student',
        removePermanentlyTitle: 'Remove from lists and free a license slot; history kept as (Inactive)',
        archiveConfirm: 'Archive this {{label}}? They cannot sign in. Their record is kept and still counts toward your license until you remove them permanently (when at capacity).',
        permanentRemoveConfirm: 'Permanently remove this archived {{label}} from your lists? Their attendance, payments, and other history stay in the system and will show as "(Inactive)". This frees one license slot. This cannot be undone.',
        reactivateConfirm: 'Reactivate this {{label}}? They will be able to sign in again.',
        loadArchiveFailed: 'Failed to load archive options for {{label}}.',
        archiveFailed: 'Failed to archive {{label}}.',
        removeFailed: 'Failed to remove {{label}}.',
        activateFailed: 'Failed to activate {{label}}.',
        labels: {
          student: 'student',
          coach: 'coach',
          manager: 'manager',
          user: 'user'
        }
      },
      carryForward: {
        columnHelp: 'Carry forward controls billing when a student is absent. If enabled, the session fee is not deducted and the credit can be used later. If disabled, the fee is still deducted even when the student is marked absent.',
        modes: {
          ALLOWED: {
            label: 'Allowed for All Students',
            desc: 'Absent students are not charged — session credits carry forward for future use.'
          },
          NOT_ALLOWED: {
            label: 'Not Allowed for Any Student',
            desc: 'Absent students are still charged — session fees are deducted from their wallet.'
          },
          ENROLLMENT_LEVEL: {
            label: 'Decide at Enrollment Level',
            desc: 'Choose carry forward individually when enrolling each student.'
          }
        },
        batchPolicyTitle: {
          ALLOWED: 'Batch policy: carry forward enabled for all students. If a student is marked absent, their session fee is not deducted — the credit carries forward.',
          NOT_ALLOWED: 'Batch policy: carry forward disabled for all students. If a student is marked absent, their session fee is still deducted from their wallet.',
          ENROLLMENT_LEVEL: 'Batch policy: carry forward is set per student at enrollment. Each student may have it enabled (no charge when absent) or disabled (charge still applies).',
          default: 'This student follows the batch carry forward policy.'
        },
        batchPolicySummary: {
          ALLOWED: 'If a student is absent, they are not charged — the session credit carries forward.',
          NOT_ALLOWED: 'If a student is absent, they are still charged — the session fee is deducted from their wallet.',
          ENROLLMENT_LEVEL: 'You can choose carry forward individually for each student when enrolling them.'
        },
        batchPolicyLabel: {
          ALLOWED: 'Carry Forward Enabled for All',
          NOT_ALLOWED: 'Carry Forward Disabled for All',
          ENROLLMENT_LEVEL: 'Enrollment Level (Student-Specific)',
          unknown: 'Unknown'
        },
        studentOverrideTitle: {
          enabled: 'Carry forward enabled for this student. If marked absent, the session fee is not deducted — the credit carries forward.',
          disabled: 'Carry forward disabled for this student. If marked absent, the session fee is still deducted from their wallet.'
        },
        studentOverrideShort: {
          enabled: 'Not charged when absent — credit carries forward',
          disabled: 'Still charged when absent — no carry forward'
        },
        attendanceCell: {
          followsBatch: 'Follows Batch ({{mode}})',
          enabled: 'Enabled',
          disabled: 'Disabled',
          unknown: 'Unknown'
        }
      },
      branchModes: {
        OFFLINE: {
          label: 'Offline',
          description: 'In-person sessions at a physical location'
        },
        ONLINE: {
          label: 'Online',
          description: 'Remote / virtual sessions only'
        },
        HYBRID: {
          label: 'Online + Offline',
          description: 'Offers both in-person and online sessions'
        }
      },
      sessionModes: {
        ANY: 'Any',
        ONLINE: 'Online',
        OFFLINE: 'Offline'
      },
      popup: {
        labels: {
          alert: 'Got it',
          confirm: 'Confirm',
          cancel: 'Cancel'
        },
        titles: {
          confirmDestructive: 'Confirm Destructive Action',
          pleaseConfirm: 'Please Confirm',
          confirmAction: 'Confirm Action',
          success: 'Success',
          attention: 'Something needs attention',
          pleaseReview: 'Please review',
          information: 'Information'
        },
        confirmLabels: {
          delete: 'Delete',
          archive: 'Archive',
          activate: 'Activate',
          execute: 'Execute',
          send: 'Send',
          trigger: 'Trigger',
          remove: 'Remove',
          continue: 'Continue'
        }
      },
      unsavedChanges: {
        title: 'Unsaved changes',
        message: 'You have changes that are not saved yet. Save them before leaving, discard them, or stay on this page.',
        save: 'Save and leave',
        discard: 'Discard changes',
        stay: 'Stay'
      },
      errors: {
        api: {
          fallback: 'Something went wrong.',
          upgradeRequired: 'Upgrade required',
          onlinePaymentsUpgrade: 'Online payment gateways require a Professional or Enterprise license. Please contact your academy to upgrade.',
          planLimitReached: 'Plan limit reached',
          planLimitMessage: 'Your current plan has reached its capacity. Please upgrade your plan or free up capacity and try again.',
          requestFailed: 'Request failed'
        }
      },
      wallet: {
        txnReasons: {
          ADVANCE_PAYMENT: 'Advance top-up',
          ONLINE_TOPUP: 'Online top-up',
          SESSION_CHARGE: 'Session charge',
          SESSION_REVERSAL: 'Session reversal',
          DISCOUNT: 'Discount credit',
          REFUND: 'Refund',
          LEGACY_DEBIT: 'Debit',
          default: 'Transaction'
        },
        dateLines: {
          session: 'Session',
          created: 'Created',
          paidOn: 'Paid on'
        }
      },
      regional: {
        presets: {
          india: 'India',
          india12: 'India (12-hour)',
          uae: 'UAE',
          us: 'United States',
          uk: 'United Kingdom',
          eurozone: 'Eurozone (generic)',
          germany: 'Germany',
          france: 'France',
          netherlands: 'Netherlands',
          belgium: 'Belgium',
          spain: 'Spain'
        },
        paymentReference: {
          IN: 'e.g. Receipt #1234, UPI Ref: 123456789012',
          US: 'e.g. Receipt #1234, Check #5678, or card auth code',
          CA: 'e.g. Receipt #1234, PAD reference, or card auth code',
          GB: 'e.g. Receipt #1234, bank reference, or cheque number',
          AE: 'e.g. Receipt #1234 or bank transfer reference',
          EU: 'e.g. Receipt #1234 or SEPA / bank transfer reference',
          default: 'e.g. Receipt #1234'
        },
        bankRouting: {
          IN: 'IFSC Code',
          US: 'Routing Number',
          CA: 'Transit / Institution Number',
          GB: 'Sort Code',
          AE: 'SWIFT / IBAN',
          EU: 'IBAN',
          default: 'Bank Code'
        }
      },
      batches: {
        title: 'Batches',
        subtitle: 'Manage and organize your chess academy batches',
        add: 'Add Batch',
        searchPlaceholder: 'Search batches by name, ID, or level...',
        entityLabel: 'batches',
        archiveConfirm: 'Archive this batch?',
        archiveFailed: 'Failed to archive batch.',
        empty: 'No batches found',
        emptyHint: 'Create your first batch to get started',
        managerSubtitle: 'Manage and organize training batches',
        active: 'Active',
        archived: 'Archived',
        batchNumber: 'Batch #{{id}}',
        unknown: 'Unknown batch',
        individualSessions: 'Individual Sessions',
        individualSessionsNamed: 'Individual Sessions - {{level}}',
        walkInNamed: 'Walk-in - Level #{{level}}',
        carryForward: 'Carry Forward',
        perStudent: 'Per Student',
        carryForwardBadge: {
          allowed: 'Carry forward allowed',
          notAllowed: 'No carry forward',
          tooltipAllowed: 'Unused credits will carry forward to the next billing cycle.',
          tooltipNotAllowed: 'Unused credits will expire at the end of the billing cycle.'
        },
        editTitle: 'Edit batch',
        sessionsTitle: 'View sessions',
        archiveTitle: 'Archive batch',
        level: 'Level',
        branch: 'Branch',
        schedule: 'Schedule',
        sessionDays: 'Session Days',
        sessionTime: 'Session Time',
        charges: 'Charges',
        lockNotes: {
          branch: 'Branch is fixed after the batch is created (via level).',
          level: 'Skill level is fixed after the batch is created and cannot be changed.',
          carryForward: 'Carry forward policy cannot be changed once enrollments or sessions exist.',
          chargesMode: 'Billing charges mode cannot be changed once enrollments or sessions exist.',
          monthlyYearly: 'Period fee and interval cannot be changed once enrollments or sessions exist.',
          systemManaged: 'This batch is managed automatically by the system and cannot be edited.',
          sessionCharge: 'Applies to future session billing only; past charges are unchanged.'
        }
      },
      dashboard: {
        welcome: 'Welcome, {{name}}',
        quickActions: 'Quick Actions',
        defaultAdmin: 'Admin',
        defaultManager: 'Manager',
        defaultCoach: 'Coach',
        defaultStudent: 'Student'
      },
      table: {
        branch: 'Branch',
        level: 'Level',
        batch: 'Batch',
        coach: 'Coach',
        time: 'Time',
        when: 'When',
        start: 'Start',
        end: 'End',
        mode: 'Mode',
        studentName: 'Student Name',
        coachName: 'Coach Name',
        branchName: 'Branch Name',
        enrolledDate: 'Enrolled Date',
        payment: 'Payment',
        balanceAfter: 'Balance After',
        planType: 'Plan Type',
        groupType: 'Group Type',
        defaultRate: 'Default Rate',
        defaultCredits: 'Default Credits',
        levelRange: 'Level Range',
        creditExpiry: 'Credit Expiry',
        attendance: 'Attendance',
        topics: 'Topics',
        balanceBefore: 'Balance Before',
        sessionFee: 'Session Fee',
        dateTime: 'Date & Time',
        date: 'Date',
        description: 'Description',
        carryForward: 'Carry Forward',
        amount: 'Amount'
      },
      pages: {
        adminDashboard: {
          emailAlertTitle: 'Outbound email is not configured.',
          emailAlertBody: 'Students and staff will not receive sign-in links, reminders, or statements until SMTP is set up.',
          configureEmail: 'Configure email',
          whatsappAlertTitle: 'WhatsApp (WATI) is not configured.',
          whatsappAlertBody: 'Connect your WATI account to send session reminders and wallet alerts on WhatsApp.',
          configureWhatsApp: 'Configure WhatsApp',
          subtitle: 'Manage your academy with complete control over users, branches, and system settings.',
          totalStudents: 'Total Students',
          activeBranches: 'Active Branches',
          activeCoaches: 'Active Coaches',
          atRiskStudents: 'At-Risk Students',
          activeEnrollments: 'Active Enrollments',
          addStudent: 'Add Student',
          addStudentDesc: 'Enroll a new student in the academy',
          createBatch: 'Create Batch',
          createBatchDesc: 'Set up a new training batch',
          walletTopup: 'Wallet Top-up',
          walletTopupDesc: 'Add funds to student wallet',
          manageCoaches: 'Manage Coaches',
          manageCoachesDesc: 'View and manage coaching staff',
          viewSessions: 'View Sessions',
          viewSessionsDesc: 'Manage all training sessions',
          paymentReports: 'Payment Reports',
          paymentReportsDesc: 'View financial collections'
        },
        managerDashboard: {
          subtitle: 'Manage operations efficiently. View coaches, students, and track branch performance.',
          coaches: 'Coaches',
          students: 'Students',
          manageBatches: 'Manage Batches',
          manageBatchesDesc: 'View and organize training batches',
          enrollments: 'Enrollments',
          enrollmentsDesc: 'Manage student enrollments',
          manageLevels: 'Manage Levels',
          manageLevelsDesc: 'Configure levels',
          sessionStatement: 'Session Statement',
          sessionStatementDesc: 'View detailed session reports',
          batchPayments: 'Batch Attendance',
          batchPaymentsDesc: 'View batch attendance matrix reports'
        },
        coachDashboard: {
          subtitle: 'Your day at a glance. Quickly check sessions, mark attendance, and plan the week ahead.',
          metricsFailed: 'Failed to load metrics.',
          todayFailed: 'Failed to load today sessions.',
          upcomingFailed: 'Failed to load upcoming sessions.',
          sessionsToday: 'Sessions Today',
          studentsToday: 'Students Today',
          hoursThisWeek: 'Hours This Week',
          attendanceWtd: 'Attendance (WTD)',
          noShowsToday: 'No-shows Today',
          todaysSchedule: 'Today\'s Schedule',
          viewAllSessions: 'View All Sessions',
          noSessionsToday: 'No sessions scheduled for today.',
          upcoming7Days: 'Upcoming (Next 7 Days)',
          noUpcomingSessions: 'No upcoming sessions in the next 7 days.',
          viewAttendance: 'View Attendance',
          markAttendance: 'Mark Attendance'
        },
        studentDashboard: {
          childOverview: '{{name}} overview',
          childFallback: 'Child',
          childSubtitle: 'Attendance summary, upcoming sessions, and wallet balance for your linked student.',
          subtitle: 'Track your progress, view upcoming sessions, and manage your wallet balance.',
          loadFailed: 'Failed to load dashboard.',
          activeEnrollments: 'Active Enrollments',
          attendanceRate: 'Attendance Rate',
          upcoming7d: 'Upcoming (7 days)',
          lastAttended: 'Last Attended',
          upcomingSessions: 'Upcoming Sessions',
          noUpcomingSessions: 'No upcoming sessions.',
          recentAttendance: 'Recent Attendance',
          noAttendanceRecords: 'No attendance records.'
        },
        parentHome: {
          loading: 'Loading parent portal…',
          portalLabel: 'Parent portal',
          hello: 'Hello, {{name}}',
          there: 'there',
          approvedSubtitle: 'Stay on top of your child\'s attendance, schedule, and academy fees — all in one place.',
          pendingSubtitle: 'Your guardian account is being set up.',
          accessPending: 'Access pending',
          pendingDefault: 'Your guardian link is waiting for academy approval. You can sign in again once an administrator approves your request.',
          noLinkedStudents: 'No linked students yet. Ask the academy to send you a guardian invite.',
          yourChildren: 'Your children',
          currentlyViewing: 'Currently viewing',
          selectChildPrompt: 'Select a child above to see their summary and quick actions.',
          quickActions: 'Quick actions',
          more: 'More',
          otherLinks: 'Other links',
          helpFaq: 'Help & FAQ',
          loadingChild: 'Loading child data…',
          snapshotLoadFailed: 'Could not load child snapshot. Refresh to try again.',
          loadFailed: 'Unable to load parent portal.',
          pendingAccess: 'Your guardian access is pending approval or has not been linked yet.',
          overview: 'Overview',
          overviewDesc: 'Attendance & upcoming sessions',
          calendar: 'Calendar',
          calendarDesc: 'Monthly schedule',
          wallet: 'Wallet',
          walletDesc: 'Balance & payments',
          attendance: 'Attendance',
          attendanceDesc: 'Session history',
          enrollments: 'Enrollments',
          sessions: 'Sessions',
          notifications: 'Notifications',
          sessionStatement: 'Session statement',
          statusPendingApproval: 'Pending approval',
          statusApproved: 'Active',
          statusRejected: 'Rejected',
          statusRevoked: 'Revoked',
          selected: 'Selected',
          guardian: 'Guardian'
        },
        managerStudents: {
          title: 'Students',
          subtitle: 'Search, review, and manage student access with a cleaner list experience.',
          addStudent: 'Add Student',
          searchPlaceholder: 'Search students by name, username, email, phone, or ID...',
          entityLabel: 'students',
          noMatch: 'No students match your search',
          empty: 'No students found',
          removeConfirm: 'Remove {{name}} from your list?\n\nThis only removes your direct manager link. The student is not archived and may still appear if enrolled in your branches.',
          removeFailed: 'Failed to remove student from list.',
          thisStudent: 'this student',
          entitySingular: 'student'
        },
        managerCoaches: {
          title: 'Coaches',
          subtitle: 'View coaches in your branch scope',
          searchPlaceholder: 'Search coaches by name, username, email, phone, or ID...',
          entityLabel: 'coaches',
          noMatch: 'No coaches match your search criteria',
          empty: 'No coaches found in the system'
        },
        managerBranches: {
          title: 'My Branches',
          subtitle: 'Branches you manage',
          searchPlaceholder: 'Search branches by name, code, phone, mode, or ID...',
          entityLabel: 'branches',
          unauthorized: 'You are not authorized to view branches.',
          loadFailed: 'Failed to load branches.',
          noMatch: 'No branches match your search',
          empty: 'No branches assigned to you yet',
          emptyHint: 'Contact an administrator to assign branches to your account'
        },
        levels: {
          title: 'Levels',
          subtitle: 'Manage chess levels',
          managerSubtitle: 'Manage chess levels',
          coachSubtitle: 'View chess levels',
          addLevel: 'Add Level',
          searchPlaceholder: 'Search levels by name, branch, description, or ID...',
          entityLabel: 'levels',
          loadFailed: 'Failed to load levels.',
          noMatch: 'No levels match your search',
          empty: 'No levels found',
          archiveConfirm: 'Archive this level?',
          archiveFailed: 'Failed to archive level.',
          colLevelName: 'Level Name',
          editLevel: 'Edit level',
          archiveLevel: 'Archive level',
          levelFallback: 'Level {{id}}',
          form: {
            levelName: 'Level Name',
            branchForLevel: 'Branch for this Level',
            branchFixedNote: 'Branch is fixed after the level is created and cannot be changed.',
            loadBranchesFailed: 'Failed to load branches',
            loadingBranches: 'Loading branches…'
          }
        },
        feePlans: {
          title: 'Fee Plans',
          subtitle: 'Branch-specific fee plans and billing configuration',
          loadFailed: 'Failed to load plans.',
          empty: 'No fee plans found.',
          allBranches: 'All branches',
          addPlan: 'Add Plan',
          selectBranch: 'Select branch…',
          selectBranchHint: 'Select a branch to view plans.',
          noPlans: 'No plans found for this branch.',
          archiveConfirm: 'Archive this plan? It will be hidden from selection but retained for history.',
          archiveFailed: 'Failed to archive plan.',
          activateFailed: 'Failed to activate plan.',
          colPlan: 'Plan#',
          colBranch: 'Branch',
          colType: 'Type',
          colGroup: 'Group',
          colRate: 'Rate',
          colCredits: 'Credits',
          colLevelScope: 'Level Scope',
          archiveTitle: 'Archive plan',
          activateTitle: 'Activate plan'
        },
        studentBulkImport: {
          title: 'Bulk Student Import',
          subtitle: 'Upload a CSV file to import multiple students at once. Download the template, fill in student data, and upload to get started.',
          downloadTemplate: 'Download Template',
          totalRows: 'Total Rows',
          imported: 'Imported',
          skipped: 'Skipped',
          failed: 'Failed',
          allSuccess: 'All {{count}} students were imported successfully!',
          completedWithIssues: 'Import completed with some issues.',
          summaryIssues: '{{success}} imported · {{skipped}} skipped (duplicates) · {{failed}} failed. Review the table below for details.',
          detailedReport: 'Detailed Import Report',
          importAnother: 'Import Another File',
          viewAllStudents: 'View All Students',
          colNum: '#',
          colName: 'Student Name',
          colUsername: 'Username',
          colEmail: 'Email',
          colStatus: 'Status',
          colReason: 'Reason / Note',
          statusImported: '✓ Imported',
          statusSkipped: '⊘ Skipped',
          statusFailed: '✗ Failed',
          duplicateSkipped: 'Skipped — student with this username or email already exists',
          unknownError: 'Unknown error',
          step1: 'Import Settings',
          passwordStrategy: 'Password Strategy',
          defaultPasswordOption: 'Set default password — students can log in immediately',
          defaultPassword: 'Default Password',
          passwordPlaceholder: 'Minimum 6 characters',
          passwordHint: 'Applied to all rows. If a row has its own password column, that takes priority.',
          step2: 'Upload CSV File',
          rowsFound: '{{count}} student row found',
          rowsFound_other: '{{count}} student rows found',
          replaceFile: 'Click or drop a new file to replace',
          dropHint: 'Drag & drop your CSV file here, or click to browse',
          csvOnly: 'Only .csv files are accepted',
          noTemplate: 'Don\'t have a template?',
          downloadHere: 'Download it here',
          step3: 'Preview & Import',
          showingFirst: 'Showing first 5 of {{total}} rows',
          startImport: 'Start Import',
          importing: 'Importing…',
          importFailed: 'Import failed. Please try again.',
          selectFileFirst: 'Please upload a CSV file first.',
          enterpriseSubtitle: 'Bulk CSV import is available only on the Enterprise license.',
          enterpriseFeature: 'Enterprise feature',
          enterpriseBody: 'Bulk student import from CSV is an Enterprise-only feature. Your current license does not include this capability.',
          enterpriseHint: 'To import students on your current plan, use the <1>{{addStudentLink}}</1> form to enroll them one at a time. Please contact your software provider to upgrade to an Enterprise license.',
          addStudentLink: 'Add Student',
          csvOnlyAlert: 'Please select a CSV file (.csv).\n\nFor Excel files, save as "CSV (Comma delimited)" first.',
          noDataRowsAlert: 'No data rows found in the file. Please check the file format.'
        },
        studentImport: {
          title: 'Student Import from Google Sheets',
          loadingSubtitle: 'Loading import configuration…',
          subtitle: 'Automatically imports new student entries from Google Sheets daily at 2:00 AM academy time',
          loadFailed: 'Failed to load import status',
          importStatus: 'Import Status',
          spreadsheetId: 'Spreadsheet ID',
          sheetName: 'Sheet Name',
          lastProcessedRow: 'Last Processed Row',
          totalImported: 'Total Imported',
          lastImportTime: 'Last Import Time',
          lastUpdated: 'Last Updated',
          lastError: 'Last Error',
          actions: 'Actions',
          triggerImport: 'Trigger Import Now',
          processing: 'Processing...',
          resetTracker: 'Reset Tracker',
          refreshStatus: 'Refresh Status',
          triggerDesc: 'Manually trigger the import process to check for new students immediately. This will only import rows after the last processed row.',
          resetDesc: 'Danger: This will reset the tracker to row 0. Use this only if you need to re-import all students or if there\'s an issue.',
          refreshDesc: 'Reload the current import status to see the latest information.',
          confirmTrigger: 'Are you sure you want to trigger student import now?',
          confirmReset: '⚠️ WARNING: This will reset the tracker and re-import ALL students from the beginning. Are you sure?',
          triggerSuccess: 'Import triggered successfully! Refresh to see results.',
          resetSuccess: 'Tracker reset successfully!',
          triggerFailed: 'Failed to trigger import',
          resetFailed: 'Failed to reset tracker',
          configTitle: 'Configuration',
          setupTitle: 'Setup Guide',
          configDaily: 'Automatic import runs daily at <strong>2:00 AM academy time</strong>',
          configNewRows: 'Only new rows (after last processed row) are imported',
          configRoleEmail: 'Each student is created with STUDENT role and invitation email sent',
          configUsername: 'Username is auto-generated from email or name',
          configMappings: 'Configure column mappings in <code>application.yml</code>',
          configEnv: 'Enable/disable via <code>STUDENT_IMPORT_ENABLED</code> environment variable',
          setupStep1: 'Create a Google Cloud Project and enable Google Sheets API',
          setupStep2: 'Create a Service Account and download credentials JSON',
          setupStep3: 'Share your Google Spreadsheet with the service account email',
          setupStep4: 'Place credentials file at <code>/app/config/google-credentials.json</code>',
          setupStep5: 'Set <code>STUDENT_IMPORT_ENABLED=true</code> in environment',
          setupStep6: 'Adjust column mappings in application.yml if your spreadsheet structure differs',
          bulkImportButton: 'Bulk Import',
          bulkImportTitle: 'Import new students from Google Sheets',
          bulkImporting: 'Importing…',
          bulkImportConfirm: 'Import students from Google Sheets?\n\nNew registrations will be pulled from the linked Google Forms.\nOnly rows added since the last import are processed.',
          bulkImportTriggered: 'Import triggered. Refresh the list in a moment to see new students.',
          bulkImportFailed: 'Failed to import students. Ensure student import is enabled on the server.'
        },
        feePlanForm: {
          title: 'Add Fee Plan',
          subtitle: 'Create a new plan for a specific branch.',
          branchLabel: 'Branch for Fee Plan',
          codeLabel: 'Fee Plan Code',
          typeLabel: 'Fee Plan Type',
          groupTypeLabel: 'Student Group Type',
          defaultRate: 'Default Fee Rate',
          defaultCredits: 'Default Session Credits',
          levelMin: 'Minimum Level',
          levelMax: 'Maximum Level',
          carryForward: 'Attendance Required',
          carryForwardEnabled: 'Enabled: No billing for ABSENT students.',
          carryForwardDisabled: 'Disabled: Billing applies whether the student is PRESENT or ABSENT.',
          creditExpiry: 'Credit Expiry (days)',
          notes: 'Fee Plan Notes',
          active: 'Fee Plan Active',
          selectBranchAlert: 'Select branch.',
          codeRequired: 'Code is required.',
          createFailed: 'Failed to create plan.',
          saving: 'Saving…',
          createPlan: 'Create Plan',
          planTypes: {
            PACKAGE: 'Package',
            MONTHLY: 'Monthly',
            PER_SESSION: 'Per session',
          },
          groupTypes: {
            GROUP: 'Group',
            ONE_TO_ONE: 'One to one',
          },
        },
        enrollmentDetail: {
          title: 'Enrollment #{{id}}',
          subtitle: 'View enrollment details and billing.',
          loadingTitle: 'Enrollment Details',
          notFound: 'Not found',
          loadFailed: 'Failed to load enrollment',
          activeExpired: 'Active (expired)',
          renew: 'Renew',
          activateConfirm: 'Are you sure you want to activate enrollment #{{id}}?',
          archiveFailed: 'Archive failed.',
          basicInfo: 'Basic Information',
          enrollmentPeriod: 'Enrollment Period',
          enrolledDate: 'Enrolled Date',
          openEnded: 'Open-ended',
          pricingBilling: 'Pricing & Billing',
          customCharges: 'Showing Custom Enrollment Charges',
          batchCharges: 'Showing Batch Default Charges',
          billingPeriod: 'Billing period',
          periodFee: 'Period fee',
          notConfigured: 'Not configured',
          hourlyRate: 'Hourly Rate',
          perSessionCharge: 'Per Session Charge',
          afterDiscount: 'After {{pct}}% discount',
          afterDiscountApplied: 'After {{value}} discount',
          enrollmentOverride: 'Enrollment Override',
          carryForward: 'Carry Forward',
          enabled: 'Enabled',
          disabled: 'Disabled',
          followsBatch: 'Follows Batch Policy',
          discountApplied: 'Discount Applied',
          noDiscount: 'No discount applied',
          paymentReceiver: 'Payment Receiver',
          receiverType: 'Receiver Type',
          receiverName: 'Receiver Name',
          additionalInfo: 'Additional Information',
          enrollmentType: 'Enrollment Type',
          enrolledBy: 'Enrolled By',
          createdAt: 'Created At'
        },
        discountForm: {
          createTitle: 'Create Discount',
          editTitle: 'Edit Discount',
          createSubtitle: 'Add a new discount code for enrollments.',
          editSubtitle: 'Update discount details and scope.',
          loadFailed: 'Failed to load discount',
          codeLabel: 'Discount Code',
          nameLabel: 'Discount Name',
          descriptionLabel: 'Description',
          typeLabel: 'Discount Type',
          valueLabel: 'Discount Value',
          branchLabel: 'Branch (optional)',
          levelLabel: 'Level (optional)',
          batchLabel: 'Batch (optional)',
          activeLabel: 'Active',
          allBranches: 'All branches',
          selectBranch: 'Select branch first',
          selectLevel: 'Select level first',
          saveFailed: 'Failed to save discount',
          saving: 'Saving…',
          create: 'Create Discount',
          update: 'Update Discount',
          codeRequired: 'Discount code is required',
          nameRequired: 'Name is required',
          valueRequired: 'Value is required',
          optionalDescription: 'Optional description',
          codePlaceholder: 'e.g., SIBLING_10, EARLY_BIRD',
          namePlaceholder: 'e.g., Sibling Discount, Early Bird Offer',
          percentValuePlaceholder: 'e.g., 10',
          flatValuePlaceholder: 'e.g., 500',
          percentOption: 'Percentage (%)',
          fixedOption: 'Fixed Amount ({{currency}})',
          scopeTitle: 'Discount Scope (Optional)',
          scopeHint: 'Leave all empty for a global discount, or restrict to a specific branch, level, or batch.',
          percentMaxAlert: 'Percentage discount cannot exceed 100%',
          updateSuccess: 'Discount updated successfully',
          createSuccess: 'Discount created successfully'
        },
        userDetails: {
          title: 'User Details',
          loadFailed: 'Failed to load user details.',
          notFound: 'User not found',
          editUser: 'Edit User',
          backToList: 'Back to list',
          personalInfo: 'Personal Information',
          contactInfo: 'Contact Information',
          accountInfo: 'Account Information',
          address: 'Address',
          noAddress: 'No address on file',
          dateOfBirth: 'Date of Birth',
          gender: 'Gender',
          memberSince: 'Member Since',
          lastLogin: 'Last Login',
          walletBalance: 'Wallet Balance',
          activeEnrollments: 'Active Enrollments',
          totalSessions: 'Total Sessions',
          attendanceRate: 'Attendance Rate',
          guardians: 'Guardians',
          noGuardians: 'No guardians linked',
          roles: 'Roles',
          username: 'Username',
          userId: 'User ID',
          coachSummary: 'Coach Summary'
        },
        coachStudents: {
          title: 'Students',
          subtitle: 'Students on your roster — from your sessions and admin assignments',
          searchPlaceholder: 'Search students by name, username, email, phone, or ID...',
          entityLabel: 'students',
          loadFailed: 'Failed to load students.',
          empty: 'No students found.',
          noMatch: 'No students match your search'
        },
        studentEnrollments: {
          title: 'My Enrollments',
          subtitle: 'View your active course enrollments',
          loadFailed: 'Failed to load enrollments.',
          searchPlaceholder: 'Search by level, batch, or date...',
          entityLabel: 'enrollments',
          noMatch: 'No enrollments match your search',
          empty: 'No enrollments yet',
          payOnline: 'Pay online'
        },
        studentSessions: {
          title: 'My Sessions',
          subtitle: 'View and search your scheduled sessions and attendance status.',
          loadFailed: 'Failed to load sessions.',
          searchPlaceholder: 'Search… (level, batch, coach, status, attendance)',
          empty: 'No sessions found.',
          noMatch: 'No sessions match your search.'
        },
        studentAttendance: {
          title: 'My Attendance',
          parentTitle: 'Attendance',
          subtitle: 'Track session attendance history across all batches',
          loadFailed: 'Failed to load attendance.',
          searchPlaceholder: 'Search by status, level, batch, coach, or time...',
          entityLabel: 'records',
          noMatch: 'No records match your search',
          empty: 'No attendance records found'
        },
        wallet: {
          title: 'My Wallet',
          childTitle: 'Child wallet',
          subtitle: 'View your balance, add funds, and review transaction history.',
          childSubtitle: 'View balance, top up online, and review your child\'s transaction history.',
          balanceLabel: 'Your Wallet Balance',
          balanceHint: 'Balance is used to pay for sessions automatically.',
          onlineUnavailableTitle: 'Online payments unavailable',
          onlineUnavailableDefault: 'Your academy\'s license does not include online payment gateways. Please contact the academy or pay via another method.',
          loadFailed: 'Failed to load wallet. Please refresh the page.',
          enrollmentPaying: 'Paying toward enrollment #{{id}}. Funds will be credited to your wallet and linked to this enrollment.',
          confirmingPayment: 'Confirming your payment…',
          addMoney: 'Add Money',
          addMoneyHint: 'Choose a payment method and complete checkout securely. Funds appear in your wallet after confirmation.',
          enterAmount: 'Enter amount',
          methodCash: 'Cash',
          securePayments: '🔒 Secure payments via {{gateway}}',
          gatewaysLoadFailed: 'Could not check online payment options. Refresh and try again.',
          topupUnavailable: 'Online top-up is not available at this time. Please contact your academy to add funds to your wallet.',
          transactionHistory: 'Transaction History',
          noTransactions: 'No transactions yet.',
          downloadReceipt: 'Download receipt',
          invalidAmount: 'Please enter a valid amount (minimum {{symbol}}1).',
          selectPaymentMethod: 'Please select a payment method.',
          invalidEnrollmentLink: 'Invalid enrollment link. Open Pay online from My Enrollments again.',
          paymentConfirmFailed: 'Payment confirmation failed. Please contact the academy.',
          paymentStartFailed: 'Could not start payment. Please try again.',
          paymentUpgradeRequired: 'Online payments require a license upgrade.',
          paymentVerifyFailed: 'Payment verification failed. Please contact the academy.',
          receiptDownloadFailed: 'Could not download receipt. Please try again.',
          razorpayTitle: 'Wallet Top-up',
          razorpayDesc: 'Add funds to your academy wallet',
          txnAdvanceTopup: 'Advance Top-up',
          txnOnlineTopup: 'Online Top-up',
          txnEnrollmentPayment: 'Enrollment payment',
          txnSessionCharge: 'Session Debit',
          txnSessionReversal: 'Session Reversal',
          txnDiscount: 'Discount Credit',
          txnRefund: 'Refund',
          txnApplied: 'Applied to Invoice',
          txnDefault: 'Transaction',
          payNow: 'Pay Now',
          redirecting: 'Redirecting…',
          ref: 'Ref'
        },
        sessionStatement: {
          eyebrow: 'Reports',
          title: 'Student Session Statement',
          subtitle: 'Full session history with attendance, fee charges, wallet credits and deductions, and running balance — for the selected student and date range.',
          embeddedSubtitle: 'Full session history with attendance, fee charges, wallet credits & deductions, and running balance — for the selected student and date range.',
          filters: 'Filters',
          selectStudent: 'Select Student',
          chooseStudent: 'Choose a student...',
          fromDate: 'From Date',
          toDate: 'To Date',
          generate: 'Generate Statement',
          generating: 'Loading...',
          selectStudentError: 'Please select a student',
          loadFailed: 'Failed to load session statement',
          exportPdfFailed: 'Failed to export PDF',
          student: 'Student',
          statementPeriod: 'Statement Period',
          openingBalance: 'Opening Balance',
          availableAtStart: 'Available at start',
          walletCredits: 'Wallet Credits',
          transactionsCount: '{{count}} transactions',
          sessionCharges: 'Session Charges',
          sessionsPresentAbsent: '{{present}}P / {{absent}}A sessions',
          closingBalance: 'Closing Balance',
          currentBalance: 'Current balance',
          walletTransactions: 'Wallet Transactions',
          walletTransactionsHint: 'Top-ups and credits added to your wallet',
          sessionHistory: 'Session History',
          sessionHistoryHint: 'Detailed view of all sessions attended during this period',
          noSessionsPeriod: 'No sessions found for this period',
          discount: 'Discount: {{amount}}',
          emailLabel: 'Enter recipient email',
          present: 'Present',
          absent: 'Absent'
        },
        notifications: {
          title: 'WhatsApp Notifications',
          subtitle: 'Choose which academy messages you receive on WhatsApp. Your phone number must be on your profile.',
          subtitleShort: 'Choose which academy messages you receive on WhatsApp.',
          loadFailed: 'Could not load notification preferences.',
          saveFailed: 'Save failed.',
          saved: 'Preferences saved.',
          optInTitle: 'Receive WhatsApp messages from my academy',
          optInHint: 'On by default. Turn off to stop all WhatsApp notifications.',
          sessionReminders: 'Session reminders',
          absenceAlerts: 'Absence alerts',
          lowBalance: 'Low wallet balance warnings',
          feeReminders: 'Fee / outstanding balance reminders',
          sessionCancelled: 'Session cancelled notices',
          classroomStarted: 'Live class started alerts',
          holidayAnnouncements: 'Holiday announcements',
          tryAgain: 'Try again',
          unavailable: 'Unavailable',
          savePreferences: 'Save preferences',
          saving: 'Saving…'
        },
        parentNotifications: {
          title: 'Child WhatsApp notifications',
          subtitle: 'Manage WhatsApp alerts sent to your child\'s phone number on their profile. These are the student\'s notification settings.',
          saved: 'Preferences saved for your child.',
          optInTitle: 'Enable WhatsApp for this student',
          optInHint: 'Alerts go to the phone number on your child\'s profile.'
        },
        profile: {
          title: 'My Profile',
          subtitle: 'Update your personal and address information',
          loadFailed: 'Failed to load profile',
          requestDataTitle: 'Request my data',
          requestDataBody: 'Download a machine-readable copy of your profile data, or ask academy administrators to prepare a fuller export (access / portability). Staff follow the academy DSAR process for erasure and extended records.',
          downloadData: 'Download my data (JSON)',
          downloadDataDone: 'Your data package was downloaded.',
          downloadDataFailed: 'Could not download your data package.',
          requestData: 'Request my data',
          requestSent: 'Request sent.',
          requestFailed: 'Could not send request. Try again later.',
          changePassword: 'Change Password',
          currentPassword: 'Current Password',
          newPassword: 'New Password',
          confirmPassword: 'Confirm New Password',
          passwordMinHint: 'Minimum 8 characters',
          profileUpdated: 'Profile updated',
          profileUpdateFailed: 'Failed to update profile',
          passwordChanged: 'Password changed successfully.',
          passwordChangeFailed: 'Failed to change password. Please try again.',
          enterCurrentPassword: 'Please enter your current password.',
          passwordMinLength: 'New password must be at least 8 characters.',
          passwordsMismatch: 'New passwords do not match.',
          passwordSameAsCurrent: 'New password must be different from current password.',
          firstNameRequired: 'First name is required and may include letters, spaces, hyphens, and apostrophes.',
          middleNameLetters: 'Middle name may include letters, spaces, hyphens, and apostrophes.',
          firstNameLetters: 'First name may include letters, spaces, hyphens, and apostrophes — not numbers.',
          lastNameLetters: 'Last name may include letters, spaces, hyphens, and apostrophes — not numbers.',
          lastNameRequired: 'Last name is required and may include letters, spaces, hyphens, and apostrophes.',
          invalidDob: 'Please enter a valid date of birth in the past.',
          invalidAddress: 'Invalid address.',
          invalidLatitude: 'Latitude must be a valid number.',
          invalidLongitude: 'Longitude must be a valid number.',
          saveChanges: 'Save Changes',
          saving: 'Saving…',
          sending: 'Sending…',
          updating: 'Updating…',
          updatePassword: 'Update Password',
          retry: 'Retry',
          preferredLanguage: 'Preferred language',
          preferredLanguageHint: 'Used for the app interface and for emails and notifications sent to you.',
          lang_en: 'English',
          lang_de: 'Deutsch',
          lang_fr: 'Français',
          lang_nl: 'Nederlands'
        },
        students: {
          title: 'Students',
          subtitle: 'Search, review, and manage student access with a cleaner list experience.',
          add: 'Add Student',
          bulkImport: 'Bulk Import',
          searchPlaceholder: 'Search students by name, username, email, phone, or ID...',
          entityLabel: 'students',
          exportFailed: 'Failed to export students. Please try again.',
          colStudentName: 'Student Name'
        },
        coaches: {
          title: 'Coaches',
          subtitle: 'Keep instructor records, invitations, and assignments organized in one modern workspace.',
          add: 'Add Coach',
          searchPlaceholder: 'Search coaches by name, username, email, phone, or ID...',
          entityLabel: 'coaches',
          colCoachName: 'Coach Name',
          manageStudents: 'Manage Students',
          manageStudentsTitle: 'Manage assigned students for individual sessions',
          manageAssignments: {
            title: 'Manage Student Assignments',
            coachLabel: 'Coach: {{name}}',
            infoBanner: 'Students assigned here become visible to this coach when recording individual sessions. Without an assignment, the coach won\'t see a student in their session dropdown — even if the student is active in the system.',
            assignedStudents: 'Assigned Students ({{count}})',
            availableStudents: 'Available Students ({{count}})',
            searchAssigned: 'Search assigned students...',
            searchAvailable: 'Search students...',
            noAssigned: 'No students assigned yet',
            noAssignedMatch: 'No assigned students match your search',
            noStudentsFound: 'No students found',
            allAssigned: 'All students are assigned',
            assignButton: 'Assign {{count}} Student(s)',
            assignButton_other: 'Assign {{count}} Students',
            assigning: 'Assigning...',
            loadFailed: 'Failed to load data',
            selectAtLeastOne: 'Please select at least one student',
            assignSuccess: 'Successfully assigned {{count}} student(s)',
            assignSuccess_other: 'Successfully assigned {{count}} students',
            assignFailed: 'Failed to assign students: {{message}}',
            removeConfirm: 'Remove this student assignment?',
            removeSuccess: 'Assignment removed successfully',
            removeFailed: 'Failed to remove assignment',
            assignedOn: 'Assigned: {{date}}'
          }
        },
        managers: {
          title: 'Managers',
          subtitle: 'Manage admin-side users with cleaner filters, better hierarchy, and consistent actions.',
          add: 'Add Manager',
          searchPlaceholder: 'Search managers by name, username, email, phone, or ID...',
          entityLabel: 'managers',
          colManagerName: 'Manager Name'
        },
        branches: {
          title: 'Branches',
          subtitle: 'Manage academy branches, archive older locations, and keep operations tidy.',
          add: 'Add Branch',
          searchPlaceholder: 'Search branches by name, code, phone, manager, or ID...',
          entityLabel: 'branches',
          loadFailed: 'Failed to load branches.',
          archiveConfirm: 'Archive this branch? This will deactivate it but keep its data.',
          archiveFailed: 'Failed to archive branch.',
          activateFailed: 'Failed to activate branch.',
          managerFallback: 'Manager',
          colBranchName: 'Branch Name',
          colCode: 'Code',
          colMode: 'Mode',
          colManagedBy: 'Managed by',
          editTitle: 'Edit branch',
          archiveTitle: 'Archive branch',
          activateTitle: 'Activate branch'
        },
        users: {
          title: 'Users',
          subtitle: 'Manage user accounts and permissions',
          add: 'Add User',
          entityLabel: 'users'
        },
        discounts: {
          title: 'Discounts Management',
          subtitle: 'Manage discount codes and offers',
          create: 'Create Discount',
          empty: 'No discounts found',
          emptyHint: 'Get started by creating a new discount code.',
          loadFailed: 'Failed to load discounts',
          archiveConfirm: 'Are you sure you want to archive this discount?',
          activateConfirm: 'Are you sure you want to activate this discount?',
          archiveSuccess: 'Discount archived successfully',
          activateSuccess: 'Discount activated successfully',
          archiveFailed: 'Failed to archive discount',
          activateFailed: 'Failed to activate discount',
          colDiscountName: 'Discount Name',
          colValue: 'Value',
          colScope: 'Scope',
          scopeBranch: 'Branch Specific',
          scopeLevel: 'Level Specific',
          scopeBatch: 'Batch Specific',
          scopeGlobal: 'Global',
          typePercent: 'Percentage',
          typeFixed: 'Fixed Amount',
          editTitle: 'Edit discount',
          archiveTitle: 'Archive discount',
          activateTitle: 'Activate discount'
        },
        atRisk: {
          eyebrow: 'Reports',
          title: 'At-Risk Students',
          subtitle: 'Students whose attendance falls below your threshold — reach out before they drop off.',
          daysLabel: 'Days to look back',
          daysSuffix: 'days',
          thresholdLabel: 'Minimum attendance (%)',
          thresholdSuffix: '% attendance',
          loadFailed: 'Failed to load at-risk students. Please try again.',
          daysError: 'Days must be between 1 and 365.',
          thresholdError: 'Threshold must be between 1 and 100.',
          riskCritical: 'Critical',
          riskHigh: 'High',
          riskMedium: 'Medium',
          tierCritical: '{{count}} critical',
          tierHigh: '{{count}} high',
          tierMedium: '{{count}} medium',
          emptyTitle: 'No students at risk',
          emptyDescription: 'All actively-enrolled students have attendance above {{threshold}}% in the last {{days}} days.',
          colStudent: 'Student',
          colRisk: 'Risk',
          colAttendanceRate: 'Attendance Rate',
          colSessions: 'Sessions',
          colLastSeen: 'Last Seen',
          colBatches: 'Batches',
          profile: 'Profile',
          emailAction: 'Email',
          footer: 'Showing {{count}} student(s) with <{{threshold}}% attendance in the last {{days}} days. Students with no sessions in the window are excluded.',
          studentFallback: 'Student #{{id}}',
          coachFallback: 'Coach #{{id}}'
        },
        auditLogs: {
          title: 'Audit Logs',
          subtitle: 'View and track system activities',
          filterUser: 'User',
          filterTable: 'Table',
          filterOperation: 'Operation',
          filterStartDate: 'Start Date',
          filterEndDate: 'End Date',
          filterSearch: 'Search',
          allUsers: 'All Users',
          allTables: 'All Tables',
          allOperations: 'All Operations',
          searchPlaceholder: 'Search in old/new data...',
          clearFilters: 'Clear Filters',
          exportCsv: 'Export to CSV',
          fetchFailed: 'Failed to fetch audit logs',
          exportFailed: 'Failed to export audit logs',
          empty: 'No audit logs found',
          colTable: 'Table',
          colOperation: 'Operation',
          colRecordId: 'Record ID',
          colTimestamp: 'Timestamp',
          oldData: 'Old Data:',
          newData: 'New Data:',

          tables: {
            users: 'Users',
            sessions: 'Sessions',
            session: 'Session',
            batches: 'Batches',
            batch: 'Batch',
            branches: 'Branches',
            address: 'Address',
            levels: 'Levels',
            enrollment: 'Enrollment',
            attendance: 'Attendance',
            fee_plan: 'Fee plan',
            discount_master: 'Discount',
            wallet_txn: 'Wallet transaction',
            billing_settings: 'Billing settings',
            coach_student_assignment: 'Coach–student assignment',
            session_participants: 'Session participants',
            password_reset_tokens: 'Password reset tokens',
            academy_holiday: 'Academy holiday',
            email_queue: 'Email queue',
            whatsapp_queue: 'WhatsApp queue',
            auth: 'Auth'
          },
          ops: {
            INSERT: 'Insert',
            UPDATE: 'Update',
            DELETE: 'Delete',
            LOGIN: 'Login',
            LOGOUT: 'Logout',
            PASSWORD_SET: 'Password set',
            PASSWORD_RESET: 'Password reset',
            BULK_UPDATE: 'Bulk update',
            ARCHIVE: 'Archive',
            ACTIVATE: 'Activate',
            DEACTIVATE: 'Deactivate'
          },
          showingEntries: 'Showing {{start}} to {{end}} of {{total}} entries'
        },
        walletTopup: {
          eyebrow: 'Fees & Billing',
          title: 'Wallet Top-up',
          subtitle: 'Add funds to a student\'s wallet. These funds can be used for session charges, with automatic deduction after each session.',
          walletBadge: 'Wallet',
          selectStudent: 'Select Student',
          searchPlaceholder: 'Search student by name...',
          noStudents: 'No students found',
          studentsLoadFailed: 'Could not load students. Refresh and try again.',
          staffLoadFailed: 'Could not load staff list. Refresh and try again.',
          change: 'Change',
          studentId: 'Student ID: {{id}}',
          idLabel: 'ID: {{id}}',
          archivedWarning: 'This student is not active in the system right now. Wallet top-ups can still be recorded for their account.',
          archivedAlertTitle: 'Archived Student',
          archivedAlertMessage: '{{name}} is not active in the system right now.\n\nThis student has been archived. You can still record a wallet top-up, but they cannot sign in or use regular student features until an admin reactivates their account.',
          addFunds: 'Add Funds',
          enterPaymentDetails: 'Enter payment details below',
          amount: 'Amount',
          paymentMode: 'Payment Mode',
          paidOn: 'Paid On',
          paidOnHint: 'Today or an earlier date only',
          reference: 'Reference / Transaction ID',
          referenceOptional: '(Optional)',
          paymentReceivedBy: 'Payment Received By',
          staffMember: 'Staff Member',
          otherPerson: 'Other Person',
          selectStaff: 'Select Staff',
          chooseStaff: '-- Choose staff member --',
          receiverName: 'Receiver Name',
          receiverNamePlaceholder: 'Enter receiver\'s full name',
          clearForm: 'Clear Form',
          recordPayment: 'Record Payment',
          processing: 'Processing...',
          chooseStudent: 'Choose a student',
          amountRequired: 'Amount must be greater than 0',
          paidOnRequired: 'Choose the paid on date',
          paidOnFuture: 'Paid on date cannot be in the future',
          paidOnInvalid: 'Enter a valid paid on date',
          receiverTypeInvalid: 'Select a valid Payment Receiver Type (USER/OTHERS).',
          staffRequired: 'Select the staff member who received the payment.',
          receiverNameRequired: 'Enter the name of the receiver.',
          success: '✅ Wallet topup successful! {{amount}} credited to student wallet.',
          failed: 'Failed to record wallet topup.'
        }
      },
      cookie: {
        body: 'This app uses essential cookies only — to keep you logged in and remember your session. No tracking, no advertising.',
        gotIt: 'Got it',
        privacy: 'Privacy Policy',
        terms: 'Terms',
        and: 'and',
        ariaLabel: 'Cookie consent'
      },
      faq: {
        title: 'Frequently Asked Questions',
        subtitle: '{{count}} questions covering everything you can do as a {{role}}.',
        findAnswers: 'Help',
        searchHeading: 'Find answers',
        searchHint: 'Search by keyword or browse by topic below.',
        searchPlaceholder: 'Search questions…',
        allSections: 'All',
        noResults: 'No results found',
        tryDifferent: 'Try a different keyword or clear the search',
        noQuestionsMatch: 'No questions match your search.',
        resultsFor: '{{count}} result for "{{query}}"',
        resultsFor_other: '{{count}} results for "{{query}}"',
        footerPrompt: 'Can\'t find what you\'re looking for?',
        footerAction: 'Contact your academy admin for assistance.',
        roles: {
          ADMIN: 'Admin',
          MANAGER: 'Manager',
          COACH: 'Coach',
          STUDENT: 'Student',
          PARENT: 'Parent'
        }
      },
      settings: {
        email: {
          title: 'Email Settings',
          loadingSubtitle: 'Loading SMTP configuration…',
          subtitle: 'Configure the SMTP account your academy uses to send sign-in links, reminders, statements, and other system emails.',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          stepThree: 'Step 3',
          smtpSection: 'SMTP connection',
          smtpSectionDesc: 'Server credentials used to send outbound system email from your academy.',
          fromSection: 'Sender identity',
          fromSectionDesc: 'How recipients see the From and Reply-To addresses on academy emails.',
          testSection: 'Verify delivery',
          testSectionDesc: 'Send a test message to confirm credentials and outbound delivery before going live.',
          statusLabel: 'Status',
          modeEnabled: 'Sending on',
          modeConfigured: 'Configured',
          modeIncomplete: 'Incomplete',
          saveSettings: 'Save settings',
          saveBeforeTest: 'Save SMTP settings before sending a test email.',
          testCompleted: 'Test completed.',
          testFailed: 'Test email failed.',
          useStarttlsDesc: 'Encrypt the SMTP session after connect. Recommended for port 587.',
          lastTestPassedHint: 'For Gmail, we confirm the message appears in the sender Sent folder. Recipients should still check spam/promotions.',
          lastTestFailedHint: 'Check credentials and try again.',
          infoBanner: 'This is your <strong>outbound sending</strong> configuration. Public support contact shown to students is managed under <1>Branding</1>. Notification toggles are under <2>Notifications</2>.',
          enableTitle: 'Enable outbound email',
          enableHint: 'Requires valid SMTP credentials below.',
          smtpHost: 'SMTP host',
          smtpPort: 'SMTP port',
          smtpUsername: 'SMTP username',
          smtpPassword: 'SMTP password / app password',
          passwordConfigured: '(already set — leave blank to keep)',
          passwordRequired: 'Required',
          fromEmail: 'From email address',
          fromName: 'From display name',
          fromNamePlaceholder: 'Defaults to academy name',
          replyTo: 'Reply-to email',
          replyToPlaceholder: 'Defaults to support email from Branding',
          useStarttls: 'Use STARTTLS (recommended for port 587)',
          enableWarning: 'Enter SMTP host, from email, and password before enabling outbound email.',
          testRecipient: 'Send test email to',
          sendTest: 'Send test',
          verifying: 'Verifying… (up to 45s)',
          lastTestPassed: 'Last test passed',
          lastTestFailed: 'Last test failed',
          loadFailed: 'Failed to load email settings.',
          saveSuccess: 'Email settings saved.',
          saveFailed: 'Failed to save email settings.'
        },
        whatsapp: {
          title: 'WhatsApp (WATI)',
          loadingSubtitle: 'Loading settings…',
          subtitle: 'Connect your academy WATI account to send session reminders, wallet alerts, and other operational messages on WhatsApp. Message costs are billed by WATI/Meta to your academy.',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          stepThree: 'Step 3',
          connectionSection: 'Connection',
          connectionSectionDesc: 'License status, master switch, and WATI credentials for outbound WhatsApp.',
          templatesSection: 'Message templates',
          templatesSectionDesc: 'Exact template names as approved in your WATI account.',
          localeTemplateNote: 'For regional locales de/fr/nl, the app sends "{{example}}" (base name + _{{lang}}). Register matching language templates in WATI, or set the base field already including the suffix.',
          recentLogDesc: 'Latest outbound WhatsApp attempts for this academy.',
          statusLabel: 'Status',
          modeUnlicensed: 'Not licensed',
          modeEnabled: 'Sending on',
          modeOff: 'Off',
          saveSettings: 'Save settings',
          notLicensed: 'WhatsApp is not included in your current ChessDesk license. Contact Axeron Technologies to upgrade (Professional or Enterprise, or enable <strong>WhatsApp Enabled</strong> on your license sheet row).',
          enableTitle: 'Enable WhatsApp',
          enableHint: 'Academy master switch (requires license + WATI credentials or mock mode).',
          mockMode: 'Mock mode',
          mockModeDesc: 'Log messages locally without calling WATI (recommended for dev/testing)',
          apiBaseUrl: 'WATI API base URL',
          apiBaseUrlNote: 'From WATI dashboard → API Docs',
          channelNumber: 'Business channel number',
          channelNumberNote: 'Your WATI WhatsApp number (required for live sends).',
          bearerToken: 'WATI Bearer token',
          tokenPlaceholder: 'Paste bearer token from WATI API Docs',
          templatesTitle: 'Approved template names in WATI',
          templateSessionReminder: 'Session reminder (students)',
          templateAbsenceAlert: 'Absence alert (students)',
          templateLowBalance: 'Low wallet (students)',
          templateFeeReminder: 'Fee / outstanding (students)',
          templatePendingSessions: 'Pending sessions (coaches)',
          templateLowBalanceAdmin: 'Low wallet alert (admins)',
          templateSessionCancelled: 'Session cancelled (students)',
          templateHolidayAnnouncement: 'Holiday announcement (all staff & students)',
          templateCorrespondenceDeadline: 'Correspondence deadline (Playing Platform)',
          templateCorrespondenceYourMove: 'Correspondence your-move (Playing Platform)',
          enableWarning: 'Enter WATI credentials and business channel number, or turn on mock mode before enabling.',
          recentLog: 'Recent message log',
          noMessages: 'No WhatsApp messages queued yet.',
          when: 'When',
          phone: 'Phone',
          type: 'Type',
          status: 'Status',
          msgStatus: {
            SENT: 'Sent',
            FAILED: 'Failed',
            PENDING: 'Pending',
            PROCESSING: 'Processing',
            QUEUED: 'Queued',
          },
          queueStatus: {
            SENT: 'Sent',
            FAILED: 'Failed',
            PENDING: 'Pending',
            PROCESSING: 'Processing',
            QUEUED: 'Queued',
          },
          unavailable: {
            notAvailableTitle: 'WhatsApp notifications are not available',
            notAvailableBody: 'Your academy has not enabled WhatsApp messaging on ChessDesk. {{phoneHint}} Contact your academy office if you need session or balance updates.',
            notSetupTitle: 'WhatsApp is not set up yet',
            notSetupBody: 'Your academy has not turned on WhatsApp notifications yet. Once they connect WhatsApp, you can choose which alerts to receive here. {{phoneHint}}',
            phoneHintParent: 'When WhatsApp is available, alerts go to the phone number on your child\'s profile.',
            phoneHintStudent: 'When WhatsApp is available, alerts go to the phone number on your profile.'
          },
          loadFailed: 'Failed to load WhatsApp settings.',
          saveSuccess: 'WhatsApp settings saved.',
          saveFailed: 'Failed to save settings.',
          testTitle: 'Send test message',
          testDesc: 'Resolves the session-reminder template for your academy locale (including _de/_fr/_nl) and sends via WATI or mock mode.',
          testPhone: 'Recipient phone (E.164)',
          sendTest: 'Send test',
          testing: 'Testing…',
          testFailed: 'WhatsApp test failed.'
        },
        chessCoach: {
          title: 'Ask AI Coach',
          loadingSubtitle: 'Loading Ask AI Coach settings…',
          subtitle: 'Enable the student-facing Ask AI Coach chatbot and connect an OpenAI-compatible API key (OpenAI, Groq, OpenRouter, etc.).',
          infoBanner: 'When enabled and configured, Ask AI Coach appears for students, parents, and staff across Playing. Engine analysis stays on your server; the API key is only used for natural-language explanations.',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          connectionSection: 'Enable & connection',
          connectionSectionDesc: 'Master switch and credentials for the language model that powers Ask AI Coach replies.',
          limitsSection: 'Limits & fair-play',
          limitsSectionDesc: 'Rate limits, reply length, and live rated-game coaching policy.',
          statusLabel: 'Status',
          modeOperational: 'Live',
          modeEnabledIncomplete: 'On — needs API key',
          modeConfigured: 'Configured (off)',
          modeIncomplete: 'Not set up',
          enableTitle: 'Enable Ask AI Coach',
          enableHint: 'Shows the coach chat for academy users when an API key is saved.',
          enableWarning: 'Paste an API key before enabling Ask AI Coach.',
          baseUrl: 'API base URL',
          baseUrlHint: 'OpenAI-compatible chat completions base (no trailing slash). Groq example: https://api.groq.com/openai/v1',
          model: 'Model',
          modelHint: 'Provider model id, e.g. gpt-4o-mini or llama-3.3-70b-versatile',
          apiKey: 'API key',
          apiKeyPlaceholder: 'Paste provider API key',
          apiKeyConfigured: '(already set — leave blank to keep)',
          apiKeyHint: 'Stored encrypted in the academy database. Never shown again after save.',
          maxTokens: 'Max reply tokens',
          rateLimit: 'Questions per user / minute',
          maxHistory: 'History turns sent to model',
          timeoutMs: 'Request timeout (ms)',
          liveRatedIdeasOnly: 'Ideas-only in live rated games',
          liveRatedIdeasOnlyDesc: 'In active rated human games, discuss plans only — never suggest a concrete best move.',
          saveSettings: 'Save settings',
          loadFailed: 'Failed to load Ask AI Coach settings.',
          saveSuccess: 'Ask AI Coach settings saved.',
          saveFailed: 'Failed to save Ask AI Coach settings.'
        },
        production: {
          title: 'EU production readiness',
          loadingSubtitle: 'Checking go-live requirements…',
          subtitle: 'Operational checklist for HTTPS webhooks, SMTP, WhatsApp locale templates, academy timezone, and counsel acknowledgment of EU legal drafts.',
          loadFailed: 'Failed to load production readiness.',
          readyBanner: 'No in-app blockers — complete live Mollie/Stripe keys and Meta template registration outside ChessDesk.',
          notReadyBanner: 'Resolve the blockers below before EU go-live.',
          readyHint: 'This screen cannot create merchant accounts or lawyer signatures; it tracks what ChessDesk can verify.',
          checklistTitle: 'Checklist',
          open: 'Open',
          checkHttps: 'Public HTTPS academy URL (FRONTEND_BASE_URL)',
          checkMollieWebhook: 'Mollie webhook URL configured',
          checkSmtp: 'SMTP enabled and operational',
          smtpLastTestFailed: 'Last SMTP test failed',
          checkWhatsapp: 'WhatsApp (if licensed)',
          whatsappSuffix: 'Locale suffix {{suffix}} — register matching Meta templates',
          whatsappEnglish: 'English base template names',
          checkLegal: 'Counsel review acknowledged',
          checkTimezone: 'Academy timezone (Regional Settings)',
          timezoneDetail: '{{academy}}',
          webhooksTitle: 'Webhook URLs to register',
          webhooksDesc: 'Copy these into Stripe, PayPal, Mollie, and Razorpay dashboards.',
          templatesTitle: 'Resolved WhatsApp template names',
          templatesDesc: 'Register these exact names in Meta/WATI for the academy locale.',
          legalTitle: 'EU legal pack & counsel acknowledgment',
          legalDesc: 'Drafts are counsel-ready, not lawyer-final. Acknowledge only after a lawyer admitted in the relevant jurisdiction has reviewed PRIVACY-POLICY-EU and DPA-EU-ANNEX.',
          docPrivacyEu: 'Privacy (EU)',
          docDpaEu: 'DPA annex (EU)',
          docPrivacy: 'Privacy',
          docDpa: 'DPA',
          docTerms: 'Terms',
          docEula: 'EULA',
          docLoading: 'Loading…',
          docLoadFailed: 'Could not load document.',
          confirmLabel: 'I confirm counsel has reviewed the current EU legal pack for this academy, and we will keep signed copies on file.',
          confirmRequired: 'Tick the confirmation checkbox first.',
          notesLabel: 'Notes (optional)',
          notesPlaceholder: 'e.g. firm name, review date',
          ackButton: 'Record counsel acknowledgment',
          ackSuccess: 'Counsel acknowledgment recorded.',
          ackFailed: 'Could not save acknowledgment.',
          alreadyAcked: 'Acknowledged by {{by}} at {{at}} (version {{version}}).'
        },
        notifications: {
          title: 'Notification Preferences',
          loadingSubtitle: 'Loading academy notification defaults…',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          stepThree: 'Step 3',
          stepFour: 'Step 4',
          saveSettings: 'Save settings',
          subtitle: 'Academy-wide defaults for email, WhatsApp (WATI), and in-app alerts. Changes apply to automated messages sent by the system. For European clubs, sample DE/FR wording is in docs/notification-template-packs-DE-FR.md (paste into SMTP/WATI templates).',
          savePreferences: 'Save preferences',
          saveSuccess: 'Preferences saved successfully.',
          saveFailed: 'Could not save preferences. Please try again.',
          emailPanel: 'Email',
          emailDesc: 'SMTP messages to students, coaches, and staff when events occur.',
          emailFooterOn: 'Turn off the master switch to pause all outbound emails without changing individual options.',
          emailFooterOff: 'Configure outbound SMTP under System → Email Settings before enabling email notifications.',
          emailNotConfigured: 'Outbound email is not configured yet. Go to <strong>System → Email Settings</strong> first.',
          whatsappPanel: 'WhatsApp',
          whatsappNotLicensed: 'WhatsApp is not included in your license. Enable it on your license sheet or upgrade your plan.',
          whatsappDesc: 'Uses WATI credentials from System → WhatsApp. Students can opt out per message type in their portal.',
          notLicensedBadge: 'Not licensed',
          whatsappFooter: 'Configure API keys under System → WhatsApp.',
          whatsappMaster: 'WhatsApp notifications (master)',
          studentsSection: 'Students',
          staffSection: 'Staff & academy',
          inappPanel: 'In-app',
          inappDesc: 'Bell notifications inside the dashboard for admins, managers, and coaches.',
          walletPanel: 'Wallet alerts',
          walletDesc: 'Used for email and WhatsApp low-balance warnings when those channels are enabled.',
          lowWalletThreshold: 'Low wallet balance threshold',
          lowWalletHelper: 'Notify when a student wallet falls below this amount.',
          walletTip: '<strong>Tip:</strong> Student-specific opt-outs live under each student\'s notification settings. This page controls what the academy is allowed to send by default.',
          emailMaster: 'Email notifications (master)',
          emailFeeReminders: 'Fee reminder emails',
          emailSessionRemindersStudent: 'Student session reminders',
          emailSessionRemindersCoach: 'Coach pending-session reminders',
          emailAbsenceAlerts: 'Absence alert emails',
          emailSessionCancelled: 'Session cancelled emails',
          emailOnlineTopupAdmin: 'Online payment received (admin alert)',
          inappMaster: 'In-app notifications (master)',
          inappFeeAlerts: 'In-app fee alerts',
          inappPendingSessions: 'In-app pending session alerts',
          inappAtRiskStudents: 'In-app at-risk student alerts',
          waSessionReminders: 'Session reminders',
          waAbsenceAlerts: 'Absence alerts',
          waLowBalance: 'Low wallet balance warnings',
          waFeeReminders: 'Fee / outstanding reminders',
          waSessionCancelled: 'Session cancelled notices',
          waPendingSessions: 'Pending sessions (coaches)',
          waLowBalanceAdmin: 'Low wallet alerts (admins)',
          waHolidayAnnouncements: 'Holiday announcements',
          waCorrespondenceDeadline: 'Correspondence deadline (Playing Platform)',
          waCorrespondenceYourMove: 'Correspondence your-move (Playing Platform)'
        },
        billing: {
          title: 'Payment enforcement',
          subtitle: 'Control attendance and fee collection behaviour',
          loadFailed: 'Failed to load payment enforcement settings.',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          stepThree: 'Step 3',
          modeLabel: 'Current mode',
          modeStrict: 'Strict — pay first',
          modeGrace: 'Grace allowed',
          modeFlexible: 'Flexible',
          attendanceSection: 'Attendance & unpaid fees',
          attendanceSectionDesc: 'Decide whether students can attend when period fees are unpaid, and how long grace lasts.',
          requirePayment: 'Require payment before attendance',
          requirePaymentDesc: 'Staff cannot enroll unpaid; wallet must be charged at enrollment.',
          allowUnpaidPeriod: 'Allow attendance while period fee is unpaid',
          allowUnpaidPeriodDesc: 'Students may attend after a period charge fails, until grace limits are hit.',
          graceSection: 'Grace limits',
          graceSectionDesc: 'Leave blank for no cap. The first limit reached blocks further attendance.',
          graceCalendarDays: 'Grace calendar days (optional)',
          graceSessions: 'Grace sessions (optional)',
          graceOptionalNote: 'Blank = no limit for this rule',
          walletSection: 'Wallet & collection',
          walletSectionDesc: 'How session/period charges behave when balance is low, and how long auto-collection retries.',
          allowNegativeWallet: 'Allow wallet to go negative when charging',
          allowNegativeWalletDesc: 'Charges still post when balance is zero — the student carries a debt.',
          collectionWindow: 'Collection retry window (calendar days)',
          collectionWindowDesc: 'How many days the system keeps retrying unpaid period fees (1–14).',
          remindersSection: 'Reminders',
          remindersSectionDesc: 'Automated notices when enrollments are about to end.',
          expiryReminders: 'Send enrollment expiry reminders',
          expiryRemindersDesc: 'Notify students (and guardians when linked) before enrollment end dates.',
          saveSettings: 'Save settings',
          saveSuccess: 'Settings saved.',
          saveFailed: 'Failed to save settings.'
        },
        regional: {
          title: 'Regional Settings',
          subtitle: 'Configure locale, currency, and how dates and times appear across the app, emails, and PDFs. Payment gateways are managed under System → Payment Methods.',
          quickPreset: 'Quick Preset',
          custom: 'Custom',
          sampleAmount: 'Sample amount:',
          sampleDate: 'Sample date:',
          sampleTime: 'Sample time:',
          timezoneLabel: 'Timezone:',
          cardLocale: 'Locale & Timezone',
          cardCountry: 'Country & Phone',
          cardCurrency: 'Currency',
          cardTax: 'Tax (display only)',
          cardDateTime: 'Date and Time',
          timezone: 'Timezone',
          timezoneNote: 'Used for PDF export timestamps and regional defaults.',
          localeTag: 'Locale Tag (BCP 47)',
          localeTagNote: 'Controls number grouping and date order in the web app.',
          countryCode: 'Country Code (ISO)',
          countryName: 'Country Name',
          phoneDialCode: 'Phone Dial Code',
          phoneFormatHint: 'Phone Format Hint',
          countryNote: 'Changing country updates which payment methods are available (UPI for India only, etc.).',
          currencyNote: 'All wallet amounts and session charges use this currency. See docs/CURRENCY-DESIGN.md.',
          currencyCode: 'Currency Code (ISO 4217)',
          currencySymbol: 'Currency Symbol',
          decimalPlaces: 'Decimal Places',
          symbolPosition: 'Symbol Position',
          symbolBefore: 'Before amount ({{example}})',
          symbolAfter: 'After amount ({{example}})',
          taxNote: 'ChessDesk shows tax labels on regional settings and receipts but does not generate statutory tax invoices.',
          taxLabel: 'Tax Label',
          taxRate: 'Tax Rate (%)',
          taxId: 'Tax ID',
          taxIdLabel: 'Tax ID Field Label',
          taxIncluded: 'Tax included in displayed prices',
          taxIncludedYes: 'Yes (inclusive)',
          taxIncludedNo: 'No (exclusive)',
          timeFormat: 'Time Format (web app)',
          timeFormatNote: 'Sessions, attendance, calendar, and time pickers.',
          time24: '24-hour (14:30)',
          time12: '12-hour (02:30 PM)',
          datetimeFormat: 'Date & Time Format (emails & PDFs)',
          datetimeFormatNote: 'Java pattern for reports, reminders, and receipts. Updates when you change time format.',
          saveSettings: 'Save Settings',
          saving: 'Saving...',
          loadFailed: 'Failed to load regional settings.',
          saveSuccess: 'Regional settings saved successfully.',
          saveFailed: 'Failed to save regional settings.',
          placeholders: {
            localeTag: 'en-IN',
            countryCode: 'IN',
            countryName: 'India',
            phoneDialCode: '+91',
            phoneFormatHint: '+91 XXXXX XXXXX',
            currencyCode: '',
            currencySymbol: '',
            taxLabel: 'GST',
            taxIdLabel: 'GSTIN',
            taxRegistration: 'Your academy {{label}}'
          }
        },
        branding: {
          title: 'Custom Branding',
          subtitle: 'Set your academy\'s name, tagline and logo. Changes take effect immediately — no rebuild needed.',
          loadingSubtitle: 'Loading branding settings…',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          stepThree: 'Step 3',
          stepFour: 'Step 4',
          sectionIdentityDesc: 'Shown in the header, login screen and footer. Leave blank to use build-time defaults.',
          sectionLogoDesc: 'Upload a square logo for headers, login screens, and PDF exports.',
          appNameNote: 'Shown in the header, login screen and footer. Leave blank to use the build-time default ({{default}}).',
          taglineNote: 'Short line displayed below the app name in the header. Leave blank to use the build-time default.',
          saveSettings: 'Save settings',
          saveSuccess: 'Branding settings saved successfully.',
          saveFailed: 'Failed to save branding settings.',
          enterpriseSubtitle: 'Runtime logo and academy-name changes are available only on the Enterprise license.',
          enterpriseFeature: 'Enterprise feature',
          enterpriseNote: 'Current non-Enterprise licenses continue using the default build-time branding from the deployment configuration.',
          loadFailed: 'Failed to load current branding settings',
          appNameTagline: 'App Name & Tagline',
          appName: 'App / Academy Name',
          tagline: 'Tagline',
          saveText: 'Save Text',
          logoImage: 'Logo Image',
          clickChooseLogo: 'Click to choose a logo',
          logoHint: 'PNG, JPG, SVG, WebP · max 2 MB · recommended 256 × 256 px',
          removeLogo: 'Remove Custom Logo',
          removing: 'Removing…',
          uploadLogo: 'Upload Logo',
          uploading: 'Uploading…',
          logoActive: 'A custom logo is currently active.',
          logoDefault: 'No custom logo set — the default logo from the build is being used.',
          academyContact: 'Academy Contact',
          contactNote: 'Shown on the login footer and help page for students, parents, and staff.',
          supportEmail: 'Support Email',
          supportPhone: 'Support Phone',
          saveContact: 'Save Contact',
          livePreview: 'Live Preview',
          previewNote: 'This is how the header will appear to users.',
          loginPreview: 'Login Screen Preview',
          instantNote: '⚡ Instant & persistent',
          instantDesc: 'Changes are stored in the database and picked up by all users on their next page load — no restart or rebuild required.',
          removeLogoConfirm: 'Remove the custom logo? The system default will be used.',
          logoUploadSuccess: 'Logo uploaded successfully.',
          logoUploadFailed: 'Failed to upload logo',
          logoRemoved: 'Custom logo removed. Default logo is now active.',
          logoRemoveFailed: 'Failed to remove logo',
          contactUpdated: 'Academy contact details updated.',
          contactSaveFailed: 'Failed to save contact details',
          textUpdated: 'Branding text updated successfully.',
          textSaveFailed: 'Failed to save branding text',
          validImageFile: 'Please select a valid image file (PNG, JPG, SVG, etc.)',
          logoTooLargeAlert: 'Logo must be smaller than 2 MB'
        },
        license: {
          title: 'License',
          subtitle: 'Your plan capacity, current usage, and a reference of all supported ChessDesk license tiers. Limits are set by your software provider (license key and Google Sheet). Contact support to upgrade.',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          stepThree: 'Step 3',
          stepFour: 'Step 4',
          yourLicense: 'Your license',
          yourLicenseDesc: 'Edition, expiry, and key issued by your software provider.',
          standardPlan: '{{edition}} · standard caps {{caps}}',
          loadFailed: 'Failed to load license information.',
          edition: 'Edition',
          expiry: 'Expiry',
          licenseKey: 'License key',
          notSpecified: 'Not specified',
          unlimited: 'Unlimited',
          daysRemaining: '{{count}} day remaining',
          daysRemaining_other: '{{count}} days remaining',
          slotsRemaining: '{{count}} slot remaining',
          slotsRemaining_other: '{{count}} slots remaining',
          atLimit: 'At limit',
          nearLimit: 'Near limit',
          capacityTitle: 'Your capacity (all students, coaches & branches)',
          capacityNote: 'Students and coaches count while active or archived. When you are at your plan limit, archived students/coaches can be <strong>removed permanently</strong> to free a slot (history is kept and labeled Inactive). Branches always count.',
          students: 'Students',
          branches: 'Branches',
          coaches: 'Coaches',
          featuresTitle: 'Features on your plan',
          featureCore: 'Wallet billing, attendance, sessions, and reports',
          featureBranding: 'Custom logo and academy name:',
          featureWhatsapp: 'WhatsApp (WATI) integration:',
          featureBulkImport: 'Bulk student CSV import:',
          plansTitle: 'Supported plans & features',
          plansDesc: 'Reference for all ChessDesk license tiers. Your active limits above may differ if your provider set custom caps on the license sheet.',
          yourPlan: 'Your plan',
          capacityLimits: 'Capacity limits (standard)',
          tierCaps: 'up to {{students}} students, {{branches}} branches, {{coaches}} coaches',
          plans: { starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' },
          featureGroups: { core: 'Core platform', multiBranch: 'Multi-branch & payments', enterprise: 'Enterprise' },
          features: {
            attendance: 'Attendance, sessions & session calendar',
            billing: 'Billing, invoices & student wallet',
            reports: 'Reports & Excel export',
            portal: 'Student & parent portal',
            email: 'Email notifications',
            atRisk: 'At-risk student detection',
            multiBranch: 'Multi-branch management',
            managerRole: 'Branch-level manager role',
            crossBranch: 'Cross-branch reporting',
            onlinePayments: 'Online payment gateways (Razorpay, Stripe, PayPal)',
            whatsapp: 'WhatsApp notifications (WATI)',
            bulkImport: 'Bulk student CSV import',
            customBranding: 'Custom logo & academy name in the app'
          },
          activeStudents: 'Active students',
          included: 'Included',
          enterpriseOnly: 'Enterprise only',
          proEnterprise: 'Professional / Enterprise (or sheet flag)'
        },
        payment: {
          title: 'Online Payment Gateways',
          subtitle: 'Enable one or more gateways for student wallet top-ups. Each academy uses its own merchant credentials.',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          stepThree: 'Step 3',
          gatewaysSection: 'Online payment gateways',
          gatewaysSectionDesc: 'Configure merchant credentials for student wallet top-ups. Only gateways available for your academy country are listed.',
          noGatewaysForCountry: 'No online gateways are available for the current academy country.',
          saveSettings: 'Save settings',
          enabled: 'Enabled',
          loadFailed: 'Failed to load payment settings.',
          saveSuccess: 'Payment gateway settings saved.',
          saveFailed: 'Failed to save settings.',
          manualMethods: 'Manual payment methods',
          manualMethodsDesc: 'Used when staff record wallet top-ups. Methods not used in your country are hidden automatically.',
          recentActivity: 'Recent payment activity',
          recentActivityDesc: 'Last 50 gateway events — useful when debugging webhooks or failed top-ups.',
          noEvents: 'No gateway events yet.',
          when: 'When',
          gateway: 'Gateway',
          event: 'Event',
          status: 'Status',
          student: 'Student',
          detail: 'Detail',
          deploymentNote: 'Deployment note',
          licenseRequired: 'Online payment gateways require a <strong>Professional</strong> or <strong>Enterprise</strong> license{{edition}}. You can review settings here, but students cannot pay online until you upgrade.',
          licenseEditionSuffix: ' (current: {{edition}})',
          httpsRequired: 'Stripe and PayPal need a public HTTPS site URL. Current base: <code class="font-mono text-xs">{{url}}</code>. Set <code class="font-mono">FRONTEND_BASE_URL=https://…</code> in production.',
          localhostHttpsHint: 'Development URL {{url}} is fine locally. For live Mollie/Stripe, set FRONTEND_BASE_URL to your public https:// academy domain and register the webhook URLs shown on each gateway card.',
          academyCurrency: 'Academy currency: <strong>{{currency}}</strong> — only payment methods for <strong>{{country}}</strong> are shown.',
          yourCountry: 'your country',
          gatewayActive: '{{name}} is active. Students can choose it when topping up their wallet.',
          gatewayInactive: '{{name}} is off. Students will not see this option.',
          eventStatus: {
            SUCCESS: 'Success',
            FAILED: 'Failed',
            PENDING: 'Pending',
          },
          gateways: {
            razorpay: {
              subtitle: 'UPI · Cards · Net Banking · Wallets',
              keyIdLabel: 'Razorpay Key ID',
              keySecretLabel: 'Razorpay Key Secret',
              keyIdPlaceholder: 'rzp_live_xxxxxxxxxxxxxxxx',
              keySecretPlaceholder: 'rzp_live_xxxxxxxxxxxxxxxx',
              webhookLabel: 'Razorpay Webhook Secret (optional backup)',
              webhookPlaceholder: 'From Razorpay Dashboard → Webhooks',
            },
            stripe: {
              subtitle: 'Cards · Apple Pay · Google Pay · Local methods',
              keyIdLabel: 'Stripe Publishable Key',
              keySecretLabel: 'Stripe Secret Key',
              keyIdPlaceholder: 'pk_live_xxxxxxxxxxxxxxxx',
              keySecretPlaceholder: 'sk_live_xxxxxxxxxxxxxxxx',
              webhookLabel: 'Stripe Webhook Signing Secret',
              webhookPlaceholder: 'whsec_xxxxxxxxxxxxxxxx',
            },
            paypal: {
              subtitle: 'PayPal balance · Cards',
              keyIdLabel: 'PayPal Client ID',
              keySecretLabel: 'PayPal Client Secret',
              keyIdPlaceholder: 'Client ID from developer.paypal.com',
              keySecretPlaceholder: 'Client Secret',
              webhookLabel: 'PayPal Webhook ID',
              webhookPlaceholder: 'Webhook ID from developer.paypal.com',
            },
            mollie: {
              subtitle: 'iDEAL · Bancontact · Cards · SEPA (Europe)',
              keyIdLabel: 'Mollie Profile ID (optional)',
              keySecretLabel: 'Mollie API Key',
              keyIdPlaceholder: 'pfl_… or leave blank',
              keySecretPlaceholder: 'test_… or live_…',
              webhookLabel: 'Unused (Mollie verifies via API fetch)',
              webhookPlaceholder: '',
            },
          },
          secretKeepPlaceholder: '(already set — leave blank to keep)',
          secretSetHelper: '✓ Secret is set. Leave blank to keep the current value.',
          secretNotSetHelper: '⚠ Secret not set yet — required to accept payments.',
          paypalSandbox: 'PayPal sandbox mode',
          paypalSandboxHint: 'Use sandbox API for testing with developer.paypal.com sandbox credentials.',
          testConnection: 'Test connection',
          testing: 'Testing…',
          connected: 'Connected.',
          connectionFailed: 'Connection failed.',
          connectionTestFailed: 'Connection test failed.',
          webhookUrlTitle: 'Webhook URL (register in {{name}} dashboard):',
          subscribeTo: 'Subscribe to:',
          keysFrom: 'Keys from',
          currencyUnsupported: '{{name}} does not support academy currency {{currency}}. Change Regional Settings or choose another gateway.',
          httpsGatewayHint: '{{name}} needs a public HTTPS URL (set FRONTEND_BASE_URL). Localhost is OK for development only.',
          resolveWarnings: 'Resolve the warnings above before {{name}} can accept live payments.',
          secretRequiredWhenEnabled: '⚠ {{label}} is required when {{name}} is enabled.',
          webhookOptionalHint: 'Optional — enables signed webhook backup if the browser never returns.',
          manualMethodDefault: 'Staff can record wallet top-ups with this method.',
          deploymentHttps: 'Stripe and PayPal require a public HTTPS URL. Your site must proxy <code class="font-mono">/api/</code> to the backend so webhooks reach <code class="font-mono">{{webhookBase}}/api/webhooks/…</code>',
          deploymentFrontendUrl: 'Set <code class="font-mono">FRONTEND_BASE_URL</code> in your environment for correct payment return URLs.'
        },
        report: {
          title: 'Report Settings',
          subtitle: 'Choose which reports are available in the Reports hub and configure report-specific inputs.',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          stepThree: 'Step 3',
          availableReports: 'Which reports are available',
          availableReportsDesc: 'Turn a report off to hide it from the Reports hub and block it in the API. Student and parent session-statement links follow the same switch.',
          enabledCountLabel: 'Enabled',
          statusOn: 'On',
          statusOff: 'Off',
          visibleTo: 'Visible to',
          appliesTo: 'Applies to',
          groups: {
            finance: 'Fees & money',
            attendance: 'Attendance & sessions',
            export: 'Data export'
          },
          coachRevenue: 'Coach commission split',
          coachRevenueDesc: 'Academy share per session type. The coach receives the remainder.',
          batchAcademyShare: 'Batch sessions — academy share (%)',
          individualAcademyShare: 'Individual sessions — academy share (%)',
          splitNote: 'Split: Coach {{coach}}% / Academy {{academy}}%',
          batchAttendance: 'Batch attendance options',
          batchAttendanceDesc: 'Controls what coaches and managers see in the batch attendance matrix. Admins always see session charges.',
          showCharges: 'Show session charges to coaches and managers',
          showChargesDesc: 'When enabled, coaches and managers see per-session fee amounts and period charge totals in batch-wise attendance reports, Excel exports, and PDFs.',
          saveSettings: 'Save settings',
          loadFailed: 'Failed to load report settings.',
          saveSuccess: 'Report settings saved successfully.',
          saveFailed: 'Failed to save report settings.',
          percentError: 'Enter a percentage between 0 and 100.',
          percentRange: 'Must be between 0 and 100.'
        },
        holidays: {
          title: 'Holiday Management',
          subtitle: 'Block academy-wide or branch-specific closed days. Sessions cannot be scheduled on holidays.',
          loadingSubtitle: 'Loading holidays…',
          stepOne: 'Step 1',
          stepTwo: 'Step 2',
          saveSuccessTitle: 'Holiday saved',
          saveSuccessWithEmails: 'The holiday was saved and notification emails are being sent to all relevant staff.',
          saveSuccessWithoutEmails: 'The holiday was saved without sending notification emails.',
          saveHolidayFailed: 'Could not save holiday.',
          deleteTitle: 'Delete holiday?',
          deletePastTitle: 'Delete past holiday?',
          deleteMessage: '"{{name}}" will be removed. New sessions can be scheduled on those dates again. If staff were already notified, tell them manually if needed.',
          deletePastMessage: '"{{name}}" has already ended. Removing it only updates this list and the calendar. Past sessions and attendance are not changed.',
          deleteHint: 'Delete holiday and allow scheduling on these dates',
          deletePastHint: 'Remove ended holiday from list',
          thisHoliday: 'this holiday',
          addNew: 'Add new holiday',
          addNewDesc: 'Define a closed period for the whole academy or a single branch.',
          holidayName: 'Holiday Name',
          startDate: 'Start date',
          endDate: 'End date',
          branch: 'Branch for Holiday',
          branchHelper: 'Choose a branch only if this holiday applies to that location alone.',
          allBranches: 'All branches (academy-wide)',
          notes: 'Holiday Notes',
          addHoliday: 'Add holiday',
          configured: 'Configured holidays',
          total: '{{count}} total',
          upcomingPast: '· {{upcoming}} upcoming · {{past}} past',
          colName: 'Holiday Name',
          colFrom: 'From',
          colTo: 'To',
          colBranch: 'Branch',
          colActions: 'Actions',
          noHolidays: 'No holidays configured yet.',
          allBranchesBadge: 'All branches',
          past: 'Past',
          delete: 'Delete',
          loadFailed: 'Failed to load holidays.',
          nameRequired: 'Holiday name is required.',
          datesRequired: 'Start date and end date are required.',
          validDatesFormat: 'Please enter valid dates in {{format}} format.',
          previewFailed: 'Could not prepare email preview.',
          deleteFailed: 'Could not delete holiday.',
          endDateInvalid: 'End date must be on or after start date.',
          notesPlaceholder: 'Optional internal note',
          namePlaceholder: 'e.g. Summer break, Diwali vacation',
          emailPreview: {
            title: 'Review holiday notification email',
            subtitle: 'Preview the email below, then choose whether to notify {{count}} relevant recipient (staff and students where applicable).',
            subtitle_other: 'Preview the email below, then choose whether to notify {{count}} relevant recipients (staff and students where applicable).',
            subjectLabel: 'Subject',
            previewLabel: 'Email preview',
            previewHelper: 'Each recipient receives a personalized greeting; preview shows a sample layout.',
            recipientsLabel: 'Recipients ({{count}})',
            noRecipients: 'No active admins, managers, coaches, or students with email addresses were found. You can still save the holiday without sending notifications.',
            roleBlockTitle: '{{role}} ({{count}})',
            roleSummaryItem: '{{count}} {{role}}',
            roleSummaryItem_other: '{{count}} {{role}}s',
            studentBulkNotice: '{{count}} student with email addresses will receive this notice.',
            studentBulkNotice_other: '{{count}} students with email addresses will receive this notice.',
            moreStudentsHidden: '…and {{count}} more student not shown here.',
            moreStudentsHidden_other: '…and {{count}} more students not shown here.',
            saveWithoutEmails: 'Save without emails',
            saveAndSend: 'Save & send emails',
            roles: {
              Admin: 'Admin',
              Manager: 'Manager',
              Coach: 'Coach',
              Student: 'Student'
            }
          }
        }
      },
      forms: {
        common: {
          firstName: 'First Name',
          middleName: 'Middle Name',
          lastName: 'Last Name',
          dateOfBirth: 'Date of Birth',
          gender: 'Gender',
          male: 'Male',
          female: 'Female',
          other: 'Other',
          loginUsername: 'Login Username',
          emailAddress: 'Email Address',
          phoneNumber: 'Phone Number',
          accountActive: 'Account Active',
          loginPassword: 'Login Password',
          confirmLoginPassword: 'Confirm Login Password',
          roles: 'Roles',
          select: 'Select…',
          cancel: 'Cancel',
          save: 'Save',
          saving: 'Saving…',
          create: 'Create',
          update: 'Update',
          none: '— None —'
        },
        placeholders: {
          firstName: 'First Name',
          middleName: 'Middle Name',
          lastName: 'Last Name',
          firstNameRequired: 'First Name *',
          lastNameRequired: 'Last Name *',
          username: 'Username',
          email: 'Email',
          password: 'Password',
          confirmPassword: 'Confirm Password'
        },
        validation: {
          firstNameRequired: 'First name is required and may include letters, spaces, hyphens, and apostrophes.',
          middleNameLetters: 'Middle name may include letters, spaces, hyphens, and apostrophes.',
          firstNameLetters: 'First name may include letters, spaces, hyphens, and apostrophes — not numbers.',
          lastNameLetters: 'Last name may include letters, spaces, hyphens, and apostrophes — not numbers.',
          lastNameRequired: 'Last name is required and may include letters, spaces, hyphens, and apostrophes.',
          invalidDob: 'Please enter a valid date of birth in the past.',
          usernameRequired: 'Username is required.',
          emailInvalid: 'Please enter a valid email address.',
          passwordMin: 'Password must be at least 6 characters.',
          passwordsMismatch: 'Password and Confirm Password do not match.',
          rolesRequired: 'Please select at least one role.',
          invalidAddress: 'Invalid address.',
          invalidLatitude: 'Latitude must be a valid number.',
          invalidLongitude: 'Longitude must be a valid number.'
        },
        phone: {
          countryCode: 'Country code',
          searchCountry: 'Search country',
          noMatches: 'No matches',
          phoneNumber: 'Phone number'
        },
        address: {
          title: 'Address',
          addressLine1: 'Address Line 1',
          addressLine2: 'Address Line 2',
          addressLine2Placeholder: 'Flat / Floor / Building (optional)',
          country: 'Country',
          provinceRegion: 'Province / Region',
          cityMunicipality: 'City / Municipality',
          postcode: 'Postcode',
          latitude: 'Latitude',
          longitude: 'Longitude',
          optional: '(optional)',
          selectCountry: 'Select Country',
          selectCountryFirst: 'Select Country first',
          selectProvince: 'Select Province / Region',
          noRegions: 'No regions for this country',
          selectProvinceFirst: 'Select Province / Region first',
          loadingCities: 'Loading cities...',
          searchCity: 'Search city',
          saved: '(saved)',
          noCityList: 'No city list for this region — you can type the city name.',
          citiesLoadFailed: 'Could not load cities — you can still type the city name.',
          fillHint: 'If you enter any address field, please fill at least <strong>Country</strong> and <strong>City</strong>. Postcode, Province, and Lat/Lng are optional.',
          countryCityRequired: 'If you enter an address, please fill at least Country and City.',
          streetPlaceholder: 'Street address',
          egExample: 'e.g. {{example}}'
        },
        student: {
          addTitle: 'Add Student',
          editTitle: 'Edit Student',
          subtitle: 'Capture student information, access details, and address data in one clean workflow.',
          fideSection: 'FIDE / rating (optional)',
          fideId: 'FIDE ID',
          eloRating: 'Elo rating',
          fideTitle: 'FIDE title',
          createButton: 'Create Student',
          updateButton: 'Update Student',
          managerRolesNote: 'Managers can only create or edit student accounts.',
          updated: 'Student updated',
          created: 'Student created',
          updateFailed: 'Failed to update student.',
          saveFailed: 'Failed to save student.',
          loadArchiveFailed: 'Failed to load archive options.',
          enrollmentsEnded: 'Enrollments ended. The student account remains active.',
          archived: 'Student archived.',
          archiveFailed: 'Failed to archive student.',
          activeDesc: 'Archived students stay in history and can be reactivated later.',
          activeDescArchive: 'Turn off to archive this student. If they have active enrollments, you will choose how to end them.'
        },
        coach: {
          addTitle: 'Add Coach',
          editTitle: 'Edit Coach',
          subtitle: 'Manage coach identity, login access, and profile details with the same polished workflow used across the product.',
          createButton: 'Create Coach',
          updateButton: 'Update Coach',
          activeDesc: 'Use this to archive or reactivate the coach without losing their history.',
          updated: 'Coach updated',
          created: 'Coach created',
          updateFailed: 'Failed to update coach.',
          saveFailed: 'Failed to save coach.'
        },
        manager: {
          addTitle: 'Add Manager',
          editTitle: 'Edit Manager',
          subtitle: 'Keep manager setup clean and consistent with the upgraded product-wide form experience.',
          createButton: 'Create Manager',
          updateButton: 'Update Manager',
          updated: 'Manager updated',
          created: 'Manager created',
          saveFailed: 'Failed to save manager.',
          activeDesc: 'Managers can be archived safely without removing their records.'
        },
        user: {
          addTitle: 'Create User',
          editTitle: 'Edit User',
          subtitle: 'Manage user account details, roles, and access.',
          emailSignInLink: 'Email sign-in link (they set their own password)',
          createButton: 'Create User',
          updateButton: 'Update User',
          loadFailed: 'Failed to load user.',
          saveFailed: 'Failed to save user. Please check inputs.'
        },
        branch: {
          addTitle: 'Add Branch',
          editTitle: 'Edit Branch',
          subtitle: 'Create or update branch details with cleaner sections, better spacing, and a more premium UI.',
          branchName: 'Branch Name',
          branchCode: 'Branch Code',
          branchMode: 'Branch Mode',
          managerOptional: 'Manager (optional)',
          branchActive: 'Branch Active',
          branchActiveDesc: 'Disable this branch to archive it while preserving all related records.',
          branchAddress: 'Branch Address',
          onlineNote: 'Physical address is hidden for online-only branches.',
          createButton: 'Create Branch',
          updateButton: 'Update Branch',
          nameRequired: 'Branch name is required.',
          codeRequired: 'Branch code is required.',
          modeRequired: 'Mode is required.',
          updateSuccess: 'Branch updated successfully',
          addSuccess: 'Branch added successfully',
          namePlaceholder: 'e.g., Main Branch',
          uniqueCodePlaceholder: 'Unique code',
          loadManagersFailed: 'Failed to load managers',
          updateFailed: 'Failed to update branch.',
          saveFailed: 'Failed to save branch.'
        },
        batch: {
          addTitle: 'Create Batch',
          editTitle: 'Edit Batch',
          subtitle: 'Configure batch schedule, fees, and session settings.',
          branch: 'Branch for Batch',
          level: 'Level',
          coach: 'Assigned Coach',
          name: 'Batch Name',
          sessionMode: 'Batch Session Mode',
          defaultStart: 'Default Start (HH:mm)',
          defaultEnd: 'Default End (HH:mm)',
          selectStartTime: 'Select start time',
          selectEndTime: 'Select end time',
          billingConfig: 'Billing Configuration',
          chargesMode: 'Billing Charges Mode',
          sessionCharge: 'Session Charge ({{currency}})',
          periodLength: 'Default period length',
          periodFee: 'Period fee ({{currency}})',
          carryForward: 'Carry Forward Policy',
          selectBranchFirst: 'Select branch first',
          selectEllipsis: 'Select…',
          notePerSession: 'Charge per session individually',
          notePerSessionHourly: 'Hourly rate × session duration',
          notePerPeriod: 'Fixed fee for a date-bounded enrollment period',
          periodNoCarryForward: 'Session carry forward does not apply to period billing batches. Unused sessions are not carried forward between billing periods.',
          validation: {
            selectBranch: 'Please select a Branch',
            selectLevel: 'Please select a Level',
            selectCoach: 'Please select a Coach',
            selectSessionMode: 'Please select Session Mode',
            enterName: 'Please enter a Name for the batch',
            selectSessionDay: 'Please select at least one Session Day',
            selectDefaultStart: 'Please select Default Start time',
            selectDefaultEnd: 'Please select Default End time',
            endAfterStart: 'Default End time must be after Default Start time',
            saveFailed: 'Failed to save batch'
          }
        },
        level: {
          addTitle: 'Add Level',
          editTitle: 'Edit Level',
          subtitle: 'Define level name and display order for batches and enrollments.',
          saveFailed: 'Save failed'
        }
      },
      enrollments: {
        title: 'Enrollments',
        subtitle: 'Manage student enrollments, period fees, and renewals',
        newEnrollment: 'New Enrollment',
        editEnrollment: 'Edit Enrollment',
        formSubtitle: 'Enroll a student in a batch with fee plan and billing options.',
        validation: {
          loadEnrollmentFailed: 'Failed to load enrollment data',
          loadFailed: 'Failed to load data',
          selectAtLeastOneStudent: 'Please select at least one student',
          carryForwardRequired: 'Carry forward decision is required for this batch',
          endDateRequired: 'End date is required for period billing enrollments',
          saveFailed: 'Failed to save enrollment'
        },
        entityLabel: 'enrollments',
        searchPlaceholder: 'Search enrollments by ID, student name, batch, or level...',
        noMatch: 'No enrollments match your search',
        empty: 'No enrollments found',
        emptyHint: 'Try adjusting your search terms',
        filters: {
          all: 'All',
          active: 'Active',
          archived: 'Archived'
        },
        columns: {
          id: 'ID',
          student: 'Student',
          level: 'Level',
          batch: 'Batch',
          billing: 'Billing',
          period: 'Period',
          payment: 'Payment',
          expiry: 'Expiry',
          status: 'Status',
          actions: 'Actions'
        },
        periodOngoing: 'ongoing',
        expiresToday: 'Expires today',
        daysLeft: '{{count}}d left',
        expired: 'Expired',
        notApplicable: 'N/A',
        renew: 'Renew',
        renewTitle: 'Create next period enrollment',
        view: 'View',
        viewTitle: 'View enrollment details',
        edit: 'Edit',
        editTitle: 'Edit enrollment',
        archive: 'Archive',
        activate: 'Activate',
        reactivateConfirm: 'Reactivate enrollment #{{id}}? Only possible if today falls within the enrollment period.',
        archiveFailed: 'Archive failed.',
        cannotArchive: 'You cannot archive this enrollment.',
        activateFailed: 'Could not activate this enrollment.',
        renewMissingData: 'Cannot renew — enrollment is missing student, level, or batch data.',
        crossBranchWarning: 'Cross-Branch Warning',
        crossBranchEnrollment: 'Enrollment #{{id}}',
        crossBranchDesc: '<strong>{{name}}</strong> is also actively enrolled in branches that are <span class="text-orange-600 font-medium">not managed by you</span>:',
        continueArchive: 'Continue to archive',
        statusActive: 'Active',
        statusArchived: 'Archived',
        form: {
          student: 'Student',
          selectStudents: 'Select Students ({{count}} selected)',
          searchStudents: 'Type to search students...',
          selectAll: 'Select All ({{count}})',
          clear: 'Clear',
          level: 'Level',
          trainingBatch: 'Training Batch',
          select: 'Select...',
          startDate: 'Enrollment Start Date',
          startDateHint: 'Date when student officially joins this batch',
          endDate: 'Enrollment End Date',
          optional: '(Optional)',
          endDatePeriodHint: 'Default from batch plan ({{interval}}); you may adjust.',
          endDateOpenHint: 'Leave blank for open-ended enrollment',
          carryForwardPolicy: 'Session Carry Forward Policy',
          batchPolicy: 'Batch Policy:',
          setCarryForward: 'Set Carry Forward for This Student:',
          enableCarryForward: 'Enable Carry Forward',
          disableCarryForward: 'Disable Carry Forward',
          carryForwardRequired: 'This batch requires you to explicitly set carry forward policy for each student.',
          carryForwardBatchLevel: 'Carry forward is controlled at batch level',
          overrideBilling: 'Override Billing Charges (Optional)',
          hide: 'Hide',
          show: 'Show',
          billingMode: 'Enrollment Billing Mode',
          billingPeriod: 'Billing period',
          discountOff: '{{value}} off',
          periodFee: 'Period fee',
          perSessionCharge: 'Per Session Charge',
          hourlyRate: 'Hourly Rate',
          periodFeeAmount: 'Period fee amount',
          useBatchDefault: 'Use batch default',
          notConfigured: 'Not configured',
          pricingSummary: 'Pricing Summary',
          batchDefaultPricing: 'Batch Default Pricing',
          chargesMode: 'Charges Mode:',
          perSession: 'Per Session:',
          periodPlan: 'Period plan:',
          applyDiscount: 'Apply Discount',
          applyDiscountCustom: 'Apply Discount (will apply to customized charges)',
          noDiscount: 'No discount applied',
          discountApplied: '{{value}}% discount will be applied',
          discountAppliedCustom: ' to your custom pricing',
          customChargesApplied: 'Custom Charges Applied',
          customChargesDesc: 'Custom pricing set for this student (overrides batch defaults)',
          customChargesDiscount: '✓ {{value}}% "{{name}}" discount will be applied on top of custom charges',
          finalCharges: 'Final Charges for This Student',
          pricingModel: 'Pricing Model:',
          perHour: 'Per Hour:',
          updateEnrollment: 'Update Enrollment',
          createEnrollment: 'Create Enrollment',
          saving: 'Saving enrollment…',
          enrolling: 'Enrolling {{current}} of {{total}}',
          preparing: 'Preparing…',
          redirecting: 'Redirecting to enrollments list...',
          successCount: '✅ {{count}} student enrolled successfully',
          successCount_other: '✅ {{count}} students enrolled successfully',
          failedCount: '❌ {{count}} student failed to enroll',
          failedCount_other: '❌ {{count}} students failed to enroll',
          renewBanner: 'Renewing from enrollment #{{id}}. Dates default to the next period — adjust if needed.'
        },
        archiveModal: {
          title: 'Archive enrollment',
          description: 'Set the end date for {{name}} (enrollment #{{id}}).',
          enrolledOn: ' Enrolled {{date}}.',
          endDate: 'End date',
          archiving: 'Archiving…'
        },
        paymentModal: {
          title: 'Period fee & wallet',
          enrolling: 'Enrolling {{name}}',
          review: 'Review payment before completing enrollment',
          periodFee: 'Period fee:',
          plan: 'Plan:',
          walletBalance: 'Wallet balance:',
          insufficient: '(insufficient for full fee)',
          amountNeeded: 'Amount needed:',
          topUp: 'Top up wallet',
          refreshing: 'Refreshing…',
          refreshBalance: 'Refresh balance',
          paymentRequired: 'Your academy requires payment before attendance. Please charge the wallet now or top up first.',
          unpaidPriorTitle: 'Previous period fee still unpaid',
          unpaidPriorDesc: 'Enrollment #{{id}} ({{period}}) has an outstanding balance of {{amount}}.',
          unpaidPriorAck: 'I understand and wish to proceed with this new enrollment.',
          insufficientHelp: 'The wallet does not cover the full period fee. Choose how you\'d like to handle collection. We will retry automatically on a daily schedule and when sessions are completed.',
          collectLegend: 'How should we collect the fee?',
          chargeNow: 'Charge wallet now',
          collectViaSchedule: 'Collect over the next few days',
          enrollUnpaid: 'Enroll without charging yet',
          chargeNowHelp: 'The period fee will be deducted from the student\'s wallet right away.',
          chargeNowInsufficient: 'The wallet will be debited immediately. Top up the wallet first, or enable negative wallet in billing settings.',
          collectViaScheduleHelp: 'We\'ll try to collect the fee daily for up to a few days, and again when sessions are completed. The student may attend according to your academy\'s grace rules.',
          enrollUnpaidHelp: 'The enrollment will be created without an immediate charge. Collection will follow your academy\'s scheduled retry rules.',
          confirmEnroll: 'Confirm & enroll'
        },
        lockReasons: {
          student: 'This enrollment belongs to this student and cannot be switched to someone else.',
          level: 'The level was set when this student joined and cannot be changed here. To move them to another level, end this enrollment and create a new one.',
          batch: 'The training batch was set when this student joined and cannot be changed here. To move them to another batch, end this enrollment and create a new one.',
          startDate: 'The start date was saved when this enrollment was created and cannot be changed afterward.',
          endDateEditable: 'Set or update when this student should stop attending this batch. Sessions already held or billed are not affected.',
          carryForward: 'This carry-forward choice was made at enrollment and cannot be changed here.',
          carryForwardBatch: 'Carry forward for this batch is the same for all students and is managed on the batch settings.',
          billingMode: 'The billing type for this enrollment cannot be changed after the student is enrolled.',
          billingAmount: 'This fee amount cannot be changed on an existing enrollment.',
          customRateEditable: 'Updates the custom rate for future sessions only. Charges already taken stay as they were.',
          customRateLocked: 'Custom pricing can only be set when the student is first enrolled, not on edit.',
          discount: 'The discount on this enrollment cannot be changed after enrollment.',
          systemManaged: 'This enrollment was created automatically for a one-off session and cannot be edited.'
        }
      },
      sessions: {
        title: 'Sessions',
        subtitle: 'Filter by level and batch. Mark attendance, edit or create sessions.',
        addWalkIn: 'Add Walk-in Session',
        addWalkInTitle: 'Create a one-time session for walk-in student',
        addIndividual: 'Add Individual Session',
        addIndividualTitle: 'Create individual one-on-one session',
        addBatch: 'Add Batch Sessions',
        addBatchTitle: 'Add Batch Sessions',
        selectBatchTitle: 'Select a batch to create session',
        showingOnDate: 'Showing sessions on',
        selectLevelBatch: ' — select a level and batch to load sessions',
        clearDateFilter: 'Clear date filter',
        filterLevel: 'Filter by Level',
        filterBatch: 'Filter by Batch',
        selectLevel: 'Select level…',
        selectBatch: 'Select batch…',
        selectLevelFirst: 'Select level first',
        sessionStatus: 'Session Status',
        allStatuses: 'All Statuses',
        sortBy: 'Sort By',
        sortDateDesc: 'Date (Newest First)',
        sortDateAsc: 'Date (Oldest First)',
        sortStatus: 'Status (Pending → Completed)',
        sortIdDesc: 'Session ID (High to Low)',
        sortIdAsc: 'Session ID (Low to High)',
        searchSessions: 'Search Sessions',
        searchPlaceholder: 'Search sessions...',
        showingResults: 'Showing {{count}} result',
        showingResults_other: 'Showing {{count}} results',
        statusFilter: 'Status:',
        clearFilters: 'Clear filters',
        selectLevelBatchEmpty: 'Select a level and batch',
        selectLevelBatchHint: 'Choose from the filters above to view sessions',
        noSessions: 'No sessions found',
        noSessionsHint: 'Create your first session for this batch',
        sessionNumber: 'Session #{{id}}',
        walkIn: 'Walk-in',
        level: 'Level',
        batch: 'Batch',
        coach: 'Coach',
        notAssigned: 'Not assigned',
        statusControl: 'Status Control',
        statusPending: 'Pending',
        statusCompleted: 'Completed',
        statusCancelled: 'Cancelled',
        completedLocked: 'Completed sessions cannot be changed',
        cancelledLocked: 'Cancelled sessions cannot be changed',
        saveStatus: 'Save status',
        save: 'Save',
        saving: 'Saving…',
        viewAttendance: 'View Attendance',
        viewAttendanceCancelled: 'View attendance for cancelled session',
        viewAttendanceCompleted: 'View attendance filled for this completed session',
        updateNotes: 'Update Session Notes',
        updateNotesTitle: 'Update session notes/topic',
        reverse: 'Reverse',
        reversing: 'Reversing...',
        reverseTitle: 'Reverse this completed session and credit students',
        markAttendance: 'Mark Attendance',
        markAttendanceTitle: 'Mark attendance for this session',
        viewAttendanceSession: 'View attendance for this session',
        attendanceCompletedLocked: 'Attendance can\'t be marked for completed sessions',
        attendanceCancelledLocked: 'Attendance can\'t be marked for cancelled sessions',
        edit: 'Edit',
        editTitle: 'Edit session details',
        pagination: 'Showing {{start}} to {{end}} of {{total}} sessions',
        rowsPerPage: 'Rows per page:',
        loadFailed: 'Failed to load sessions.',
        loadDateFailed: 'Failed to load sessions for selected date.',
        markBeforeComplete: 'Please mark attendance before marking the session as COMPLETED.',
        completeSuccess: 'Session marked as COMPLETED. Student billing has been processed.',
        completeFailed: 'Failed to complete session and process billing:\\n\\n{{message}}\\n\\nPlease ensure students have sufficient wallet balance and try again.',
        statusUpdateFailed: 'Failed to update session status: {{message}}',
        reversalReasonRequired: 'Reversal reason is required.',
        reversalConfirm: 'Reversing this session will:\\n- Credit amounts back to students\' wallets\\n- Mark the session as CANCELLED\\n\\nAre you sure you want to continue?',
        reversalSuccess: 'Session reversed successfully.',
        reversalFailed: 'Failed to reverse session: {{message}}',
        walkInCreated: 'Walk-in session created successfully!\\nSession ID: {{sessionId}}\\nEnrollment ID: {{enrollmentId}}',
        individualCreated: 'Individual session created successfully!\\nSession ID: {{sessionId}}\\nBatch: {{batch}}\\nStudent: {{student}}\\nCharge: {{charge}}',
        reversalModal: {
          title: 'Reverse Session #{{id}}',
          willDo: 'This action will:',
          creditWallets: 'Credit amounts back to all students\' wallets',
          markCancelled: 'Mark the session as CANCELLED',
          compensatingTxns: 'Create compensating wallet transactions',
          recordReason: 'Record the reversal reason for audit',
          reasonLabel: 'Reversal Reason',
          reasonPlaceholder: 'e.g., Session cancelled due to coach unavailability...',
          confirm: 'Confirm Reversal'
        },
        form: {
          editTitle: 'Edit Session',
          addTitle: 'Add Batch Sessions',
          editSubtitle: 'Update session details',
          addSubtitle: 'Schedule recurring sessions for your batch',
          close: 'Close',
          trainingBatch: 'Training Batch',
          selectBatch: 'Select batch...',
          sessionCoach: 'Session Coach',
          selectCoach: 'Select coach...',
          autoSelected: 'Auto-selected',
          sessionTiming: 'Session Timing',
          startTime: 'Session Start Time',
          endTime: 'Session End Time',
          selectStartTime: 'Select start time',
          selectEndTime: 'Select end time',
          sessionDate: 'Session Date',
          dateRange: 'Date Range',
          date: 'Date ({{format}})',
          from: 'From ({{format}})',
          to: 'To ({{format}})',
          selectDays: 'Select days for recurrence',
          selected: 'Selected:',
          update: 'Update',
          create: 'Create',
          updating: 'Updating...',
          creating: 'Creating...',
          fillRequired: 'Please fill all required fields.',
          endAfterStart: 'End time must be after start time',
          selectDay: 'Please select at least one day.',
          invalidDateRange: 'Invalid date range.',
          invalidDate: 'Invalid date.',
          updated: 'Session updated successfully.',
          created: 'Created {{count}} session(s).',
          updateFailed: 'Failed to update session.',
          createFailed: 'Failed to create sessions.',
          longSessionWarning: '⚠️ Long Session Duration Warning\\n\\nSession Date(s): {{dates}}\\nStart Time: {{start}}\\nEnd Time: {{end}}\\nDuration: {{duration}}\\n\\nThis session duration exceeds 3 hours. Do you want to proceed?',
          hour: '{{count}} hour',
          hour_other: '{{count}} hours',
          minute: '{{count}} minute',
          minute_other: '{{count}} minutes'
        },
        walkInModal: {
          title: 'Add Walk-in Session',
          info: 'Walk-in Session:',
          infoBody: 'Creates a one-time session for a student who is not regularly enrolled. The system will automatically create an enrollment and batch as needed.',
          students: 'Students',
          searchStudents: 'Search and select students...',
          noStudentsFound: 'No students found',
          allStudentsSelected: 'All students selected',
          searchHint: 'Type to search and select one or more students',
          sessionDetails: 'Session Details',
          selectLevel: 'Select level...',
          sessionCoach: 'Session Coach',
          selectCoach: 'Select coach...',
          coachAutoNote: 'Auto-selected (coaches can only create sessions for themselves)',
          timing: 'Timing',
          sessionDate: 'Session Date',
          startTime: 'Start Time',
          endTime: 'End Time',
          selectTime: 'Select Time',
          sessionCharge: 'Session Charge ({{symbol}})',
          enterAmount: 'Enter amount',
          chargeNote: 'This is the total charge for the entire session duration, not an hourly rate.',
          creating: 'Creating...',
          create: 'Create Walk-in Session',
          fillRequired: 'Please fill in all required fields and select at least one student',
          endAfterStart: 'End time must be after start time',
          createFailed: 'Failed to create walk-in session',
          longSessionTitle: 'Long Session Duration Warning',
          longSessionProceed: 'This session duration exceeds 3 hours. Do you want to proceed?',
          sessionDateLabel: 'Session Date:',
          startTimeLabel: 'Start Time:',
          endTimeLabel: 'End Time:',
          durationLabel: 'Duration:',
          userFallback: 'User #{{id}}',
          idLabel: 'ID: #{{id}}'
        },
        individualModal: {
          title: 'Add Individual Session',
          autoActions: 'Automatic Actions:',
          autoPresent: 'Attendance will be automatically marked as PRESENT',
          autoCharge: 'Session charge will be deducted immediately from student\'s wallet',
          autoCompleted: 'Session status will be set to COMPLETED (for accounting purposes)',
          sessionDetails: 'Session Details',
          selectLevel: 'Select Level',
          studentName: 'Student Name',
          selectStudent: 'Select Student',
          sessionCoach: 'Session Coach',
          selectCoach: 'Select Coach',
          timing: 'Timing',
          sessionDate: 'Session Date',
          startTime: 'Start Time',
          endTime: 'End Time',
          selectTime: 'Select Time',
          sessionTopic: 'Session Topic',
          topicPlaceholder: 'e.g., Chess Opening Theory, Endgame Practice',
          sessionCharge: 'Session Charge ({{symbol}})',
          chargePlaceholder: 'e.g., 600',
          chargeNote: 'Default rate shown is hourly charge. It automatically adjusts based on session duration. You can manually edit this amount.',
          creating: 'Creating...',
          create: 'Create Individual Session',
          selectLevelError: 'Please select a level',
          selectStudentError: 'Please select a student',
          selectCoachError: 'Please select a coach',
          selectDateError: 'Please select session date',
          selectStartError: 'Please select start time',
          selectEndError: 'Please select end time',
          topicRequired: 'Please enter session topic',
          chargeRequired: 'Please enter a valid charge amount',
          endAfterStart: 'End time must be after start time',
          invalidDate: 'Please enter a valid session date.',
          createFailed: 'Failed to create individual session',
          longSessionTitle: 'Long Session Duration Warning',
          longSessionProceed: 'This session duration exceeds 3 hours. Do you want to proceed?',
          sessionDateLabel: 'Session Date:',
          startTimeLabel: 'Start Time:',
          endTimeLabel: 'End Time:',
          durationLabel: 'Duration:'
        },
        updateNotesModal: {
          title: 'Update Session Notes',
          subtitle: 'Session #{{sessionId}} - {{batchName}}',
          sessionNotes: 'Session Notes',
          placeholder: 'Enter session notes...',
          charCount: '{{count}} / 2000 characters',
          saving: 'Saving...',
          saveNotes: 'Save Notes',
          updateFailed: 'Failed to update session notes. Please try again.'
        }
      },
      attendance: {
        markTitle: 'Mark Attendance',
        viewTitle: 'Session Attendance',
        viewLoading: 'Loading attendance details…',
        coach: 'Coach',
        status: 'Status',
        sessionNotes: 'Session Notes',
        sessionNotesRequired: 'Session Notes',
        sessionNotesPlaceholder: 'Add notes for this session before marking attendance',
        sessionNotesHint: 'Required before saving or completing the session',
        searchStudents: 'Search students… (name or #id)',
        warning: 'Warning',
        success: 'Success',
        markAllPresent: 'Mark All Present',
        markAllAbsent: 'Mark All Absent',
        saveAttendance: 'Save Attendance',
        saving: 'Saving…',
        completeSession: 'Complete Session',
        completing: 'Completing…',
        completeTitle: 'Save attendance and mark this session as COMPLETED.',
        alreadyCompleted: 'Session is already marked as COMPLETED.',
        cancelledCannotComplete: 'Cancelled sessions cannot be completed.',
        loadingStudents: 'Loading students…',
        checkInOutInfo: 'Check-in and Check-out Times',
        checkInOutHint: 'These times are optional. Only fill them in when a student\'s attended time differs from the scheduled session time.',
        student: 'Student',
        attendanceStatus: 'Attendance Status',
        checkIn: 'Check-in Time',
        checkOut: 'Check-out Time',
        notMarked: 'Not Marked',
        present: 'Present',
        absent: 'Absent',
        notApplicable: 'Not Applicable',
        selectCheckIn: 'Select check-in time',
        selectCheckOut: 'Select check-out time',
        noStudentsMatch: 'No students match "{{query}}".',
        walkInStudents: 'Walk-in Students ({{count}})',
        addWalkIn: '+ Add Walk-in Student',
        duplicateWalkIns: '{{count}} walk-in record overlaps with enrolled batch students and is hidden below to avoid confusion.',
        duplicateWalkIns_other: '{{count}} walk-in records overlap with enrolled batch students and are hidden below to avoid confusion.',
        removeDuplicate: 'Remove duplicate: {{name}}',
        noWalkIns: 'No walk-in students for this session',
        walkInsLoadFailed: 'Could not load walk-in students. Refresh and try again.',
        walkInHint: 'Walk-in students are automatically marked present and charged on session completion',
        notes: 'Notes',
        actions: 'Actions',
        remove: 'Remove',
        sessionStart: 'Session Start',
        sessionEnd: 'Session End',
        walkInAdded: 'Walk-in student added successfully!',
        walkInRemoved: 'Walk-in student removed successfully!',
        removeWalkInConfirm: 'Remove this walk-in student? They will not be charged for this session.',
        removeWalkInFailed: 'Failed to remove walk-in student',
        notesRequiredSave: 'Session Notes is required before saving attendance.',
        notesRequiredComplete: 'Session Notes is required before completing the session.',
        markBeforeComplete: 'Please mark attendance before completing the session.',
        savedAck: 'Attendance saved at {{time}}. You can update it until the session is marked as COMPLETED.',
        completeConfirm: 'Mark this session as COMPLETED? Once completed, it cannot be changed.',
        completeSuccess: 'Session marked as COMPLETED successfully!',
        completeFailed: 'Failed to complete session: {{message}}',
        saveFailed: 'Failed to save attendance.',
        loadFailed: 'Failed to load attendance data.',
        sessionIdMissing: 'Session ID is missing from route/state.',
        batchUnknown: 'Batch is unknown for this session. Showing only students who already have marks.',
        batchStudents: 'Batch Students',
        walkInLabel: 'Walk-in Students',
        type: 'Type',
        walkInType: 'Walk-in',
        carryForward: 'Carry Forward',
        carryForwardColumnHelp: 'Carry forward controls billing when a student is absent. If enabled, the session fee is not deducted and the credit can be used later. If disabled, the fee is still deducted even when the student is marked absent.',
        sessionCancelled: 'Session Cancelled',
        cancelledDesc: 'This session has been cancelled/reversed. All billing charges have been refunded.',
        cancelledHistorical: ' The attendance data shown below is historical and no longer active.',
        missingSession: 'Missing session information.',
        loadDetailsFailed: 'Failed to load attendance details.',
        addWalkInStudent: {
          title: 'Add Walk-in Student',
          selectStudent: 'Select Student',
          searchPlaceholder: 'Search by name or email...',
          alreadyOnSession: 'Matching students are already on this session and cannot be added as walk-ins.',
          noMatch: 'No other students match your search',
          allOnSession: 'All available students are already on this session',
          noStudents: 'No students found',
          change: 'Change',
          checkIn: 'Check-in Time (Optional)',
          checkInHint: 'Leave empty to use session start time',
          checkOut: 'Check-out Time (Optional)',
          checkOutHint: 'Leave empty to use session end time',
          notes: 'Notes (Optional)',
          notesPlaceholder: 'Any additional notes...',
          adding: 'Adding...',
          add: 'Add Walk-in Student',
          selectRequired: 'Please select a student',
          alreadyOnRoster: 'This student is already on the session roster or walk-in list',
          loadFailed: 'Failed to load students',
          addFailed: 'Failed to add walk-in student',
          noteTitle: 'Note:',
          noteBody: 'Walk-in students will be automatically charged the session rate when the session is completed. The charge will be deducted from their wallet. Students already enrolled in this batch are not shown here.'
        }
      },
      reports: {
        eyebrow: 'Reports',
        title: 'Reports',
        subtitle: 'View and export detailed reports for your academy',
        loading: 'Loading reports…',
        noReportsEnabled: 'No reports are enabled',
        noReportsHint: 'Your academy administrator has disabled all reports. Contact an admin to enable reports under Reports → Report Settings.',
        tabs: {
          'coach-revenue': 'Coach Revenue & Commission',
          'session-statement': 'Student Session Statement',
          'batch-attendance': 'Batch-wise Attendance',
          'academy-revenue': 'Academy Revenue Summary',
          'student-attendance': 'Student Attendance Summary',
          'payments-due': 'Payments Due',
          'data-export': 'Data Export',
          'sessions-summary': 'Sessions Summary',
          'monthly-statements': 'Monthly Statement Control'
        },
        tabsManager: {
          'session-statement': 'Session Statement'
        },
        descriptions: {
          'coach-revenue': 'Session-level revenue with academy vs coach commission split.',
          'session-statement': 'Per-student fee ledger with payments, session charges, and PDF export.',
          'batch-attendance': 'Matrix of students vs sessions for a batch in a date range.',
          'academy-revenue': 'Monthly and branch-level gross revenue with commission breakdown.',
          'student-attendance': 'Attendance rates and counts per student for a period.',
          'payments-due': 'Point-in-time list of students with overdue or low wallet balance requiring fee collection.',
          'data-export': 'CSV exports for student roster, guardians, wallet transactions, and Playing Platform games.',
          'sessions-summary': 'Session date/time, level, batch, coach, and topic for a student or coach over a date range.',
          'monthly-statements': 'Preview, send, and track monthly session statement emails with fee estimates.'
        },
        dataExport: {
          title: 'Data Export',
          subtitle: 'Download academy data as CSV files. All exports include a UTF-8 BOM for correct display in Excel.',
          preparing: 'Preparing…',
          from: 'From',
          to: 'To',
          studentRoster: 'Student Roster',
          studentRosterDesc: 'All students with contact details, date of birth, optional FIDE/Elo fields, active enrolment batches, and current wallet balance.',
          downloadStudents: 'Download students.csv',
          guardianLinks: 'Guardian Links',
          guardianLinksDesc: 'Student–guardian relationships and link status for DSAR / parental access requests.',
          downloadGuardians: 'Download guardians.csv',
          playingGames: 'Playing Platform games',
          playingGamesDesc: 'Chess games (PGN, result, clocks) for the selected student — for DSAR / portability packs.',
          downloadPlaying: 'Download playing-games.csv',
          playingStudentRequired: 'Choose a student to export Playing Platform games.',
          exportPlayingFailed: 'Playing Platform export failed.',
          walletTransactions: 'Wallet Transactions',
          walletTransactionsDesc: 'All wallet credits and debits for the selected period — including student name, amount, reason, payment method, and reference.',
          downloadTransactions: 'Download transactions.csv',
          footer: 'CSV files open directly in Microsoft Excel, Google Sheets, and LibreOffice Calc. For large academies, exports may take a few seconds.',
          invalidDateRange: 'Please select a valid date range.',
          exportStudentsFailed: 'Failed to export student list. Please try again.',
          exportGuardiansFailed: 'Failed to export guardian links. Please try again.',
          exportTxnsFailed: 'Failed to export transactions. Please try again.',
          dsarBannerTitle: 'Data access request — act here',
          dsarBannerBody: '{{name}} (user #{{userId}}, {{username}}) asked for a copy of their personal data. Download the exports below, then open their profile if you need to verify identity.',
          dsarUnknownName: 'This user',
          dsarOpenProfile: 'Open profile',
          dsarOpenWallet: 'Open wallet top-up',
          studentFilter: 'Student',
          studentFilterHint: 'All CSV downloads below use this selection. Pick one student for a DSAR pack, or All students for academy-wide exports.',
          chooseStudent: 'Search students…',
          allStudents: 'All students',
          studentFallback: 'Student #{{id}}',
          scopedToStudent: 'Export scope: {{student}}',
          scopedToAll: 'Export scope: all students',
          dsarLocked: 'DSAR focus',
          clearStudentFilter: 'Clear student filter',
          studentRosterDescOne: 'Contact details, DOB, optional FIDE/Elo, active batches, and wallet balance for the selected student only.',
          guardianLinksDescOne: 'Guardian relationships and link status for the selected student only.',
          walletTransactionsDescOne: 'Pick a student, then download their wallet credits and debits for the date range.',
          downloadOneStudent: 'Download this student.csv',
          downloadOneGuardians: 'Download this student’s guardians.csv',
          downloadOneTransactions: 'Download this student’s transactions.csv',
          walletStudentFilter: 'Student',
          walletExportingFor: 'Exporting transactions for {{student}}',
          studentRequiredForTxns: 'Select a student before downloading wallet transactions.',
          studentsLoadFailed: 'Could not load students. Check that the backend is running, then refresh.',
          studentsEmpty: 'No active students found.'
        },
        common: {
          filters: 'Filters',
          fromDate: 'From Date',
          toDate: 'To Date',
          from: 'From',
          to: 'To',
          filterByBranch: 'Filter by Branch',
          allBranches: 'All Branches',
          filterByCoach: 'Filter by Coach',
          allCoaches: 'All Coaches',
          filterByLevel: 'Filter by Level',
          selectLevel: 'Select Level',
          allLevels: 'All Levels',
          filterByBatch: 'Filter by Batch',
          selectBatch: 'Select Batch',
          allBatches: 'All Batches',
          search: 'Search',
          generate: 'Generate',
          generateReport: 'Generate Report',
          loadingReport: 'Loading report data…',
          failedToLoad: 'Failed to load report.',
          selectDateRange: 'Please select a date range',
          invalidDateFormat: 'Invalid date format. Please use {{format}}',
          invalidDateRange: 'Please select a valid date range.',
          selectFromToBatch: 'Please select From Date, To Date and Batch',
          enterEmail: 'Enter email address',
          enterEmailAlert: 'Please enter email address',
          emailSent: 'Report emailed successfully',
          emailFailed: 'Failed to send email',
          exportPdfFailed: 'Failed to export PDF',
          close: 'Close',
          present: 'Present',
          absent: 'Absent',
          upcoming: 'Upcoming',
          ongoing: 'Ongoing',
          past: 'Past',
          walkIn: 'Walk In',
          student: 'Student',
          studentId: 'Student ID',
          batch: 'Batch',
          level: 'Level',
          branch: 'Branch',
          coach: 'Coach',
          sessions: 'Sessions',
          status: 'Status',
          email: 'Email',
          phone: 'Phone',
          amount: 'Amount',
          plan: 'Plan',
          endDate: 'End Date',
          grandTotal: 'Grand Total',
          periodTotalAllPages: 'Period total (all pages)',
          unknown: 'Unknown',
          pageSize: 'Page size:',
          first: 'First',
          previous: 'Previous',
          next: 'Next',
          last: 'Last',
          showingSessionsRange: 'Showing {{start}} - {{end}} of {{total}} sessions',
          showingSessionsPage: 'Showing {{count}} on this page · {{total}} total · Page {{page}} of {{pages}}',
          pageOf: 'Page {{page}} of {{pages}}',
          records: '{{count}} records',
          downloadPageExcel: 'Download Page Excel',
          exportFullHint: 'Use Export PDF/CSV below for the full report (all pages), matching the on-screen summary.',
          individualSession: 'Individual Session',
          individual: 'Individual',
          sessionNotes: 'Session Notes',
          dateTime: 'Date / Time',
          sessionDateTime: 'Session Date & Time',
          sessionTopic: 'Session Topic',
          topic: 'Topic',
          gross: 'Gross',
          grossRevenue: 'Gross Revenue',
          academy: 'Academy',
          academyShare: 'Academy Share',
          academyEarnings: 'Academy Earnings',
          coachEarnings: 'Coach Earnings',
          attendanceRate: 'Attendance Rate',
          viewAttendance: 'View attendance details',
          clickViewAttendance: 'Click to view attendance details',
          sessionAttendance: 'Session Attendance',
          sessionDetails: 'Session Details',
          dateAndTime: 'Date & Time',
          batchAndBranch: 'Batch & Branch',
          students: 'Students',
          avg: 'Avg',
          average: 'average',
          commissionSplit: 'Commission Split',
          totalSessionCharges: 'Total Session Charges',
          charges: 'Charges',
          totals: 'Totals',
          errorPrefix: 'Error: {{message}}',
          noServerResponse: 'No response from server. Please check your connection.',
          presentCount: 'Present ({{count}}):',
          absentCount: 'Absent ({{count}}):',
          attendancePct: 'Attendance %',
          month: 'Month',
          type: 'Type',
          startTime: 'Start Time',
          endTime: 'End Time',
          sessionId: 'Session ID',
          date: 'Date',
          studentsFound: '{{count}} student found',
          studentsFound_other: '{{count}} students found',
          never: 'Never',
          amountDue: 'Amount Due',
          lastTopup: 'Last Top-up',
          batchesCol: 'Batches',
          balance: 'Balance',
          inScope: 'In scope',
          windowDays: 'Window (days)',
          overdue: 'OVERDUE',
          lowBalance: 'LOW BALANCE',
          expired: 'EXPIRED',
          expiringSoon: 'EXPIRING SOON',
          byStudent: 'By Student',
          byCoach: 'By Coach',
          choosePerson: 'Choose a {{mode}}…',
          allLevelsLabel: 'ALL Levels',
          allBatchesLabel: 'ALL Batches',
          noSessionsFilter: 'No sessions found for this filter.',
          selectPerson: 'Please select a {{mode}}.',
          selectValidDates: 'Please provide a valid From and To date.',
          confirm: 'Confirm'
        },
        exportBar: {
          sendReportViaEmail: 'Send report via email',
          exportPdf: 'Export PDF',
          generatingPdf: 'Generating PDF…',
          exportCsv: 'Export CSV',
          generatingCsv: 'Generating CSV…',
          sending: 'Sending…',
          sent: 'Sent!',
          sendEmail: 'Send Email',
          exportPdfFailed: 'Failed to export PDF. Please try again.',
          exportCsvFailed: 'Failed to export CSV. Please try again.',
          enterEmail: 'Please enter an email address',
          enterValidEmail: 'Please enter a valid email address',
          sendEmailFailed: 'Failed to send email. Please try again.',
          generateFirstHint: 'Generate a report first to enable export options.'
        },
        coachRevenue: {
          title: 'Coach Revenue & Commission',
          subtitle: 'Tracks session-wise earnings, commission percentages, and payout totals per coach — filterable by date range, branch, and coach.',
          totalSessions: 'Total Sessions',
          sessionMix: '{{individual}} Individual, {{batch}} Batch',
          noSessionsFound: 'No Sessions Found',
          noSessionsHint: 'No sessions found for the selected filters and date range',
          coachWiseEarnings: 'Coach-wise earnings',
          dayWiseEarnings: 'Day-wise earnings',
          daysCount: '{{count}} days',
          daysScrollHint: '{{count}} days · scroll for more'
        },
        sessionAttendance: {
          subtitle: 'Student-by-session attendance matrix for a selected batch — shows present/absent status',
          subtitleCharges: ' and per-session fee charge',
          subtitleEnd: ' for each student.'
        },
        studentAttendance: {
          subtitleCoach: 'Attendance summary for your sessions and roster students — filter by level or batch you coach.',
          subtitleAdmin: 'Student-level attendance summary across batches — total sessions, present/absent counts and attendance percentage, with filtering by branch, level, batch, and coach.',
          totalStudents: 'Total Students',
          avgAttendance: 'Avg Attendance',
          totalPresent: 'Total Present',
          totalAbsent: 'Total Absent',
          detailsTitle: 'Student Attendance Details',
          lowAttendanceLegend: 'Highlighted rows indicate attendance below 50%',
          empty: 'No student records found for the selected filters.'
        },
        academyRevenue: {
          subtitle: 'Monthly and branch-level gross revenue with academy vs coach commission breakdown for a date range.',
          monthlyBreakdown: 'Monthly Breakdown',
          branchBreakdown: 'Branch Breakdown',
          month: 'Month',
          sessions: 'Sessions',
          grossRevenue: 'Gross Revenue',
          academyCommission: 'Academy Commission',
          academyRevenue: 'Academy Revenue',
          coachPayout: 'Coach Payout',
          walletTopUps: 'Wallet Top-ups',
          walletBalanceHeld: 'Wallet Balance Held',
          empty: 'No revenue data found for the selected filters.'
        },
        paymentsDue: {
          subtitle: 'Point-in-time report of students with a wallet balance below the configured low-balance threshold (at risk) or a negative balance (overdue). No date range needed — reflects the current state of all wallet accounts.',
          paymentStatus: 'Payment Status',
          statusAll: 'All (Overdue + Low Balance)',
          statusOverdue: 'Overdue Only (negative balance)',
          statusLowBalance: 'At Risk Only (not overdue)',
          thresholdOverride: 'Low Balance Threshold Override',
          thresholdPlaceholder: 'Leave blank to use system default',
          thresholdHint: 'System default: {{amount}}',
          overdueStudents: 'Overdue Students',
          overdueSub: 'Negative wallet balance',
          totalAmountDue: 'Total Amount Due',
          totalDueSub: 'Sum owed by overdue students',
          belowThreshold: 'Below Threshold',
          belowSub: '{{pct}}% of students in scope',
          studentsInScope: 'Students in Scope',
          empty: 'No students match the selected filters — all wallets are above the threshold.',
          sessionsSince: 'Sessions Since',
          ratePerSession: 'Rate/Session'
        },
        sessionsSummary: {
          title: 'Sessions Summary',
          subtitle: 'View session date/time, level, batch, coach, and topic for a student or coach over a date range.',
          loadFailed: 'Failed to load sessions summary.'
        },
        monthlyStatements: {
          title: 'Monthly Statement Control',
          subtitle: 'Preview, send, and track session statement emails. Choose the statement period and a separate fee estimate period; already-paid sessions are excluded from the estimate.',
          periodSettings: 'Period settings',
          statementAndEstimate: 'Statement & fee estimate',
          periodDesc: 'Choose what activity goes on the statement, then the window used for upcoming fee estimates.',
          statementLabel: 'Statement:',
          statementPeriod: 'Statement period',
          statementPeriodDesc: 'Activity, wallet charges, and attendance.',
          estimatePeriod: 'Fee estimate period',
          estimatePeriodDesc: 'Upcoming sessions used to estimate fees on the statement.',
          thisMonth: 'This month',
          lastMonth: 'Last month',
          includeEstimate: 'Include upcoming fee estimate',
          estimateAuto: 'Auto-fill estimate from statement end',
          estimateFrom: 'Estimate from',
          estimateTo: 'Estimate to',
          loadPreview: 'Load Preview',
          loading: 'Loading…',
          loadFailed: 'Failed to load data.',
          totalStudents: 'Total Students',
          sent: 'Sent',
          pending: 'Pending',
          failed: 'Failed',
          notSent: 'Not Sent',
          searchStudents: 'Search students…',
          sendAll: 'Send All',
          refreshQueue: 'Refresh Queue',
          studentCol: 'Student',
          emailCol: 'Email',
          walletBalance: 'Wallet Balance',
          estimateFees: 'Est. Fees',
          queueStatus: 'Queue Status',
          actions: 'Actions',
          previewPdf: 'Preview PDF',
          downloadPdf: 'Download PDF',
          send: 'Send',
          cancelEmail: 'Cancel',
          retry: 'Retry',
          noStudents: 'No students found for this period.',
          allowPopups: 'Please allow pop-ups to preview the PDF.',
          accessDenied: 'Access denied — make sure you are logged in as ADMIN.',
          pdfLoadFailed: 'Failed to load PDF.',
          pdfDownloadFailed: 'Failed to download PDF.',
          pdfError: 'PDF Error: {{message}}',
          confirmSendAllTitle: 'Send Statements for {{range}}',
          confirmSendAllMsg: 'This will queue emails for {{count}} student{{suffix}}.{{skipNote}}',
          skipNote: ' {{count}} student{{suffix}} already SENT/PENDING will be skipped automatically.',
          sendAllLabel: 'Send All',
          queuedAll: 'Statements queued for all students.',
          sendAllFailed: 'Failed to send all.',
          confirmSendOneTitle: 'Send Statement for {{name}}',
          confirmSendOneMsg: 'Queue a statement email for {{name}} covering {{range}}?',
          queued: 'Queued ✓',
          queueFailed: 'Failed to queue.',
          cancelTitle: 'Cancel Email',
          cancelMsg: 'Cancel this pending email? The student will not receive the statement unless you send it again.',
          cancelLabel: 'Cancel Email',
          cancelled: 'Cancelled ✓',
          cancelFailed: 'Failed to cancel.',
          retryQueued: 'Retry queued ✓',
          retryFailed: 'Failed to retry.',
          statusSent: 'Sent',
          statusPending: 'Pending',
          statusProcessing: 'Processing',
          statusFailed: 'Failed',
          statusCancelled: 'Cancelled',
          months: {
            '1': 'January',
            '2': 'February',
            '3': 'March',
            '4': 'April',
            '5': 'May',
            '6': 'June',
            '7': 'July',
            '8': 'August',
            '9': 'September',
            '10': 'October',
            '11': 'November',
            '12': 'December'
          },
          queueing: 'Queueing…',
          sendAllWithCount: '🚀 Send All ({{count}})',
          includeFeeEstimateTitle: 'Include fee estimate & deposit reminder',
          includeFeeEstimateDesc: 'Uncheck to send activity statement only. Students with sufficient wallet credit still skip the payment reminder when this is checked.',
          resetDefault: 'Reset default',
          resetDefaultTitle: 'Reset estimate to the default range based on the statement end date',
          then: 'then',
          autoRange: 'Auto: {{range}} ({{count}} day{{daySuffix}})',
          customEstimateRange: 'Custom estimate range',
          estimateInvalid: 'Fee estimate period requires valid from/to dates.',
          estimateLabel: 'Estimate:',
          studentsWithActivity: '{{count}} student{{suffix}} with payment/enrollment activity',
          clearSearch: 'Clear search',
          noEmailOnFile: 'No email on file',
          sendStatementEmail: 'Send statement email',
          resendTooltip: 'Student already received this. Sends again.',
          resend: 'Resend',
          charges: 'Charges',
          closingBalance: 'Closing Balance',
          upcomingEst: 'Upcoming Est.',
          emailStatus: 'Email Status',
          notSentStatus: 'Not sent',
          archived: 'Archived',
          noStudentsActivity: 'No students with payment or enrollment activity found for {{range}}.',
          noStudentsMatch: 'No students match "{{term}}".',
          refresh: 'Refresh',
          refreshTitle: 'Refresh data',
          showingStudents: 'Showing {{filtered}} of {{total}} students',
          depositDue: 'Deposit Due',
          reportOnly: 'Report only',
          prepaid: 'Prepaid',
          balanceCredit: '{{amount}} Cr',
          balanceDue: '{{amount}} Due',
          amountDue: 'Due {{amount}}',
          estimateHidden: 'Est. {{amount}} hidden',
          estimateHiddenEmail: 'Est. {{amount}} hidden in email',
          sessionCountShort: '{{count}} sess',
          confirmSendAllMsg_one: 'This will queue emails for {{count}} student.{{skipNote}}',
          confirmSendAllMsg_other: 'This will queue emails for {{count}} students.{{skipNote}}',
          skipNote_one: ' {{count}} student already SENT/PENDING will be skipped automatically.',
          skipNote_other: ' {{count}} students already SENT/PENDING will be skipped automatically.',
          studentsWithActivity_one: '{{count}} student with payment/enrollment activity',
          studentsWithActivity_other: '{{count}} students with payment/enrollment activity',
          autoRange_one: 'Auto: {{range}} ({{count}} day)',
          autoRange_other: 'Auto: {{range}} ({{count}} days)',
          paymentReminder: 'Payment Reminder'
        }
      },
      calendar: {
        eyebrow: 'Manage',
        title: 'Session Calendar',
        subtitleStaff: 'All scheduled sessions at a glance',
        subtitleStudent: 'Your enrolled sessions and attendance',
        subtitleParent: 'Your child\'s enrolled sessions and attendance',
        subtitleCoach: 'Your sessions — filter by a roster student to see only your sessions for them',
        loadingSubtitle: 'Your enrolled sessions and attendance',
        allSessions: 'All Sessions',
        myBranches: 'My Branches',
        mySessions: 'My Sessions',
        pending: '{{count}} pending',
        completed: '{{count}} completed',
        today: 'Today',
        month: 'Month',
        week: 'Week',
        viewing: 'Viewing',
        searchName: 'Search name…',
        coaches: 'Coaches',
        students: 'Students',
        myRosterStudents: 'My roster students',
        noRosterStudents: 'No roster students yet',
        noResults: 'No results',
        student: 'student',
        coach: 'coach',
        batches: 'Batches:',
        showAll: 'Show all',
        batchFilterHintAll: 'Click a batch to show only its sessions · click more batches to combine',
        batchFilterHintSelected: '{{count}} batch selected · click another to add · click selected to remove',
        batchFilterHintSelected_other: '{{count}} batches selected · click another to add · click selected to remove',
        legendPending: 'Pending',
        legendCompleted: 'Completed',
        legendCancelled: 'Cancelled',
        legendBatchColors: 'Colours = batch',
        legendHoliday: '🏖 = academy holiday',
        legendHover: 'Hover a session for details',
        legendPresent: '✓ Present',
        legendAbsent: '✗ Absent',
        legendClickDay: 'Click a day for full list',
        legendClickAttendance: 'Click to open attendance',
        shortcuts: 'Shortcuts: ← → navigate · T today',
        shortcutsStaff: ' · Click a day to open sessions list',
        loadFailed: 'Failed to load sessions. Please try again.',
        noSessionsMonth: 'No sessions scheduled in {{month}} {{year}}',
        noSessionsHint: 'Try another month or check your batch filters',
        holiday: 'Holiday',
        months: {
          '1': 'January',
          '2': 'February',
          '3': 'March',
          '4': 'April',
          '5': 'May',
          '6': 'June',
          '7': 'July',
          '8': 'August',
          '9': 'September',
          '10': 'October',
          '11': 'November',
          '12': 'December'
        },
        session: 'Session',
        level: 'Level',
        sessionCoach: 'Session Coach',
        time: 'Time',
        sessionStatus: 'Session Status',
        attendance: 'Attendance',
        sessionNotes: 'Session Notes',
        present: 'Present',
        absent: 'Absent',
        marked: 'Marked',
        notMarked: 'Not marked',
        more: '+{{count}} more',
        showLess: 'Show less',
        sessionsCount: '{{count}} session',
        sessionsCount_other: '{{count}} sessions',
        sessAbbr: '{{count}} sess.',
        noSessionsDay: 'No sessions on this day.',
        prevYear: 'Previous year',
        nextYear: 'Next year',
        prev: 'Previous',
        next: 'Next',
        close: 'Close'
      },
      notificationsPage: {
        title: 'Notifications',
        subtitle: 'Alerts and reminders from your academy workspace.',
        markAllRead: 'Mark all read',
        empty: 'All caught up — no notifications yet.',
        pageOf: 'Page {{page}} of {{totalPages}} · {{total}} total',
        previous: 'Previous',
        next: 'Next',
        justNow: 'just now',
        minutesAgo: '{{count}}m ago',
        hoursAgo: '{{count}}h ago',
        daysAgo: '{{count}}d ago',
        subtitleValue: 'Actionable alerts for wallets, attendance, and privacy requests — open an item to jump to the right screen.',
        unreadBanner: 'You have {{count}} unread notification(s). Open one to review and act.',
        emptyHint: 'When students go below wallet threshold, absences are marked, or a DSAR is requested, alerts appear here with a direct link.'
      },
      form: {
        sessionDays: 'Session Days',
        selectOneDay: 'Select at least one day'
      },
      authPages: {
        forgotTitle: 'Forgot password',
        emailOrUsername: 'Email or Username',
        emailOrUsernamePlaceholder: 'you@example.com or your username',
        sending: 'Sending…',
        backToSignIn: 'Back to sign in',
        setPasswordTitle: 'Set your password',
        missingToken: 'Missing token in URL. Please use the latest sign-in link from your email.',
        newPassword: 'New password',
        confirmPassword: 'Confirm password',
        passwordRules: 'Must be 8+ chars, include 1 uppercase, 1 number, and 1 special (@ $ ! % * ? &).',
        setPasswordButton: 'Set Password',
        saving: 'Saving...',
        invalidToken: 'This sign-in link is invalid. Missing token in the URL.',
        passwordInvalid: 'Password must be at least 8 characters and include one uppercase, one number, and one special character.',
        passwordsMismatch: 'Passwords do not match.',
        passwordSetSuccess: 'Password set successfully. You can log in now.',
        linkInvalidExpired: 'Link is invalid or expired.'
      },
      roleSelect: {
        title: 'Select your role',
        noRoles: 'No roles found on your account.',
        contactAdmin: 'Please contact an administrator.',
        switchHint: 'You can switch your role anytime from the header menu.'
      },
      setupWizard: {
        welcomeTitle: 'Welcome to the app',
        welcomeSubtitle: 'Let\'s set up your academy in a few quick steps.',
        stepOf: 'Step {{current}} of {{total}}',
        footerHint: 'All settings can be changed anytime from the admin panel.',
        continue: 'Continue →',
        back: '← Back',
        saving: 'Saving…',
        finishing: 'Finishing…',
        finishSetup: 'Finish Setup →',
        enterDashboard: 'Enter Dashboard →',
        optional: '(optional)',
        saved: 'Saved',
        skipped: 'Skipped',
        configured: 'Configured',
        notConfiguredEmail: 'Not configured — System → Email Settings',
        notConfiguredWhatsApp: 'Not configured — System → WhatsApp',
        saveError: 'Something went wrong. Please try again.',
        regionalSaveError: 'Regional settings were not saved. Please try again.',
        emailSaveError: 'Could not save email settings.',
        whatsappSaveError: 'Could not save WhatsApp settings.',
        step1: {
          title: 'Name your academy',
          description: 'This appears in the header, login page, emails, and all PDF exports.',
          logo: 'Academy Logo',
          clickUpload: 'Click to upload logo',
          logoFormats: 'PNG, JPG, SVG · Max 2 MB',
          clickChange: 'Click to change',
          logoPreviewAlt: 'Logo preview',
          academyName: 'Academy Name',
          tagline: 'Tagline',
          nameRequired: 'Academy name is required.',
          logoTooLarge: 'Logo must be under 2 MB.',
          namePlaceholder: 'e.g. Kingside Chess School',
          taglinePlaceholder: 'e.g. Think deeper, play stronger'
        },
        step2: {
          title: 'Contact details',
          description: 'This public support contact is shown to students and parents. It is not the same as your outbound SMTP sending address (configured in the next optional step).',
          supportPhone: 'Support Phone Number',
          supportEmail: 'Support Email',
          phonePlaceholder: 'Support phone number',
          emailPlaceholder: 'e.g. support@myacademy.in',
          phoneHint: 'Shown on invoices, emails, and the student portal.',
          emailHint: 'Students can reach out to this address for help.',
          phoneRequired: 'Phone number is required.',
          emailRequired: 'Email address is required.',
          emailInvalid: 'Please enter a valid email address.'
        },
        step3: {
          title: 'Region & payments',
          description: 'Choose your academy\'s country and currency. Online gateways (Razorpay, Stripe, PayPal) are configured later under System → Payment Methods.'
        },
        step4: {
          title: 'Connect communications',
          description: 'Optional — configure outbound email and WhatsApp now, or skip and set them up later from the admin panel.',
          skipHint: 'Skipped items can always be configured later under System → Email Settings and System → WhatsApp.',
          emailSection: 'Outbound email (SMTP)',
          emailSectionHint: 'Used for sign-in links, reminders, statements, and alerts.',
          whatsappSection: 'WhatsApp (WATI)',
          whatsappSectionHint: 'Session reminders, wallet alerts, and operational messages.',
          whatsappNotLicensed: 'WhatsApp is not included in your license. You can skip this and upgrade later.',
          saveEmail: 'Save email settings',
          saveWhatsApp: 'Save WhatsApp settings',
          skipForNow: 'Skip for now',
          smtpHost: 'SMTP host',
          smtpPort: 'SMTP port',
          smtpUsername: 'SMTP username',
          smtpPassword: 'SMTP password',
          fromEmail: 'From email address',
          alreadySet: '(already set)',
          requiredToSave: 'Required to save',
          watiBaseUrl: 'WATI API base URL',
          channelNumber: 'Business channel number',
          watiToken: 'WATI bearer token',
          pasteToken: 'Paste token'
        },
        step5: {
          title: 'You\'re all set!',
          ready: '{{name}} is ready to go. You can update these details anytime from Settings → Branding.',
          academyLabel: 'Academy',
          supportLabel: 'Support',
          communicationsLabel: 'Communications',
          outboundEmail: 'Outbound email:',
          whatsapp: 'WhatsApp:'
        }
      },
      publicHelp: {
        backToLogin: '← Back to login',
        title: 'Help & Getting Started',
        subtitle: 'Quick answers before you sign in to {{name}}.',
        signingIn: 'Signing in',
        signingInBody: 'Use the username and password provided by your academy administrator. If you received a sign-in email, open the link in that message to set your password first, then return here to sign in.',
        forgotPassword: 'Forgot your password?',
        forgotPasswordBody: 'Use the <1>Forgot your password?</1> link on the login page. You will receive a password reset link at your registered email address.',
        forgotPasswordLink: 'Forgot your password?',
        contactAcademy: 'Contact your academy',
        contactAcademyBody: 'For account access or academy-specific questions, reach out directly:',
        email: 'Email:',
        phone: 'Phone:',
        privacy: 'Privacy',
        privacyBody: 'Read our <1>Privacy Policy</1> to understand how your data is handled on this platform.',
        privacyLink: 'Privacy Policy'
      },
      licenseInvalid: {
        heading: 'Software License Inactive',
        body: 'Access to the Attendance System has been suspended because the software license is no longer valid. Your data remains completely safe and intact.',
        restoreAccess: 'Restore Access',
        softwareProvider: 'Software Provider',
        tryAgain: 'Try Again',
        contactSupport: 'Contact Support',
        footer: '{{name}} — Attendance Management System'
      },
      guardianPublic: {
        inviteTitle: 'Guardian invitation',
        invalidLink: 'This guardian invitation link is invalid or has expired.',
        createAccount: 'Create guardian account',
        linkingFor: ' — you are linking as a guardian for ',
        confirmLink: 'Confirm guardian link',
        linkAccountFor: ' — link your account as a guardian for ',
        invitedEmail: 'Invited email: {{email}}',
        accountEmail: 'Account email: {{email}}',
        relationship: 'Your relationship to the student',
        relationships: {
          MOTHER: 'Mother',
          FATHER: 'Father',
          GUARDIAN: 'Guardian',
          GRANDPARENT: 'Grandparent',
          OTHER: 'Other'
        },
        passwordNote: '8+ characters, uppercase, number, and special character',
        confirmPassword: 'Confirm password',
        submitRegistration: 'Submit registration',
        submitting: 'Submitting…',
        acceptRequest: 'Accept & request access',
        decline: 'Decline',
        nameRequired: 'First name, last name, and username are required.',
        passwordInvalid: 'Password must be at least 8 characters and include one uppercase, one number, and one special character.',
        passwordsMismatch: 'Passwords do not match.',
        registrationSubmitted: 'Registration submitted. Please sign in after your academy approves guardian access.',
        registrationFailed: 'Registration failed. The link may be invalid or expired.',
        requestSubmitted: 'Request submitted. Please sign in after your academy approves guardian access.',
        invitationDeclined: 'Invitation declined.',
        processFailed: 'Could not process this invitation.'
      },
      managerForms: {
        editBatch: 'Edit Batch',
        createBatch: 'Create Batch',
        batchSubtitle: 'Configure batch details, schedule, and billing settings.',
        editLevel: 'Edit Level',
        addLevel: 'Add Level',
        levelSubtitle: 'Define level name and display order for your branch batches.',
        saveFailed: 'Save failed'
      },
      walletStaff: {
        topupTitle: 'Wallet Top-up',
        referenceOptional: 'Reference (optional)',
        clear: 'Clear',
        topup: 'Top-up',
        saving: 'Saving…',
        missingStudent: 'Missing student',
        invalidAmount: 'Enter valid amount',
        credited: 'Wallet credited: {{amount}}.',
        topupFailed: 'Top-up failed.',
        paymentMethodsLoadFailed: 'Could not load payment methods — using a fallback option.',
        refundTitle: 'Process Wallet Refund',
        refundImportant: 'Important:',
        refundWarning: 'This will deduct the refund amount from the student\'s wallet. Maximum refundable:',
        missingStudentInfo: 'Missing student information',
        invalidAmountPositive: 'Enter valid amount greater than 0',
        cannotRefundMore: 'Cannot refund more than current balance: {{amount}}',
        reasonRequired: 'Please provide a reason for the refund',
        refundSuccess: 'Refund of {{amount}} processed successfully.',
        refundFailed: 'Refund failed',
        refundAmount: 'Refund Amount',
        paymentMode: 'Payment Mode',
        maxPlaceholder: 'Max: {{amount}}',
        referenceTxn: 'Reference/Transaction Number',
        referencePlaceholder: 'Transaction ID, Cheque #, etc.',
        gatewayRefund: 'Also refund via payment gateway (Razorpay/Stripe/PayPal). Uncheck for wallet-only adjustment when the gateway was already refunded manually.',
        reasonForRefund: 'Reason for Refund',
        reasonPlaceholder: 'E.g., Student discontinuing, Excess payment, etc.',
        processing: 'Processing…',
        processRefund: 'Process Refund',
        refundConfirm: 'Refund {{amount}} from this wallet? This cannot be undone from here.',
        balanceLabel: 'Wallet Balance',
        refresh: 'Refresh',
        refund: 'Refund',
        transactions: 'Wallet Transactions',
        noTransactions: 'No transactions yet.',
        showAllTransactions: 'Show all wallet transactions',
        moreCount: ' ({{count}} more)',
        loadFailedPortal: 'Could not load your wallet. Please refresh the page.',
        loadFailedStaff: 'Could not load this student\'s wallet. They may be outside your branch scope.',
        grossCharge: 'Gross Charge:',
        discountApplied: 'Discount Applied:',
        reversalCredit: 'Reversal Credit:',
        netCharge: 'Net Charge:',
        reversed: '(Reversed)',
        refunded: 'REFUNDED',
        sessionReversed: '(Session Reversed)',
        modifiedOn: 'Modified on {{date}}',
        original: 'Original:',
        modify: 'Modify',
        revert: 'Revert',
        balanceShort: 'Bal:',
        sessionBatch: 'Session #{{sessionId}} (Batch #{{batchId}})',
        sessionOnly: 'Session #{{sessionId}}',
        modifyTxn: 'Modify Transaction',
        currentAmount: 'Current Amount',
        newAmount: 'New Amount *',
        newAmountPlaceholder: 'Enter new amount',
        reasonOptional: 'Reason (Optional)',
        modificationReasonPlaceholder: 'Enter modification reason',
        saveChanges: 'Save Changes',
        revertTxn: 'Revert Transaction',
        revertWarningTitle: 'Warning: This action cannot be undone!',
        revertWarningBody: 'This will permanently delete the transaction and reduce the wallet balance by {{amount}}.',
        txnDetails: 'Transaction Details',
        amountLabel: 'Amount:',
        typeLabel: 'Type:',
        reasonLabel: 'Reason:',
        dateLabel: 'Date:',
        revertReasonOptional: 'Reason for Reverting (Optional)',
        revertReasonPlaceholder: 'Enter reason for reverting this transaction',
        confirmRevert: 'Confirm Revert',
        validAmountRequired: 'Please enter a valid positive amount',
        modifyFailed: 'Failed to modify transaction',
        revertFailed: 'Failed to revert transaction'
      },
      guardiansSection: {
        title: 'Guardians',
        intro: 'Invite up to {{max}} parents or guardians by email. They choose their relationship when they accept.',
        canApprove: ' You can approve or reject pending requests.',
        contactAcademy: ' To remove an approved guardian link, contact your academy.',
        loading: 'Loading guardians…',
        loadFailed: 'Failed to load guardians.',
        enterEmail: 'Enter the guardian email address.',
        cancelInviteConfirm: 'Cancel this guardian invite?',
        cancelInviteFailed: 'Could not cancel invite.',
        approveFailed: 'Could not approve guardian.',
        revokeConfirm: 'Remove this guardian link? They will lose access after revocation.',
        revokeFailed: 'Could not revoke guardian.',
        rejectConfirm: 'Reject this guardian request?',
        rejectFailed: 'Could not reject guardian.',
        emptyTitle: 'No guardians linked yet',
        emptyBody: 'Invite a parent or guardian by email. They will register or confirm their account, then your academy must approve access.',
        linkedTitle: 'Linked guardians',
        pendingTitle: 'Pending invites',
        approve: 'Approve',
        reject: 'Reject',
        remove: 'Remove',
        emailPlaceholder: 'Guardian email address',
        expires: ' · expires {{date}}',
        expired: ' · expired',
        limitReached: 'Guardian limit reached ({{used}}/{{max}}).',
        limitRevokeHint: ' Cancel a pending invite or remove a link before inviting another.',
        limitCancelHint: ' Cancel a pending invite before inviting another.',
        status: {
          PENDING_APPROVAL: 'Pending approval',
          APPROVED: 'Approved',
          REJECTED: 'Rejected',
          REVOKED: 'Revoked'
        },
        inviteType: {
          NEW_REGISTRATION: 'New account',
          EXISTING_USER_LINK: 'Link existing account'
        },
        inviteButton: 'Invite guardian',
        resendButton: 'Resend guardian invite',
        sending: 'Sending…',
        inviteFailure: 'Could not send guardian invite.',
        resendFailure: 'Could not resend guardian invite.'
      },
      archiveStudent: {
        titleWithEnrollments: 'Archive student account',
        titleSimple: 'Archive student',
        hasEnrollments: '{{name}} has {{count}} active enrollment.',
        hasEnrollments_other: '{{name}} has {{count}} active enrollments.',
        chooseProceed: 'Choose how to proceed.',
        archiveSimple: 'Archive {{name}}? They will not be able to sign in. Their history is kept.',
        thisStudent: 'This student',
        thisStudentLower: 'this student',
        loadingEnrollments: 'Loading enrollment details…',
        activeEnrollments: 'Active enrollments',
        ongoing: 'ongoing',
        whatHappens: 'What should happen?',
        endEnrollmentsOnly: 'End enrollments only',
        endEnrollmentsOnlyDesc: 'Stop batch membership on the date below. Account stays active — student can still sign in.',
        endAndArchive: 'End enrollments & archive account',
        recommended: '(recommended)',
        endAndArchiveDesc: 'Typical when a student is leaving. Ends enrollments and revokes portal access.',
        archiveOnly: 'Archive account only',
        archiveOnlyDesc: 'Revoke sign-in but leave enrollments active. Attendance and billing continue until each enrollment ends.',
        endDateLabel: 'End date for all listed enrollments',
        archiveOnlyConfirm: 'I understand enrollments stay active, attendance can still be marked, and the student cannot sign in until reactivated.',
        processing: 'Processing…',
        continue: 'Continue',
        archive: 'Archive'
      },
      privacy: {
        metaLastUpdated: 'Last updated: July 2026 · Applies to: {{name}}',
        metaGdprFraming: ' · GDPR / UK GDPR framing',
        metaDpdpFraming: ' · DPDP Act 2023 framing',
        whoWeAre: {
          title: '1. Who we are',
          p1: 'This application (&quot;<strong>{{name}}</strong>&quot;) is operated by your chess academy (the &quot;Academy&quot;, &quot;we&quot;, &quot;us&quot;). The Academy acts as the <strong>data controller</strong> for personal data processed through this platform. Axeron Technologies (software vendor) typically acts as a <strong>processor</strong> only where it provides licensed software, remote support, or licence validation — see the Data Processing Agreement with your Academy.',
          p2: 'For privacy questions, contact the Academy administrator first.'
        },
        whatWeCollect: {
          title: '2. What personal data we collect',
          intro: 'We collect and process the following categories of personal data:',
          identity: '<strong>Identity data</strong> — first name, last name, date of birth (students); optional FIDE id / Elo where enabled',
          contact: '<strong>Contact data</strong> — email address, phone number, home address',
          account: '<strong>Account data</strong> — username, hashed password, assigned role',
          attendance: '<strong>Attendance &amp; enrollment data</strong> — sessions, batches, enrollments',
          financial: '<strong>Financial data</strong> — wallet balance, top-ups, fee references',
          guardian: '<strong>Guardian / parent links</strong> — where configured for minors',
          technical: '<strong>Technical data</strong> — login timestamps, IP in server logs, JWT in localStorage',
          noBiometric: 'We do <strong>not</strong> collect biometric data or full payment card numbers.',
          payments: 'Online payments are processed by the Academy&apos;s chosen gateway (<strong>Razorpay</strong>, <strong>Stripe</strong>, <strong>PayPal</strong>, and/or <strong>Mollie</strong>).'
        },
        legalBasis: {
          title: '3. Why we process your data (legal basis)',
          gdpr: {
            contract: '<strong>Art. 6(1)(b) contract</strong> — enrollments, attendance, wallet billing, coaching services.',
            legitimate: '<strong>Art. 6(1)(f) legitimate interests</strong> — security, fraud prevention, audit logs.',
            obligation: '<strong>Art. 6(1)(c) legal obligation</strong> — where the Academy must retain financial records under local tax law.',
            consent: '<strong>Art. 6(1)(a) / Art. 8 consent</strong> — where required for children (parental consent via guardian features).'
          },
          dpdp: {
            contract: '<strong>Performance of a contract</strong> — enrollments, attendance, wallet top-ups and fee deductions.',
            legitimate: '<strong>Legitimate interests</strong> — audit logs, fraud detection, secure operation.',
            obligation: '<strong>Legal obligation</strong> — retention of financial records as required under Indian Companies Act / GST (typically 8 years).'
          }
        },
        retention: {
          title: '4. How long we keep your data',
          activeAccount: '<strong>Active account data</strong> — while your account remains with the Academy.',
          attendance: '<strong>Attendance &amp; enrollment</strong> — typically 3 years after the last session (Academy policy may vary).',
          financialGdpr: '<strong>Financial records</strong> — as required by local tax law (often 6–10 years in EU Member States).',
          financialDpdp: '<strong>Financial records</strong> — as required by local tax law (typically 8 years under Indian GST / Income Tax rules).',
          auditLogs: '<strong>Audit logs</strong> — retained for about 12 months, then purged.'
        },
        sharing: {
          title: '5. Who we share your data with',
          intro: 'We do not sell personal data. Sharing is limited to:',
          gateways: '<strong>Payment gateways</strong> — Razorpay, Stripe, PayPal, and/or Mollie (Academy-configured).',
          messaging: '<strong>Messaging</strong> — email SMTP provider; WhatsApp via WATI/Meta when enabled by the Academy.',
          hosting: '<strong>Hosting</strong> — the server where this self-hosted instance runs (chosen by the Academy; EU academies should host in the EU).',
          licence: '<strong>Licence validation</strong> — installation fingerprint / licence checks may contact Axeron&apos;s licence relay (see vendor DPA).',
          staff: '<strong>Academy staff</strong> — role-scoped access only.'
        },
        cookies: {
          title: '6. Cookies and local storage',
          intro: 'Essential storage only:',
          jwt: '<strong>JWT</strong> (localStorage) — session.',
          consentFlag: '<strong>Cookie consent flag</strong> (localStorage).',
          gatewaySession: '<strong>Pending gateway session id</strong> (sessionStorage) during payment redirects.',
          noAds: 'No advertising or third-party analytics cookies.'
        },
        rights: {
          gdprTitle: '7. Your rights under GDPR / UK GDPR',
          gdprP1: 'Depending on your location you may have rights to access, rectification, erasure, restriction, portability, and objection, and rights related to automated decision-making where applicable.',
          gdprP2: 'Contact the Academy administrator to exercise rights. Academies should respond without undue delay and within one month (extendable as permitted by law).',
          gdprP3: 'You may complain to your local supervisory authority (for example CNIL, BfDI, AP, ICO in the UK).',
          dpdpTitle: '7. Your rights under the DPDP Act, 2023',
          dpdpP1: 'As a data principal under the Digital Personal Data Protection Act, 2023 (India), you may request access, correction, erasure (subject to legal retention), and related rights via the Academy administrator (response target: 30 days).',
          dpdpP2: 'Complaints may be directed to the Data Protection Board of India once fully operational, or to the Academy administrator.'
        },
        security: {
          title: '8. Data security',
          https: 'HTTPS/TLS in transit (when deployed with TLS).',
          bcrypt: 'Passwords hashed with bcrypt.',
          roles: 'Role-restricted access.',
          encryption: 'Gateway secrets encrypted at rest when <code>PAYMENT_SECRETS_ENCRYPTION_KEY</code> is set.'
        },
        changes: {
          title: '9. Changes',
          body: 'We may update this page; the &quot;Last updated&quot; date will change. Continued use after an update means you accept the revised policy.'
        },
        footer: {
          line1: '{{name}} · Privacy Policy · Reflects practices of the Academy operating this instance.',
          line2: 'Vendor legal pack: see <code>legal/PRIVACY-POLICY.txt</code> and <codeEu>legal/PRIVACY-POLICY-EU.txt</codeEu> (lawyer review required before customer signature).'
        }
      },
      terms: {
        agreement: {
          title: '1. Agreement',
          body: 'These Terms govern use of <strong>{{name}}</strong>, academy operations software licensed by Axeron Technologies and operated by your academy (the &quot;Academy&quot;). By signing in, you agree to these terms and the <privacyLink>Privacy Policy</privacyLink>.'
        },
        licence: {
          title: '2. Licence and acceptable use',
          body: 'The Academy grants authorised users (admins, managers, coaches, students, and guardians) access to the platform for academy management only. You must not attempt to circumvent licence limits, share credentials unsafely, or misuse payment gateways.'
        },
        fees: {
          title: '3. Fees and payments',
          body1: 'Student wallets and membership fees are charged by the Academy, not by Axeron Technologies. Online gateways (Razorpay, Stripe, PayPal, Mollie) are configured by the Academy under its own merchant accounts.',
          euLicence: 'Licence fees charged by Axeron Technologies to European academies are quoted in the currency stated on the order, exclusive of applicable VAT unless otherwise stated on the invoice.',
          nonEuLicence: 'Software licence fees charged by Axeron Technologies are quoted in the currency agreed on the order form, exclusive of tax unless stated otherwise.'
        },
        support: {
          title: '4. Support',
          body: 'Support is provided by the Academy administrator for day-to-day use, and by Axeron Technologies for licensing and product defects under the Academies&apos; EULA / support agreement.',
          euHours: ' Support responses for European customers are targeted within EU business hours (CET/CEST) where possible.',
          indiaHours: ' Support hours for India customers are typically Mon–Sat 10:00–18:00 IST unless otherwise agreed.'
        },
        liability: {
          title: '5. Liability',
          body: 'The software is provided as described in the End User License Agreement between Axeron Technologies and the Academy. The Academy is responsible for configuration, user access, and compliance with local laws (including data protection).'
        },
        governingLaw: {
          title: '6. Governing law',
          euBody: 'For European academy customers, commercial terms may be subject to EU/EEA consumer and commercial rules as set out in the signed order / EULA addendum. Dispute resolution is as specified in that agreement. Vendor legal documents under <code>legal/</code> marked for lawyer review before signature.',
          indiaBody: 'Unless a signed agreement says otherwise, vendor licence agreements for India customers are governed by the laws of India (Gujarat), as stated in the EULA and Terms maintained by Axeron Technologies.'
        },
        footer: '{{name}} · Terms of Service · See also the vendor EULA in the software licence pack.'
      },
      legal: {
        backToLogin: '← Back to login',
        termsTitle: 'Terms of Service',
        privacyTitle: 'Privacy Policy',
        lastUpdated: 'Last updated: July 2026 · Applies to: {{name}}'
      },
      notifications: {
        categories: {
          general: 'General',
          billing: 'Billing',
          attendance: 'Attendance',
          privacy: 'Privacy',
          chess: 'Chess',
          chessChallenge: 'Challenge',
          chessGame: 'Game',
          chessFairPlay: 'Fair play',
          chessCorrespondence: 'Correspondence',
        },
        content: {
          new: 'New',
          open: 'Open',
          fallbackTitle: 'Notification',
          lowWallet: {
            title: 'Low wallet balance',
            body: '{{studentName}} is at {{balance}} (threshold {{threshold}}). Top up or collect payment.',
            action: 'Top up this wallet'
          },
          lowWalletDigest: {
            title: '{{count}} students have low wallets',
            body: '{{count}} students are below the wallet threshold. Open Payments Due to review and collect.',
            action: 'Open payments due'
          },
          absence: {
            title: 'Absence recorded',
            body: '{{studentName}} was marked absent for the session on {{sessionInfo}}.',
            action: 'Open attendance'
          },
          dsar: {
            title: 'Data access request (DSAR)',
            body: '{{name}} (user #{{userId}}, {{username}}) requested a copy of their personal data. Export from Reports → Data Export.',
            action: 'Export their data'
          },
          chessGeneric: {
            CHESS_CHALLENGE: 'Chess challenge',
            CHESS_GAME: 'Chess game',
            CHESS_FAIR_PLAY: 'Fair play',
            CHESS_CORR: 'Correspondence chess',
            CHESS: 'Chess',
            bodyFallback: 'Open the Playing section for details.'
          }
        }
      }
    }
  },
  de: {
    translation: {
      brand: {
        tagline: 'Verwalten. Überwachen. Wachsen.'
      },
      nav: {
        branches: 'Filialen',
        levels: 'Stufen',
        batches: 'Gruppen',
        students: 'Schüler',
        coaches: 'Trainer',
        managers: 'Manager',
        enrollments: 'Einschreibungen',
        sessions: 'Stunden',
        calendar: 'Kalender',
        reports: 'Berichte',
        wallet: 'Mein Guthaben',
        faq: 'FAQ',
        dashboard: 'Übersicht',
        help: 'Hilfe',
        finances: 'Finanzen',
        manage: 'Verwalten',
        people: 'Personen',
        academySetup: 'Akademie-Einrichtung',
        operations: 'Betrieb',
        system: 'System',
        feesBilling: 'Gebühren & Abrechnung',
        myAccount: 'Mein Konto',
        settings: 'Einstellungen',
        discounts: 'Rabatte',
        sessionsSummary: 'Stundenübersicht',
        monthlyStatements: 'Monatsabrechnungen',
        reportSettings: 'Berichteinstellungen',
        atRiskStudents: 'Gefährdete Schüler',
        walletTopup: 'Guthaben aufladen',
        paymentEnforcement: 'Zahlungsdurchsetzung',
        paymentMethods: 'Zahlungsmethoden',
        regionalSettings: 'Regionale Einstellungen',
        productionReadiness: 'EU-Produktionsbereitschaft',
        playingPlatform: 'Spielplattform',
        playingHub: 'Spiel-Hub',
        fairPlay: 'Fairplay',
        playNow: 'Gegen Computer',
        liveGames: 'Live-Partien',
        myGames: 'Meine Partien',
        challenges: 'Herausforderungen',
        leaderboard: 'Rangliste',
        classroom: 'Klassenzimmer',
        classroomOverview: 'Übersicht',
        classroomLive: 'Live-Unterricht',
        classroomZoom: 'Zoom-Verbindung',
        classroomSettings: 'Klassenzimmer-Einstellungen',
        emailSettings: 'E-Mail-Einstellungen',
        whatsapp: 'WhatsApp',
        aiCoachSettings: 'Ask AI Coach',
        branding: 'Markenauftritt',
        license: 'Lizenz',
        holidays: 'Feiertage',
        notifications: 'Benachrichtigungen',
        auditLogs: 'Prüfprotokolle',
        mySessions: 'Meine Stunden',
        myEnrollments: 'Meine Einschreibungen',
        sessionStatement: 'Stundenabrechnung',
        parentPortal: 'Elternportal',
        home: 'Start',
        myChild: 'Mein Kind',
        overview: 'Übersicht',
        attendance: 'Anwesenheit',
        more: 'Mehr'
      },
      classroom: {
        hub: {
          title: 'Klassenzimmer',
          subtitleStaff: 'Online-Unterricht mit Zoom — starten Sie hier und hosten oder treten Sie unter Live bei.',
          subtitleLearner: 'Treten Sie den Live-Online-Kursen Ihrer Akademie hier bei.',
          zoomFirstBanner: 'Klassenzimmer ist Zoom-first. Videomeetings nutzen das Zoom-Konto der Akademie; ChessDesk erstellt und öffnet das Meeting für Coaches und Schüler.',
          liveTitle: 'Live-Unterricht',
          liveDescStaff: 'Sehen Sie heutige Online-Stunden und Start / Host / Beitreten, sobald Zoom verbunden ist.',
          liveDescLearner: 'Sehen Sie kommende Online-Kurse und treten Sie bei, wenn Ihr Coach das Meeting startet.',
          zoomTitle: 'Zoom-Verbindung',
          zoomDesc: 'Verbinden Sie Zoom, damit Kursvideo in ChessDesk läuft.',
          zoomDescConnected: 'Zoom ist verbunden. Zugangsdaten verwalten oder trennen.',
          settingsTitle: 'Klassenzimmer-Einstellungen',
          settingsDesc: 'Standardwerte für Warteraum, Stummschaltung, Kamera und Startbenachrichtigungen.',
          open: 'Öffnen',
          zoomConnectedBanner: 'Zoom ist für diese Akademie verbunden. Öffnen Sie Live-Unterricht zum Starten oder Beitreten.',
          zoomNotConnectedBanner: 'Zoom ist noch nicht verbunden. Verbinden Sie das Zoom-Konto der Akademie, um Start / Host / Beitreten zu aktivieren.',
          externalModeBanner: 'Klassenzimmer nutzt externe Meeting-Links. Fügen Sie die URL an der Stunde ein und nutzen Sie Start / Beitreten unter Live oder Stunden.',
          statusLoadFailed: 'Klassenzimmer-Status konnte nicht geladen werden.',
          zoomStatusUnknown: 'Zoom-Verbindung konnte nicht geprüft werden. Aktualisieren oder Live-Unterricht öffnen.',
        },
        joinAction: {
          stillWaiting: "Noch warten — Ihr Coach hat noch nicht gestartet.",
          notStartedYet: 'Noch nicht gestartet',
          notStartedHint: 'Ihr Coach hat diesen Online-Unterricht noch nicht gestartet. Tippen Sie, um erneut zu prüfen.',
          checking: 'Prüfen…',
          checkFailed: 'Klassenzimmer-Status konnte nicht geprüft werden. Erneut versuchen oder Live-Unterricht öffnen.',
        },
        live: {
          title: 'Live-Unterricht',
          subtitleStaff: 'Starten oder hosten Sie Zoom für Ihnen zugewiesene Online-Stunden.',
          subtitleAdmin: 'Starten oder hosten Sie Zoom für Online-Stunden der gesamten Akademie.',
          subtitleLearner: 'Treten Sie bei, wenn Ihr Coach das Klassenzimmer öffnet.',
          zoomPendingHint: 'Zoom ist noch nicht verbunden. Sie können Stunden öffnen; Start erklärt, was fehlt, bis ein Admin Zoom verbindet.',
          zoomPendingHintLearner: 'Zoom ist noch nicht verbunden. Beitreten erscheint hier, wenn Ihr Coach einen Online-Kurs startet.',
          connectZoomCta: 'Zoom verbinden',
          todayHeading: 'Heute & demnächst',
          todayHint: 'Online-Stunden mit Zoom-Link erscheinen hier.',
          todayHintLive: 'Geeignete Online-Stunden in den nächsten 48 Stunden. Start erstellt ein Meeting in ChessDesk; Host tritt als Host bei; Beitreten als Teilnehmer.',
          todayHintLearner: 'Geeignete Online-Stunden in den nächsten 48 Stunden. Beitreten öffnet den Teilnehmerlink, wenn Ihr Coach den Kurs startet.',
          openSessions: 'Stunden öffnen',
          refresh: 'Aktualisieren',
          emptyTitle: 'Noch kein Live-Klassenzimmer',
          emptyStaff: 'Keine geeigneten Online-Stunden in den nächsten 48 Stunden. Nutzen Sie „Stunden öffnen“ zum Planen. Start funktioniert unter Stunden, sobald Zoom verbunden ist.',
          emptyLearner: 'Wenn Ihr Coach einen Online-Kurs startet, erscheint hier Beitreten. Den Stundenplan finden Sie auch unter Stunden.',
          emptyStaffConnected: 'Keine Online-Stunden in diesem Zeitraum. Unter Stunden planen, dann hier Start oder Host nutzen.',
          emptyLearnerConnected: 'Gerade kein Live-Kurs. Wenn Ihr Coach einen startet, erscheint hier Beitreten.',
          chipStart: 'Start',
          chipJoin: 'Beitreten',
          chipHost: 'Host',
          statusLoadFailed: 'Klassenzimmer-Status konnte nicht geladen werden. Bitte aktualisieren.',
          zoomConnectedHint: 'Zoom ist verbunden. Start / Host öffnet als Host, Beitreten wenn ein Link vorhanden ist.',
          start: 'Start',
          startZoom: 'Zoom starten',
          host: 'Host',
          restart: 'Neu starten',
          join: 'Beitreten',
          joinSession: 'Stunde beitreten',
          starting: 'Öffnet…',
          startFailed: 'Zoom-Klassenzimmer konnte nicht gestartet werden.',
          openInZoomFailed: 'Kein Zoom-Host-Link verfügbar. Host versuchen oder Admin bitten, Zoom erneut zu verbinden.',
          meetingReady: 'Meeting bereit',
          meetingNotCreated: 'Meeting noch nicht erstellt',
          end: "Unterricht beenden",
          ending: "Beenden…",
          endFailed: "Unterricht konnte nicht beendet werden.",
          meetingStatusCreating: 'Wird erstellt…',
          meetingStatusStarted: 'Gestartet',
          meetingStatusFailed: 'Fehlgeschlagen — Start erneut versuchen',
          meetingStatusEnded: 'Beendet',
          meetingStatusUnknown: 'Status unbekannt',
          unnamedSession: 'Stunde',
          selectChildTitle: 'Kind auswählen',
          selectChildHint: 'Wählen Sie im Elternportal-Kopf ein Kind, um dessen Live-Kurse zu sehen.',
          noChildrenTitle: 'Keine verknüpften Kinder',
          noChildrenHint: 'Mit diesem Elternkonto ist noch kein genehmigtes Kind verknüpft.',
          childrenLoadFailed: 'Verknüpfte Kinder konnten nicht geladen werden. Seite aktualisieren oder erneut versuchen.',
          externalModeHint: 'Externe Meeting-Links werden verwendet. Start öffnet den Stundenlink; Beitreten den Teilnehmerlink, falls vorhanden.',
        },
        meeting: {
          title: 'Klassenzimmer-Meeting',
          subtitle: 'Videokurs in ChessDesk',
          preparing: 'Zoom-Klassenzimmer wird vorbereitet…',
          preparingHint: 'Meeting wird eingerichtet. Das dauert meist nur wenige Sekunden.',
          joinFailed: 'Eingebettetes Klassenzimmer konnte nicht beigetreten werden.',
          notStarted: 'Der Host hat den Unterricht noch nicht gestartet. Warten Sie, bis Ihr Coach das Klassenzimmer öffnet.',
          back: 'Zurück',
          backToLive: 'Live-Unterricht',
          cancelled: "Diese Sitzung wurde abgebrochen.",
          ended: "Dieser Unterricht ist beendet.",
          startAsHost: 'Als Host starten',
          redirectingExternal: 'Externes Meeting wird geöffnet…',
          openExternalNow: 'Meeting jetzt öffnen',
          askAdminZoomSetup: 'Bitten Sie Ihren Admin, Zoom unter Klassenzimmer → Zoom zu konfigurieren.',
          embedUnavailable: 'Einbettungsdaten für das Meeting sind nicht verfügbar.',
          hostCredentialsMissing: 'Host-Anmeldedaten fehlen. Prüfen Sie Klassenzimmer → Einstellungen → Zoom-Host-E-Mail.',
          hostOpenFailed: 'Klassenzimmer konnte nicht geöffnet werden. Bitte erneut versuchen.',
          hostTokenInvalid: 'Klassenzimmer konnte nicht geöffnet werden. Bitte erneut versuchen.',
          hostTokenInvalidStaff: 'Klassenzimmer konnte nicht geöffnet werden. Bitte erneut versuchen.',
          noExternalLink: 'Für diese Stunde ist kein externer Meeting-Link hinterlegt.',
          invalidSession: 'Ungültige Stunde.',
          leave: 'Meeting verlassen',
          retry: 'Erneut versuchen',
          openInZoom: 'In Zoom-App öffnen',
          inMeetingHint: 'Video läuft in ChessDesk. Verlassen, wenn Sie fertig sind.',
          connectingHint: 'Verbinden…',
          connectedHint: 'Live in ChessDesk',
          livePill: 'Live',
          zoomToolbarHint: 'Nutzen Sie die Leiste im Video zum Einschalten von Mikrofon und Kamera.',
          cameraOffTitle: 'Kamera ist aus',
          cameraOffHint: 'Tippen Sie unten auf das Kamera-Symbol, um sich der Klasse zu zeigen.',
          waitingRoomAdmit: 'Zulassen',
          waitingRoomAdmitAll: 'Alle zulassen',
          waitingRoomView: 'Teilnehmer',
          waitingRoomDismiss: 'Warteraum-Hinweis schließen',
          waitingRoomOne: '{{name}} ist im Warteraum.',
          waitingRoomMany: '{{count}} Teilnehmer sind im Warteraum: {{names}}.',
          waitingRoomManyMore: '{{count}} Teilnehmer sind im Warteraum ({{names}} und weitere).',
          waitingForHostPill: 'Warten auf Host',
          learnerWaitingHint: 'Sie warten darauf, dass der Host Sie zulässt. Lassen Sie diese Seite geöffnet.',
          waitingForHostAutoRetry: 'Wir versuchen es automatisch erneut, sobald Ihr Coach den Unterricht startet.',
          leaveHostConfirm: 'Verlassen ohne den Unterricht zu beenden? Das Zoom-Meeting bleibt live, bis Sie „Unterricht beenden“ wählen.',
          endHostConfirm: 'Unterricht für alle beenden? Teilnehmer werden aus dem Zoom-Meeting entfernt.',
          leaving: 'Meeting wird verlassen…',
          joinTimedOut: 'Das Klassenzimmer öffnet sich zu lange. Erneut versuchen oder In Zoom-App öffnen.',
          hostConflictWarning: 'Ein anderer Host ist möglicherweise schon im Meeting. Nur eine Person sollte Zulassen steuern.',
          fixZoomSetup: 'Zoom Meeting-SDK einrichten →',
          sdkLoadFailed: 'Videoklassenzimmer konnte nicht geladen werden. Erneut versuchen oder In Zoom-App öffnen.',
          signatureInvalid: 'Zoom hat die Meeting-Signatur abgelehnt. Unter Klassenzimmer → Zoom die Development Client-ID und das Secret für eingebettetes Video eintragen.',
        },
        settings: {
          title: 'Klassenzimmer-Einstellungen',
          subtitle: 'Standard-Meeting-Optionen, wenn ChessDesk ein Zoom-Klassenzimmer erstellt.',
          infoBanner: 'Diese Einstellungen werden lokal als Entwurf gespeichert, bis Klassenzimmer-Einstellungen serverseitig verfügbar sind.',
          infoBannerLive: 'Diese Standards gelten, wenn ChessDesk ein Zoom-Meeting für eine Stunde erstellt.',
          defaultProvider: 'Standard-Videoanbieter',
          defaultProviderNote: 'Zoom ist der Hauptanbieter. Externer Link ist ein Fallback für eigene Meeting-URLs.',
          providerExternalLink: 'Externer Meeting-Link',
          hostUserEmail: 'Zoom-Host-E-Mail',
          hostUserEmailNote: 'Optional. Zoom-Konto-E-Mail zum Erstellen von Meetings. Leer = E-Mail des Stunden-Coaches.',
          hostEmailInvalid: 'Gültige Zoom-Host-E-Mail eingeben (oder leer lassen für Coach-E-Mail).',
          waitingRoom: 'Warteraum aktivieren',
          waitingRoomNote: 'Teilnehmer warten, bis der Host sie zulässt.',
          muteOnEntry: 'Teilnehmer beim Eintritt stummschalten',
          allowStudentCameraDefault: 'Schülerkamera standardmäßig erlauben',
          allowStudentCameraNote: 'Hosts können dies in Zoom pro Meeting ändern.',
          notifyOnStart: 'Schüler benachrichtigen, wenn der Kurs startet',
          notifyOnStartNote: 'Wenn aktiviert, erhalten eingeschriebene Schüler und verknüpfte Eltern eine In-App-Benachrichtigung mit Join-Link, wenn ein Coach das Klassenzimmer startet.',
          saveDraft: 'Entwurf speichern',
          save: 'Einstellungen speichern',
          saving: 'Speichern…',
          saveSuccess: 'Klassenzimmer-Einstellungen gespeichert.',
          saveFailed: 'Klassenzimmer-Einstellungen konnten nicht gespeichert werden.',
          loadFailed: 'Klassenzimmer-Einstellungen konnten nicht geladen werden.',
          retryLoad: 'Erneut versuchen',
          draftSaved: 'Entwurf in diesem Browser gespeichert.',
          draftSaveFailed: 'Entwurf konnte in diesem Browser nicht gespeichert werden.',
          gotoZoom: 'Zoom-Verbindung'
        },
        zoom: {
          title: 'Zoom-Verbindung',
          subtitle: 'Verknüpfen Sie das Zoom-Konto der Akademie, damit ChessDesk Meetings erstellen und öffnen kann.',
          infoBanner: 'Erstellen Sie eine Server-to-Server-OAuth-App im Zoom Marketplace. Account-ID und Client-Zugangsdaten eintragen, dann Verbinden. Erforderliche Scopes: meeting:write:meeting:admin, meeting:read:meeting:admin, user:read:user:admin und user:read:token:admin (für eingebettetes Host-Video). Separat eine General-App mit Meeting SDK Embed für Development Client-ID + Secret. Für lokal http://localhost:3000 zur Allow-List hinzufügen.',
          statusConnected: 'Verbunden',
          statusConnectedHint: 'ChessDesk kann Meetings erstellen und eingebettetes Video mit diesem Zoom-Konto hosten.',
          statusNotConnected: 'Nicht verbunden',
          statusNotConnectedHint: 'Ein gespeicherter Entwurf verbindet Zoom nicht. OAuth / API muss noch serverseitig angebunden werden.',
          statusNotConnectedHintLive: 'Zugangsdaten eingeben und „Mit Zoom verbinden“ klicken. ChessDesk prüft sie bei Zoom und speichert sie sicher auf dem Server.',
          serverToServerHeading: 'Server-to-Server OAuth',
          serverToServerHint: 'Empfohlen für akademieeigene Zoom-Konten. Das Client-Secret gehört in Produktion nicht in den Browser — diese Entwurfs-UI dient nur der Einrichtung.',
          serverToServerHintLive: 'Empfohlen für akademieeigene Zoom-Konten. Das Client-Secret wird auf dem Server verschlüsselt und nie wieder im Browser angezeigt.',
          accountId: 'Account-ID',
          accountIdNote: 'Aus Zoom Marketplace → Ihre App → App Credentials.',
          clientId: 'Client-ID',
          clientSecret: 'Client-Secret',
          clientSecretNote: 'Wird nicht im lokalen Entwurf gespeichert. Nur eingeben, wenn die API-Verbindung bereit ist.',
          clientSecretConfiguredNote: 'Ein Client-Secret ist bereits gespeichert. Leer lassen zum Behalten, oder neues Secret eingeben.',
          clientSecretConnectNote: 'Zum Verbinden erforderlich. Wird verschlüsselt auf dem Server gespeichert — nach dem Speichern nicht mehr sichtbar.',
          saveDraft: 'Entwurf speichern',
          saveCredentials: 'Zugangsdaten speichern',
          clearDraft: 'Entwurf löschen',
          connectOAuth: 'Mit Zoom verbinden',
          connectDisabledTitle: 'Backend-Verbindung noch nicht verfügbar',
          connectDisabledHint: '„Mit Zoom verbinden“ bleibt deaktiviert, bis der Server Zugangsdaten austauschen und Tokens sicher speichern kann.',
          connectHintLive: 'Verbinden tauscht Zugangsdaten mit Zoom aus und markiert die Akademie nur bei Erfolg als verbunden.',
          connectNeedsFields: 'Account-ID, Client-ID und Client-Secret eingeben, dann „Mit Zoom verbinden“ klicken.',
          draftSaved: 'Entwurf in diesem Browser gespeichert. Zoom ist weiterhin nicht verbunden.',
          draftSavedServer: 'Zugangsdaten auf dem Server gespeichert. Zoom ist erst verbunden, wenn Sie „Mit Zoom verbinden“ klicken.',
          draftSavedConnected: 'Zugangsdaten aktualisiert. Verbindungsstatus bleibt bis zum erneuten Verbinden gleich.',
          draftSaveFailed: 'Zoom-Einstellungen konnten nicht gespeichert werden.',
          draftCleared: 'Entwurf gelöscht.',
          loadFailed: 'Zoom-Einstellungen konnten nicht geladen werden.',
          validationRequired: 'Account-ID und Client-ID sind erforderlich.',
          secretRequired: 'Client-Secret ist zum Verbinden erforderlich (oder zuerst speichern).',
          connectSuccess: 'Erfolgreich mit Zoom verbunden.',
          connectFailed: 'Verbindung mit Zoom fehlgeschlagen. Zugangsdaten und App-Aktivierung prüfen.',
          disconnect: 'Trennen',
          disconnected: 'Zoom getrennt. Zugangsdaten bleiben zum erneuten Verbinden erhalten.',
          disconnectFailed: 'Zoom konnte nicht getrennt werden.',
          webhookHeading: 'Ereignis-Abonnements (Webhooks)',
          meetingSdkHeading: 'Eingebettetes Video (Meeting SDK)',
          meetingSdkHint: 'Optional für In-App-Video. Bis die Zoom-App veröffentlicht ist: Client-ID und Secret unter App Credentials → Development eintragen.',
          meetingSdkDevRequired: 'Eingebettetes Beitreten braucht Development Meeting-SDK-Zugangsdaten hier.',
          meetingSdkClientId: 'Meeting-SDK Client-ID (Entwicklung)',
          meetingSdkClientIdNote: 'Aus derselben Zoom-App → App Credentials → Registerkarte Development.',
          meetingSdkClientSecret: 'Meeting-SDK Client-Secret (Entwicklung)',
          meetingSdkClientSecretNote: 'Development Client-Secret — nur zum Signieren eingebetteter Tokens.',
          meetingSdkClientSecretConfiguredNote: 'Ein Meeting-SDK-Secret ist gespeichert. Leer lassen zum Behalten.',
          webhookHint: 'In Zoom Marketplace → Feature → Event Subscriptions die URL eintragen und meeting.started / meeting.ended / meeting.deleted abonnieren.',
          webhookSecret: 'Webhook-Secret-Token',
          webhookSecretNote: 'Aus Zoom Event Subscriptions. Erforderlich zur Prüfung von Meeting-Lebenszyklus-Webhooks.',
          webhookSecretConfiguredNote: 'Ein Webhook-Secret ist gespeichert. Leer lassen zum Behalten.',
          clearCredentials: 'Zugangsdaten löschen',
          clearConfirm: 'Gespeicherte Zoom Account-ID, Client-ID und Client-Secret für diese Akademie entfernen?',
          cleared: 'Zoom-Zugangsdaten gelöscht.',
          clearFailed: 'Zoom-Zugangsdaten konnten nicht gelöscht werden.',
          clearing: 'Löschen…',
          saving: 'Speichern…',
          connecting: 'Verbinden…',
          disconnecting: 'Trennen…',
          gotoSettings: 'Klassenzimmer-Einstellungen →'
        }
      },
      playing: {
        hubTitle: 'Spielplattform',
        hubSubtitle: 'Spiele Schach, fordere Vereinskollegen heraus und steigere dich in der Akademie-Rangliste.',
        playTitle: 'Schachbrett',
        playSubtitle: 'Mach deinen Zug',
        loadFailed: 'Spielplattform-Daten konnten nicht geladen werden.',
        moveFailed: 'Ungültiger oder abgelehnter Zug.',
        resignFailed: 'Aufgabe nicht möglich.',
        yourRating: 'Dein Rating',
        record: 'S {{w}} · R {{d}} · N {{l}}',
        leaderboard: 'Rangliste',
        leaderboardSubtitle: 'Beste Spieler dieser Akademie nach internem Rating.',
        viewLeaderboard: 'Rangliste anzeigen',
        playComputer: 'Gegen Computer spielen',
        playComputerDesc: 'Trainiere gegen eine eingebaute Engine. Höhere Stufen spielen stärkere Taktik.',
        computerLevel: 'Schwierigkeit',
        ratedGame: 'Gewertete Partie',
        casualGame: 'Freundschaftspartie',
        playAsWhite: 'Als Weiß spielen',
        playAsBlack: 'Als Schwarz spielen',
        challenges: 'Eingehende Herausforderungen',
        challengePlayer: 'Spieler herausfordern',
        challengeSubtitle: 'Sende eine Partieeinladung an ein anderes Akademie-Mitglied.',
        noChallenges: 'Keine ausstehenden Herausforderungen.',
        accept: 'Annehmen',
        decline: 'Ablehnen',
        myGames: 'Meine Partien',
        myGamesSubtitle: 'Deine letzten Partien auf dieser Plattform.',
        noGames: 'Noch keine Partien — starte eine im Spiel-Hub.',
        players: 'Spieler',
        player: 'Spieler',
        correspondence: 'Zeitmodus',
        liveClock: 'Live-Uhr',
        correspondenceMode: 'Fernschach ({{n}} Tage pro Zug)',
        daysPerMove: '{{n}} Tag(e) pro Zug',
        correspondenceHint: 'Jeder Spieler hat {{n}} Tage für einen Zug. Keine tickende Uhr.',
        moveDeadline: 'Zug bis {{when}}',
        type: 'Typ',
        statusLabel: 'Status',
        open: 'Öffnen',
        gameInfo: 'Partie',
        check: 'Schach!',
        resign: 'Aufgeben',
        backHub: 'Zurück zum Spiel-Hub',
        noMovesYet: 'Noch keine Züge.',
        rating: 'Rating',
        games: 'Partien',
        noRatingsYet: 'Noch keine gewerteten Partien.',
        create: 'Erstellen',
        join: 'Beitreten',
        score: 'Punkte',
        searchPlayers: 'Spieler suchen',
        challenge: 'Herausfordern',
        challengeSent: 'Herausforderung gesendet.',
        challengeFailed: 'Herausforderung konnte nicht gesendet werden.',
        reason: 'Ergebnis',
        liveGames: 'Live-Partien',
        liveGamesSubtitle: 'Laufende Akademie-Partien in Echtzeit zuschauen.',
        viewLive: 'Jetzt zuschauen',
        spectate: 'Zuschauen',
        spectateTitle: 'Zuschauen',
        spectatingHint: 'Nur-Lese-Ansicht — du kannst keine Figuren ziehen.',
        noLiveGames: 'Gerade keine Live-Partien.',
        noLiveGamesHint: 'Wenn Vereinskollegen eine Partie starten, erscheint sie hier.',
        allSpeeds: 'Alle',
        noLiveGamesForSpeed: 'Keine Live-Partien in dieser Tempoklasse',
        tryAnotherSpeedFilter: 'Andere Tempoklasse wählen oder Alle anzeigen.',
        noOpenSeeksExpired: 'Offene Gesuche abgelaufen — neues anlegen oder herausfordern.',
        noChallengesExpired: 'Eingehende Herausforderungen abgelaufen',
        noChallengesExpiredHint: 'Sende eine neue Herausforderung oder warte auf die nächste.',
        challengeExpired: 'Herausforderung abgelaufen',
        openSharedTip: 'Tipp öffnen',
        boardSettingsLoadFailed: 'Bretteinstellungen konnten nicht geladen werden.',
        prefsSaveFailed: 'Einstellungen konnten nicht mit dem Server synchronisiert werden.',
        prevPly: 'Vorheriger Zug',
        nextPly: 'Nächster Zug',
        coachPlyOf: 'Zug {{current}} / {{total}}',
        fairPlayShowAllLevels: 'Alle Stufen zeigen',
        expiresIn: 'Läuft ab in {{when}}',
        incomingChallenges: 'Eingehende Herausforderungen',
        confirmTitle: 'Bitte bestätigen',
        sendChallenge: 'Herausforderung senden',
        inboxPartialLoadFailed: 'Einige Herausforderungslisten konnten nicht geladen werden. Bitte aktualisieren.',
        bye: 'Freilos',
        format: 'Format',
        teamA: 'Name Mannschaft A',
        teamB: 'Name Mannschaft B',
        chooseTeam: 'Team wählen',
        selectTeam: 'Team auswählen…',
        team: 'Team',
        fairPlay: 'Fairplay',
        fairPlaySubtitle: 'Beendete Mensch-gegen-Mensch-Partien auf ungewöhnliche Bedenkzeiten und engine-ähnliches Spiel prüfen.',
        fairPlayReview: 'Fairplay-Prüfung',
        fairPlayMinLevel: 'Mindeststufe',
        fairPlayEmpty: 'Keine markierten Partien',
        fairPlayEmptyHint: 'Beendete Partien oberhalb der Schwelle erscheinen hier.',
        fairPlayMedian: 'Median-Bedenkzeit',
        fairPlayFastMoves: 'Schnelle Züge',
        fairPlayStreak: 'Schnelle Serie',
        fairPlayCase: 'Fall',
        fairPlayCases: 'Fälle',
        fairPlayNotes: 'Mitarbeiter-Notizen',
        fairPlayAddNote: 'Notiz hinzufügen',
        fairPlayNotePlaceholder: 'Beobachtung, Belege, Follow-up…',
        fairPlaySaveNote: 'Notiz speichern',
        fairPlayOpenCase: 'Fall öffnen / aktualisieren',
        fairPlayBanPlayer: 'Von der Spielplattform sperren',
        fairPlayBanReason: 'Sperrgrund',
        fairPlayBanDays: 'Tage (leer = unbefristet)',
        fairPlayLiftBan: 'Sperre aufheben',
        fairPlayActiveBans: 'Aktive Sperren',
        fairPlayCaseStatus: 'Fallstatus',
        fairPlayResolution: 'Entscheidung',
        fairPlaySubject: 'Betroffener',
        fairPlayModeration: 'Moderation',
        fairPlayNoteFailed: 'Notiz konnte nicht gespeichert werden.',
        fairPlayCaseFailed: 'Fall konnte nicht aktualisiert werden.',
        fairPlayBanFailed: 'Sperre konnte nicht verhängt werden.',
        fairPlayReportFailed: 'Meldung konnte nicht aktualisiert werden.',
        fairPlayAppealFailed: 'Einspruch konnte nicht aktualisiert werden.',
        fairPlayCaseStatusLabel: {
          OPEN: 'Offen',
          WATCHING: 'Beobachtung',
          ACTIONED: 'Maßnahmen',
          CLOSED: 'Geschlossen',
          DISMISSED: 'Abgewiesen'
        },
        fairPlayResolutionLabel: {
          NONE: 'Keine',
          WARNING: 'Verwarnung',
          BAN: 'Sperre',
          DISMISSED: 'Abgewiesen'
        },

        moveTiming: 'Zugzeiten',
        thinkTime: 'Bedenkzeit',
        openBoard: 'Brett öffnen',
        runEngineReview: 'Engine-Prüfung starten',
        engineReviewRunning: 'Stockfish läuft…',
        engineReviewFailed: 'Engine-Prüfung fehlgeschlagen.',
        engineReviewAutoPending: 'Automatische Anti-Cheat-Prüfung ist in der Warteschlange…',
        engineReviewAutoRunning: 'Automatische Anti-Cheat analysiert jeden Zug…',
        engineReviewAutoDone: 'Automatische Zug-für-Zug-Anti-Cheat abgeschlossen.',
        engineReviewAutoFailed: 'Automatische Anti-Cheat fehlgeschlagen — Sie können unten erneut starten.',
        engineFlag: 'Flag',

        engineMatchTop1: 'Top-1-Treffer',
        engineMatchTop3: 'Top-3-Treffer',
        avgCpLoss: 'Ø CPL',
        engineLevel: 'Engine-Flag',
        engineMatch: 'Treffer',
        cpLoss: 'CPL',
        jumpToPly: 'Springen',
        thinkSparkTitle: 'Bedenkzeit-Profil',
        thinkSparkHint: 'Gelb/rot = sehr schnelle Züge nach der Eröffnung.',
        analysisQuick: 'Schnell (d10)',
        analysisDeep: 'Tief (d16)',
        analysisCloud: 'Cloud-Analyse',
        analysisDepth: 'Analysetiefe',
        clubReview: 'Club-Analyse',
        playAgain: 'Nochmal spielen',
        prevMistake: 'Vorheriger Fehler',
        nextMistake: 'Nächster Fehler',
        reviewReady: 'Analyse bereit',
        retryClubReview: 'Club-Analyse erneut',
        clubReviewNoMoves: 'Keine Züge — Club-Analyse nicht verfügbar. Nutzen Sie die lokale Analyse.',
        clubReviewTooShort: 'Partie zu kurz für automatische Club-Analyse. Schnell- oder Cloud-Analyse versuchen.',
        clubReviewFailed: 'Club-Analyse fehlgeschlagen. Erneut versuchen oder Schnell-/Cloud-Analyse.',
        clubReviewPending: 'Club-Analyse bereitet Zugbewertungen vor…',
        clubReviewSlow: 'Club-Analyse dauert länger als üblich. Sie können lokal analysieren.',
        clubReviewUnavailable: 'Club-Analyse für diese Partie nicht verfügbar. Schnell- oder Cloud-Analyse nutzen.',
        clubReviewRequestFailed: 'Club-Analyse konnte nicht angestoßen werden.',
        cancelAnalysis: 'Abbrechen',
        cloudAnalysisFailed: 'Cloud-Analyse fehlgeschlagen. Ist Stockfish auf dem Server verfügbar?',
        fairPlayFastSummary: 'W schnell {{white}} · S schnell {{black}}',
        moveNumber: '#',
        san: 'SAN',
        noGamesMatchFilter: 'Keine Partien für diesen Filter',
        noGamesMatchFilterHint: 'Anderen Filter wählen oder zu Alle zurück.',
        noGamesMatchSearch: 'Keine Partien für diese Suche',
        noGamesMatchSearchHint: 'Anderen Namen versuchen oder Suche löschen.',
        clearSearch: 'Suche löschen',
        teamNamesRequired: 'Bitte zwei unterschiedliche Teamnamen eingeben.',
        filterAll: 'Alle',
        yourMove: 'Ihr Zug',
        correspondenceGames: 'Fernschach',
        fairPlayLevel: {
          LOW: 'Unauffällig',
          WATCH: 'Beobachten',
          HIGH: 'Hoch'
        },
        customTime: 'Benutzerdefiniert',
        minutes: 'Minuten',
        increment: 'Inkrement (Sek.)',
        yourColor: 'Deine Farbe',
        colorRandom: 'Zufällig',
        colorRandomSide: 'Zufällige Seite',
        colorWhite: 'Weiß',
        colorBlack: 'Schwarz',
        offerDraw: 'Remis anbieten',
        acceptDraw: 'Remis annehmen',
        declineDraw: 'Remis ablehnen',
        opponentOffersDraw: 'Gegner bietet Remis an',
        youOfferedDraw: 'Remis angeboten — warte auf den Gegner',
        drawFailed: 'Remisangebot konnte nicht aktualisiert werden.',
        abort: 'Partie abbrechen',
        abortFailed: 'Diese Partie konnte nicht abgebrochen werden.',
        abortLeaveFailed: 'Warte-Herausforderung konnte nicht abgebrochen werden. Sie kann für den Gegner bis zum Ablauf sichtbar bleiben.',
        whiteToMove: 'Weiß am Zug',
        blackToMove: 'Schwarz am Zug',
        yourTurn: 'Du bist am Zug',
        engineThinking: 'Engine denkt nach…',
        moves: 'Züge',
        resignConfirm: 'Diese Partie aufgeben?',
        playNow: 'Jetzt spielen',
        resume: 'Fortsetzen',
        hubRatingHint: 'Steige in der Akademie-Rangliste mit gewerteten Partien.',
        challengeShortcut: 'Vereinskollegen einladen',
        myGamesShortcut: 'Fortsetzen oder ansehen',
        challengesHint: 'Wird live aktualisiert, wenn dich jemand herausfordert.',
        noChallengesHint: 'Fordere einen Vereinskollegen heraus, um eine Live-Partie zu starten.',
        noGamesHint: 'Spiele gegen den Computer oder fordere jemanden heraus, um die Liste zu füllen.',
        noRatingsHint: 'Spiele ein paar gewertete Partien, um auf der Rangliste zu erscheinen.',
        noPlayersFound: 'Keine Spieler gefunden',
        noPlayersFoundHint: 'Probiere einen anderen Namen oder Benutzernamen.',
        searchPlayersPlaceholder: 'Nach Name oder Benutzername suchen',
        levelEasy: 'Leicht',
        levelHard: 'Schwer',
        recordShort: 'S/R/N',
        waitingOpponent: 'Warte auf Gegner…',
        premoveHint: 'Du kannst vormerken, während du wartest.',
        moveDeadlineShort: 'Zug bis {{when}}',
        waitingToJoinTitle: 'Warte auf Beitritt des Gegners',
        waitingToJoinHint: 'Warte darauf, dass {{name}} beitritt… Uhren starten bei Annahme.',
        waitingToJoinInviteeHint: 'Annehmen, um die Partie gegen {{name}} zu starten.',
        premoveArmed: 'Vormerkzug bereit: {{from}}→{{to}} — Tippen Sie auf Clear oder die Felder',
        clearPremove: 'Vormerkung löschen',
        banQuickLinkBlocked: 'Nicht verfügbar während einer Sperre',
        banConfirm: 'Diesen Spieler von der Spielplattform sperren?',
        ratedConfirm: 'Dies ist eine gewertete Partie und beeinflusst Ihre Wertung. Fortfahren?',
        challengeAcceptFailed: 'Herausforderung konnte nicht angenommen werden. Erneut versuchen.',
        challengeDeclineFailed: 'Ablehnen fehlgeschlagen. Erneut versuchen.',
        sharedAnalysis: 'Geteilte Analyse von {{name}}',
        loading: 'Laden…',
        banBanner: 'Sie sind von der Spielplattform gesperrt.',
        banBannerReason: 'Grund: {{reason}}',
        banBannerUntil: 'Bis {{when}}',
        banBannerIndefinite: 'Diese Sperre hat kein Enddatum.',
        timeControl: {
          label: 'Bedenkzeit',
          daysPerMove: '{{count}}T/Zug'
        },
        countdown: {
          zeroSeconds: '0s',
          daysHours: '{{d}}T {{h}}Std',
          days: '{{d}}T',
          hoursMinutes: '{{h}}Std {{m}}Min',
          hours: '{{h}}Std',
          minutes: '{{m}}Min',
          seconds: '{{s}}s'
        },
        shareAnalysisBtn: 'Analyse teilen',
        shareAnalysisDone: 'Analyse mit Zuschauern geteilt',
        shareAnalysisFailed: 'Analyse konnte nicht geteilt werden.',
        bookMoves: 'Buch',
        bookMovesHint: 'Vorschläge aus dem Club-Buch (nur gegen Computer)',
        exploreOpening: 'Erkunden',
        pickOpening: 'Club-Eröffnung laden',
        pickOpeningPlaceholder: 'Eröffnung wählen…',
        openingLoaded: '{{name}} geladen',
        openingLoadFailed: 'Eröffnung konnte nicht geladen werden',
        liveNowPeek: 'Jetzt live',
        autoQueen: 'Immer zur Dame umwandeln',
        showBookHints: 'Eröffnungsbuch-Hinweise',
        myStudents: 'Meine Schüler',
        rosterLoadFailed: 'Schülerliste konnte nicht geladen werden.',
        noRosterStudents: 'Noch keine Schüler in deiner Liste',
        typeToSearchOrRoster: 'Name suchen oder oben einen Schüler wählen.',
        drillOn: 'Buch-Drill an',
        drillOff: 'Buch-Drill',
        drillScore: '{{correct}} / {{attempts}} Buchzüge',
        drillOutOfBook: 'Außerhalb des Buchs',
        drillCorrect: 'Buchzug!',
        drillWrong: 'Nicht im Club-Buch{{hint}}',
        pgnPackBusy: 'PGN wird erstellt…',
        pgnPackEmpty: 'Noch keine beendeten Partien zum Export.',
        pgnPackFailed: 'PGN-Paket konnte nicht geladen werden.',
        accuracyChip: '{{pct}}% Genauigkeit',
        accuracy: 'Genauigkeit',
        recentAccuracy: '{{pct}}% aktuelle Genauigkeit',
        recentAccuracyGames: '{{n}} Partien',
        vsYouRecord: 'gegen dich {{w}}–{{d}}–{{l}}',
        openEvent: 'Event öffnen',
        recentOpponents: 'Letzte Gegner',
        gameLoadedOnCoachBoard: 'Partie #{{id}} geladen',
        gameLoadFailed: 'Partie konnte nicht aufs Trainerbrett geladen werden.',
        noActiveBans: 'Keine aktiven Sperren.',
        liftBanConfirm: 'Diese Sperre aufheben?',
        hub: 'Zurück zum Spiel-Hub',

        currentRound: 'Aktuelle Runde',
        allowSpectators: 'Zuschauer erlauben',
        spectatorsOff: 'Ohne Zuschauer',
        fairPlayCaseAutoOpened: 'Automatisch (Engine)',
        showCoordinates: 'Koordinaten',
        leaderboardGames: 'Partien',
        flipBoard: 'Brett drehen',
        copyPgn: 'PGN kopieren',
        downloadPgn: 'PGN herunterladen',
        copyGameLink: 'Partielink kopieren',
        copied: 'Kopiert!',
        copyFailed: 'PGN konnte nicht kopiert werden.',
        rematch: 'Revanche',
        rematchSent: 'Herausforderung gesendet',
        rematchSentHint: 'Revanche-Herausforderung gesendet — warte auf deinen Gegner.',
        rematchFailed: 'Revanche-Herausforderung konnte nicht gesendet werden.',
        rematchInboxLoadFailed: 'Revanche-Herausforderungen konnten nicht geladen werden. Erneut versuchen.',
        offerTakeback: 'Zugrücknahme',
        acceptTakeback: 'Zugrücknahme annehmen',
        declineTakeback: 'Zugrücknahme ablehnen',
        takebackFailed: 'Zugrücknahme konnte nicht aktualisiert werden.',
        opponentOffersTakeback: 'Gegner bittet um Zugrücknahme',
        youOfferedTakeback: 'Zugrücknahme angeboten — warte auf den Gegner',
        typeMove: 'Zug eingeben',
        typeMovePlaceholder: 'e2e4 oder Nf3',
        playMove: 'Los',
        chat: 'Chat',
        chatPlaceholder: 'Nachricht an Gegner…',
        chatSend: 'Senden',
        chatEmpty: 'Noch keine Nachrichten.',
        chatReadOnly: 'Zuschauen',
        chatLoadFailed: 'Chat konnte nicht geladen werden.',
        chatSendFailed: 'Nachricht konnte nicht gesendet werden.',
        chatOffline: 'Sie sind offline — verbinden Sie sich erneut, um zu chatten.',
        quickMatch: 'Schnellpartie',
        quickMatchDesc: 'Stelle eine Suche ein oder tritt einer offenen Lobby mit derselben Bedenkzeit bei.',
        findOpponent: 'Gegner finden',
        seekWaiting: 'Warte auf einen Gegner…',
        cancelSeek: 'Suche abbrechen',
        seekFailed: 'Suche konnte nicht aktualisiert werden.',
        cancelSeekFailed: 'Suche konnte nicht abgebrochen werden.',
        acceptSeekFailed: 'Dieser Suche konnte nicht beigetreten werden.',
        seekMatched: 'Gegner gefunden — Brett wird geöffnet…',
        outgoingChallenges: 'Ausgehende Herausforderungen',
        outgoingChallengesHint: 'Herausforderungen, die du gesendet hast und die noch warten.',
        noOutgoingChallenges: 'Keine ausgehenden Herausforderungen.',
        noOutgoingChallengesHint: 'Wenn du jemanden herausforderst, erscheint er hier, bis angenommen oder abgebrochen.',
        cancelChallenge: 'Abbrechen',
        cancelChallengeFailed: 'Diese Herausforderung konnte nicht abgebrochen werden.',
        challengeAccepted: 'Herausforderung angenommen',
        challengeDeclined: 'Herausforderung abgelehnt',
        challengeCancelled: 'Herausforderung abgebrochen',
        accuracy: 'Genauigkeit',
        moveQuality: {
          best: 'Bester Zug',
          brilliant: 'Brillant',
          good: 'Gut',
          inaccuracy: 'Ungenauigkeit',
          mistake: 'Fehler',
          blunder: 'Patzer'
        },
        continueGame: 'Partie läuft',
        activeGames: 'Laufend',
        pastGames: 'Beendet',
        noPastGames: 'Noch keine beendeten Partien.',
        review: 'Nachspielen',
        cancel: 'Abbrechen',
        noPairings: 'Noch keine Paarungen',
        noPairingsHint: 'Paarungen erscheinen, nachdem der Organisator eine Runde gestartet hat.',
        choosePromotion: 'Umwandlung wählen',
        challengeSentHint: 'Warte darauf, dass <profile>{{name}}</profile> im Spiel-Hub annimmt.',
        challengeAnother: 'Jemand anderen herausfordern',
        typeToSearch: 'Spieler finden',
        typeToSearchHint: 'Gib mindestens 2 Zeichen ein, um in der Akademie zu suchen.',
        levelDepth: 'Engine-Tiefe ~{{depth}}',
        abortConfirm: 'Diese Partie abbrechen?',
        soundOn: 'Ton an',
        soundOff: 'Ton aus',
        reviewingPly: 'Zug {{ply}} / {{total}}',
        you: 'Du',
        reviewMoves: 'Züge nachspielen',
        showResult: 'Ergebnis',
        dismissResult: 'Schließen',
        backToLive: 'Live',
        retry: 'Erneut versuchen',
        plyStart: 'Erster Zug',
        plyPrev: 'Vorheriger Zug',
        plyNext: 'Nächster Zug',
        plyEnd: 'Letzter Zug',
        searchFailed: 'Spielersuche fehlgeschlagen. Bitte erneut versuchen.',
        searchFailedHint: 'Prüfe deine Verbindung und gib eine neue Suche ein.',
        outsideTop: 'Außerhalb der aktuellen Ranglisten-Seite',
        reconnecting: 'Verbindung verloren — stelle wieder her und synchronisiere…',
        timeoutFailed: 'Zeitüberschreitung konnte nicht beansprucht werden. Bitte aktualisieren.',
        firstMoveYourTurn: 'Mache deinen ersten Zug — {{seconds}}s (danach laufen die Uhren)',
        firstMoveWaiting: 'Warte auf den ersten Zug — {{seconds}}s',
        engineFailed: 'Computerzug fehlgeschlagen. Aktualisiere, falls das Brett hängt.',
        challengeIncoming: 'Neue Herausforderung',
        analyze: 'Analysieren',
        reanalyze: 'Neu analysieren',
        analyzing: 'Analysiere… {{pct}}%',
        hideAnalysis: 'Analyse ausblenden',
        analysis: 'Engine-Bewertung',
        bestMove: 'Bester Zug',
        levelBand: {
          beginner: 'Anfänger',
          intermediate: 'Fortgeschritten',
          club: 'Vereinsstärke',
          strong: 'Stark'
        },
        gameType: {
          VS_COMPUTER: 'gg. Computer',
          CASUAL: 'Freundschaft',
          RATED: 'Gewertet'
        },
        resultReason: {
          CHECKMATE: 'Schachmatt',
          RESIGN: 'Aufgabe',
          STALEMATE: 'Patt',
          AGREEMENT: 'Remis durch Einigung',
          THREEFOLD: 'Dreifache Stellungswiederholung',
          FIFTY_MOVE: '50-Züge-Regel',
          INSUFFICIENT: 'Unzureichendes Material',
          TIMEOUT: 'Zeitüberschreitung',
          ABORT: 'Abgebrochen',
          COMPUTER: 'Computer'
        },
        status: {
          ACTIVE: 'Laufend',
          WHITE_WON: 'Weiß hat gewonnen',
          BLACK_WON: 'Schwarz hat gewonnen',
          DRAW: 'Remis',
          ABORTED: 'Abgebrochen',
          PENDING: 'Wartet auf Start'
        },

        acceptAppeal: 'Einspruch annehmen',
        acceptRematch: 'Revanche annehmen',
        adjudicateConfirm: 'Weiß-Sieg für diese Paarung setzen?',
        adjudicateConfirmBlack: 'Schwarz-Sieg für diese Paarung setzen?',
        adjudicateConfirmDraw: 'Remis für diese Paarung setzen?',
        appealFailed: 'Einspruch konnte nicht gesendet werden.',
        appealPending: 'Einspruch in Prüfung',
        appealPlaceholder: 'Warum die Sperre aufgehoben werden sollte…',
        appealRejectNotePrompt: 'Notiz zur Ablehnung (optional)',
        appealRejectedNote: 'Einspruch abgelehnt',
        applyFen: 'FEN anwenden',
        applyPgn: 'PGN laden',
        animationSpeed: 'Animation',
        animationSlow: 'Langsam',
        animationNormal: 'Normal',
        animationFast: 'Schnell',
        banStatusUnknown: 'Sperrstatus nicht verfügbar',
        boardSettings: 'Bretteinstellungen',
        boardSettingsHint: 'Synchronisierte Einstellungen für jedes Brett',
        boardTheme: 'Brett-Thema',
        boardThemes: { classic: 'Klassisch', green: 'Grün', brown: 'Braun', blue: 'Blau' },
        openings: {
          'alekhine_defence': "Alechin-Verteidigung",
          'benoni_benko_path': "Benoni / Benko path",
          'birds_opening': "Bird's Opening",
          'caro_kann_defence': "Caro-Kann-Verteidigung",
          'caro_kann_advance': "Caro-Kann — Advance",
          'caro_kann_classical': "Caro-Kann — Classical",
          'dutch_defence': "Dutch Defence",
          'english_opening': "English Opening",
          'english_1_e5': "English — 1…e5",
          'english_1_e6': "English — 1…e6",
          'english_anglo_indian': "English — Anglo-Indian",
          'english_symmetrical': "English — Symmetrical",
          'evans_gambit': "Evans-Gambit",
          'french_defence': "Französische Verteidigung",
          'french_advance': "French — Advance",
          'french_classical': "French — Classical",
          'french_exchange': "French — Exchange",
          'giuoco_piano': "Giuoco Piano",
          'giuoco_piano_open': "Giuoco Piano — Open",
          'giuoco_piano_c3': "Giuoco Piano — c3",
          'giuoco_piano_cxd4': "Giuoco Piano — cxd4",
          'giuoco_piano_d4': "Giuoco Piano — d4",
          'indian_defence': "Indian Defence",
          'indian_3_nf3': "Indian — 3.Nf3",
          'italian_game': "Italienische Partie",
          'kings_gambit': "Königsgambit",
          'kings_indian_defence': "King's Indian Defence",
          'kings_knight_opening': "Königsspringer-Eröffnung",
          'kings_knight_two_knights_path': "King's Knight — Two Knights path",
          'kings_pawn': "Königsbauer",
          'london_system': "Londoner System",
          'london_system_nc6': "Londoner System — Nc6",
          'london_system_c5': "Londoner System — c5",
          'london_system_main_line': "Londoner System — main line",
          'modern_defence': "Moderne Verteidigung",
          'nimzo_indian_defence': "Nimzo-Indian Defence",
          'nimzo_indian_path': "Nimzo-Indian path",
          'nimzo_larsen_attack': "Nimzo–Larsen Attack",
          'open_game': "Offene Partie",
          'owens_defence': "Owen-Verteidigung",
          'petrov_defence': "Petrow-Verteidigung",
          'philidor_defence': "Philidor-Verteidigung",
          'pirc_defence': "Pirc-Verteidigung",
          'qgd': "Abgelehntes Damengambit",
          'queens_gambit': "Damengambit",
          'queens_gambit_accepted': "Damengambit Accepted",
          'queens_gambit_declined': "Abgelehntes Damengambit",
          'queens_gambit_declined_3_nf3': "Abgelehntes Damengambit — 3.Nf3",
          'queens_gambit_declined_bg5': "Abgelehntes Damengambit — Bg5",
          'queens_gambit_declined_london_hybrid': "Abgelehntes Damengambit — London hybrid",
          'queens_indian_nimzo_path': "Queen's Indian / Nimzo path",
          'queens_pawn': "Damenbauer",
          'queens_pawn_game': "Damenbauereröffnung",
          'ruy_lopez': "Spanische Partie",
          'ruy_lopez_morphy': "Spanische Partie — Morphy",
          'ruy_lopez_morphy_defence': "Spanische Partie — Morphy Defence",
          'ruy_lopez_morphy_bb3': "Spanische Partie — Morphy, Bb3",
          'ruy_lopez_morphy_closed': "Spanische Partie — Morphy, Closed",
          'ruy_lopez_morphy_re1': "Spanische Partie — Morphy, Re1",
          'ruy_lopez_morphy_b5': "Spanische Partie — Morphy, b5",
          'ruy_lopez_morphy_d6': "Spanische Partie — Morphy, d6",
          'r_ti_opening': "Réti Opening",
          'r_ti_1_nf6': "Réti — 1…Nf6",
          'r_ti_1_c5': "Réti — 1…c5",
          'r_ti_1_d5': "Réti — 1…d5",
          'scandinavian_defence': "Skandinavische Verteidigung",
          'scandinavian_2_nf6': "Scandinavian — 2…Nf6",
          'scandinavian_2_qxd5': "Scandinavian — 2…Qxd5",
          'sicilian_defence': "Sizilianische Verteidigung",
          'sicilian_open': "Sizilianisch Offen",
          'sicilian_2_nf3': "Sicilian — 2.Nf3",
          'sicilian_2_nc6': "Sicilian — 2…Nc6",
          'sicilian_2_d6': "Sicilian — 2…d6",
          'sicilian_2_e6': "Sicilian — 2…e6",
          'sicilian_classical_path': "Sicilian — Classical path",
          'sicilian_dragon': "Sicilian — Dragon",
          'sicilian_dragon_classical': "Sicilian — Dragon, Classical",
          'sicilian_dragon_yugoslav': "Sicilian — Dragon, Yugoslav",
          'sicilian_hyperaccelerated': "Sicilian — Hyperaccelerated",
          'sicilian_najdorf': "Sicilian — Najdorf",
          'sicilian_najdorf_english_attack': "Sicilian — Najdorf, English Attack",
          'slav_defence': "Slav Defence",
          'two_knights_defence': "Zweispringerspiel",
          'van_geet_opening': "Van Geet Opening"
        },
        defaultTeamA: 'Mannschaft A',
        defaultTeamB: 'Mannschaft B',
        claimDraw: 'Remis beanspruchen',
        claimDrawFailed: 'Remis konnte nicht beansprucht werden.',
        clearHighlights: 'Markierungen löschen',
        coachBoard: 'Trainerbrett',
        coachBoardArrowHint: 'Rechtsklick markiert; zweiter Rechtsklick zeichnet einen Pfeil.',
        coachBoardClassroomHint: 'Für Audio/Video Classroom Live nutzen. Dieses Brett ist nur für Stellungen und Pfeile.',
        coachBoardSubtitle: 'Gemeinsames Lehrbrett — Stellung setzen, Pfeile, Classroom für Live-A/V',
        confirmMove: 'Zug bestätigen',
        copyFen: 'FEN kopieren',
        copyFailed: 'Kopieren fehlgeschlagen',
        dismissReport: 'Verwerfen',
        favoriteOpenings: 'Lieblingseröffnungen',
        fenCopied: 'FEN kopiert',
        gameChat: 'Partie-Chat',
        highContrast: 'Hoher Kontrast',
        hubPartialLoad: 'Einige Hub-Daten konnten nicht geladen werden',
        invalidFen: 'Ungültige FEN',
        invalidPgn: 'PGN konnte nicht gelesen werden',
        loadMoreGames: 'Mehr laden ({{loaded}} von {{total}})',
        moveConfirmation: 'Züge bestätigen',
        moveSending: 'Zug wird gesendet…',
        myRatingLoadFailed: 'Rating konnte nicht geladen werden',
        noOpenAppeals: 'Keine offenen Einsprüche',
        noOpenReports: 'Keine offenen Meldungen',
        noOpenSeeks: 'Keine offenen Suchen',
        noOpeningsYet: 'Noch keine Eröffnungen',
        openAppeals: 'Offene Einsprüche',
        openClassroomLive: 'Classroom Live öffnen',
        openGame: 'Partie öffnen',
        openReports: 'Offene Meldungen',
        peakRating: 'Höchst',
        pgnLoaded: 'PGN auf Brett geladen',
        pgnPaste: 'PGN einfügen',
        pgnPasteHint: 'Vollständige PGN oder Zugliste…',
        pieceSet: 'Figurensatz',
        pieceSetStandard: 'Standard',
        pieceSetUnicode: 'Unicode',
        playerNotFound: 'Spieler nicht gefunden',
        playerProfile: 'Spielerprofil',
        playerProfileSubtitle: 'Ratings nach Tempo und Partien',
        playingNow: 'Spielt gerade',
        prefsSaved: 'Einstellungen gespeichert',
        ratingTrend: 'Aktueller Rating-Verlauf',
        ratingsBySpeed: 'Ratings nach Tempo',
        recentGames: 'Letzte Partien',
        rejectAppeal: 'Einspruch ablehnen',
        rematchIncoming: 'Revanche-Angebot',
        rematchIncomingToast: 'Revanche-Herausforderung',
        rematchSending: 'Senden…',
        reportDetails: 'Details (wahlfrei)',
        reportFailed: 'Meldung konnte nicht gesendet werden.',
        reportPlayer: 'Spieler melden',
        reportPrompt: 'Kurz beschreiben, warum Sie melden (wahlfrei):',
        reportReasonPrompt: 'Meldegrund',
        reportSent: 'Meldung gesendet. Das Team prüft sie.',
        reportedBy: 'Gemeldet von',
        reportsAppealsLoadFailed: 'Meldungen/Einsprüche nicht geladen',
        resetBoard: 'Zurücksetzen',
        resolveReport: 'Erledigen',
        searchGames: 'Partien suchen',
        searchGamesPlaceholder: 'Gegnername oder Status…',
        shortcutEnds: 'Zum Anfang / Ende',
        shortcutHelpTitle: 'Tastenkürzel',
        shortcutScrub: 'Züge durchblättern',
        shortcutToggleHelp: 'Hilfe ein-/ausblenden',
        shortcuts: 'Kürzel',
        sound: 'Ton',
        speedClass: 'Tempo',
        speedClassLabel: 'Tempoklasse',
        submitAppeal: 'Einspruch senden',
        submitReport: 'Meldung senden',
        submitting: 'Senden…',
        themesLoadFailed: 'Themen nicht geladen',
        tourneyFilterActive: 'Offen & laufend',
        tourneyFilterDraft: 'Entwurf',
        tourneyFilterCompleted: 'Beendet',
        undoMove: 'Zug zurück',
        reportReason: {
          assistance: 'Verdacht auf Engine / Hilfe',
          chat: 'Beleidigender Chat',
          stalling: 'Verzögern / Nichtspielen',
          other: 'Sonstiges'
        },
},
      auth: {
        welcome: 'Willkommen',
        signInTo: 'Anmelden bei {{name}}',
        login: 'Anmelden',
        username: 'Benutzername',
        password: 'Passwort',
        forgot: 'Passwort vergessen?',
        enterUsername: 'Benutzername eingeben',
        enterPassword: 'Passwort eingeben',
        invalid: 'Ungültiger Benutzername oder Passwort',
        showPassword: 'Passwort anzeigen',
        hidePassword: 'Passwort verbergen',
        agreePrefix: 'Mit der Anmeldung stimmen Sie unserer',
        agreeAnd: 'und den',
        privacyPolicy: 'Datenschutzerklärung',
        terms: 'Nutzungsbedingungen',
        tooManyAttempts: 'Zu viele Versuche. Bitte versuchen Sie es später erneut.',
        sessionExpired: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
        sessionInvalid: 'Ihre Sitzung ist nicht mehr gültig. Bitte melden Sie sich erneut an.',
        sessionInactive: 'Ihre Sitzung ist wegen Inaktivität abgelaufen. Bitte melden Sie sich erneut an.'
      },
      footer: {
        quickLinks: 'Schnelllinks',
        academyContact: 'Akademie-Kontakt',
        licenseInfo: 'Lizenzinfo',
        privacyPolicy: 'Datenschutzerklärung',
        termsOfService: 'Nutzungsbedingungen',
        terms: 'Bedingungen',
        forgotPassword: 'Passwort vergessen',
        helpGettingStarted: 'Hilfe & Einstieg',
        helpFaq: 'Hilfe & FAQ',
        allRightsReserved: 'Alle Rechte vorbehalten.',
        developedBy: 'Entwickelt & lizenziert von',
        chessManagementSystem: 'Schach-Managementsystem',
        contactDetailsHint: 'Kontaktdaten können von Ihrem Akademie-Administrator hinterlegt werden.',
        licensePending: 'Lizenzstatus erscheint hier, sobald verfügbar.',
        daysRemaining: '{{count}} Tag verbleibend',
        daysRemaining_other: '{{count}} Tage verbleibend',
        licenseActive: 'Lizenz aktiv',
        built: 'Build {{version}} · {{tz}}',
        unknownVersion: 'unbekannt'
      },
      common: {
        language: 'Sprache',
        save: 'Speichern',
        cancel: 'Abbrechen',
        loading: 'Laden…',
        edit: 'Bearbeiten',
        archive: 'Archivieren',
        add: 'Hinzufügen',
        all: 'Alle',
        showing: 'Anzeige',
        of: 'von',
        to: 'bis',
        results: 'Ergebnisse',
        rowsPerPage: 'Zeilen pro Seite:',
        downloadExcel: 'Excel herunterladen',
        search: 'Suchen',
        id: 'ID:',
        idColumn: 'ID',
        items: 'Einträge',
        back: 'Zurück',
        firstPage: 'Erste Seite',
        previousPage: 'Vorherige Seite',
        nextPage: 'Nächste Seite',
        lastPage: 'Letzte Seite',
        prev: 'Zurück',
        next: 'Weiter',
        pageOf: 'Seite {{current}} von {{total}}',
        showingRange: 'Anzeige {{start}} bis {{end}} von {{total}} Ergebnissen',
        noDataExport: 'Keine Daten zum Exportieren',
        exportFailed: 'Export fehlgeschlagen. Bitte erneut versuchen.',
        downloadAsExcel: 'Als Excel (.xlsx) herunterladen',
        active: 'Aktiv',
        archived: 'Archiviert',
        inactive: 'Inaktiv',
        inactiveSuffix: ' (Inaktiv)',
        actions: 'Aktionen',
        dismiss: 'Schließen',
        yes: 'Ja',
        no: 'Nein',
        done: 'Erledigt.',
        close: 'Schließen',
        gotIt: 'Verstanden',
        confirm: 'Bestätigen',
        popupInfo: 'Information',
        minutesCount: '{{count}} Minuten',
        unknown: 'Unbekannt',
        userFallback: 'Benutzer #{{id}}',
        branchFallback: 'Filiale #{{id}}',
        levelFallback: 'Stufe #{{id}}',
        popupSuccess: 'Erfolg',
        popupWarning: 'Bitte prüfen',
        popupError: 'Achtung erforderlich',
        pressEscCancel: 'Esc drücken zum Abbrechen',
        pressEscClose: 'Esc drücken zum Schließen',
        backendDown: 'Backend-Server ist nicht erreichbar — bitte Administrator kontaktieren oder später erneut versuchen.',
        ariaClose: 'Schließen',
        info: 'Info',
        learnMore: 'Mehr erfahren',
        remove: 'Entfernen',
        assigned: 'Zugewiesen',
        activate: 'Aktivieren',
        status: 'Status',
        username: 'Benutzername',
        email: 'E-Mail',
        phone: 'Telefon',
        roles: 'Rollen',
        fullName: 'Vollständiger Name',
        name: 'Name',
        description: 'Beschreibung',
        noDataFound: 'Keine Daten gefunden',
        emptySearchHint: 'Passen Sie Ihre Suchbegriffe an',
        noMatchSearch: 'Keine {{entity}} entsprechen Ihrer Suche',
        noFound: 'Keine {{entity}} gefunden',
        exporting: 'Exportiere…',
        showDetails: 'Details anzeigen',
        hideDetails: 'Details ausblenden',
        system: 'System',
        na: 'k. A.',
        retry: 'Erneut versuchen',
        listLoadFailed: 'Liste konnte nicht geladen werden. Aktualisieren und erneut versuchen.',
        optionsLoadFailed: 'Optionen konnten nicht geladen werden. Seite aktualisieren oder erneut versuchen.',
        unknownError: 'Unbekannter Fehler',
        entries: 'Einträge',
        apply: 'Anwenden',
        never: 'Nie',
        code: 'Code',
        location: 'Ort',
        place: 'Platz',
        score: 'Punkte'
      },
      parent: {
        childSwitcher: {
          loadingChildren: 'Kinder werden geladen…',
          viewingChild: 'Angezeigtes Kind',
          switchChild: 'Kind wechseln',
          selectChild: 'Kind auswählen…'
        },
        chooseChildTitle: 'Kind auswählen',
        chooseChildBody: 'Wählen Sie den verknüpften Schüler, den Sie anzeigen möchten, oder kehren Sie zur Eltern-Startseite zurück.',
        backToParentHome: 'Zur Eltern-Startseite',
        parentHome: 'Eltern-Startseite',
        viewingPrefix: 'Ansicht',
        readOnly: 'Nur Lesen',
        roleLabel: 'Eltern'
      },
      header: {
        accountMenu: 'Kontomenü',
        openNav: 'Navigationsmenü öffnen',
        closeNav: 'Navigationsmenü schließen',
        closeNavSidebar: 'Navigation schließen',
        myProfile: 'Mein Profil',
        switchRole: 'Rolle wechseln',
        logout: 'Abmelden'
      },
      selectSearch: {
        select: 'Auswählen…',
        search: 'Suchen…',
        loading: 'Laden…',
        noOptions: 'Keine Optionen gefunden',
        loadFailed: 'Optionen konnten nicht geladen werden — erneut versuchen',
      },
      datePicker: {
        clearDate: 'Datum löschen',
        selectTime: 'Zeit wählen',
        selectDateTime: 'Datum und Uhrzeit wählen',
        selectTimeIntervals: 'Zeit wählen (15-Min.-Intervalle)',
        selectTimeWithFormat: 'Zeit wählen ({{format}}, 15-Min.-Intervalle)',
        dateWithFormat: 'Datum ({{format}})',
        timeWithFormat: 'Uhrzeit ({{format}})',
        noTimeOptions: 'Keine Zeitoptionen verfügbar',
        duration: 'Dauer: {{hours}} Std. {{minutes}} Min.',
        exceeds3Hours: 'Überschreitet 3 Stunden',
        hour12: '12-Stunden',
        hour24: '24-Stunden',
        clear: 'Leeren',
        done: 'Fertig'
      },
      notificationBell: {
        ariaLabel: 'Benachrichtigungen',
        ariaLabelUnread: 'Benachrichtigungen ({{count}} ungelesen)',
        title: 'Benachrichtigungen',
        unread: '{{count}} ungelesen',
        markAllRead: 'Alle als gelesen markieren',
        empty: 'Alles erledigt!',
        viewAll: 'Alle Benachrichtigungen anzeigen',
        unreadHint: '{{count}} erfordern Ihre Aufmerksamkeit',
        caughtUpHint: 'Keine ungelesenen Hinweise',
        emptyHint: 'Wallet-, Anwesenheits- und Datenschutzhinweise erscheinen hier, wenn Handlung nötig ist.'
      },
      publicLayout: {
        developedBy: 'Entwickelt & lizenziert von',
        licenseSupport: 'Für Lizenzkauf oder Support',
        contactSupport: 'Support kontaktieren'
      },
      accountAccess: {
        signInLink: {
          button: 'Anmeldelink per E-Mail',
          buttonTitle: 'E-Mail mit Link zum Festlegen oder Zurücksetzen des Passworts senden',
          buttonTitleNoEmail: 'E-Mail-Adresse im Profil hinzufügen, um einen Anmeldelink zu senden',
          noEmailAlert: 'Dieser {{role}} hat keine E-Mail-Adresse. Bitte zuerst eine E-Mail hinzufügen, bevor ein Anmeldelink gesendet wird.',
          listConfirm: 'Anmeldelink an diesen {{role}} senden? Er kann damit sein Passwort festlegen oder zurücksetzen.',
          editConfirm: 'Neuen Anmeldelink an diesen {{role}} senden?',
          success: 'Anmeldelink erfolgreich per E-Mail gesendet.',
          failure: 'Anmeldelink konnte nicht per E-Mail gesendet werden.',
          createCheckbox: 'Anmeldelink per E-Mail (Passwort selbst festlegen)',
          createNote: 'Ein einmaliger Anmeldelink wird per E-Mail gesendet, damit das Passwort festgelegt werden kann.',
          bulkImportOption: 'Anmeldelink per E-Mail — Schüler legen ihr Passwort selbst fest'
        },
        passwordReset: {
          pageSubtitle: 'Wir senden einen Link zum Zurücksetzen des Passworts, falls ein Konto existiert',
          successMessage: 'Falls ein Konto für diese E-Mail oder diesen Benutzernamen existiert, wurde ein Link zum Zurücksetzen des Passworts gesendet. Bitte Posteingang prüfen.',
          submitButton: 'Link zum Zurücksetzen per E-Mail senden',
          publicHelp: 'Sie erhalten einen Link zum Zurücksetzen des Passworts an Ihre registrierte E-Mail-Adresse.'
        }
      },
      userActions: {
        edit: 'Bearbeiten',
        archive: 'Archivieren',
        activate: 'Aktivieren',
        removeFromList: 'Aus Liste entfernen',
        removePermanently: 'Endgültig entfernen',
        editTitle: '{{label}} bearbeiten',
        archiveTitle: '{{label}} archivieren',
        reactivateTitle: '{{label}} reaktivieren',
        removeFromListTitle: 'Aus Ihrer Liste entfernen, ohne den Schüler zu archivieren',
        removePermanentlyTitle: 'Aus Listen entfernen und Lizenzplatz freigeben; Verlauf bleibt als (Inaktiv)',
        archiveConfirm: 'Diesen {{label}} archivieren? Anmeldung nicht mehr möglich. Der Datensatz bleibt erhalten und zählt weiter zur Lizenz, bis er endgültig entfernt wird (bei voller Kapazität).',
        permanentRemoveConfirm: 'Diesen archivierten {{label}} endgültig aus Ihren Listen entfernen? Anwesenheit, Zahlungen und anderer Verlauf bleiben im System und werden als „(Inaktiv)“ angezeigt. Dies gibt einen Lizenzplatz frei. Nicht rückgängig zu machen.',
        reactivateConfirm: 'Diesen {{label}} reaktivieren? Er kann sich wieder anmelden.',
        loadArchiveFailed: 'Archivierungsoptionen für {{label}} konnten nicht geladen werden.',
        archiveFailed: '{{label}} konnte nicht archiviert werden.',
        removeFailed: '{{label}} konnte nicht entfernt werden.',
        activateFailed: '{{label}} konnte nicht aktiviert werden.',
        labels: {
          student: 'Schüler',
          coach: 'Trainer',
          manager: 'Manager',
          user: 'Benutzer'
        }
      },
      search: {
        placeholder: 'Schüler, Trainer, Gruppen suchen…',
        aria: 'Globale Suche',
        searching: 'Suche…',
        noResults: 'Keine Ergebnisse für „{{q}}“',
        students: 'Schüler',
        coaches: 'Trainer',
        batches: 'Gruppen',
        apiUnavailable: 'Such-API nicht verfügbar. Backend-Container neu starten.',
        failed: 'Suche fehlgeschlagen. Bitte erneut versuchen.'
      },
      days: {
        short: {
          '1': 'Mo',
          '2': 'Di',
          '3': 'Mi',
          '4': 'Do',
          '5': 'Fr',
          '6': 'Sa',
          '7': 'So'
        }
      },
      charges: {
        modes: {
          PER_SESSION: 'Pro Stunde (pauschal)',
          PER_SESSION_HOURLY: 'Pro Stunde (stündlich)',
          PER_PERIOD: 'Periodenplan',
          RECURRING_SESSIONS: 'Pro Stunde (stündlich)',
          MONTHLY: 'Periodenplan',
          YEARLY: 'Periodenplan'
        },
        intervals: {
          WEEKLY: 'Wöchentlich (7 Tage)',
          MONTHLY: 'Monatlich (30 Tage)',
          QUARTERLY: 'Vierteljährlich (90 Tage)',
          SEMI_ANNUAL: 'Halbjährlich (180 Tage)',
          YEARLY: 'Jährlich (365 Tage)'
        },
        status: {
          PAID: 'Bezahlt',
          UNPAID: 'Unbezahlt',
          ESCALATED: 'Eskaliert'
        }
      },
      carryForward: {
        columnHelp: 'Übertrag steuert die Abrechnung, wenn ein Schüler abwesend ist. Wenn aktiviert, wird die Stundengebühr nicht abgezogen und das Guthaben kann später genutzt werden. Wenn deaktiviert, wird die Gebühr auch bei Abwesenheit abgezogen.',
        modes: {
          ALLOWED: {
            label: 'Für alle Schüler erlaubt',
            desc: 'Abwesende Schüler werden nicht belastet — Stundenguthaben wird für später übertragen.'
          },
          NOT_ALLOWED: {
            label: 'Für keinen Schüler erlaubt',
            desc: 'Abwesende Schüler werden weiterhin belastet — Stundengebühren werden vom Guthaben abgezogen.'
          },
          ENROLLMENT_LEVEL: {
            label: 'Bei der Einschreibung festlegen',
            desc: 'Übertrag für jeden Schüler individuell bei der Einschreibung wählen.'
          }
        },
        batchPolicyTitle: {
          ALLOWED: 'Gruppenrichtlinie: Übertrag für alle Schüler aktiviert. Bei Abwesenheit wird die Stundengebühr nicht abgezogen — das Guthaben wird übertragen.',
          NOT_ALLOWED: 'Gruppenrichtlinie: Übertrag für alle Schüler deaktiviert. Bei Abwesenheit wird die Stundengebühr weiterhin vom Guthaben abgezogen.',
          ENROLLMENT_LEVEL: 'Gruppenrichtlinie: Übertrag wird pro Schüler bei der Einschreibung festgelegt. Pro Schüler kann Übertrag aktiviert (keine Belastung bei Abwesenheit) oder deaktiviert sein.',
          default: 'Dieser Schüler folgt der Übertragsrichtlinie der Gruppe.'
        },
        batchPolicySummary: {
          ALLOWED: 'Bei Abwesenheit wird nicht belastet — das Stundenguthaben wird übertragen.',
          NOT_ALLOWED: 'Bei Abwesenheit wird weiterhin belastet — die Stundengebühr wird vom Guthaben abgezogen.',
          ENROLLMENT_LEVEL: 'Übertrag kann für jeden Schüler individuell bei der Einschreibung gewählt werden.'
        },
        batchPolicyLabel: {
          ALLOWED: 'Übertrag für alle aktiviert',
          NOT_ALLOWED: 'Übertrag für alle deaktiviert',
          ENROLLMENT_LEVEL: 'Einschreibungsebene (schülerspezifisch)',
          unknown: 'Unbekannt'
        },
        studentOverrideTitle: {
          enabled: 'Übertrag für diesen Schüler aktiviert. Bei Abwesenheit wird die Stundengebühr nicht abgezogen — das Guthaben wird übertragen.',
          disabled: 'Übertrag für diesen Schüler deaktiviert. Bei Abwesenheit wird die Stundengebühr weiterhin vom Guthaben abgezogen.'
        },
        studentOverrideShort: {
          enabled: 'Bei Abwesenheit keine Belastung — Guthaben wird übertragen',
          disabled: 'Bei Abwesenheit weiterhin belastet — kein Übertrag'
        },
        attendanceCell: {
          followsBatch: 'Folgt Gruppe ({{mode}})',
          enabled: 'Aktiviert',
          disabled: 'Deaktiviert',
          unknown: 'Unbekannt'
        }
      },
      branchModes: {
        OFFLINE: {
          label: 'Präsenz',
          description: 'Stunden vor Ort an einem physischen Standort'
        },
        ONLINE: {
          label: 'Online',
          description: 'Nur Remote- / virtuelle Stunden'
        },
        HYBRID: {
          label: 'Online + Präsenz',
          description: 'Bietet sowohl Präsenz- als auch Online-Stunden an'
        }
      },
      sessionModes: {
        ANY: 'Beliebig',
        ONLINE: 'Online',
        OFFLINE: 'Vor Ort'
      },
      popup: {
        labels: {
          alert: 'Verstanden',
          confirm: 'Bestätigen',
          cancel: 'Abbrechen'
        },
        titles: {
          confirmDestructive: 'Destruktive Aktion bestätigen',
          pleaseConfirm: 'Bitte bestätigen',
          confirmAction: 'Aktion bestätigen',
          success: 'Erfolg',
          attention: 'Etwas erfordert Ihre Aufmerksamkeit',
          pleaseReview: 'Bitte prüfen',
          information: 'Information'
        },
        confirmLabels: {
          delete: 'Löschen',
          archive: 'Archivieren',
          activate: 'Aktivieren',
          execute: 'Ausführen',
          send: 'Senden',
          trigger: 'Auslösen',
          remove: 'Entfernen',
          continue: 'Fortfahren'
        }
      },
      unsavedChanges: {
        title: 'Ungespeicherte Änderungen',
        message: 'Sie haben noch nicht gespeicherte Änderungen. Speichern Sie vor dem Verlassen, verwerfen Sie sie, oder bleiben Sie auf dieser Seite.',
        save: 'Speichern und verlassen',
        discard: 'Änderungen verwerfen',
        stay: 'Bleiben'
      },
      errors: {
        api: {
          fallback: 'Etwas ist schiefgelaufen.',
          upgradeRequired: 'Upgrade erforderlich',
          onlinePaymentsUpgrade: 'Online-Zahlungsgateways erfordern eine Professional- oder Enterprise-Lizenz. Bitte wenden Sie sich an Ihre Akademie für ein Upgrade.',
          planLimitReached: 'Planlimit erreicht',
          planLimitMessage: 'Ihr aktueller Plan hat seine Kapazität erreicht. Bitte upgraden Sie Ihren Plan oder schaffen Sie Kapazität frei und versuchen Sie es erneut.',
          requestFailed: 'Anfrage fehlgeschlagen'
        }
      },
      wallet: {
        txnReasons: {
          ADVANCE_PAYMENT: 'Vorauszahlung',
          ONLINE_TOPUP: 'Online-Aufladung',
          SESSION_CHARGE: 'Stundengebühr',
          SESSION_REVERSAL: 'Stundenerstattung',
          DISCOUNT: 'Rabattgutschrift',
          REFUND: 'Erstattung',
          LEGACY_DEBIT: 'Belastung',
          default: 'Transaktion'
        },
        dateLines: {
          session: 'Stunde',
          created: 'Erstellt',
          paidOn: 'Bezahlt am'
        }
      },
      regional: {
        presets: {
          india: 'Indien',
          india12: 'Indien (12-Stunden)',
          uae: 'VAE',
          us: 'Vereinigte Staaten',
          uk: 'Vereinigtes Königreich',
          eurozone: 'Eurozone (allgemein)',
          germany: 'Deutschland',
          france: 'Frankreich',
          netherlands: 'Niederlande',
          belgium: 'Belgien',
          spain: 'Spanien'
        },
        paymentReference: {
          IN: 'z. B. Beleg Nr. 1234, UPI-Ref.: 123456789012',
          US: 'z. B. Beleg Nr. 1234, Scheck Nr. 5678 oder Karten-Auth-Code',
          CA: 'e.g. Receipt #1234, PAD reference, or card auth code',
          GB: 'z. B. Beleg Nr. 1234, Bankreferenz oder Schecknummer',
          AE: 'z. B. Beleg Nr. 1234 oder Überweisungsreferenz',
          EU: 'z. B. Beleg Nr. 1234 oder SEPA-/Überweisungsreferenz',
          default: 'z. B. Beleg Nr. 1234'
        },
        bankRouting: {
          IN: 'IFSC-Code',
          US: 'Routing-Nummer',
          CA: 'Transit / Institution Number',
          GB: 'Sort Code',
          AE: 'SWIFT / IBAN',
          EU: 'IBAN',
          default: 'Bankleitzahl'
        }
      },
      batches: {
        title: 'Gruppen',
        subtitle: 'Gruppen Ihrer Schachakademie verwalten und organisieren',
        add: 'Gruppe hinzufügen',
        searchPlaceholder: 'Gruppen nach Name, ID oder Stufe suchen…',
        entityLabel: 'Gruppen',
        archiveConfirm: 'Diese Gruppe archivieren?',
        archiveFailed: 'Gruppe konnte nicht archiviert werden.',
        empty: 'Keine Gruppen gefunden',
        emptyHint: 'Erstellen Sie Ihre erste Gruppe, um zu starten',
        managerSubtitle: 'Trainingsgruppen verwalten und organisieren',
        active: 'Aktiv',
        archived: 'Archiviert',
        batchNumber: 'Gruppe #{{id}}',
        unknown: 'Unbekannte Gruppe',
        individualSessions: 'Einzelsitzungen',
        individualSessionsNamed: 'Einzelsitzungen - {{level}}',
        walkInNamed: 'Walk-in - Stufe #{{level}}',
        carryForward: 'Übertrag',
        perStudent: 'Pro Schüler',
        carryForwardBadge: {
          allowed: 'Übertrag erlaubt',
          notAllowed: 'Kein Übertrag',
          tooltipAllowed: 'Nicht genutztes Guthaben wird in den nächsten Abrechnungszeitraum übertragen.',
          tooltipNotAllowed: 'Nicht genutztes Guthaben verfällt am Ende des Abrechnungszeitraums.'
        },
        editTitle: 'Gruppe bearbeiten',
        sessionsTitle: 'Stunden anzeigen',
        archiveTitle: 'Gruppe archivieren',
        level: 'Stufe',
        branch: 'Filiale',
        schedule: 'Zeitplan',
        sessionDays: 'Unterrichtstage',
        sessionTime: 'Unterrichtszeit',
        charges: 'Gebühren',
        lockNotes: {
          branch: 'Die Filiale ist nach Erstellung der Gruppe festgelegt (über die Stufe).',
          level: 'Die Stufe ist nach Erstellung der Gruppe festgelegt und kann nicht geändert werden.',
          carryForward: 'Die Übertragsrichtlinie ist nach Erstellung der Gruppe festgelegt.',
          chargesMode: 'Der Abrechnungsmodus kann nicht geändert werden, sobald Einschreibungen oder Stunden existieren.',
          monthlyYearly: 'Periodengebühr und Intervall sind nach Erstellung der Gruppe festgelegt.',
          systemManaged: 'Diese Gruppe wird automatisch vom System verwaltet und kann nicht bearbeitet werden.',
          sessionCharge: 'Gilt nur für künftige Stundenabrechnungen; vergangene Belastungen bleiben unverändert.'
        }
      },
      dashboard: {
        welcome: 'Willkommen, {{name}}',
        quickActions: 'Schnellaktionen',
        defaultAdmin: 'Admin',
        defaultManager: 'Manager',
        defaultCoach: 'Trainer',
        defaultStudent: 'Schüler'
      },
      table: {
        branch: 'Filiale',
        level: 'Stufe',
        batch: 'Gruppe',
        coach: 'Trainer',
        time: 'Zeit',
        when: 'Wann',
        start: 'Beginn',
        end: 'Ende',
        mode: 'Modus',
        studentName: 'Schülername',
        coachName: 'Trainername',
        branchName: 'Filialname',
        enrolledDate: 'Einschreibedatum',
        payment: 'Zahlung',
        balanceAfter: 'Saldo danach',
        planType: 'Plantyp',
        groupType: 'Gruppentyp',
        defaultRate: 'Standardtarif',
        defaultCredits: 'Standardguthaben',
        levelRange: 'Stufenbereich',
        creditExpiry: 'Guthabenablauf',
        attendance: 'Anwesenheit',
        topics: 'Themen',
        balanceBefore: 'Saldo vorher',
        sessionFee: 'Stundengebühr',
        dateTime: 'Datum & Uhrzeit',
        date: 'Datum',
        description: 'Beschreibung',
        carryForward: 'Übertrag',
        amount: 'Betrag'
      },
      pages: {
        adminDashboard: {
          emailAlertTitle: 'Ausgehende E-Mail ist nicht konfiguriert.',
          emailAlertBody: 'Schüler und Mitarbeiter erhalten keine Anmeldelinks, Erinnerungen oder Abrechnungen, bis SMTP eingerichtet ist.',
          configureEmail: 'E-Mail konfigurieren',
          whatsappAlertTitle: 'WhatsApp (WATI) ist nicht konfiguriert.',
          whatsappAlertBody: 'Verbinden Sie Ihr WATI-Konto, um Stundenerinnerungen und Guthabenwarnungen per WhatsApp zu senden.',
          configureWhatsApp: 'WhatsApp konfigurieren',
          subtitle: 'Verwalten Sie Ihre Akademie mit voller Kontrolle über Benutzer, Filialen und Systemeinstellungen.',
          totalStudents: 'Schüler gesamt',
          activeBranches: 'Aktive Filialen',
          activeCoaches: 'Aktive Trainer',
          atRiskStudents: 'Gefährdete Schüler',
          activeEnrollments: 'Aktive Einschreibungen',
          addStudent: 'Schüler hinzufügen',
          addStudentDesc: 'Neuen Schüler in der Akademie einschreiben',
          createBatch: 'Gruppe erstellen',
          createBatchDesc: 'Neue Trainingsgruppe einrichten',
          walletTopup: 'Guthaben aufladen',
          walletTopupDesc: 'Guthaben auf Schülerkonto einzahlen',
          manageCoaches: 'Trainer verwalten',
          manageCoachesDesc: 'Trainerteam anzeigen und verwalten',
          viewSessions: 'Stunden anzeigen',
          viewSessionsDesc: 'Alle Trainingsstunden verwalten',
          paymentReports: 'Zahlungsberichte',
          paymentReportsDesc: 'Finanzielle Einnahmen anzeigen'
        },
        managerDashboard: {
          subtitle: 'Betrieb effizient verwalten. Trainer, Schüler und Filialleistung im Blick behalten.',
          coaches: 'Trainer',
          students: 'Schüler',
          manageBatches: 'Gruppen verwalten',
          manageBatchesDesc: 'Trainingsgruppen anzeigen und organisieren',
          enrollments: 'Einschreibungen',
          enrollmentsDesc: 'Schülereinschreibungen verwalten',
          manageLevels: 'Stufen verwalten',
          manageLevelsDesc: 'Stufen konfigurieren',
          sessionStatement: 'Stundenabrechnung',
          sessionStatementDesc: 'Detaillierte Stundenberichte anzeigen',
          batchPayments: 'Gruppenanwesenheit',
          batchPaymentsDesc: 'Gruppen-Anwesenheitsmatrix anzeigen'
        },
        coachDashboard: {
          subtitle: 'Ihr Tag auf einen Blick. Stunden prüfen, Anwesenheit markieren und die Woche planen.',
          metricsFailed: 'Kennzahlen konnten nicht geladen werden.',
          todayFailed: 'Heutige Stunden konnten nicht geladen werden.',
          upcomingFailed: 'Kommende Stunden konnten nicht geladen werden.',
          sessionsToday: 'Stunden heute',
          studentsToday: 'Schüler heute',
          hoursThisWeek: 'Stunden diese Woche',
          attendanceWtd: 'Anwesenheit (WTD)',
          noShowsToday: 'Nicht erschienen heute',
          todaysSchedule: 'Heutiger Plan',
          viewAllSessions: 'Alle Stunden anzeigen',
          noSessionsToday: 'Heute sind keine Stunden geplant.',
          upcoming7Days: 'Kommend (nächste 7 Tage)',
          noUpcomingSessions: 'Keine kommenden Stunden in den nächsten 7 Tagen.',
          viewAttendance: 'Anwesenheit anzeigen',
          markAttendance: 'Anwesenheit markieren'
        },
        studentDashboard: {
          childOverview: '{{name}} — Übersicht',
          childFallback: 'Kind',
          childSubtitle: 'Anwesenheitsübersicht, kommende Stunden und Guthaben für Ihren verknüpften Schüler.',
          subtitle: 'Fortschritt verfolgen, kommende Stunden anzeigen und Guthaben verwalten.',
          loadFailed: 'Dashboard konnte nicht geladen werden.',
          activeEnrollments: 'Aktive Einschreibungen',
          attendanceRate: 'Anwesenheitsquote',
          upcoming7d: 'Kommend (7 Tage)',
          lastAttended: 'Zuletzt teilgenommen',
          upcomingSessions: 'Kommende Stunden',
          noUpcomingSessions: 'Keine kommenden Stunden.',
          recentAttendance: 'Letzte Anwesenheit',
          noAttendanceRecords: 'Keine Anwesenheitseinträge.'
        },
        parentHome: {
          loading: 'Elternportal wird geladen…',
          portalLabel: 'Elternportal',
          hello: 'Hallo, {{name}}',
          there: 'Sie',
          approvedSubtitle: 'Behalten Sie Anwesenheit, Zeitplan und Akademiegebühren Ihres Kindes im Blick — alles an einem Ort.',
          pendingSubtitle: 'Ihr Erziehungsberechtigten-Konto wird eingerichtet.',
          accessPending: 'Zugang ausstehend',
          pendingDefault: 'Ihre Erziehungsberechtigten-Verknüpfung wartet auf Genehmigung durch die Akademie. Melden Sie sich erneut an, sobald ein Administrator Ihre Anfrage genehmigt hat.',
          noLinkedStudents: 'Noch keine verknüpften Schüler. Bitten Sie die Akademie, Ihnen eine Einladung zu senden.',
          yourChildren: 'Ihre Kinder',
          currentlyViewing: 'Aktuell angezeigt',
          selectChildPrompt: 'Wählen Sie oben ein Kind, um Zusammenfassung und Schnellaktionen zu sehen.',
          quickActions: 'Schnellaktionen',
          more: 'Mehr',
          otherLinks: 'Weitere Links',
          helpFaq: 'Hilfe & FAQ',
          loadingChild: 'Kinddaten werden geladen…',
          loadFailed: 'Elternportal konnte nicht geladen werden.',
          pendingAccess: 'Ihr Erziehungsberechtigten-Zugang wartet auf Genehmigung oder ist noch nicht verknüpft.',
          overview: 'Übersicht',
          overviewDesc: 'Anwesenheit & kommende Stunden',
          calendar: 'Kalender',
          calendarDesc: 'Monatsplan',
          wallet: 'Guthaben',
          walletDesc: 'Saldo & Zahlungen',
          attendance: 'Anwesenheit',
          attendanceDesc: 'Stundenverlauf',
          enrollments: 'Einschreibungen',
          sessions: 'Stunden',
          notifications: 'Benachrichtigungen',
          sessionStatement: 'Stundenabrechnung',
          statusPendingApproval: 'Genehmigung ausstehend',
          statusApproved: 'Aktiv',
          statusRejected: 'Abgelehnt',
          statusRevoked: 'Widerrufen',
          selected: 'Ausgewählt',
          guardian: 'Erziehungsberechtigter'
        },
        managerStudents: {
          title: 'Schüler',
          subtitle: 'Schülerzugang suchen, prüfen und verwalten — mit übersichtlicher Liste.',
          addStudent: 'Schüler hinzufügen',
          searchPlaceholder: 'Schüler nach Name, Benutzername, E-Mail, Telefon oder ID suchen…',
          entityLabel: 'Schüler',
          noMatch: 'Keine Schüler entsprechen Ihrer Suche',
          empty: 'Keine Schüler gefunden',
          removeConfirm: '{{name}} aus Ihrer Liste entfernen?\n\nDies entfernt nur Ihre direkte Manager-Verknüpfung. Der Schüler wird nicht archiviert und kann weiterhin erscheinen, wenn er in Ihren Filialen eingeschrieben ist.',
          removeFailed: 'Schüler konnte nicht aus der Liste entfernt werden.',
          thisStudent: 'dieser Schüler',
          entitySingular: 'Schüler'
        },
        managerCoaches: {
          title: 'Trainer',
          subtitle: 'Trainer in Ihrem Filialbereich anzeigen',
          searchPlaceholder: 'Trainer nach Name, Benutzername, E-Mail, Telefon oder ID suchen…',
          entityLabel: 'Trainer',
          noMatch: 'Keine Trainer entsprechen Ihrer Suche',
          empty: 'Keine Trainer im System gefunden'
        },
        coaches: {
          manageAssignments: {
            title: 'Schülerzuweisungen verwalten',
            coachLabel: 'Trainer: {{name}}',
            infoBanner: 'Hier zugewiesene Schüler sind für diesen Trainer sichtbar, wenn individuelle Stunden erfasst werden. Ohne Zuweisung erscheint der Schüler nicht in der Stundenauswahl — auch wenn er im System aktiv ist.',
            assignedStudents: 'Zugewiesene Schüler ({{count}})',
            availableStudents: 'Verfügbare Schüler ({{count}})',
            searchAssigned: 'Zugewiesene Schüler suchen...',
            searchAvailable: 'Schüler suchen...',
            noAssigned: 'Noch keine Schüler zugewiesen',
            noAssignedMatch: 'Keine zugewiesenen Schüler entsprechen Ihrer Suche',
            noStudentsFound: 'Keine Schüler gefunden',
            allAssigned: 'Alle Schüler sind zugewiesen',
            assignButton: '{{count}} Schüler zuweisen',
            assignButton_other: '{{count}} Schüler zuweisen',
            assigning: 'Zuweisung...',
            loadFailed: 'Daten konnten nicht geladen werden',
            selectAtLeastOne: 'Bitte mindestens einen Schüler auswählen',
            assignSuccess: '{{count}} Schüler erfolgreich zugewiesen',
            assignSuccess_other: '{{count}} Schüler erfolgreich zugewiesen',
            assignFailed: 'Schüler konnten nicht zugewiesen werden: {{message}}',
            removeConfirm: 'Diese Schülerzuweisung entfernen?',
            removeSuccess: 'Zuweisung erfolgreich entfernt',
            removeFailed: 'Zuweisung konnte nicht entfernt werden',
            assignedOn: 'Zugewiesen: {{date}}'
          },
          title: 'Trainer',
          subtitle: 'Trainerdaten, Einladungen und Zuweisungen übersichtlich in einem modernen Arbeitsbereich verwalten.',
          add: 'Trainer hinzufügen',
          searchPlaceholder: 'Trainer nach Name, Benutzername, E-Mail, Telefon oder ID suchen…',
          entityLabel: 'Trainer',
          colCoachName: 'Trainername',
          manageStudents: 'Schüler verwalten',
          manageStudentsTitle: 'Zugewiesene Schüler für Einzelstunden verwalten'
        },
        managerBranches: {
          title: 'Meine Filialen',
          subtitle: 'Filialen, die Sie verwalten',
          searchPlaceholder: 'Filialen nach Name, Code, Telefon, Modus oder ID suchen…',
          entityLabel: 'Filialen',
          unauthorized: 'Sie sind nicht berechtigt, Filialen anzuzeigen.',
          loadFailed: 'Filialen konnten nicht geladen werden.',
          noMatch: 'Keine Filialen entsprechen Ihrer Suche',
          empty: 'Ihnen sind noch keine Filialen zugewiesen',
          emptyHint: 'Wenden Sie sich an einen Administrator, um Filialen zuzuweisen'
        },
        levels: {
          title: 'Stufen',
          managerSubtitle: 'Schachstufen verwalten',
          coachSubtitle: 'Schachstufen anzeigen',
          addLevel: 'Stufe hinzufügen',
          searchPlaceholder: 'Stufen nach Name, Filiale, Beschreibung oder ID suchen…',
          entityLabel: 'Stufen',
          loadFailed: 'Stufen konnten nicht geladen werden.',
          noMatch: 'Keine Stufen entsprechen Ihrer Suche',
          empty: 'Keine Stufen gefunden',
          colLevelName: 'Stufenname',
          editLevel: 'Stufe bearbeiten',
          archiveLevel: 'Stufe archivieren',
          levelFallback: 'Stufe {{id}}',
          form: {
            levelName: 'Stufenname',
            branchForLevel: 'Filiale für diese Stufe',
            branchFixedNote: 'Die Filiale ist nach Erstellung der Stufe fest und kann nicht geändert werden.',
            loadBranchesFailed: 'Filialen konnten nicht geladen werden',
            loadingBranches: 'Filialen werden geladen…'
          },
          subtitle: 'Schachstufen verwalten',
          archiveConfirm: 'Diese Stufe archivieren?',
          archiveFailed: 'Stufe konnte nicht archiviert werden.'
        },
        feePlans: {
          title: 'Gebührenpläne',
          subtitle: 'Filialspezifische Gebührenpläne und Abrechnungskonfiguration',
          loadFailed: 'Pläne konnten nicht geladen werden.',
          empty: 'Keine Gebührenpläne gefunden.',
          allBranches: 'Alle Filialen',
          addPlan: 'Plan hinzufügen',
          selectBranch: 'Filiale wählen…',
          selectBranchHint: 'Wählen Sie eine Filiale, um Pläne anzuzeigen.',
          noPlans: 'Keine Pläne für diese Filiale gefunden.',
          archiveConfirm: 'Diesen Plan archivieren? Er wird aus der Auswahl ausgeblendet, bleibt aber für die Historie erhalten.',
          archiveFailed: 'Plan konnte nicht archiviert werden.',
          activateFailed: 'Plan konnte nicht aktiviert werden.',
          colPlan: 'Plan-Nr.',
          colBranch: 'Filiale',
          colType: 'Typ',
          colGroup: 'Gruppe',
          colRate: 'Tarif',
          colCredits: 'Guthaben',
          colLevelScope: 'Stufenbereich',
          archiveTitle: 'Plan archivieren',
          activateTitle: 'Plan aktivieren'
        },
        studentBulkImport: {
          title: 'Massenimport Schüler',
          subtitle: 'CSV-Datei hochladen, um mehrere Schüler auf einmal zu importieren. Vorlage herunterladen, Schülerdaten ausfüllen und hochladen.',
          downloadTemplate: 'Vorlage herunterladen',
          totalRows: 'Zeilen gesamt',
          imported: 'Importiert',
          skipped: 'Übersprungen',
          failed: 'Fehlgeschlagen',
          allSuccess: 'Alle {{count}} Schüler wurden erfolgreich importiert!',
          completedWithIssues: 'Import mit einigen Problemen abgeschlossen.',
          summaryIssues: '{{success}} importiert · {{skipped}} übersprungen (Duplikate) · {{failed}} fehlgeschlagen. Details in der Tabelle unten.',
          detailedReport: 'Detaillierter Importbericht',
          importAnother: 'Weitere Datei importieren',
          viewAllStudents: 'Alle Schüler anzeigen',
          colNum: '#',
          colName: 'Schülername',
          colUsername: 'Benutzername',
          colEmail: 'E-Mail',
          colStatus: 'Status',
          colReason: 'Grund / Hinweis',
          statusImported: '✓ Importiert',
          statusSkipped: '⊘ Übersprungen',
          statusFailed: '✗ Fehlgeschlagen',
          step1: 'Importeinstellungen',
          passwordStrategy: 'Passwortstrategie',
          defaultPasswordOption: 'Standardpasswort setzen — Schüler können sich sofort anmelden',
          defaultPassword: 'Standardpasswort',
          passwordPlaceholder: 'Mindestens 6 Zeichen',
          passwordHint: 'Gilt für alle Zeilen. Eine eigene Passwort-Spalte hat Vorrang.',
          step2: 'CSV-Datei hochladen',
          rowsFound: '{{count}} Schülerzeile gefunden',
          rowsFound_other: '{{count}} Schülerzeilen gefunden',
          replaceFile: 'Klicken oder neue Datei ablegen zum Ersetzen',
          dropHint: 'CSV hier ablegen oder klicken zum Durchsuchen',
          csvOnly: 'Nur .csv-Dateien werden akzeptiert',
          noTemplate: 'Keine Vorlage?',
          downloadHere: 'Hier herunterladen',
          step3: 'Vorschau & Import',
          showingFirst: 'Erste 5 von {{total}} Zeilen',
          startImport: 'Import starten',
          importing: 'Import läuft…',
          importFailed: 'Import fehlgeschlagen. Bitte erneut versuchen.',
          selectFileFirst: 'Bitte zuerst eine CSV-Datei hochladen.',
          enterpriseSubtitle: 'CSV-Massenimport ist nur mit der Enterprise-Lizenz verfügbar.',
          enterpriseFeature: 'Enterprise-Funktion',
          enterpriseBody: 'Der Massenimport von Schülern per CSV ist nur in Enterprise enthalten. Ihre aktuelle Lizenz bietet diese Funktion nicht.',
          enterpriseHint: 'Auf Ihrem aktuellen Tarif können Sie Schüler über das Formular <1>{{addStudentLink}}</1> einzeln anlegen. Wenden Sie sich an Ihren Softwareanbieter für ein Enterprise-Upgrade.',
          addStudentLink: 'Schüler hinzufügen',
          csvOnlyAlert: 'Bitte eine CSV-Datei (.csv) auswählen.\n\nFür Excel-Dateien zuerst als "CSV (Komma getrennt)" speichern.',
          noDataRowsAlert: 'Keine Datenzeilen in der Datei gefunden. Bitte Dateiformat prüfen.'
        },
        studentImport: {
          title: 'Schülerimport aus Google Sheets',
          loadingSubtitle: 'Importkonfiguration wird geladen…',
          subtitle: 'Importiert täglich um 2:00 Uhr (Akademie-Ortszeit) neue Schülereinträge aus Google Sheets',
          loadFailed: 'Importstatus konnte nicht geladen werden',
          importStatus: 'Importstatus',
          spreadsheetId: 'Tabellen-ID',
          sheetName: 'Blattname',
          lastProcessedRow: 'Zuletzt verarbeitete Zeile',
          totalImported: 'Gesamt importiert',
          lastImportTime: 'Letzter Import',
          lastUpdated: 'Zuletzt aktualisiert',
          lastError: 'Letzter Fehler',
          actions: 'Aktionen',
          triggerImport: 'Import jetzt auslösen',
          processing: 'Verarbeitung…',
          resetTracker: 'Tracker zurücksetzen',
          refreshStatus: 'Status aktualisieren',
          triggerDesc: 'Import manuell auslösen, um sofort nach neuen Schülern zu suchen. Es werden nur Zeilen nach der zuletzt verarbeiteten importiert.',
          resetDesc: 'Achtung: Setzt den Tracker auf Zeile 0 zurück. Nur verwenden, wenn alle Schüler neu importiert werden sollen.',
          refreshDesc: 'Aktuellen Importstatus neu laden.',
          confirmTrigger: 'Schülerimport jetzt auslösen?',
          confirmReset: '⚠️ WARNUNG: Tracker wird zurückgesetzt und ALLE Schüler werden neu importiert. Fortfahren?',
          triggerSuccess: 'Import erfolgreich ausgelöst! Aktualisieren für Ergebnisse.',
          resetSuccess: 'Tracker erfolgreich zurückgesetzt!',
          triggerFailed: 'Import konnte nicht ausgelöst werden',
          resetFailed: 'Tracker konnte nicht zurückgesetzt werden',
          configTitle: 'Konfiguration',
          setupTitle: 'Einrichtungsanleitung',
          configDaily: 'Automatischer Import täglich um <strong>2:00 Uhr (Akademie-Ortszeit)</strong>',
          configNewRows: 'Nur neue Zeilen (nach der zuletzt verarbeiteten) werden importiert',
          configRoleEmail: 'Jeder Schüler wird mit STUDENT-Rolle erstellt und erhält eine Einladungs-E-Mail',
          configUsername: 'Benutzername wird aus E-Mail oder Name automatisch erzeugt',
          configMappings: 'Spaltenzuordnungen in <code>application.yml</code> konfigurieren',
          configEnv: 'Aktivieren/Deaktivieren über Umgebungsvariable <code>STUDENT_IMPORT_ENABLED</code>',
          setupStep1: 'Google-Cloud-Projekt erstellen und Google Sheets API aktivieren',
          setupStep2: 'Servicekonto erstellen und Anmeldedaten-JSON herunterladen',
          setupStep3: 'Google-Tabelle mit der Servicekonto-E-Mail freigeben',
          setupStep4: 'Anmeldedaten unter <code>/app/config/google-credentials.json</code> ablegen',
          setupStep5: '<code>STUDENT_IMPORT_ENABLED=true</code> in der Umgebung setzen',
          setupStep6: 'Spaltenzuordnungen in application.yml anpassen, falls die Tabellenstruktur abweicht',
          bulkImportButton: 'Massenimport',
          bulkImportTitle: 'Neue Schüler aus Google Sheets importieren',
          bulkImporting: 'Import läuft…',
          bulkImportConfirm: 'Schüler aus Google Sheets importieren?\n\nNeue Anmeldungen werden aus den verknüpften Google-Formularen übernommen.\nEs werden nur Zeilen seit dem letzten Import verarbeitet.',
          bulkImportTriggered: 'Import ausgelöst. Aktualisieren Sie die Liste in Kürze, um neue Schüler zu sehen.',
          bulkImportFailed: 'Schülerimport fehlgeschlagen. Stellen Sie sicher, dass der Import auf dem Server aktiviert ist.'
        },
        feePlanForm: {
          title: 'Gebührenplan hinzufügen',
          subtitle: 'Neuen Plan für eine Filiale erstellen.',
          branchLabel: 'Filiale für Gebührenplan',
          codeLabel: 'Gebührenplan-Code',
          typeLabel: 'Plantyp',
          groupTypeLabel: 'Schülergruppentyp',
          defaultRate: 'Standardgebühr',
          defaultCredits: 'Standard-Stundenguthaben',
          levelMin: 'Mindeststufe',
          levelMax: 'Höchststufe',
          carryForward: 'Anwesenheit erforderlich',
          carryForwardEnabled: 'Aktiviert: Keine Abrechnung bei ABWESEND.',
          carryForwardDisabled: 'Deaktiviert: Abrechnung bei ANWESEND und ABWESEND.',
          creditExpiry: 'Guthabenablauf (Tage)',
          notes: 'Planhinweise',
          active: 'Plan aktiv',
          selectBranchAlert: 'Filiale wählen.',
          codeRequired: 'Code ist erforderlich.',
          createFailed: 'Plan konnte nicht erstellt werden.',
          saving: 'Speichern…',
          createPlan: 'Plan erstellen'
        },
        enrollmentDetail: {
          title: 'Einschreibung #{{id}}',
          subtitle: 'Einschreibungsdetails und Abrechnung anzeigen.',
          loadingTitle: 'Einschreibungsdetails',
          notFound: 'Nicht gefunden',
          loadFailed: 'Einschreibung konnte nicht geladen werden',
          activeExpired: 'Aktiv (abgelaufen)',
          renew: 'Erneuern',
          activateConfirm: 'Einschreibung #{{id}} wirklich aktivieren?',
          archiveFailed: 'Archivierung fehlgeschlagen.',
          basicInfo: 'Grundinformationen',
          enrollmentPeriod: 'Einschreibungszeitraum',
          enrolledDate: 'Einschreibungsdatum',
          openEnded: 'Unbefristet',
          pricingBilling: 'Preise & Abrechnung',
          customCharges: 'Individuelle Einschreibungsgebühren',
          batchCharges: 'Gruppen-Standardgebühren',
          billingPeriod: 'Abrechnungszeitraum',
          periodFee: 'Periodengebühr',
          notConfigured: 'Nicht konfiguriert',
          hourlyRate: 'Stundensatz',
          perSessionCharge: 'Gebühr pro Stunde',
          afterDiscount: 'Nach {{pct}} % Rabatt',
          enrollmentOverride: 'Einschreibungs-Override',
          carryForward: 'Übertrag',
          enabled: 'Aktiviert',
          disabled: 'Deaktiviert',
          followsBatch: 'Folgt Gruppenrichtlinie',
          discountApplied: 'Rabatt angewendet',
          noDiscount: 'Kein Rabatt angewendet',
          paymentReceiver: 'Zahlungsempfänger',
          receiverType: 'Empfängertyp',
          receiverName: 'Empfängername',
          additionalInfo: 'Zusätzliche Informationen',
          enrollmentType: 'Einschreibungstyp',
          enrolledBy: 'Eingeschrieben von',
          createdAt: 'Erstellt am'
        },
        discountForm: {
          createTitle: 'Rabatt erstellen',
          editTitle: 'Rabatt bearbeiten',
          createSubtitle: 'Neuen Rabattcode für Einschreibungen hinzufügen.',
          editSubtitle: 'Rabattdetails und Gültigkeitsbereich aktualisieren.',
          loadFailed: 'Rabatt konnte nicht geladen werden',
          codeLabel: 'Rabattcode',
          nameLabel: 'Rabattname',
          descriptionLabel: 'Beschreibung',
          typeLabel: 'Rabatttyp',
          valueLabel: 'Rabattwert',
          branchLabel: 'Filiale (optional)',
          levelLabel: 'Stufe (optional)',
          batchLabel: 'Gruppe (optional)',
          activeLabel: 'Aktiv',
          allBranches: 'Alle Filialen',
          selectBranch: 'Zuerst Filiale wählen',
          selectLevel: 'Zuerst Stufe wählen',
          saveFailed: 'Rabatt konnte nicht gespeichert werden',
          saving: 'Speichern…',
          create: 'Rabatt erstellen',
          update: 'Rabatt aktualisieren',
          codeRequired: 'Rabattcode ist erforderlich',
          nameRequired: 'Name ist erforderlich',
          valueRequired: 'Wert ist erforderlich',
          optionalDescription: 'Optionale Beschreibung',
          percentOption: 'Prozent (%)',
          fixedOption: 'Fester Betrag ({{currency}})',
          scopeTitle: 'Rabattumfang (optional)',
          scopeHint: 'Alle leer lassen für globalen Rabatt oder auf Filiale, Stufe oder Gruppe beschränken.',
          percentMaxAlert: 'Prozentrabatt darf 100 % nicht überschreiten',
          updateSuccess: 'Rabatt erfolgreich aktualisiert',
          createSuccess: 'Rabatt erfolgreich erstellt'
        },
        userDetails: {
          title: 'Benutzerdetails',
          loadFailed: 'Benutzerdetails konnten nicht geladen werden.',
          notFound: 'Benutzer nicht gefunden',
          editUser: 'Benutzer bearbeiten',
          backToList: 'Zurück zur Liste',
          personalInfo: 'Persönliche Informationen',
          contactInfo: 'Kontaktinformationen',
          accountInfo: 'Kontoinformationen',
          address: 'Adresse',
          noAddress: 'Keine Adresse hinterlegt',
          dateOfBirth: 'Geburtsdatum',
          gender: 'Geschlecht',
          memberSince: 'Mitglied seit',
          lastLogin: 'Letzte Anmeldung',
          walletBalance: 'Guthaben',
          activeEnrollments: 'Aktive Einschreibungen',
          totalSessions: 'Stunden gesamt',
          attendanceRate: 'Anwesenheitsquote',
          guardians: 'Erziehungsberechtigte',
          noGuardians: 'Keine Erziehungsberechtigten verknüpft',
          roles: 'Rollen',
          username: 'Benutzername',
          userId: 'Benutzer-ID',
          coachSummary: 'Trainerübersicht'
        },
        coachStudents: {
          title: 'Schüler',
          subtitle: 'Schüler auf Ihrer Liste — aus Ihren Stunden und Admin-Zuweisungen',
          searchPlaceholder: 'Schüler nach Name, Benutzername, E-Mail, Telefon oder ID suchen…',
          entityLabel: 'Schüler',
          loadFailed: 'Schüler konnten nicht geladen werden.',
          empty: 'Keine Schüler gefunden.',
          noMatch: 'Keine Schüler entsprechen Ihrer Suche'
        },
        studentEnrollments: {
          title: 'Meine Einschreibungen',
          subtitle: 'Ihre aktiven Kurseinschreibungen anzeigen',
          loadFailed: 'Einschreibungen konnten nicht geladen werden.',
          searchPlaceholder: 'Nach Stufe, Gruppe oder Datum suchen…',
          entityLabel: 'Einschreibungen',
          noMatch: 'Keine Einschreibungen entsprechen Ihrer Suche',
          empty: 'Noch keine Einschreibungen',
          payOnline: 'Online bezahlen'
        },
        studentSessions: {
          title: 'Meine Stunden',
          subtitle: 'Geplante Stunden und Anwesenheitsstatus anzeigen und durchsuchen.',
          loadFailed: 'Stunden konnten nicht geladen werden.',
          searchPlaceholder: 'Suchen… (Stufe, Gruppe, Trainer, Status, Anwesenheit)',
          empty: 'Keine Stunden gefunden.',
          noMatch: 'Keine Stunden entsprechen Ihrer Suche.'
        },
        studentAttendance: {
          title: 'Meine Anwesenheit',
          parentTitle: 'Anwesenheit',
          subtitle: 'Anwesenheitsverlauf über alle Gruppen hinweg',
          loadFailed: 'Anwesenheit konnte nicht geladen werden.',
          searchPlaceholder: 'Nach Status, Stufe, Gruppe, Trainer oder Zeit suchen…',
          entityLabel: 'Einträge',
          noMatch: 'Keine Einträge entsprechen Ihrer Suche',
          empty: 'Keine Anwesenheitseinträge gefunden'
        },
        wallet: {
          title: 'Mein Guthaben',
          childTitle: 'Kinderguthaben',
          subtitle: 'Saldo anzeigen, Guthaben aufladen und Transaktionsverlauf einsehen.',
          childSubtitle: 'Saldo anzeigen, online aufladen und Transaktionsverlauf Ihres Kindes einsehen.',
          balanceLabel: 'Ihr Guthabensaldo',
          balanceHint: 'Das Guthaben wird automatisch für Stunden verwendet.',
          onlineUnavailableTitle: 'Online-Zahlungen nicht verfügbar',
          onlineUnavailableDefault: 'Die Lizenz Ihrer Akademie enthält keine Online-Zahlungsgateways. Bitte kontaktieren Sie die Akademie oder zahlen Sie auf andere Weise.',
          loadFailed: 'Guthaben konnte nicht geladen werden. Bitte Seite aktualisieren.',
          enrollmentPaying: 'Zahlung für Einschreibung #{{id}}. Der Betrag wird Ihrem Guthaben gutgeschrieben und mit dieser Einschreibung verknüpft.',
          confirmingPayment: 'Zahlung wird bestätigt…',
          addMoney: 'Guthaben hinzufügen',
          addMoneyHint: 'Zahlungsmethode wählen und sicher bezahlen. Guthaben erscheint nach Bestätigung.',
          enterAmount: 'Betrag eingeben',
          securePayments: '🔒 Sichere Zahlungen über {{gateway}}',
          topupUnavailable: 'Online-Aufladung ist derzeit nicht verfügbar. Bitte kontaktieren Sie Ihre Akademie.',
          transactionHistory: 'Transaktionsverlauf',
          noTransactions: 'Noch keine Transaktionen.',
          downloadReceipt: 'Beleg herunterladen',
          invalidAmount: 'Bitte gültigen Betrag eingeben (Minimum {{symbol}}1).',
          selectPaymentMethod: 'Bitte Zahlungsmethode wählen.',
          invalidEnrollmentLink: 'Ungültiger Einschreibungslink. Öffnen Sie „Online bezahlen“ erneut unter Meine Einschreibungen.',
          paymentConfirmFailed: 'Zahlungsbestätigung fehlgeschlagen. Bitte Akademie kontaktieren.',
          paymentStartFailed: 'Zahlung konnte nicht gestartet werden. Bitte erneut versuchen.',
          paymentUpgradeRequired: 'Online-Zahlungen erfordern ein Lizenz-Upgrade.',
          paymentVerifyFailed: 'Zahlungsverifizierung fehlgeschlagen. Bitte Akademie kontaktieren.',
          receiptDownloadFailed: 'Beleg konnte nicht heruntergeladen werden. Bitte erneut versuchen.',
          razorpayTitle: 'Guthaben aufladen',
          razorpayDesc: 'Guthaben auf Ihr Akademiekonto einzahlen',
          txnAdvanceTopup: 'Vorauszahlung',
          txnOnlineTopup: 'Online-Aufladung',
          txnEnrollmentPayment: 'Einschreibungszahlung',
          txnSessionCharge: 'Stundenabbuchung',
          txnSessionReversal: 'Stornierung',
          txnDiscount: 'Rabattgutschrift',
          txnRefund: 'Erstattung',
          txnApplied: 'Auf Rechnung angewendet',
          txnDefault: 'Transaktion',
          payNow: 'Jetzt bezahlen',
          redirecting: 'Weiterleitung…',
          ref: 'Ref'
        },
        sessionStatement: {
          eyebrow: 'Berichte',
          title: 'Schüler-Stundenabrechnung',
          subtitle: 'Vollständiger Stundenverlauf mit Anwesenheit, Gebühren, Guthabengutschriften und -abbuchungen sowie laufendem Saldo.',
          embeddedSubtitle: 'Vollständiger Stundenverlauf mit Anwesenheit, Gebühren, Guthabengutschriften & -abbuchungen sowie laufendem Saldo.',
          filters: 'Filter',
          selectStudent: 'Schüler wählen',
          chooseStudent: 'Schüler auswählen…',
          fromDate: 'Von Datum',
          toDate: 'Bis Datum',
          generate: 'Abrechnung erstellen',
          generating: 'Laden…',
          selectStudentError: 'Bitte Schüler auswählen',
          loadFailed: 'Stundenabrechnung konnte nicht geladen werden',
          exportPdfFailed: 'PDF-Export fehlgeschlagen',
          student: 'Schüler',
          statementPeriod: 'Abrechnungszeitraum',
          openingBalance: 'Anfangssaldo',
          availableAtStart: 'Verfügbar zu Beginn',
          walletCredits: 'Guthabengutschriften',
          transactionsCount: '{{count}} Transaktionen',
          sessionCharges: 'Stundengebühren',
          sessionsPresentAbsent: '{{present}}Anw. / {{absent}}Abw. Stunden',
          closingBalance: 'Endsaldo',
          currentBalance: 'Aktueller Saldo',
          walletTransactions: 'Guthabentransaktionen',
          walletTransactionsHint: 'Aufladungen und Gutschriften auf Ihr Guthaben',
          sessionHistory: 'Stundenverlauf',
          sessionHistoryHint: 'Detaillierte Ansicht aller Stunden in diesem Zeitraum',
          noSessionsPeriod: 'Keine Stunden in diesem Zeitraum gefunden',
          discount: 'Rabatt: {{amount}}',
          emailLabel: 'Empfänger-E-Mail eingeben',
          present: 'Anwesend',
          absent: 'Abwesend'
        },
        notifications: {
          title: 'WhatsApp-Benachrichtigungen',
          subtitle: 'Wählen Sie, welche Akademienachrichten Sie per WhatsApp erhalten. Ihre Telefonnummer muss im Profil hinterlegt sein.',
          subtitleShort: 'Wählen Sie, welche Akademienachrichten Sie per WhatsApp erhalten.',
          loadFailed: 'Benachrichtigungseinstellungen konnten nicht geladen werden.',
          saveFailed: 'Speichern fehlgeschlagen.',
          saved: 'Einstellungen gespeichert.',
          optInTitle: 'WhatsApp-Nachrichten von meiner Akademie erhalten',
          optInHint: 'Standardmäßig aktiv. Deaktivieren, um alle WhatsApp-Benachrichtigungen zu stoppen.',
          sessionReminders: 'Stundenerinnerungen',
          absenceAlerts: 'Abwesenheitswarnungen',
          lowBalance: 'Warnungen bei niedrigem Guthaben',
          feeReminders: 'Gebühren- / offene Saldo-Erinnerungen',
          sessionCancelled: 'Stundenabsage-Hinweise',
          holidayAnnouncements: 'Feiertagsankündigungen',
          tryAgain: 'Erneut versuchen',
          unavailable: 'Nicht verfügbar',
          savePreferences: 'Einstellungen speichern',
          saving: 'Speichern…'
        },
        parentNotifications: {
          title: 'WhatsApp-Benachrichtigungen für Kind',
          subtitle: 'WhatsApp-Warnungen verwalten, die an die Telefonnummer Ihres Kindes im Profil gesendet werden.',
          saved: 'Einstellungen für Ihr Kind gespeichert.',
          optInTitle: 'WhatsApp für diesen Schüler aktivieren',
          optInHint: 'Warnungen gehen an die Telefonnummer im Profil Ihres Kindes.'
        },
        profile: {
          title: 'Mein Profil',
          subtitle: 'Persönliche und Adressdaten aktualisieren',
          loadFailed: 'Profil konnte nicht geladen werden',
          requestDataTitle: 'Meine Daten anfordern',
          requestDataBody: 'Laden Sie eine maschinenlesbare Kopie Ihrer Profildaten herunter, oder bitten Sie die Akademie-Administratoren um einen vollständigen Export (Auskunft / Portabilität).',
          downloadData: 'Meine Daten herunterladen (JSON)',
          downloadDataDone: 'Ihr Datenpaket wurde heruntergeladen.',
          downloadDataFailed: 'Datenpaket konnte nicht heruntergeladen werden.',
          requestData: 'Meine Daten anfordern',
          requestSent: 'Anfrage gesendet.',
          requestFailed: 'Anfrage konnte nicht gesendet werden. Später erneut versuchen.',
          changePassword: 'Passwort ändern',
          currentPassword: 'Aktuelles Passwort',
          newPassword: 'Neues Passwort',
          confirmPassword: 'Neues Passwort bestätigen',
          passwordMinHint: 'Mindestens 8 Zeichen',
          profileUpdated: 'Profil aktualisiert',
          profileUpdateFailed: 'Profil konnte nicht aktualisiert werden',
          passwordChanged: 'Passwort erfolgreich geändert.',
          passwordChangeFailed: 'Passwort konnte nicht geändert werden. Bitte erneut versuchen.',
          enterCurrentPassword: 'Bitte aktuelles Passwort eingeben.',
          passwordMinLength: 'Neues Passwort muss mindestens 8 Zeichen haben.',
          passwordsMismatch: 'Neue Passwörter stimmen nicht überein.',
          passwordSameAsCurrent: 'Neues Passwort muss sich vom aktuellen unterscheiden.',
          firstNameRequired: 'Vorname ist erforderlich und darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten.',
          middleNameLetters: 'Zweiter Vorname darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten.',
          firstNameLetters: 'Vorname darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten — keine Ziffern.',
          lastNameLetters: 'Nachname darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten — keine Ziffern.',
          lastNameRequired: 'Nachname ist erforderlich und darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten.',
          invalidDob: 'Bitte gültiges Geburtsdatum in der Vergangenheit eingeben.',
          invalidAddress: 'Ungültige Adresse.',
          invalidLatitude: 'Breitengrad muss eine gültige Zahl sein.',
          invalidLongitude: 'Längengrad muss eine gültige Zahl sein.',
          saveChanges: 'Änderungen speichern',
          saving: 'Speichern…',
          sending: 'Senden…',
          updating: 'Aktualisieren…',
          updatePassword: 'Passwort aktualisieren',
          retry: 'Erneut versuchen',
          preferredLanguage: 'Bevorzugte Sprache',
          preferredLanguageHint: 'Gilt für die App-Oberfläche sowie für E-Mails und Benachrichtigungen an Sie.',
          lang_en: 'English',
          lang_de: 'Deutsch',
          lang_fr: 'Français',
          lang_nl: 'Nederlands'
        },
        students: {
          title: 'Schüler',
          subtitle: 'Schülerzugang suchen, prüfen und verwalten — mit übersichtlicher Liste.',
          add: 'Schüler hinzufügen',
          bulkImport: 'Massenimport',
          searchPlaceholder: 'Schüler nach Name, Benutzername, E-Mail, Telefon oder ID suchen…',
          entityLabel: 'Schüler',
          exportFailed: 'Schüler konnten nicht exportiert werden. Bitte erneut versuchen.',
          colStudentName: 'Schülername'
        },
        managers: {
          title: 'Manager',
          subtitle: 'Admin-Benutzer mit besseren Filtern, klarer Hierarchie und einheitlichen Aktionen verwalten.',
          add: 'Manager hinzufügen',
          searchPlaceholder: 'Manager nach Name, Benutzername, E-Mail, Telefon oder ID suchen…',
          entityLabel: 'Manager',
          colManagerName: 'Managername'
        },
        branches: {
          title: 'Filialen',
          subtitle: 'Akademiefilialen verwalten, ältere Standorte archivieren und den Betrieb ordentlich halten.',
          add: 'Filiale hinzufügen',
          searchPlaceholder: 'Filialen nach Name, Code, Telefon, Manager oder ID suchen…',
          entityLabel: 'Filialen',
          loadFailed: 'Filialen konnten nicht geladen werden.',
          archiveConfirm: 'Diese Filiale archivieren? Sie wird deaktiviert, aber alle Daten bleiben erhalten.',
          archiveFailed: 'Filiale konnte nicht archiviert werden.',
          activateFailed: 'Filiale konnte nicht aktiviert werden.',
          managerFallback: 'Manager',
          colBranchName: 'Filialname',
          colCode: 'Code',
          colMode: 'Modus',
          colManagedBy: 'Verwaltet von',
          editTitle: 'Filiale bearbeiten',
          archiveTitle: 'Filiale archivieren',
          activateTitle: 'Filiale aktivieren'
        },
        users: {
          title: 'Benutzer',
          subtitle: 'Benutzerkonten und Berechtigungen verwalten',
          add: 'Benutzer hinzufügen',
          entityLabel: 'Benutzer'
        },
        discounts: {
          title: 'Rabattverwaltung',
          subtitle: 'Rabattcodes und Angebote verwalten',
          create: 'Rabatt erstellen',
          empty: 'Keine Rabatte gefunden',
          emptyHint: 'Erstellen Sie einen neuen Rabattcode.',
          loadFailed: 'Rabatte konnten nicht geladen werden',
          archiveConfirm: 'Diesen Rabatt wirklich archivieren?',
          activateConfirm: 'Diesen Rabatt wirklich aktivieren?',
          archiveSuccess: 'Rabatt erfolgreich archiviert',
          activateSuccess: 'Rabatt erfolgreich aktiviert',
          archiveFailed: 'Rabatt konnte nicht archiviert werden',
          activateFailed: 'Rabatt konnte nicht aktiviert werden',
          colDiscountName: 'Rabattname',
          colValue: 'Wert',
          colScope: 'Gültigkeitsbereich',
          scopeBranch: 'Filialspezifisch',
          scopeLevel: 'Stufenspezifisch',
          scopeBatch: 'Gruppenspezifisch',
          scopeGlobal: 'Global',
          typePercent: 'Prozent',
          typeFixed: 'Fester Betrag',
          editTitle: 'Rabatt bearbeiten',
          archiveTitle: 'Rabatt archivieren',
          activateTitle: 'Rabatt aktivieren'
        },
        atRisk: {
          eyebrow: 'Berichte',
          title: 'Gefährdete Schüler',
          subtitle: 'Schüler mit Anwesenheit unter Ihrer Schwelle — rechtzeitig nachfassen, bevor sie abbrechen.',
          daysLabel: 'Tage zurückblicken',
          daysSuffix: 'Tage',
          thresholdLabel: 'Mindestanwesenheit (%)',
          thresholdSuffix: '% Anwesenheit',
          loadFailed: 'Gefährdete Schüler konnten nicht geladen werden. Bitte erneut versuchen.',
          daysError: 'Tage müssen zwischen 1 und 365 liegen.',
          thresholdError: 'Schwelle muss zwischen 1 und 100 liegen.',
          riskCritical: 'Kritisch',
          riskHigh: 'Hoch',
          riskMedium: 'Mittel',
          tierCritical: '{{count}} kritisch',
          tierHigh: '{{count}} hoch',
          tierMedium: '{{count}} mittel',
          emptyTitle: 'Keine gefährdeten Schüler',
          emptyDescription: 'Alle aktiv eingeschriebenen Schüler haben in den letzten {{days}} Tagen eine Anwesenheit über {{threshold}} %.',
          colStudent: 'Schüler',
          colRisk: 'Risiko',
          colAttendanceRate: 'Anwesenheitsquote',
          colSessions: 'Stunden',
          colLastSeen: 'Zuletzt gesehen',
          colBatches: 'Gruppen',
          profile: 'Profil',
          emailAction: 'E-Mail',
          footer: '{{count}} Schüler mit <{{threshold}} % Anwesenheit in den letzten {{days}} Tagen. Schüler ohne Stunden im Zeitraum sind ausgeschlossen.',
          studentFallback: 'Schüler #{{id}}',
          coachFallback: 'Trainer #{{id}}'
        },
        auditLogs: {
          title: 'Prüfprotokolle',
          subtitle: 'Systemaktivitäten anzeigen und nachverfolgen',
          filterUser: 'Benutzer',
          filterTable: 'Tabelle',
          filterOperation: 'Vorgang',
          filterStartDate: 'Startdatum',
          filterEndDate: 'Enddatum',
          filterSearch: 'Suche',
          allUsers: 'Alle Benutzer',
          allTables: 'Alle Tabellen',
          allOperations: 'Alle Vorgänge',
          searchPlaceholder: 'In alten/neuen Daten suchen…',
          clearFilters: 'Filter zurücksetzen',
          exportCsv: 'Als CSV exportieren',
          fetchFailed: 'Prüfprotokolle konnten nicht geladen werden',
          exportFailed: 'Prüfprotokolle konnten nicht exportiert werden',
          empty: 'Keine Prüfprotokolle gefunden',
          colTable: 'Tabelle',
          colOperation: 'Vorgang',
          colRecordId: 'Datensatz-ID',
          colTimestamp: 'Zeitstempel',
          oldData: 'Alte Daten:',
          newData: 'Neue Daten:',

          tables: {
            users: 'Benutzer',
            sessions: 'Stunden',
            session: 'Stunde',
            batches: 'Gruppen',
            batch: 'Gruppe',
            branches: 'Filialen',
            address: 'Adresse',
            levels: 'Stufen',
            enrollment: 'Einschreibung',
            attendance: 'Anwesenheit',
            fee_plan: 'Gebührenplan',
            discount_master: 'Rabatt',
            wallet_txn: 'Wallet-Buchung',
            billing_settings: 'Abrechnungseinstellungen',
            coach_student_assignment: 'Trainer–Schüler-Zuordnung',
            session_participants: 'Stundenteilnehmer',
            password_reset_tokens: 'Passwort-Reset-Token',
            academy_holiday: 'Akademie-Feiertag',
            email_queue: 'E-Mail-Warteschlange',
            whatsapp_queue: 'WhatsApp-Warteschlange',
            auth: 'Authentifizierung'
          },
          ops: {
            INSERT: 'Einfügen',
            UPDATE: 'Aktualisieren',
            DELETE: 'Löschen',
            LOGIN: 'Anmeldung',
            LOGOUT: 'Abmeldung',
            PASSWORD_SET: 'Passwort gesetzt',
            PASSWORD_RESET: 'Passwort zurückgesetzt',
            BULK_UPDATE: 'Massenaktualisierung',
            ARCHIVE: 'Archivieren',
            ACTIVATE: 'Aktivieren',
            DEACTIVATE: 'Deaktivieren'
          },
          showingEntries: 'Einträge {{start}} bis {{end}} von {{total}}'
        },
        walletTopup: {
          eyebrow: 'Gebühren & Abrechnung',
          title: 'Guthaben aufladen',
          subtitle: 'Guthaben auf das Schülerkonto einzahlen. Dieses Guthaben kann für Stundengebühren verwendet werden und wird nach jeder Stunde automatisch abgebucht.',
          walletBadge: 'Guthaben',
          selectStudent: 'Schüler wählen',
          searchPlaceholder: 'Schüler nach Name suchen…',
          noStudents: 'Keine Schüler gefunden',
          change: 'Ändern',
          studentId: 'Schüler-ID: {{id}}',
          idLabel: 'ID: {{id}}',
          archivedWarning: 'Dieser Schüler ist derzeit nicht aktiv. Guthabenaufladungen können trotzdem erfasst werden.',
          archivedAlertTitle: 'Archivierter Schüler',
          archivedAlertMessage: '{{name}} ist derzeit nicht aktiv im System.\n\nDieser Schüler wurde archiviert. Sie können weiterhin eine Guthabenaufladung erfassen, aber er kann sich nicht anmelden oder reguläre Schülerfunktionen nutzen, bis ein Admin das Konto reaktiviert.',
          addFunds: 'Guthaben hinzufügen',
          enterPaymentDetails: 'Zahlungsdetails unten eingeben',
          amount: 'Betrag',
          paymentMode: 'Zahlungsart',
          paidOn: 'Bezahlt am',
          paidOnHint: 'Nur heute oder ein früheres Datum',
          reference: 'Referenz / Transaktions-ID',
          referenceOptional: '(Optional)',
          paymentReceivedBy: 'Zahlung erhalten von',
          staffMember: 'Mitarbeiter',
          otherPerson: 'Andere Person',
          selectStaff: 'Mitarbeiter wählen',
          chooseStaff: '-- Mitarbeiter wählen --',
          receiverName: 'Empfängername',
          receiverNamePlaceholder: 'Vollständigen Namen des Empfängers eingeben',
          clearForm: 'Formular leeren',
          recordPayment: 'Zahlung erfassen',
          processing: 'Verarbeitung…',
          chooseStudent: 'Schüler wählen',
          amountRequired: 'Betrag muss größer als 0 sein',
          paidOnRequired: 'Datum "Bezahlt am" wählen',
          paidOnFuture: 'Datum "Bezahlt am" darf nicht in der Zukunft liegen',
          paidOnInvalid: 'Gültiges Datum "Bezahlt am" eingeben',
          receiverTypeInvalid: 'Gültigen Zahlungsempfängertyp wählen (USER/OTHERS).',
          staffRequired: 'Mitarbeiter wählen, der die Zahlung erhalten hat.',
          receiverNameRequired: 'Name des Empfängers eingeben.',
          success: '✅ Guthabenaufladung erfolgreich! {{amount}} dem Schülerkonto gutgeschrieben.',
          failed: 'Guthabenaufladung konnte nicht erfasst werden.'
        }
      },
      cookie: {
        body: 'Diese App verwendet nur notwendige Cookies — zur Anmeldung und Sitzung. Kein Tracking, keine Werbung.',
        gotIt: 'Verstanden',
        privacy: 'Datenschutzerklärung',
        terms: 'Nutzungsbedingungen',
        and: 'und',
        ariaLabel: 'Cookie-Einwilligung'
      },
      enrollments: {
        title: 'Einschreibungen',
        subtitle: 'Schüler-Einschreibungen, Periodengebühren und Verlängerungen verwalten',
        newEnrollment: 'Neue Einschreibung',
        editEnrollment: 'Einschreibung bearbeiten',
        formSubtitle: 'Schüler in einer Gruppe mit Gebührenplan und Abrechnungsoptionen einschreiben.',
        validation: {
          loadEnrollmentFailed: 'Einschreibungsdaten konnten nicht geladen werden',
          loadFailed: 'Daten konnten nicht geladen werden',
          selectAtLeastOneStudent: 'Bitte mindestens einen Schüler auswählen',
          carryForwardRequired: 'Für diese Gruppe ist eine Übertragsentscheidung erforderlich',
          endDateRequired: 'Enddatum ist für Periodenabrechnungs-Einschreibungen erforderlich',
          saveFailed: 'Einschreibung konnte nicht gespeichert werden'
        },
        entityLabel: 'Einschreibungen',
        searchPlaceholder: 'Einschreibungen nach ID, Schülername, Gruppe oder Stufe suchen...',
        noMatch: 'Keine Einschreibungen entsprechen Ihrer Suche',
        empty: 'Keine Einschreibungen gefunden',
        emptyHint: 'Versuchen Sie, Ihre Suchbegriffe anzupassen',
        filters: {
          all: 'Alle',
          active: 'Aktiv',
          archived: 'Archiviert'
        },
        columns: {
          id: 'ID',
          student: 'Schüler',
          level: 'Stufe',
          batch: 'Gruppe',
          billing: 'Abrechnung',
          period: 'Periode',
          payment: 'Zahlung',
          expiry: 'Ablauf',
          status: 'Status',
          actions: 'Aktionen'
        },
        periodOngoing: 'laufend',
        expiresToday: 'Läuft heute ab',
        daysLeft: 'noch {{count}} T.',
        expired: 'Abgelaufen',
        notApplicable: 'k. A.',
        renew: 'Verlängern',
        renewTitle: 'Einschreibung für nächste Periode erstellen',
        view: 'Anzeigen',
        viewTitle: 'Einschreibungsdetails anzeigen',
        edit: 'Bearbeiten',
        editTitle: 'Einschreibung bearbeiten',
        archive: 'Archivieren',
        activate: 'Aktivieren',
        reactivateConfirm: 'Einschreibung #{{id}} reaktivieren? Nur möglich, wenn heute innerhalb des Einschreibungszeitraums liegt.',
        archiveFailed: 'Archivierung fehlgeschlagen.',
        cannotArchive: 'Sie können diese Einschreibung nicht archivieren.',
        activateFailed: 'Einschreibung konnte nicht aktiviert werden.',
        renewMissingData: 'Verlängerung nicht möglich — Schüler-, Stufen- oder Gruppendaten fehlen.',
        crossBranchWarning: 'Filialübergreifende Warnung',
        crossBranchEnrollment: 'Einschreibung #{{id}}',
        crossBranchDesc: '<strong>{{name}}</strong> ist außerdem aktiv in Filialen eingeschrieben, die <span class="text-orange-600 font-medium">nicht von Ihnen verwaltet werden</span>:',
        continueArchive: 'Mit Archivierung fortfahren',
        statusActive: 'Aktiv',
        statusArchived: 'Archiviert',
        form: {
          student: 'Schüler',
          selectStudents: 'Schüler auswählen ({{count}} ausgewählt)',
          searchStudents: 'Schüler suchen...',
          selectAll: 'Alle auswählen ({{count}})',
          clear: 'Leeren',
          level: 'Stufe',
          trainingBatch: 'Trainingsgruppe',
          select: 'Auswählen...',
          startDate: 'Einschreibungsbeginn',
          startDateHint: 'Datum, an dem der Schüler offiziell dieser Gruppe beitritt',
          endDate: 'Einschreibungsende',
          optional: '(Optional)',
          endDatePeriodHint: 'Standard aus Gruppenplan ({{interval}}); anpassbar.',
          endDateOpenHint: 'Leer lassen für unbefristete Einschreibung',
          carryForwardPolicy: 'Stunden-Übertragsrichtlinie',
          batchPolicy: 'Gruppenrichtlinie:',
          setCarryForward: 'Übertrag für diesen Schüler festlegen:',
          enableCarryForward: 'Übertrag aktivieren',
          disableCarryForward: 'Übertrag deaktivieren',
          carryForwardRequired: 'Diese Gruppe erfordert eine explizite Übertragsrichtlinie für jeden Schüler.',
          carryForwardBatchLevel: 'Übertrag wird auf Gruppenebene gesteuert',
          overrideBilling: 'Abrechnungsgebühren überschreiben (Optional)',
          hide: 'Ausblenden',
          show: 'Anzeigen',
          billingMode: 'Einschreibungs-Abrechnungsmodus',
          billingPeriod: 'Abrechnungsperiode',
          discountOff: '{{value}} Rabatt',
          periodFee: 'Periodengebühr',
          perSessionCharge: 'Stundengebühr',
          hourlyRate: 'Stundensatz',
          periodFeeAmount: 'Periodengebühr',
          useBatchDefault: 'Gruppenstandard verwenden',
          notConfigured: 'Nicht konfiguriert',
          pricingSummary: 'Preisübersicht',
          batchDefaultPricing: 'Gruppen-Standardpreise',
          chargesMode: 'Abrechnungsmodus:',
          perSession: 'Pro Stunde:',
          periodPlan: 'Periodenplan:',
          applyDiscount: 'Rabatt anwenden',
          applyDiscountCustom: 'Rabatt anwenden (gilt für angepasste Gebühren)',
          noDiscount: 'Kein Rabatt angewendet',
          discountApplied: '{{value}} % Rabatt wird angewendet',
          discountAppliedCustom: ' auf Ihre individuellen Preise',
          customChargesApplied: 'Individuelle Gebühren angewendet',
          customChargesDesc: 'Individuelle Preise für diesen Schüler (überschreibt Gruppenstandard)',
          customChargesDiscount: '✓ {{value}} % Rabatt „{{name}}" wird zusätzlich auf individuelle Gebühren angewendet',
          finalCharges: 'Endgültige Gebühren für diesen Schüler',
          pricingModel: 'Preismodell:',
          perHour: 'Pro Stunde:',
          updateEnrollment: 'Einschreibung aktualisieren',
          createEnrollment: 'Einschreibung erstellen',
          saving: 'Einschreibung wird gespeichert…',
          enrolling: 'Einschreibung {{current}} von {{total}}',
          preparing: 'Vorbereitung…',
          redirecting: 'Weiterleitung zur Einschreibungsliste...',
          successCount: '✅ {{count}} Schüler erfolgreich eingeschrieben',
          successCount_other: '✅ {{count}} Schüler erfolgreich eingeschrieben',
          failedCount: '❌ {{count}} Schüler konnte nicht eingeschrieben werden',
          failedCount_other: '❌ {{count}} Schüler konnten nicht eingeschrieben werden',
          renewBanner: 'Verlängerung von Einschreibung #{{id}}. Daten standardmäßig für nächste Periode — bei Bedarf anpassen.'
        },
        archiveModal: {
          title: 'Einschreibung archivieren',
          description: 'Enddatum für {{name}} (Einschreibung #{{id}}) festlegen.',
          enrolledOn: ' Eingeschrieben am {{date}}.',
          endDate: 'Enddatum',
          archiving: 'Archivierung…'
        },
        paymentModal: {
          title: 'Periodengebühr & Guthaben',
          enrolling: 'Einschreibung von {{name}}',
          review: 'Zahlung vor Abschluss der Einschreibung prüfen',
          periodFee: 'Periodengebühr:',
          plan: 'Plan:',
          walletBalance: 'Guthaben:',
          insufficient: '(unzureichend für volle Gebühr)',
          amountNeeded: 'Benötigter Betrag:',
          topUp: 'Guthaben aufladen',
          refreshing: 'Aktualisierung…',
          refreshBalance: 'Guthaben aktualisieren',
          paymentRequired: 'Ihre Akademie verlangt Zahlung vor der Anwesenheit. Bitte Guthaben belasten oder zuerst aufladen.',
          unpaidPriorTitle: 'Vorherige Periodengebühr noch unbezahlt',
          unpaidPriorDesc: 'Einschreibung #{{id}} ({{period}}) hat einen offenen Saldo von {{amount}}.',
          unpaidPriorAck: 'Ich verstehe und möchte mit dieser neuen Einschreibung fortfahren.',
          insufficientHelp: 'Das Guthaben deckt die volle Periodengebühr nicht. Wählen Sie, wie die Gebühr eingezogen werden soll. Wir versuchen es automatisch täglich und bei abgeschlossenen Stunden erneut.',
          collectLegend: 'Wie soll die Gebühr eingezogen werden?',
          chargeNow: 'Guthaben jetzt belasten',
          collectViaSchedule: 'In den nächsten Tagen einziehen',
          enrollUnpaid: 'Noch ohne Belastung einschreiben',
          chargeNowHelp: 'Die Periodengebühr wird sofort vom Guthaben des Schülers abgebucht.',
          chargeNowInsufficient: 'Das Guthaben wird sofort belastet. Laden Sie zuerst auf oder aktivieren Sie negatives Guthaben in den Abrechnungseinstellungen.',
          collectViaScheduleHelp: 'Wir versuchen die Gebühr täglich über einige Tage und erneut bei abgeschlossenen Stunden einzuziehen. Der Schüler kann gemäß den Kulanzregeln Ihrer Akademie teilnehmen.',
          enrollUnpaidHelp: 'Die Einschreibung wird ohne sofortige Belastung erstellt. Der Einzug folgt den geplanten Wiederholungsregeln Ihrer Akademie.',
          confirmEnroll: 'Bestätigen & einschreiben'
        },
        lockReasons: {
          student: 'Diese Einschreibung gehört zu diesem Schüler und kann nicht auf jemand anderen übertragen werden.',
          level: 'Die Stufe wurde bei Beitritt festgelegt und kann hier nicht geändert werden. Um den Schüler in eine andere Stufe zu verschieben, beenden Sie diese Einschreibung und erstellen Sie eine neue.',
          batch: 'Die Trainingsgruppe wurde bei Beitritt festgelegt und kann hier nicht geändert werden. Um den Schüler in eine andere Gruppe zu verschieben, beenden Sie diese Einschreibung und erstellen Sie eine neue.',
          startDate: 'Das Startdatum wurde bei Erstellung der Einschreibung gespeichert und kann danach nicht geändert werden.',
          endDateEditable: 'Festlegen oder aktualisieren, wann der Schüler diese Gruppe nicht mehr besuchen soll. Bereits abgehaltene oder abgerechnete Stunden sind nicht betroffen.',
          carryForward: 'Diese Übertragsentscheidung wurde bei der Einschreibung getroffen und kann hier nicht geändert werden.',
          carryForwardBatch: 'Der Übertrag für diese Gruppe gilt für alle Schüler gleich und wird in den Gruppeneinstellungen verwaltet.',
          billingMode: 'Der Abrechnungstyp für diese Einschreibung kann nach der Einschreibung nicht geändert werden.',
          billingAmount: 'Dieser Gebührenbetrag kann bei einer bestehenden Einschreibung nicht geändert werden.',
          customRateEditable: 'Aktualisiert den individuellen Satz nur für künftige Stunden. Bereits erfolgte Belastungen bleiben unverändert.',
          customRateLocked: 'Individuelle Preise können nur bei der ersten Einschreibung festgelegt werden, nicht bei der Bearbeitung.',
          discount: 'Der Rabatt für diese Einschreibung kann nach der Einschreibung nicht geändert werden.',
          systemManaged: 'Diese Einschreibung wurde automatisch für eine Einzelstunde erstellt und kann nicht bearbeitet werden.'
        }
      },
      sessions: {
        title: 'Stunden',
        subtitle: 'Nach Stufe und Gruppe filtern. Anwesenheit erfassen, Stunden bearbeiten oder erstellen.',
        addWalkIn: 'Spontanstunde hinzufügen',
        addWalkInTitle: 'Einmalige Stunde für Spontanteilnehmer erstellen',
        addIndividual: 'Einzelstunde hinzufügen',
        addIndividualTitle: 'Individuelle Einzelstunde erstellen',
        addBatch: 'Gruppenstunden hinzufügen',
        addBatchTitle: 'Gruppenstunden hinzufügen',
        selectBatchTitle: 'Gruppe für Stundenerstellung auswählen',
        showingOnDate: 'Stunden am',
        selectLevelBatch: ' — Stufe und Gruppe wählen, um Stunden zu laden',
        clearDateFilter: 'Datumsfilter löschen',
        filterLevel: 'Nach Stufe filtern',
        filterBatch: 'Nach Gruppe filtern',
        selectLevel: 'Stufe wählen…',
        selectBatch: 'Gruppe wählen…',
        selectLevelFirst: 'Zuerst Stufe wählen',
        sessionStatus: 'Stundenstatus',
        allStatuses: 'Alle Status',
        sortBy: 'Sortieren nach',
        sortDateDesc: 'Datum (Neueste zuerst)',
        sortDateAsc: 'Datum (Älteste zuerst)',
        sortStatus: 'Status (Ausstehend → Abgeschlossen)',
        sortIdDesc: 'Stunden-ID (Hoch zu Niedrig)',
        sortIdAsc: 'Stunden-ID (Niedrig zu Hoch)',
        searchSessions: 'Stunden suchen',
        searchPlaceholder: 'Stunden suchen...',
        showingResults: '{{count}} Ergebnis angezeigt',
        showingResults_other: '{{count}} Ergebnisse angezeigt',
        statusFilter: 'Status:',
        clearFilters: 'Filter löschen',
        selectLevelBatchEmpty: 'Stufe und Gruppe wählen',
        selectLevelBatchHint: 'Wählen Sie oben aus den Filtern, um Stunden anzuzeigen',
        noSessions: 'Keine Stunden gefunden',
        noSessionsHint: 'Erstellen Sie Ihre erste Stunde für diese Gruppe',
        sessionNumber: 'Stunde #{{id}}',
        walkIn: 'Spontan',
        level: 'Stufe',
        batch: 'Gruppe',
        coach: 'Trainer',
        notAssigned: 'Nicht zugewiesen',
        statusControl: 'Statussteuerung',
        statusPending: 'Ausstehend',
        statusCompleted: 'Abgeschlossen',
        statusCancelled: 'Abgesagt',
        completedLocked: 'Abgeschlossene Stunden können nicht geändert werden',
        cancelledLocked: 'Abgesagte Stunden können nicht geändert werden',
        saveStatus: 'Status speichern',
        save: 'Speichern',
        saving: 'Speichern…',
        viewAttendance: 'Anwesenheit anzeigen',
        viewAttendanceCancelled: 'Anwesenheit für abgesagte Stunde anzeigen',
        viewAttendanceCompleted: 'Erfasste Anwesenheit für diese abgeschlossene Stunde anzeigen',
        updateNotes: 'Stundennotizen aktualisieren',
        updateNotesTitle: 'Stundennotizen/Thema aktualisieren',
        reverse: 'Stornieren',
        reversing: 'Stornierung...',
        reverseTitle: 'Diese abgeschlossene Stunde stornieren und Schüler gutschreiben',
        markAttendance: 'Anwesenheit erfassen',
        markAttendanceTitle: 'Anwesenheit für diese Stunde erfassen',
        viewAttendanceSession: 'Anwesenheit für diese Stunde anzeigen',
        attendanceCompletedLocked: 'Anwesenheit kann für abgeschlossene Stunden nicht erfasst werden',
        attendanceCancelledLocked: 'Anwesenheit kann für abgesagte Stunden nicht erfasst werden',
        edit: 'Bearbeiten',
        editTitle: 'Stundendetails bearbeiten',
        pagination: 'Zeige {{start}} bis {{end}} von {{total}} Stunden',
        rowsPerPage: 'Zeilen pro Seite:',
        loadFailed: 'Stunden konnten nicht geladen werden.',
        loadDateFailed: 'Stunden für ausgewähltes Datum konnten nicht geladen werden.',
        markBeforeComplete: 'Bitte Anwesenheit erfassen, bevor die Stunde als ABGESCHLOSSEN markiert wird.',
        completeSuccess: 'Stunde als ABGESCHLOSSEN markiert. Schülerabrechnung wurde verarbeitet.',
        completeFailed: 'Stunde konnte nicht abgeschlossen und Abrechnung verarbeitet werden:\\n\\n{{message}}\\n\\nStellen Sie sicher, dass Schüler ausreichend Guthaben haben, und versuchen Sie es erneut.',
        statusUpdateFailed: 'Stundenstatus konnte nicht aktualisiert werden: {{message}}',
        reversalReasonRequired: 'Stornierungsgrund ist erforderlich.',
        reversalConfirm: 'Die Stornierung dieser Stunde wird:\\n- Beträge auf Schülerguthaben zurückbuchen\\n- Die Stunde als ABGESAGT markieren\\n\\nMöchten Sie fortfahren?',
        reversalSuccess: 'Stunde erfolgreich storniert.',
        reversalFailed: 'Stunde konnte nicht storniert werden: {{message}}',
        walkInCreated: 'Spontanstunde erfolgreich erstellt!\\nStunden-ID: {{sessionId}}\\nEinschreibungs-ID: {{enrollmentId}}',
        individualCreated: 'Einzelstunde erfolgreich erstellt!\\nStunden-ID: {{sessionId}}\\nGruppe: {{batch}}\\nSchüler: {{student}}\\nGebühr: {{charge}}',
        reversalModal: {
          title: 'Stunde #{{id}} stornieren',
          willDo: 'Diese Aktion wird:',
          creditWallets: 'Beträge auf alle Schülerguthaben zurückbuchen',
          markCancelled: 'Die Stunde als ABGESAGT markieren',
          compensatingTxns: 'Ausgleichende Guthabentransaktionen erstellen',
          recordReason: 'Stornierungsgrund für Audit protokollieren',
          reasonLabel: 'Stornierungsgrund',
          reasonPlaceholder: 'z. B. Stunde wegen Trainerausfall abgesagt...',
          confirm: 'Stornierung bestätigen'
        },
        form: {
          editTitle: 'Stunde bearbeiten',
          addTitle: 'Gruppenstunden hinzufügen',
          editSubtitle: 'Stundendetails aktualisieren',
          addSubtitle: 'Wiederkehrende Stunden für Ihre Gruppe planen',
          close: 'Schließen',
          trainingBatch: 'Trainingsgruppe',
          selectBatch: 'Gruppe wählen...',
          sessionCoach: 'Stundentrainer',
          selectCoach: 'Trainer wählen...',
          autoSelected: 'Automatisch ausgewählt',
          sessionTiming: 'Stundenzeit',
          startTime: 'Stundenbeginn',
          endTime: 'Stundenende',
          selectStartTime: 'Startzeit wählen',
          selectEndTime: 'Endzeit wählen',
          sessionDate: 'Stundendatum',
          dateRange: 'Datumsbereich',
          date: 'Datum ({{format}})',
          from: 'Von ({{format}})',
          to: 'Bis ({{format}})',
          selectDays: 'Wochentage für Wiederholung wählen',
          selected: 'Ausgewählt:',
          update: 'Aktualisieren',
          create: 'Erstellen',
          updating: 'Aktualisierung...',
          creating: 'Erstellung...',
          fillRequired: 'Bitte alle Pflichtfelder ausfüllen.',
          endAfterStart: 'Endzeit muss nach Startzeit liegen',
          selectDay: 'Bitte mindestens einen Tag wählen.',
          invalidDateRange: 'Ungültiger Datumsbereich.',
          invalidDate: 'Ungültiges Datum.',
          updated: 'Stunde erfolgreich aktualisiert.',
          created: '{{count}} Stunde(n) erstellt.',
          updateFailed: 'Stunde konnte nicht aktualisiert werden.',
          createFailed: 'Stunden konnten nicht erstellt werden.',
          longSessionWarning: '⚠️ Warnung: Lange Stundendauer\\n\\nStundendatum/-daten: {{dates}}\\nStartzeit: {{start}}\\nEndzeit: {{end}}\\nDauer: {{duration}}\\n\\nDiese Stunde dauert über 3 Stunden. Fortfahren?',
          hour: '{{count}} Stunde',
          hour_other: '{{count}} Stunden',
          minute: '{{count}} Minute',
          minute_other: '{{count}} Minuten'
        },
        walkInModal: {
          title: 'Spontanstunde hinzufügen',
          info: 'Spontanstunde:',
          infoBody: 'Erstellt eine einmalige Stunde für einen Schüler ohne reguläre Einschreibung. Das System legt bei Bedarf automatisch Einschreibung und Gruppe an.',
          students: 'Schüler',
          searchStudents: 'Schüler suchen und auswählen...',
          noStudentsFound: 'Keine Schüler gefunden',
          allStudentsSelected: 'Alle Schüler ausgewählt',
          searchHint: 'Tippen, um einen oder mehrere Schüler zu suchen und auszuwählen',
          sessionDetails: 'Stundendetails',
          selectLevel: 'Stufe wählen...',
          sessionCoach: 'Stundentrainer',
          selectCoach: 'Trainer wählen...',
          coachAutoNote: 'Automatisch ausgewählt (Trainer können nur Stunden für sich selbst erstellen)',
          timing: 'Zeitplan',
          sessionDate: 'Stundendatum',
          startTime: 'Startzeit',
          endTime: 'Endzeit',
          selectTime: 'Zeit wählen',
          sessionCharge: 'Stundengebühr ({{symbol}})',
          enterAmount: 'Betrag eingeben',
          chargeNote: 'Dies ist die Gesamtgebühr für die gesamte Stundendauer, kein Stundensatz.',
          creating: 'Erstellung...',
          create: 'Spontanstunde erstellen',
          fillRequired: 'Bitte alle Pflichtfelder ausfüllen und mindestens einen Schüler auswählen',
          endAfterStart: 'Endzeit muss nach Startzeit liegen',
          createFailed: 'Spontanstunde konnte nicht erstellt werden',
          longSessionTitle: 'Warnung: Lange Stundendauer',
          longSessionProceed: 'Diese Stunde dauert über 3 Stunden. Fortfahren?',
          sessionDateLabel: 'Stundendatum:',
          startTimeLabel: 'Startzeit:',
          endTimeLabel: 'Endzeit:',
          durationLabel: 'Dauer:',
          userFallback: 'Benutzer #{{id}}',
          idLabel: 'ID: #{{id}}'
        },
        individualModal: {
          title: 'Einzelstunde hinzufügen',
          autoActions: 'Automatische Aktionen:',
          autoPresent: 'Anwesenheit wird automatisch als ANWESEND markiert',
          autoCharge: 'Stundengebühr wird sofort vom Schülerguthaben abgebucht',
          autoCompleted: 'Stundenstatus wird auf ABGESCHLOSSEN gesetzt (für die Buchhaltung)',
          sessionDetails: 'Stundendetails',
          selectLevel: 'Stufe wählen',
          studentName: 'Schülername',
          selectStudent: 'Schüler wählen',
          sessionCoach: 'Stundentrainer',
          selectCoach: 'Trainer wählen',
          timing: 'Zeitplan',
          sessionDate: 'Stundendatum',
          startTime: 'Startzeit',
          endTime: 'Endzeit',
          selectTime: 'Zeit wählen',
          sessionTopic: 'Stundenthema',
          topicPlaceholder: 'z. B. Eröffnungstheorie, Endspieltraining',
          sessionCharge: 'Stundengebühr ({{symbol}})',
          chargePlaceholder: 'z. B. 600',
          chargeNote: 'Der angezeigte Standardsatz ist ein Stundensatz. Er passt sich automatisch an die Stundendauer an. Sie können den Betrag manuell ändern.',
          creating: 'Erstellung...',
          create: 'Einzelstunde erstellen',
          selectLevelError: 'Bitte eine Stufe wählen',
          selectStudentError: 'Bitte einen Schüler wählen',
          selectCoachError: 'Bitte einen Trainer wählen',
          selectDateError: 'Bitte ein Stundendatum wählen',
          selectStartError: 'Bitte eine Startzeit wählen',
          selectEndError: 'Bitte eine Endzeit wählen',
          topicRequired: 'Bitte ein Stundenthema eingeben',
          chargeRequired: 'Bitte einen gültigen Gebührenbetrag eingeben',
          endAfterStart: 'Endzeit muss nach Startzeit liegen',
          invalidDate: 'Bitte ein gültiges Stundendatum eingeben.',
          createFailed: 'Einzelstunde konnte nicht erstellt werden',
          longSessionTitle: 'Warnung: Lange Stundendauer',
          longSessionProceed: 'Diese Stunde dauert über 3 Stunden. Fortfahren?',
          sessionDateLabel: 'Stundendatum:',
          startTimeLabel: 'Startzeit:',
          endTimeLabel: 'Endzeit:',
          durationLabel: 'Dauer:'
        },
        updateNotesModal: {
          title: 'Stundennotizen aktualisieren',
          subtitle: 'Stunde #{{sessionId}} - {{batchName}}',
          sessionNotes: 'Stundennotizen',
          placeholder: 'Stundennotizen eingeben...',
          charCount: '{{count}} / 2000 Zeichen',
          saving: 'Speichern...',
          saveNotes: 'Notizen speichern',
          updateFailed: 'Stundennotizen konnten nicht aktualisiert werden. Bitte erneut versuchen.'
        }
      },
      attendance: {
        markTitle: 'Anwesenheit erfassen',
        viewTitle: 'Stundenanwesenheit',
        viewLoading: 'Anwesenheitsdetails werden geladen…',
        coach: 'Trainer',
        status: 'Status',
        sessionNotes: 'Stundennotizen',
        sessionNotesRequired: 'Stundennotizen',
        sessionNotesPlaceholder: 'Notizen für diese Stunde vor der Anwesenheitserfassung hinzufügen',
        sessionNotesHint: 'Erforderlich vor Speichern oder Abschluss der Stunde',
        searchStudents: 'Schüler suchen… (Name oder #id)',
        warning: 'Warnung',
        success: 'Erfolg',
        markAllPresent: 'Alle anwesend',
        markAllAbsent: 'Alle abwesend',
        saveAttendance: 'Anwesenheit speichern',
        saving: 'Speichern…',
        completeSession: 'Stunde abschließen',
        completing: 'Abschluss…',
        completeTitle: 'Anwesenheit speichern und diese Stunde als ABGESCHLOSSEN markieren.',
        alreadyCompleted: 'Stunde ist bereits als ABGESCHLOSSEN markiert.',
        cancelledCannotComplete: 'Abgesagte Stunden können nicht abgeschlossen werden.',
        loadingStudents: 'Schüler werden geladen…',
        checkInOutInfo: 'Check-in- und Check-out-Zeiten',
        checkInOutHint: 'Diese Zeiten sind optional. Nur ausfüllen, wenn die Anwesenheitszeit vom geplanten Stundenzeit abweicht.',
        student: 'Schüler',
        attendanceStatus: 'Anwesenheitsstatus',
        checkIn: 'Check-in-Zeit',
        checkOut: 'Check-out-Zeit',
        notMarked: 'Nicht erfasst',
        present: 'Anwesend',
        absent: 'Abwesend',
        notApplicable: 'Nicht zutreffend',
        selectCheckIn: 'Check-in-Zeit wählen',
        selectCheckOut: 'Check-out-Zeit wählen',
        noStudentsMatch: 'Keine Schüler entsprechen „{{query}}".',
        walkInStudents: 'Spontanteilnehmer ({{count}})',
        addWalkIn: '+ Spontanteilnehmer hinzufügen',
        duplicateWalkIns: '{{count}} Spontaneintrag überschneidet sich mit eingeschriebenen Gruppenschülern und ist unten ausgeblendet.',
        duplicateWalkIns_other: '{{count}} Spontaneinträge überschneiden sich mit eingeschriebenen Gruppenschülern und sind unten ausgeblendet.',
        removeDuplicate: 'Duplikat entfernen: {{name}}',
        noWalkIns: 'Keine Spontanteilnehmer für diese Stunde',
        walkInHint: 'Spontanteilnehmer werden automatisch als anwesend markiert und bei Stundenabschluss belastet',
        notes: 'Notizen',
        actions: 'Aktionen',
        remove: 'Entfernen',
        sessionStart: 'Stundenbeginn',
        sessionEnd: 'Stundenende',
        walkInAdded: 'Spontanteilnehmer erfolgreich hinzugefügt!',
        walkInRemoved: 'Spontanteilnehmer erfolgreich entfernt!',
        removeWalkInConfirm: 'Diesen Spontanteilnehmer entfernen? Er wird für diese Stunde nicht belastet.',
        removeWalkInFailed: 'Spontanteilnehmer konnte nicht entfernt werden',
        notesRequiredSave: 'Stundennotizen sind vor dem Speichern der Anwesenheit erforderlich.',
        notesRequiredComplete: 'Stundennotizen sind vor dem Abschluss der Stunde erforderlich.',
        markBeforeComplete: 'Bitte Anwesenheit erfassen, bevor die Stunde abgeschlossen wird.',
        savedAck: 'Anwesenheit gespeichert um {{time}}. Sie können sie aktualisieren, bis die Stunde als ABGESCHLOSSEN markiert ist.',
        completeConfirm: 'Diese Stunde als ABGESCHLOSSEN markieren? Nach Abschluss kann sie nicht mehr geändert werden.',
        completeSuccess: 'Stunde erfolgreich als ABGESCHLOSSEN markiert!',
        completeFailed: 'Stunde konnte nicht abgeschlossen werden: {{message}}',
        saveFailed: 'Anwesenheit konnte nicht gespeichert werden.',
        loadFailed: 'Anwesenheitsdaten konnten nicht geladen werden.',
        sessionIdMissing: 'Stunden-ID fehlt in Route/Status.',
        batchUnknown: 'Gruppe für diese Stunde unbekannt. Es werden nur Schüler mit bestehenden Einträgen angezeigt.',
        batchStudents: 'Gruppenschüler',
        walkInLabel: 'Spontanteilnehmer',
        type: 'Typ',
        walkInType: 'Spontan',
        carryForward: 'Übertrag',
        carryForwardColumnHelp: 'Übertrag steuert die Abrechnung, wenn ein Schüler abwesend ist. Wenn aktiviert, wird die Stundengebühr nicht abgezogen und das Guthaben kann später genutzt werden. Wenn deaktiviert, wird die Gebühr auch bei Abwesenheit abgezogen.',
        sessionCancelled: 'Stunde abgesagt',
        cancelledDesc: 'Diese Stunde wurde abgesagt/storniert. Alle Abrechnungsgebühren wurden erstattet.',
        cancelledHistorical: ' Die unten angezeigten Anwesenheitsdaten sind historisch und nicht mehr aktiv.',
        missingSession: 'Stundeninformationen fehlen.',
        loadDetailsFailed: 'Anwesenheitsdetails konnten nicht geladen werden.',
        addWalkInStudent: {
          title: 'Spontanteilnehmer hinzufügen',
          selectStudent: 'Schüler wählen',
          searchPlaceholder: 'Nach Name oder E-Mail suchen...',
          alreadyOnSession: 'Passende Schüler sind bereits in dieser Stunde und können nicht als Spontanteilnehmer hinzugefügt werden.',
          noMatch: 'Keine weiteren Schüler entsprechen Ihrer Suche',
          allOnSession: 'Alle verfügbaren Schüler sind bereits in dieser Stunde',
          noStudents: 'Keine Schüler gefunden',
          change: 'Ändern',
          checkIn: 'Check-in-Zeit (optional)',
          checkInHint: 'Leer lassen, um Stundenstartzeit zu verwenden',
          checkOut: 'Check-out-Zeit (optional)',
          checkOutHint: 'Leer lassen, um Stundenendzeit zu verwenden',
          notes: 'Notizen (optional)',
          notesPlaceholder: 'Zusätzliche Notizen...',
          adding: 'Hinzufügen...',
          add: 'Spontanteilnehmer hinzufügen',
          selectRequired: 'Bitte einen Schüler wählen',
          alreadyOnRoster: 'Dieser Schüler ist bereits auf der Stundenliste oder Spontanliste',
          loadFailed: 'Schüler konnten nicht geladen werden',
          addFailed: 'Spontanteilnehmer konnte nicht hinzugefügt werden',
          noteTitle: 'Hinweis:',
          noteBody: 'Spontanteilnehmer werden bei Stundenabschluss automatisch mit dem Stundensatz belastet. Der Betrag wird vom Guthaben abgebucht. Bereits in dieser Gruppe eingeschriebene Schüler werden hier nicht angezeigt.'
        }
      },
      reports: {
        eyebrow: 'Berichte',
        title: 'Berichte',
        subtitle: 'Detaillierte Berichte für Ihre Akademie anzeigen und exportieren',
        loading: 'Berichte werden geladen…',
        noReportsEnabled: 'Keine Berichte aktiviert',
        noReportsHint: 'Ihr Akademie-Administrator hat alle Berichte deaktiviert. Wenden Sie sich an einen Admin unter Berichte → Berichteinstellungen.',
        tabs: {
          'coach-revenue': 'Trainereinnahmen & Provision',
          'session-statement': 'Schüler-Stundenabrechnung',
          'batch-attendance': 'Gruppen-Anwesenheit',
          'academy-revenue': 'Akademie-Einnahmenübersicht',
          'student-attendance': 'Schüler-Anwesenheitsübersicht',
          'payments-due': 'Offene Zahlungen',
          'data-export': 'Datenexport',
          'sessions-summary': 'Stundenübersicht',
          'monthly-statements': 'Monatsabrechnungssteuerung'
        },
        tabsManager: {
          'session-statement': 'Stundenabrechnung'
        },
        descriptions: {
          'coach-revenue': 'Stundeneinnahmen mit Aufteilung Akademie vs. Trainerprovision.',
          'session-statement': 'Gebührenkonto pro Schüler mit Zahlungen, Stundengebühren und PDF-Export.',
          'batch-attendance': 'Matrix Schüler vs. Stunden für eine Gruppe in einem Datumsbereich.',
          'academy-revenue': 'Monatliche und filialbezogene Bruttoeinnahmen mit Provisionsaufschlüsselung.',
          'student-attendance': 'Anwesenheitsquoten und -zahlen pro Schüler für einen Zeitraum.',
          'payments-due': 'Momentaufnahme der Schüler mit überfälligen oder niedrigen Guthaben, die Gebühreneinzug erfordern.',
          'data-export': 'CSV-Exporte für Schülerliste, Erziehungsberechtigte und Guthabentransaktionen.',
          'sessions-summary': 'Stundendatum/-zeit, Stufe, Gruppe, Trainer und Thema für Schüler oder Trainer in einem Datumsbereich.',
          'monthly-statements': 'Monatliche Stundenabrechnungs-E-Mails mit Gebührenschätzungen vorschauen, senden und verfolgen.'
        },
        dataExport: {
          title: 'Datenexport',
          subtitle: 'Akademiedaten als CSV-Dateien herunterladen. Alle Exporte enthalten UTF-8-BOM für korrekte Anzeige in Excel.',
          preparing: 'Vorbereitung…',
          from: 'Von',
          to: 'Bis',
          studentRoster: 'Schülerliste',
          studentRosterDesc: 'Alle Schüler mit Kontaktdaten, Geburtsdatum, optionalen FIDE/Elo-Feldern, aktiven Einschreibungsgruppen und aktuellem Guthaben.',
          downloadStudents: 'students.csv herunterladen',
          guardianLinks: 'Erziehungsberechtigten-Verknüpfungen',
          guardianLinksDesc: 'Schüler–Erziehungsberechtigten-Beziehungen und Verknüpfungsstatus für DSAR / elterlichen Zugriff.',
          downloadGuardians: 'guardians.csv herunterladen',
          walletTransactions: 'Guthabentransaktionen',
          walletTransactionsDesc: 'Alle Guthaben-Gutschriften und -Belastungen für den gewählten Zeitraum — einschließlich Schülername, Betrag, Grund, Zahlungsmethode und Referenz.',
          downloadTransactions: 'transactions.csv herunterladen',
          footer: 'CSV-Dateien öffnen sich direkt in Microsoft Excel, Google Sheets und LibreOffice Calc. Bei großen Akademien kann der Export einige Sekunden dauern.',
          invalidDateRange: 'Bitte gültigen Datumsbereich wählen.',
          exportStudentsFailed: 'Schülerliste konnte nicht exportiert werden. Bitte erneut versuchen.',
          exportGuardiansFailed: 'Erziehungsberechtigten-Verknüpfungen konnten nicht exportiert werden. Bitte erneut versuchen.',
          exportTxnsFailed: 'Transaktionen konnten nicht exportiert werden. Bitte erneut versuchen.',
          dsarBannerTitle: 'Datenauskunft — hier handeln',
          dsarBannerBody: '{{name}} (Benutzer #{{userId}}, {{username}}) hat eine Kopie der personenbezogenen Daten beantragt. Laden Sie die Exporte unten herunter und öffnen Sie bei Bedarf das Profil zur Identitätsprüfung.',
          dsarUnknownName: 'Dieser Benutzer',
          dsarOpenProfile: 'Profil öffnen',
          dsarOpenWallet: 'Wallet-Aufladung öffnen',
          studentFilter: 'Schüler',
          studentFilterHint: 'Alle CSV-Downloads unten verwenden diese Auswahl. Wählen Sie einen Schüler für ein DSAR-Paket oder Alle Schüler für akademieweite Exporte.',
          chooseStudent: 'Schüler suchen…',
          allStudents: 'Alle Schüler',
          studentFallback: 'Schüler #{{id}}',
          scopedToStudent: 'Exportumfang: {{student}}',
          scopedToAll: 'Exportumfang: alle Schüler',
          dsarLocked: 'DSAR-Fokus',
          clearStudentFilter: 'Schülerfilter löschen',
          studentRosterDescOne: 'Kontaktdaten, Geburtsdatum, optionale FIDE/Elo-Felder, aktive Gruppen und Wallet-Stand nur für den gewählten Schüler.',
          guardianLinksDescOne: 'Erziehungsberechtigten-Beziehungen und Status nur für den gewählten Schüler.',
          walletTransactionsDescOne: 'Wallet-Gutschriften und -Belastungen nur für den gewählten Schüler und Zeitraum.',
          downloadOneStudent: 'Diesen Schüler als CSV laden',
          downloadOneGuardians: 'Guardians.csv für diesen Schüler',
          downloadOneTransactions: 'Transaktionen.csv für diesen Schüler',
          walletStudentFilter: 'Schüler',
          walletExportingFor: 'Exportiere Transaktionen für {{student}}',
          studentRequiredForTxns: 'Wählen Sie einen Schüler, bevor Sie Wallet-Transaktionen herunterladen.',
          studentsLoadFailed: 'Schüler konnten nicht geladen werden. Prüfen Sie, ob das Backend läuft, und aktualisieren Sie.',
          studentsEmpty: 'Keine aktiven Schüler gefunden.'
        },
        common: {
          filters: 'Filter',
          fromDate: 'Von Datum',
          toDate: 'Bis Datum',
          from: 'Von',
          to: 'Bis',
          filterByBranch: 'Nach Filiale filtern',
          allBranches: 'Alle Filialen',
          filterByCoach: 'Nach Trainer filtern',
          allCoaches: 'Alle Trainer',
          filterByLevel: 'Nach Stufe filtern',
          selectLevel: 'Stufe wählen',
          allLevels: 'Alle Stufen',
          filterByBatch: 'Nach Gruppe filtern',
          selectBatch: 'Gruppe wählen',
          allBatches: 'Alle Gruppen',
          search: 'Suchen',
          generate: 'Generieren',
          generateReport: 'Bericht generieren',
          loadingReport: 'Berichtsdaten werden geladen…',
          failedToLoad: 'Bericht konnte nicht geladen werden.',
          selectDateRange: 'Bitte Datumsbereich wählen',
          invalidDateFormat: 'Ungültiges Datumsformat. Bitte {{format}} verwenden',
          invalidDateRange: 'Bitte gültigen Datumsbereich wählen.',
          selectFromToBatch: 'Bitte Von-Datum, Bis-Datum und Gruppe wählen',
          enterEmail: 'E-Mail-Adresse eingeben',
          enterEmailAlert: 'Bitte E-Mail-Adresse eingeben',
          emailSent: 'Bericht erfolgreich per E-Mail gesendet',
          emailFailed: 'E-Mail konnte nicht gesendet werden',
          exportPdfFailed: 'PDF-Export fehlgeschlagen',
          close: 'Schließen',
          present: 'Anwesend',
          absent: 'Abwesend',
          upcoming: 'Bevorstehend',
          ongoing: 'Laufend',
          past: 'Vergangen',
          walkIn: 'Spontan',
          student: 'Schüler',
          studentId: 'Schüler-ID',
          batch: 'Gruppe',
          level: 'Stufe',
          branch: 'Filiale',
          coach: 'Trainer',
          sessions: 'Stunden',
          status: 'Status',
          email: 'E-Mail',
          phone: 'Telefon',
          amount: 'Betrag',
          plan: 'Tarif',
          endDate: 'Enddatum',
          grandTotal: 'Gesamtsumme',
          periodTotalAllPages: 'Periodensumme (alle Seiten)',
          unknown: 'Unbekannt',
          pageSize: 'Seitengröße:',
          first: 'Erste',
          previous: 'Zurück',
          next: 'Weiter',
          last: 'Letzte',
          showingSessionsRange: 'Anzeige {{start}} - {{end}} von {{total}} Stunden',
          showingSessionsPage: 'Anzeige {{count}} auf dieser Seite · {{total}} gesamt · Seite {{page}} von {{pages}}',
          pageOf: 'Seite {{page}} von {{pages}}',
          records: '{{count}} Einträge',
          downloadPageExcel: 'Seite als Excel herunterladen',
          exportFullHint: 'Export PDF/CSV unten für den vollständigen Bericht (alle Seiten).',
          individualSession: 'Einzelstunde',
          individual: 'Einzel',
          sessionNotes: 'Stundennotizen',
          dateTime: 'Datum / Zeit',
          sessionDateTime: 'Stundendatum & -zeit',
          sessionTopic: 'Stundenthema',
          topic: 'Thema',
          gross: 'Brutto',
          grossRevenue: 'Bruttoeinnahmen',
          academy: 'Akademie',
          academyShare: 'Akademieanteil',
          academyEarnings: 'Akademieeinnahmen',
          coachEarnings: 'Trainereinnahmen',
          attendanceRate: 'Anwesenheitsquote',
          viewAttendance: 'Anwesenheitsdetails anzeigen',
          clickViewAttendance: 'Klicken für Anwesenheitsdetails',
          sessionAttendance: 'Stundenanwesenheit',
          sessionDetails: 'Stundendetails',
          dateAndTime: 'Datum & Zeit',
          batchAndBranch: 'Gruppe & Filiale',
          students: 'Schüler',
          avg: 'Ø',
          average: 'Durchschnitt',
          commissionSplit: 'Provisionsaufteilung',
          totalSessionCharges: 'Gesamt-Stundengebühren',
          charges: 'Gebühren',
          totals: 'Summen',
          errorPrefix: 'Fehler: {{message}}',
          noServerResponse: 'Keine Serverantwort. Bitte Verbindung prüfen.',
          presentCount: 'Anwesend ({{count}}):',
          absentCount: 'Abwesend ({{count}}):',
          attendancePct: 'Anwesenheit %',
          month: 'Monat',
          type: 'Typ',
          startTime: 'Startzeit',
          endTime: 'Endzeit',
          sessionId: 'Stunden-ID',
          date: 'Datum',
          studentsFound: '{{count}} Schüler gefunden',
          studentsFound_other: '{{count}} Schüler gefunden',
          never: 'Nie',
          amountDue: 'Fälliger Betrag',
          lastTopup: 'Letzte Aufladung',
          batchesCol: 'Gruppen',
          balance: 'Guthaben',
          inScope: 'Im Umfang',
          windowDays: 'Fenster (Tage)',
          overdue: 'ÜBERFÄLLIG',
          lowBalance: 'NIEDRIGES GUTHABEN',
          expired: 'ABGELAUFEN',
          expiringSoon: 'LÄUFT BALD AB',
          byStudent: 'Nach Schüler',
          byCoach: 'Nach Trainer',
          choosePerson: '{{mode}} wählen…',
          allLevelsLabel: 'ALLE Stufen',
          allBatchesLabel: 'ALLE Gruppen',
          noSessionsFilter: 'Keine Stunden für diesen Filter gefunden.',
          selectPerson: 'Bitte {{mode}} wählen.',
          selectValidDates: 'Bitte gültiges Von- und Bis-Datum angeben.',
          confirm: 'Bestätigen'
        },
        exportBar: {
          sendReportViaEmail: 'Bericht per E-Mail senden',
          exportPdf: 'PDF exportieren',
          generatingPdf: 'PDF wird erstellt…',
          exportCsv: 'CSV exportieren',
          generatingCsv: 'CSV wird erstellt…',
          sending: 'Wird gesendet…',
          sent: 'Gesendet!',
          sendEmail: 'E-Mail senden',
          exportPdfFailed: 'PDF-Export fehlgeschlagen. Bitte erneut versuchen.',
          exportCsvFailed: 'CSV-Export fehlgeschlagen. Bitte erneut versuchen.',
          enterEmail: 'Bitte E-Mail-Adresse eingeben',
          enterValidEmail: 'Bitte gültige E-Mail-Adresse eingeben',
          sendEmailFailed: 'E-Mail konnte nicht gesendet werden. Bitte erneut versuchen.',
          generateFirstHint: 'Erstellen Sie zuerst einen Bericht, um Exportoptionen zu aktivieren.'
        },
        coachRevenue: {
          title: 'Trainereinnahmen & Provision',
          subtitle: 'Stundenweise Einnahmen, Provisionsprozente und Auszahlungssummen pro Trainer — filterbar nach Datumsbereich, Filiale und Trainer.',
          totalSessions: 'Stunden gesamt',
          sessionMix: '{{individual}} Einzel, {{batch}} Gruppe',
          noSessionsFound: 'Keine Stunden gefunden',
          noSessionsHint: 'Keine Stunden für die gewählten Filter und den Datumsbereich',
          coachWiseEarnings: 'Einnahmen nach Trainer',
          dayWiseEarnings: 'Einnahmen nach Tag',
          daysCount: '{{count}} Tage',
          daysScrollHint: '{{count}} Tage · scrollen für mehr'
        },
        sessionAttendance: {
          subtitle: 'Schüler-Stunden-Anwesenheitsmatrix für eine gewählte Gruppe — zeigt Anwesenheitsstatus',
          subtitleCharges: ' und Gebühr pro Stunde',
          subtitleEnd: ' für jeden Schüler.'
        },
        studentAttendance: {
          subtitleCoach: 'Anwesenheitsübersicht für Ihre Stunden und Kaderschüler — nach Stufe oder Gruppe filtern.',
          subtitleAdmin: 'Anwesenheitsübersicht pro Schüler über Gruppen — Stunden gesamt, anwesend/abwesend und Anwesenheitsquote.',
          totalStudents: 'Schüler gesamt',
          avgAttendance: 'Ø Anwesenheit',
          totalPresent: 'Anwesend gesamt',
          totalAbsent: 'Abwesend gesamt',
          detailsTitle: 'Schüler-Anwesenheitsdetails',
          lowAttendanceLegend: 'Hervorgehobene Zeilen zeigen Anwesenheit unter 50 %',
          empty: 'Keine Schülereinträge für die gewählten Filter.'
        },
        academyRevenue: {
          subtitle: 'Monatliche und filialbezogene Bruttoeinnahmen mit Provisionsaufschlüsselung.',
          monthlyBreakdown: 'Monatliche Aufschlüsselung',
          branchBreakdown: 'Filialaufschlüsselung',
          month: 'Monat',
          sessions: 'Stunden',
          grossRevenue: 'Bruttoeinnahmen',
          academyCommission: 'Akademieprovision',
          academyRevenue: 'Akademieeinnahmen',
          coachPayout: 'Trainerauszahlung',
          walletTopUps: 'Guthabenaufladungen',
          walletBalanceHeld: 'Gehaltenes Guthaben',
          empty: 'Keine Umsatzdaten für die gewählten Filter.'
        },
        paymentsDue: {
          subtitle: 'Momentaufnahme der Schüler mit niedrigem oder negativem Guthaben.',
          paymentStatus: 'Zahlungsstatus',
          statusAll: 'Alle (überfällig + niedriges Guthaben)',
          statusOverdue: 'Nur überfällig (negatives Guthaben)',
          statusLowBalance: 'Nur gefährdet (nicht überfällig)',
          thresholdOverride: 'Schwellenwert-Override',
          thresholdPlaceholder: 'Leer lassen für Systemstandard',
          thresholdHint: 'Systemstandard: {{amount}}',
          overdueStudents: 'Überfällige Schüler',
          overdueSub: 'Negatives Guthaben',
          totalAmountDue: 'Gesamtbetrag fällig',
          totalDueSub: 'Summe der überfälligen Schüler',
          belowThreshold: 'Unter Schwellenwert',
          belowSub: '{{pct}} % der Schüler im Umfang',
          studentsInScope: 'Schüler im Umfang',
          empty: 'Keine Schüler entsprechen den Filtern.',
          sessionsSince: 'Stunden seitdem',
          ratePerSession: 'Satz/Stunde'
        },
        sessionsSummary: {
          title: 'Stundenübersicht',
          subtitle: 'Stundendatum/-zeit, Stufe, Gruppe, Trainer und Thema für Schüler oder Trainer.',
          loadFailed: 'Stundenübersicht konnte nicht geladen werden.'
        },
        monthlyStatements: {
          title: 'Monatsabrechnungssteuerung',
          subtitle: 'Stundenabrechnungs-E-Mails vorschauen, senden und verfolgen. Abrechnungszeitraum und separaten Gebührenschätzzeitraum wählen; bereits bezahlte Stunden werden von der Schätzung ausgeschlossen.',
          periodSettings: 'Zeitraumeinstellungen',
          statementAndEstimate: 'Abrechnung & Gebührenschätzung',
          periodDesc: 'Aktivität auf der Abrechnung und Fenster für Gebührenschätzungen wählen.',
          statementLabel: 'Abrechnung:',
          statementPeriod: 'Abrechnungszeitraum',
          statementPeriodDesc: 'Aktivität, Guthabenbelastungen und Anwesenheit.',
          estimatePeriod: 'Gebührenschätzzeitraum',
          estimatePeriodDesc: 'Nächster voller Monat nach Monatsend-Abrechnungen, sonst Rest des Monats.',
          thisMonth: 'Dieser Monat',
          lastMonth: 'Letzter Monat',
          includeEstimate: 'Kommende Gebührenschätzung einschließen',
          estimateAuto: 'Schätzung automatisch ausfüllen',
          estimateFrom: 'Schätzung von',
          estimateTo: 'Schätzung bis',
          loadPreview: 'Vorschau laden',
          loading: 'Laden…',
          loadFailed: 'Daten konnten nicht geladen werden.',
          totalStudents: 'Schüler gesamt',
          sent: 'Gesendet',
          pending: 'Ausstehend',
          failed: 'Fehlgeschlagen',
          notSent: 'Nicht gesendet',
          searchStudents: 'Schüler nach Name, E-Mail oder ID suchen…',
          sendAll: 'Alle senden',
          refreshQueue: 'Warteschlange aktualisieren',
          studentCol: 'Schüler',
          emailCol: 'E-Mail',
          walletBalance: 'Guthaben',
          estimateFees: 'Gesch. Gebühren',
          queueStatus: 'Warteschlangenstatus',
          actions: 'Aktionen',
          previewPdf: 'PDF-Vorschau',
          downloadPdf: 'PDF herunterladen',
          send: 'Senden',
          cancelEmail: 'Abbrechen',
          retry: 'Erneut versuchen',
          noStudents: 'Keine Schüler für diesen Zeitraum.',
          allowPopups: 'Bitte Pop-ups für PDF-Vorschau zulassen.',
          accessDenied: 'Zugriff verweigert — als ADMIN anmelden.',
          pdfLoadFailed: 'PDF konnte nicht geladen werden.',
          pdfDownloadFailed: 'PDF-Download fehlgeschlagen.',
          pdfError: 'PDF-Fehler: {{message}}',
          confirmSendAllTitle: 'Abrechnungen senden für {{range}}',
          confirmSendAllMsg: 'E-Mails werden für {{count}} Schüler{{suffix}} in die Warteschlange gestellt.{{skipNote}}',
          skipNote: ' {{count}} Schüler{{suffix}} bereits GESENDET/AUSSTEHEND werden übersprungen.',
          sendAllLabel: 'Alle senden',
          queuedAll: 'Abrechnungen für alle Schüler in Warteschlange.',
          sendAllFailed: 'Senden aller fehlgeschlagen.',
          confirmSendOneTitle: 'Abrechnung senden für {{name}}',
          confirmSendOneMsg: 'Abrechnungs-E-Mail für {{name}} für {{range}} in Warteschlange stellen?',
          queued: 'In Warteschlange ✓',
          queueFailed: 'Warteschlange fehlgeschlagen.',
          cancelTitle: 'E-Mail abbrechen',
          cancelMsg: 'Diese ausstehende E-Mail abbrechen? Der Schüler erhält die Abrechnung nicht, es sei denn, Sie senden sie erneut.',
          cancelLabel: 'E-Mail abbrechen',
          cancelled: 'Abgebrochen ✓',
          cancelFailed: 'Abbrechen fehlgeschlagen.',
          retryQueued: 'Erneuter Versuch ✓',
          retryFailed: 'Erneuter Versuch fehlgeschlagen.',
          statusSent: 'Gesendet',
          statusPending: 'Ausstehend',
          statusProcessing: 'In Bearbeitung',
          statusFailed: 'Fehlgeschlagen',
          statusCancelled: 'Abgebrochen',
          months: {
            '1': 'Januar',
            '2': 'Februar',
            '3': 'März',
            '4': 'April',
            '5': 'Mai',
            '6': 'Juni',
            '7': 'Juli',
            '8': 'August',
            '9': 'September',
            '10': 'Oktober',
            '11': 'November',
            '12': 'Dezember'
          },
          queueing: 'Wird eingereiht…',
          sendAllWithCount: '🚀 Alle senden ({{count}})',
          includeFeeEstimateTitle: 'Gebührenschätzung & Einzahlungserinnerung einschließen',
          includeFeeEstimateDesc: 'Deaktivieren, um nur die Aktivitätsabrechnung zu senden. Schüler mit ausreichendem Guthaben überspringen die Zahlungserinnerung weiterhin, wenn dies aktiviert ist.',
          resetDefault: 'Standard zurücksetzen',
          resetDefaultTitle: 'Schätzung auf Standardbereich basierend auf Abrechnungsenddatum zurücksetzen',
          then: 'dann',
          autoRange: 'Auto: {{range}} ({{count}} Tag{{daySuffix}})',
          customEstimateRange: 'Benutzerdefinierter Schätzbereich',
          estimateInvalid: 'Gebührenschätzzeitraum erfordert gültige Von-/Bis-Daten.',
          estimateLabel: 'Schätzung:',
          studentsWithActivity: '{{count}} Schüler{{suffix}} mit Zahlungs-/Einschreibungsaktivität',
          clearSearch: 'Suche löschen',
          noEmailOnFile: 'Keine E-Mail hinterlegt',
          sendStatementEmail: 'Abrechnungs-E-Mail senden',
          resendTooltip: 'Schüler hat diese bereits erhalten. Sendet erneut.',
          resend: 'Erneut senden',
          charges: 'Gebühren',
          closingBalance: 'Schlusssaldo',
          upcomingEst: 'Kommende Schätz.',
          emailStatus: 'E-Mail-Status',
          notSentStatus: 'Nicht gesendet',
          archived: 'Archiviert',
          noStudentsActivity: 'Keine Schüler mit Zahlungs- oder Einschreibungsaktivität für {{range}} gefunden.',
          noStudentsMatch: 'Keine Schüler entsprechen „{{term}}“.',
          refresh: 'Aktualisieren',
          refreshTitle: 'Daten aktualisieren',
          showingStudents: '{{filtered}} von {{total}} Schülern angezeigt',
          depositDue: 'Einzahlung fällig',
          reportOnly: 'Nur Bericht',
          prepaid: 'Vorausbezahlt',
          balanceCredit: '{{amount}} Guthaben',
          balanceDue: '{{amount}} fällig',
          amountDue: 'Fällig {{amount}}',
          estimateHidden: 'Schätz. {{amount}} ausgeblendet',
          estimateHiddenEmail: 'Schätz. {{amount}} in E-Mail ausgeblendet',
          sessionCountShort: '{{count}} Std.',
          confirmSendAllMsg_one: 'E-Mails werden für {{count}} Schüler in die Warteschlange gestellt.{{skipNote}}',
          confirmSendAllMsg_other: 'E-Mails werden für {{count}} Schüler in die Warteschlange gestellt.{{skipNote}}',
          skipNote_one: ' {{count}} Schüler bereits GESENDET/AUSSTEHEND wird übersprungen.',
          skipNote_other: ' {{count}} Schüler bereits GESENDET/AUSSTEHEND werden übersprungen.',
          studentsWithActivity_one: '{{count}} Schüler mit Zahlungs-/Einschreibungsaktivität',
          studentsWithActivity_other: '{{count}} Schüler mit Zahlungs-/Einschreibungsaktivität',
          autoRange_one: 'Auto: {{range}} ({{count}} Tag)',
          autoRange_other: 'Auto: {{range}} ({{count}} Tage)',
          paymentReminder: 'Zahlungserinnerung'
        }
      },
      calendar: {
        eyebrow: 'Verwalten',
        title: 'Stundenkalender',
        subtitleStaff: 'Alle geplanten Stunden auf einen Blick',
        subtitleStudent: 'Ihre eingeschriebenen Stunden und Anwesenheit',
        subtitleParent: 'Eingeschriebene Stunden und Anwesenheit Ihres Kindes',
        subtitleCoach: 'Ihre Stunden — nach Kaderschüler filtern, um nur deren Stunden zu sehen',
        loadingSubtitle: 'Ihre eingeschriebenen Stunden und Anwesenheit',
        allSessions: 'Alle Stunden',
        myBranches: 'Meine Filialen',
        mySessions: 'Meine Stunden',
        pending: '{{count}} ausstehend',
        completed: '{{count}} abgeschlossen',
        today: 'Heute',
        month: 'Monat',
        week: 'Woche',
        viewing: 'Ansicht',
        searchName: 'Name suchen…',
        coaches: 'Trainer',
        students: 'Schüler',
        myRosterStudents: 'Meine Kaderschüler',
        noRosterStudents: 'Noch keine Kaderschüler',
        noResults: 'Keine Ergebnisse',
        student: 'Schüler',
        coach: 'Trainer',
        batches: 'Gruppen:',
        showAll: 'Alle anzeigen',
        batchFilterHintAll: 'Gruppe anklicken, um nur deren Stunden anzuzeigen · weitere Gruppen zum Kombinieren anklicken',
        batchFilterHintSelected: '{{count}} Gruppe ausgewählt · weitere zum Hinzufügen anklicken · Ausgewählte zum Entfernen anklicken',
        batchFilterHintSelected_other: '{{count}} Gruppen ausgewählt · weitere zum Hinzufügen anklicken · Ausgewählte zum Entfernen anklicken',
        legendPending: 'Ausstehend',
        legendCompleted: 'Abgeschlossen',
        legendCancelled: 'Abgesagt',
        legendBatchColors: 'Farben = Gruppe',
        legendHoliday: '🏖 = Akademie-Feiertag',
        legendHover: 'Stunde für Details überfahren',
        legendPresent: '✓ Anwesend',
        legendAbsent: '✗ Abwesend',
        legendClickDay: 'Tag für vollständige Liste anklicken',
        legendClickAttendance: 'Anklicken für Anwesenheit',
        shortcuts: 'Tastenkürzel: ← → navigieren · T heute',
        shortcutsStaff: ' · Tag anklicken für Stundenliste',
        loadFailed: 'Stunden konnten nicht geladen werden. Bitte erneut versuchen.',
        noSessionsMonth: 'Keine Stunden geplant im {{month}} {{year}}',
        noSessionsHint: 'Anderen Monat wählen oder Gruppenfilter prüfen',
        holiday: 'Feiertag',
        months: {
          '1': 'Januar',
          '2': 'Februar',
          '3': 'März',
          '4': 'April',
          '5': 'Mai',
          '6': 'Juni',
          '7': 'Juli',
          '8': 'August',
          '9': 'September',
          '10': 'Oktober',
          '11': 'November',
          '12': 'Dezember'
        },
        session: 'Stunde',
        level: 'Stufe',
        sessionCoach: 'Stundentrainer',
        time: 'Zeit',
        sessionStatus: 'Stundenstatus',
        attendance: 'Anwesenheit',
        sessionNotes: 'Stundennotizen',
        present: 'Anwesend',
        absent: 'Abwesend',
        marked: 'Erfasst',
        notMarked: 'Nicht erfasst',
        more: '+{{count}} weitere',
        showLess: 'Weniger anzeigen',
        sessionsCount: '{{count}} Stunde',
        sessionsCount_other: '{{count}} Stunden',
        sessAbbr: '{{count}} Std.',
        noSessionsDay: 'Keine Stunden an diesem Tag.',
        prevYear: 'Vorheriges Jahr',
        nextYear: 'Nächstes Jahr',
        prev: 'Zurück',
        next: 'Weiter',
        close: 'Schließen'
      },
      notificationsPage: {
        title: 'Benachrichtigungen',
        subtitle: 'Warnungen und Erinnerungen aus Ihrem Akademie-Arbeitsbereich.',
        markAllRead: 'Alle als gelesen markieren',
        empty: 'Alles erledigt — noch keine Benachrichtigungen.',
        pageOf: 'Seite {{page}} von {{totalPages}} · {{total}} gesamt',
        previous: 'Zurück',
        next: 'Weiter',
        justNow: 'gerade eben',
        minutesAgo: 'vor {{count}} Min.',
        hoursAgo: 'vor {{count}} Std.',
        daysAgo: 'vor {{count}} T.',
        subtitleValue: 'Handlungsrelevante Hinweise zu Guthaben, Anwesenheit und Datenschutz — tippen Sie, um zur richtigen Seite zu springen.',
        unreadBanner: 'Sie haben {{count}} ungelesene Benachrichtigung(en). Öffnen Sie eine zum Prüfen und Handeln.',
        emptyHint: 'Wenn Schüler unter die Wallet-Schwelle fallen, Fehlzeiten markiert werden oder eine DSAR gestellt wird, erscheinen Hinweise hier mit Direktlink.'
      },
      form: {
        sessionDays: 'Unterrichtstage',
        selectOneDay: 'Mindestens einen Tag wählen'
      },
      faq: {
        title: 'Häufig gestellte Fragen',
        subtitle: '{{count}} Fragen zu allem, was Sie als {{role}} tun können.',
        findAnswers: 'Hilfe',
        searchHeading: 'Antworten finden',
        searchHint: 'Nach Stichwort suchen oder unten nach Thema browsen.',
        searchPlaceholder: 'Fragen suchen…',
        allSections: 'Alle',
        noResults: 'Keine Ergebnisse gefunden',
        tryDifferent: 'Versuchen Sie ein anderes Stichwort oder löschen Sie die Suche',
        noQuestionsMatch: 'Keine Fragen entsprechen Ihrer Suche.',
        resultsFor: '{{count}} Ergebnis für „{{query}}“',
        resultsFor_other: '{{count}} Ergebnisse für „{{query}}“',
        footerPrompt: 'Nicht gefunden, wonach Sie suchen?',
        footerAction: 'Wenden Sie sich an Ihren Akademie-Administrator.',
        roles: {
          ADMIN: 'Admin',
          MANAGER: 'Manager',
          COACH: 'Trainer',
          STUDENT: 'Schüler',
          PARENT: 'Elternteil'
        }
      },
      settings: {
        email: {
          title: 'E-Mail-Einstellungen',
          loadingSubtitle: 'SMTP-Konfiguration wird geladen…',
          subtitle: 'Konfigurieren Sie das SMTP-Konto, mit dem Ihre Akademie Anmeldelinks, Erinnerungen, Abrechnungen und andere System-E-Mails versendet.',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          stepThree: 'Schritt 3',
          smtpSection: 'SMTP-Verbindung',
          smtpSectionDesc: 'Serverzugangsdaten für ausgehende System-E-Mails Ihrer Akademie.',
          fromSection: 'Absenderidentität',
          fromSectionDesc: 'Wie Empfänger Absender und Antwortadresse sehen.',
          testSection: 'Zustellung prüfen',
          testSectionDesc: 'Senden Sie eine Testnachricht, bevor Sie ausgehende E-Mails freigeben.',
          statusLabel: 'Status',
          modeEnabled: 'Versand an',
          modeConfigured: 'Konfiguriert',
          modeIncomplete: 'Unvollständig',
          saveSettings: 'Einstellungen speichern',
          saveBeforeTest: 'SMTP-Einstellungen speichern, bevor Sie eine Test-E-Mail senden.',
          testCompleted: 'Test abgeschlossen.',
          testFailed: 'Test-E-Mail fehlgeschlagen.',
          useStarttlsDesc: 'SMTP-Sitzung nach Verbindung verschlüsseln. Empfohlen für Port 587.',
          lastTestPassedHint: 'Bei Gmail prüfen wir den Ordner „Gesendet“. Empfänger sollten weiterhin Spam/Werbung prüfen.',
          lastTestFailedHint: 'Zugangsdaten prüfen und erneut versuchen.',
          infoBanner: 'Dies ist Ihre <strong>Ausgangskonfiguration</strong>. Der öffentliche Support-Kontakt für Schüler wird unter <1>Markenauftritt</1> verwaltet. Benachrichtigungsschalter finden Sie unter <2>Benachrichtigungen</2>.',
          enableTitle: 'Ausgehende E-Mails aktivieren',
          enableHint: 'Erfordert gültige SMTP-Zugangsdaten unten.',
          smtpHost: 'SMTP-Host',
          smtpPort: 'SMTP-Port',
          smtpUsername: 'SMTP-Benutzername',
          smtpPassword: 'SMTP-Passwort / App-Passwort',
          passwordConfigured: '(bereits gesetzt — leer lassen zum Beibehalten)',
          passwordRequired: 'Erforderlich',
          fromEmail: 'Absender-E-Mail',
          fromName: 'Anzeigename des Absenders',
          fromNamePlaceholder: 'Standard: Akademiename',
          replyTo: 'Antwort-an-E-Mail',
          replyToPlaceholder: 'Standard: Support-E-Mail aus Markenauftritt',
          useStarttls: 'STARTTLS verwenden (empfohlen für Port 587)',
          enableWarning: 'Geben Sie SMTP-Host, Absender-E-Mail und Passwort ein, bevor Sie ausgehende E-Mails aktivieren.',
          testRecipient: 'Test-E-Mail senden an',
          sendTest: 'Test senden',
          verifying: 'Wird geprüft… (bis zu 45 s)',
          lastTestPassed: 'Letzter Test bestanden',
          lastTestFailed: 'Letzter Test fehlgeschlagen',
          loadFailed: 'E-Mail-Einstellungen konnten nicht geladen werden.',
          saveSuccess: 'E-Mail-Einstellungen gespeichert.',
          saveFailed: 'E-Mail-Einstellungen konnten nicht gespeichert werden.'
        },
        whatsapp: {
          title: 'WhatsApp (WATI)',
          loadingSubtitle: 'Einstellungen werden geladen…',
          subtitle: 'Verbinden Sie Ihr WATI-Konto, um Stundenerinnerungen, Guthabenwarnungen und andere Nachrichten über WhatsApp zu senden. Nachrichtenkosten werden von WATI/Meta an Ihre Akademie berechnet.',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          stepThree: 'Schritt 3',
          connectionSection: 'Verbindung',
          connectionSectionDesc: 'Lizenzstatus, Hauptschalter und WATI-Zugangsdaten für ausgehende WhatsApp-Nachrichten.',
          templatesSection: 'Nachrichtenvorlagen',
          templatesSectionDesc: 'Exakte Vorlagenamen wie in Ihrem WATI-Konto genehmigt.',
          localeTemplateNote: 'Bei Regionalsprachen de/fr/nl sendet die App „{{example}}“ (Basisname + _{{lang}}). Registrieren Sie passende Sprachvorlagen in WATI.',
          recentLogDesc: 'Neueste ausgehende WhatsApp-Versuche für diese Akademie.',
          statusLabel: 'Status',
          modeUnlicensed: 'Nicht lizenziert',
          modeEnabled: 'Versand an',
          modeOff: 'Aus',
          saveSettings: 'Einstellungen speichern',
          notLicensed: 'WhatsApp ist in Ihrer aktuellen ChessDesk-Lizenz nicht enthalten. Kontaktieren Sie Axeron Technologies für ein Upgrade (Professional oder Enterprise, oder aktivieren Sie <strong>WhatsApp Enabled</strong> in Ihrer Lizenzzeile).',
          enableTitle: 'WhatsApp aktivieren',
          enableHint: 'Akademie-Hauptschalter (Lizenz + WATI-Zugangsdaten oder Mock-Modus erforderlich).',
          mockMode: 'Mock-Modus',
          mockModeDesc: 'Nachrichten lokal protokollieren ohne WATI-Aufruf (empfohlen für Entwicklung/Test)',
          apiBaseUrl: 'WATI-API-Basis-URL',
          apiBaseUrlNote: 'Aus WATI-Dashboard → API-Dokumentation',
          channelNumber: 'Geschäfts-Kanalnummer',
          channelNumberNote: 'Ihre WATI-WhatsApp-Nummer (für Live-Versand erforderlich).',
          bearerToken: 'WATI-Bearer-Token',
          tokenPlaceholder: 'Bearer-Token aus WATI-API-Dokumentation einfügen',
          templatesTitle: 'Genehmigte Vorlagenamen in WATI',
          templateSessionReminder: 'Stundenerinnerung (Schüler)',
          templateAbsenceAlert: 'Fehlzeitenalarm (Schüler)',
          templateLowBalance: 'Niedriges Guthaben (Schüler)',
          templateFeeReminder: 'Gebühren / offene Beträge (Schüler)',
          templatePendingSessions: 'Ausstehende Stunden (Trainer)',
          templateLowBalanceAdmin: 'Guthabenwarnung (Admins)',
          templateSessionCancelled: 'Stunde abgesagt (Schüler)',
          templateHolidayAnnouncement: 'Feiertagsankündigung (Personal & Schüler)',
          templateCorrespondenceDeadline: 'Korrespondenz-Frist (Spielplattform)',
          templateCorrespondenceYourMove: 'Korrespondenz – Ihr Zug (Spielplattform)',
          enableWarning: 'Geben Sie WATI-Zugangsdaten und Kanalnummer ein oder aktivieren Sie den Mock-Modus, bevor Sie WhatsApp aktivieren.',
          recentLog: 'Aktuelles Nachrichtenprotokoll',
          noMessages: 'Noch keine WhatsApp-Nachrichten in der Warteschlange.',
          when: 'Wann',
          phone: 'Telefon',
          type: 'Typ',
          status: 'Status',
          unavailable: {
            notAvailableTitle: 'WhatsApp-Benachrichtigungen sind nicht verfügbar',
            notAvailableBody: 'Ihre Akademie hat WhatsApp-Nachrichten in ChessDesk nicht aktiviert. {{phoneHint}} Wenden Sie sich an Ihr Akademie-Büro, wenn Sie Stunden- oder Guthaben-Updates benötigen.',
            notSetupTitle: 'WhatsApp ist noch nicht eingerichtet',
            notSetupBody: 'Ihre Akademie hat WhatsApp-Benachrichtigungen noch nicht aktiviert. Sobald WhatsApp verbunden ist, können Sie hier wählen, welche Hinweise Sie erhalten. {{phoneHint}}',
            phoneHintParent: 'Sobald WhatsApp verfügbar ist, gehen Hinweise an die Telefonnummer im Profil Ihres Kindes.',
            phoneHintStudent: 'Sobald WhatsApp verfügbar ist, gehen Hinweise an die Telefonnummer in Ihrem Profil.'
          },
          loadFailed: 'WhatsApp-Einstellungen konnten nicht geladen werden.',
          saveSuccess: 'WhatsApp-Einstellungen gespeichert.',
          saveFailed: 'Einstellungen konnten nicht gespeichert werden.',
          testTitle: 'Testnachricht senden',
          testDesc: 'Löst die Sitzungserinnerungs-Vorlage für die Akademiesprache auf (inkl. _de/_fr/_nl) und sendet per WATI oder Mock-Modus.',
          testPhone: 'Empfängertelefon (E.164)',
          sendTest: 'Test senden',
          testing: 'Testen…',
          testFailed: 'WhatsApp-Test fehlgeschlagen.'
        },
        chessCoach: {
          title: 'Ask AI Coach',
          loadingSubtitle: 'Ask-AI-Coach-Einstellungen werden geladen…',
          subtitle: 'Aktivieren Sie den Ask-AI-Coach-Chatbot und hinterlegen Sie einen OpenAI-kompatiblen API-Schlüssel (OpenAI, Groq, OpenRouter usw.).',
          infoBanner: 'Wenn aktiviert und konfiguriert, erscheint Ask AI Coach für Schüler, Eltern und Mitarbeiter in Playing. Die Engine-Analyse bleibt auf Ihrem Server; der API-Schlüssel dient nur der sprachlichen Erklärung.',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          connectionSection: 'Aktivierung & Verbindung',
          connectionSectionDesc: 'Hauptschalter und Zugangsdaten für das Sprachmodell hinter Ask AI Coach.',
          limitsSection: 'Limits & Fairplay',
          limitsSectionDesc: 'Ratenlimits, Antwortlänge und Richtlinie für bewertete Live-Partien.',
          statusLabel: 'Status',
          modeOperational: 'Live',
          modeEnabledIncomplete: 'An — API-Schlüssel fehlt',
          modeConfigured: 'Konfiguriert (aus)',
          modeIncomplete: 'Nicht eingerichtet',
          enableTitle: 'Ask AI Coach aktivieren',
          enableHint: 'Zeigt den Coach-Chat für Akademie-Nutzer, wenn ein API-Schlüssel gespeichert ist.',
          enableWarning: 'Bitte API-Schlüssel einfügen, bevor Sie Ask AI Coach aktivieren.',
          baseUrl: 'API-Basis-URL',
          baseUrlHint: 'OpenAI-kompatible Chat-Completions-Basis (ohne abschließenden Schrägstrich). Groq: https://api.groq.com/openai/v1',
          model: 'Modell',
          modelHint: 'Modell-ID des Anbieters, z. B. gpt-4o-mini oder llama-3.3-70b-versatile',
          apiKey: 'API-Schlüssel',
          apiKeyPlaceholder: 'API-Schlüssel einfügen',
          apiKeyConfigured: '(bereits gesetzt — leer lassen zum Behalten)',
          apiKeyHint: 'Wird in der Akademie-Datenbank gespeichert und nach dem Speichern nicht erneut angezeigt.',
          maxTokens: 'Max. Antwort-Tokens',
          rateLimit: 'Fragen pro Nutzer / Minute',
          maxHistory: 'Verlaufsnachrichten an das Modell',
          timeoutMs: 'Timeout (ms)',
          liveRatedIdeasOnly: 'Nur Ideen in bewerteten Live-Partien',
          liveRatedIdeasOnlyDesc: 'In aktiven bewerteten Mensch-Partien nur Pläne besprechen — keinen konkreten besten Zug vorschlagen.',
          saveSettings: 'Einstellungen speichern',
          loadFailed: 'Ask-AI-Coach-Einstellungen konnten nicht geladen werden.',
          saveSuccess: 'Ask-AI-Coach-Einstellungen gespeichert.',
          saveFailed: 'Ask-AI-Coach-Einstellungen konnten nicht gespeichert werden.'
        },
        production: {
          title: 'EU-Produktionsbereitschaft',
          loadingSubtitle: 'Go-Live-Anforderungen werden geprüft…',
          subtitle: 'Checkliste für HTTPS-Webhooks, SMTP, WhatsApp-Sprachvorlagen, Zeitzone und Anwaltsbestätigung der EU-Rechtsentwürfe.',
          loadFailed: 'Produktionsbereitschaft konnte nicht geladen werden.',
          readyBanner: 'Keine In-App-Blocker — Live-Mollie/Stripe-Keys und Meta-Vorlagen außerhalb von ChessDesk abschließen.',
          notReadyBanner: 'Beheben Sie die Blocker unten vor dem EU-Go-Live.',
          readyHint: 'Diese Seite kann keine Händlerkonten oder Anwaltssignaturen erzeugen; sie prüft, was ChessDesk verifizieren kann.',
          checklistTitle: 'Checkliste',
          open: 'Öffnen',
          checkHttps: 'Öffentliche HTTPS-Akademie-URL (FRONTEND_BASE_URL)',
          checkMollieWebhook: 'Mollie-Webhook-URL konfiguriert',
          checkSmtp: 'SMTP aktiv und betriebsbereit',
          smtpLastTestFailed: 'Letzter SMTP-Test fehlgeschlagen',
          checkWhatsapp: 'WhatsApp (falls lizenziert)',
          whatsappSuffix: 'Sprachsuffix {{suffix}} — passende Meta-Vorlagen registrieren',
          whatsappEnglish: 'Englische Basis-Vorlagennamen',
          checkLegal: 'Anwaltsprüfung bestätigt',
          checkTimezone: 'Akademie-Zeitzone (Regionale Einstellungen)',
          timezoneDetail: '{{academy}}',
          webhooksTitle: 'Webhook-URLs zum Registrieren',
          webhooksDesc: 'In Stripe-, PayPal-, Mollie- und Razorpay-Dashboards eintragen.',
          templatesTitle: 'Aufgelöste WhatsApp-Vorlagennamen',
          templatesDesc: 'Exakt so in Meta/WATI für die Akademiesprache registrieren.',
          legalTitle: 'EU-Rechtspaket & Anwaltsbestätigung',
          legalDesc: 'Entwürfe sind anwaltsbereit, nicht anwaltsfinal. Nur bestätigen, nachdem ein zugelassener Anwalt PRIVACY-POLICY-EU und DPA-EU-ANNEX geprüft hat.',
          docPrivacyEu: 'Datenschutz (EU)',
          docDpaEu: 'AVV-Anhang (EU)',
          docPrivacy: 'Datenschutz',
          docDpa: 'AVV',
          docTerms: 'AGB',
          docEula: 'EULA',
          docLoading: 'Laden…',
          docLoadFailed: 'Dokument konnte nicht geladen werden.',
          confirmLabel: 'Ich bestätige, dass ein Anwalt das aktuelle EU-Rechtspaket für diese Akademie geprüft hat, und wir unterschriebene Kopien aufbewahren.',
          confirmRequired: 'Zuerst die Bestätigungs-Checkbox aktivieren.',
          notesLabel: 'Notizen (optional)',
          notesPlaceholder: 'z. B. Kanzlei, Prüfdatum',
          ackButton: 'Anwaltsbestätigung speichern',
          ackSuccess: 'Anwaltsbestätigung gespeichert.',
          ackFailed: 'Bestätigung konnte nicht gespeichert werden.',
          alreadyAcked: 'Bestätigt von {{by}} am {{at}} (Version {{version}}).'
        },
        notifications: {
          title: 'Benachrichtigungseinstellungen',
          loadingSubtitle: 'Akademie-Standardbenachrichtigungen werden geladen…',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          stepThree: 'Schritt 3',
          stepFour: 'Schritt 4',
          saveSettings: 'Einstellungen speichern',
          subtitle: 'Akademieweite Standardwerte für E-Mail, WhatsApp (WATI) und In-App-Hinweise. Änderungen gelten für automatische Systemnachrichten.',
          savePreferences: 'Einstellungen speichern',
          saveSuccess: 'Einstellungen erfolgreich gespeichert.',
          saveFailed: 'Einstellungen konnten nicht gespeichert werden. Bitte erneut versuchen.',
          emailPanel: 'E-Mail',
          emailDesc: 'SMTP-Nachrichten an Schüler, Trainer und Personal bei Ereignissen.',
          emailFooterOn: 'Schalten Sie den Hauptschalter aus, um alle ausgehenden E-Mails zu pausieren.',
          emailFooterOff: 'Konfigurieren Sie ausgehendes SMTP unter System → E-Mail-Einstellungen, bevor Sie E-Mail-Benachrichtigungen aktivieren.',
          emailNotConfigured: 'Ausgehende E-Mail ist noch nicht konfiguriert. Gehen Sie zuerst zu <strong>System → E-Mail-Einstellungen</strong>.',
          whatsappPanel: 'WhatsApp',
          whatsappNotLicensed: 'WhatsApp ist in Ihrer Lizenz nicht enthalten. Aktivieren Sie es in Ihrer Lizenzzeile oder upgraden Sie Ihren Plan.',
          whatsappDesc: 'Verwendet WATI-Zugangsdaten aus System → WhatsApp. Schüler können pro Nachrichtentyp im Portal abbestellen.',
          notLicensedBadge: 'Nicht lizenziert',
          whatsappFooter: 'API-Schlüssel unter System → WhatsApp konfigurieren.',
          whatsappMaster: 'WhatsApp-Benachrichtigungen (Hauptschalter)',
          studentsSection: 'Schüler',
          staffSection: 'Personal & Akademie',
          inappPanel: 'In-App',
          inappDesc: 'Glocken-Benachrichtigungen im Dashboard für Admins, Manager und Trainer.',
          walletPanel: 'Guthabenwarnungen',
          walletDesc: 'Für E-Mail- und WhatsApp-Warnungen bei niedrigem Guthaben, wenn diese Kanäle aktiviert sind.',
          lowWalletThreshold: 'Schwellenwert niedriges Guthaben',
          lowWalletHelper: 'Benachrichtigen, wenn das Schülerguthaben unter diesen Betrag fällt.',
          walletTip: '<strong>Tipp:</strong> Schülerspezifische Abmeldungen finden Sie in den Benachrichtigungseinstellungen jedes Schülers. Diese Seite steuert, was die Akademie standardmäßig senden darf.',
          emailMaster: 'E-Mail-Benachrichtigungen (Hauptschalter)',
          emailFeeReminders: 'Gebührenerinnerungen per E-Mail',
          emailSessionRemindersStudent: 'Stundenerinnerungen für Schüler',
          emailSessionRemindersCoach: 'Erinnerungen an ausstehende Stunden (Trainer)',
          emailAbsenceAlerts: 'Fehlzeitenalarme per E-Mail',
          emailSessionCancelled: 'Stundenabsage-E-Mails',
          emailOnlineTopupAdmin: 'Online-Zahlung erhalten (Admin-Alarm)',
          inappMaster: 'In-App-Benachrichtigungen (Hauptschalter)',
          inappFeeAlerts: 'In-App-Gebührenwarnungen',
          inappPendingSessions: 'In-App-Warnungen ausstehende Stunden',
          inappAtRiskStudents: 'In-App-Warnungen gefährdete Schüler',
          waSessionReminders: 'Stundenerinnerungen',
          waAbsenceAlerts: 'Fehlzeitenalarme',
          waLowBalance: 'Warnungen niedriges Guthaben',
          waFeeReminders: 'Gebühren / offene Beträge',
          waSessionCancelled: 'Stundenabsagen',
          waPendingSessions: 'Ausstehende Stunden (Trainer)',
          waLowBalanceAdmin: 'Guthabenwarnungen (Admins)',
          waHolidayAnnouncements: 'Feiertagsankündigungen',
          waCorrespondenceDeadline: 'Korrespondenz-Frist (Spielplattform)',
          waCorrespondenceYourMove: 'Korrespondenz – Ihr Zug (Spielplattform)'
        },
        billing: {
          title: 'Zahlungsdurchsetzung',
          subtitle: 'Anwesenheits- und Gebühreneinzugsverhalten steuern',
          loadFailed: 'Zahlungsdurchsetzungseinstellungen konnten nicht geladen werden.',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          stepThree: 'Schritt 3',
          modeLabel: 'Aktueller Modus',
          modeStrict: 'Streng — zuerst zahlen',
          modeGrace: 'Karenz erlaubt',
          modeFlexible: 'Flexibel',
          attendanceSection: 'Anwesenheit & unbezahlte Gebühren',
          attendanceSectionDesc: 'Festlegen, ob Schüler bei unbezahlter Periodengebühr teilnehmen dürfen und wie lange die Karenz dauert.',
          requirePayment: 'Zahlung vor Anwesenheit erforderlich',
          requirePaymentDesc: 'Personal kann unbezahlt nicht einschreiben; Guthaben muss bei Einschreibung belastet werden.',
          allowUnpaidPeriod: 'Anwesenheit bei unbezahlter Periodengebühr erlauben',
          allowUnpaidPeriodDesc: 'Schüler dürfen nach fehlgeschlagenem Periodeneinzug teilnehmen, bis Karenzgrenzen erreicht sind.',
          graceSection: 'Karenzgrenzen',
          graceSectionDesc: 'Leer lassen = kein Limit. Das zuerst erreichte Limit sperrt weitere Anwesenheit.',
          graceCalendarDays: 'Karenz Kalendertage (optional)',
          graceSessions: 'Karenz Stunden (optional)',
          graceOptionalNote: 'Leer = kein Limit für diese Regel',
          walletSection: 'Guthaben & Einzug',
          walletSectionDesc: 'Verhalten bei niedrigem Guthaben und Dauer der automatischen Einzugsversuche.',
          allowNegativeWallet: 'Negatives Guthaben bei Belastung erlauben',
          allowNegativeWalletDesc: 'Belastungen erfolgen auch bei Nullsaldo — der Schüler trägt eine Schuld.',
          collectionWindow: 'Einzugs-Wiederholungsfenster (Kalendertage)',
          collectionWindowDesc: 'Wie viele Tage das System unbezahlte Periodengebühren erneut versucht (1–14).',
          remindersSection: 'Erinnerungen',
          remindersSectionDesc: 'Automatische Hinweise, wenn Einschreibungen enden.',
          expiryReminders: 'Einschreibungsablauf-Erinnerungen senden',
          expiryRemindersDesc: 'Schüler (und verknüpfte Erziehungsberechtigte) vor dem Einschreibungsende benachrichtigen.',
          saveSettings: 'Einstellungen speichern',
          saveSuccess: 'Einstellungen gespeichert.',
          saveFailed: 'Einstellungen konnten nicht gespeichert werden.'
        },
        regional: {
          title: 'Regionale Einstellungen',
          subtitle: 'Sprache, Währung und Datums-/Zeitdarstellung in App, E-Mails und PDFs konfigurieren. Zahlungsgateways unter System → Zahlungsmethoden.',
          quickPreset: 'Schnellvorlage',
          custom: 'Benutzerdefiniert',
          sampleAmount: 'Beispielbetrag:',
          sampleDate: 'Beispieldatum:',
          sampleTime: 'Beispielzeit:',
          timezoneLabel: 'Zeitzone:',
          cardLocale: 'Sprache & Zeitzone',
          cardCountry: 'Land & Telefon',
          cardCurrency: 'Währung',
          cardTax: 'Steuer (nur Anzeige)',
          cardDateTime: 'Datum und Uhrzeit',
          timezone: 'Zeitzone',
          timezoneNote: 'Für PDF-Export-Zeitstempel und regionale Standardwerte.',
          localeTag: 'Locale-Tag (BCP 47)',
          localeTagNote: 'Steuert Zahlengruppierung und Datumsreihenfolge in der Web-App.',
          countryCode: 'Ländercode (ISO)',
          countryName: 'Ländername',
          phoneDialCode: 'Telefonvorwahl',
          phoneFormatHint: 'Telefonformat-Hinweis',
          countryNote: 'Landesänderung aktualisiert verfügbare Zahlungsmethoden (UPI nur für Indien usw.).',
          currencyNote: 'Alle Guthaben- und Stundengebühren verwenden diese Währung.',
          currencyCode: 'Währungscode (ISO 4217)',
          currencySymbol: 'Währungssymbol',
          decimalPlaces: 'Dezimalstellen',
          symbolPosition: 'Symbolposition',
          symbolBefore: 'Vor Betrag ({{example}})',
          symbolAfter: 'Nach Betrag ({{example}})',
          taxNote: 'ChessDesk zeigt Steuerbezeichnungen, erstellt aber keine gesetzlichen Steuerrechnungen.',
          taxLabel: 'Steuerbezeichnung',
          taxRate: 'Steuersatz (%)',
          taxId: 'Steuer-ID',
          taxIdLabel: 'Steuer-ID-Feldbezeichnung',
          taxIncluded: 'Steuer in angezeigten Preisen enthalten',
          taxIncludedYes: 'Ja (inklusive)',
          taxIncludedNo: 'Nein (exklusive)',
          timeFormat: 'Zeitformat (Web-App)',
          timeFormatNote: 'Stunden, Anwesenheit, Kalender und Zeitauswahl.',
          time24: '24-Stunden (14:30)',
          time12: '12-Stunden (02:30 PM)',
          datetimeFormat: 'Datums- & Zeitformat (E-Mails & PDFs)',
          datetimeFormatNote: 'Java-Muster für Berichte, Erinnerungen und Belege.',
          saveSettings: 'Einstellungen speichern',
          saving: 'Speichern…',
          loadFailed: 'Regionale Einstellungen konnten nicht geladen werden.',
          saveSuccess: 'Regionale Einstellungen erfolgreich gespeichert.',
          saveFailed: 'Regionale Einstellungen konnten nicht gespeichert werden.',
          placeholders: {
            localeTag: 'de-DE',
            countryCode: 'DE',
            countryName: 'Deutschland',
            phoneDialCode: '+49',
            phoneFormatHint: '+49 XXXX XXXXXXX',
            currencyCode: '',
            currencySymbol: '',
            taxLabel: 'MwSt.',
            taxIdLabel: 'USt-IdNr.',
            taxRegistration: 'Ihre Akademie {{label}}'
          }
        },
        branding: {
          title: 'Individueller Markenauftritt',
          subtitle: 'Akademiename, Slogan und Logo festlegen. Änderungen gelten sofort — kein Neuaufbau nötig.',
          loadingSubtitle: 'Markeneinstellungen werden geladen…',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          stepThree: 'Schritt 3',
          stepFour: 'Schritt 4',
          sectionIdentityDesc: 'Wird in Kopfzeile, Login und Fußzeile angezeigt. Leer lassen für Build-Standardwerte.',
          sectionLogoDesc: 'Quadratisches Logo für Kopfzeilen, Login-Bildschirme und PDF-Exporte hochladen.',
          appNameNote: 'Wird in Kopfzeile, Login und Fußzeile angezeigt. Leer lassen für Build-Standard ({{default}}).',
          taglineNote: 'Kurze Zeile unter dem App-Namen in der Kopfzeile. Leer lassen für Build-Standard.',
          saveSettings: 'Einstellungen speichern',
          saveSuccess: 'Markeneinstellungen erfolgreich gespeichert.',
          saveFailed: 'Markeneinstellungen konnten nicht gespeichert werden.',
          enterpriseSubtitle: 'Logo- und Namensänderungen zur Laufzeit nur mit Enterprise-Lizenz.',
          enterpriseFeature: 'Enterprise-Funktion',
          enterpriseNote: 'Nicht-Enterprise-Lizenzen verwenden weiterhin die Standard-Markenkonfiguration aus dem Deployment.',
          loadFailed: 'Markeneinstellungen konnten nicht geladen werden',
          appNameTagline: 'App-Name & Slogan',
          appName: 'App- / Akademiename',
          tagline: 'Slogan',
          saveText: 'Text speichern',
          logoImage: 'Logo-Bild',
          clickChooseLogo: 'Klicken, um ein Logo auszuwählen',
          logoHint: 'PNG, JPG, SVG, WebP · max. 2 MB · empfohlen 256 × 256 px',
          removeLogo: 'Individuelles Logo entfernen',
          removing: 'Entfernen…',
          uploadLogo: 'Logo hochladen',
          uploading: 'Hochladen…',
          logoActive: 'Ein individuelles Logo ist aktiv.',
          logoDefault: 'Kein individuelles Logo — Standard-Logo aus dem Build wird verwendet.',
          academyContact: 'Akademie-Kontakt',
          contactNote: 'Wird in Login-Fußzeile und Hilfeseite für Schüler, Eltern und Personal angezeigt.',
          supportEmail: 'Support-E-Mail',
          supportPhone: 'Support-Telefon',
          saveContact: 'Kontakt speichern',
          livePreview: 'Live-Vorschau',
          previewNote: 'So erscheint die Kopfzeile für Benutzer.',
          loginPreview: 'Login-Bildschirm-Vorschau',
          instantNote: '⚡ Sofort & persistent',
          instantDesc: 'Änderungen werden in der Datenbank gespeichert und beim nächsten Seitenaufruf übernommen — kein Neustart nötig.',
          removeLogoConfirm: 'Individuelles Logo entfernen? Das Standard-Logo wird verwendet.',
          logoUploadSuccess: 'Logo erfolgreich hochgeladen.',
          logoUploadFailed: 'Logo konnte nicht hochgeladen werden',
          logoRemoved: 'Individuelles Logo entfernt. Standard-Logo ist jetzt aktiv.',
          logoRemoveFailed: 'Logo konnte nicht entfernt werden',
          contactUpdated: 'Akademie-Kontaktdaten aktualisiert.',
          contactSaveFailed: 'Kontaktdaten konnten nicht gespeichert werden',
          textUpdated: 'Markentext erfolgreich aktualisiert.',
          textSaveFailed: 'Markentext konnte nicht gespeichert werden',
          validImageFile: 'Bitte eine gültige Bilddatei wählen (PNG, JPG, SVG usw.)',
          logoTooLargeAlert: 'Logo muss kleiner als 2 MB sein'
        },
        license: {
          title: 'Lizenz',
          subtitle: 'Plan-Kapazität, aktuelle Nutzung und Referenz aller ChessDesk-Lizenzstufen. Limits werden vom Anbieter festgelegt.',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          stepThree: 'Schritt 3',
          stepFour: 'Schritt 4',
          yourLicense: 'Ihre Lizenz',
          yourLicenseDesc: 'Edition, Ablauf und Schlüssel Ihres Softwareanbieters.',
          standardPlan: '{{edition}} · Standardlimits {{caps}}',
          loadFailed: 'Lizenzinformationen konnten nicht geladen werden.',
          edition: 'Edition',
          expiry: 'Ablauf',
          licenseKey: 'Lizenzschlüssel',
          notSpecified: 'Nicht angegeben',
          unlimited: 'Unbegrenzt',
          daysRemaining: '{{count}} Tag verbleibend',
          daysRemaining_other: '{{count}} Tage verbleibend',
          slotsRemaining: '{{count}} Platz verbleibend',
          slotsRemaining_other: '{{count}} Plätze verbleibend',
          atLimit: 'Am Limit',
          nearLimit: 'Nahe am Limit',
          capacityTitle: 'Ihre Kapazität (alle Schüler, Trainer & Filialen)',
          capacityNote: 'Schüler und Trainer zählen aktiv oder archiviert. Am Planlimit können archivierte Konten <strong>dauerhaft entfernt</strong> werden, um Plätze freizugeben.',
          students: 'Schüler',
          branches: 'Filialen',
          coaches: 'Trainer',
          featuresTitle: 'Funktionen Ihres Plans',
          featureCore: 'Wallet-Abrechnung, Anwesenheit, Sitzungen und Berichte',
          featureBranding: 'Eigenes Logo und Akademiename:',
          featureWhatsapp: 'WhatsApp (WATI)-Integration:',
          featureBulkImport: 'Massenimport von Schülern per CSV:',
          plansTitle: 'Unterstützte Pläne & Funktionen',
          plansDesc: 'Referenz aller ChessDesk-Lizenzstufen. Ihre aktiven Limits können abweichen, wenn der Anbieter individuelle Obergrenzen gesetzt hat.',
          yourPlan: 'Ihr Plan',
          capacityLimits: 'Kapazitätslimits (Standard)',
          tierCaps: 'bis zu {{students}} Schüler, {{branches}} Filialen, {{coaches}} Trainer',
          plans: { starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' },
          featureGroups: { core: 'Kernplattform', multiBranch: 'Mehrere Standorte & Zahlungen', enterprise: 'Enterprise' },
          features: {
            attendance: 'Anwesenheit, Sitzungen & Sitzungskalender',
            billing: 'Abrechnung, Rechnungen & Schüler-Wallet',
            reports: 'Berichte & Excel-Export',
            portal: 'Schüler- & Elternportal',
            email: 'E-Mail-Benachrichtigungen',
            atRisk: 'Erkennung gefährdeter Schüler',
            multiBranch: 'Verwaltung mehrerer Standorte',
            managerRole: 'Filial-Manager-Rolle',
            crossBranch: 'Standortübergreifende Berichte',
            onlinePayments: 'Online-Zahlungsanbieter (Razorpay, Stripe, PayPal)',
            whatsapp: 'WhatsApp-Benachrichtigungen (WATI)',
            bulkImport: 'Massenimport von Schülern (CSV)',
            customBranding: 'Eigenes Logo & Akademiename in der App'
          },
          activeStudents: 'Aktive Schüler',
          included: 'Enthalten',
          enterpriseOnly: 'Nur Enterprise',
          proEnterprise: 'Professional / Enterprise (oder Sheet-Flag)'
        },
        payment: {
          title: 'Online-Zahlungsgateways',
          subtitle: 'Ein oder mehrere Gateways für Schüler-Guthabenaufladungen aktivieren. Jede Akademie verwendet eigene Händlerzugangsdaten.',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          stepThree: 'Schritt 3',
          gatewaysSection: 'Online-Zahlungsgateways',
          gatewaysSectionDesc: 'Händlerzugangsdaten für Schüler-Guthabenaufladungen konfigurieren. Nur für Ihr Akademieland verfügbare Gateways werden angezeigt.',
          noGatewaysForCountry: 'Für das aktuelle Akademieland sind keine Online-Gateways verfügbar.',
          saveSettings: 'Einstellungen speichern',
          enabled: 'Aktiviert',
          loadFailed: 'Zahlungseinstellungen konnten nicht geladen werden.',
          saveSuccess: 'Gateway-Einstellungen gespeichert.',
          saveFailed: 'Einstellungen konnten nicht gespeichert werden.',
          manualMethods: 'Manuelle Zahlungsmethoden',
          manualMethodsDesc: 'Für manuelle Guthabenaufladungen durch Personal. Nicht genutzte Methoden werden automatisch ausgeblendet.',
          recentActivity: 'Aktuelle Zahlungsaktivität',
          recentActivityDesc: 'Letzte 50 Gateway-Ereignisse — hilfreich bei Webhook- oder Aufladefehlern.',
          noEvents: 'Noch keine Gateway-Ereignisse.',
          when: 'Wann',
          gateway: 'Gateway',
          event: 'Ereignis',
          status: 'Status',
          student: 'Schüler',
          detail: 'Detail',
          deploymentNote: 'Deployment-Hinweis',
          licenseRequired: 'Online-Zahlungsgateways erfordern eine <strong>Professional</strong>- oder <strong>Enterprise</strong>-Lizenz{{edition}}. Einstellungen können hier eingesehen werden, aber Schüler können online erst nach Upgrade zahlen.',
          licenseEditionSuffix: ' (aktuell: {{edition}})',
          httpsRequired: 'Stripe und PayPal benötigen eine öffentliche HTTPS-URL. Aktuelle Basis: <code class="font-mono text-xs">{{url}}</code>. Setzen Sie <code class="font-mono">FRONTEND_BASE_URL=https://…</code> in Produktion.',
          localhostHttpsHint: 'Entwicklungs-URL {{url}} ist lokal in Ordnung. Für Live-Mollie/Stripe setzen Sie FRONTEND_BASE_URL auf Ihre öffentliche https://-Akademie-Domain und registrieren Sie die Webhook-URLs auf den Gateway-Karten.',
          academyCurrency: 'Akademiewährung: <strong>{{currency}}</strong> — nur Zahlungsmethoden für <strong>{{country}}</strong> werden angezeigt.',
          yourCountry: 'Ihr Land',
          gatewayActive: '{{name}} ist aktiv. Schüler können es bei Guthabenaufladungen wählen.',
          gatewayInactive: '{{name}} ist aus. Schüler sehen diese Option nicht.',
          secretKeepPlaceholder: '(bereits gesetzt — leer lassen zum Beibehalten)',
          secretSetHelper: '✓ Secret ist gesetzt. Leer lassen, um den aktuellen Wert zu behalten.',
          secretNotSetHelper: '⚠ Secret noch nicht gesetzt — erforderlich für Zahlungen.',
          paypalSandbox: 'PayPal-Sandbox-Modus',
          paypalSandboxHint: 'Sandbox-API für Tests mit developer.paypal.com-Sandbox-Zugangsdaten verwenden.',
          testConnection: 'Verbindung testen',
          testing: 'Teste…',
          connected: 'Verbunden.',
          connectionFailed: 'Verbindung fehlgeschlagen.',
          connectionTestFailed: 'Verbindungstest fehlgeschlagen.',
          webhookUrlTitle: 'Webhook-URL (im {{name}}-Dashboard registrieren):',
          subscribeTo: 'Abonnieren:',
          keysFrom: 'Schlüssel von',
          currencyUnsupported: '{{name}} unterstützt Akademiewährung {{currency}} nicht. Regionale Einstellungen ändern oder anderes Gateway wählen.',
          httpsGatewayHint: '{{name}} benötigt eine öffentliche HTTPS-URL (FRONTEND_BASE_URL setzen). Localhost ist nur für Entwicklung OK.',
          resolveWarnings: 'Beheben Sie die Warnungen oben, bevor {{name}} Live-Zahlungen akzeptieren kann.',
          secretRequiredWhenEnabled: '⚠ {{label}} ist erforderlich, wenn {{name}} aktiviert ist.',
          webhookOptionalHint: 'Optional — ermöglicht signiertes Webhook-Backup, wenn der Browser nicht zurückkehrt.',
          manualMethodDefault: 'Personal kann Guthabenaufladungen mit dieser Methode erfassen.',
          deploymentHttps: 'Stripe und PayPal benötigen eine öffentliche HTTPS-URL. Ihre Site muss <code class="font-mono">/api/</code> an das Backend weiterleiten, damit Webhooks <code class="font-mono">{{webhookBase}}/api/webhooks/…</code> erreichen',
          deploymentFrontendUrl: 'Setzen Sie <code class="font-mono">FRONTEND_BASE_URL</code> in Ihrer Umgebung für korrekte Zahlungs-Rückgabe-URLs.'
        },
        report: {
          title: 'Berichteinstellungen',
          subtitle: 'Verfügbare Berichte im Berichte-Hub wählen und berichtsspezifische Eingaben konfigurieren.',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          stepThree: 'Schritt 3',
          availableReports: 'Welche Berichte sind verfügbar',
          availableReportsDesc: 'Schalten Sie einen Bericht aus, um ihn im Berichte-Hub auszublenden und per API zu blockieren. Schüler- und Eltern-Stundenabrechnung folgt demselben Schalter.',
          enabledCountLabel: 'Aktiv',
          statusOn: 'An',
          statusOff: 'Aus',
          visibleTo: 'Sichtbar für',
          appliesTo: 'Gilt für',
          groups: {
            finance: 'Gebühren & Geld',
            attendance: 'Anwesenheit & Stunden',
            export: 'Datenexport'
          },
          coachRevenue: 'Trainer-Provisionssplit',
          coachRevenueDesc: 'Akademieanteil pro Stundentyp. Der Trainer erhält den Rest.',
          batchAcademyShare: 'Gruppenstunden — Akademieanteil (%)',
          individualAcademyShare: 'Einzelstunden — Akademieanteil (%)',
          splitNote: 'Aufteilung: Trainer {{coach}}% / Akademie {{academy}}%',
          batchAttendance: 'Gruppen-Anwesenheitsoptionen',
          batchAttendanceDesc: 'Steuert, was Trainer und Manager in der Anwesenheitsmatrix sehen. Admins sehen Stundengebühren immer.',
          showCharges: 'Stundengebühren für Trainer und Manager anzeigen',
          showChargesDesc: 'Trainer und Manager sehen Gebühren in Berichten, Excel-Exporten und PDFs.',
          saveSettings: 'Einstellungen speichern',
          loadFailed: 'Berichteinstellungen konnten nicht geladen werden.',
          saveSuccess: 'Berichteinstellungen erfolgreich gespeichert.',
          saveFailed: 'Berichteinstellungen konnten nicht gespeichert werden.',
          percentError: 'Geben Sie einen Prozentsatz zwischen 0 und 100 ein.',
          percentRange: 'Muss zwischen 0 und 100 liegen.'
        },
        holidays: {
          title: 'Feiertagsverwaltung',
          subtitle: 'Akademie- oder filialspezifische Schließtage blockieren. Auf Feiertagen können keine Stunden geplant werden.',
          loadingSubtitle: 'Feiertage werden geladen…',
          stepOne: 'Schritt 1',
          stepTwo: 'Schritt 2',
          saveSuccessTitle: 'Feiertag gespeichert',
          saveSuccessWithEmails: 'Der Feiertag wurde gespeichert und Benachrichtigungs-E-Mails werden an alle relevanten Mitarbeiter gesendet.',
          saveSuccessWithoutEmails: 'Der Feiertag wurde gespeichert, ohne Benachrichtigungs-E-Mails zu senden.',
          saveHolidayFailed: 'Feiertag konnte nicht gespeichert werden.',
          deleteTitle: 'Feiertag löschen?',
          deletePastTitle: 'Vergangenen Feiertag löschen?',
          deleteMessage: '"{{name}}" wird entfernt. Neue Stunden können an diesen Tagen wieder geplant werden. Falls Personal bereits benachrichtigt wurde, informieren Sie es bei Bedarf manuell.',
          deletePastMessage: '"{{name}}" ist bereits beendet. Das Entfernen aktualisiert nur diese Liste und den Kalender. Vergangene Stunden und Anwesenheit bleiben unverändert.',
          deleteHint: 'Feiertag löschen und Planung an diesen Tagen wieder erlauben',
          deletePastHint: 'Beendeten Feiertag aus der Liste entfernen',
          thisHoliday: 'dieser Feiertag',
          addNew: 'Neuen Feiertag hinzufügen',
          addNewDesc: 'Schließzeitraum für die gesamte Akademie oder eine Filiale festlegen.',
          holidayName: 'Feiertagsname',
          startDate: 'Startdatum',
          endDate: 'Enddatum',
          branch: 'Filiale für Feiertag',
          branchHelper: 'Nur wählen, wenn der Feiertag nur für diesen Standort gilt.',
          allBranches: 'Alle Filialen (akademieweit)',
          notes: 'Feiertagsnotizen',
          addHoliday: 'Feiertag hinzufügen',
          configured: 'Konfigurierte Feiertage',
          total: '{{count}} gesamt',
          upcomingPast: '· {{upcoming}} bevorstehend · {{past}} vergangen',
          colName: 'Feiertagsname',
          colFrom: 'Von',
          colTo: 'Bis',
          colBranch: 'Filiale',
          colActions: 'Aktionen',
          noHolidays: 'Noch keine Feiertage konfiguriert.',
          allBranchesBadge: 'Alle Filialen',
          past: 'Vergangen',
          delete: 'Löschen',
          loadFailed: 'Feiertage konnten nicht geladen werden.',
          nameRequired: 'Feiertagsname ist erforderlich.',
          datesRequired: 'Start- und Enddatum sind erforderlich.',
          validDatesFormat: 'Bitte gültige Daten im Format {{format}} eingeben.',
          previewFailed: 'E-Mail-Vorschau konnte nicht vorbereitet werden.',
          deleteFailed: 'Feiertag konnte nicht gelöscht werden.',
          endDateInvalid: 'Das Enddatum muss am oder nach dem Startdatum liegen.',
          notesPlaceholder: 'Optionale interne Notiz',
          namePlaceholder: 'z. B. Sommerferien, Diwali-Ferien',
          emailPreview: {
            title: 'Feiertags-Benachrichtigungs-E-Mail prüfen',
            subtitle: 'E-Mail unten ansehen, dann wählen, ob {{count}} relevanter Empfänger benachrichtigt werden soll (Personal und Schüler, wo zutreffend).',
            subtitle_other: 'E-Mail unten ansehen, dann wählen, ob {{count}} relevante Empfänger benachrichtigt werden sollen (Personal und Schüler, wo zutreffend).',
            subjectLabel: 'Betreff',
            previewLabel: 'E-Mail-Vorschau',
            previewHelper: 'Jeder Empfänger erhält eine personalisierte Anrede; die Vorschau zeigt ein Beispiellayout.',
            recipientsLabel: 'Empfänger ({{count}})',
            noRecipients: 'Keine aktiven Admins, Manager, Trainer oder Schüler mit E-Mail-Adressen gefunden. Sie können den Feiertag trotzdem ohne Benachrichtigungen speichern.',
            roleBlockTitle: '{{role}} ({{count}})',
            roleSummaryItem: '{{count}} {{role}}',
            roleSummaryItem_other: '{{count}} {{role}}',
            studentBulkNotice: '{{count}} Schüler mit E-Mail-Adresse erhält diese Mitteilung.',
            studentBulkNotice_other: '{{count}} Schüler mit E-Mail-Adressen erhalten diese Mitteilung.',
            moreStudentsHidden: '…und {{count}} weiterer Schüler hier nicht angezeigt.',
            moreStudentsHidden_other: '…und {{count}} weitere Schüler hier nicht angezeigt.',
            saveWithoutEmails: 'Ohne E-Mails speichern',
            saveAndSend: 'Speichern & E-Mails senden',
            roles: {
              Admin: 'Admin',
              Manager: 'Manager',
              Coach: 'Trainer',
              Student: 'Schüler'
            }
          }
        }
      },
      forms: {
        common: {
          firstName: 'Vorname',
          middleName: 'Zweiter Vorname',
          lastName: 'Nachname',
          dateOfBirth: 'Geburtsdatum',
          gender: 'Geschlecht',
          male: 'Männlich',
          female: 'Weiblich',
          other: 'Divers',
          loginUsername: 'Anmelde-Benutzername',
          emailAddress: 'E-Mail-Adresse',
          phoneNumber: 'Telefonnummer',
          accountActive: 'Konto aktiv',
          loginPassword: 'Anmeldepasswort',
          confirmLoginPassword: 'Anmeldepasswort bestätigen',
          roles: 'Rollen',
          select: 'Auswählen…',
          cancel: 'Abbrechen',
          save: 'Speichern',
          saving: 'Speichern…',
          create: 'Erstellen',
          update: 'Aktualisieren',
          none: '— Keine —'
        },
        placeholders: {
          firstName: 'Vorname',
          middleName: 'Zweiter Vorname',
          lastName: 'Nachname',
          firstNameRequired: 'Vorname *',
          lastNameRequired: 'Nachname *',
          username: 'Benutzername',
          email: 'E-Mail',
          password: 'Passwort',
          confirmPassword: 'Passwort bestätigen'
        },
        phone: {
          countryCode: 'Ländervorwahl',
          searchCountry: 'Land suchen',
          noMatches: 'Keine Treffer',
          phoneNumber: 'Telefonnummer'
        },
        address: {
          title: 'Adresse',
          addressLine1: 'Adresszeile 1',
          addressLine2: 'Adresszeile 2',
          addressLine2Placeholder: 'Wohnung / Etage / Gebäude (optional)',
          country: 'Land',
          provinceRegion: 'Bundesland / Region',
          cityMunicipality: 'Stadt / Gemeinde',
          postcode: 'Postleitzahl',
          latitude: 'Breitengrad',
          longitude: 'Längengrad',
          optional: '(optional)',
          selectCountry: 'Land auswählen',
          selectCountryFirst: 'Zuerst Land auswählen',
          selectProvince: 'Bundesland / Region auswählen',
          noRegions: 'Keine Regionen für dieses Land',
          selectProvinceFirst: 'Zuerst Bundesland / Region auswählen',
          loadingCities: 'Städte werden geladen...',
          searchCity: 'Stadt suchen',
          saved: '(gespeichert)',
          noCityList: 'Keine Stadtliste für diese Region — Sie können den Stadtnamen eingeben.',
          fillHint: 'Wenn Sie ein Adressfeld ausfüllen, geben Sie bitte mindestens <strong>Land</strong> und <strong>Stadt</strong> an. Postleitzahl, Region und Lat/Lng sind optional.',
          countryCityRequired: 'Wenn Sie eine Adresse eingeben, füllen Sie bitte mindestens Land und Stadt aus.',
          streetPlaceholder: 'Straßenadresse',
          egExample: 'z. B. {{example}}'
        },
        student: {
          addTitle: 'Schüler hinzufügen',
          editTitle: 'Schüler bearbeiten',
          subtitle: 'Schülerinformationen, Zugangsdaten und Adresse in einem Workflow erfassen.',
          fideSection: 'FIDE / Wertung (optional)',
          fideId: 'FIDE-ID',
          eloRating: 'Elo-Wertung',
          fideTitle: 'FIDE-Titel',
          createButton: 'Schüler erstellen',
          updateButton: 'Schüler aktualisieren',
          managerRolesNote: 'Manager können nur Schülerkonten erstellen oder bearbeiten.',
          updated: 'Schüler aktualisiert',
          created: 'Schüler erstellt',
          updateFailed: 'Schüler konnte nicht aktualisiert werden.',
          saveFailed: 'Schüler konnte nicht gespeichert werden.',
          loadArchiveFailed: 'Archivierungsoptionen konnten nicht geladen werden.',
          enrollmentsEnded: 'Einschreibungen beendet. Das Schülerkonto bleibt aktiv.',
          archived: 'Schüler archiviert.',
          archiveFailed: 'Schüler konnte nicht archiviert werden.',
          activeDesc: 'Archivierte Schüler bleiben in der Historie und können später reaktiviert werden.',
          activeDescArchive: 'Deaktivieren, um diesen Schüler zu archivieren. Bei aktiven Einschreibungen wählen Sie, wie diese beendet werden.'
        },
        coach: {
          addTitle: 'Trainer hinzufügen',
          editTitle: 'Trainer bearbeiten',
          subtitle: 'Traineridentität, Login-Zugang und Profildetails verwalten.',
          createButton: 'Trainer erstellen',
          updateButton: 'Trainer aktualisieren',
          activeDesc: 'Zum Archivieren oder Reaktivieren des Trainers ohne Verlust der Historie.',
          updated: 'Trainer aktualisiert',
          created: 'Trainer erstellt',
          updateFailed: 'Trainer konnte nicht aktualisiert werden.',
          saveFailed: 'Trainer konnte nicht gespeichert werden.'
        },
        manager: {
          addTitle: 'Manager hinzufügen',
          editTitle: 'Manager bearbeiten',
          subtitle: 'Manager-Einrichtung konsistent und übersichtlich halten.',
          createButton: 'Manager erstellen',
          updateButton: 'Manager aktualisieren',
          updated: 'Manager aktualisiert',
          created: 'Manager erstellt',
          saveFailed: 'Manager konnte nicht gespeichert werden.',
          activeDesc: 'Manager können sicher archiviert werden, ohne ihre Datensätze zu entfernen.'
        },
        user: {
          addTitle: 'Benutzer erstellen',
          editTitle: 'Benutzer bearbeiten',
          subtitle: 'Benutzerkontodetails, Rollen und Zugriff verwalten.',
          emailSignInLink: 'E-Mail-Anmeldelink (Passwort wird selbst gesetzt)',
          createButton: 'Benutzer erstellen',
          updateButton: 'Benutzer aktualisieren',
          loadFailed: 'Benutzer konnte nicht geladen werden.',
          saveFailed: 'Benutzer konnte nicht gespeichert werden. Bitte Eingaben prüfen.'
        },
        branch: {
          addTitle: 'Filiale hinzufügen',
          editTitle: 'Filiale bearbeiten',
          subtitle: 'Filialdetails mit übersichtlichen Abschnitten erstellen oder aktualisieren.',
          branchName: 'Filialname',
          branchCode: 'Filialcode',
          branchMode: 'Filialmodus',
          managerOptional: 'Manager (wahlfrei)',
          branchActive: 'Filiale aktiv',
          branchActiveDesc: 'Deaktivieren, um die Filiale zu archivieren und alle Datensätze zu behalten.',
          branchAddress: 'Filialadresse',
          onlineNote: 'Physische Adresse wird für reine Online-Filialen ausgeblendet.',
          createButton: 'Filiale erstellen',
          updateButton: 'Filiale aktualisieren',
          loadManagersFailed: 'Manager konnten nicht geladen werden',
          updateFailed: 'Filiale konnte nicht aktualisiert werden.',
          saveFailed: 'Filiale konnte nicht gespeichert werden.',
          nameRequired: 'Filialname ist erforderlich.',
          codeRequired: 'Filialcode ist erforderlich.',
          modeRequired: 'Modus ist erforderlich.',
          updateSuccess: 'Filiale erfolgreich aktualisiert',
          addSuccess: 'Filiale erfolgreich hinzugefügt',
          namePlaceholder: 'z. B. Hauptfiliale',
          uniqueCodePlaceholder: 'Eindeutiger Code'
        },
        batch: {
          addTitle: 'Gruppe erstellen',
          editTitle: 'Gruppe bearbeiten',
          subtitle: 'Gruppenplan, Gebühren und Stundeneinstellungen konfigurieren.',
          branch: 'Filiale für Gruppe',
          level: 'Stufe',
          coach: 'Zugewiesener Trainer',
          name: 'Gruppenname',
          sessionMode: 'Gruppen-Stundenmodus',
          defaultStart: 'Standard-Start (HH:mm)',
          defaultEnd: 'Standard-Ende (HH:mm)',
          selectStartTime: 'Startzeit wählen',
          selectEndTime: 'Endzeit wählen',
          billingConfig: 'Abrechnungskonfiguration',
          chargesMode: 'Abrechnungsmodus',
          sessionCharge: 'Stundengebühr ({{currency}})',
          periodLength: 'Standard-Periodenlänge',
          periodFee: 'Periodengebühr ({{currency}})',
          carryForward: 'Übertragsrichtlinie',
          selectBranchFirst: 'Zuerst Filiale wählen',
          selectEllipsis: 'Auswählen…',
          notePerSession: 'Gebühr pro Sitzung einzeln',
          notePerSessionHourly: 'Stundensatz × Sitzungsdauer',
          notePerPeriod: 'Feste Gebühr für einen datumsbegrenzten Einschreibungszeitraum',
          periodNoCarryForward: 'Sitzungsübertragung gilt nicht für Periodenabrechnungsgruppen. Ungenutzte Sitzungen werden nicht zwischen Abrechnungsperioden übertragen.',
          validation: {
            selectBranch: 'Bitte eine Filiale auswählen',
            selectLevel: 'Bitte eine Stufe auswählen',
            selectCoach: 'Bitte einen Trainer auswählen',
            selectSessionMode: 'Bitte einen Stundenmodus auswählen',
            enterName: 'Bitte einen Namen für die Gruppe eingeben',
            selectSessionDay: 'Bitte mindestens einen Unterrichtstag auswählen',
            selectDefaultStart: 'Bitte Standard-Startzeit auswählen',
            selectDefaultEnd: 'Bitte Standard-Endzeit auswählen',
            endAfterStart: 'Standard-Endzeit muss nach der Standard-Startzeit liegen',
            saveFailed: 'Gruppe konnte nicht gespeichert werden'
          }
        },
        level: {
          addTitle: 'Stufe hinzufügen',
          editTitle: 'Stufe bearbeiten',
          subtitle: 'Stufenname und Anzeigereihenfolge für Gruppen und Einschreibungen festlegen.',
          saveFailed: 'Speichern fehlgeschlagen'
        },
        validation: {
          firstNameRequired: 'Vorname ist erforderlich und darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten.',
          middleNameLetters: 'Zweiter Vorname darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten.',
          firstNameLetters: 'Vorname darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten — keine Ziffern.',
          lastNameLetters: 'Nachname darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten — keine Ziffern.',
          lastNameRequired: 'Nachname ist erforderlich und darf Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten.',
          invalidDob: 'Bitte ein gültiges Geburtsdatum in der Vergangenheit eingeben.',
          usernameRequired: 'Benutzername ist erforderlich.',
          emailInvalid: 'Bitte eine gültige E-Mail-Adresse eingeben.',
          passwordMin: 'Passwort muss mindestens 6 Zeichen haben.',
          passwordsMismatch: 'Passwort und Bestätigung stimmen nicht überein.',
          rolesRequired: 'Bitte mindestens eine Rolle auswählen.',
          invalidAddress: 'Ungültige Adresse.',
          invalidLatitude: 'Breitengrad muss eine gültige Zahl sein.',
          invalidLongitude: 'Längengrad muss eine gültige Zahl sein.'
        }
      },
      authPages: {
        forgotTitle: 'Passwort vergessen',
        emailOrUsername: 'E-Mail oder Benutzername',
        emailOrUsernamePlaceholder: 'sie@beispiel.de oder Ihr Benutzername',
        sending: 'Senden…',
        backToSignIn: 'Zurück zur Anmeldung',
        setPasswordTitle: 'Passwort festlegen',
        missingToken: 'Token in URL fehlt. Bitte verwenden Sie den neuesten Anmeldelink aus Ihrer E-Mail.',
        newPassword: 'Neues Passwort',
        confirmPassword: 'Passwort bestätigen',
        passwordRules: 'Mindestens 8 Zeichen, 1 Großbuchstabe, 1 Zahl und 1 Sonderzeichen (@ $ ! % * ? &).',
        setPasswordButton: 'Passwort festlegen',
        saving: 'Speichern…',
        invalidToken: 'Dieser Anmeldelink ist ungültig. Token in der URL fehlt.',
        passwordInvalid: 'Passwort muss mindestens 8 Zeichen haben und einen Großbuchstaben, eine Zahl und ein Sonderzeichen enthalten.',
        passwordsMismatch: 'Passwörter stimmen nicht überein.',
        passwordSetSuccess: 'Passwort erfolgreich festgelegt. Sie können sich jetzt anmelden.',
        linkInvalidExpired: 'Link ist ungültig oder abgelaufen.'
      },
      roleSelect: {
        title: 'Rolle wählen',
        noRoles: 'Keine Rollen in Ihrem Konto gefunden.',
        contactAdmin: 'Bitte wenden Sie sich an einen Administrator.',
        switchHint: 'Sie können Ihre Rolle jederzeit im Kopfmenü wechseln.'
      },
      setupWizard: {
        welcomeTitle: 'Willkommen in der App',
        welcomeSubtitle: 'Richten Sie Ihre Akademie in wenigen Schritten ein.',
        stepOf: 'Schritt {{current}} von {{total}}',
        footerHint: 'Alle Einstellungen können jederzeit im Admin-Bereich geändert werden.',
        continue: 'Weiter →',
        back: '← Zurück',
        saving: 'Speichern…',
        finishing: 'Abschließen…',
        finishSetup: 'Einrichtung abschließen →',
        enterDashboard: 'Zum Dashboard →',
        optional: '(optional)',
        saved: 'Gespeichert',
        skipped: 'Übersprungen',
        configured: 'Konfiguriert',
        notConfiguredEmail: 'Nicht konfiguriert — System → E-Mail-Einstellungen',
        notConfiguredWhatsApp: 'Nicht konfiguriert — System → WhatsApp',
        saveError: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
        regionalSaveError: 'Regionale Einstellungen wurden nicht gespeichert. Bitte erneut versuchen.',
        emailSaveError: 'E-Mail-Einstellungen konnten nicht gespeichert werden.',
        whatsappSaveError: 'WhatsApp-Einstellungen konnten nicht gespeichert werden.',
        step1: {
          title: 'Akademie benennen',
          description: 'Erscheint in Kopfzeile, Login, E-Mails und allen PDF-Exporten.',
          logo: 'Akademie-Logo',
          clickUpload: 'Klicken, um Logo hochzuladen',
          logoFormats: 'PNG, JPG, SVG · max. 2 MB',
          clickChange: 'Klicken zum Ändern',
          logoPreviewAlt: 'Logo-Vorschau',
          academyName: 'Akademiename',
          tagline: 'Slogan',
          nameRequired: 'Akademiename ist erforderlich.',
          logoTooLarge: 'Logo muss unter 2 MB sein.',
          namePlaceholder: 'z. B. Kingside Schachschule',
          taglinePlaceholder: 'z. B. Tiefer denken, stärker spielen'
        },
        step2: {
          title: 'Kontaktdaten',
          description: 'Dieser öffentliche Support-Kontakt wird Schülern und Eltern angezeigt. Er ist nicht dasselbe wie Ihre ausgehende SMTP-Absenderadresse (im nächsten optionalen Schritt).',
          supportPhone: 'Support-Telefonnummer',
          supportEmail: 'Support-E-Mail',
          phonePlaceholder: 'Support-Telefonnummer',
          emailPlaceholder: 'z. B. support@meineakademie.de',
          phoneHint: 'Wird auf Rechnungen, E-Mails und im Schülerportal angezeigt.',
          emailHint: 'Schüler können sich unter dieser Adresse melden.',
          phoneRequired: 'Telefonnummer ist erforderlich.',
          emailRequired: 'E-Mail-Adresse ist erforderlich.',
          emailInvalid: 'Bitte eine gültige E-Mail-Adresse eingeben.'
        },
        step3: {
          title: 'Region & Zahlungen',
          description: 'Wählen Sie Land und Währung Ihrer Akademie. Online-Zahlungsanbieter (Razorpay, Stripe, PayPal) konfigurieren Sie später unter System → Zahlungsmethoden.'
        },
        step4: {
          title: 'Kommunikation verbinden',
          description: 'Optional — ausgehende E-Mail und WhatsApp jetzt einrichten oder später im Admin-Bereich.',
          skipHint: 'Übersprungene Punkte können später unter System → E-Mail-Einstellungen und System → WhatsApp konfiguriert werden.',
          emailSection: 'Ausgehende E-Mail (SMTP)',
          emailSectionHint: 'Für Anmeldelinks, Erinnerungen, Abrechnungen und Alarme.',
          whatsappSection: 'WhatsApp (WATI)',
          whatsappSectionHint: 'Stundenerinnerungen, Guthabenwarnungen und Betriebsnachrichten.',
          whatsappNotLicensed: 'WhatsApp ist in Ihrer Lizenz nicht enthalten. Sie können dies überspringen und später upgraden.',
          saveEmail: 'E-Mail-Einstellungen speichern',
          saveWhatsApp: 'WhatsApp-Einstellungen speichern',
          skipForNow: 'Vorerst überspringen',
          smtpHost: 'SMTP-Host',
          smtpPort: 'SMTP-Port',
          smtpUsername: 'SMTP-Benutzername',
          smtpPassword: 'SMTP-Passwort',
          fromEmail: 'Absender-E-Mail',
          alreadySet: '(bereits gesetzt)',
          requiredToSave: 'Zum Speichern erforderlich',
          watiBaseUrl: 'WATI-API-Basis-URL',
          channelNumber: 'Geschäfts-Kanalnummer',
          watiToken: 'WATI-Bearer-Token',
          pasteToken: 'Token einfügen'
        },
        step5: {
          title: 'Alles bereit!',
          ready: '{{name}} ist startklar. Sie können diese Angaben jederzeit unter Einstellungen → Markenauftritt aktualisieren.',
          academyLabel: 'Akademie',
          supportLabel: 'Support',
          communicationsLabel: 'Kommunikation',
          outboundEmail: 'Ausgehende E-Mail:',
          whatsapp: 'WhatsApp:'
        }
      },
      publicHelp: {
        backToLogin: '← Zurück zur Anmeldung',
        title: 'Hilfe & Erste Schritte',
        subtitle: 'Kurze Antworten, bevor Sie sich bei {{name}} anmelden.',
        signingIn: 'Anmeldung',
        signingInBody: 'Verwenden Sie Benutzername und Passwort von Ihrem Akademie-Administrator. Wenn Sie eine Anmelde-E-Mail erhalten haben, öffnen Sie den Link darin, legen Sie zuerst Ihr Passwort fest und melden Sie sich dann hier an.',
        forgotPassword: 'Passwort vergessen?',
        forgotPasswordBody: 'Nutzen Sie den Link <1>Passwort vergessen?</1> auf der Anmeldeseite. Sie erhalten einen Link zum Zurücksetzen an Ihre registrierte E-Mail.',
        forgotPasswordLink: 'Passwort vergessen?',
        contactAcademy: 'Akademie kontaktieren',
        contactAcademyBody: 'Bei Zugangsfragen oder akademiespezifischen Anliegen wenden Sie sich direkt an:',
        email: 'E-Mail:',
        phone: 'Telefon:',
        privacy: 'Datenschutz',
        privacyBody: 'Lesen Sie unsere <1>Datenschutzerklärung</1>, um zu erfahren, wie Ihre Daten auf dieser Plattform behandelt werden.',
        privacyLink: 'Datenschutzerklärung'
      },
      licenseInvalid: {
        heading: 'Softwarelizenz inaktiv',
        body: 'Der Zugang zum Anwesenheitssystem wurde ausgesetzt, weil die Softwarelizenz nicht mehr gültig ist. Ihre Daten bleiben vollständig sicher und unverändert.',
        restoreAccess: 'Zugang wiederherstellen',
        softwareProvider: 'Softwareanbieter',
        tryAgain: 'Erneut versuchen',
        contactSupport: 'Support kontaktieren',
        footer: '{{name}} — Anwesenheitsverwaltungssystem'
      },
      guardianPublic: {
        inviteTitle: 'Erziehungsberechtigten-Einladung',
        invalidLink: 'Dieser Einladungslink ist ungültig oder abgelaufen.',
        createAccount: 'Erziehungsberechtigten-Konto erstellen',
        linkingFor: ' — Sie verknüpfen sich als Erziehungsberechtigter für ',
        confirmLink: 'Erziehungsberechtigten-Verknüpfung bestätigen',
        linkAccountFor: ' — verknüpfen Sie Ihr Konto als Erziehungsberechtigter für ',
        invitedEmail: 'Eingeladene E-Mail: {{email}}',
        accountEmail: 'Konto-E-Mail: {{email}}',
        relationship: 'Ihre Beziehung zum Schüler',
        relationships: {
          MOTHER: 'Mutter',
          FATHER: 'Vater',
          GUARDIAN: 'Erziehungsberechtigter',
          GRANDPARENT: 'Großelternteil',
          OTHER: 'Sonstige'
        },
        passwordNote: '8+ Zeichen, Großbuchstabe, Zahl und Sonderzeichen',
        confirmPassword: 'Passwort bestätigen',
        submitRegistration: 'Registrierung absenden',
        submitting: 'Wird gesendet…',
        acceptRequest: 'Annehmen & Zugang anfordern',
        decline: 'Ablehnen',
        nameRequired: 'Vorname, Nachname und Benutzername sind erforderlich.',
        passwordInvalid: 'Passwort muss mindestens 8 Zeichen haben und einen Großbuchstaben, eine Zahl und ein Sonderzeichen enthalten.',
        passwordsMismatch: 'Passwörter stimmen nicht überein.',
        registrationSubmitted: 'Registrierung eingereicht. Bitte melden Sie sich an, nachdem Ihre Akademie den Erziehungsberechtigten-Zugang genehmigt hat.',
        registrationFailed: 'Registrierung fehlgeschlagen. Der Link ist möglicherweise ungültig oder abgelaufen.',
        requestSubmitted: 'Anfrage eingereicht. Bitte melden Sie sich an, nachdem Ihre Akademie den Erziehungsberechtigten-Zugang genehmigt hat.',
        invitationDeclined: 'Einladung abgelehnt.',
        processFailed: 'Diese Einladung konnte nicht verarbeitet werden.'
      },
      managerForms: {
        editBatch: 'Gruppe bearbeiten',
        createBatch: 'Gruppe erstellen',
        batchSubtitle: 'Gruppendetails, Zeitplan und Abrechnungseinstellungen konfigurieren.',
        editLevel: 'Stufe bearbeiten',
        addLevel: 'Stufe hinzufügen',
        levelSubtitle: 'Stufenname und Anzeigereihenfolge für Ihre Filialgruppen festlegen.',
        saveFailed: 'Speichern fehlgeschlagen'
      },
      walletStaff: {
        topupTitle: 'Guthaben aufladen',
        referenceOptional: 'Referenz (optional)',
        clear: 'Leeren',
        topup: 'Aufladen',
        saving: 'Speichern…',
        missingStudent: 'Schüler fehlt',
        invalidAmount: 'Gültigen Betrag eingeben',
        credited: 'Guthaben gutgeschrieben: {{amount}}.',
        topupFailed: 'Aufladung fehlgeschlagen.',
        refundTitle: 'Guthaben-Rückerstattung verarbeiten',
        refundImportant: 'Wichtig:',
        refundWarning: 'Der Rückerstattungsbetrag wird vom Schülerguthaben abgezogen. Maximal erstattbar:',
        missingStudentInfo: 'Schülerinformationen fehlen',
        invalidAmountPositive: 'Gültigen Betrag größer als 0 eingeben',
        cannotRefundMore: 'Es kann nicht mehr erstattet werden als das aktuelle Guthaben: {{amount}}',
        reasonRequired: 'Bitte einen Grund für die Rückerstattung angeben',
        refundSuccess: 'Rückerstattung von {{amount}} erfolgreich verarbeitet.',
        refundFailed: 'Rückerstattung fehlgeschlagen',
        refundAmount: 'Rückerstattungsbetrag',
        paymentMode: 'Zahlungsart',
        maxPlaceholder: 'Max.: {{amount}}',
        referenceTxn: 'Referenz/Transaktionsnummer',
        referencePlaceholder: 'Transaktions-ID, Scheck-Nr. usw.',
        gatewayRefund: 'Auch über Zahlungsgateway erstatten (Razorpay/Stripe/PayPal). Deaktivieren für reine Guthabenanpassung, wenn das Gateway bereits manuell erstattet wurde.',
        reasonForRefund: 'Grund für Rückerstattung',
        reasonPlaceholder: 'z. B. Schüler scheidet aus, Überzahlung usw.',
        processing: 'Verarbeitung…',
        processRefund: 'Rückerstattung verarbeiten',
        balanceLabel: 'Guthaben',
        refresh: 'Aktualisieren',
        refund: 'Erstatten',
        transactions: 'Guthabentransaktionen',
        noTransactions: 'Noch keine Transaktionen.',
        showAllTransactions: 'Alle Guthabentransaktionen anzeigen',
        moreCount: ' ({{count}} weitere)',
        loadFailedPortal: 'Ihr Guthaben konnte nicht geladen werden. Bitte Seite aktualisieren.',
        loadFailedStaff: 'Guthaben dieses Schülers konnte nicht geladen werden. Möglicherweise außerhalb Ihres Filialbereichs.',
        grossCharge: 'Bruttobelastung:',
        discountApplied: 'Rabatt angewendet:',
        reversalCredit: 'Stornogutschrift:',
        netCharge: 'Nettobelastung:',
        reversed: '(Storniert)',
        refunded: 'ERSTATTET',
        sessionReversed: '(Stunde storniert)',
        modifiedOn: 'Geändert am {{date}}',
        original: 'Original:',
        modify: 'Ändern',
        revert: 'Zurücksetzen',
        balanceShort: 'Saldo:',
        sessionBatch: 'Stunde #{{sessionId}} (Gruppe #{{batchId}})',
        sessionOnly: 'Stunde #{{sessionId}}',
        modifyTxn: 'Transaktion ändern',
        currentAmount: 'Aktueller Betrag',
        newAmount: 'Neuer Betrag *',
        newAmountPlaceholder: 'Neuen Betrag eingeben',
        reasonOptional: 'Grund (optional)',
        modificationReasonPlaceholder: 'Änderungsgrund eingeben',
        saveChanges: 'Änderungen speichern',
        revertTxn: 'Transaktion zurücksetzen',
        revertWarningTitle: 'Warnung: Diese Aktion kann nicht rückgängig gemacht werden!',
        revertWarningBody: 'Die Transaktion wird dauerhaft gelöscht und das Guthaben um {{amount}} reduziert.',
        txnDetails: 'Transaktionsdetails',
        amountLabel: 'Betrag:',
        typeLabel: 'Typ:',
        reasonLabel: 'Grund:',
        dateLabel: 'Datum:',
        revertReasonOptional: 'Grund für Zurücksetzung (optional)',
        revertReasonPlaceholder: 'Grund für das Zurücksetzen dieser Transaktion eingeben',
        confirmRevert: 'Zurücksetzen bestätigen',
        validAmountRequired: 'Bitte einen gültigen positiven Betrag eingeben',
        modifyFailed: 'Transaktion konnte nicht geändert werden',
        revertFailed: 'Transaktion konnte nicht zurückgesetzt werden'
      },
      guardiansSection: {
        title: 'Erziehungsberechtigte',
        intro: 'Laden Sie bis zu {{max}} Eltern oder Erziehungsberechtigte per E-Mail ein. Sie wählen ihre Beziehung bei der Annahme.',
        canApprove: ' Sie können ausstehende Anfragen genehmigen oder ablehnen.',
        contactAcademy: ' Zum Entfernen einer genehmigten Verknüpfung wenden Sie sich an Ihre Akademie.',
        loading: 'Erziehungsberechtigte werden geladen…',
        loadFailed: 'Erziehungsberechtigte konnten nicht geladen werden.',
        enterEmail: 'E-Mail-Adresse des Erziehungsberechtigten eingeben.',
        cancelInviteConfirm: 'Diese Einladung abbrechen?',
        cancelInviteFailed: 'Einladung konnte nicht abgebrochen werden.',
        approveFailed: 'Erziehungsberechtigter konnte nicht genehmigt werden.',
        revokeConfirm: 'Diese Verknüpfung entfernen? Nach dem Entzug verlieren sie den Zugang.',
        revokeFailed: 'Erziehungsberechtigter konnte nicht entzogen werden.',
        rejectConfirm: 'Diese Anfrage ablehnen?',
        rejectFailed: 'Erziehungsberechtigter konnte nicht abgelehnt werden.',
        emptyTitle: 'Noch keine Erziehungsberechtigten verknüpft',
        emptyBody: 'Laden Sie einen Elternteil oder Erziehungsberechtigten per E-Mail ein. Diese registrieren oder bestätigen ihr Konto, dann muss Ihre Akademie den Zugang genehmigen.',
        linkedTitle: 'Verknüpfte Erziehungsberechtigte',
        pendingTitle: 'Ausstehende Einladungen',
        approve: 'Genehmigen',
        reject: 'Ablehnen',
        remove: 'Entfernen',
        emailPlaceholder: 'E-Mail des Erziehungsberechtigten',
        expires: ' · läuft ab {{date}}',
        expired: ' · abgelaufen',
        limitReached: 'Limit für Erziehungsberechtigte erreicht ({{used}}/{{max}}).',
        limitRevokeHint: ' Brechen Sie eine ausstehende Einladung ab oder entfernen Sie eine Verknüpfung, bevor Sie eine weitere einladen.',
        limitCancelHint: ' Brechen Sie eine ausstehende Einladung ab, bevor Sie eine weitere einladen.',
        status: {
          PENDING_APPROVAL: 'Genehmigung ausstehend',
          APPROVED: 'Genehmigt',
          REJECTED: 'Abgelehnt',
          REVOKED: 'Entzogen'
        },
        inviteType: {
          NEW_REGISTRATION: 'Neues Konto',
          EXISTING_USER_LINK: 'Bestehendes Konto verknüpfen'
        },
        inviteButton: 'Erziehungsberechtigten einladen',
        resendButton: 'Einladung erneut senden',
        sending: 'Wird gesendet…',
        inviteFailure: 'Einladung konnte nicht gesendet werden.',
        resendFailure: 'Einladung konnte nicht erneut gesendet werden.'
      },
      archiveStudent: {
        titleWithEnrollments: 'Schülerkonto archivieren',
        titleSimple: 'Schüler archivieren',
        hasEnrollments: '{{name}} hat {{count}} aktive Einschreibung.',
        hasEnrollments_other: '{{name}} hat {{count}} aktive Einschreibungen.',
        chooseProceed: 'Wählen Sie, wie fortgefahren werden soll.',
        archiveSimple: '{{name}} archivieren? Anmeldung ist nicht mehr möglich. Der Verlauf bleibt erhalten.',
        thisStudent: 'Dieser Schüler',
        thisStudentLower: 'diesen Schüler',
        loadingEnrollments: 'Einschreibungsdetails werden geladen…',
        activeEnrollments: 'Aktive Einschreibungen',
        ongoing: 'laufend',
        whatHappens: 'Was soll passieren?',
        endEnrollmentsOnly: 'Nur Einschreibungen beenden',
        endEnrollmentsOnlyDesc: 'Gruppenmitgliedschaft am unten stehenden Datum beenden. Konto bleibt aktiv — Schüler kann sich weiter anmelden.',
        endAndArchive: 'Einschreibungen beenden & Konto archivieren',
        recommended: '(empfohlen)',
        endAndArchiveDesc: 'Typisch beim Austritt. Beendet Einschreibungen und entzieht Portalzugang.',
        archiveOnly: 'Nur Konto archivieren',
        archiveOnlyDesc: 'Anmeldung entziehen, Einschreibungen bleiben aktiv. Anwesenheit und Abrechnung laufen bis zum Ende jeder Einschreibung.',
        endDateLabel: 'Enddatum für alle aufgeführten Einschreibungen',
        archiveOnlyConfirm: 'Ich verstehe, dass Einschreibungen aktiv bleiben, Anwesenheit weiter erfasst werden kann und der Schüler sich bis zur Reaktivierung nicht anmelden kann.',
        processing: 'Verarbeitung…',
        continue: 'Weiter',
        archive: 'Archivieren'
      },
      privacy: {
        metaLastUpdated: 'Zuletzt aktualisiert: Juli 2026 · Gilt für: {{name}}',
        metaGdprFraming: ' · DSGVO / UK-DSGVO-Rahmen',
        metaDpdpFraming: ' · DPDP Act 2023-Rahmen',
        whoWeAre: {
          title: '1. Wer wir sind',
          p1: 'Diese Anwendung (&quot;<strong>{{name}}</strong>&quot;) wird von Ihrer Schachakademie betrieben (die &quot;Akademie&quot;, &quot;wir&quot;, &quot;uns&quot;). Die Akademie ist <strong>Verantwortlicher</strong> für personenbezogene Daten, die über diese Plattform verarbeitet werden. Axeron Technologies (Softwareanbieter) handelt in der Regel nur als <strong>Auftragsverarbeiter</strong>, wenn lizenzierte Software, Remote-Support oder Lizenzvalidierung bereitgestellt werden — siehe den Auftragsverarbeitungsvertrag mit Ihrer Akademie.',
          p2: 'Bei Datenschutzfragen wenden Sie sich zuerst an den Akademie-Administrator.'
        },
        whatWeCollect: {
          title: '2. Welche personenbezogenen Daten wir erheben',
          intro: 'Wir erheben und verarbeiten folgende Kategorien personenbezogener Daten:',
          identity: '<strong>Identitätsdaten</strong> — Vorname, Nachname, Geburtsdatum (Schüler); optional FIDE-ID / Elo, falls aktiviert',
          contact: '<strong>Kontaktdaten</strong> — E-Mail-Adresse, Telefonnummer, Wohnadresse',
          account: '<strong>Kontodaten</strong> — Benutzername, gehashtes Passwort, zugewiesene Rolle',
          attendance: '<strong>Anwesenheits- &amp; Einschreibungsdaten</strong> — Stunden, Gruppen, Einschreibungen',
          financial: '<strong>Finanzdaten</strong> — Guthaben, Aufladungen, Gebührenreferenzen',
          guardian: '<strong>Erziehungsberechtigten-/Elternverknüpfungen</strong> — sofern für Minderjährige konfiguriert',
          technical: '<strong>Technische Daten</strong> — Anmeldezeitstempel, IP in Serverprotokollen, JWT in localStorage',
          noBiometric: 'Wir erheben <strong>keine</strong> biometrischen Daten oder vollständigen Zahlungskartennummern.',
          payments: 'Online-Zahlungen werden über das von der Akademie gewählte Gateway abgewickelt (<strong>Razorpay</strong>, <strong>Stripe</strong>, <strong>PayPal</strong> und/oder <strong>Mollie</strong>).'
        },
        legalBasis: {
          title: '3. Warum wir Ihre Daten verarbeiten (Rechtsgrundlage)',
          gdpr: {
            contract: '<strong>Art. 6 Abs. 1 lit. b Vertrag</strong> — Einschreibungen, Anwesenheit, Guthabenabrechnung, Trainingsleistungen.',
            legitimate: '<strong>Art. 6 Abs. 1 lit. f berechtigte Interessen</strong> — Sicherheit, Betrugsprävention, Audit-Protokolle.',
            obligation: '<strong>Art. 6 Abs. 1 lit. c rechtliche Verpflichtung</strong> — wenn die Akademie Finanzunterlagen nach lokalem Steuerrecht aufbewahren muss.',
            consent: '<strong>Art. 6 Abs. 1 lit. a / Art. 8 Einwilligung</strong> — wo für Kinder erforderlich (elterliche Einwilligung über Erziehungsberechtigten-Funktionen).'
          },
          dpdp: {
            contract: '<strong>Vertragserfüllung</strong> — Einschreibungen, Anwesenheit, Guthabenaufladungen und Gebührenabzüge.',
            legitimate: '<strong>Berechtigte Interessen</strong> — Audit-Protokolle, Betrugserkennung, sicherer Betrieb.',
            obligation: '<strong>Rechtliche Verpflichtung</strong> — Aufbewahrung von Finanzunterlagen gemäß indischem Companies Act / GST (in der Regel 8 Jahre).'
          }
        },
        retention: {
          title: '4. Wie lange wir Ihre Daten aufbewahren',
          activeAccount: '<strong>Aktive Kontodaten</strong> — solange Ihr Konto bei der Akademie besteht.',
          attendance: '<strong>Anwesenheit &amp; Einschreibung</strong> — in der Regel 3 Jahre nach der letzten Stunde (Akademierichtlinie kann abweichen).',
          financialGdpr: '<strong>Finanzunterlagen</strong> — wie nach lokalem Steuerrecht erforderlich (in EU-Mitgliedstaaten oft 6–10 Jahre).',
          financialDpdp: '<strong>Finanzunterlagen</strong> — wie nach lokalem Steuerrecht erforderlich (in der Regel 8 Jahre nach indischen GST-/Einkommensteuerregeln).',
          auditLogs: '<strong>Audit-Protokolle</strong> — etwa 12 Monate aufbewahrt, dann gelöscht.'
        },
        sharing: {
          title: '5. Mit wem wir Ihre Daten teilen',
          intro: 'Wir verkaufen keine personenbezogenen Daten. Die Weitergabe ist beschränkt auf:',
          gateways: '<strong>Zahlungsgateways</strong> — Razorpay, Stripe, PayPal und/oder Mollie (akademiekonfiguriert).',
          messaging: '<strong>Nachrichten</strong> — E-Mail-SMTP-Anbieter; WhatsApp über WATI/Meta, wenn von der Akademie aktiviert.',
          hosting: '<strong>Hosting</strong> — der Server, auf dem diese selbst gehostete Instanz läuft (von der Akademie gewählt; EU-Akademien sollten in der EU hosten).',
          licence: '<strong>Lizenzvalidierung</strong> — Installations-Fingerprint / Lizenzprüfungen können Axerons Lizenz-Relay kontaktieren (siehe Anbieter-AVV).',
          staff: '<strong>Akademiepersonal</strong> — nur rollenbasiert eingeschränkter Zugriff.'
        },
        cookies: {
          title: '6. Cookies und lokaler Speicher',
          intro: 'Nur essenzielle Speicherung:',
          jwt: '<strong>JWT</strong> (localStorage) — Sitzung.',
          consentFlag: '<strong>Cookie-Einwilligungsflag</strong> (localStorage).',
          gatewaySession: '<strong>Ausstehende Gateway-Sitzungs-ID</strong> (sessionStorage) während Zahlungsweiterleitungen.',
          noAds: 'Keine Werbe- oder Drittanbieter-Analyse-Cookies.'
        },
        rights: {
          gdprTitle: '7. Ihre Rechte nach DSGVO / UK-DSGVO',
          gdprP1: 'Je nach Standort können Ihnen Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch sowie Rechte im Zusammenhang mit automatisierter Entscheidungsfindung zustehen, soweit anwendbar.',
          gdprP2: 'Wenden Sie sich an den Akademie-Administrator, um Rechte auszuüben. Akademien sollten ohne unangemessene Verzögerung und innerhalb eines Monats antworten (Verlängerung gesetzlich zulässig).',
          gdprP3: 'Sie können sich bei Ihrer lokalen Aufsichtsbehörde beschweren (z. B. CNIL, BfDI, AP, ICO im Vereinigten Königreich).',
          dpdpTitle: '7. Ihre Rechte nach dem DPDP Act, 2023',
          dpdpP1: 'Als betroffene Person nach dem Digital Personal Data Protection Act, 2023 (Indien) können Sie Auskunft, Berichtigung, Löschung (vorbehaltlich gesetzlicher Aufbewahrung) und verwandte Rechte über den Akademie-Administrator anfordern (Antwortziel: 30 Tage).',
          dpdpP2: 'Beschwerden können an das Data Protection Board of India gerichtet werden, sobald es vollständig operativ ist, oder an den Akademie-Administrator.'
        },
        security: {
          title: '8. Datensicherheit',
          https: 'HTTPS/TLS bei der Übertragung (wenn mit TLS bereitgestellt).',
          bcrypt: 'Passwörter mit bcrypt gehasht.',
          roles: 'Rollenbeschränkter Zugriff.',
          encryption: 'Gateway-Geheimnisse ruhend verschlüsselt, wenn <code>PAYMENT_SECRETS_ENCRYPTION_KEY</code> gesetzt ist.'
        },
        changes: {
          title: '9. Änderungen',
          body: 'Wir können diese Seite aktualisieren; das Datum &quot;Zuletzt aktualisiert&quot; ändert sich. Fortgesetzte Nutzung nach einer Aktualisierung bedeutet, dass Sie die überarbeitete Richtlinie akzeptieren.'
        },
        footer: {
          line1: '{{name}} · Datenschutzerklärung · Spiegelt die Praktiken der Akademie wider, die diese Instanz betreibt.',
          line2: 'Anbieter-Rechtspaket: siehe <code>legal/PRIVACY-POLICY.txt</code> und <codeEu>legal/PRIVACY-POLICY-EU.txt</codeEu> (anwaltliche Prüfung vor Kundenunterzeichnung erforderlich).'
        }
      },
      terms: {
        agreement: {
          title: '1. Vereinbarung',
          body: 'Diese Nutzungsbedingungen regeln die Nutzung von <strong>{{name}}</strong>, Akademie-Betriebssoftware, lizenziert von Axeron Technologies und betrieben von Ihrer Akademie (die &quot;Akademie&quot;). Mit der Anmeldung stimmen Sie diesen Bedingungen und der <privacyLink>Datenschutzerklärung</privacyLink> zu.'
        },
        licence: {
          title: '2. Lizenz und zulässige Nutzung',
          body: 'Die Akademie gewährt autorisierten Nutzern (Admins, Managern, Trainern, Schülern und Erziehungsberechtigten) Zugang zur Plattform nur für Akademieverwaltung. Sie dürfen Lizenzlimits nicht umgehen, Zugangsdaten unsicher teilen oder Zahlungsgateways missbrauchen.'
        },
        fees: {
          title: '3. Gebühren und Zahlungen',
          body1: 'Schülerguthaben und Mitgliedsgebühren werden von der Akademie berechnet, nicht von Axeron Technologies. Online-Gateways (Razorpay, Stripe, PayPal, Mollie) werden von der Akademie unter eigenen Händlerkonten konfiguriert.',
          euLicence: 'Von Axeron Technologies an europäische Akademien berechnete Lizenzgebühren werden in der in der Bestellung angegebenen Währung ausgewiesen, zzgl. geltender MwSt., sofern auf der Rechnung nicht anders angegeben.',
          nonEuLicence: 'Von Axeron Technologies berechnete Softwarelizenzgebühren werden in der auf dem Bestellformular vereinbarten Währung angegeben, zzgl. Steuern, sofern nicht anders angegeben.'
        },
        support: {
          title: '4. Support',
          body: 'Support wird vom Akademie-Administrator für den täglichen Gebrauch und von Axeron Technologies für Lizenzierung und Produktfehler gemäß EULA/Supportvereinbarung der Akademien bereitgestellt.',
          euHours: ' Support-Antworten für europäische Kunden werden nach Möglichkeit innerhalb der EU-Geschäftszeiten (MEZ/MESZ) angestrebt.',
          indiaHours: ' Supportzeiten für indische Kunden sind in der Regel Mo–Sa 10:00–18:00 IST, sofern nicht anders vereinbart.'
        },
        liability: {
          title: '5. Haftung',
          body: 'Die Software wird wie in der Endbenutzer-Lizenzvereinbarung zwischen Axeron Technologies und der Akademie beschrieben bereitgestellt. Die Akademie ist verantwortlich für Konfiguration, Benutzerzugang und Einhaltung lokaler Gesetze (einschließlich Datenschutz).'
        },
        governingLaw: {
          title: '6. Anwendbares Recht',
          euBody: 'Für europäische Akademiekunden können kommerzielle Bedingungen EU/EWR-Verbraucher- und Handelsregeln unterliegen, wie in der unterzeichneten Bestellung / EULA-Nachtrag festgelegt. Streitbeilegung wie in dieser Vereinbarung angegeben. Anbieter-Rechtsdokumente unter <code>legal/</code> vor Unterzeichnung anwaltlich prüfen lassen.',
          indiaBody: 'Sofern eine unterzeichnete Vereinbarung nichts anderes vorsieht, unterliegen Anbieter-Lizenzvereinbarungen für indische Kunden dem Recht Indiens (Gujarat), wie in der von Axeron Technologies gepflegten EULA und den Nutzungsbedingungen angegeben.'
        },
        footer: '{{name}} · Nutzungsbedingungen · Siehe auch die Anbieter-EULA im Software-Lizenzpaket.'
      },
      legal: {
        backToLogin: '← Zurück zur Anmeldung',
        termsTitle: 'Nutzungsbedingungen',
        privacyTitle: 'Datenschutzerklärung',
        lastUpdated: 'Zuletzt aktualisiert: Juli 2026 · Gilt für: {{name}}'
      },
      notifications: {
        categories: {
          general: 'Allgemein',
          billing: 'Abrechnung',
          attendance: 'Anwesenheit',
          privacy: 'Datenschutz',
          chess: 'Schach',
          chessChallenge: 'Herausforderung',
          chessGame: 'Partie',
          chessFairPlay: 'Fair Play',
          chessCorrespondence: 'Fernschach',
        },
        content: {
          new: 'Neu',
          open: 'Öffnen',
          fallbackTitle: 'Benachrichtigung',
          lowWallet: {
            title: 'Niedriger Wallet-Stand',
            body: '{{studentName}} hat {{balance}} (Schwelle {{threshold}}). Aufladen oder Zahlung einziehen.',
            action: 'Dieses Wallet aufladen'
          },
          lowWalletDigest: {
            title: '{{count}} Schüler mit niedrigem Wallet',
            body: '{{count}} Schüler liegen unter der Wallet-Schwelle. Öffnen Sie Offene Zahlungen zum Prüfen und Einziehen.',
            action: 'Offene Zahlungen öffnen'
          },
          absence: {
            title: 'Fehlzeit erfasst',
            body: '{{studentName}} wurde für die Sitzung am {{sessionInfo}} als abwesend markiert.',
            action: 'Anwesenheit öffnen'
          },
          dsar: {
            title: 'Datenauskunft (DSAR)',
            body: '{{name}} (Benutzer #{{userId}}, {{username}}) hat eine Kopie der personenbezogenen Daten beantragt. Export unter Berichte → Datenexport.',
            action: 'Daten exportieren'
          },
          chessGeneric: {
            CHESS_CHALLENGE: 'Schach-Herausforderung',
            CHESS_GAME: 'Schachpartie',
            CHESS_FAIR_PLAY: 'Fair Play',
            CHESS_CORR: 'Fernschach',
            CHESS: 'Schach',
            bodyFallback: 'Öffnen Sie den Spielbereich für Details.'
          }
        }
      }
    }
  },
  fr: {
    translation: {
      brand: {
        tagline: 'Gérer. Suivre. Grandir.'
      },
      nav: {
        branches: 'Sites',
        levels: 'Niveaux',
        batches: 'Groupes',
        students: 'Élèves',
        coaches: 'Entraîneurs',
        managers: 'Responsables',
        enrollments: 'Inscriptions',
        sessions: 'Séances',
        calendar: 'Calendrier',
        reports: 'Rapports',
        wallet: 'Mon portefeuille',
        faq: 'FAQ',
        dashboard: 'Tableau de bord',
        help: 'Aide',
        finances: 'Finances',
        manage: 'Gestion',
        people: 'Personnes',
        academySetup: 'Configuration académie',
        operations: 'Opérations',
        system: 'Système',
        feesBilling: 'Frais & facturation',
        myAccount: 'Mon compte',
        settings: 'Paramètres',
        discounts: 'Remises',
        sessionsSummary: 'Résumé des séances',
        monthlyStatements: 'Relevés mensuels',
        reportSettings: 'Paramètres des rapports',
        atRiskStudents: 'Élèves à risque',
        walletTopup: 'Recharger le portefeuille',
        paymentEnforcement: 'Application des paiements',
        paymentMethods: 'Moyens de paiement',
        regionalSettings: 'Paramètres régionaux',
        productionReadiness: 'Prêt production UE',
        playingPlatform: 'Plateforme de jeu',
        playingHub: 'Hub de jeu',
        fairPlay: 'Jeu loyal',
        playNow: 'Contre l\'ordinateur',
        liveGames: 'Parties en direct',
        myGames: 'Mes parties',
        challenges: 'Défis',
        leaderboard: 'Classement',
        classroom: 'Salle de classe',
        classroomOverview: 'Aperçu',
        classroomLive: 'Cours en direct',
        classroomZoom: 'Connexion Zoom',
        classroomSettings: 'Paramètres de classe',
        emailSettings: 'Paramètres e-mail',
        whatsapp: 'WhatsApp',
        aiCoachSettings: 'Ask AI Coach',
        branding: 'Image de marque',
        license: 'Licence',
        holidays: 'Jours fériés',
        notifications: 'Notifications',
        auditLogs: 'Journaux d\'audit',
        mySessions: 'Mes séances',
        myEnrollments: 'Mes inscriptions',
        sessionStatement: 'Relevé de séances',
        parentPortal: 'Portail parents',
        home: 'Accueil',
        myChild: 'Mon enfant',
        overview: 'Aperçu',
        attendance: 'Présence',
        more: 'Plus'
      },
      classroom: {
        hub: {
          title: 'Salle de classe',
          subtitleStaff: 'Animez des cours en ligne avec Zoom — commencez ici, puis hébergez ou rejoignez depuis En direct.',
          subtitleLearner: 'Rejoignez ici les cours en ligne de votre académie.',
          zoomFirstBanner: 'La salle de classe est centrée sur Zoom. Les réunions vidéo utilisent le compte Zoom de l’académie ; ChessDesk crée et ouvre la réunion pour coachs et élèves.',
          liveTitle: 'Cours en direct',
          liveDescStaff: 'Voir les séances en ligne du jour et Démarrer / Héberger / Rejoindre une fois Zoom connecté.',
          liveDescLearner: 'Voir les cours à venir et rejoindre lorsque votre coach démarre la réunion.',
          zoomTitle: 'Connexion Zoom',
          zoomDesc: 'Connectez Zoom pour la vidéo de classe dans ChessDesk.',
          zoomDescConnected: 'Zoom est connecté. Gérez les identifiants ou déconnectez ici.',
          settingsTitle: 'Paramètres de classe',
          settingsDesc: 'Valeurs par défaut pour salle d’attente, sourdine, caméra et notifications de démarrage.',
          open: 'Ouvrir',
          zoomConnectedBanner: 'Zoom est connecté pour cette académie. Ouvrez Cours en direct pour démarrer ou rejoindre.',
          zoomNotConnectedBanner: 'Zoom n’est pas encore connecté. Connectez le compte Zoom de l’académie pour activer Démarrer / Héberger / Rejoindre.',
          externalModeBanner: 'La classe utilise des liens de réunion externes. Collez l’URL sur la séance, puis utilisez Démarrer / Rejoindre depuis En direct ou Séances.',
          statusLoadFailed: 'Impossible de charger l’état de la salle de classe.',
          zoomStatusUnknown: 'Impossible de vérifier la connexion Zoom. Actualisez ou ouvrez Cours en direct.',
        },
        joinAction: {
          stillWaiting: "Toujours en attente — votre coach n’a pas encore démarré.",
          notStartedYet: 'Pas encore démarré',
          notStartedHint: 'Votre coach n’a pas encore démarré ce cours en ligne. Touchez pour revérifier.',
          checking: 'Vérification…',
          checkFailed: 'Impossible de vérifier le statut. Réessayez ou ouvrez Cours en direct.',
        },
        live: {
          title: 'Cours en direct',
          subtitleStaff: 'Démarrez ou hébergez Zoom pour les séances en ligne qui vous sont assignées.',
          subtitleAdmin: 'Démarrez ou hébergez Zoom pour les séances en ligne de toute l’académie.',
          subtitleLearner: 'Rejoignez lorsque votre coach ouvre la salle de classe.',
          zoomPendingHint: 'Zoom n’est pas encore connecté. Vous pouvez ouvrir les séances ; Démarrer expliquera ce qui manque jusqu’à la connexion par un admin.',
          zoomPendingHintLearner: 'Zoom n’est pas encore connecté. Rejoindre apparaîtra ici lorsque votre coach démarre un cours en ligne.',
          connectZoomCta: 'Connecter Zoom',
          todayHeading: 'Aujourd’hui et à venir',
          todayHint: 'Les séances en ligne avec un lien Zoom s’afficheront ici.',
          todayHintLive: 'Séances en ligne éligibles dans les 48 prochaines heures. Démarrer crée la réunion dans ChessDesk ; Héberger reprend en tant qu’hôte ; Rejoindre en tant que participant.',
          todayHintLearner: 'Séances en ligne éligibles dans les 48 prochaines heures. Rejoindre ouvre le lien participant lorsque votre coach démarre le cours.',
          openSessions: 'Ouvrir les séances',
          refresh: 'Actualiser',
          emptyTitle: 'Pas encore de classe en direct',
          emptyStaff: 'Aucune séance en ligne éligible dans les 48 prochaines heures. Utilisez Ouvrir les séances pour planifier. Démarrer fonctionne aussi sous Séances une fois Zoom connecté.',
          emptyLearner: 'Lorsque votre coach démarre un cours en ligne, Rejoindre apparaîtra ici. Consultez aussi Séances pour l’emploi du temps.',
          emptyStaffConnected: 'Aucune séance en ligne dans cette fenêtre. Planifiez sous Séances, puis revenez ici pour Démarrer ou Héberger.',
          emptyLearnerConnected: 'Pas de cours en direct pour le moment. Quand votre coach en démarre un, Rejoindre apparaîtra ici.',
          chipStart: 'Démarrer',
          chipJoin: 'Rejoindre',
          chipHost: 'Héberger',
          statusLoadFailed: 'Impossible de charger le statut de la classe. Réessayez.',
          zoomConnectedHint: 'Zoom est connecté. Utilisez Démarrer / Héberger comme hôte, ou Rejoindre si un lien existe.',
          start: 'Démarrer',
          startZoom: 'Démarrer Zoom',
          host: 'Héberger',
          restart: 'Relancer',
          join: 'Rejoindre',
          joinSession: 'Rejoindre la séance',
          starting: 'Ouverture…',
          startFailed: 'Impossible de démarrer la classe Zoom.',
          openInZoomFailed: 'Aucun lien hôte Zoom disponible. Essayez Héberger, ou demandez à un admin de reconnecter Zoom.',
          meetingReady: 'Réunion prête',
          meetingNotCreated: 'Réunion pas encore créée',
          end: "Terminer le cours",
          ending: "Fin…",
          endFailed: "Impossible de terminer le cours.",
          meetingStatusCreating: 'Création…',
          meetingStatusStarted: 'Démarrée',
          meetingStatusFailed: 'Échec — réessayez Démarrer',
          meetingStatusEnded: 'Terminé',
          meetingStatusUnknown: 'Statut indisponible',
          unnamedSession: 'Séance',
          selectChildTitle: 'Sélectionner un enfant',
          selectChildHint: 'Choisissez un enfant dans l’en-tête du portail parent pour voir ses cours en direct.',
          noChildrenTitle: 'Aucun enfant lié',
          noChildrenHint: 'Aucun enfant approuvé n’est encore lié à ce compte parent.',
          childrenLoadFailed: 'Impossible de charger les enfants liés. Actualisez la page ou réessayez.',
          externalModeHint: 'Liens de réunion externes. Démarrer ouvre le lien de la séance ; Rejoindre utilise l’URL participant si disponible.',
        },
        meeting: {
          title: 'Réunion de classe',
          subtitle: 'Cours vidéo dans ChessDesk',
          preparing: 'Préparation de la classe Zoom…',
          preparingHint: 'Préparation de la réunion. Cela prend généralement quelques secondes.',
          joinFailed: 'Impossible de rejoindre la classe intégrée.',
          notStarted: 'L’hôte n’a pas encore démarré le cours. Attendez que votre coach ouvre la classe.',
          back: 'Retour',
          backToLive: 'Cours en direct',
          cancelled: "Cette séance a été annulée.",
          ended: "Ce cours est terminé.",
          startAsHost: 'Démarrer en tant qu’hôte',
          redirectingExternal: 'Ouverture de la réunion externe…',
          openExternalNow: 'Ouvrir la réunion maintenant',
          askAdminZoomSetup: 'Demandez à votre admin de configurer Zoom sous Classe → Zoom.',
          embedUnavailable: 'Identifiants d’intégration indisponibles.',
          hostCredentialsMissing: 'Identifiants hôte manquants. Vérifiez Classe → Paramètres → e-mail hôte Zoom.',
          hostOpenFailed: 'Impossible d’ouvrir la classe. Réessayez dans un instant.',
          hostTokenInvalid: 'Impossible d’ouvrir la classe. Réessayez dans un instant.',
          hostTokenInvalidStaff: 'Impossible d’ouvrir la classe. Réessayez dans un instant.',
          noExternalLink: 'Aucun lien externe n’est configuré pour cette séance.',
          invalidSession: 'Séance invalide.',
          leave: 'Quitter la réunion',
          retry: 'Réessayer',
          openInZoom: 'Ouvrir dans l’app Zoom',
          inMeetingHint: 'La vidéo s’exécute dans ChessDesk. Quittez quand vous avez terminé.',
          connectingHint: 'Connexion…',
          connectedHint: 'En direct dans ChessDesk',
          livePill: 'Live',
          zoomToolbarHint: 'Utilisez la barre dans la vidéo pour activer micro et caméra.',
          cameraOffTitle: 'Caméra désactivée',
          cameraOffHint: 'Touchez l’icône caméra en bas pour vous montrer à la classe.',
          waitingRoomAdmit: 'Admettre',
          waitingRoomAdmitAll: 'Tout admettre',
          waitingRoomView: 'Participants',
          waitingRoomDismiss: 'Fermer l’avis de salle d’attente',
          waitingRoomOne: '{{name}} est dans la salle d’attente.',
          waitingRoomMany: '{{count}} participants sont dans la salle d’attente : {{names}}.',
          waitingRoomManyMore: '{{count}} participants sont dans la salle d’attente ({{names}}, et plus).',
          waitingForHostPill: 'En attente de l’hôte',
          learnerWaitingHint: 'Vous attendez que l’hôte vous admette. Gardez cette page ouverte.',
          waitingForHostAutoRetry: 'Nous réessaierons automatiquement quand votre coach démarrera le cours.',
          leaveHostConfirm: 'Quitter sans terminer le cours ? La réunion Zoom reste active jusqu’à « Terminer le cours ».',
          endHostConfirm: 'Terminer le cours pour tout le monde ? Les élèves seront retirés de la réunion Zoom.',
          leaving: 'Sortie de la réunion…',
          joinTimedOut: 'La classe met trop de temps à s’ouvrir. Réessayez ou ouvrez dans l’app Zoom.',
          hostConflictWarning: 'Un autre hôte est peut-être déjà dans la réunion. Une seule personne doit gérer Admettre.',
          fixZoomSetup: 'Configurer le Meeting SDK Zoom →',
          sdkLoadFailed: 'Impossible de charger la classe vidéo. Réessayez ou ouvrez dans l’app Zoom.',
          signatureInvalid: 'Zoom a rejeté la signature. Sous Classe → Zoom, collez le Client ID et Secret Development pour la vidéo intégrée.',
        },
        settings: {
          title: 'Paramètres de classe',
          subtitle: 'Options de réunion par défaut lorsque ChessDesk crée une classe Zoom.',
          infoBanner: 'Ces préférences sont enregistrées en brouillon local jusqu’au stockage serveur des paramètres de classe.',
          infoBannerLive: 'Ces valeurs s’appliquent lorsque ChessDesk crée une réunion Zoom pour une séance.',
          defaultProvider: 'Fournisseur vidéo par défaut',
          defaultProviderNote: 'Zoom est le fournisseur principal. Lien externe = secours pour coller votre propre URL.',
          providerExternalLink: 'Lien de réunion externe',
          hostUserEmail: 'E-mail hôte Zoom',
          hostUserEmailNote: 'Facultatif. E-mail du compte Zoom pour créer les réunions. Vide = e-mail du coach de la séance.',
          hostEmailInvalid: 'Saisissez un e-mail hôte Zoom valide (ou laissez vide pour l’e-mail du coach).',
          waitingRoom: 'Activer la salle d’attente',
          waitingRoomNote: 'Les participants attendent que l’hôte les admette.',
          muteOnEntry: 'Couper le micro à l’entrée',
          allowStudentCameraDefault: 'Autoriser la caméra élève par défaut',
          allowStudentCameraNote: 'Les hôtes peuvent toujours changer cela dans Zoom par réunion.',
          notifyOnStart: 'Notifier les élèves au démarrage du cours',
          notifyOnStartNote: 'Lorsque c’est activé, les élèves inscrits et parents liés reçoivent une notification in-app avec un lien Rejoindre quand un coach démarre la classe.',
          saveDraft: 'Enregistrer le brouillon',
          save: 'Enregistrer les paramètres',
          saving: 'Enregistrement…',
          saveSuccess: 'Paramètres de classe enregistrés.',
          saveFailed: 'Impossible d’enregistrer les paramètres de classe.',
          loadFailed: 'Impossible de charger les paramètres de classe.',
          retryLoad: 'Réessayer',
          draftSaved: 'Brouillon enregistré dans ce navigateur.',
          draftSaveFailed: 'Impossible d’enregistrer le brouillon dans ce navigateur.',
          gotoZoom: 'Connexion Zoom'
        },
        zoom: {
          title: 'Connexion Zoom',
          subtitle: 'Liez le compte Zoom de l’académie pour que ChessDesk puisse créer et ouvrir des réunions.',
          infoBanner: 'Créez une app OAuth Server-to-Server dans le Zoom Marketplace. Collez Account ID et identifiants client, puis Connectez. Scopes requis : meeting:write:meeting:admin, meeting:read:meeting:admin, user:read:user:admin et user:read:token:admin (vidéo hôte intégrée). Créez aussi une app General avec Meeting SDK Embed pour le Client ID + Secret Development. Ajoutez http://localhost:3000 à la liste d’autorisation en local.',
          statusConnected: 'Connecté',
          statusConnectedHint: 'ChessDesk peut créer des réunions et héberger la vidéo intégrée avec ce compte Zoom.',
          statusNotConnected: 'Non connecté',
          statusNotConnectedHint: 'Enregistrer un brouillon ne connecte pas Zoom. L’OAuth / API serveur reste à brancher.',
          statusNotConnectedHintLive: 'Saisissez les identifiants puis cliquez sur Se connecter avec Zoom. ChessDesk les vérifie auprès de Zoom et les stocke côté serveur.',
          serverToServerHeading: 'OAuth Server-to-Server',
          serverToServerHint: 'Recommandé pour les comptes Zoom de l’académie. Le secret client ne doit pas rester dans le navigateur en production — cette UI est uniquement pour la configuration.',
          serverToServerHintLive: 'Recommandé pour les comptes Zoom de l’académie. Le secret client est chiffré sur le serveur et n’est jamais renvoyé au navigateur.',
          accountId: 'ID du compte',
          accountIdNote: 'Depuis Zoom Marketplace → votre app → App Credentials.',
          clientId: 'ID client',
          clientSecret: 'Secret client',
          clientSecretNote: 'Non stocké dans le brouillon local. Saisissez-le seulement pour la connexion API.',
          clientSecretConfiguredNote: 'Un secret client est déjà enregistré. Laissez vide pour le conserver, ou saisissez-en un nouveau.',
          clientSecretConnectNote: 'Requis pour se connecter. Stocké chiffré sur le serveur — jamais réaffiché après enregistrement.',
          saveDraft: 'Enregistrer le brouillon',
          saveCredentials: 'Enregistrer les identifiants',
          clearDraft: 'Effacer le brouillon',
          connectOAuth: 'Se connecter avec Zoom',
          connectDisabledTitle: 'Connexion backend pas encore disponible',
          connectDisabledHint: '« Se connecter avec Zoom » reste désactivé tant que le serveur ne peut pas échanger les identifiants et stocker les jetons en sécurité.',
          connectHintLive: 'La connexion échange les identifiants avec Zoom et marque l’académie comme connectée uniquement en cas de succès.',
          connectNeedsFields: 'Saisissez Account ID, Client ID et secret client, puis cliquez sur Se connecter avec Zoom.',
          draftSaved: 'Brouillon enregistré dans ce navigateur. Zoom n’est toujours pas connecté.',
          draftSavedServer: 'Identifiants enregistrés sur le serveur. Zoom n’est connecté qu’après « Se connecter avec Zoom ».',
          draftSavedConnected: 'Identifiants mis à jour. Le statut de connexion reste inchangé jusqu’à une reconnexion.',
          draftSaveFailed: 'Impossible d’enregistrer les paramètres Zoom.',
          draftCleared: 'Brouillon effacé.',
          loadFailed: 'Impossible de charger les paramètres Zoom.',
          validationRequired: 'Account ID et Client ID sont obligatoires.',
          secretRequired: 'Le secret client est requis pour se connecter (ou enregistrez-le d’abord).',
          connectSuccess: 'Connexion à Zoom réussie.',
          connectFailed: 'Impossible de se connecter à Zoom. Vérifiez les identifiants et l’activation de l’app.',
          disconnect: 'Déconnecter',
          disconnected: 'Zoom déconnecté. Les identifiants sont conservés pour une reconnexion.',
          disconnectFailed: 'Impossible de déconnecter Zoom.',
          webhookHeading: 'Abonnements d’événements (webhooks)',
          meetingSdkHeading: 'Vidéo intégrée (Meeting SDK)',
          meetingSdkHint: 'Optionnel pour la vidéo in-app. Tant que l’app Zoom n’est pas publiée, collez Client ID et Secret sous App Credentials → Development.',
          meetingSdkDevRequired: 'La jointure intégrée nécessite des identifiants Meeting SDK Development ici.',
          meetingSdkClientId: 'ID client Meeting SDK (développement)',
          meetingSdkClientIdNote: 'Depuis la même app Zoom → App Credentials → onglet Development.',
          meetingSdkClientSecret: 'Secret client Meeting SDK (développement)',
          meetingSdkClientSecretNote: 'Secret Development — uniquement pour signer les jetons intégrés.',
          meetingSdkClientSecretConfiguredNote: 'Un secret Meeting SDK est enregistré. Laissez vide pour le conserver.',
          webhookHint: 'Dans Zoom Marketplace → Feature → Event Subscriptions, ajoutez l’URL et abonnez-vous à meeting.started, meeting.ended et meeting.deleted.',
          webhookSecret: 'Jeton secret webhook',
          webhookSecretNote: 'Depuis Zoom Event Subscriptions. Requis pour vérifier les webhooks de cycle de vie.',
          webhookSecretConfiguredNote: 'Un secret webhook est enregistré. Laissez vide pour le conserver.',
          clearCredentials: 'Effacer les identifiants',
          clearConfirm: 'Supprimer l’Account ID, le Client ID et le secret client Zoom stockés pour cette académie ?',
          cleared: 'Identifiants Zoom effacés.',
          clearFailed: 'Impossible d’effacer les identifiants Zoom.',
          clearing: 'Effacement…',
          saving: 'Enregistrement…',
          connecting: 'Connexion…',
          disconnecting: 'Déconnexion…',
          gotoSettings: 'Paramètres de classe →'
        }
      },
      playing: {
        hubTitle: 'Plateforme de jeu',
        hubSubtitle: 'Jouez aux échecs, défiez des membres du club et grimpez au classement de l’académie.',
        playTitle: 'Échiquier',
        playSubtitle: 'À vous de jouer',
        loadFailed: 'Impossible de charger les données de la plateforme de jeu.',
        moveFailed: 'Coup illégal ou refusé.',
        resignFailed: 'Impossible d’abandonner.',
        yourRating: 'Votre Elo',
        record: 'V {{w}} · N {{d}} · D {{l}}',
        leaderboard: 'Classement',
        leaderboardSubtitle: 'Meilleurs joueurs de cette académie selon le classement interne.',
        viewLeaderboard: 'Voir le classement',
        playComputer: 'Jouer contre l’ordinateur',
        playComputerDesc: 'Entraînez-vous contre un moteur intégré. Les niveaux élevés jouent des tactiques plus fortes.',
        computerLevel: 'Difficulté',
        ratedGame: 'Partie classée',
        casualGame: 'Partie amicale',
        playAsWhite: 'Jouer les Blancs',
        playAsBlack: 'Jouer les Noirs',
        challenges: 'Défis entrants',
        challengePlayer: 'Défier un joueur',
        challengeSubtitle: 'Envoyez une invitation de partie à un autre membre de l’académie.',
        noChallenges: 'Aucun défi en attente.',
        accept: 'Accepter',
        decline: 'Refuser',
        myGames: 'Mes parties',
        myGamesSubtitle: 'Vos parties récentes sur cette plateforme.',
        noGames: 'Pas encore de parties — commencez-en une depuis le Hub de jeu.',
        players: 'Joueurs',
        player: 'Joueur',
        correspondence: 'Mode de temps',
        liveClock: 'Pendule en direct',
        correspondenceMode: 'Correspondance ({{n}} jours par coup)',
        daysPerMove: '{{n}} jour(s) par coup',
        correspondenceHint: 'Chaque joueur a {{n}} jours pour jouer. Pas d’horloge qui défile.',
        moveDeadline: 'Jouer avant {{when}}',
        type: 'Type',
        statusLabel: 'Statut',
        open: 'Ouvrir',
        gameInfo: 'Partie',
        check: 'Échec !',
        resign: 'Abandonner',
        backHub: 'Retour au Hub de jeu',
        noMovesYet: 'Pas encore de coups.',
        rating: 'Classement',
        games: 'Parties',
        noRatingsYet: 'Pas encore de parties classées.',
        create: 'Créer',
        join: 'Rejoindre',
        score: 'Score',
        searchPlayers: 'Rechercher des joueurs',
        challenge: 'Défier',
        challengeSent: 'Défi envoyé.',
        challengeFailed: 'Impossible d’envoyer le défi.',
        reason: 'Résultat',
        liveGames: 'Parties en direct',
        liveGamesSubtitle: 'Assistez aux parties en cours de l’académie en temps réel.',
        viewLive: 'Regarder maintenant',
        spectate: 'Assister',
        spectateTitle: 'Observation',
        spectatingHint: 'Vue en lecture seule — vous ne pouvez pas déplacer les pièces.',
        noLiveGames: 'Aucune partie en direct pour le moment.',
        noLiveGamesHint: 'Quand des membres du club commencent une partie, elle apparaîtra ici.',
        allSpeeds: 'Toutes',
        noLiveGamesForSpeed: 'Aucune partie en direct pour cette cadence',
        tryAnotherSpeedFilter: 'Essayez une autre cadence ou Toutes.',
        noOpenSeeksExpired: 'Les recherches ont expiré — créez-en une ou défiez quelqu’un.',
        noChallengesExpired: 'Défis entrants expirés',
        noChallengesExpiredHint: 'Envoyez un nouveau défi ou attendez la prochaine invitation.',
        challengeExpired: 'Défi expiré',
        openSharedTip: 'Ouvrir le conseil',
        boardSettingsLoadFailed: 'Impossible de charger les réglages du plateau.',
        prefsSaveFailed: 'Impossible de synchroniser les préférences avec le serveur.',
        prevPly: 'Coup préc.',
        nextPly: 'Coup suiv.',
        coachPlyOf: 'Coup {{current}} / {{total}}',
        fairPlayShowAllLevels: 'Afficher tous les niveaux',
        expiresIn: 'Expire dans {{when}}',
        incomingChallenges: 'Défis entrants',
        confirmTitle: 'Veuillez confirmer',
        sendChallenge: 'Envoyer un défi',
        inboxPartialLoadFailed: 'Certaines listes de défis n’ont pas pu être chargées. Actualisez.',
        bye: 'Exempt',
        format: 'Format',
        teamA: 'Nom équipe A',
        teamB: 'Nom équipe B',
        chooseTeam: 'Rejoindre une équipe',
        selectTeam: 'Choisir une équipe…',
        team: 'Équipe',
        fairPlay: 'Jeu loyal',
        fairPlaySubtitle: 'Examiner les parties humaines terminées pour des temps inhabituels et un jeu type moteur.',
        fairPlayReview: 'Revue fair-play',
        fairPlayMinLevel: 'Niveau minimum',
        fairPlayEmpty: 'Aucune partie signalée',
        fairPlayEmptyHint: 'Les parties terminées au-dessus du seuil apparaîtront ici.',
        fairPlayMedian: 'Médiane',
        fairPlayFastMoves: 'Coups rapides',
        fairPlayStreak: 'Série rapide',
        fairPlayCase: 'Dossier',
        fairPlayCases: 'Dossiers',
        fairPlayNotes: 'Notes du staff',
        fairPlayAddNote: 'Ajouter une note',
        fairPlayNotePlaceholder: 'Observation, preuves, suivi…',
        fairPlaySaveNote: 'Enregistrer la note',
        fairPlayOpenCase: 'Ouvrir / mettre à jour le dossier',
        fairPlayBanPlayer: 'Bannir de la plateforme de jeu',
        fairPlayBanReason: 'Motif du ban',
        fairPlayBanDays: 'Jours (vide = indéfini)',
        fairPlayLiftBan: 'Lever le ban',
        fairPlayActiveBans: 'Bans actifs',
        fairPlayCaseStatus: 'Statut du dossier',
        fairPlayResolution: 'Décision',
        fairPlaySubject: 'Concerné',
        fairPlayModeration: 'Modération',
        fairPlayNoteFailed: 'Impossible d’enregistrer la note.',
        fairPlayCaseFailed: 'Impossible de mettre à jour le dossier.',
        fairPlayBanFailed: 'Impossible d’appliquer le ban.',
        fairPlayReportFailed: 'Impossible de mettre à jour ce signalement.',
        fairPlayAppealFailed: 'Impossible de mettre à jour cet appel.',
        fairPlayCaseStatusLabel: {
          OPEN: 'Ouvert',
          WATCHING: 'Surveillance',
          ACTIONED: 'Sanctionné',
          CLOSED: 'Clos',
          DISMISSED: 'Classé sans suite'
        },
        fairPlayResolutionLabel: {
          NONE: 'Aucune',
          WARNING: 'Avertissement',
          BAN: 'Exclusion',
          DISMISSED: 'Classé sans suite'
        },

        moveTiming: 'Temps par coup',
        thinkTime: 'Réflexion',
        openBoard: 'Ouvrir l’échiquier',
        runEngineReview: 'Lancer l’analyse moteur',
        engineReviewRunning: 'Stockfish en cours…',
        engineReviewFailed: 'Échec de l’analyse moteur.',
        engineReviewAutoPending: 'L’anti-triche automatique est en file d’attente…',
        engineReviewAutoRunning: 'L’anti-triche automatique analyse chaque coup…',
        engineReviewAutoDone: 'Anti-triche automatique coup par coup terminée.',
        engineReviewAutoFailed: 'Anti-triche automatique échouée — vous pouvez relancer ci-dessous.',
        engineFlag: 'Indicateur',

        engineMatchTop1: 'Match top-1',
        engineMatchTop3: 'Match top-3',
        avgCpLoss: 'CPL moy.',
        engineLevel: 'Alerte moteur',
        engineMatch: 'Match',
        cpLoss: 'CPL',
        jumpToPly: 'Aller',
        thinkSparkTitle: 'Profil des temps',
        thinkSparkHint: 'Barres ambre/rouge = coups ultra-rapides après l’ouverture.',
        analysisQuick: 'Rapide (d10)',
        analysisDeep: 'Profonde (d16)',
        analysisCloud: 'Analyse cloud',
        analysisDepth: 'Profondeur d’analyse',
        clubReview: 'Revue club',
        playAgain: 'Rejouer',
        prevMistake: 'Erreur précédente',
        nextMistake: 'Erreur suivante',
        reviewReady: 'Revue prête',
        retryClubReview: 'Relancer la revue club',
        clubReviewNoMoves: 'Aucun coup joué — revue club indisponible. Utilisez Analyser.',
        clubReviewTooShort: 'Partie trop courte pour la revue club auto. Essayez Rapide ou Cloud.',
        clubReviewFailed: 'Échec de la revue club. Réessayez, ou analyse Rapide / Cloud.',
        clubReviewPending: 'La revue club prépare les qualités de coups…',
        clubReviewSlow: 'La revue club est plus lente que d’habitude. Vous pouvez analyser en local.',
        clubReviewUnavailable: 'Revue club indisponible pour cette partie. Utilisez Rapide ou Cloud.',
        clubReviewRequestFailed: 'Impossible de lancer la revue club.',
        cancelAnalysis: 'Annuler',
        cloudAnalysisFailed: 'Échec de l’analyse cloud. Stockfish serveur disponible ?',
        fairPlayFastSummary: 'B rapides {{white}} · N rapides {{black}}',
        moveNumber: '#',
        san: 'SAN',
        noGamesMatchFilter: 'Aucune partie pour ce filtre',
        noGamesMatchFilterHint: 'Essayez un autre filtre, ou revenez à Tous.',
        noGamesMatchSearch: 'Aucune partie pour cette recherche',
        noGamesMatchSearchHint: 'Essayez un autre nom ou effacez la recherche.',
        clearSearch: 'Effacer la recherche',
        teamNamesRequired: 'Saisissez deux noms d’équipe différents.',
        filterAll: 'Tous',
        yourMove: 'À vous',
        correspondenceGames: 'Correspondance',
        fairPlayLevel: {
          LOW: 'OK',
          WATCH: 'À surveiller',
          HIGH: 'Élevé'
        },
        customTime: 'Personnalisé',
        minutes: 'Minutes',
        increment: 'Incrément (sec)',
        yourColor: 'Votre couleur',
        colorRandom: 'Aléatoire',
        colorRandomSide: 'Camp aléatoire',
        colorWhite: 'Blancs',
        colorBlack: 'Noirs',
        offerDraw: 'Proposer nulle',
        acceptDraw: 'Accepter la nulle',
        declineDraw: 'Refuser la nulle',
        opponentOffersDraw: 'L’adversaire propose la nulle',
        youOfferedDraw: 'Nulle proposée — en attente de l’adversaire',
        drawFailed: 'Impossible de mettre à jour la proposition de nulle.',
        abort: 'Annuler la partie',
        abortFailed: 'Impossible d’annuler cette partie.',
        abortLeaveFailed: 'Impossible d’annuler votre défi en attente. Il peut rester visible pour l’adversaire jusqu’à expiration.',
        whiteToMove: 'Trait aux Blancs',
        blackToMove: 'Trait aux Noirs',
        yourTurn: 'À votre tour',
        engineThinking: 'Le moteur réfléchit…',
        moves: 'Coups',
        resignConfirm: 'Abandonner cette partie ?',
        playNow: 'Jouer maintenant',
        resume: 'Reprendre',
        hubRatingHint: 'Grimpez l’échelle de l’académie avec des parties classées.',
        challengeShortcut: 'Inviter un membre du club',
        myGamesShortcut: 'Continuer ou revoir',
        challengesHint: 'Mis à jour en direct quand quelqu’un vous défie.',
        noChallengesHint: 'Défiez un membre du club pour lancer une partie en direct.',
        noGamesHint: 'Jouez contre l’ordinateur ou défiez quelqu’un pour remplir cette liste.',
        noRatingsHint: 'Jouez quelques parties classées pour apparaître au classement.',
        noPlayersFound: 'Aucun joueur trouvé',
        noPlayersFoundHint: 'Essayez un autre nom ou nom d’utilisateur.',
        searchPlayersPlaceholder: 'Rechercher par nom ou nom d’utilisateur',
        levelEasy: 'Facile',
        levelHard: 'Difficile',
        recordShort: 'V/N/D',
        waitingOpponent: 'En attente de l’adversaire…',
        premoveHint: 'Vous pouvez pré-jouer en attendant.',
        moveDeadlineShort: 'Coup avant {{when}}',
        waitingToJoinTitle: 'En attente que l’adversaire rejoigne',
        waitingToJoinHint: 'En attente que {{name}} rejoigne… Les horloges démarrent à l’acceptation.',
        waitingToJoinInviteeHint: 'Acceptez pour démarrer la partie contre {{name}}.',
        premoveArmed: 'Pré-coup prêt : {{from}}→{{to}} — appuyez sur Clear ou les cases',
        clearPremove: 'Effacer le pré-coup',
        banQuickLinkBlocked: 'Indisponible pendant un ban',
        banConfirm: 'Bannir ce joueur de la plateforme de jeu ?',
        ratedConfirm: 'Partie classée — cela affectera votre classement. Continuer ?',
        challengeAcceptFailed: 'Impossible d’accepter le défi. Réessayez.',
        challengeDeclineFailed: 'Impossible de refuser. Réessayez.',
        sharedAnalysis: 'Analyse partagée par {{name}}',
        loading: 'Chargement…',
        banBanner: 'Vous êtes banni de la plateforme de jeu.',
        banBannerReason: 'Motif : {{reason}}',
        banBannerUntil: 'Jusqu’au {{when}}',
        banBannerIndefinite: 'Cette exclusion n’a pas de date de fin.',
        timeControl: {
          label: 'Cadence',
          daysPerMove: '{{count}}j/coup'
        },
        countdown: {
          zeroSeconds: '0s',
          daysHours: '{{d}}j {{h}}h',
          days: '{{d}}j',
          hoursMinutes: '{{h}}h {{m}}min',
          hours: '{{h}}h',
          minutes: '{{m}}min',
          seconds: '{{s}}s'
        },
        shareAnalysisBtn: 'Partager l’analyse',
        shareAnalysisDone: 'Analyse partagée avec les spectateurs',
        shareAnalysisFailed: 'Impossible de partager l’analyse.',
        bookMoves: 'Livre',
        bookMovesHint: 'Suggestions du livre du club (contre l\'ordinateur uniquement)',
        exploreOpening: 'Explorer',
        pickOpening: 'Charger une ouverture du club',
        pickOpeningPlaceholder: 'Choisir une ouverture…',
        openingLoaded: '{{name}} chargée',
        openingLoadFailed: 'Impossible de charger cette ouverture',
        liveNowPeek: 'En direct',
        autoQueen: 'Toujours promouvoir en dame',
        showBookHints: 'Indices du livre d\'ouvertures',
        myStudents: 'Mes eleves',
        rosterLoadFailed: 'Impossible de charger vos eleves.',
        noRosterStudents: 'Aucun eleve dans votre liste',
        typeToSearchOrRoster: 'Tapez un nom ou choisissez un eleve ci-dessus.',
        drillOn: 'Drill livre active',
        drillOff: 'Drill livre',
        drillScore: '{{correct}} / {{attempts}} coups du livre',
        drillOutOfBook: 'Hors livre',
        drillCorrect: 'Coup du livre !',
        drillWrong: 'Pas dans le livre du club{{hint}}',
        pgnPackBusy: 'Creation du PGN…',
        pgnPackEmpty: 'Aucune partie terminee a exporter.',
        pgnPackFailed: 'Impossible de telecharger le pack PGN.',
        accuracyChip: '{{pct}}% de precision',
        accuracy: 'Precision',
        recentAccuracy: '{{pct}}% de precision recente',
        recentAccuracyGames: '{{n}} parties',
        vsYouRecord: 'contre vous {{w}}–{{d}}–{{l}}',
        openEvent: 'Ouvrir l’evenement',
        recentOpponents: 'Adversaires recents',
        gameLoadedOnCoachBoard: 'Partie #{{id}} chargee',
        gameLoadFailed: 'Impossible de charger cette partie sur le plateau coach.',
        noActiveBans: 'Aucun ban actif.',
        liftBanConfirm: 'Lever ce ban ?',
        hub: 'Retour au Hub de jeu',

        currentRound: 'Round actuel',
        allowSpectators: 'Autoriser les spectateurs',
        spectatorsOff: 'Sans spectateurs',
        fairPlayCaseAutoOpened: 'Ouvert automatiquement (moteur)',
        showCoordinates: 'Coordonnées',
        leaderboardGames: 'Parties',
        flipBoard: 'Retourner l’échiquier',
        copyPgn: 'Copier le PGN',
        downloadPgn: 'Télécharger le PGN',
        copyGameLink: 'Copier le lien de partie',
        copied: 'Copié !',
        copyFailed: 'Impossible de copier le PGN.',
        rematch: 'Revanche',
        rematchSent: 'Défi envoyé',
        rematchSentHint: 'Défi de revanche envoyé — en attente de votre adversaire.',
        rematchFailed: 'Impossible d’envoyer le défi de revanche.',
        rematchInboxLoadFailed: 'Impossible de charger les défis de revanche. Réessayez.',
        offerTakeback: 'Reprise',
        acceptTakeback: 'Accepter la reprise',
        declineTakeback: 'Refuser la reprise',
        takebackFailed: 'Impossible de mettre à jour la reprise.',
        opponentOffersTakeback: 'L’adversaire demande une reprise',
        youOfferedTakeback: 'Reprise proposée — en attente de l’adversaire',
        typeMove: 'Saisir un coup',
        typeMovePlaceholder: 'e2e4 ou Nf3',
        playMove: 'Jouer',
        chat: 'Chat',
        chatPlaceholder: 'Message à l’adversaire…',
        chatSend: 'Envoyer',
        chatEmpty: 'Pas encore de messages.',
        chatReadOnly: 'Observation',
        chatLoadFailed: 'Impossible de charger le chat.',
        chatSendFailed: 'Impossible d’envoyer le message.',
        chatOffline: 'Vous êtes hors ligne — reconnectez-vous pour envoyer un message.',
        quickMatch: 'Partie rapide',
        quickMatchDesc: 'Publiez une recherche ou rejoignez un lobby ouvert avec la même cadence.',
        findOpponent: 'Trouver un adversaire',
        seekWaiting: 'En attente d’un adversaire…',
        cancelSeek: 'Annuler la recherche',
        seekFailed: 'Impossible de mettre à jour la recherche.',
        cancelSeekFailed: 'Impossible d’annuler cette recherche.',
        acceptSeekFailed: 'Impossible de rejoindre cette recherche.',
        seekMatched: 'Adversaire trouvé — ouverture de l’échiquier…',
        outgoingChallenges: 'Défis sortants',
        outgoingChallengesHint: 'Défis que vous avez envoyés et qui sont encore en attente.',
        noOutgoingChallenges: 'Aucun défi sortant.',
        noOutgoingChallengesHint: 'Quand vous défiez quelqu’un, il apparaît ici jusqu’à acceptation ou annulation.',
        cancelChallenge: 'Annuler',
        cancelChallengeFailed: 'Impossible d’annuler ce défi.',
        challengeAccepted: 'Défi accepté',
        challengeDeclined: 'Défi refusé',
        challengeCancelled: 'Défi annulé',
        accuracy: 'Précision',
        moveQuality: {
          best: 'Meilleur',
          brilliant: 'Brillant',
          good: 'Bon',
          inaccuracy: 'Imprécision',
          mistake: 'Erreur',
          blunder: 'Gaffe'
        },
        continueGame: 'Partie en cours',
        activeGames: 'En cours',
        pastGames: 'Terminées',
        noPastGames: 'Pas encore de parties terminées.',
        review: 'Revoir',
        cancel: 'Annuler',
        noPairings: 'Pas encore d’appariements',
        noPairingsHint: 'Les appariements apparaissent après le démarrage d’une ronde par l’organisateur.',
        choosePromotion: 'Choisir la promotion',
        challengeSentHint: 'En attente que <profile>{{name}}</profile> accepte sur le Hub de jeu.',
        challengeAnother: 'Défier quelqu’un d’autre',
        typeToSearch: 'Trouver un joueur',
        typeToSearchHint: 'Saisissez au moins 2 caractères pour rechercher dans l’académie.',
        levelDepth: 'Profondeur du moteur ~{{depth}}',
        abortConfirm: 'Annuler cette partie ?',
        soundOn: 'Activer le son',
        soundOff: 'Couper le son',
        reviewingPly: 'Coup {{ply}} / {{total}}',
        you: 'Vous',
        reviewMoves: 'Revoir les coups',
        showResult: 'Résultat',
        dismissResult: 'Fermer',
        backToLive: 'En direct',
        retry: 'Réessayer',
        plyStart: 'Premier coup',
        plyPrev: 'Coup précédent',
        plyNext: 'Coup suivant',
        plyEnd: 'Dernier coup',
        searchFailed: 'Échec de la recherche de joueurs. Réessayez.',
        searchFailedHint: 'Vérifiez votre connexion, puis saisissez une autre recherche.',
        outsideTop: 'Hors de la page actuelle du classement',
        reconnecting: 'Connexion perdue — reconnexion et synchronisation…',
        timeoutFailed: 'Impossible de réclamer le temps écoulé. Essayez d’actualiser.',
        firstMoveYourTurn: 'Jouez votre premier coup — {{seconds}}s (puis les pendules démarrent)',
        firstMoveWaiting: 'En attente du premier coup — {{seconds}}s',
        engineFailed: 'Coup de l’ordinateur échoué. Actualisez si l’échiquier semble bloqué.',
        challengeIncoming: 'Nouveau défi',
        analyze: 'Analyser',
        reanalyze: 'Réanalyser',
        analyzing: 'Analyse… {{pct}}%',
        hideAnalysis: 'Masquer l’analyse',
        analysis: 'Éval. moteur',
        bestMove: 'Meilleur coup',
        levelBand: {
          beginner: 'Débutant',
          intermediate: 'Intermédiaire',
          club: 'Niveau club',
          strong: 'Fort'
        },
        gameType: {
          VS_COMPUTER: 'vs Ordinateur',
          CASUAL: 'Amicale',
          RATED: 'Classée'
        },
        resultReason: {
          CHECKMATE: 'Échec et mat',
          RESIGN: 'Abandon',
          STALEMATE: 'Pat',
          AGREEMENT: 'Nulle par accord',
          THREEFOLD: 'Triple répétition',
          FIFTY_MOVE: 'Règle des 50 coups',
          INSUFFICIENT: 'Matériel insuffisant',
          TIMEOUT: 'Temps écoulé',
          ABORT: 'Annulée',
          COMPUTER: 'Ordinateur'
        },
        status: {
          ACTIVE: 'En cours',
          WHITE_WON: 'Blancs gagnent',
          BLACK_WON: 'Noirs gagnent',
          DRAW: 'Nulle',
          ABORTED: 'Annulée',
          PENDING: 'En attente de démarrage'
        },

        acceptAppeal: 'Accepter l\'appel',
        acceptRematch: 'Accepter la revanche',
        adjudicateConfirm: 'Valider la victoire des blancs ?',
        adjudicateConfirmBlack: 'Valider la victoire des noirs ?',
        adjudicateConfirmDraw: 'Valider la nulle ?',
        appealFailed: 'Impossible d\'envoyer l\'appel.',
        appealPending: 'Appel en cours d\'examen',
        appealPlaceholder: 'Expliquez pourquoi lever le ban…',
        appealRejectNotePrompt: 'Note de refus (optionnel)',
        appealRejectedNote: 'Appel rejeté',
        applyFen: 'Appliquer le FEN',
        applyPgn: 'Charger le PGN',
        animationSpeed: 'Animation',
        animationSlow: 'Lente',
        animationNormal: 'Normale',
        animationFast: 'Rapide',
        banStatusUnknown: 'Statut de ban indisponible',
        boardSettings: 'Paramètres du plateau',
        boardSettingsHint: 'Préférences synchronisées sur chaque plateau',
        boardTheme: 'Thème du plateau',
        boardThemes: { classic: 'Classique', green: 'Vert', brown: 'Brun', blue: 'Bleu' },
        openings: {
          'alekhine_defence': "Défense Alekhine",
          'benoni_benko_path': "Benoni / Benko path",
          'birds_opening': "Bird's Opening",
          'caro_kann_defence': "Défense Caro-Kann",
          'caro_kann_advance': "Caro-Kann — Advance",
          'caro_kann_classical': "Caro-Kann — Classical",
          'dutch_defence': "Dutch Defence",
          'english_opening': "English Opening",
          'english_1_e5': "English — 1…e5",
          'english_1_e6': "English — 1…e6",
          'english_anglo_indian': "English — Anglo-Indian",
          'english_symmetrical': "English — Symmetrical",
          'evans_gambit': "Gambit Evans",
          'french_defence': "Défense française",
          'french_advance': "French — Advance",
          'french_classical': "French — Classical",
          'french_exchange': "French — Exchange",
          'giuoco_piano': "Giuoco Piano",
          'giuoco_piano_open': "Giuoco Piano — Open",
          'giuoco_piano_c3': "Giuoco Piano — c3",
          'giuoco_piano_cxd4': "Giuoco Piano — cxd4",
          'giuoco_piano_d4': "Giuoco Piano — d4",
          'indian_defence': "Indian Defence",
          'indian_3_nf3': "Indian — 3.Nf3",
          'italian_game': "Partie italienne",
          'kings_gambit': "Gambit du roi",
          'kings_indian_defence': "King's Indian Defence",
          'kings_knight_opening': "Ouverture du cavalier roi",
          'kings_knight_two_knights_path': "King's Knight — Two Knights path",
          'kings_pawn': "Pion roi",
          'london_system': "Système de Londres",
          'london_system_nc6': "Système de Londres — Nc6",
          'london_system_c5': "Système de Londres — c5",
          'london_system_main_line': "Système de Londres — main line",
          'modern_defence': "Défense moderne",
          'nimzo_indian_defence': "Nimzo-Indian Defence",
          'nimzo_indian_path': "Nimzo-Indian path",
          'nimzo_larsen_attack': "Nimzo–Larsen Attack",
          'open_game': "Partie ouverte",
          'owens_defence': "Défense Owen",
          'petrov_defence': "Défense Petrov",
          'philidor_defence': "Défense Philidor",
          'pirc_defence': "Défense Pirc",
          'qgd': "Gambit dame refusé",
          'queens_gambit': "Gambit dame",
          'queens_gambit_accepted': "Gambit dame Accepted",
          'queens_gambit_declined': "Gambit dame refusé",
          'queens_gambit_declined_3_nf3': "Gambit dame refusé — 3.Nf3",
          'queens_gambit_declined_bg5': "Gambit dame refusé — Bg5",
          'queens_gambit_declined_london_hybrid': "Gambit dame refusé — London hybrid",
          'queens_indian_nimzo_path': "Queen's Indian / Nimzo path",
          'queens_pawn': "Pion dame",
          'queens_pawn_game': "Partie du pion dame",
          'ruy_lopez': "Partie espagnole",
          'ruy_lopez_morphy': "Partie espagnole — Morphy",
          'ruy_lopez_morphy_defence': "Partie espagnole — Morphy Defence",
          'ruy_lopez_morphy_bb3': "Partie espagnole — Morphy, Bb3",
          'ruy_lopez_morphy_closed': "Partie espagnole — Morphy, Closed",
          'ruy_lopez_morphy_re1': "Partie espagnole — Morphy, Re1",
          'ruy_lopez_morphy_b5': "Partie espagnole — Morphy, b5",
          'ruy_lopez_morphy_d6': "Partie espagnole — Morphy, d6",
          'r_ti_opening': "Réti Opening",
          'r_ti_1_nf6': "Réti — 1…Nf6",
          'r_ti_1_c5': "Réti — 1…c5",
          'r_ti_1_d5': "Réti — 1…d5",
          'scandinavian_defence': "Défense scandinave",
          'scandinavian_2_nf6': "Scandinavian — 2…Nf6",
          'scandinavian_2_qxd5': "Scandinavian — 2…Qxd5",
          'sicilian_defence': "Défense sicilienne",
          'sicilian_open': "Sicilienne ouverte",
          'sicilian_2_nf3': "Sicilian — 2.Nf3",
          'sicilian_2_nc6': "Sicilian — 2…Nc6",
          'sicilian_2_d6': "Sicilian — 2…d6",
          'sicilian_2_e6': "Sicilian — 2…e6",
          'sicilian_classical_path': "Sicilian — Classical path",
          'sicilian_dragon': "Sicilian — Dragon",
          'sicilian_dragon_classical': "Sicilian — Dragon, Classical",
          'sicilian_dragon_yugoslav': "Sicilian — Dragon, Yugoslav",
          'sicilian_hyperaccelerated': "Sicilian — Hyperaccelerated",
          'sicilian_najdorf': "Sicilian — Najdorf",
          'sicilian_najdorf_english_attack': "Sicilian — Najdorf, English Attack",
          'slav_defence': "Slav Defence",
          'two_knights_defence': "Défense des deux cavaliers",
          'van_geet_opening': "Van Geet Opening"
        },
        defaultTeamA: 'Équipe A',
        defaultTeamB: 'Équipe B',
        claimDraw: 'Revendiquer la nulle',
        claimDrawFailed: 'Impossible de revendiquer la nulle.',
        clearHighlights: 'Effacer les surlignages',
        coachBoard: 'Plateau coach',
        coachBoardArrowHint: 'Clic droit pour surligner ; un second dessine une flèche.',
        coachBoardClassroomHint: 'Pour l\'audio/vidéo, utilisez Classroom live. Ce plateau sert aux positions et flèches.',
        coachBoardSubtitle: 'Plateau pédagogique — position, flèches, Classroom pour l\'A/V',
        confirmMove: 'Confirmer le coup',
        copyFen: 'Copier le FEN',
        copyFailed: 'Copie impossible',
        dismissReport: 'Rejeter',
        favoriteOpenings: 'Ouvertures favorites',
        fenCopied: 'FEN copié',
        gameChat: 'Chat de partie',
        highContrast: 'Contraste élevé',
        hubPartialLoad: 'Certaines données du hub n\'ont pas chargé',
        invalidFen: 'FEN invalide',
        invalidPgn: 'PGN illisible',
        loadMoreGames: 'Charger plus ({{loaded}} sur {{total}})',
        moveConfirmation: 'Confirmer les coups',
        moveSending: 'Envoi du coup…',
        myRatingLoadFailed: 'Impossible de charger votre rating',
        noOpenAppeals: 'Aucun appel ouvert',
        noOpenReports: 'Aucun signalement ouvert',
        noOpenSeeks: 'Aucune recherche ouverte',
        noOpeningsYet: 'Pas encore d\'ouvertures',
        openAppeals: 'Appels ouverts',
        openClassroomLive: 'Ouvrir Classroom live',
        openGame: 'Ouvrir la partie',
        openReports: 'Signalements ouverts',
        peakRating: 'Pic',
        pgnLoaded: 'PGN chargé sur le plateau',
        pgnPaste: 'Coller le PGN',
        pgnPasteHint: 'PGN complet ou liste de coups…',
        pieceSet: 'Jeu de pièces',
        pieceSetStandard: 'Standard',
        pieceSetUnicode: 'Unicode',
        playerNotFound: 'Joueur introuvable',
        playerProfile: 'Profil joueur',
        playerProfileSubtitle: 'Ratings par cadence et parties',
        playingNow: 'En train de jouer',
        prefsSaved: 'Préférences enregistrées',
        ratingTrend: 'Tendance de rating récente',
        ratingsBySpeed: 'Ratings par cadence',
        recentGames: 'Parties récentes',
        rejectAppeal: 'Rejeter l\'appel',
        rematchIncoming: 'Offre de revanche',
        rematchIncomingToast: 'Défi de revanche',
        rematchSending: 'Envoi…',
        reportDetails: 'Détails (optionnel)',
        reportFailed: 'Impossible d\'envoyer le signalement.',
        reportPlayer: 'Signaler le joueur',
        reportPrompt: 'Décrivez brièvement la raison (optionnel) :',
        reportReasonPrompt: 'Motif du signalement',
        reportSent: 'Signalement envoyé. L\'équipe examinera.',
        reportedBy: 'Signalé par',
        reportsAppealsLoadFailed: 'Impossible de charger signalements/appels',
        resetBoard: 'Réinitialiser',
        resolveReport: 'Résoudre',
        searchGames: 'Rechercher des parties',
        searchGamesPlaceholder: 'Nom d\'adversaire ou statut…',
        shortcutEnds: 'Aller au début / à la fin',
        shortcutHelpTitle: 'Raccourcis clavier',
        shortcutScrub: 'Parcourir les coups',
        shortcutToggleHelp: 'Afficher / masquer l\'aide',
        shortcuts: 'Raccourcis',
        sound: 'Son',
        speedClass: 'Cadence',
        speedClassLabel: 'Classe de cadence',
        submitAppeal: 'Envoyer l\'appel',
        submitReport: 'Envoyer le signalement',
        submitting: 'Envoi…',
        themesLoadFailed: 'Impossible de charger les thèmes',
        tourneyFilterActive: 'Ouverts & en cours',
        tourneyFilterDraft: 'Brouillon',
        tourneyFilterCompleted: 'Terminés',
        undoMove: 'Annuler le coup',
        reportReason: {
          assistance: 'Suspicion d\'assistance / moteur',
          chat: 'Chat abusif',
          stalling: 'Temporisation / refus de jouer',
          other: 'Autre'
        },
},
      auth: {
        welcome: 'Bienvenue',
        signInTo: 'Connexion à {{name}}',
        login: 'Connexion',
        username: 'Nom d\'utilisateur',
        password: 'Mot de passe',
        forgot: 'Mot de passe oublié ?',
        enterUsername: 'Saisissez votre nom d\'utilisateur',
        enterPassword: 'Saisissez votre mot de passe',
        invalid: 'Nom d\'utilisateur ou mot de passe invalide',
        showPassword: 'Afficher le mot de passe',
        hidePassword: 'Masquer le mot de passe',
        agreePrefix: 'En vous connectant, vous acceptez notre',
        agreeAnd: 'et nos',
        privacyPolicy: 'Politique de confidentialité',
        terms: 'Conditions d\'utilisation',
        tooManyAttempts: 'Trop de tentatives. Veuillez réessayer plus tard.',
        sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',
        sessionInvalid: 'Votre session n\'est plus valide. Veuillez vous reconnecter.',
        sessionInactive: 'Votre session a expiré pour cause d\'inactivité. Veuillez vous reconnecter.'
      },
      footer: {
        quickLinks: 'Liens rapides',
        academyContact: 'Contact académie',
        licenseInfo: 'Infos licence',
        privacyPolicy: 'Politique de confidentialité',
        termsOfService: 'Conditions d\'utilisation',
        terms: 'Conditions',
        forgotPassword: 'Mot de passe oublié',
        helpGettingStarted: 'Aide et démarrage',
        helpFaq: 'Aide et FAQ',
        allRightsReserved: 'Tous droits réservés.',
        developedBy: 'Développé et sous licence par',
        chessManagementSystem: 'Système de gestion d’échecs',
        contactDetailsHint: 'Les coordonnées peuvent être ajoutées par l’administrateur de votre académie.',
        licensePending: 'Le statut de la licence apparaîtra ici lorsqu’il sera disponible.',
        daysRemaining: '{{count}} jour restant',
        daysRemaining_other: '{{count}} jours restants',
        licenseActive: 'Licence active',
        built: 'Build {{version}} · {{tz}}',
        unknownVersion: 'inconnu'
      },
      common: {
        language: 'Langue',
        save: 'Enregistrer',
        cancel: 'Annuler',
        loading: 'Chargement…',
        edit: 'Modifier',
        archive: 'Archiver',
        add: 'Ajouter',
        all: 'Tous',
        showing: 'Affichage',
        of: 'sur',
        to: 'à',
        results: 'résultats',
        rowsPerPage: 'Lignes par page :',
        downloadExcel: 'Télécharger Excel',
        search: 'Rechercher',
        id: 'ID :',
        idColumn: 'ID',
        items: 'éléments',
        back: 'Retour',
        firstPage: 'Première page',
        previousPage: 'Page précédente',
        nextPage: 'Page suivante',
        lastPage: 'Dernière page',
        prev: 'Préc.',
        next: 'Suiv.',
        pageOf: 'Page {{current}} sur {{total}}',
        showingRange: 'Affichage {{start}} à {{end}} sur {{total}} résultats',
        noDataExport: 'Aucune donnée à exporter',
        exportFailed: 'Échec de l\'export. Veuillez réessayer.',
        downloadAsExcel: 'Télécharger en Excel (.xlsx)',
        active: 'Actifs',
        archived: 'Archivés',
        inactive: 'Inactif',
        inactiveSuffix: ' (Inactif)',
        activate: 'Activer',
        actions: 'Actions',
        status: 'Statut',
        username: 'Nom d\'utilisateur',
        email: 'E-mail',
        phone: 'Téléphone',
        roles: 'Rôles',
        fullName: 'Nom complet',
        name: 'Nom',
        description: 'Description',
        noDataFound: 'Aucune donnée trouvée',
        emptySearchHint: 'Essayez d’ajuster vos termes de recherche',
        noMatchSearch: 'Aucun {{entity}} ne correspond à votre recherche',
        noFound: 'Aucun {{entity}} trouvé',
        exporting: 'Export en cours…',
        showDetails: 'Afficher les détails',
        hideDetails: 'Masquer les détails',
        system: 'Système',
        na: 'N/D',
        retry: 'Réessayer',
        listLoadFailed: 'Impossible de charger cette liste. Actualisez et réessayez.',
        optionsLoadFailed: 'Impossible de charger les options. Actualisez la page ou réessayez.',
        unknownError: 'Erreur inconnue',
        entries: 'entrées',
        apply: 'Appliquer',
        never: 'Jamais',
        code: 'Code',
        location: 'Lieu',
        place: 'Place',
        score: 'Score',
        dismiss: 'Fermer',
        yes: 'Oui',
        no: 'Non',
        done: 'Terminé.',
        close: 'Fermer',
        gotIt: 'J\'ai compris',
        confirm: 'Confirmer',
        popupInfo: 'Information',
        minutesCount: '{{count}} minutes',
        unknown: 'Inconnu',
        userFallback: 'Utilisateur #{{id}}',
        branchFallback: 'Succursale #{{id}}',
        levelFallback: 'Niveau #{{id}}',
        popupSuccess: 'Succès',
        popupWarning: 'À vérifier',
        popupError: 'Attention requise',
        pressEscCancel: 'Appuyez sur Échap pour annuler',
        pressEscClose: 'Appuyez sur Échap pour fermer',
        backendDown: 'Le serveur backend est indisponible — contactez un administrateur ou réessayez plus tard.',
        ariaClose: 'Fermer',
        info: 'Info',
        learnMore: 'En savoir plus',
        remove: 'Retirer',
        assigned: 'Assigné'
      },
      parent: {
        childSwitcher: {
          loadingChildren: 'Chargement des enfants…',
          viewingChild: 'Enfant affiché',
          switchChild: 'Changer d\'enfant',
          selectChild: 'Sélectionner un enfant…'
        },
        chooseChildTitle: 'Choisir un enfant',
        chooseChildBody: 'Sélectionnez l’élève lié que vous souhaitez consulter, ou revenez à l’accueil parent.',
        backToParentHome: 'Retour à l’accueil parent',
        parentHome: 'Accueil parent',
        viewingPrefix: 'Affichage',
        readOnly: 'Lecture seule',
        roleLabel: 'Parent'
      },
      header: {
        accountMenu: 'Menu du compte',
        openNav: 'Ouvrir le menu de navigation',
        closeNav: 'Fermer le menu de navigation',
        closeNavSidebar: 'Fermer la navigation',
        myProfile: 'Mon profil',
        switchRole: 'Changer de rôle',
        logout: 'Déconnexion'
      },
      selectSearch: {
        select: 'Sélectionner…',
        search: 'Rechercher…',
        loading: 'Chargement…',
        noOptions: 'Aucune option trouvée',
        loadFailed: 'Impossible de charger les options — réessayez',
      },
      datePicker: {
        clearDate: 'Effacer la date',
        selectTime: 'Sélectionner l\'heure',
        selectDateTime: 'Sélectionner la date et l\'heure',
        selectTimeIntervals: 'Sélectionner l\'heure (intervalles de 15 min)',
        selectTimeWithFormat: 'Sélectionner l\'heure ({{format}}, intervalles de 15 min)',
        dateWithFormat: 'Date ({{format}})',
        timeWithFormat: 'Heure ({{format}})',
        noTimeOptions: 'Aucune option horaire disponible',
        duration: 'Durée : {{hours}} h {{minutes}} min',
        exceeds3Hours: 'Dépasse 3 heures',
        hour12: '12 heures',
        hour24: '24 heures',
        clear: 'Effacer',
        done: 'Terminé'
      },
      notificationBell: {
        ariaLabel: 'Notifications',
        ariaLabelUnread: 'Notifications ({{count}} non lues)',
        title: 'Notifications',
        unread: '{{count}} non lues',
        markAllRead: 'Tout marquer comme lu',
        empty: 'Tout est à jour !',
        viewAll: 'Voir toutes les notifications',
        unreadHint: '{{count}} demandent votre attention',
        caughtUpHint: 'Aucune alerte non lue',
        emptyHint: 'Les alertes portefeuille, présence et confidentialité apparaissent ici quand une action est nécessaire.'
      },
      publicLayout: {
        developedBy: 'Développé et sous licence par',
        licenseSupport: 'Pour l\'achat de licence ou le support',
        contactSupport: 'Contacter le support'
      },
      accountAccess: {
        signInLink: {
          button: 'Envoyer le lien de connexion par e-mail',
          buttonTitle: 'Envoyer un e-mail avec un lien pour définir ou réinitialiser le mot de passe',
          buttonTitleNoEmail: 'Ajoutez une adresse e-mail au profil pour envoyer un lien de connexion',
          noEmailAlert: 'Ce {{role}} n\'a pas d\'adresse e-mail. Ajoutez-en une avant d\'envoyer un lien de connexion.',
          listConfirm: 'Envoyer un lien de connexion à ce {{role}} ? Il pourra définir ou réinitialiser son mot de passe.',
          editConfirm: 'Envoyer un nouveau lien de connexion à ce {{role}} ?',
          success: 'Lien de connexion envoyé par e-mail avec succès.',
          failure: 'Impossible d\'envoyer le lien de connexion par e-mail.',
          createCheckbox: 'Envoyer le lien de connexion par e-mail (mot de passe défini par l\'utilisateur)',
          createNote: 'Un lien de connexion à usage unique sera envoyé par e-mail pour définir le mot de passe.',
          bulkImportOption: 'Lien de connexion par e-mail — les élèves définissent leur propre mot de passe'
        },
        passwordReset: {
          pageSubtitle: 'Nous enverrons un lien de réinitialisation si un compte existe',
          successMessage: 'Si un compte existe pour cet e-mail ou ce nom d\'utilisateur, un lien de réinitialisation a été envoyé. Veuillez vérifier votre boîte de réception.',
          submitButton: 'Envoyer le lien de réinitialisation par e-mail',
          publicHelp: 'Vous recevrez un lien de réinitialisation à votre adresse e-mail enregistrée.'
        }
      },
      userActions: {
        edit: 'Modifier',
        archive: 'Archiver',
        activate: 'Activer',
        removeFromList: 'Retirer de la liste',
        removePermanently: 'Supprimer définitivement',
        editTitle: 'Modifier {{label}}',
        archiveTitle: 'Archiver {{label}}',
        reactivateTitle: 'Réactiver {{label}}',
        removeFromListTitle: 'Retirer de votre liste sans archiver l\'élève',
        removePermanentlyTitle: 'Retirer des listes et libérer une place de licence ; historique conservé comme (Inactif)',
        archiveConfirm: 'Archiver ce {{label}} ? Connexion impossible. Le dossier est conservé et compte toujours dans la licence jusqu\'à suppression définitive (si capacité atteinte).',
        permanentRemoveConfirm: 'Supprimer définitivement ce {{label}} archivé de vos listes ? Présence, paiements et historique restent dans le système et apparaissent comme « (Inactif) ». Cela libère une place de licence. Action irréversible.',
        reactivateConfirm: 'Réactiver ce {{label}} ? Il pourra à nouveau se connecter.',
        loadArchiveFailed: 'Impossible de charger les options d\'archivage pour {{label}}.',
        archiveFailed: 'Échec de l\'archivage de {{label}}.',
        removeFailed: 'Échec de la suppression de {{label}}.',
        activateFailed: 'Échec de l\'activation de {{label}}.',
        labels: {
          student: 'élève',
          coach: 'entraîneur',
          manager: 'manager',
          user: 'utilisateur'
        }
      },
      search: {
        placeholder: 'Rechercher élèves, entraîneurs, groupes…',
        aria: 'Recherche globale',
        searching: 'Recherche…',
        noResults: 'Aucun résultat pour « {{q}} »',
        students: 'Élèves',
        coaches: 'Entraîneurs',
        batches: 'Groupes',
        apiUnavailable: 'API de recherche indisponible. Redémarrez le conteneur backend.',
        failed: 'Échec de la recherche. Veuillez réessayer.'
      },
      days: {
        short: {
          '1': 'Lun',
          '2': 'Mar',
          '3': 'Mer',
          '4': 'Jeu',
          '5': 'Ven',
          '6': 'Sam',
          '7': 'Dim'
        }
      },
      charges: {
        modes: {
          PER_SESSION: 'Par séance (forfait)',
          PER_SESSION_HOURLY: 'Par séance (horaire)',
          PER_PERIOD: 'Forfait périodique',
          RECURRING_SESSIONS: 'Par séance (horaire)',
          MONTHLY: 'Forfait périodique',
          YEARLY: 'Forfait périodique'
        },
        intervals: {
          WEEKLY: 'Hebdomadaire (7 jours)',
          MONTHLY: 'Mensuel (30 jours)',
          QUARTERLY: 'Trimestriel (90 jours)',
          SEMI_ANNUAL: 'Semestriel (180 jours)',
          YEARLY: 'Annuel (365 jours)'
        },
        status: {
          PAID: 'Payé',
          UNPAID: 'Impayé',
          ESCALATED: 'Escaladé'
        }
      },
      carryForward: {
        columnHelp: 'Le report contrôle la facturation lorsqu\'un élève est absent. Si activé, les frais de séance ne sont pas déduits et le crédit peut être utilisé plus tard. Si désactivé, les frais sont toujours déduits même si l\'élève est marqué absent.',
        modes: {
          ALLOWED: {
            label: 'Autorisé pour tous les élèves',
            desc: 'Les élèves absents ne sont pas facturés — les crédits de séance sont reportés pour une utilisation ultérieure.'
          },
          NOT_ALLOWED: {
            label: 'Non autorisé pour aucun élève',
            desc: 'Les élèves absents sont toujours facturés — les frais de séance sont déduits de leur portefeuille.'
          },
          ENROLLMENT_LEVEL: {
            label: 'Décider au niveau de l\'inscription',
            desc: 'Choisir le report individuellement lors de l\'inscription de chaque élève.'
          }
        },
        batchPolicyTitle: {
          ALLOWED: 'Politique du groupe : report activé pour tous les élèves. Si un élève est marqué absent, ses frais de séance ne sont pas déduits — le crédit est reporté.',
          NOT_ALLOWED: 'Politique du groupe : report désactivé pour tous les élèves. Si un élève est marqué absent, ses frais de séance sont toujours déduits de son portefeuille.',
          ENROLLMENT_LEVEL: 'Politique du groupe : le report est défini par élève à l\'inscription. Chaque élève peut l\'avoir activé (pas de facturation si absent) ou désactivé.',
          default: 'Cet élève suit la politique de report du groupe.'
        },
        batchPolicySummary: {
          ALLOWED: 'Si un élève est absent, il n\'est pas facturé — le crédit de séance est reporté.',
          NOT_ALLOWED: 'Si un élève est absent, il est toujours facturé — les frais de séance sont déduits de son portefeuille.',
          ENROLLMENT_LEVEL: 'Vous pouvez choisir le report individuellement pour chaque élève lors de son inscription.'
        },
        batchPolicyLabel: {
          ALLOWED: 'Report activé pour tous',
          NOT_ALLOWED: 'Report désactivé pour tous',
          ENROLLMENT_LEVEL: 'Niveau inscription (spécifique à l\'élève)',
          unknown: 'Inconnu'
        },
        studentOverrideTitle: {
          enabled: 'Report activé pour cet élève. S\'il est marqué absent, les frais de séance ne sont pas déduits — le crédit est reporté.',
          disabled: 'Report désactivé pour cet élève. S\'il est marqué absent, les frais de séance sont toujours déduits de son portefeuille.'
        },
        studentOverrideShort: {
          enabled: 'Non facturé si absent — crédit reporté',
          disabled: 'Toujours facturé si absent — pas de report'
        },
        attendanceCell: {
          followsBatch: 'Suit le groupe ({{mode}})',
          enabled: 'Activé',
          disabled: 'Désactivé',
          unknown: 'Inconnu'
        }
      },
      branchModes: {
        OFFLINE: {
          label: 'Présentiel',
          description: 'Séances en personne sur un lieu physique'
        },
        ONLINE: {
          label: 'En ligne',
          description: 'Séances à distance / virtuelles uniquement'
        },
        HYBRID: {
          label: 'En ligne + Présentiel',
          description: 'Propose des séances en personne et en ligne'
        }
      },
      sessionModes: {
        ANY: 'Tous',
        ONLINE: 'En ligne',
        OFFLINE: 'Présentiel'
      },
      popup: {
        labels: {
          alert: 'Compris',
          confirm: 'Confirmer',
          cancel: 'Annuler'
        },
        titles: {
          confirmDestructive: 'Confirmer une action destructive',
          pleaseConfirm: 'Veuillez confirmer',
          confirmAction: 'Confirmer l\'action',
          success: 'Succès',
          attention: 'Une action est requise',
          pleaseReview: 'Veuillez vérifier',
          information: 'Information'
        },
        confirmLabels: {
          delete: 'Supprimer',
          archive: 'Archiver',
          activate: 'Activer',
          execute: 'Exécuter',
          send: 'Envoyer',
          trigger: 'Déclencher',
          remove: 'Retirer',
          continue: 'Continuer'
        }
      },
      unsavedChanges: {
        title: 'Modifications non enregistrées',
        message: 'Vous avez des modifications non enregistrées. Enregistrez-les avant de quitter, ignorez-les, ou restez sur cette page.',
        save: 'Enregistrer et quitter',
        discard: 'Ignorer les modifications',
        stay: 'Rester'
      },
      errors: {
        api: {
          fallback: 'Une erreur s\'est produite.',
          upgradeRequired: 'Mise à niveau requise',
          onlinePaymentsUpgrade: 'Les passerelles de paiement en ligne nécessitent une licence Professional ou Enterprise. Veuillez contacter votre académie pour une mise à niveau.',
          planLimitReached: 'Limite du plan atteinte',
          planLimitMessage: 'Votre plan actuel a atteint sa capacité. Veuillez mettre à niveau votre plan ou libérer de la capacité et réessayer.',
          requestFailed: 'Échec de la requête'
        }
      },
      wallet: {
        txnReasons: {
          ADVANCE_PAYMENT: 'Recharge anticipée',
          ONLINE_TOPUP: 'Recharge en ligne',
          SESSION_CHARGE: 'Frais de séance',
          SESSION_REVERSAL: 'Annulation de séance',
          DISCOUNT: 'Crédit remise',
          REFUND: 'Remboursement',
          LEGACY_DEBIT: 'Débit',
          default: 'Transaction'
        },
        dateLines: {
          session: 'Séance',
          created: 'Créé',
          paidOn: 'Payé le'
        }
      },
      regional: {
        presets: {
          india: 'Inde',
          india12: 'Inde (12 heures)',
          uae: 'Émirats arabes unis',
          us: 'États-Unis',
          uk: 'Royaume-Uni',
          eurozone: 'Zone euro (générique)',
          germany: 'Allemagne',
          france: 'France',
          netherlands: 'Pays-Bas',
          belgium: 'Belgique',
          spain: 'Espagne'
        },
        paymentReference: {
          IN: 'p. ex. Reçu n° 1234, réf. UPI : 123456789012',
          CA: 'e.g. Receipt #1234, PAD reference, or card auth code',
          US: 'p. ex. Reçu n° 1234, chèque n° 5678 ou code d\'autorisation carte',
          GB: 'p. ex. Reçu n° 1234, référence bancaire ou numéro de chèque',
          AE: 'p. ex. Reçu n° 1234 ou référence de virement',
          EU: 'p. ex. Reçu n° 1234 ou référence SEPA / virement',
          default: 'p. ex. Reçu n° 1234'
        },
        bankRouting: {
          IN: 'Code IFSC',
          US: 'Numéro de routage',
          CA: 'Transit / Institution Number',
          GB: 'Sort code',
          AE: 'SWIFT / IBAN',
          EU: 'IBAN',
          default: 'Code banque'
        }
      },
      batches: {
        title: 'Groupes',
        subtitle: 'Gérer et organiser les groupes de votre académie d’échecs',
        add: 'Ajouter un groupe',
        searchPlaceholder: 'Rechercher par nom, ID ou niveau…',
        entityLabel: 'groupes',
        archiveConfirm: 'Archiver ce groupe ?',
        archiveFailed: 'Échec de l’archivage du groupe.',
        empty: 'Aucun groupe trouvé',
        emptyHint: 'Créez votre premier groupe pour commencer',
        managerSubtitle: 'Gérer et organiser les groupes de formation',
        active: 'Actifs',
        archived: 'Archivés',
        batchNumber: 'Groupe #{{id}}',
        unknown: 'Groupe inconnu',
        individualSessions: 'Séances individuelles',
        individualSessionsNamed: 'Séances individuelles - {{level}}',
        walkInNamed: 'Walk-in - Niveau #{{level}}',
        carryForward: 'Report',
        perStudent: 'Par élève',
        carryForwardBadge: {
          allowed: 'Report autorisé',
          notAllowed: 'Pas de report',
          tooltipAllowed: 'Les crédits non utilisés seront reportés sur le prochain cycle de facturation.',
          tooltipNotAllowed: 'Les crédits non utilisés expireront à la fin du cycle de facturation.'
        },
        editTitle: 'Modifier le groupe',
        sessionsTitle: 'Voir les séances',
        archiveTitle: 'Archiver le groupe',
        level: 'Niveau',
        branch: 'Site',
        schedule: 'Planning',
        sessionDays: 'Jours de séance',
        sessionTime: 'Horaire',
        charges: 'Tarifs',
        lockNotes: {
          branch: 'Le site est fixé après la création du groupe (via le niveau).',
          level: 'Le niveau est fixé après la création du groupe et ne peut pas être modifié.',
          carryForward: 'La politique de report est fixée après la création du groupe.',
          chargesMode: 'Le mode de facturation ne peut pas être modifié une fois des inscriptions ou séances existent.',
          monthlyYearly: 'Les frais de période et l\'intervalle sont fixés après la création du groupe.',
          systemManaged: 'Ce groupe est géré automatiquement par le système et ne peut pas être modifié.',
          sessionCharge: 'S\'applique uniquement à la facturation future des séances ; les frais passés restent inchangés.'
        }
      },
      dashboard: {
        welcome: 'Bienvenue, {{name}}',
        quickActions: 'Actions rapides',
        defaultAdmin: 'Admin',
        defaultManager: 'Responsable',
        defaultCoach: 'Entraîneur',
        defaultStudent: 'Élève'
      },
      table: {
        branch: 'Site',
        level: 'Niveau',
        batch: 'Groupe',
        coach: 'Entraîneur',
        time: 'Heure',
        when: 'Quand',
        start: 'Début',
        end: 'Fin',
        mode: 'Mode',
        studentName: 'Nom de l\'élève',
        coachName: 'Nom de l\'entraîneur',
        branchName: 'Nom du site',
        enrolledDate: 'Date d\'inscription',
        payment: 'Paiement',
        balanceAfter: 'Solde après',
        planType: 'Type de forfait',
        groupType: 'Type de groupe',
        defaultRate: 'Tarif par défaut',
        defaultCredits: 'Crédits par défaut',
        levelRange: 'Plage de niveaux',
        creditExpiry: 'Expiration du crédit',
        attendance: 'Présence',
        topics: 'Sujets',
        balanceBefore: 'Solde avant',
        sessionFee: 'Frais de séance',
        dateTime: 'Date et heure',
        date: 'Date',
        description: 'Description',
        carryForward: 'Report',
        amount: 'Montant'
      },
      pages: {
        adminDashboard: {
          emailAlertTitle: 'L\'e-mail sortant n\'est pas configuré.',
          emailAlertBody: 'Les élèves et le personnel ne recevront pas de liens de connexion, rappels ou relevés tant que SMTP n\'est pas configuré.',
          configureEmail: 'Configurer e-mail',
          whatsappAlertTitle: 'WhatsApp (WATI) n\'est pas configuré.',
          whatsappAlertBody: 'Connectez votre compte WATI pour envoyer des rappels de séance et alertes portefeuille sur WhatsApp.',
          configureWhatsApp: 'Configurer WhatsApp',
          subtitle: 'Gérez votre académie avec un contrôle total sur les utilisateurs, sites et paramètres système.',
          totalStudents: 'Total élèves',
          activeBranches: 'Sites actifs',
          activeCoaches: 'Entraîneurs actifs',
          atRiskStudents: 'Élèves à risque',
          activeEnrollments: 'Inscriptions actives',
          addStudent: 'Ajouter un élève',
          addStudentDesc: 'Inscrire un nouvel élève à l\'académie',
          createBatch: 'Créer un groupe',
          createBatchDesc: 'Configurer un nouveau groupe de formation',
          walletTopup: 'Recharger le portefeuille',
          walletTopupDesc: 'Ajouter des fonds au portefeuille élève',
          manageCoaches: 'Gérer les entraîneurs',
          manageCoachesDesc: 'Voir et gérer le personnel d\'entraînement',
          viewSessions: 'Voir les séances',
          viewSessionsDesc: 'Gérer toutes les séances de formation',
          paymentReports: 'Rapports de paiement',
          paymentReportsDesc: 'Voir les encaissements financiers'
        },
        managerDashboard: {
          subtitle: 'Gérez les opérations efficacement. Consultez entraîneurs, élèves et performance des sites.',
          coaches: 'Entraîneurs',
          students: 'Élèves',
          manageBatches: 'Gérer les groupes',
          manageBatchesDesc: 'Voir et organiser les groupes de formation',
          enrollments: 'Inscriptions',
          enrollmentsDesc: 'Gérer les inscriptions des élèves',
          manageLevels: 'Gérer les niveaux',
          manageLevelsDesc: 'Configurer les niveaux',
          sessionStatement: 'Relevé de séances',
          sessionStatementDesc: 'Voir les rapports de séances détaillés',
          batchPayments: 'Présence par groupe',
          batchPaymentsDesc: 'Voir la matrice de présence par groupe'
        },
        coachDashboard: {
          subtitle: 'Votre journée en un coup d\'œil. Vérifiez les séances, marquez la présence et planifiez la semaine.',
          metricsFailed: 'Échec du chargement des indicateurs.',
          todayFailed: 'Échec du chargement des séances du jour.',
          upcomingFailed: 'Échec du chargement des séances à venir.',
          sessionsToday: 'Séances aujourd\'hui',
          studentsToday: 'Élèves aujourd\'hui',
          hoursThisWeek: 'Heures cette semaine',
          attendanceWtd: 'Présence (semaine)',
          noShowsToday: 'Absences aujourd\'hui',
          todaysSchedule: 'Planning du jour',
          viewAllSessions: 'Voir toutes les séances',
          noSessionsToday: 'Aucune séance prévue aujourd\'hui.',
          upcoming7Days: 'À venir (7 prochains jours)',
          noUpcomingSessions: 'Aucune séance à venir dans les 7 prochains jours.',
          viewAttendance: 'Voir la présence',
          markAttendance: 'Marquer la présence'
        },
        studentDashboard: {
          childOverview: 'Aperçu — {{name}}',
          childFallback: 'Enfant',
          childSubtitle: 'Résumé de présence, séances à venir et solde portefeuille pour votre élève lié.',
          subtitle: 'Suivez vos progrès, consultez les séances à venir et gérez votre portefeuille.',
          loadFailed: 'Échec du chargement du tableau de bord.',
          activeEnrollments: 'Inscriptions actives',
          attendanceRate: 'Taux de présence',
          upcoming7d: 'À venir (7 jours)',
          lastAttended: 'Dernière présence',
          upcomingSessions: 'Séances à venir',
          noUpcomingSessions: 'Aucune séance à venir.',
          recentAttendance: 'Présence récente',
          noAttendanceRecords: 'Aucun enregistrement de présence.'
        },
        parentHome: {
          loading: 'Chargement du portail parents…',
          portalLabel: 'Portail parents',
          hello: 'Bonjour, {{name}}',
          there: 'vous',
          approvedSubtitle: 'Suivez la présence, le planning et les frais d\'académie de votre enfant — tout en un seul endroit.',
          pendingSubtitle: 'Votre compte tuteur est en cours de configuration.',
          accessPending: 'Accès en attente',
          pendingDefault: 'Votre lien tuteur attend l\'approbation de l\'académie. Reconnectez-vous une fois qu\'un administrateur a approuvé votre demande.',
          noLinkedStudents: 'Aucun élève lié pour l\'instant. Demandez à l\'académie de vous envoyer une invitation tuteur.',
          yourChildren: 'Vos enfants',
          currentlyViewing: 'Affichage actuel',
          selectChildPrompt: 'Sélectionnez un enfant ci-dessus pour voir son résumé et les actions rapides.',
          quickActions: 'Actions rapides',
          more: 'Plus',
          otherLinks: 'Autres liens',
          helpFaq: 'Aide & FAQ',
          loadingChild: 'Chargement des données enfant…',
          loadFailed: 'Impossible de charger le portail parents.',
          pendingAccess: 'Votre accès tuteur est en attente d\'approbation ou n\'a pas encore été lié.',
          overview: 'Aperçu',
          overviewDesc: 'Présence & séances à venir',
          calendar: 'Calendrier',
          calendarDesc: 'Planning mensuel',
          wallet: 'Portefeuille',
          walletDesc: 'Solde & paiements',
          attendance: 'Présence',
          attendanceDesc: 'Historique des séances',
          enrollments: 'Inscriptions',
          sessions: 'Séances',
          notifications: 'Notifications',
          sessionStatement: 'Relevé de séances',
          statusPendingApproval: 'En attente d\'approbation',
          statusApproved: 'Actif',
          statusRejected: 'Rejeté',
          statusRevoked: 'Révoqué',
          selected: 'Sélectionné',
          guardian: 'Tuteur'
        },
        managerStudents: {
          title: 'Élèves',
          subtitle: 'Rechercher, consulter et gérer l\'accès des élèves avec une liste plus claire.',
          addStudent: 'Ajouter un élève',
          searchPlaceholder: 'Rechercher par nom, identifiant, e-mail, téléphone ou ID…',
          entityLabel: 'élèves',
          noMatch: 'Aucun élève ne correspond à votre recherche',
          empty: 'Aucun élève trouvé',
          removeConfirm: 'Retirer {{name}} de votre liste ?\n\nCela supprime uniquement votre lien direct responsable. L\'élève n\'est pas archivé et peut encore apparaître s\'il est inscrit dans vos sites.',
          removeFailed: 'Échec du retrait de l\'élève de la liste.',
          thisStudent: 'cet élève',
          entitySingular: 'élève'
        },
        managerCoaches: {
          title: 'Entraîneurs',
          subtitle: 'Voir les entraîneurs dans votre périmètre de sites',
          searchPlaceholder: 'Rechercher par nom, identifiant, e-mail, téléphone ou ID…',
          entityLabel: 'entraîneurs',
          noMatch: 'Aucun entraîneur ne correspond à votre recherche',
          empty: 'Aucun entraîneur trouvé dans le système'
        },
        coaches: {
          manageAssignments: {
            title: 'Gérer les affectations d\'élèves',
            coachLabel: 'Entraîneur : {{name}}',
            infoBanner: 'Les élèves affectés ici deviennent visibles pour cet entraîneur lors de l\'enregistrement de séances individuelles. Sans affectation, l\'entraîneur ne verra pas l\'élève dans la liste des séances — même s\'il est actif dans le système.',
            assignedStudents: 'Élèves affectés ({{count}})',
            availableStudents: 'Élèves disponibles ({{count}})',
            searchAssigned: 'Rechercher parmi les élèves affectés...',
            searchAvailable: 'Rechercher des élèves...',
            noAssigned: 'Aucun élève affecté pour l\'instant',
            noAssignedMatch: 'Aucun élève affecté ne correspond à votre recherche',
            noStudentsFound: 'Aucun élève trouvé',
            allAssigned: 'Tous les élèves sont affectés',
            assignButton: 'Affecter {{count}} élève',
            assignButton_other: 'Affecter {{count}} élèves',
            assigning: 'Affectation...',
            loadFailed: 'Échec du chargement des données',
            selectAtLeastOne: 'Veuillez sélectionner au moins un élève',
            assignSuccess: '{{count}} élève affecté avec succès',
            assignSuccess_other: '{{count}} élèves affectés avec succès',
            assignFailed: 'Échec de l\'affectation des élèves : {{message}}',
            removeConfirm: 'Retirer cette affectation d\'élève ?',
            removeSuccess: 'Affectation retirée avec succès',
            removeFailed: 'Échec du retrait de l\'affectation',
            assignedOn: 'Assigné : {{date}}'
          },
          title: 'Entraîneurs',
          subtitle: 'Gérez les fiches entraîneurs, invitations et affectations dans un espace moderne.',
          add: 'Ajouter un entraîneur',
          searchPlaceholder: 'Rechercher par nom, identifiant, e-mail, téléphone ou ID…',
          entityLabel: 'entraîneurs',
          colCoachName: 'Nom de l\'entraîneur',
          manageStudents: 'Gérer les élèves',
          manageStudentsTitle: 'Gérer les élèves affectés pour les séances individuelles'
        },
        managerBranches: {
          title: 'Mes sites',
          subtitle: 'Sites que vous gérez',
          searchPlaceholder: 'Rechercher par nom, code, téléphone, mode ou ID…',
          entityLabel: 'sites',
          unauthorized: 'Vous n\'êtes pas autorisé à voir les sites.',
          loadFailed: 'Échec du chargement des sites.',
          noMatch: 'Aucun site ne correspond à votre recherche',
          empty: 'Aucun site ne vous est encore assigné',
          emptyHint: 'Contactez un administrateur pour assigner des sites à votre compte'
        },
        levels: {
          title: 'Niveaux',
          managerSubtitle: 'Gérer les niveaux d\'échecs',
          coachSubtitle: 'Voir les niveaux d\'échecs',
          addLevel: 'Ajouter un niveau',
          searchPlaceholder: 'Rechercher par nom, site, description ou ID…',
          entityLabel: 'niveaux',
          loadFailed: 'Échec du chargement des niveaux.',
          noMatch: 'Aucun niveau ne correspond à votre recherche',
          empty: 'Aucun niveau trouvé',
          colLevelName: 'Nom du niveau',
          editLevel: 'Modifier le niveau',
          archiveLevel: 'Archiver le niveau',
          levelFallback: 'Niveau {{id}}',
          form: {
            levelName: 'Nom du niveau',
            branchForLevel: 'Site pour ce niveau',
            branchFixedNote: 'Le site est fixé après la création du niveau et ne peut pas être modifié.',
            loadBranchesFailed: 'Échec du chargement des sites',
            loadingBranches: 'Chargement des sites…'
          },
          subtitle: 'Gérer les niveaux d\'échecs',
          archiveConfirm: 'Archiver ce niveau ?',
          archiveFailed: 'Échec de l\'archivage du niveau.'
        },
        feePlans: {
          title: 'Forfaits tarifaires',
          subtitle: 'Forfaits par site et configuration de facturation',
          loadFailed: 'Échec du chargement des forfaits.',
          empty: 'Aucun forfait tarifaire trouvé.',
          allBranches: 'Tous les sites',
          addPlan: 'Ajouter un forfait',
          selectBranch: 'Sélectionner un site…',
          selectBranchHint: 'Sélectionnez un site pour voir les forfaits.',
          noPlans: 'Aucun forfait trouvé pour ce site.',
          archiveConfirm: 'Archiver ce forfait ? Il sera masqué de la sélection mais conservé pour l\'historique.',
          archiveFailed: 'Échec de l\'archivage du forfait.',
          activateFailed: 'Échec de l\'activation du forfait.',
          colPlan: 'Forfait n°',
          colBranch: 'Site',
          colType: 'Type',
          colGroup: 'Groupe',
          colRate: 'Tarif',
          colCredits: 'Crédits',
          colLevelScope: 'Portée niveau',
          archiveTitle: 'Archiver le forfait',
          activateTitle: 'Activer le forfait'
        },
        studentBulkImport: {
          title: 'Import groupé d\'élèves',
          subtitle: 'Téléversez un CSV pour importer plusieurs élèves à la fois. Téléchargez le modèle, remplissez les données et téléversez.',
          downloadTemplate: 'Télécharger le modèle',
          totalRows: 'Lignes totales',
          imported: 'Importé',
          skipped: 'Ignoré',
          failed: 'Échoué',
          allSuccess: 'Les {{count}} élèves ont été importés avec succès !',
          completedWithIssues: 'Import terminé avec quelques problèmes.',
          summaryIssues: '{{success}} importés · {{skipped}} ignorés (doublons) · {{failed}} échoués. Voir le tableau ci-dessous.',
          detailedReport: 'Rapport d\'import détaillé',
          importAnother: 'Importer un autre fichier',
          viewAllStudents: 'Voir tous les élèves',
          colNum: '#',
          colName: 'Nom de l\'élève',
          colUsername: 'Identifiant',
          colEmail: 'E-mail',
          colStatus: 'Statut',
          colReason: 'Raison / Note',
          statusImported: '✓ Importé',
          statusSkipped: '⊘ Ignoré',
          statusFailed: '✗ Échoué',
          step1: 'Paramètres d\'import',
          passwordStrategy: 'Stratégie de mot de passe',
          defaultPasswordOption: 'Définir un mot de passe par défaut — connexion immédiate',
          defaultPassword: 'Mot de passe par défaut',
          passwordPlaceholder: 'Minimum 6 caractères',
          passwordHint: 'Appliqué à toutes les lignes. Une colonne mot de passe propre a priorité.',
          step2: 'Téléverser le fichier CSV',
          rowsFound: '{{count}} ligne élève trouvée',
          rowsFound_other: '{{count}} lignes élève trouvées',
          replaceFile: 'Cliquer ou déposer un nouveau fichier',
          dropHint: 'Glissez-déposez votre CSV ici ou cliquez pour parcourir',
          csvOnly: 'Seuls les fichiers .csv sont acceptés',
          noTemplate: 'Pas de modèle ?',
          downloadHere: 'Télécharger ici',
          step3: 'Aperçu & Import',
          showingFirst: '5 premières lignes sur {{total}}',
          startImport: 'Démarrer l\'import',
          importing: 'Import en cours…',
          importFailed: 'Échec de l\'import. Veuillez réessayer.',
          selectFileFirst: 'Veuillez d\'abord téléverser un fichier CSV.',
          enterpriseSubtitle: 'L\'import CSV groupé est disponible uniquement avec la licence Enterprise.',
          enterpriseFeature: 'Fonction Enterprise',
          enterpriseBody: 'L\'import groupé d\'élèves par CSV est réservé à Enterprise. Votre licence actuelle n\'inclut pas cette fonctionnalité.',
          enterpriseHint: 'Sur votre formule actuelle, utilisez le formulaire <1>{{addStudentLink}}</1> pour inscrire les élèves un par un. Contactez votre fournisseur logiciel pour passer à Enterprise.',
          addStudentLink: 'Ajouter un élève',
          csvOnlyAlert: 'Veuillez sélectionner un fichier CSV (.csv).\n\nPour Excel, enregistrez d\'abord au format « CSV (délimité par des virgules) ».',
          noDataRowsAlert: 'Aucune ligne de données trouvée dans le fichier. Vérifiez le format du fichier.'
        },
        studentImport: {
          title: 'Import élèves depuis Google Sheets',
          loadingSubtitle: 'Chargement de la configuration d\'import…',
          subtitle: 'Importe automatiquement les nouveaux élèves depuis Google Sheets chaque jour à 2h00 (heure locale de l\'académie)',
          loadFailed: 'Échec du chargement du statut d\'import',
          importStatus: 'Statut d\'import',
          spreadsheetId: 'ID feuille de calcul',
          sheetName: 'Nom de la feuille',
          lastProcessedRow: 'Dernière ligne traitée',
          totalImported: 'Total importé',
          lastImportTime: 'Dernier import',
          lastUpdated: 'Dernière mise à jour',
          lastError: 'Dernière erreur',
          actions: 'Actions',
          triggerImport: 'Déclencher l\'import maintenant',
          processing: 'Traitement…',
          resetTracker: 'Réinitialiser le suivi',
          refreshStatus: 'Actualiser le statut',
          triggerDesc: 'Déclencher manuellement l\'import pour vérifier immédiatement les nouveaux élèves. Seules les lignes après la dernière traitée sont importées.',
          resetDesc: 'Danger : réinitialise le suivi à la ligne 0. À utiliser uniquement pour réimporter tous les élèves.',
          refreshDesc: 'Recharger le statut d\'import actuel.',
          confirmTrigger: 'Déclencher l\'import des élèves maintenant ?',
          confirmReset: '⚠️ ATTENTION : le suivi sera réinitialisé et TOUS les élèves seront réimportés. Continuer ?',
          triggerSuccess: 'Import déclenché avec succès ! Actualisez pour voir les résultats.',
          resetSuccess: 'Suivi réinitialisé avec succès !',
          triggerFailed: 'Échec du déclenchement de l\'import',
          resetFailed: 'Échec de la réinitialisation du suivi',
          configTitle: 'Configuration',
          setupTitle: 'Guide d\'installation',
          configDaily: 'Import automatique quotidien à <strong>2h00 (heure locale de l\'académie)</strong>',
          configNewRows: 'Seules les nouvelles lignes (après la dernière traitée) sont importées',
          configRoleEmail: 'Chaque élève est créé avec le rôle STUDENT et reçoit un e-mail d\'invitation',
          configUsername: 'Le nom d\'utilisateur est généré automatiquement à partir de l\'e-mail ou du nom',
          configMappings: 'Configurer les mappings de colonnes dans <code>application.yml</code>',
          configEnv: 'Activer/désactiver via la variable d\'environnement <code>STUDENT_IMPORT_ENABLED</code>',
          setupStep1: 'Créer un projet Google Cloud et activer l\'API Google Sheets',
          setupStep2: 'Créer un compte de service et télécharger le JSON d\'identifiants',
          setupStep3: 'Partager votre feuille Google avec l\'e-mail du compte de service',
          setupStep4: 'Placer le fichier d\'identifiants sous <code>/app/config/google-credentials.json</code>',
          setupStep5: 'Définir <code>STUDENT_IMPORT_ENABLED=true</code> dans l\'environnement',
          setupStep6: 'Ajuster les mappings de colonnes dans application.yml si la structure de la feuille diffère',
          bulkImportButton: 'Import groupé',
          bulkImportTitle: 'Importer de nouveaux élèves depuis Google Sheets',
          bulkImporting: 'Import en cours…',
          bulkImportConfirm: 'Importer des élèves depuis Google Sheets ?\n\nLes nouvelles inscriptions seront récupérées depuis les Google Forms liés.\nSeules les lignes ajoutées depuis le dernier import seront traitées.',
          bulkImportTriggered: 'Import déclenché. Actualisez la liste dans un instant pour voir les nouveaux élèves.',
          bulkImportFailed: 'Échec de l\'import des élèves. Vérifiez que l\'import est activé sur le serveur.'
        },
        feePlanForm: {
          title: 'Ajouter un forfait',
          subtitle: 'Créer un nouveau forfait pour un site.',
          branchLabel: 'Site du forfait',
          codeLabel: 'Code forfait',
          typeLabel: 'Type de forfait',
          groupTypeLabel: 'Type de groupe d\'élèves',
          defaultRate: 'Tarif par défaut',
          defaultCredits: 'Crédits de séance par défaut',
          levelMin: 'Niveau minimum',
          levelMax: 'Niveau maximum',
          carryForward: 'Présence requise',
          carryForwardEnabled: 'Activé : pas de facturation si ABSENT.',
          carryForwardDisabled: 'Désactivé : facturation que l\'élève soit PRÉSENT ou ABSENT.',
          creditExpiry: 'Expiration des crédits (jours)',
          notes: 'Notes du forfait',
          active: 'Forfait actif',
          selectBranchAlert: 'Sélectionnez un site.',
          codeRequired: 'Le code est requis.',
          createFailed: 'Échec de la création du forfait.',
          saving: 'Enregistrement…',
          createPlan: 'Créer le forfait'
        },
        enrollmentDetail: {
          title: 'Inscription #{{id}}',
          subtitle: 'Voir les détails et la facturation de l\'inscription.',
          loadingTitle: 'Détails de l\'inscription',
          notFound: 'Introuvable',
          loadFailed: 'Échec du chargement de l\'inscription',
          activeExpired: 'Actif (expiré)',
          renew: 'Renouveler',
          activateConfirm: 'Activer l\'inscription #{{id}} ?',
          archiveFailed: 'Échec de l\'archivage.',
          basicInfo: 'Informations de base',
          enrollmentPeriod: 'Période d\'inscription',
          enrolledDate: 'Date d\'inscription',
          openEnded: 'Sans date de fin',
          pricingBilling: 'Tarifs & facturation',
          customCharges: 'Frais d\'inscription personnalisés',
          batchCharges: 'Frais par défaut du groupe',
          billingPeriod: 'Période de facturation',
          periodFee: 'Frais de période',
          notConfigured: 'Non configuré',
          hourlyRate: 'Tarif horaire',
          perSessionCharge: 'Frais par séance',
          afterDiscount: 'Après {{pct}} % de remise',
          enrollmentOverride: 'Override inscription',
          carryForward: 'Report',
          enabled: 'Activé',
          disabled: 'Désactivé',
          followsBatch: 'Suit la politique du groupe',
          discountApplied: 'Remise appliquée',
          noDiscount: 'Aucune remise appliquée',
          paymentReceiver: 'Bénéficiaire du paiement',
          receiverType: 'Type de bénéficiaire',
          receiverName: 'Nom du bénéficiaire',
          additionalInfo: 'Informations supplémentaires',
          enrollmentType: 'Type d\'inscription',
          enrolledBy: 'Inscrit par',
          createdAt: 'Créé le'
        },
        discountForm: {
          createTitle: 'Créer une remise',
          editTitle: 'Modifier la remise',
          createSubtitle: 'Ajouter un code remise pour les inscriptions.',
          editSubtitle: 'Mettre à jour les détails et la portée de la remise.',
          loadFailed: 'Échec du chargement de la remise',
          codeLabel: 'Code remise',
          nameLabel: 'Nom de la remise',
          descriptionLabel: 'Description',
          typeLabel: 'Type de remise',
          valueLabel: 'Valeur de remise',
          branchLabel: 'Site (optionnel)',
          levelLabel: 'Niveau (optionnel)',
          batchLabel: 'Groupe (optionnel)',
          activeLabel: 'Actif',
          allBranches: 'Tous les sites',
          selectBranch: 'Sélectionnez d\'abord un site',
          selectLevel: 'Sélectionnez d\'abord un niveau',
          saveFailed: 'Échec de l\'enregistrement de la remise',
          saving: 'Enregistrement…',
          create: 'Créer la remise',
          update: 'Mettre à jour la remise',
          codeRequired: 'Le code remise est requis',
          nameRequired: 'Le nom est requis',
          valueRequired: 'La valeur est requise',
          optionalDescription: 'Description facultative',
          percentOption: 'Pourcentage (%)',
          fixedOption: 'Montant fixe ({{currency}})',
          scopeTitle: 'Portée de la remise (facultatif)',
          scopeHint: 'Laisser tout vide pour une remise globale, ou restreindre à un site, niveau ou groupe.',
          percentMaxAlert: 'La remise en pourcentage ne peut pas dépasser 100 %',
          updateSuccess: 'Remise mise à jour avec succès',
          createSuccess: 'Remise créée avec succès'
        },
        userDetails: {
          title: 'Détails utilisateur',
          loadFailed: 'Échec du chargement des détails.',
          notFound: 'Utilisateur introuvable',
          editUser: 'Modifier l\'utilisateur',
          backToList: 'Retour à la liste',
          personalInfo: 'Informations personnelles',
          contactInfo: 'Coordonnées',
          accountInfo: 'Informations du compte',
          address: 'Adresse',
          noAddress: 'Aucune adresse enregistrée',
          dateOfBirth: 'Date de naissance',
          gender: 'Genre',
          memberSince: 'Membre depuis',
          lastLogin: 'Dernière connexion',
          walletBalance: 'Solde portefeuille',
          activeEnrollments: 'Inscriptions actives',
          totalSessions: 'Séances totales',
          attendanceRate: 'Taux de présence',
          guardians: 'Tuteurs',
          noGuardians: 'Aucun tuteur lié',
          roles: 'Rôles',
          username: 'Identifiant',
          userId: 'ID utilisateur',
          coachSummary: 'Résumé entraîneur'
        },
        coachStudents: {
          title: 'Élèves',
          subtitle: 'Élèves de votre liste — de vos séances et assignations admin',
          searchPlaceholder: 'Rechercher par nom, identifiant, e-mail, téléphone ou ID…',
          entityLabel: 'élèves',
          loadFailed: 'Échec du chargement des élèves.',
          empty: 'Aucun élève trouvé.',
          noMatch: 'Aucun élève ne correspond à votre recherche'
        },
        studentEnrollments: {
          title: 'Mes inscriptions',
          subtitle: 'Voir vos inscriptions de cours actives',
          loadFailed: 'Échec du chargement des inscriptions.',
          searchPlaceholder: 'Rechercher par niveau, groupe ou date…',
          entityLabel: 'inscriptions',
          noMatch: 'Aucune inscription ne correspond à votre recherche',
          empty: 'Aucune inscription pour l\'instant',
          payOnline: 'Payer en ligne'
        },
        studentSessions: {
          title: 'Mes séances',
          subtitle: 'Voir et rechercher vos séances planifiées et statut de présence.',
          loadFailed: 'Échec du chargement des séances.',
          searchPlaceholder: 'Rechercher… (niveau, groupe, entraîneur, statut, présence)',
          empty: 'Aucune séance trouvée.',
          noMatch: 'Aucune séance ne correspond à votre recherche.'
        },
        studentAttendance: {
          title: 'Ma présence',
          parentTitle: 'Présence',
          subtitle: 'Historique de présence aux séances pour tous les groupes',
          loadFailed: 'Échec du chargement de la présence.',
          searchPlaceholder: 'Rechercher par statut, niveau, groupe, entraîneur ou horaire…',
          entityLabel: 'enregistrements',
          noMatch: 'Aucun enregistrement ne correspond à votre recherche',
          empty: 'Aucun enregistrement de présence trouvé'
        },
        wallet: {
          title: 'Mon portefeuille',
          childTitle: 'Portefeuille enfant',
          subtitle: 'Voir votre solde, ajouter des fonds et consulter l\'historique des transactions.',
          childSubtitle: 'Voir le solde, recharger en ligne et consulter l\'historique des transactions de votre enfant.',
          balanceLabel: 'Solde de votre portefeuille',
          balanceHint: 'Le solde est utilisé automatiquement pour payer les séances.',
          onlineUnavailableTitle: 'Paiements en ligne indisponibles',
          onlineUnavailableDefault: 'La licence de votre académie n\'inclut pas les passerelles de paiement en ligne. Contactez l\'académie ou payez par un autre moyen.',
          loadFailed: 'Échec du chargement du portefeuille. Veuillez actualiser la page.',
          enrollmentPaying: 'Paiement pour l\'inscription #{{id}}. Les fonds seront crédités sur votre portefeuille et liés à cette inscription.',
          confirmingPayment: 'Confirmation de votre paiement…',
          addMoney: 'Ajouter des fonds',
          addMoneyHint: 'Choisissez un moyen de paiement et finalisez en toute sécurité. Les fonds apparaissent après confirmation.',
          enterAmount: 'Saisir le montant',
          securePayments: '🔒 Paiements sécurisés via {{gateway}}',
          topupUnavailable: 'La recharge en ligne n\'est pas disponible pour le moment. Contactez votre académie.',
          transactionHistory: 'Historique des transactions',
          noTransactions: 'Aucune transaction pour l\'instant.',
          downloadReceipt: 'Télécharger le reçu',
          invalidAmount: 'Veuillez saisir un montant valide (minimum {{symbol}}1).',
          selectPaymentMethod: 'Veuillez sélectionner un moyen de paiement.',
          invalidEnrollmentLink: 'Lien d\'inscription invalide. Rouvrez Payer en ligne depuis Mes inscriptions.',
          paymentConfirmFailed: 'Échec de la confirmation du paiement. Contactez l\'académie.',
          paymentStartFailed: 'Impossible de démarrer le paiement. Veuillez réessayer.',
          paymentUpgradeRequired: 'Les paiements en ligne nécessitent une mise à niveau de licence.',
          paymentVerifyFailed: 'Échec de la vérification du paiement. Contactez l\'académie.',
          receiptDownloadFailed: 'Impossible de télécharger le reçu. Veuillez réessayer.',
          razorpayTitle: 'Recharger le portefeuille',
          razorpayDesc: 'Ajouter des fonds à votre portefeuille académie',
          txnAdvanceTopup: 'Acompte',
          txnOnlineTopup: 'Recharge en ligne',
          txnEnrollmentPayment: 'Paiement d\'inscription',
          txnSessionCharge: 'Débit séance',
          txnSessionReversal: 'Annulation séance',
          txnDiscount: 'Crédit remise',
          txnRefund: 'Remboursement',
          txnApplied: 'Appliqué à la facture',
          txnDefault: 'Transaction',
          payNow: 'Payer maintenant',
          redirecting: 'Redirection…',
          ref: 'Réf'
        },
        sessionStatement: {
          eyebrow: 'Rapports',
          title: 'Relevé de séances élève',
          subtitle: 'Historique complet avec présence, frais, crédits et débits portefeuille, et solde courant — pour l\'élève et la période sélectionnés.',
          embeddedSubtitle: 'Historique complet avec présence, frais, crédits & débits portefeuille, et solde courant — pour l\'élève et la période sélectionnés.',
          filters: 'Filtres',
          selectStudent: 'Sélectionner un élève',
          chooseStudent: 'Choisir un élève…',
          fromDate: 'Date de début',
          toDate: 'Date de fin',
          generate: 'Générer le relevé',
          generating: 'Chargement…',
          selectStudentError: 'Veuillez sélectionner un élève',
          loadFailed: 'Échec du chargement du relevé de séances',
          exportPdfFailed: 'Échec de l\'export PDF',
          student: 'Élève',
          statementPeriod: 'Période du relevé',
          openingBalance: 'Solde d\'ouverture',
          availableAtStart: 'Disponible au début',
          walletCredits: 'Crédits portefeuille',
          transactionsCount: '{{count}} transactions',
          sessionCharges: 'Frais de séances',
          sessionsPresentAbsent: '{{present}}P / {{absent}}A séances',
          closingBalance: 'Solde de clôture',
          currentBalance: 'Solde actuel',
          walletTransactions: 'Transactions portefeuille',
          walletTransactionsHint: 'Recharges et crédits ajoutés à votre portefeuille',
          sessionHistory: 'Historique des séances',
          sessionHistoryHint: 'Vue détaillée de toutes les séances suivies durant cette période',
          noSessionsPeriod: 'Aucune séance trouvée pour cette période',
          discount: 'Remise : {{amount}}',
          emailLabel: 'Saisir l\'e-mail du destinataire',
          present: 'Présent',
          absent: 'Absent'
        },
        notifications: {
          title: 'Notifications WhatsApp',
          subtitle: 'Choisissez quels messages de l\'académie vous recevez sur WhatsApp. Votre numéro doit figurer sur votre profil.',
          subtitleShort: 'Choisissez quels messages de l\'académie vous recevez sur WhatsApp.',
          loadFailed: 'Impossible de charger les préférences de notification.',
          saveFailed: 'Échec de l\'enregistrement.',
          saved: 'Préférences enregistrées.',
          optInTitle: 'Recevoir des messages WhatsApp de mon académie',
          optInHint: 'Activé par défaut. Désactivez pour arrêter toutes les notifications WhatsApp.',
          sessionReminders: 'Rappels de séance',
          absenceAlerts: 'Alertes d\'absence',
          lowBalance: 'Alertes de solde portefeuille bas',
          feeReminders: 'Rappels de frais / solde impayé',
          sessionCancelled: 'Avis de séance annulée',
          holidayAnnouncements: 'Annonces de jours fériés',
          tryAgain: 'Réessayer',
          unavailable: 'Indisponible',
          savePreferences: 'Enregistrer les préférences',
          saving: 'Enregistrement…'
        },
        parentNotifications: {
          title: 'Notifications WhatsApp enfant',
          subtitle: 'Gérer les alertes WhatsApp envoyées au numéro de téléphone de votre enfant sur son profil.',
          saved: 'Préférences enregistrées pour votre enfant.',
          optInTitle: 'Activer WhatsApp pour cet élève',
          optInHint: 'Les alertes vont au numéro de téléphone sur le profil de votre enfant.'
        },
        profile: {
          title: 'Mon profil',
          subtitle: 'Mettre à jour vos informations personnelles et d\'adresse',
          loadFailed: 'Échec du chargement du profil',
          requestDataTitle: 'Demander mes données',
          requestDataBody: 'Téléchargez une copie lisible par machine de votre profil, ou demandez aux administrateurs un export plus complet (accès / portabilité).',
          downloadData: 'Télécharger mes données (JSON)',
          downloadDataDone: 'Votre paquet de données a été téléchargé.',
          downloadDataFailed: 'Impossible de télécharger le paquet de données.',
          requestData: 'Demander mes données',
          requestSent: 'Demande envoyée.',
          requestFailed: 'Impossible d\'envoyer la demande. Réessayez plus tard.',
          changePassword: 'Changer le mot de passe',
          currentPassword: 'Mot de passe actuel',
          newPassword: 'Nouveau mot de passe',
          confirmPassword: 'Confirmer le nouveau mot de passe',
          passwordMinHint: 'Minimum 8 caractères',
          profileUpdated: 'Profil mis à jour',
          profileUpdateFailed: 'Échec de la mise à jour du profil',
          passwordChanged: 'Mot de passe changé avec succès.',
          passwordChangeFailed: 'Échec du changement de mot de passe. Veuillez réessayer.',
          enterCurrentPassword: 'Veuillez saisir votre mot de passe actuel.',
          passwordMinLength: 'Le nouveau mot de passe doit contenir au moins 8 caractères.',
          passwordsMismatch: 'Les nouveaux mots de passe ne correspondent pas.',
          passwordSameAsCurrent: 'Le nouveau mot de passe doit être différent de l\'actuel.',
          firstNameRequired: 'Le prénom est requis et peut contenir des lettres, espaces, tirets et apostrophes.',
          middleNameLetters: 'Le deuxième prénom peut contenir des lettres, espaces, tirets et apostrophes.',
          firstNameLetters: 'Le prénom peut contenir des lettres, espaces, tirets et apostrophes — pas de chiffres.',
          lastNameLetters: 'Le nom peut contenir des lettres, espaces, tirets et apostrophes — pas de chiffres.',
          lastNameRequired: 'Le nom est requis et peut contenir des lettres, espaces, tirets et apostrophes.',
          invalidDob: 'Veuillez saisir une date de naissance valide dans le passé.',
          invalidAddress: 'Adresse invalide.',
          invalidLatitude: 'La latitude doit être un nombre valide.',
          invalidLongitude: 'La longitude doit être un nombre valide.',
          saveChanges: 'Enregistrer les modifications',
          saving: 'Enregistrement…',
          sending: 'Envoi…',
          updating: 'Mise à jour…',
          updatePassword: 'Mettre à jour le mot de passe',
          retry: 'Réessayer',
          preferredLanguage: 'Langue préférée',
          preferredLanguageHint: 'Utilisée pour l’interface et pour les e-mails et notifications qui vous sont envoyés.',
          lang_en: 'English',
          lang_de: 'Deutsch',
          lang_fr: 'Français',
          lang_nl: 'Nederlands'
        },
        students: {
          title: 'Élèves',
          subtitle: 'Rechercher, consulter et gérer l\'accès des élèves avec une liste plus claire.',
          add: 'Ajouter un élève',
          bulkImport: 'Import groupé',
          searchPlaceholder: 'Rechercher par nom, identifiant, e-mail, téléphone ou ID…',
          entityLabel: 'élèves',
          exportFailed: 'Échec de l\'export des élèves. Veuillez réessayer.',
          colStudentName: 'Nom de l\'élève'
        },
        managers: {
          title: 'Responsables',
          subtitle: 'Gérer les utilisateurs admin avec de meilleurs filtres, une hiérarchie claire et des actions cohérentes.',
          add: 'Ajouter un responsable',
          searchPlaceholder: 'Rechercher par nom, identifiant, e-mail, téléphone ou ID…',
          entityLabel: 'responsables',
          colManagerName: 'Nom du responsable'
        },
        branches: {
          title: 'Sites',
          subtitle: 'Gérer les sites de l\'académie, archiver les anciens emplacements et garder les opérations organisées.',
          add: 'Ajouter un site',
          searchPlaceholder: 'Rechercher par nom, code, téléphone, responsable ou ID…',
          entityLabel: 'sites',
          loadFailed: 'Échec du chargement des sites.',
          archiveConfirm: 'Archiver ce site ? Il sera désactivé mais ses données seront conservées.',
          archiveFailed: 'Échec de l\'archivage du site.',
          activateFailed: 'Échec de l\'activation du site.',
          managerFallback: 'Responsable',
          colBranchName: 'Nom du site',
          colCode: 'Code',
          colMode: 'Mode',
          colManagedBy: 'Géré par',
          editTitle: 'Modifier le site',
          archiveTitle: 'Archiver le site',
          activateTitle: 'Activer le site'
        },
        users: {
          title: 'Utilisateurs',
          subtitle: 'Gérer les comptes utilisateurs et les permissions',
          add: 'Ajouter un utilisateur',
          entityLabel: 'utilisateurs'
        },
        discounts: {
          title: 'Gestion des remises',
          subtitle: 'Gérer les codes remise et offres',
          create: 'Créer une remise',
          empty: 'Aucune remise trouvée',
          emptyHint: 'Commencez par créer un nouveau code remise.',
          loadFailed: 'Échec du chargement des remises',
          archiveConfirm: 'Archiver cette remise ?',
          activateConfirm: 'Activer cette remise ?',
          archiveSuccess: 'Remise archivée avec succès',
          activateSuccess: 'Remise activée avec succès',
          archiveFailed: 'Échec de l\'archivage de la remise',
          activateFailed: 'Échec de l\'activation de la remise',
          colDiscountName: 'Nom de la remise',
          colValue: 'Valeur',
          colScope: 'Portée',
          scopeBranch: 'Spécifique au site',
          scopeLevel: 'Spécifique au niveau',
          scopeBatch: 'Spécifique au groupe',
          scopeGlobal: 'Global',
          typePercent: 'Pourcentage',
          typeFixed: 'Montant fixe',
          editTitle: 'Modifier la remise',
          archiveTitle: 'Archiver la remise',
          activateTitle: 'Activer la remise'
        },
        atRisk: {
          eyebrow: 'Rapports',
          title: 'Élèves à risque',
          subtitle: 'Élèves dont la présence est sous votre seuil — contactez-les avant qu\'ils abandonnent.',
          daysLabel: 'Jours à examiner',
          daysSuffix: 'jours',
          thresholdLabel: 'Présence minimale (%)',
          thresholdSuffix: '% de présence',
          loadFailed: 'Échec du chargement des élèves à risque. Veuillez réessayer.',
          daysError: 'Les jours doivent être entre 1 et 365.',
          thresholdError: 'Le seuil doit être entre 1 et 100.',
          riskCritical: 'Critique',
          riskHigh: 'Élevé',
          riskMedium: 'Moyen',
          tierCritical: '{{count}} critique',
          tierHigh: '{{count}} élevé',
          tierMedium: '{{count}} moyen',
          emptyTitle: 'Aucun élève à risque',
          emptyDescription: 'Tous les élèves activement inscrits ont une présence supérieure à {{threshold}} % sur les {{days}} derniers jours.',
          colStudent: 'Élève',
          colRisk: 'Risque',
          colAttendanceRate: 'Taux de présence',
          colSessions: 'Séances',
          colLastSeen: 'Dernière présence',
          colBatches: 'Groupes',
          profile: 'Profil',
          emailAction: 'E-mail',
          footer: '{{count}} élève(s) avec <{{threshold}} % de présence sur les {{days}} derniers jours. Les élèves sans séance dans la période sont exclus.',
          studentFallback: 'Élève #{{id}}',
          coachFallback: 'Entraîneur #{{id}}'
        },
        auditLogs: {
          title: 'Journaux d\'audit',
          subtitle: 'Afficher et suivre les activités système',
          filterUser: 'Utilisateur',
          filterTable: 'Table',
          filterOperation: 'Opération',
          filterStartDate: 'Date de début',
          filterEndDate: 'Date de fin',
          filterSearch: 'Recherche',
          allUsers: 'Tous les utilisateurs',
          allTables: 'Toutes les tables',
          allOperations: 'Toutes les opérations',
          searchPlaceholder: 'Rechercher dans les anciennes/nouvelles données…',
          clearFilters: 'Effacer les filtres',
          exportCsv: 'Exporter en CSV',
          fetchFailed: 'Échec du chargement des journaux d\'audit',
          exportFailed: 'Échec de l\'export des journaux d\'audit',
          empty: 'Aucun journal d\'audit trouvé',
          colTable: 'Table',
          colOperation: 'Opération',
          colRecordId: 'ID enregistrement',
          colTimestamp: 'Horodatage',
          oldData: 'Anciennes données :',
          newData: 'Nouvelles données :',

          tables: {
            users: 'Utilisateurs',
            sessions: 'Séances',
            session: 'Séance',
            batches: 'Groupes',
            batch: 'Groupe',
            branches: 'Sites',
            address: 'Adresse',
            levels: 'Niveaux',
            enrollment: 'Inscription',
            attendance: 'Présence',
            fee_plan: 'Forfait',
            discount_master: 'Remise',
            wallet_txn: 'Transaction portefeuille',
            billing_settings: 'Paramètres de facturation',
            coach_student_assignment: 'Affectation entraîneur–élève',
            session_participants: 'Participants à la séance',
            password_reset_tokens: 'Jetons de réinitialisation',
            academy_holiday: 'Congé académie',
            email_queue: 'File e-mail',
            whatsapp_queue: 'File WhatsApp',
            auth: 'Authentification'
          },
          ops: {
            INSERT: 'Insertion',
            UPDATE: 'Mise à jour',
            DELETE: 'Suppression',
            LOGIN: 'Connexion',
            LOGOUT: 'Déconnexion',
            PASSWORD_SET: 'Mot de passe défini',
            PASSWORD_RESET: 'Réinitialisation du mot de passe',
            BULK_UPDATE: 'Mise à jour groupée',
            ARCHIVE: 'Archivage',
            ACTIVATE: 'Activation',
            DEACTIVATE: 'Désactivation'
          },
          showingEntries: 'Affichage de {{start}} à {{end}} sur {{total}} entrées'
        },
        walletTopup: {
          eyebrow: 'Frais & facturation',
          title: 'Recharger le portefeuille',
          subtitle: 'Ajouter des fonds au portefeuille d\'un élève. Ces fonds peuvent être utilisés pour les frais de séance, avec déduction automatique après chaque séance.',
          walletBadge: 'Portefeuille',
          selectStudent: 'Sélectionner un élève',
          searchPlaceholder: 'Rechercher un élève par nom…',
          noStudents: 'Aucun élève trouvé',
          change: 'Changer',
          studentId: 'ID élève : {{id}}',
          idLabel: 'ID : {{id}}',
          archivedWarning: 'Cet élève n\'est pas actif dans le système pour l\'instant. Les recharges peuvent quand même être enregistrées.',
          archivedAlertTitle: 'Élève archivé',
          archivedAlertMessage: '{{name}} n\'est pas actif dans le système pour l\'instant.\n\nCet élève a été archivé. Vous pouvez encore enregistrer une recharge, mais il ne pourra pas se connecter ni utiliser les fonctions élève tant qu\'un admin ne réactive pas son compte.',
          addFunds: 'Ajouter des fonds',
          enterPaymentDetails: 'Saisissez les détails de paiement ci-dessous',
          amount: 'Montant',
          paymentMode: 'Mode de paiement',
          paidOn: 'Payé le',
          paidOnHint: 'Aujourd\'hui ou une date antérieure uniquement',
          reference: 'Référence / ID transaction',
          referenceOptional: '(Facultatif)',
          paymentReceivedBy: 'Paiement reçu par',
          staffMember: 'Membre du personnel',
          otherPerson: 'Autre personne',
          selectStaff: 'Sélectionner le personnel',
          chooseStaff: '-- Choisir un membre du personnel --',
          receiverName: 'Nom du bénéficiaire',
          receiverNamePlaceholder: 'Saisir le nom complet du bénéficiaire',
          clearForm: 'Effacer le formulaire',
          recordPayment: 'Enregistrer le paiement',
          processing: 'Traitement…',
          chooseStudent: 'Choisir un élève',
          amountRequired: 'Le montant doit être supérieur à 0',
          paidOnRequired: 'Choisir la date de paiement',
          paidOnFuture: 'La date de paiement ne peut pas être dans le futur',
          paidOnInvalid: 'Saisir une date de paiement valide',
          receiverTypeInvalid: 'Sélectionner un type de bénéficiaire valide (USER/OTHERS).',
          staffRequired: 'Sélectionner le membre du personnel ayant reçu le paiement.',
          receiverNameRequired: 'Saisir le nom du bénéficiaire.',
          success: '✅ Recharge réussie ! {{amount}} crédités au portefeuille de l\'élève.',
          failed: 'Échec de l\'enregistrement de la recharge.'
        }
      },
      cookie: {
        body: 'Cette application utilise uniquement des cookies essentiels — connexion et session. Pas de suivi, pas de publicité.',
        gotIt: 'J\'ai compris',
        privacy: 'Politique de confidentialité',
        terms: 'Conditions d\'utilisation',
        and: 'et',
        ariaLabel: 'Consentement aux cookies'
      },
      enrollments: {
        title: 'Inscriptions',
        subtitle: 'Gérer les inscriptions, frais de période et renouvellements',
        newEnrollment: 'Nouvelle inscription',
        editEnrollment: 'Modifier l\'inscription',
        formSubtitle: 'Inscrire un élève à un groupe avec plan tarifaire et options de facturation.',
        validation: {
          loadEnrollmentFailed: 'Échec du chargement des données d\'inscription',
          loadFailed: 'Échec du chargement des données',
          selectAtLeastOneStudent: 'Veuillez sélectionner au moins un élève',
          carryForwardRequired: 'Une décision de report est requise pour ce groupe',
          endDateRequired: 'La date de fin est requise pour les inscriptions en facturation périodique',
          saveFailed: 'Échec de l\'enregistrement de l\'inscription'
        },
        entityLabel: 'inscriptions',
        searchPlaceholder: 'Rechercher inscriptions par ID, nom d\'élève, groupe ou niveau...',
        noMatch: 'Aucune inscription ne correspond à votre recherche',
        empty: 'Aucune inscription trouvée',
        emptyHint: 'Essayez d\'ajuster vos termes de recherche',
        filters: {
          all: 'Toutes',
          active: 'Actives',
          archived: 'Archivées'
        },
        columns: {
          id: 'ID',
          student: 'Élève',
          level: 'Niveau',
          batch: 'Groupe',
          billing: 'Facturation',
          period: 'Période',
          payment: 'Paiement',
          expiry: 'Expiration',
          status: 'Statut',
          actions: 'Actions'
        },
        periodOngoing: 'en cours',
        expiresToday: 'Expire aujourd\'hui',
        daysLeft: '{{count}} j restants',
        expired: 'Expirée',
        notApplicable: 'N/A',
        renew: 'Renouveler',
        renewTitle: 'Créer l\'inscription pour la période suivante',
        view: 'Voir',
        viewTitle: 'Voir les détails de l\'inscription',
        edit: 'Modifier',
        editTitle: 'Modifier l\'inscription',
        archive: 'Archiver',
        activate: 'Activer',
        reactivateConfirm: 'Réactiver l\'inscription #{{id}} ? Possible uniquement si aujourd\'hui est dans la période d\'inscription.',
        archiveFailed: 'Échec de l\'archivage.',
        cannotArchive: 'Vous ne pouvez pas archiver cette inscription.',
        crossBranchWarning: 'Avertissement inter-sites',
        crossBranchEnrollment: 'Inscription #{{id}}',
        crossBranchDesc: '<strong>{{name}}</strong> est aussi activement inscrit(e) dans des sites <span class="text-orange-600 font-medium">non gérés par vous</span> :',
        continueArchive: 'Continuer l\'archivage',
        statusActive: 'Active',
        statusArchived: 'Archivée',
        form: {
          student: 'Élève',
          selectStudents: 'Sélectionner des élèves ({{count}} sélectionné(s))',
          searchStudents: 'Rechercher des élèves...',
          selectAll: 'Tout sélectionner ({{count}})',
          clear: 'Effacer',
          level: 'Niveau',
          trainingBatch: 'Groupe de formation',
          select: 'Sélectionner...',
          startDate: 'Date de début d\'inscription',
          startDateHint: 'Date à laquelle l\'élève rejoint officiellement ce groupe',
          endDate: 'Date de fin d\'inscription',
          optional: '(Optionnel)',
          endDatePeriodHint: 'Par défaut du plan du groupe ({{interval}}) ; ajustable.',
          endDateOpenHint: 'Laisser vide pour une inscription sans date de fin',
          carryForwardPolicy: 'Politique de report de séances',
          batchPolicy: 'Politique du groupe :',
          setCarryForward: 'Définir le report pour cet élève :',
          enableCarryForward: 'Activer le report',
          disableCarryForward: 'Désactiver le report',
          carryForwardRequired: 'Ce groupe exige de définir explicitement la politique de report pour chaque élève.',
          carryForwardBatchLevel: 'Le report est contrôlé au niveau du groupe',
          overrideBilling: 'Remplacer les frais de facturation (Optionnel)',
          hide: 'Masquer',
          show: 'Afficher',
          billingMode: 'Mode de facturation d\'inscription',
          billingPeriod: 'Période de facturation',
          discountOff: '{{value}} de réduction',
          periodFee: 'Frais de période',
          perSessionCharge: 'Frais par séance',
          hourlyRate: 'Tarif horaire',
          periodFeeAmount: 'Montant des frais de période',
          useBatchDefault: 'Utiliser le défaut du groupe',
          notConfigured: 'Non configuré',
          pricingSummary: 'Résumé tarifaire',
          batchDefaultPricing: 'Tarifs par défaut du groupe',
          chargesMode: 'Mode de facturation :',
          perSession: 'Par séance :',
          periodPlan: 'Plan de période :',
          applyDiscount: 'Appliquer une remise',
          applyDiscountCustom: 'Appliquer une remise (s\'appliquera aux frais personnalisés)',
          noDiscount: 'Aucune remise appliée',
          discountApplied: 'Remise de {{value}} % sera appliquée',
          discountAppliedCustom: ' à vos tarifs personnalisés',
          customChargesApplied: 'Frais personnalisés appliqués',
          customChargesDesc: 'Tarifs personnalisés pour cet élève (remplace les défauts du groupe)',
          customChargesDiscount: '✓ Remise de {{value}} % « {{name}} » sera appliquée en plus des frais personnalisés',
          finalCharges: 'Frais finaux pour cet élève',
          pricingModel: 'Modèle tarifaire :',
          perHour: 'Par heure :',
          updateEnrollment: 'Mettre à jour l\'inscription',
          createEnrollment: 'Créer l\'inscription',
          saving: 'Enregistrement de l\'inscription…',
          enrolling: 'Inscription {{current}} sur {{total}}',
          preparing: 'Préparation…',
          redirecting: 'Redirection vers la liste des inscriptions...',
          successCount: '✅ {{count}} élève inscrit avec succès',
          successCount_other: '✅ {{count}} élèves inscrits avec succès',
          failedCount: '❌ {{count}} élève n\'a pas pu être inscrit',
          failedCount_other: '❌ {{count}} élèves n\'ont pas pu être inscrits',
          renewBanner: 'Renouvellement depuis l\'inscription #{{id}}. Dates par défaut pour la période suivante — ajuster si nécessaire.'
        },
        archiveModal: {
          title: 'Archiver l\'inscription',
          description: 'Définir la date de fin pour {{name}} (inscription #{{id}}).',
          enrolledOn: ' Inscrit(e) le {{date}}.',
          endDate: 'Date de fin',
          archiving: 'Archivage…'
        },
        paymentModal: {
          title: 'Frais de période & portefeuille',
          enrolling: 'Inscription de {{name}}',
          review: 'Vérifier le paiement avant de finaliser l\'inscription',
          periodFee: 'Frais de période :',
          plan: 'Plan :',
          walletBalance: 'Solde du portefeuille :',
          insufficient: '(insuffisant pour le montant total)',
          amountNeeded: 'Montant requis :',
          topUp: 'Recharger le portefeuille',
          refreshing: 'Actualisation…',
          refreshBalance: 'Actualiser le solde',
          paymentRequired: 'Votre académie exige un paiement avant la présence. Veuillez débiter le portefeuille ou recharger d\'abord.',
          unpaidPriorTitle: 'Frais de période précédente encore impayés',
          unpaidPriorDesc: 'L\'inscription #{{id}} ({{period}}) a un solde impayé de {{amount}}.',
          unpaidPriorAck: 'Je comprends et souhaite poursuivre cette nouvelle inscription.',
          insufficientHelp: 'Le portefeuille ne couvre pas l\'intégralité des frais de période. Choisissez comment procéder au recouvrement. Nous réessaierons automatiquement chaque jour et à la fin des séances.',
          collectLegend: 'Comment recouvrer les frais ?',
          chargeNow: 'Débiter le portefeuille maintenant',
          collectViaSchedule: 'Recouvrer sur les prochains jours',
          enrollUnpaid: 'Inscrire sans débiter pour l\'instant',
          chargeNowHelp: 'Les frais de période seront débités immédiatement du portefeuille de l\'élève.',
          chargeNowInsufficient: 'Le portefeuille sera débité immédiatement. Rechargez d\'abord ou activez le portefeuille négatif dans les paramètres de facturation.',
          collectViaScheduleHelp: 'Nous tenterons de recouvrer les frais quotidiennement pendant quelques jours, puis à nouveau lors de séances terminées. L\'élève peut assister selon les règles de grâce de votre académie.',
          enrollUnpaidHelp: 'L\'inscription sera créée sans débit immédiat. Le recouvrement suivra les règles de relance programmées de votre académie.',
          confirmEnroll: 'Confirmer & inscrire'
        },
        lockReasons: {
          student: 'Cette inscription appartient à cet élève et ne peut pas être transférée à quelqu\'un d\'autre.',
          level: 'Le niveau a été défini à l\'inscription et ne peut pas être modifié ici. Pour le déplacer vers un autre niveau, terminez cette inscription et créez-en une nouvelle.',
          batch: 'Le groupe de formation a été défini à l\'inscription et ne peut pas être modifié ici. Pour le déplacer vers un autre groupe, terminez cette inscription et créez-en une nouvelle.',
          startDate: 'La date de début a été enregistrée à la création de l\'inscription et ne peut pas être modifiée ensuite.',
          endDateEditable: 'Définir ou mettre à jour la date à laquelle l\'élève doit cesser d\'assister à ce groupe. Les séances déjà tenues ou facturées ne sont pas affectées.',
          carryForward: 'Ce choix de report a été fait à l\'inscription et ne peut pas être modifié ici.',
          carryForwardBatch: 'Le report pour ce groupe est identique pour tous les élèves et est géré dans les paramètres du groupe.',
          billingMode: 'Le type de facturation de cette inscription ne peut pas être modifié après l\'inscription de l\'élève.',
          billingAmount: 'Ce montant de frais ne peut pas être modifié sur une inscription existante.',
          customRateEditable: 'Met à jour le tarif personnalisé uniquement pour les séances futures. Les frais déjà prélevés restent inchangés.',
          customRateLocked: 'Les tarifs personnalisés ne peuvent être définis qu\'à la première inscription, pas lors de la modification.',
          discount: 'La remise sur cette inscription ne peut pas être modifiée après l\'inscription.',
          systemManaged: 'Cette inscription a été créée automatiquement pour une séance ponctuelle et ne peut pas être modifiée.'
        }
      },
      sessions: {
        title: 'Séances',
        subtitle: 'Filtrer par niveau et groupe. Marquer la présence, modifier ou créer des séances.',
        addWalkIn: 'Ajouter une séance ponctuelle',
        addWalkInTitle: 'Créer une séance unique pour un élève ponctuel',
        addIndividual: 'Ajouter une séance individuelle',
        addIndividualTitle: 'Créer une séance individuelle en tête-à-tête',
        addBatch: 'Ajouter des séances de groupe',
        addBatchTitle: 'Ajouter des séances de groupe',
        selectBatchTitle: 'Sélectionner un groupe pour créer une séance',
        showingOnDate: 'Séances du',
        selectLevelBatch: ' — sélectionnez un niveau et un groupe pour charger les séances',
        clearDateFilter: 'Effacer le filtre de date',
        filterLevel: 'Filtrer par niveau',
        filterBatch: 'Filtrer par groupe',
        selectLevel: 'Sélectionner un niveau…',
        selectBatch: 'Sélectionner un groupe…',
        selectLevelFirst: 'Sélectionnez d\'abord un niveau',
        sessionStatus: 'Statut de séance',
        allStatuses: 'Tous les statuts',
        sortBy: 'Trier par',
        sortDateDesc: 'Date (Plus récentes)',
        sortDateAsc: 'Date (Plus anciennes)',
        sortStatus: 'Statut (En attente → Terminées)',
        sortIdDesc: 'ID séance (Élevé à bas)',
        sortIdAsc: 'ID séance (Bas à élevé)',
        searchSessions: 'Rechercher des séances',
        searchPlaceholder: 'Rechercher des séances...',
        showingResults: '{{count}} résultat affiché',
        showingResults_other: '{{count}} résultats affichés',
        statusFilter: 'Statut :',
        clearFilters: 'Effacer les filtres',
        selectLevelBatchEmpty: 'Sélectionner un niveau et un groupe',
        selectLevelBatchHint: 'Choisissez dans les filtres ci-dessus pour voir les séances',
        noSessions: 'Aucune séance trouvée',
        noSessionsHint: 'Créez votre première séance pour ce groupe',
        sessionNumber: 'Séance #{{id}}',
        walkIn: 'Ponctuel',
        level: 'Niveau',
        batch: 'Groupe',
        coach: 'Entraîneur',
        notAssigned: 'Non assigné',
        statusControl: 'Contrôle du statut',
        statusPending: 'En attente',
        statusCompleted: 'Terminée',
        statusCancelled: 'Annulée',
        completedLocked: 'Les séances terminées ne peuvent pas être modifiées',
        cancelledLocked: 'Les séances annulées ne peuvent pas être modifiées',
        saveStatus: 'Enregistrer le statut',
        save: 'Enregistrer',
        saving: 'Enregistrement…',
        viewAttendance: 'Voir la présence',
        viewAttendanceCancelled: 'Voir la présence pour la séance annulée',
        viewAttendanceCompleted: 'Voir la présence saisie pour cette séance terminée',
        updateNotes: 'Mettre à jour les notes de séance',
        updateNotesTitle: 'Mettre à jour notes/thème de séance',
        reverse: 'Annuler',
        reversing: 'Annulation...',
        reverseTitle: 'Annuler cette séance terminée et créditer les élèves',
        markAttendance: 'Marquer la présence',
        markAttendanceTitle: 'Marquer la présence pour cette séance',
        viewAttendanceSession: 'Voir la présence pour cette séance',
        attendanceCompletedLocked: 'La présence ne peut pas être marquée pour les séances terminées',
        attendanceCancelledLocked: 'La présence ne peut pas être marquée pour les séances annulées',
        edit: 'Modifier',
        editTitle: 'Modifier les détails de la séance',
        pagination: 'Affichage de {{start}} à {{end}} sur {{total}} séances',
        rowsPerPage: 'Lignes par page :',
        loadFailed: 'Échec du chargement des séances.',
        loadDateFailed: 'Échec du chargement des séances pour la date sélectionnée.',
        markBeforeComplete: 'Veuillez marquer la présence avant de marquer la séance comme TERMINÉE.',
        completeSuccess: 'Séance marquée comme TERMINÉE. La facturation élève a été traitée.',
        completeFailed: 'Échec de la finalisation de la séance et du traitement de la facturation :\\n\\n{{message}}\\n\\nAssurez-vous que les élèves ont un solde suffisant et réessayez.',
        statusUpdateFailed: 'Échec de la mise à jour du statut de séance : {{message}}',
        reversalReasonRequired: 'Le motif d\'annulation est requis.',
        reversalConfirm: 'L\'annulation de cette séance va :\\n- Recréditer les montants sur les portefeuilles des élèves\\n- Marquer la séance comme ANNULÉE\\n\\nVoulez-vous continuer ?',
        reversalSuccess: 'Séance annulée avec succès.',
        reversalFailed: 'Échec de l\'annulation de la séance : {{message}}',
        walkInCreated: 'Séance ponctuelle créée avec succès !\\nID séance : {{sessionId}}\\nID inscription : {{enrollmentId}}',
        individualCreated: 'Séance individuelle créée avec succès !\\nID séance : {{sessionId}}\\nGroupe : {{batch}}\\nÉlève : {{student}}\\nFrais : {{charge}}',
        reversalModal: {
          title: 'Annuler la séance #{{id}}',
          willDo: 'Cette action va :',
          creditWallets: 'Recréditer les montants sur tous les portefeuilles élèves',
          markCancelled: 'Marquer la séance comme ANNULÉE',
          compensatingTxns: 'Créer des transactions compensatoires sur le portefeuille',
          recordReason: 'Enregistrer le motif d\'annulation pour audit',
          reasonLabel: 'Motif d\'annulation',
          reasonPlaceholder: 'p. ex., Séance annulée pour indisponibilité de l\'entraîneur...',
          confirm: 'Confirmer l\'annulation'
        },
        form: {
          editTitle: 'Modifier la séance',
          addTitle: 'Ajouter des séances de groupe',
          editSubtitle: 'Mettre à jour les détails de la séance',
          addSubtitle: 'Planifier des séances récurrentes pour votre groupe',
          close: 'Fermer',
          trainingBatch: 'Groupe de formation',
          selectBatch: 'Sélectionner un groupe...',
          sessionCoach: 'Entraîneur de séance',
          selectCoach: 'Sélectionner un entraîneur...',
          autoSelected: 'Sélection automatique',
          sessionTiming: 'Horaire de séance',
          startTime: 'Heure de début',
          endTime: 'Heure de fin',
          selectStartTime: 'Sélectionner l\'heure de début',
          selectEndTime: 'Sélectionner l\'heure de fin',
          sessionDate: 'Date de séance',
          dateRange: 'Plage de dates',
          date: 'Date ({{format}})',
          from: 'Du ({{format}})',
          to: 'Au ({{format}})',
          selectDays: 'Sélectionner les jours de récurrence',
          selected: 'Sélectionné :',
          update: 'Mettre à jour',
          create: 'Créer',
          updating: 'Mise à jour...',
          creating: 'Création...',
          fillRequired: 'Veuillez remplir tous les champs obligatoires.',
          endAfterStart: 'L\'heure de fin doit être après l\'heure de début',
          selectDay: 'Veuillez sélectionner au moins un jour.',
          invalidDateRange: 'Plage de dates invalide.',
          invalidDate: 'Date invalide.',
          updated: 'Séance mise à jour avec succès.',
          created: '{{count}} séance(s) créée(s).',
          updateFailed: 'Échec de la mise à jour de la séance.',
          createFailed: 'Échec de la création des séances.',
          longSessionWarning: '⚠️ Avertissement : durée de séance longue\\n\\nDate(s) de séance : {{dates}}\\nHeure de début : {{start}}\\nHeure de fin : {{end}}\\nDurée : {{duration}}\\n\\nCette séance dépasse 3 heures. Continuer ?',
          hour: '{{count}} heure',
          hour_other: '{{count}} heures',
          minute: '{{count}} minute',
          minute_other: '{{count}} minutes'
        },
        walkInModal: {
          title: 'Ajouter une séance ponctuelle',
          info: 'Séance ponctuelle :',
          infoBody: 'Crée une séance unique pour un élève non inscrit régulièrement. Le système créera automatiquement une inscription et un groupe si nécessaire.',
          students: 'Élèves',
          searchStudents: 'Rechercher et sélectionner des élèves...',
          noStudentsFound: 'Aucun élève trouvé',
          allStudentsSelected: 'Tous les élèves sélectionnés',
          searchHint: 'Tapez pour rechercher et sélectionner un ou plusieurs élèves',
          sessionDetails: 'Détails de la séance',
          selectLevel: 'Sélectionner un niveau...',
          sessionCoach: 'Entraîneur de séance',
          selectCoach: 'Sélectionner un entraîneur...',
          coachAutoNote: 'Sélection automatique (les entraîneurs ne peuvent créer des séances que pour eux-mêmes)',
          timing: 'Horaire',
          sessionDate: 'Date de séance',
          startTime: 'Heure de début',
          endTime: 'Heure de fin',
          selectTime: 'Sélectionner l\'heure',
          sessionCharge: 'Frais de séance ({{symbol}})',
          enterAmount: 'Saisir le montant',
          chargeNote: 'Il s\'agit du montant total pour toute la durée de la séance, et non d\'un tarif horaire.',
          creating: 'Création...',
          create: 'Créer la séance ponctuelle',
          fillRequired: 'Veuillez remplir tous les champs obligatoires et sélectionner au moins un élève',
          endAfterStart: 'L\'heure de fin doit être après l\'heure de début',
          createFailed: 'Échec de la création de la séance ponctuelle',
          longSessionTitle: 'Avertissement : durée de séance longue',
          longSessionProceed: 'Cette séance dépasse 3 heures. Voulez-vous continuer ?',
          sessionDateLabel: 'Date de séance :',
          startTimeLabel: 'Heure de début :',
          endTimeLabel: 'Heure de fin :',
          durationLabel: 'Durée :',
          userFallback: 'Utilisateur #{{id}}',
          idLabel: 'ID : #{{id}}'
        },
        individualModal: {
          title: 'Ajouter une séance individuelle',
          autoActions: 'Actions automatiques :',
          autoPresent: 'La présence sera automatiquement marquée PRÉSENT',
          autoCharge: 'Les frais de séance seront débités immédiatement du portefeuille de l\'élève',
          autoCompleted: 'Le statut de la séance sera défini sur TERMINÉ (à des fins comptables)',
          sessionDetails: 'Détails de la séance',
          selectLevel: 'Sélectionner un niveau',
          studentName: 'Nom de l\'élève',
          selectStudent: 'Sélectionner un élève',
          sessionCoach: 'Entraîneur de séance',
          selectCoach: 'Sélectionner un entraîneur',
          timing: 'Horaire',
          sessionDate: 'Date de séance',
          startTime: 'Heure de début',
          endTime: 'Heure de fin',
          selectTime: 'Sélectionner l\'heure',
          sessionTopic: 'Sujet de la séance',
          topicPlaceholder: 'p. ex., Théorie des ouvertures, Entraînement aux finales',
          sessionCharge: 'Frais de séance ({{symbol}})',
          chargePlaceholder: 'p. ex., 600',
          chargeNote: 'Le tarif affiché par défaut est horaire. Il s\'ajuste automatiquement selon la durée. Vous pouvez modifier ce montant manuellement.',
          creating: 'Création...',
          create: 'Créer la séance individuelle',
          selectLevelError: 'Veuillez sélectionner un niveau',
          selectStudentError: 'Veuillez sélectionner un élève',
          selectCoachError: 'Veuillez sélectionner un entraîneur',
          selectDateError: 'Veuillez sélectionner la date de séance',
          selectStartError: 'Veuillez sélectionner l\'heure de début',
          selectEndError: 'Veuillez sélectionner l\'heure de fin',
          topicRequired: 'Veuillez saisir le sujet de la séance',
          chargeRequired: 'Veuillez saisir un montant de frais valide',
          endAfterStart: 'L\'heure de fin doit être après l\'heure de début',
          invalidDate: 'Veuillez saisir une date de séance valide.',
          createFailed: 'Échec de la création de la séance individuelle',
          longSessionTitle: 'Avertissement : durée de séance longue',
          longSessionProceed: 'Cette séance dépasse 3 heures. Voulez-vous continuer ?',
          sessionDateLabel: 'Date de séance :',
          startTimeLabel: 'Heure de début :',
          endTimeLabel: 'Heure de fin :',
          durationLabel: 'Durée :'
        },
        updateNotesModal: {
          title: 'Mettre à jour les notes de séance',
          subtitle: 'Séance #{{sessionId}} - {{batchName}}',
          sessionNotes: 'Notes de séance',
          placeholder: 'Saisir les notes de séance...',
          charCount: '{{count}} / 2000 caractères',
          saving: 'Enregistrement...',
          saveNotes: 'Enregistrer les notes',
          updateFailed: 'Échec de la mise à jour des notes. Veuillez réessayer.'
        }
      },
      attendance: {
        markTitle: 'Marquer la présence',
        viewTitle: 'Présence à la séance',
        viewLoading: 'Chargement des détails de présence…',
        coach: 'Entraîneur',
        status: 'Statut',
        sessionNotes: 'Notes de séance',
        sessionNotesRequired: 'Notes de séance',
        sessionNotesPlaceholder: 'Ajouter des notes pour cette séance avant de marquer la présence',
        sessionNotesHint: 'Requis avant d\'enregistrer ou de terminer la séance',
        searchStudents: 'Rechercher des élèves… (nom ou #id)',
        warning: 'Avertissement',
        success: 'Succès',
        markAllPresent: 'Tous présents',
        markAllAbsent: 'Tous absents',
        saveAttendance: 'Enregistrer la présence',
        saving: 'Enregistrement…',
        completeSession: 'Terminer la séance',
        completing: 'Finalisation…',
        completeTitle: 'Enregistrer la présence et marquer cette séance comme TERMINÉE.',
        alreadyCompleted: 'La séance est déjà marquée comme TERMINÉE.',
        cancelledCannotComplete: 'Les séances annulées ne peuvent pas être terminées.',
        loadingStudents: 'Chargement des élèves…',
        checkInOutInfo: 'Heures d\'arrivée et de départ',
        checkInOutHint: 'Ces heures sont optionnelles. Ne les remplissez que si le temps de présence diffère de l\'horaire prévu.',
        student: 'Élève',
        attendanceStatus: 'Statut de présence',
        checkIn: 'Heure d\'arrivée',
        checkOut: 'Heure de départ',
        notMarked: 'Non marqué',
        present: 'Présent',
        absent: 'Absent',
        notApplicable: 'Non applicable',
        selectCheckIn: 'Sélectionner l\'heure d\'arrivée',
        selectCheckOut: 'Sélectionner l\'heure de départ',
        noStudentsMatch: 'Aucun élève ne correspond à « {{query}} ».',
        walkInStudents: 'Élèves ponctuels ({{count}})',
        addWalkIn: '+ Ajouter un élève ponctuel',
        duplicateWalkIns: '{{count}} enregistrement ponctuel chevauche des élèves inscrits au groupe et est masqué ci-dessous.',
        duplicateWalkIns_other: '{{count}} enregistrements ponctuels chevauchent des élèves inscrits au groupe et sont masqués ci-dessous.',
        removeDuplicate: 'Supprimer le doublon : {{name}}',
        noWalkIns: 'Aucun élève ponctuel pour cette séance',
        walkInHint: 'Les élèves ponctuels sont automatiquement marqués présents et facturés à la fin de la séance',
        notes: 'Notes',
        actions: 'Actions',
        remove: 'Supprimer',
        sessionStart: 'Début de séance',
        sessionEnd: 'Fin de séance',
        walkInAdded: 'Élève ponctuel ajouté avec succès !',
        walkInRemoved: 'Élève ponctuel supprimé avec succès !',
        removeWalkInConfirm: 'Supprimer cet élève ponctuel ? Il ne sera pas facturé pour cette séance.',
        removeWalkInFailed: 'Échec de la suppression de l\'élève ponctuel',
        notesRequiredSave: 'Les notes de séance sont requises avant d\'enregistrer la présence.',
        notesRequiredComplete: 'Les notes de séance sont requises avant de terminer la séance.',
        markBeforeComplete: 'Veuillez marquer la présence avant de terminer la séance.',
        savedAck: 'Présence enregistrée à {{time}}. Vous pouvez la modifier jusqu\'à ce que la séance soit marquée TERMINÉE.',
        completeConfirm: 'Marquer cette séance comme TERMINÉE ? Une fois terminée, elle ne pourra plus être modifiée.',
        completeSuccess: 'Séance marquée comme TERMINÉE avec succès !',
        completeFailed: 'Échec de la finalisation de la séance : {{message}}',
        saveFailed: 'Échec de l\'enregistrement de la présence.',
        loadFailed: 'Échec du chargement des données de présence.',
        sessionIdMissing: 'ID de séance manquant dans la route/état.',
        batchUnknown: 'Groupe inconnu pour cette séance. Affichage uniquement des élèves ayant déjà des marques.',
        batchStudents: 'Élèves du groupe',
        walkInLabel: 'Élèves ponctuels',
        type: 'Type',
        walkInType: 'Ponctuel',
        carryForward: 'Report',
        carryForwardColumnHelp: 'Le report contrôle la facturation lorsqu\'un élève est absent. Si activé, les frais de séance ne sont pas déduits et le crédit peut être utilisé plus tard. Si désactivé, les frais sont toujours déduits même si l\'élève est marqué absent.',
        sessionCancelled: 'Séance annulée',
        cancelledDesc: 'Cette séance a été annulée/contre-passée. Tous les frais de facturation ont été remboursés.',
        cancelledHistorical: ' Les données de présence ci-dessous sont historiques et ne sont plus actives.',
        missingSession: 'Informations de séance manquantes.',
        loadDetailsFailed: 'Échec du chargement des détails de présence.',
        addWalkInStudent: {
          title: 'Ajouter un élève ponctuel',
          selectStudent: 'Sélectionner un élève',
          searchPlaceholder: 'Rechercher par nom ou e-mail...',
          alreadyOnSession: 'Les élèves correspondants sont déjà sur cette séance et ne peuvent pas être ajoutés en ponctuel.',
          noMatch: 'Aucun autre élève ne correspond à votre recherche',
          allOnSession: 'Tous les élèves disponibles sont déjà sur cette séance',
          noStudents: 'Aucun élève trouvé',
          change: 'Changer',
          checkIn: 'Heure d\'arrivée (optionnel)',
          checkInHint: 'Laisser vide pour utiliser l\'heure de début de séance',
          checkOut: 'Heure de départ (optionnel)',
          checkOutHint: 'Laisser vide pour utiliser l\'heure de fin de séance',
          notes: 'Notes (optionnel)',
          notesPlaceholder: 'Notes supplémentaires...',
          adding: 'Ajout...',
          add: 'Ajouter un élève ponctuel',
          selectRequired: 'Veuillez sélectionner un élève',
          alreadyOnRoster: 'Cet élève est déjà sur la liste de séance ou ponctuelle',
          loadFailed: 'Échec du chargement des élèves',
          addFailed: 'Échec de l\'ajout de l\'élève ponctuel',
          noteTitle: 'Note :',
          noteBody: 'Les élèves ponctuels seront automatiquement facturés au tarif de la séance à la fin. Le montant sera débité de leur portefeuille. Les élèves déjà inscrits à ce groupe ne sont pas affichés ici.'
        }
      },
      reports: {
        eyebrow: 'Rapports',
        title: 'Rapports',
        subtitle: 'Consulter et exporter des rapports détaillés pour votre académie',
        loading: 'Chargement des rapports…',
        noReportsEnabled: 'Aucun rapport activé',
        noReportsHint: 'L\'administrateur de votre académie a désactivé tous les rapports. Contactez un admin sous Rapports → Paramètres des rapports.',
        tabs: {
          'coach-revenue': 'Revenus entraîneur & commission',
          'session-statement': 'Relevé de séances élève',
          'batch-attendance': 'Présence par groupe',
          'academy-revenue': 'Résumé des revenus académie',
          'student-attendance': 'Résumé de présence élève',
          'payments-due': 'Paiements dus',
          'data-export': 'Export de données',
          'sessions-summary': 'Résumé des séances',
          'monthly-statements': 'Contrôle des relevés mensuels'
        },
        tabsManager: {
          'session-statement': 'Relevé de séances'
        },
        descriptions: {
          'coach-revenue': 'Revenus par séance avec répartition académie vs commission entraîneur.',
          'session-statement': 'Grand livre des frais par élève avec paiements, frais de séance et export PDF.',
          'batch-attendance': 'Matrice élèves vs séances pour un groupe sur une plage de dates.',
          'academy-revenue': 'Revenus bruts mensuels et par site avec détail des commissions.',
          'student-attendance': 'Taux et nombres de présence par élève sur une période.',
          'payments-due': 'Liste instantanée des élèves avec solde impayé ou portefeuille bas nécessitant un recouvrement.',
          'data-export': 'Exports CSV pour liste d\'élèves, tuteurs et transactions portefeuille.',
          'sessions-summary': 'Date/heure, niveau, groupe, entraîneur et thème pour un élève ou entraîneur sur une plage de dates.',
          'monthly-statements': 'Prévisualiser, envoyer et suivre les e-mails de relevé mensuel de séances avec estimations de frais.'
        },
        dataExport: {
          title: 'Export de données',
          subtitle: 'Télécharger les données académie en CSV. Tous les exports incluent un BOM UTF-8 pour un affichage correct dans Excel.',
          preparing: 'Préparation…',
          from: 'Du',
          to: 'Au',
          studentRoster: 'Liste des élèves',
          studentRosterDesc: 'Tous les élèves avec coordonnées, date de naissance, champs FIDE/Elo optionnels, groupes d\'inscription actifs et solde portefeuille actuel.',
          downloadStudents: 'Télécharger students.csv',
          guardianLinks: 'Liens tuteurs',
          guardianLinksDesc: 'Relations élève–tuteur et statut de lien pour DSAR / demandes d\'accès parental.',
          downloadGuardians: 'Télécharger guardians.csv',
          walletTransactions: 'Transactions portefeuille',
          walletTransactionsDesc: 'Tous les crédits et débits portefeuille pour la période sélectionnée — nom élève, montant, motif, mode de paiement et référence.',
          downloadTransactions: 'Télécharger transactions.csv',
          footer: 'Les fichiers CSV s\'ouvrent directement dans Microsoft Excel, Google Sheets et LibreOffice Calc. Pour les grandes académies, l\'export peut prendre quelques secondes.',
          invalidDateRange: 'Veuillez sélectionner une plage de dates valide.',
          exportStudentsFailed: 'Échec de l\'export de la liste d\'élèves. Veuillez réessayer.',
          exportGuardiansFailed: 'Échec de l\'export des liens tuteurs. Veuillez réessayer.',
          exportTxnsFailed: 'Échec de l\'export des transactions. Veuillez réessayer.',
          dsarBannerTitle: 'Demande d’accès aux données — agissez ici',
          dsarBannerBody: '{{name}} (utilisateur #{{userId}}, {{username}}) a demandé une copie de ses données personnelles. Téléchargez les exports ci-dessous, puis ouvrez le profil si vous devez vérifier l’identité.',
          dsarUnknownName: 'Cet utilisateur',
          dsarOpenProfile: 'Ouvrir le profil',
          dsarOpenWallet: 'Ouvrir la recharge portefeuille',
          studentFilter: 'Élève',
          studentFilterHint: 'Tous les téléchargements CSV ci-dessous utilisent cette sélection. Choisissez un élève pour un pack DSAR, ou Tous les élèves pour un export académie.',
          chooseStudent: 'Rechercher des élèves…',
          allStudents: 'Tous les élèves',
          studentFallback: 'Élève #{{id}}',
          scopedToStudent: 'Portée de l’export : {{student}}',
          scopedToAll: 'Portée de l’export : tous les élèves',
          dsarLocked: 'Focus DSAR',
          clearStudentFilter: 'Effacer le filtre élève',
          studentRosterDescOne: 'Coordonnées, date de naissance, champs FIDE/Elo optionnels, groupes actifs et solde portefeuille pour l’élève sélectionné uniquement.',
          guardianLinksDescOne: 'Relations tuteurs et statut pour l’élève sélectionné uniquement.',
          walletTransactionsDescOne: 'Crédits et débits portefeuille pour l’élève sélectionné et la période uniquement.',
          downloadOneStudent: 'Télécharger cet élève.csv',
          downloadOneGuardians: 'Télécharger guardians.csv de cet élève',
          downloadOneTransactions: 'Télécharger transactions.csv de cet élève',
          walletStudentFilter: 'Élève',
          walletExportingFor: 'Export des transactions pour {{student}}',
          studentRequiredForTxns: 'Sélectionnez un élève avant de télécharger les transactions portefeuille.',
          studentsLoadFailed: 'Impossible de charger les élèves. Vérifiez que le backend tourne, puis actualisez.',
          studentsEmpty: 'Aucun élève actif trouvé.'
        },
        common: {
          filters: 'Filtres',
          fromDate: 'Date de début',
          toDate: 'Date de fin',
          from: 'Du',
          to: 'Au',
          filterByBranch: 'Filtrer par succursale',
          allBranches: 'Toutes les succursales',
          filterByCoach: 'Filtrer par entraîneur',
          allCoaches: 'Tous les entraîneurs',
          filterByLevel: 'Filtrer par niveau',
          selectLevel: 'Choisir un niveau',
          allLevels: 'Tous les niveaux',
          filterByBatch: 'Filtrer par groupe',
          selectBatch: 'Choisir un groupe',
          allBatches: 'Tous les groupes',
          search: 'Rechercher',
          generate: 'Générer',
          generateReport: 'Générer le rapport',
          loadingReport: 'Chargement des données…',
          failedToLoad: 'Échec du chargement du rapport.',
          selectDateRange: 'Veuillez sélectionner une plage de dates',
          invalidDateFormat: 'Format de date invalide. Utilisez {{format}}',
          invalidDateRange: 'Veuillez sélectionner une plage de dates valide.',
          selectFromToBatch: 'Veuillez sélectionner date de début, date de fin et groupe',
          enterEmail: 'Saisir l\'adresse e-mail',
          enterEmailAlert: 'Veuillez saisir une adresse e-mail',
          emailSent: 'Rapport envoyé par e-mail avec succès',
          emailFailed: 'Échec de l\'envoi de l\'e-mail',
          exportPdfFailed: 'Échec de l\'export PDF',
          close: 'Fermer',
          present: 'Présent',
          absent: 'Absent',
          upcoming: 'À venir',
          ongoing: 'En cours',
          past: 'Passé',
          walkIn: 'Passage',
          student: 'Élève',
          studentId: 'ID élève',
          batch: 'Groupe',
          level: 'Niveau',
          branch: 'Succursale',
          coach: 'Entraîneur',
          sessions: 'Séances',
          status: 'Statut',
          email: 'E-mail',
          phone: 'Téléphone',
          amount: 'Montant',
          plan: 'Formule',
          endDate: 'Date de fin',
          grandTotal: 'Total général',
          periodTotalAllPages: 'Total de la période (toutes les pages)',
          unknown: 'Inconnu',
          pageSize: 'Taille de page :',
          first: 'Première',
          previous: 'Précédent',
          next: 'Suivant',
          last: 'Dernière',
          showingSessionsRange: 'Affichage {{start}} - {{end}} sur {{total}} séances',
          showingSessionsPage: 'Affichage {{count}} sur cette page · {{total}} au total · Page {{page}} sur {{pages}}',
          pageOf: 'Page {{page}} sur {{pages}}',
          records: '{{count}} enregistrements',
          downloadPageExcel: 'Télécharger la page Excel',
          exportFullHint: 'Utilisez Export PDF/CSV ci-dessous pour le rapport complet.',
          individualSession: 'Séance individuelle',
          individual: 'Individuel',
          sessionNotes: 'Notes de séance',
          dateTime: 'Date / Heure',
          sessionDateTime: 'Date et heure de séance',
          sessionTopic: 'Sujet de séance',
          topic: 'Sujet',
          gross: 'Brut',
          grossRevenue: 'Revenus bruts',
          academy: 'Académie',
          academyShare: 'Part académie',
          academyEarnings: 'Revenus académie',
          coachEarnings: 'Revenus entraîneur',
          attendanceRate: 'Taux de présence',
          viewAttendance: 'Voir les détails de présence',
          clickViewAttendance: 'Cliquer pour voir la présence',
          sessionAttendance: 'Présence à la séance',
          sessionDetails: 'Détails de séance',
          dateAndTime: 'Date et heure',
          batchAndBranch: 'Groupe et succursale',
          students: 'Élèves',
          avg: 'Moy.',
          average: 'moyenne',
          commissionSplit: 'Répartition commission',
          totalSessionCharges: 'Total frais de séances',
          charges: 'Frais',
          totals: 'Totaux',
          errorPrefix: 'Erreur : {{message}}',
          noServerResponse: 'Pas de réponse du serveur. Vérifiez votre connexion.',
          presentCount: 'Présents ({{count}}) :',
          absentCount: 'Absents ({{count}}) :',
          attendancePct: 'Présence %',
          month: 'Mois',
          type: 'Type',
          startTime: 'Heure de début',
          endTime: 'Heure de fin',
          sessionId: 'ID séance',
          date: 'Date',
          studentsFound: '{{count}} élève trouvé',
          studentsFound_other: '{{count}} élèves trouvés',
          never: 'Jamais',
          amountDue: 'Montant dû',
          lastTopup: 'Dernière recharge',
          batchesCol: 'Groupes',
          balance: 'Solde',
          inScope: 'Dans le périmètre',
          windowDays: 'Fenêtre (jours)',
          overdue: 'EN RETARD',
          lowBalance: 'SOLDE FAIBLE',
          expired: 'EXPIRÉ',
          expiringSoon: 'EXPIRE BIENTÔT',
          byStudent: 'Par élève',
          byCoach: 'Par entraîneur',
          choosePerson: 'Choisir un {{mode}}…',
          allLevelsLabel: 'TOUS les niveaux',
          allBatchesLabel: 'TOUS les groupes',
          noSessionsFilter: 'Aucune séance pour ce filtre.',
          selectPerson: 'Veuillez sélectionner un {{mode}}.',
          selectValidDates: 'Veuillez fournir des dates valides.',
          confirm: 'Confirmer'
        },
        exportBar: {
          sendReportViaEmail: 'Envoyer le rapport par e-mail',
          exportPdf: 'Exporter en PDF',
          generatingPdf: 'Génération du PDF…',
          exportCsv: 'Exporter en CSV',
          generatingCsv: 'Génération du CSV…',
          sending: 'Envoi…',
          sent: 'Envoyé !',
          sendEmail: 'Envoyer l\'e-mail',
          exportPdfFailed: 'Échec de l\'export PDF. Veuillez réessayer.',
          exportCsvFailed: 'Échec de l\'export CSV. Veuillez réessayer.',
          enterEmail: 'Veuillez saisir une adresse e-mail',
          enterValidEmail: 'Veuillez saisir une adresse e-mail valide',
          sendEmailFailed: 'Échec de l\'envoi de l\'e-mail. Veuillez réessayer.',
          generateFirstHint: 'Générez d\'abord un rapport pour activer les options d\'export.'
        },
        coachRevenue: {
          title: 'Revenus entraîneur & commission',
          subtitle: 'Revenus par séance, commissions et totaux par entraîneur — filtrable par dates, succursale et entraîneur.',
          totalSessions: 'Séances totales',
          sessionMix: '{{individual}} individuelles, {{batch}} groupe',
          noSessionsFound: 'Aucune séance trouvée',
          noSessionsHint: 'Aucune séance pour les filtres et dates sélectionnés',
          coachWiseEarnings: 'Revenus par entraîneur',
          dayWiseEarnings: 'Revenus par jour',
          daysCount: '{{count}} jours',
          daysScrollHint: '{{count}} jours · faire défiler pour plus'
        },
        sessionAttendance: {
          subtitle: 'Matrice présence élève par séance pour un groupe — statut présent/absent',
          subtitleCharges: ' et frais par séance',
          subtitleEnd: ' pour chaque élève.'
        },
        studentAttendance: {
          subtitleCoach: 'Résumé de présence pour vos séances et élèves du roster.',
          subtitleAdmin: 'Résumé de présence par élève sur les groupes.',
          totalStudents: 'Élèves totaux',
          avgAttendance: 'Présence moy.',
          totalPresent: 'Présents totaux',
          totalAbsent: 'Absents totaux',
          detailsTitle: 'Détails de présence élève',
          lowAttendanceLegend: 'Lignes surlignées : présence inférieure à 50 %',
          empty: 'Aucun enregistrement pour les filtres sélectionnés.'
        },
        academyRevenue: {
          subtitle: 'Revenus bruts mensuels et par succursale avec répartition commission.',
          monthlyBreakdown: 'Répartition mensuelle',
          branchBreakdown: 'Répartition par succursale',
          month: 'Mois',
          sessions: 'Séances',
          grossRevenue: 'Revenus bruts',
          academyCommission: 'Commission académie',
          academyRevenue: 'Revenus académie',
          coachPayout: 'Paiement entraîneur',
          walletTopUps: 'Recharges portefeuille',
          walletBalanceHeld: 'Solde portefeuille détenu',
          empty: 'Aucune donnée de revenus.'
        },
        paymentsDue: {
          subtitle: 'Instantané des élèves à solde faible ou négatif.',
          paymentStatus: 'Statut de paiement',
          statusAll: 'Tous (retard + solde faible)',
          statusOverdue: 'Retard uniquement (solde négatif)',
          statusLowBalance: 'À risque uniquement',
          thresholdOverride: 'Remplacement seuil solde faible',
          thresholdPlaceholder: 'Laisser vide pour la valeur par défaut',
          thresholdHint: 'Défaut système : {{amount}}',
          overdueStudents: 'Élèves en retard',
          overdueSub: 'Solde négatif',
          totalAmountDue: 'Montant total dû',
          totalDueSub: 'Somme due par les élèves en retard',
          belowThreshold: 'Sous le seuil',
          belowSub: '{{pct}} % des élèves dans le périmètre',
          studentsInScope: 'Élèves dans le périmètre',
          empty: 'Aucun élève ne correspond aux filtres.',
          sessionsSince: 'Séances depuis',
          ratePerSession: 'Tarif/séance'
        },
        sessionsSummary: {
          title: 'Résumé des séances',
          subtitle: 'Date/heure, niveau, groupe, entraîneur et sujet pour un élève ou entraîneur.',
          loadFailed: 'Échec du chargement du résumé.'
        },
        monthlyStatements: {
          title: 'Contrôle des relevés mensuels',
          subtitle: 'Prévisualiser, envoyer et suivre les e-mails de relevé. Choisissez la période du relevé et une période d\'estimation des frais distincte ; les séances déjà payées sont exclues de l\'estimation.',
          periodSettings: 'Paramètres de période',
          statementAndEstimate: 'Relevé & estimation des frais',
          periodDesc: 'Choisissez l\'activité sur le relevé, puis la fenêtre pour les estimations de frais à venir.',
          statementLabel: 'Relevé :',
          statementPeriod: 'Période du relevé',
          statementPeriodDesc: 'Activité, frais portefeuille et présence.',
          estimatePeriod: 'Période d\'estimation des frais',
          estimatePeriodDesc: 'Mois complet suivant les relevés de fin de mois, sinon le reste de ce mois.',
          thisMonth: 'Ce mois-ci',
          lastMonth: 'Mois dernier',
          includeEstimate: 'Inclure l\'estimation des frais à venir',
          estimateAuto: 'Remplir automatiquement l\'estimation',
          estimateFrom: 'Estimation du',
          estimateTo: 'Estimation au',
          loadPreview: 'Charger l\'aperçu',
          loading: 'Chargement…',
          loadFailed: 'Échec du chargement.',
          totalStudents: 'Élèves totaux',
          sent: 'Envoyé',
          pending: 'En attente',
          failed: 'Échoué',
          notSent: 'Non envoyé',
          searchStudents: 'Rechercher par nom, e-mail ou ID…',
          sendAll: 'Tout envoyer',
          refreshQueue: 'Actualiser la file',
          studentCol: 'Élève',
          emailCol: 'E-mail',
          walletBalance: 'Solde portefeuille',
          estimateFees: 'Frais est.',
          queueStatus: 'Statut file',
          actions: 'Actions',
          previewPdf: 'Aperçu PDF',
          downloadPdf: 'Télécharger PDF',
          send: 'Envoyer',
          cancelEmail: 'Annuler',
          retry: 'Réessayer',
          noStudents: 'Aucun élève pour cette période.',
          allowPopups: 'Autorisez les pop-ups pour l\'aperçu PDF.',
          accessDenied: 'Accès refusé — connectez-vous en ADMIN.',
          pdfLoadFailed: 'Échec du chargement PDF.',
          pdfDownloadFailed: 'Échec du téléchargement PDF.',
          pdfError: 'Erreur PDF : {{message}}',
          confirmSendAllTitle: 'Envoyer les relevés pour {{range}}',
          confirmSendAllMsg: 'Mise en file pour {{count}} élève{{suffix}}.{{skipNote}}',
          skipNote: ' {{count}} élève{{suffix}} déjà ENVOYÉ/EN ATTENTE seront ignorés.',
          sendAllLabel: 'Tout envoyer',
          queuedAll: 'Relevés mis en file pour tous.',
          sendAllFailed: 'Échec de l\'envoi global.',
          confirmSendOneTitle: 'Envoyer le relevé pour {{name}}',
          confirmSendOneMsg: 'Mettre en file un e-mail pour {{name}} couvrant {{range}} ?',
          queued: 'En file ✓',
          queueFailed: 'Échec de la mise en file.',
          cancelTitle: 'Annuler l\'e-mail',
          cancelMsg: 'Annuler cet e-mail en attente ? L\'élève ne recevra pas le relevé sauf si vous le renvoyez.',
          cancelLabel: 'Annuler l\'e-mail',
          cancelled: 'Annulé ✓',
          cancelFailed: 'Échec de l\'annulation.',
          retryQueued: 'Nouvel essai ✓',
          retryFailed: 'Échec du nouvel essai.',
          statusSent: 'Envoyé',
          statusPending: 'En attente',
          statusProcessing: 'En cours',
          statusFailed: 'Échoué',
          statusCancelled: 'Annulé',
          months: {
            '1': 'janvier',
            '2': 'février',
            '3': 'mars',
            '4': 'avril',
            '5': 'mai',
            '6': 'juin',
            '7': 'juillet',
            '8': 'août',
            '9': 'septembre',
            '10': 'octobre',
            '11': 'novembre',
            '12': 'décembre'
          },
          queueing: 'Mise en file…',
          sendAllWithCount: '🚀 Tout envoyer ({{count}})',
          includeFeeEstimateTitle: 'Inclure l\'estimation des frais et le rappel de dépôt',
          includeFeeEstimateDesc: 'Décocher pour n\'envoyer que le relevé d\'activité. Les élèves avec un crédit suffisant ignorent toujours le rappel de paiement si cette option est cochée.',
          resetDefault: 'Réinitialiser',
          resetDefaultTitle: 'Réinitialiser l\'estimation selon la date de fin du relevé',
          then: 'puis',
          autoRange: 'Auto : {{range}} ({{count}} jour{{daySuffix}})',
          customEstimateRange: 'Plage d\'estimation personnalisée',
          estimateInvalid: 'La période d\'estimation des frais nécessite des dates de/à valides.',
          estimateLabel: 'Estimation :',
          studentsWithActivity: '{{count}} élève{{suffix}} avec activité de paiement/inscription',
          clearSearch: 'Effacer la recherche',
          noEmailOnFile: 'Aucun e-mail enregistré',
          sendStatementEmail: 'Envoyer l\'e-mail de relevé',
          resendTooltip: 'L\'élève l\'a déjà reçu. Renvoie.',
          resend: 'Renvoyer',
          charges: 'Frais',
          closingBalance: 'Solde de clôture',
          upcomingEst: 'Estim. à venir',
          emailStatus: 'Statut e-mail',
          notSentStatus: 'Non envoyé',
          archived: 'Archivé',
          noStudentsActivity: 'Aucun élève avec activité de paiement ou d\'inscription pour {{range}}.',
          noStudentsMatch: 'Aucun élève ne correspond à « {{term}} ».',
          refresh: 'Actualiser',
          refreshTitle: 'Actualiser les données',
          showingStudents: 'Affichage de {{filtered}} sur {{total}} élèves',
          depositDue: 'Dépôt dû',
          reportOnly: 'Rapport seul',
          prepaid: 'Prépayé',
          balanceCredit: '{{amount}} Crédit',
          balanceDue: '{{amount}} dû',
          amountDue: 'Dû {{amount}}',
          estimateHidden: 'Est. {{amount}} masqué',
          estimateHiddenEmail: 'Est. {{amount}} masqué dans l\'e-mail',
          sessionCountShort: '{{count}} séances',
          confirmSendAllMsg_one: 'Mise en file pour {{count}} élève.{{skipNote}}',
          confirmSendAllMsg_other: 'Mise en file pour {{count}} élèves.{{skipNote}}',
          skipNote_one: ' {{count}} élève déjà ENVOYÉ/EN ATTENTE sera ignoré.',
          skipNote_other: ' {{count}} élèves déjà ENVOYÉ/EN ATTENTE seront ignorés.',
          studentsWithActivity_one: '{{count}} élève avec activité de paiement/inscription',
          studentsWithActivity_other: '{{count}} élèves avec activité de paiement/inscription',
          autoRange_one: 'Auto : {{range}} ({{count}} jour)',
          autoRange_other: 'Auto : {{range}} ({{count}} jours)',
          paymentReminder: 'Rappel de paiement'
        }
      },
      calendar: {
        eyebrow: 'Gérer',
        title: 'Calendrier des séances',
        subtitleStaff: 'Toutes les séances planifiées en un coup d\'œil',
        subtitleStudent: 'Vos séances inscrites et présence',
        subtitleParent: 'Séances inscrites et présence de votre enfant',
        subtitleCoach: 'Vos séances — filtrez par élève du roster pour voir uniquement leurs séances',
        loadingSubtitle: 'Vos séances inscrites et présence',
        allSessions: 'Toutes les séances',
        myBranches: 'Mes sites',
        mySessions: 'Mes séances',
        pending: '{{count}} en attente',
        completed: '{{count}} terminées',
        today: 'Aujourd\'hui',
        month: 'Mois',
        week: 'Semaine',
        viewing: 'Affichage',
        searchName: 'Rechercher un nom…',
        coaches: 'Entraîneurs',
        students: 'Élèves',
        myRosterStudents: 'Mes élèves du roster',
        noRosterStudents: 'Pas encore d\'élèves au roster',
        noResults: 'Aucun résultat',
        student: 'élève',
        coach: 'entraîneur',
        batches: 'Groupes :',
        showAll: 'Tout afficher',
        batchFilterHintAll: 'Cliquez un groupe pour n\'afficher que ses séances · cliquez d\'autres groupes pour combiner',
        batchFilterHintSelected: '{{count}} groupe sélectionné · cliquez un autre pour ajouter · cliquez la sélection pour retirer',
        batchFilterHintSelected_other: '{{count}} groupes sélectionnés · cliquez un autre pour ajouter · cliquez la sélection pour retirer',
        legendPending: 'En attente',
        legendCompleted: 'Terminées',
        legendCancelled: 'Annulées',
        legendBatchColors: 'Couleurs = groupe',
        legendHoliday: '🏖 = jour férié académie',
        legendHover: 'Survoler une séance pour les détails',
        legendPresent: '✓ Présent',
        legendAbsent: '✗ Absent',
        legendClickDay: 'Cliquer un jour pour la liste complète',
        legendClickAttendance: 'Cliquer pour ouvrir la présence',
        shortcuts: 'Raccourcis : ← → naviguer · T aujourd\'hui',
        shortcutsStaff: ' · Cliquer un jour pour la liste des séances',
        loadFailed: 'Échec du chargement des séances. Veuillez réessayer.',
        noSessionsMonth: 'Aucune séance planifiée en {{month}} {{year}}',
        noSessionsHint: 'Essayez un autre mois ou vérifiez vos filtres de groupe',
        holiday: 'Jour férié',
        months: {
          '1': 'janvier',
          '2': 'février',
          '3': 'mars',
          '4': 'avril',
          '5': 'mai',
          '6': 'juin',
          '7': 'juillet',
          '8': 'août',
          '9': 'septembre',
          '10': 'octobre',
          '11': 'novembre',
          '12': 'décembre'
        },
        session: 'Séance',
        level: 'Niveau',
        sessionCoach: 'Entraîneur de séance',
        time: 'Heure',
        sessionStatus: 'Statut de séance',
        attendance: 'Présence',
        sessionNotes: 'Notes de séance',
        present: 'Présent',
        absent: 'Absent',
        marked: 'Marqué',
        notMarked: 'Non marqué',
        more: '+{{count}} de plus',
        showLess: 'Afficher moins',
        sessionsCount: '{{count}} séance',
        sessionsCount_other: '{{count}} séances',
        sessAbbr: '{{count}} séa.',
        noSessionsDay: 'Aucune séance ce jour.',
        prevYear: 'Année précédente',
        nextYear: 'Année suivante',
        prev: 'Précédent',
        next: 'Suivant',
        close: 'Fermer'
      },
      notificationsPage: {
        title: 'Notifications',
        subtitle: 'Alertes et rappels de votre espace académie.',
        markAllRead: 'Tout marquer comme lu',
        empty: 'Tout est à jour — aucune notification pour l\'instant.',
        pageOf: 'Page {{page}} sur {{totalPages}} · {{total}} au total',
        previous: 'Précédent',
        next: 'Suivant',
        justNow: 'à l\'instant',
        minutesAgo: 'il y a {{count}} min',
        hoursAgo: 'il y a {{count}} h',
        daysAgo: 'il y a {{count}} j',
        subtitleValue: 'Alertes actionnables pour portefeuilles, présence et demandes de confidentialité — ouvrez un élément pour aller à l’écran concerné.',
        unreadBanner: 'Vous avez {{count}} notification(s) non lue(s). Ouvrez-en une pour examiner et agir.',
        emptyHint: 'Quand des élèves passent sous le seuil de portefeuille, qu’une absence est saisie ou qu’une DSAR est demandée, les alertes apparaissent ici avec un lien direct.'
      },
      form: {
        sessionDays: 'Jours de séance',
        selectOneDay: 'Sélectionnez au moins un jour'
      },
      faq: {
        title: 'Foire aux questions',
        subtitle: '{{count}} questions couvrant tout ce que vous pouvez faire en tant que {{role}}.',
        findAnswers: 'Aide',
        searchHeading: 'Trouver des réponses',
        searchHint: 'Recherchez par mot-clé ou parcourez les sujets ci-dessous.',
        searchPlaceholder: 'Rechercher une question…',
        allSections: 'Tout',
        noResults: 'Aucun résultat trouvé',
        tryDifferent: 'Essayez un autre mot-clé ou effacez la recherche',
        noQuestionsMatch: 'Aucune question ne correspond à votre recherche.',
        resultsFor: '{{count}} résultat pour « {{query}} »',
        resultsFor_other: '{{count}} résultats pour « {{query}} »',
        footerPrompt: 'Vous ne trouvez pas ce que vous cherchez ?',
        footerAction: 'Contactez l\'administrateur de votre académie.',
        roles: {
          ADMIN: 'Admin',
          MANAGER: 'Responsable',
          COACH: 'Entraîneur',
          STUDENT: 'Élève',
          PARENT: 'Parent'
        }
      },
      settings: {
        email: {
          title: 'Paramètres e-mail',
          loadingSubtitle: 'Chargement de la configuration SMTP…',
          subtitle: 'Configurez le compte SMTP utilisé par votre académie pour envoyer les liens de connexion, rappels, relevés et autres e-mails système.',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          stepThree: 'Étape 3',
          smtpSection: 'Connexion SMTP',
          smtpSectionDesc: 'Identifiants serveur utilisés pour envoyer les e-mails système de votre académie.',
          fromSection: 'Identité de l\'expéditeur',
          fromSectionDesc: 'Comment les destinataires voient les adresses From et Reply-To.',
          testSection: 'Vérifier la livraison',
          testSectionDesc: 'Envoyez un message test pour confirmer les identifiants avant la mise en production.',
          statusLabel: 'Statut',
          modeEnabled: 'Envoi activé',
          modeConfigured: 'Configuré',
          modeIncomplete: 'Incomplet',
          saveSettings: 'Enregistrer les paramètres',
          saveBeforeTest: 'Enregistrez les paramètres SMTP avant d\'envoyer un e-mail test.',
          testCompleted: 'Test terminé.',
          testFailed: 'Échec de l\'e-mail test.',
          useStarttlsDesc: 'Chiffrer la session SMTP après connexion. Recommandé pour le port 587.',
          lastTestPassedHint: 'Pour Gmail, nous confirmons le dossier Envoyés. Les destinataires doivent aussi vérifier les indésirables.',
          lastTestFailedHint: 'Vérifiez les identifiants et réessayez.',
          infoBanner: 'Il s\'agit de votre configuration <strong>d\'envoi sortant</strong>. Le contact support public est géré sous <1>Image de marque</1>. Les bascules de notification sont sous <2>Notifications</2>.',
          enableTitle: 'Activer l\'e-mail sortant',
          enableHint: 'Nécessite des identifiants SMTP valides ci-dessous.',
          smtpHost: 'Hôte SMTP',
          smtpPort: 'Port SMTP',
          smtpUsername: 'Nom d\'utilisateur SMTP',
          smtpPassword: 'Mot de passe SMTP / mot de passe d\'application',
          passwordConfigured: '(déjà défini — laisser vide pour conserver)',
          passwordRequired: 'Obligatoire',
          fromEmail: 'Adresse e-mail expéditeur',
          fromName: 'Nom d\'affichage expéditeur',
          fromNamePlaceholder: 'Par défaut : nom de l\'académie',
          replyTo: 'E-mail de réponse',
          replyToPlaceholder: 'Par défaut : e-mail support depuis Image de marque',
          useStarttls: 'Utiliser STARTTLS (recommandé pour le port 587)',
          enableWarning: 'Saisissez l\'hôte SMTP, l\'e-mail expéditeur et le mot de passe avant d\'activer l\'e-mail sortant.',
          testRecipient: 'Envoyer un e-mail test à',
          sendTest: 'Envoyer le test',
          verifying: 'Vérification… (jusqu\'à 45 s)',
          lastTestPassed: 'Dernier test réussi',
          lastTestFailed: 'Dernier test échoué',
          loadFailed: 'Échec du chargement des paramètres e-mail.',
          saveSuccess: 'Paramètres e-mail enregistrés.',
          saveFailed: 'Échec de l\'enregistrement des paramètres e-mail.'
        },
        whatsapp: {
          title: 'WhatsApp (WATI)',
          loadingSubtitle: 'Chargement des paramètres…',
          subtitle: 'Connectez votre compte WATI pour envoyer rappels de séances, alertes de portefeuille et autres messages WhatsApp. Les coûts sont facturés par WATI/Meta à votre académie.',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          stepThree: 'Étape 3',
          connectionSection: 'Connexion',
          connectionSectionDesc: 'Statut de licence, interrupteur principal et identifiants WATI pour WhatsApp sortant.',
          templatesSection: 'Modèles de messages',
          templatesSectionDesc: 'Noms exacts des modèles approuvés dans votre compte WATI.',
          localeTemplateNote: 'Pour les locales de/fr/nl, l\'app envoie « {{example}} » (nom de base + _{{lang}}). Enregistrez les modèles correspondants dans WATI.',
          recentLogDesc: 'Dernières tentatives WhatsApp sortantes pour cette académie.',
          statusLabel: 'Statut',
          modeUnlicensed: 'Non licencié',
          modeEnabled: 'Envoi activé',
          modeOff: 'Désactivé',
          saveSettings: 'Enregistrer les paramètres',
          notLicensed: 'WhatsApp n\'est pas inclus dans votre licence ChessDesk actuelle. Contactez Axeron Technologies pour une mise à niveau (Professional ou Enterprise, ou activez <strong>WhatsApp Enabled</strong> sur votre ligne de licence).',
          enableTitle: 'Activer WhatsApp',
          enableHint: 'Interrupteur principal de l\'académie (licence + identifiants WATI ou mode simulation requis).',
          mockMode: 'Mode simulation',
          mockModeDesc: 'Journaliser les messages localement sans appeler WATI (recommandé pour dev/test)',
          apiBaseUrl: 'URL de base API WATI',
          apiBaseUrlNote: 'Depuis le tableau de bord WATI → Documentation API',
          channelNumber: 'Numéro de canal professionnel',
          channelNumberNote: 'Votre numéro WhatsApp WATI (requis pour l\'envoi en direct).',
          bearerToken: 'Jeton Bearer WATI',
          tokenPlaceholder: 'Coller le jeton Bearer depuis la documentation API WATI',
          templatesTitle: 'Noms de modèles approuvés dans WATI',
          templateSessionReminder: 'Rappel de séance (élèves)',
          templateAbsenceAlert: 'Alerte d\'absence (élèves)',
          templateLowBalance: 'Portefeuille bas (élèves)',
          templateFeeReminder: 'Frais / impayés (élèves)',
          templatePendingSessions: 'Séances en attente (entraîneurs)',
          templateLowBalanceAdmin: 'Alerte portefeuille bas (admins)',
          templateSessionCancelled: 'Séance annulée (élèves)',
          templateHolidayAnnouncement: 'Annonce de jour férié (personnel & élèves)',
          templateCorrespondenceDeadline: 'Échéance correspondance (plateforme de jeu)',
          templateCorrespondenceYourMove: 'À vous de jouer — correspondance (plateforme de jeu)',
          enableWarning: 'Saisissez les identifiants WATI et le numéro de canal, ou activez le mode simulation avant d\'activer WhatsApp.',
          recentLog: 'Journal des messages récents',
          noMessages: 'Aucun message WhatsApp en file d\'attente.',
          when: 'Quand',
          phone: 'Téléphone',
          type: 'Type',
          status: 'Statut',
          unavailable: {
            notAvailableTitle: 'Les notifications WhatsApp ne sont pas disponibles',
            notAvailableBody: 'Votre académie n\'a pas activé la messagerie WhatsApp sur ChessDesk. {{phoneHint}} Contactez le bureau de votre académie si vous avez besoin de mises à jour de séances ou de solde.',
            notSetupTitle: 'WhatsApp n\'est pas encore configuré',
            notSetupBody: 'Votre académie n\'a pas encore activé les notifications WhatsApp. Une fois WhatsApp connecté, vous pourrez choisir ici quelles alertes recevoir. {{phoneHint}}',
            phoneHintParent: 'Lorsque WhatsApp sera disponible, les alertes iront au numéro de téléphone du profil de votre enfant.',
            phoneHintStudent: 'Lorsque WhatsApp sera disponible, les alertes iront au numéro de téléphone de votre profil.'
          },
          loadFailed: 'Échec du chargement des paramètres WhatsApp.',
          saveSuccess: 'Paramètres WhatsApp enregistrés.',
          saveFailed: 'Échec de l\'enregistrement des paramètres.',
          testTitle: 'Envoyer un message test',
          testDesc: 'Résout le modèle de rappel de séance pour la locale de l\'académie (y compris _de/_fr/_nl) et envoie via WATI ou le mode mock.',
          testPhone: 'Téléphone destinataire (E.164)',
          sendTest: 'Envoyer le test',
          testing: 'Test…',
          testFailed: 'Échec du test WhatsApp.'
        },
        chessCoach: {
          title: 'Ask AI Coach',
          loadingSubtitle: 'Chargement des paramètres Ask AI Coach…',
          subtitle: 'Activez le chatbot Ask AI Coach et saisissez une clé API compatible OpenAI (OpenAI, Groq, OpenRouter, etc.).',
          infoBanner: 'Une fois activé et configuré, Ask AI Coach apparaît pour les élèves, parents et le staff dans Playing. L’analyse moteur reste sur votre serveur ; la clé API sert uniquement aux explications en langage naturel.',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          connectionSection: 'Activation et connexion',
          connectionSectionDesc: 'Interrupteur principal et identifiants du modèle de langage qui alimente Ask AI Coach.',
          limitsSection: 'Limites et fair-play',
          limitsSectionDesc: 'Limites de débit, longueur des réponses et politique en partie classée en direct.',
          statusLabel: 'Statut',
          modeOperational: 'En ligne',
          modeEnabledIncomplete: 'Activé — clé API manquante',
          modeConfigured: 'Configuré (désactivé)',
          modeIncomplete: 'Non configuré',
          enableTitle: 'Activer Ask AI Coach',
          enableHint: 'Affiche le chat coach pour les utilisateurs de l’académie lorsqu’une clé API est enregistrée.',
          enableWarning: 'Collez une clé API avant d’activer Ask AI Coach.',
          baseUrl: 'URL de base de l’API',
          baseUrlHint: 'Base chat completions compatible OpenAI (sans slash final). Ex. Groq : https://api.groq.com/openai/v1',
          model: 'Modèle',
          modelHint: 'Identifiant du modèle, ex. gpt-4o-mini ou llama-3.3-70b-versatile',
          apiKey: 'Clé API',
          apiKeyPlaceholder: 'Coller la clé API du fournisseur',
          apiKeyConfigured: '(déjà définie — laisser vide pour conserver)',
          apiKeyHint: 'Stockée dans la base de l’académie. Ne sera plus affichée après enregistrement.',
          maxTokens: 'Jetons max. par réponse',
          rateLimit: 'Questions par utilisateur / minute',
          maxHistory: 'Tours d’historique envoyés au modèle',
          timeoutMs: 'Délai d’attente (ms)',
          liveRatedIdeasOnly: 'Idées uniquement en parties classées en direct',
          liveRatedIdeasOnlyDesc: 'Dans les parties humaines classées actives, discuter des plans seulement — jamais suggérer un meilleur coup concret.',
          saveSettings: 'Enregistrer',
          loadFailed: 'Échec du chargement des paramètres Ask AI Coach.',
          saveSuccess: 'Paramètres Ask AI Coach enregistrés.',
          saveFailed: 'Échec de l’enregistrement des paramètres Ask AI Coach.'
        },
        production: {
          title: 'Prêt pour la production UE',
          loadingSubtitle: 'Vérification des prérequis de mise en production…',
          subtitle: 'Liste de contrôle : webhooks HTTPS, SMTP, modèles WhatsApp localisés, fuseau horaire et accusé de revue juridique des projets UE.',
          loadFailed: 'Impossible de charger l\'état de production.',
          readyBanner: 'Aucun bloqueur in-app — finalisez les clés Mollie/Stripe live et les modèles Meta hors ChessDesk.',
          notReadyBanner: 'Résolvez les bloqueurs ci-dessous avant la mise en production UE.',
          readyHint: 'Cet écran ne crée pas de comptes marchands ni de signatures d\'avocat ; il suit ce que ChessDesk peut vérifier.',
          checklistTitle: 'Liste de contrôle',
          open: 'Ouvrir',
          checkHttps: 'URL HTTPS publique (FRONTEND_BASE_URL)',
          checkMollieWebhook: 'URL webhook Mollie configurée',
          checkSmtp: 'SMTP activé et opérationnel',
          smtpLastTestFailed: 'Dernier test SMTP échoué',
          checkWhatsapp: 'WhatsApp (si licencié)',
          whatsappSuffix: 'Suffixe locale {{suffix}} — enregistrer les modèles Meta correspondants',
          whatsappEnglish: 'Noms de modèles de base en anglais',
          checkLegal: 'Revue conseil juridique confirmée',
          checkTimezone: 'Fuseau horaire académie (paramètres régionaux)',
          timezoneDetail: '{{academy}}',
          webhooksTitle: 'URL de webhooks à enregistrer',
          webhooksDesc: 'À copier dans les tableaux de bord Stripe, PayPal, Mollie et Razorpay.',
          templatesTitle: 'Noms de modèles WhatsApp résolus',
          templatesDesc: 'Enregistrer ces noms exacts dans Meta/WATI pour la locale de l\'académie.',
          legalTitle: 'Pack juridique UE & accusé de revue',
          legalDesc: 'Projets prêts pour avocat, non définitifs. Confirmer seulement après revue par un avocat admis dans la juridiction concernée.',
          docPrivacyEu: 'Confidentialité (UE)',
          docDpaEu: 'Annexe DPA (UE)',
          docPrivacy: 'Confidentialité',
          docDpa: 'DPA',
          docTerms: 'Conditions',
          docEula: 'EULA',
          docLoading: 'Chargement…',
          docLoadFailed: 'Impossible de charger le document.',
          confirmLabel: 'Je confirme qu\'un avocat a examiné le pack juridique UE actuel pour cette académie, et que nous conserverons des copies signées.',
          confirmRequired: 'Cochez d\'abord la case de confirmation.',
          notesLabel: 'Notes (optionnel)',
          notesPlaceholder: 'ex. cabinet, date de revue',
          ackButton: 'Enregistrer l\'accusé de revue',
          ackSuccess: 'Accusé de revue enregistré.',
          ackFailed: 'Impossible d\'enregistrer l\'accusé.',
          alreadyAcked: 'Confirmé par {{by}} le {{at}} (version {{version}}).'
        },
        notifications: {
          title: 'Préférences de notification',
          loadingSubtitle: 'Chargement des valeurs par défaut de l\'académie…',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          stepThree: 'Étape 3',
          stepFour: 'Étape 4',
          saveSettings: 'Enregistrer les paramètres',
          subtitle: 'Valeurs par défaut pour e-mail, WhatsApp (WATI) et alertes in-app. Les modifications s\'appliquent aux messages automatiques du système.',
          savePreferences: 'Enregistrer les préférences',
          saveSuccess: 'Préférences enregistrées avec succès.',
          saveFailed: 'Impossible d\'enregistrer les préférences. Veuillez réessayer.',
          emailPanel: 'E-mail',
          emailDesc: 'Messages SMTP aux élèves, entraîneurs et personnel lors d\'événements.',
          emailFooterOn: 'Désactivez l\'interrupteur principal pour suspendre tous les e-mails sortants.',
          emailFooterOff: 'Configurez le SMTP sortant sous Système → Paramètres e-mail avant d\'activer les notifications e-mail.',
          emailNotConfigured: 'L\'e-mail sortant n\'est pas encore configuré. Allez d\'abord à <strong>Système → Paramètres e-mail</strong>.',
          whatsappPanel: 'WhatsApp',
          whatsappNotLicensed: 'WhatsApp n\'est pas inclus dans votre licence. Activez-le sur votre feuille de licence ou mettez à niveau votre plan.',
          whatsappDesc: 'Utilise les identifiants WATI de Système → WhatsApp. Les élèves peuvent se désabonner par type de message dans leur portail.',
          notLicensedBadge: 'Non licencié',
          whatsappFooter: 'Configurez les clés API sous Système → WhatsApp.',
          whatsappMaster: 'Notifications WhatsApp (principal)',
          studentsSection: 'Élèves',
          staffSection: 'Personnel & académie',
          inappPanel: 'In-app',
          inappDesc: 'Notifications cloche dans le tableau de bord pour admins, responsables et entraîneurs.',
          walletPanel: 'Alertes portefeuille',
          walletDesc: 'Utilisé pour les avertissements de solde bas par e-mail et WhatsApp lorsque ces canaux sont activés.',
          lowWalletThreshold: 'Seuil de solde portefeuille bas',
          lowWalletHelper: 'Notifier lorsque le portefeuille d\'un élève descend sous ce montant.',
          walletTip: '<strong>Astuce :</strong> Les désabonnements par élève se trouvent dans les paramètres de notification de chaque élève. Cette page contrôle ce que l\'académie peut envoyer par défaut.',
          emailMaster: 'Notifications e-mail (principal)',
          emailFeeReminders: 'E-mails de rappel de frais',
          emailSessionRemindersStudent: 'Rappels de séance pour élèves',
          emailSessionRemindersCoach: 'Rappels séances en attente (entraîneurs)',
          emailAbsenceAlerts: 'E-mails d\'alerte d\'absence',
          emailSessionCancelled: 'E-mails de séance annulée',
          emailOnlineTopupAdmin: 'Paiement en ligne reçu (alerte admin)',
          inappMaster: 'Notifications in-app (principal)',
          inappFeeAlerts: 'Alertes de frais in-app',
          inappPendingSessions: 'Alertes séances en attente in-app',
          inappAtRiskStudents: 'Alertes élèves à risque in-app',
          waSessionReminders: 'Rappels de séance',
          waAbsenceAlerts: 'Alertes d\'absence',
          waLowBalance: 'Avertissements solde bas',
          waFeeReminders: 'Rappels frais / impayés',
          waSessionCancelled: 'Avis de séance annulée',
          waPendingSessions: 'Séances en attente (entraîneurs)',
          waLowBalanceAdmin: 'Alertes solde bas (admins)',
          waHolidayAnnouncements: 'Annonces de jours fériés',
          waCorrespondenceDeadline: 'Échéance correspondance (plateforme de jeu)',
          waCorrespondenceYourMove: 'À vous de jouer — correspondance (plateforme de jeu)'
        },
        billing: {
          title: 'Application des paiements',
          subtitle: 'Contrôler le comportement de présence et de recouvrement des frais',
          loadFailed: 'Échec du chargement des paramètres d\'application des paiements.',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          stepThree: 'Étape 3',
          modeLabel: 'Mode actuel',
          modeStrict: 'Strict — payer d\'abord',
          modeGrace: 'Grâce autorisée',
          modeFlexible: 'Flexible',
          attendanceSection: 'Présence et frais impayés',
          attendanceSectionDesc: 'Décidez si les élèves peuvent assister quand la période n\'est pas payée, et combien de temps dure la grâce.',
          requirePayment: 'Exiger le paiement avant la présence',
          requirePaymentDesc: 'Le personnel ne peut pas inscrire sans paiement ; le portefeuille doit être débité à l\'inscription.',
          allowUnpaidPeriod: 'Autoriser la présence tant que la période n\'est pas payée',
          allowUnpaidPeriodDesc: 'Les élèves peuvent assister après un échec de débit de période, jusqu\'aux limites de grâce.',
          graceSection: 'Limites de grâce',
          graceSectionDesc: 'Laisser vide = pas de plafond. La première limite atteinte bloque la présence.',
          graceCalendarDays: 'Jours de grâce calendaires (optionnel)',
          graceSessions: 'Séances de grâce (optionnel)',
          graceOptionalNote: 'Vide = pas de limite pour cette règle',
          walletSection: 'Portefeuille et recouvrement',
          walletSectionDesc: 'Comportement des débits à solde bas, et durée des relances automatiques.',
          allowNegativeWallet: 'Autoriser un portefeuille négatif lors du débit',
          allowNegativeWalletDesc: 'Les débits s\'enregistrent même à zéro — l\'élève porte une dette.',
          collectionWindow: 'Fenêtre de relance de recouvrement (jours calendaires)',
          collectionWindowDesc: 'Nombre de jours pendant lesquels le système relance les frais de période impayés (1–14).',
          remindersSection: 'Rappels',
          remindersSectionDesc: 'Avis automatiques lorsque les inscriptions arrivent à échéance.',
          expiryReminders: 'Envoyer des rappels d\'expiration d\'inscription',
          expiryRemindersDesc: 'Notifier les élèves (et tuteurs liés) avant la fin d\'inscription.',
          saveSettings: 'Enregistrer les paramètres',
          saveSuccess: 'Paramètres enregistrés.',
          saveFailed: 'Échec de l\'enregistrement des paramètres.'
        },
        regional: {
          title: 'Paramètres régionaux',
          subtitle: 'Configurer locale, devise et affichage des dates/heures dans l\'app, les e-mails et les PDF. Passerelles sous Système → Moyens de paiement.',
          quickPreset: 'Préréglage rapide',
          custom: 'Personnalisé',
          sampleAmount: 'Montant exemple :',
          sampleDate: 'Date exemple :',
          sampleTime: 'Heure exemple :',
          timezoneLabel: 'Fuseau horaire :',
          cardLocale: 'Locale & fuseau horaire',
          cardCountry: 'Pays & téléphone',
          cardCurrency: 'Devise',
          cardTax: 'Taxe (affichage uniquement)',
          cardDateTime: 'Date et heure',
          timezone: 'Fuseau horaire',
          timezoneNote: 'Utilisé pour les horodatages d\'export PDF et les valeurs régionales par défaut.',
          localeTag: 'Tag locale (BCP 47)',
          localeTagNote: 'Contrôle le regroupement des nombres et l\'ordre des dates dans l\'app web.',
          countryCode: 'Code pays (ISO)',
          countryName: 'Nom du pays',
          phoneDialCode: 'Indicatif téléphonique',
          phoneFormatHint: 'Indication format téléphone',
          countryNote: 'Changer de pays met à jour les moyens de paiement disponibles (UPI pour l\'Inde uniquement, etc.).',
          currencyNote: 'Tous les montants de portefeuille et frais de séance utilisent cette devise.',
          currencyCode: 'Code devise (ISO 4217)',
          currencySymbol: 'Symbole devise',
          decimalPlaces: 'Décimales',
          symbolPosition: 'Position du symbole',
          symbolBefore: 'Avant le montant ({{example}})',
          symbolAfter: 'Après le montant ({{example}})',
          taxNote: 'ChessDesk affiche les libellés fiscaux mais ne génère pas de factures fiscales légales.',
          taxLabel: 'Libellé taxe',
          taxRate: 'Taux de taxe (%)',
          taxId: 'ID fiscal',
          taxIdLabel: 'Libellé champ ID fiscal',
          taxIncluded: 'Taxe incluse dans les prix affichés',
          taxIncludedYes: 'Oui (TTC)',
          taxIncludedNo: 'Non (HT)',
          timeFormat: 'Format horaire (app web)',
          timeFormatNote: 'Séances, présence, calendrier et sélecteurs d\'heure.',
          time24: '24 heures (14:30)',
          time12: '12 heures (02:30 PM)',
          datetimeFormat: 'Format date & heure (e-mails & PDF)',
          datetimeFormatNote: 'Modèle Java pour rapports, rappels et reçus.',
          saveSettings: 'Enregistrer les paramètres',
          saving: 'Enregistrement…',
          loadFailed: 'Échec du chargement des paramètres régionaux.',
          saveSuccess: 'Paramètres régionaux enregistrés avec succès.',
          saveFailed: 'Échec de l\'enregistrement des paramètres régionaux.',
          placeholders: {
            localeTag: 'fr-FR',
            countryCode: 'FR',
            countryName: 'France',
            phoneDialCode: '+33',
            phoneFormatHint: '+33 X XX XX XX XX',
            currencyCode: '',
            currencySymbol: '',
            taxLabel: 'TVA',
            taxIdLabel: 'N° TVA',
            taxRegistration: 'Votre académie {{label}}'
          }
        },
        branding: {
          title: 'Image de marque personnalisée',
          subtitle: 'Définissez le nom, le slogan et le logo de votre académie. Les changements prennent effet immédiatement — pas de reconstruction nécessaire.',
          loadingSubtitle: 'Chargement des paramètres de marque…',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          stepThree: 'Étape 3',
          stepFour: 'Étape 4',
          sectionIdentityDesc: 'Affiché dans l\'en-tête, l\'écran de connexion et le pied de page. Laisser vide pour les valeurs par défaut du build.',
          sectionLogoDesc: 'Téléversez un logo carré pour les en-têtes, écrans de connexion et exports PDF.',
          appNameNote: 'Affiché dans l\'en-tête, l\'écran de connexion et le pied de page. Laisser vide pour la valeur par défaut du build ({{default}}).',
          taglineNote: 'Courte ligne sous le nom de l\'app dans l\'en-tête. Laisser vide pour la valeur par défaut du build.',
          saveSettings: 'Enregistrer les paramètres',
          saveSuccess: 'Paramètres de marque enregistrés avec succès.',
          saveFailed: 'Échec de l\'enregistrement des paramètres de marque.',
          enterpriseSubtitle: 'Modifications de logo et nom en runtime disponibles uniquement avec la licence Enterprise.',
          enterpriseFeature: 'Fonction Enterprise',
          enterpriseNote: 'Les licences non-Enterprise continuent d\'utiliser l\'image de marque par défaut du déploiement.',
          loadFailed: 'Échec du chargement des paramètres de marque',
          appNameTagline: 'Nom de l\'app & slogan',
          appName: 'Nom app / académie',
          tagline: 'Slogan',
          saveText: 'Enregistrer le texte',
          logoImage: 'Image du logo',
          clickChooseLogo: 'Cliquer pour choisir un logo',
          logoHint: 'PNG, JPG, SVG, WebP · max 2 Mo · recommandé 256 × 256 px',
          removeLogo: 'Supprimer le logo personnalisé',
          removing: 'Suppression…',
          uploadLogo: 'Téléverser le logo',
          uploading: 'Téléversement…',
          logoActive: 'Un logo personnalisé est actif.',
          logoDefault: 'Aucun logo personnalisé — le logo par défaut du build est utilisé.',
          academyContact: 'Contact académie',
          contactNote: 'Affiché dans le pied de page de connexion et la page d\'aide pour élèves, parents et personnel.',
          supportEmail: 'E-mail support',
          supportPhone: 'Téléphone support',
          saveContact: 'Enregistrer le contact',
          livePreview: 'Aperçu en direct',
          previewNote: 'Voici comment l\'en-tête apparaîtra aux utilisateurs.',
          loginPreview: 'Aperçu écran de connexion',
          instantNote: '⚡ Instantané & persistant',
          instantDesc: 'Les changements sont stockés en base et pris en compte au prochain chargement de page — pas de redémarrage requis.',
          removeLogoConfirm: 'Supprimer le logo personnalisé ? Le logo par défaut sera utilisé.',
          logoUploadSuccess: 'Logo téléversé avec succès.',
          logoUploadFailed: 'Échec du téléversement du logo',
          logoRemoved: 'Logo personnalisé supprimé. Le logo par défaut est maintenant actif.',
          logoRemoveFailed: 'Échec de la suppression du logo',
          contactUpdated: 'Coordonnées de l\'académie mises à jour.',
          contactSaveFailed: 'Échec de l\'enregistrement des coordonnées',
          textUpdated: 'Texte de marque mis à jour avec succès.',
          textSaveFailed: 'Échec de l\'enregistrement du texte de marque',
          validImageFile: 'Veuillez sélectionner un fichier image valide (PNG, JPG, SVG, etc.)',
          logoTooLargeAlert: 'Le logo doit être inférieur à 2 Mo'
        },
        license: {
          title: 'Licence',
          subtitle: 'Capacité du plan, utilisation actuelle et référence de tous les niveaux de licence ChessDesk. Les limites sont définies par votre fournisseur.',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          stepThree: 'Étape 3',
          stepFour: 'Étape 4',
          yourLicense: 'Votre licence',
          yourLicenseDesc: 'Édition, expiration et clé délivrées par votre fournisseur.',
          standardPlan: '{{edition}} · plafonds standard {{caps}}',
          loadFailed: 'Échec du chargement des informations de licence.',
          edition: 'Édition',
          expiry: 'Expiration',
          licenseKey: 'Clé de licence',
          notSpecified: 'Non spécifié',
          unlimited: 'Illimité',
          daysRemaining: '{{count}} jour restant',
          daysRemaining_other: '{{count}} jours restants',
          slotsRemaining: '{{count}} place restante',
          slotsRemaining_other: '{{count}} places restantes',
          atLimit: 'Limite atteinte',
          nearLimit: 'Proche de la limite',
          capacityTitle: 'Votre capacité (tous élèves, entraîneurs & sites)',
          capacityNote: 'Les élèves et entraîneurs comptent actifs ou archivés. À la limite du plan, les comptes archivés peuvent être <strong>supprimés définitivement</strong> pour libérer une place.',
          students: 'Élèves',
          branches: 'Sites',
          coaches: 'Entraîneurs',
          featuresTitle: 'Fonctionnalités de votre plan',
          featureCore: 'Facturation portefeuille, présence, séances et rapports',
          featureBranding: 'Logo et nom d\'académie personnalisés :',
          featureWhatsapp: 'Intégration WhatsApp (WATI) :',
          featureBulkImport: 'Import CSV groupé d\'élèves :',
          plansTitle: 'Plans & fonctionnalités pris en charge',
          plansDesc: 'Référence de tous les niveaux de licence ChessDesk. Vos limites actives peuvent différer si le fournisseur a défini des plafonds personnalisés.',
          yourPlan: 'Votre plan',
          capacityLimits: 'Limites de capacité (standard)',
          tierCaps: 'jusqu\'à {{students}} élèves, {{branches}} succursales, {{coaches}} entraîneurs',
          plans: { starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' },
          featureGroups: { core: 'Plateforme principale', multiBranch: 'Multi-succursales & paiements', enterprise: 'Enterprise' },
          features: {
            attendance: 'Présence, séances et calendrier',
            billing: 'Facturation, factures et portefeuille élève',
            reports: 'Rapports et export Excel',
            portal: 'Portail élèves et parents',
            email: 'Notifications e-mail',
            atRisk: 'Détection des élèves à risque',
            multiBranch: 'Gestion multi-succursales',
            managerRole: 'Rôle manager par succursale',
            crossBranch: 'Rapports inter-succursales',
            onlinePayments: 'Passerelles de paiement (Razorpay, Stripe, PayPal)',
            whatsapp: 'Notifications WhatsApp (WATI)',
            bulkImport: 'Import CSV d\'élèves en masse',
            customBranding: 'Logo et nom d\'académie personnalisés'
          },
          activeStudents: 'Élèves actifs',
          included: 'Inclus',
          enterpriseOnly: 'Enterprise uniquement',
          proEnterprise: 'Professional / Enterprise (ou flag feuille)'
        },
        payment: {
          title: 'Passerelles de paiement en ligne',
          subtitle: 'Activez une ou plusieurs passerelles pour les recharges de portefeuille des élèves. Chaque académie utilise ses propres identifiants marchands.',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          stepThree: 'Étape 3',
          gatewaysSection: 'Passerelles de paiement en ligne',
          gatewaysSectionDesc: 'Configurez les identifiants marchands pour les recharges. Seules les passerelles disponibles pour le pays de l\'académie sont listées.',
          noGatewaysForCountry: 'Aucune passerelle en ligne n\'est disponible pour le pays actuel de l\'académie.',
          saveSettings: 'Enregistrer les paramètres',
          enabled: 'Activé',
          loadFailed: 'Échec du chargement des paramètres de paiement.',
          saveSuccess: 'Paramètres de passerelle enregistrés.',
          saveFailed: 'Échec de l\'enregistrement des paramètres.',
          manualMethods: 'Moyens de paiement manuels',
          manualMethodsDesc: 'Utilisés lorsque le personnel enregistre des recharges. Les méthodes non utilisées dans votre pays sont masquées automatiquement.',
          recentActivity: 'Activité de paiement récente',
          recentActivityDesc: '50 derniers événements passerelle — utile pour déboguer webhooks ou recharges échouées.',
          noEvents: 'Aucun événement passerelle pour l\'instant.',
          when: 'Quand',
          gateway: 'Passerelle',
          event: 'Événement',
          status: 'Statut',
          student: 'Élève',
          detail: 'Détail',
          deploymentNote: 'Note de déploiement',
          licenseRequired: 'Les passerelles de paiement en ligne nécessitent une licence <strong>Professional</strong> ou <strong>Enterprise</strong>{{edition}}. Vous pouvez consulter les paramètres ici, mais les élèves ne pourront payer en ligne qu\'après une mise à niveau.',
          licenseEditionSuffix: ' (actuelle : {{edition}})',
          httpsRequired: 'Stripe et PayPal nécessitent une URL HTTPS publique. Base actuelle : <code class="font-mono text-xs">{{url}}</code>. Définissez <code class="font-mono">FRONTEND_BASE_URL=https://…</code> en production.',
          localhostHttpsHint: 'L\'URL de développement {{url}} convient en local. Pour Mollie/Stripe en production, définissez FRONTEND_BASE_URL sur votre domaine https:// public et enregistrez les URL de webhook sur chaque carte de passerelle.',
          academyCurrency: 'Devise de l\'académie : <strong>{{currency}}</strong> — seuls les moyens de paiement pour <strong>{{country}}</strong> sont affichés.',
          yourCountry: 'votre pays',
          gatewayActive: '{{name}} est actif. Les élèves peuvent le choisir pour recharger leur portefeuille.',
          gatewayInactive: '{{name}} est désactivé. Les élèves ne verront pas cette option.',
          secretKeepPlaceholder: '(déjà défini — laisser vide pour conserver)',
          secretSetHelper: '✓ Secret défini. Laisser vide pour conserver la valeur actuelle.',
          secretNotSetHelper: '⚠ Secret non défini — requis pour accepter les paiements.',
          paypalSandbox: 'Mode sandbox PayPal',
          paypalSandboxHint: 'Utiliser l\'API sandbox pour tester avec les identifiants sandbox de developer.paypal.com.',
          testConnection: 'Tester la connexion',
          testing: 'Test…',
          connected: 'Connecté.',
          connectionFailed: 'Échec de la connexion.',
          connectionTestFailed: 'Échec du test de connexion.',
          webhookUrlTitle: 'URL webhook (à enregistrer dans le tableau de bord {{name}}) :',
          subscribeTo: 'S\'abonner à :',
          keysFrom: 'Clés depuis',
          currencyUnsupported: '{{name}} ne prend pas en charge la devise {{currency}} de l\'académie. Modifiez les paramètres régionaux ou choisissez une autre passerelle.',
          httpsGatewayHint: '{{name}} nécessite une URL HTTPS publique (définir FRONTEND_BASE_URL). Localhost OK uniquement en développement.',
          resolveWarnings: 'Résolvez les avertissements ci-dessus avant que {{name}} puisse accepter des paiements en direct.',
          secretRequiredWhenEnabled: '⚠ {{label}} est requis lorsque {{name}} est activé.',
          webhookOptionalHint: 'Facultatif — active une sauvegarde webhook signée si le navigateur ne revient pas.',
          manualMethodDefault: 'Le personnel peut enregistrer des recharges de portefeuille avec cette méthode.',
          deploymentHttps: 'Stripe et PayPal nécessitent une URL HTTPS publique. Votre site doit proxy <code class="font-mono">/api/</code> vers le backend pour que les webhooks atteignent <code class="font-mono">{{webhookBase}}/api/webhooks/…</code>',
          deploymentFrontendUrl: 'Définissez <code class="font-mono">FRONTEND_BASE_URL</code> dans votre environnement pour des URL de retour de paiement correctes.'
        },
        report: {
          title: 'Paramètres des rapports',
          subtitle: 'Choisir les rapports disponibles dans le hub Rapports et configurer les entrées spécifiques.',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          stepThree: 'Étape 3',
          availableReports: 'Quels rapports sont disponibles',
          availableReportsDesc: 'Désactivez un rapport pour le masquer du hub Rapports et le bloquer via l\'API. Les liens de relevé de séance élève/parent suivent le même interrupteur.',
          enabledCountLabel: 'Activés',
          statusOn: 'Activé',
          statusOff: 'Désactivé',
          visibleTo: 'Visible pour',
          appliesTo: 'S\'applique à',
          groups: {
            finance: 'Frais & argent',
            attendance: 'Présence & séances',
            export: 'Export de données'
          },
          coachRevenue: 'Répartition des commissions entraîneur',
          coachRevenueDesc: 'Part académie par type de séance. L\'entraîneur reçoit le reste.',
          batchAcademyShare: 'Séances de groupe — part académie (%)',
          individualAcademyShare: 'Séances individuelles — part académie (%)',
          splitNote: 'Répartition : Entraîneur {{coach}}% / Académie {{academy}}%',
          batchAttendance: 'Options de présence par groupe',
          batchAttendanceDesc: 'Contrôle ce que voient entraîneurs et responsables dans la matrice de présence. Les admins voient toujours les frais.',
          showCharges: 'Afficher les frais de séance aux entraîneurs et responsables',
          showChargesDesc: 'Entraîneurs et responsables voient les montants dans rapports, exports Excel et PDF.',
          saveSettings: 'Enregistrer les paramètres',
          loadFailed: 'Échec du chargement des paramètres de rapport.',
          saveSuccess: 'Paramètres de rapport enregistrés avec succès.',
          saveFailed: 'Échec de l\'enregistrement des paramètres de rapport.',
          percentError: 'Saisissez un pourcentage entre 0 et 100.',
          percentRange: 'Doit être entre 0 et 100.'
        },
        holidays: {
          title: 'Gestion des jours fériés',
          subtitle: 'Bloquer les jours de fermeture académie ou par site. Aucune séance ne peut être planifiée les jours fériés.',
          loadingSubtitle: 'Chargement des jours fériés…',
          stepOne: 'Étape 1',
          stepTwo: 'Étape 2',
          saveSuccessTitle: 'Jour férié enregistré',
          saveSuccessWithEmails: 'Le jour férié a été enregistré et les e-mails de notification sont envoyés à tout le personnel concerné.',
          saveSuccessWithoutEmails: 'Le jour férié a été enregistré sans envoi d\'e-mails de notification.',
          saveHolidayFailed: 'Impossible d\'enregistrer le jour férié.',
          deleteTitle: 'Supprimer le jour férié ?',
          deletePastTitle: 'Supprimer un jour férié passé ?',
          deleteMessage: '"{{name}}" sera supprimé. De nouvelles séances pourront être planifiées à ces dates. Si le personnel a déjà été notifié, informez-le manuellement si nécessaire.',
          deletePastMessage: '"{{name}}" est déjà terminé. La suppression ne met à jour que cette liste et le calendrier. Les séances et présences passées ne changent pas.',
          deleteHint: 'Supprimer le jour férié et autoriser la planification à ces dates',
          deletePastHint: 'Retirer le jour férié terminé de la liste',
          thisHoliday: 'ce jour férié',
          addNew: 'Ajouter un jour férié',
          addNewDesc: 'Définir une période de fermeture pour toute l\'académie ou un site.',
          holidayName: 'Nom du jour férié',
          startDate: 'Date de début',
          endDate: 'Date de fin',
          branch: 'Site pour le jour férié',
          branchHelper: 'Choisir un site uniquement si ce jour férié s\'applique à cet emplacement seul.',
          allBranches: 'Tous les sites (académie entière)',
          notes: 'Notes du jour férié',
          addHoliday: 'Ajouter le jour férié',
          configured: 'Jours fériés configurés',
          total: '{{count}} au total',
          upcomingPast: '· {{upcoming}} à venir · {{past}} passés',
          colName: 'Nom du jour férié',
          colFrom: 'Du',
          colTo: 'Au',
          colBranch: 'Site',
          colActions: 'Actions',
          noHolidays: 'Aucun jour férié configuré.',
          allBranchesBadge: 'Tous les sites',
          past: 'Passé',
          delete: 'Supprimer',
          loadFailed: 'Échec du chargement des jours fériés.',
          nameRequired: 'Le nom du jour férié est requis.',
          datesRequired: 'Les dates de début et de fin sont requises.',
          validDatesFormat: 'Veuillez saisir des dates valides au format {{format}}.',
          previewFailed: 'Impossible de préparer l\'aperçu de l\'e-mail.',
          deleteFailed: 'Impossible de supprimer le jour férié.',
          endDateInvalid: 'La date de fin doit être égale ou postérieure à la date de début.',
          notesPlaceholder: 'Note interne facultative',
          namePlaceholder: 'p. ex. vacances d\'été, congés Diwali',
          emailPreview: {
            title: 'Examiner l\'e-mail de notification de jour férié',
            subtitle: 'Aperçu de l\'e-mail ci-dessous, puis choisissez si {{count}} destinataire pertinent doit être notifié (personnel et élèves le cas échéant).',
            subtitle_other: 'Aperçu de l\'e-mail ci-dessous, puis choisissez si {{count}} destinataires pertinents doivent être notifiés (personnel et élèves le cas échéant).',
            subjectLabel: 'Objet',
            previewLabel: 'Aperçu de l\'e-mail',
            previewHelper: 'Chaque destinataire reçoit une salutation personnalisée ; l\'aperçu montre une mise en page exemple.',
            recipientsLabel: 'Destinataires ({{count}})',
            noRecipients: 'Aucun admin, responsable, entraîneur ou élève actif avec adresse e-mail n\'a été trouvé. Vous pouvez quand même enregistrer le jour férié sans envoyer de notifications.',
            roleBlockTitle: '{{role}} ({{count}})',
            roleSummaryItem: '{{count}} {{role}}',
            roleSummaryItem_other: '{{count}} {{role}}',
            studentBulkNotice: '{{count}} élève avec adresse e-mail recevra cet avis.',
            studentBulkNotice_other: '{{count}} élèves avec adresse e-mail recevront cet avis.',
            moreStudentsHidden: '…et {{count}} autre élève non affiché ici.',
            moreStudentsHidden_other: '…et {{count}} autres élèves non affichés ici.',
            saveWithoutEmails: 'Enregistrer sans e-mails',
            saveAndSend: 'Enregistrer & envoyer les e-mails',
            roles: {
              Admin: 'Admin',
              Manager: 'Responsable',
              Coach: 'Entraîneur',
              Student: 'Élève'
            }
          }
        }
      },
      forms: {
        common: {
          firstName: 'Prénom',
          middleName: 'Deuxième prénom',
          lastName: 'Nom',
          dateOfBirth: 'Date de naissance',
          gender: 'Genre',
          male: 'Homme',
          female: 'Femme',
          other: 'Autre',
          loginUsername: 'Nom d\'utilisateur de connexion',
          emailAddress: 'Adresse e-mail',
          phoneNumber: 'Numéro de téléphone',
          accountActive: 'Compte actif',
          loginPassword: 'Mot de passe de connexion',
          confirmLoginPassword: 'Confirmer le mot de passe',
          roles: 'Rôles',
          select: 'Sélectionner…',
          cancel: 'Annuler',
          save: 'Enregistrer',
          saving: 'Enregistrement…',
          create: 'Créer',
          update: 'Mettre à jour',
          none: '— Aucun —'
        },
        placeholders: {
          firstName: 'Prénom',
          middleName: 'Deuxième prénom',
          lastName: 'Nom',
          firstNameRequired: 'Prénom *',
          lastNameRequired: 'Nom *',
          username: 'Nom d\'utilisateur',
          email: 'E-mail',
          password: 'Mot de passe',
          confirmPassword: 'Confirmer le mot de passe'
        },
        validation: {
          firstNameRequired: 'Le prénom est obligatoire et peut contenir des lettres, espaces, tirets et apostrophes.',
          middleNameLetters: 'Le deuxième prénom peut contenir des lettres, espaces, tirets et apostrophes.',
          firstNameLetters: 'Le prénom peut contenir des lettres, espaces, tirets et apostrophes — pas de chiffres.',
          lastNameLetters: 'Le nom peut contenir des lettres, espaces, tirets et apostrophes — pas de chiffres.',
          lastNameRequired: 'Le nom est obligatoire et peut contenir des lettres, espaces, tirets et apostrophes.',
          invalidDob: 'Veuillez saisir une date de naissance valide dans le passé.',
          usernameRequired: 'Le nom d\'utilisateur est obligatoire.',
          emailInvalid: 'Veuillez saisir une adresse e-mail valide.',
          passwordMin: 'Le mot de passe doit comporter au moins 6 caractères.',
          passwordsMismatch: 'Le mot de passe et la confirmation ne correspondent pas.',
          rolesRequired: 'Veuillez sélectionner au moins un rôle.',
          invalidAddress: 'Adresse invalide.',
          invalidLatitude: 'La latitude doit être un nombre valide.',
          invalidLongitude: 'La longitude doit être un nombre valide.'
        },
        phone: {
          countryCode: 'Indicatif pays',
          searchCountry: 'Rechercher un pays',
          noMatches: 'Aucune correspondance',
          phoneNumber: 'Numéro de téléphone'
        },
        address: {
          title: 'Adresse',
          addressLine1: 'Ligne d\'adresse 1',
          addressLine2: 'Ligne d\'adresse 2',
          addressLine2Placeholder: 'Appartement / étage / bâtiment (facultatif)',
          country: 'Pays',
          provinceRegion: 'Province / région',
          cityMunicipality: 'Ville / commune',
          postcode: 'Code postal',
          latitude: 'Latitude',
          longitude: 'Longitude',
          optional: '(facultatif)',
          selectCountry: 'Sélectionner un pays',
          selectCountryFirst: 'Sélectionnez d\'abord un pays',
          selectProvince: 'Sélectionner une province / région',
          noRegions: 'Aucune région pour ce pays',
          selectProvinceFirst: 'Sélectionnez d\'abord une province / région',
          loadingCities: 'Chargement des villes...',
          searchCity: 'Rechercher une ville',
          saved: '(enregistré)',
          noCityList: 'Aucune liste de villes pour cette région — vous pouvez saisir le nom de la ville.',
          fillHint: 'Si vous renseignez un champ d\'adresse, veuillez remplir au minimum le <strong>Pays</strong> et la <strong>Ville</strong>. Code postal, province et lat/long sont facultatifs.',
          countryCityRequired: 'Si vous saisissez une adresse, renseignez au moins le pays et la ville.',
          streetPlaceholder: 'Adresse',
          egExample: 'ex. {{example}}'
        },
        student: {
          addTitle: 'Ajouter un élève',
          editTitle: 'Modifier l\'élève',
          subtitle: 'Saisir informations élève, accès et adresse dans un flux unifié.',
          fideSection: 'FIDE / classement (optionnel)',
          fideId: 'ID FIDE',
          eloRating: 'Classement Elo',
          fideTitle: 'Titre FIDE',
          createButton: 'Créer l\'élève',
          updateButton: 'Mettre à jour l\'élève',
          managerRolesNote: 'Les responsables ne peuvent créer ou modifier que des comptes élèves.',
          updated: 'Élève mis à jour',
          created: 'Élève créé',
          updateFailed: 'Échec de la mise à jour de l\'élève.',
          saveFailed: 'Échec de l\'enregistrement de l\'élève.',
          loadArchiveFailed: 'Échec du chargement des options d\'archivage.',
          enrollmentsEnded: 'Inscriptions terminées. Le compte élève reste actif.',
          archived: 'Élève archivé.',
          archiveFailed: 'Échec de l\'archivage de l\'élève.',
          activeDesc: 'Les élèves archivés restent dans l\'historique et peuvent être réactivés plus tard.',
          activeDescArchive: 'Désactivez pour archiver cet élève. S\'il a des inscriptions actives, vous choisirez comment les terminer.'
        },
        coach: {
          addTitle: 'Ajouter un entraîneur',
          editTitle: 'Modifier l\'entraîneur',
          subtitle: 'Gérer identité, accès et profil de l\'entraîneur.',
          createButton: 'Créer l\'entraîneur',
          updateButton: 'Mettre à jour l\'entraîneur',
          activeDesc: 'Archiver ou réactiver l\'entraîneur sans perdre son historique.',
          updated: 'Entraîneur mis à jour',
          created: 'Entraîneur créé',
          updateFailed: 'Échec de la mise à jour de l\'entraîneur.',
          saveFailed: 'Échec de l\'enregistrement de l\'entraîneur.'
        },
        manager: {
          addTitle: 'Ajouter un responsable',
          editTitle: 'Modifier le responsable',
          subtitle: 'Configuration responsable cohérente avec l\'expérience formulaire du produit.',
          createButton: 'Créer le responsable',
          updateButton: 'Mettre à jour le responsable',
          updated: 'Responsable mis à jour',
          created: 'Responsable créé',
          saveFailed: 'Échec de l\'enregistrement du responsable.',
          activeDesc: 'Les responsables peuvent être archivés en toute sécurité sans supprimer leurs dossiers.'
        },
        user: {
          addTitle: 'Créer un utilisateur',
          editTitle: 'Modifier l\'utilisateur',
          subtitle: 'Gérer détails du compte, rôles et accès.',
          emailSignInLink: 'Lien de connexion par e-mail (mot de passe défini par l\'utilisateur)',
          createButton: 'Créer l\'utilisateur',
          updateButton: 'Mettre à jour l\'utilisateur',
          loadFailed: 'Échec du chargement de l\'utilisateur.',
          saveFailed: 'Échec de l\'enregistrement de l\'utilisateur. Veuillez vérifier les saisies.'
        },
        branch: {
          addTitle: 'Ajouter un site',
          editTitle: 'Modifier le site',
          subtitle: 'Créer ou mettre à jour les détails du site avec des sections claires.',
          branchName: 'Nom du site',
          branchCode: 'Code du site',
          branchMode: 'Mode du site',
          managerOptional: 'Responsable (optionnel)',
          branchActive: 'Site actif',
          branchActiveDesc: 'Désactiver pour archiver le site en conservant tous les enregistrements.',
          branchAddress: 'Adresse du site',
          onlineNote: 'L\'adresse physique est masquée pour les sites uniquement en ligne.',
          createButton: 'Créer le site',
          updateButton: 'Mettre à jour le site',
          loadManagersFailed: 'Échec du chargement des responsables',
          updateFailed: 'Échec de la mise à jour du site.',
          saveFailed: 'Échec de l\'enregistrement du site.',
          nameRequired: 'Le nom du site est obligatoire.',
          codeRequired: 'Le code du site est obligatoire.',
          modeRequired: 'Le mode est obligatoire.',
          updateSuccess: 'Site mis à jour avec succès',
          addSuccess: 'Site ajouté avec succès',
          namePlaceholder: 'p. ex. Site principal',
          uniqueCodePlaceholder: 'Code unique'
        },
        batch: {
          addTitle: 'Créer un groupe',
          editTitle: 'Modifier le groupe',
          subtitle: 'Configurer planning, frais et paramètres de séance du groupe.',
          branch: 'Site pour le groupe',
          level: 'Niveau',
          coach: 'Entraîneur assigné',
          name: 'Nom du groupe',
          sessionMode: 'Mode de séance du groupe',
          defaultStart: 'Début par défaut (HH:mm)',
          defaultEnd: 'Fin par défaut (HH:mm)',
          selectStartTime: 'Sélectionner l\'heure de début',
          selectEndTime: 'Sélectionner l\'heure de fin',
          billingConfig: 'Configuration de facturation',
          chargesMode: 'Mode de facturation',
          sessionCharge: 'Frais de séance ({{currency}})',
          periodLength: 'Durée de période par défaut',
          periodFee: 'Frais de période ({{currency}})',
          carryForward: 'Politique de report',
          selectBranchFirst: 'Sélectionnez d\'abord un site',
          selectEllipsis: 'Sélectionner…',
          notePerSession: 'Facturation par séance individuelle',
          notePerSessionHourly: 'Tarif horaire × durée de la séance',
          notePerPeriod: 'Frais fixes pour une période d\'inscription bornée dans le temps',
          periodNoCarryForward: 'Le report de séances ne s\'applique pas aux groupes en facturation périodique. Les séances non utilisées ne sont pas reportées entre périodes de facturation.',
          validation: {
            selectBranch: 'Veuillez sélectionner un site',
            selectLevel: 'Veuillez sélectionner un niveau',
            selectCoach: 'Veuillez sélectionner un entraîneur',
            selectSessionMode: 'Veuillez sélectionner un mode de séance',
            enterName: 'Veuillez saisir un nom pour le groupe',
            selectSessionDay: 'Veuillez sélectionner au moins un jour de séance',
            selectDefaultStart: 'Veuillez sélectionner l\'heure de début par défaut',
            selectDefaultEnd: 'Veuillez sélectionner l\'heure de fin par défaut',
            endAfterStart: 'L\'heure de fin par défaut doit être postérieure à l\'heure de début par défaut',
            saveFailed: 'Échec de l\'enregistrement du groupe'
          }
        },
        level: {
          addTitle: 'Ajouter un niveau',
          editTitle: 'Modifier le niveau',
          subtitle: 'Définir le nom et l\'ordre d\'affichage pour groupes et inscriptions.',
          saveFailed: 'Échec de l\'enregistrement'
        }
      },
      authPages: {
        forgotTitle: 'Mot de passe oublié',
        emailOrUsername: 'E-mail ou nom d\'utilisateur',
        emailOrUsernamePlaceholder: 'vous@exemple.com ou votre nom d\'utilisateur',
        sending: 'Envoi…',
        backToSignIn: 'Retour à la connexion',
        setPasswordTitle: 'Définir votre mot de passe',
        missingToken: 'Jeton manquant dans l\'URL. Utilisez le dernier lien de connexion reçu par e-mail.',
        newPassword: 'Nouveau mot de passe',
        confirmPassword: 'Confirmer le mot de passe',
        passwordRules: '8 caractères minimum, 1 majuscule, 1 chiffre et 1 caractère spécial (@ $ ! % * ? &).',
        setPasswordButton: 'Définir le mot de passe',
        saving: 'Enregistrement…',
        invalidToken: 'Ce lien de connexion est invalide. Jeton manquant dans l\'URL.',
        passwordInvalid: 'Le mot de passe doit comporter au moins 8 caractères avec une majuscule, un chiffre et un caractère spécial.',
        passwordsMismatch: 'Les mots de passe ne correspondent pas.',
        passwordSetSuccess: 'Mot de passe défini avec succès. Vous pouvez vous connecter.',
        linkInvalidExpired: 'Lien invalide ou expiré.'
      },
      roleSelect: {
        title: 'Sélectionnez votre rôle',
        noRoles: 'Aucun rôle trouvé sur votre compte.',
        contactAdmin: 'Veuillez contacter un administrateur.',
        switchHint: 'Vous pouvez changer de rôle à tout moment depuis le menu en-tête.'
      },
      setupWizard: {
        welcomeTitle: 'Bienvenue dans l\'application',
        welcomeSubtitle: 'Configurons votre académie en quelques étapes rapides.',
        stepOf: 'Étape {{current}} sur {{total}}',
        footerHint: 'Tous les paramètres peuvent être modifiés à tout moment depuis le panneau admin.',
        continue: 'Continuer →',
        back: '← Retour',
        saving: 'Enregistrement…',
        finishing: 'Finalisation…',
        finishSetup: 'Terminer la configuration →',
        enterDashboard: 'Accéder au tableau de bord →',
        optional: '(optionnel)',
        saved: 'Enregistré',
        skipped: 'Ignoré',
        configured: 'Configuré',
        notConfiguredEmail: 'Non configuré — Système → Paramètres e-mail',
        notConfiguredWhatsApp: 'Non configuré — Système → WhatsApp',
        saveError: 'Une erreur s\'est produite. Veuillez réessayer.',
        regionalSaveError: 'Les paramètres régionaux n\'ont pas été enregistrés. Veuillez réessayer.',
        emailSaveError: 'Impossible d\'enregistrer les paramètres e-mail.',
        whatsappSaveError: 'Impossible d\'enregistrer les paramètres WhatsApp.',
        step1: {
          title: 'Nommez votre académie',
          description: 'Apparaît dans l\'en-tête, la page de connexion, les e-mails et tous les exports PDF.',
          logo: 'Logo de l\'académie',
          clickUpload: 'Cliquer pour téléverser le logo',
          logoFormats: 'PNG, JPG, SVG · max. 2 Mo',
          clickChange: 'Cliquer pour modifier',
          logoPreviewAlt: 'Aperçu du logo',
          academyName: 'Nom de l\'académie',
          tagline: 'Slogan',
          nameRequired: 'Le nom de l\'académie est requis.',
          logoTooLarge: 'Le logo doit faire moins de 2 Mo.',
          namePlaceholder: 'p. ex., École d\'échecs Kingside',
          taglinePlaceholder: 'p. ex., Pensez plus profond, jouez plus fort'
        },
        step2: {
          title: 'Coordonnées',
          description: 'Ce contact support public est affiché aux élèves et aux parents. Ce n\'est pas la même chose que votre adresse SMTP sortante (configurée à l\'étape optionnelle suivante).',
          supportPhone: 'Numéro de téléphone support',
          supportEmail: 'E-mail support',
          phonePlaceholder: 'Numéro de téléphone support',
          emailPlaceholder: 'p. ex., support@monacademie.fr',
          phoneHint: 'Affiché sur les factures, e-mails et le portail élève.',
          emailHint: 'Les élèves peuvent contacter cette adresse pour obtenir de l\'aide.',
          phoneRequired: 'Le numéro de téléphone est requis.',
          emailRequired: 'L\'adresse e-mail est requise.',
          emailInvalid: 'Veuillez saisir une adresse e-mail valide.'
        },
        step3: {
          title: 'Région & paiements',
          description: 'Choisissez le pays et la devise de votre académie. Les passerelles en ligne (Razorpay, Stripe, PayPal) se configurent plus tard sous Système → Modes de paiement.'
        },
        step4: {
          title: 'Connecter les communications',
          description: 'Optionnel — configurez l\'e-mail sortant et WhatsApp maintenant, ou ignorez et configurez plus tard depuis le panneau admin.',
          skipHint: 'Les éléments ignorés peuvent toujours être configurés plus tard sous Système → Paramètres e-mail et Système → WhatsApp.',
          emailSection: 'E-mail sortant (SMTP)',
          emailSectionHint: 'Utilisé pour les liens de connexion, rappels, relevés et alertes.',
          whatsappSection: 'WhatsApp (WATI)',
          whatsappSectionHint: 'Rappels de séance, alertes portefeuille et messages opérationnels.',
          whatsappNotLicensed: 'WhatsApp n\'est pas inclus dans votre licence. Vous pouvez ignorer et mettre à niveau plus tard.',
          saveEmail: 'Enregistrer les paramètres e-mail',
          saveWhatsApp: 'Enregistrer les paramètres WhatsApp',
          skipForNow: 'Ignorer pour l\'instant',
          smtpHost: 'Hôte SMTP',
          smtpPort: 'Port SMTP',
          smtpUsername: 'Nom d\'utilisateur SMTP',
          smtpPassword: 'Mot de passe SMTP',
          fromEmail: 'Adresse e-mail expéditeur',
          alreadySet: '(déjà défini)',
          requiredToSave: 'Requis pour enregistrer',
          watiBaseUrl: 'URL de base API WATI',
          channelNumber: 'Numéro de canal professionnel',
          watiToken: 'Jeton bearer WATI',
          pasteToken: 'Coller le jeton'
        },
        step5: {
          title: 'Tout est prêt !',
          ready: '{{name}} est prêt. Vous pouvez mettre à jour ces informations à tout moment sous Paramètres → Image de marque.',
          academyLabel: 'Académie',
          supportLabel: 'Support',
          communicationsLabel: 'Communications',
          outboundEmail: 'E-mail sortant :',
          whatsapp: 'WhatsApp :'
        }
      },
      publicHelp: {
        backToLogin: '← Retour à la connexion',
        title: 'Aide & premiers pas',
        subtitle: 'Réponses rapides avant de vous connecter à {{name}}.',
        signingIn: 'Connexion',
        signingInBody: 'Utilisez le nom d\'utilisateur et le mot de passe fournis par l\'administrateur de votre académie. Si vous avez reçu un e-mail de connexion, ouvrez le lien pour définir votre mot de passe, puis revenez ici pour vous connecter.',
        forgotPassword: 'Mot de passe oublié ?',
        forgotPasswordBody: 'Utilisez le lien <1>Mot de passe oublié ?</1> sur la page de connexion. Vous recevrez un lien de réinitialisation à votre adresse e-mail enregistrée.',
        forgotPasswordLink: 'Mot de passe oublié ?',
        contactAcademy: 'Contacter votre académie',
        contactAcademyBody: 'Pour l\'accès au compte ou des questions spécifiques à l\'académie, contactez directement :',
        email: 'E-mail :',
        phone: 'Téléphone :',
        privacy: 'Confidentialité',
        privacyBody: 'Consultez notre <1>Politique de confidentialité</1> pour comprendre comment vos données sont traitées sur cette plateforme.',
        privacyLink: 'Politique de confidentialité'
      },
      licenseInvalid: {
        heading: 'Licence logicielle inactive',
        body: 'L\'accès au système de présence a été suspendu car la licence logicielle n\'est plus valide. Vos données restent entièrement sûres et intactes.',
        restoreAccess: 'Restaurer l\'accès',
        softwareProvider: 'Fournisseur logiciel',
        tryAgain: 'Réessayer',
        contactSupport: 'Contacter le support',
        footer: '{{name}} — Système de gestion des présences'
      },
      guardianPublic: {
        inviteTitle: 'Invitation tuteur',
        invalidLink: 'Ce lien d\'invitation tuteur est invalide ou a expiré.',
        createAccount: 'Créer un compte tuteur',
        linkingFor: ' — vous vous liez en tant que tuteur pour ',
        confirmLink: 'Confirmer le lien tuteur',
        linkAccountFor: ' — liez votre compte en tant que tuteur pour ',
        invitedEmail: 'E-mail invité : {{email}}',
        accountEmail: 'E-mail du compte : {{email}}',
        relationship: 'Votre relation avec l\'élève',
        relationships: {
          MOTHER: 'Mère',
          FATHER: 'Père',
          GUARDIAN: 'Tuteur',
          GRANDPARENT: 'Grand-parent',
          OTHER: 'Autre'
        },
        passwordNote: '8+ caractères, majuscule, chiffre et caractère spécial',
        confirmPassword: 'Confirmer le mot de passe',
        submitRegistration: 'Soumettre l\'inscription',
        submitting: 'Envoi…',
        acceptRequest: 'Accepter & demander l\'accès',
        decline: 'Refuser',
        nameRequired: 'Le prénom, le nom et le nom d\'utilisateur sont requis.',
        passwordInvalid: 'Le mot de passe doit contenir au moins 8 caractères, une majuscule, un chiffre et un caractère spécial.',
        passwordsMismatch: 'Les mots de passe ne correspondent pas.',
        registrationSubmitted: 'Inscription soumise. Veuillez vous connecter après l\'approbation de l\'accès tuteur par votre académie.',
        registrationFailed: 'Échec de l\'inscription. Le lien est peut-être invalide ou expiré.',
        requestSubmitted: 'Demande soumise. Veuillez vous connecter après l\'approbation de l\'accès tuteur par votre académie.',
        invitationDeclined: 'Invitation refusée.',
        processFailed: 'Impossible de traiter cette invitation.'
      },
      managerForms: {
        editBatch: 'Modifier le groupe',
        createBatch: 'Créer un groupe',
        batchSubtitle: 'Configurer les détails, l\'horaire et la facturation du groupe.',
        editLevel: 'Modifier le niveau',
        addLevel: 'Ajouter un niveau',
        levelSubtitle: 'Définir le nom du niveau et l\'ordre d\'affichage pour les groupes de votre filiale.',
        saveFailed: 'Échec de l\'enregistrement'
      },
      walletStaff: {
        topupTitle: 'Recharge portefeuille',
        referenceOptional: 'Référence (optionnel)',
        clear: 'Effacer',
        topup: 'Recharger',
        saving: 'Enregistrement…',
        missingStudent: 'Élève manquant',
        invalidAmount: 'Saisir un montant valide',
        credited: 'Portefeuille crédité : {{amount}}.',
        topupFailed: 'Échec de la recharge.',
        refundTitle: 'Traiter un remboursement portefeuille',
        refundImportant: 'Important :',
        refundWarning: 'Le montant du remboursement sera déduit du portefeuille de l\'élève. Remboursement maximum :',
        missingStudentInfo: 'Informations élève manquantes',
        invalidAmountPositive: 'Saisir un montant valide supérieur à 0',
        cannotRefundMore: 'Impossible de rembourser plus que le solde actuel : {{amount}}',
        reasonRequired: 'Veuillez indiquer un motif de remboursement',
        refundSuccess: 'Remboursement de {{amount}} traité avec succès.',
        refundFailed: 'Échec du remboursement',
        refundAmount: 'Montant du remboursement',
        paymentMode: 'Mode de paiement',
        maxPlaceholder: 'Max : {{amount}}',
        referenceTxn: 'Référence/Numéro de transaction',
        referencePlaceholder: 'ID transaction, n° chèque, etc.',
        gatewayRefund: 'Rembourser aussi via la passerelle (Razorpay/Stripe/PayPal). Décochez pour un ajustement portefeuille uniquement si la passerelle a déjà été remboursée manuellement.',
        reasonForRefund: 'Motif du remboursement',
        reasonPlaceholder: 'p. ex., Arrêt de l\'élève, Paiement en excès, etc.',
        processing: 'Traitement…',
        processRefund: 'Traiter le remboursement',
        balanceLabel: 'Solde portefeuille',
        refresh: 'Actualiser',
        refund: 'Rembourser',
        transactions: 'Transactions portefeuille',
        noTransactions: 'Aucune transaction pour l\'instant.',
        showAllTransactions: 'Afficher toutes les transactions portefeuille',
        moreCount: ' ({{count}} de plus)',
        loadFailedPortal: 'Impossible de charger votre portefeuille. Veuillez actualiser la page.',
        loadFailedStaff: 'Impossible de charger le portefeuille de cet élève. Il est peut-être hors de votre périmètre de filiale.',
        grossCharge: 'Frais bruts :',
        discountApplied: 'Remise appliquée :',
        reversalCredit: 'Crédit d\'annulation :',
        netCharge: 'Frais nets :',
        reversed: '(Annulé)',
        refunded: 'REMBOURSÉ',
        sessionReversed: '(Séance annulée)',
        modifiedOn: 'Modifié le {{date}}',
        original: 'Original :',
        modify: 'Modifier',
        revert: 'Annuler',
        balanceShort: 'Solde :',
        sessionBatch: 'Séance #{{sessionId}} (Groupe #{{batchId}})',
        sessionOnly: 'Séance #{{sessionId}}',
        modifyTxn: 'Modifier la transaction',
        currentAmount: 'Montant actuel',
        newAmount: 'Nouveau montant *',
        newAmountPlaceholder: 'Saisir le nouveau montant',
        reasonOptional: 'Motif (optionnel)',
        modificationReasonPlaceholder: 'Saisir le motif de modification',
        saveChanges: 'Enregistrer les modifications',
        revertTxn: 'Annuler la transaction',
        revertWarningTitle: 'Attention : cette action est irréversible !',
        revertWarningBody: 'La transaction sera définitivement supprimée et le solde portefeuille réduit de {{amount}}.',
        txnDetails: 'Détails de la transaction',
        amountLabel: 'Montant :',
        typeLabel: 'Type :',
        reasonLabel: 'Motif :',
        dateLabel: 'Date :',
        revertReasonOptional: 'Motif d\'annulation (optionnel)',
        revertReasonPlaceholder: 'Saisir le motif d\'annulation de cette transaction',
        confirmRevert: 'Confirmer l\'annulation',
        validAmountRequired: 'Veuillez saisir un montant positif valide',
        modifyFailed: 'Échec de la modification de la transaction',
        revertFailed: 'Échec de l\'annulation de la transaction'
      },
      guardiansSection: {
        title: 'Tuteurs',
        intro: 'Invitez jusqu\'à {{max}} parents ou tuteurs par e-mail. Ils choisissent leur relation lors de l\'acceptation.',
        canApprove: ' Vous pouvez approuver ou rejeter les demandes en attente.',
        contactAcademy: ' Pour retirer un lien tuteur approuvé, contactez votre académie.',
        loading: 'Chargement des tuteurs…',
        loadFailed: 'Échec du chargement des tuteurs.',
        enterEmail: 'Saisissez l\'adresse e-mail du tuteur.',
        cancelInviteConfirm: 'Annuler cette invitation tuteur ?',
        cancelInviteFailed: 'Impossible d\'annuler l\'invitation.',
        approveFailed: 'Impossible d\'approuver le tuteur.',
        revokeConfirm: 'Retirer ce lien tuteur ? Ils perdront l\'accès après révocation.',
        revokeFailed: 'Impossible de révoquer le tuteur.',
        rejectConfirm: 'Rejeter cette demande tuteur ?',
        rejectFailed: 'Impossible de rejeter le tuteur.',
        emptyTitle: 'Aucun tuteur lié pour l\'instant',
        emptyBody: 'Invitez un parent ou tuteur par e-mail. Il s\'inscrira ou confirmera son compte, puis votre académie devra approuver l\'accès.',
        linkedTitle: 'Tuteurs liés',
        pendingTitle: 'Invitations en attente',
        approve: 'Approuver',
        reject: 'Rejeter',
        remove: 'Retirer',
        emailPlaceholder: 'Adresse e-mail du tuteur',
        expires: ' · expire le {{date}}',
        expired: ' · expiré',
        limitReached: 'Limite de tuteurs atteinte ({{used}}/{{max}}).',
        limitRevokeHint: ' Annulez une invitation en attente ou retirez un lien avant d\'en inviter un autre.',
        limitCancelHint: ' Annulez une invitation en attente avant d\'en inviter une autre.',
        status: {
          PENDING_APPROVAL: 'Approbation en attente',
          APPROVED: 'Approuvé',
          REJECTED: 'Rejeté',
          REVOKED: 'Révoqué'
        },
        inviteType: {
          NEW_REGISTRATION: 'Nouveau compte',
          EXISTING_USER_LINK: 'Lier un compte existant'
        },
        inviteButton: 'Inviter un tuteur',
        resendButton: 'Renvoyer l\'invitation tuteur',
        sending: 'Envoi…',
        inviteFailure: 'Impossible d\'envoyer l\'invitation tuteur.',
        resendFailure: 'Impossible de renvoyer l\'invitation tuteur.'
      },
      archiveStudent: {
        titleWithEnrollments: 'Archiver le compte élève',
        titleSimple: 'Archiver l\'élève',
        hasEnrollments: '{{name}} a {{count}} inscription active.',
        hasEnrollments_other: '{{name}} a {{count}} inscriptions actives.',
        chooseProceed: 'Choisissez comment procéder.',
        archiveSimple: 'Archiver {{name}} ? Il ne pourra plus se connecter. Son historique est conservé.',
        thisStudent: 'Cet élève',
        thisStudentLower: 'cet élève',
        loadingEnrollments: 'Chargement des détails d\'inscription…',
        activeEnrollments: 'Inscriptions actives',
        ongoing: 'en cours',
        whatHappens: 'Que doit-il se passer ?',
        endEnrollmentsOnly: 'Terminer les inscriptions uniquement',
        endEnrollmentsOnlyDesc: 'Arrêter l\'appartenance au groupe à la date ci-dessous. Le compte reste actif — l\'élève peut encore se connecter.',
        endAndArchive: 'Terminer les inscriptions & archiver le compte',
        recommended: '(recommandé)',
        endAndArchiveDesc: 'Typique quand un élève part. Termine les inscriptions et révoque l\'accès au portail.',
        archiveOnly: 'Archiver le compte uniquement',
        archiveOnlyDesc: 'Révoquer la connexion mais laisser les inscriptions actives. Présence et facturation continuent jusqu\'à la fin de chaque inscription.',
        endDateLabel: 'Date de fin pour toutes les inscriptions listées',
        archiveOnlyConfirm: 'Je comprends que les inscriptions restent actives, que la présence peut encore être marquée et que l\'élève ne peut pas se connecter jusqu\'à réactivation.',
        processing: 'Traitement…',
        continue: 'Continuer',
        archive: 'Archiver'
      },
      privacy: {
        metaLastUpdated: 'Dernière mise à jour : juillet 2026 · S\'applique à : {{name}}',
        metaGdprFraming: ' · Cadre RGPD / UK GDPR',
        metaDpdpFraming: ' · Cadre DPDP Act 2023',
        whoWeAre: {
          title: '1. Qui sommes-nous',
          p1: 'Cette application (&quot;<strong>{{name}}</strong>&quot;) est exploitée par votre académie d\'échecs (l\'&quot;Académie&quot;, &quot;nous&quot;). L\'Académie agit en tant que <strong>responsable du traitement</strong> des données personnelles traitées via cette plateforme. Axeron Technologies (fournisseur de logiciel) agit généralement uniquement en tant que <strong>sous-traitant</strong> lorsqu\'il fournit un logiciel sous licence, une assistance à distance ou une validation de licence — voir l\'accord de traitement des données avec votre Académie.',
          p2: 'Pour toute question relative à la confidentialité, contactez d\'abord l\'administrateur de l\'Académie.'
        },
        whatWeCollect: {
          title: '2. Quelles données personnelles nous collectons',
          intro: 'Nous collectons et traitons les catégories de données personnelles suivantes :',
          identity: '<strong>Données d\'identité</strong> — prénom, nom, date de naissance (élèves) ; identifiant FIDE / Elo optionnel si activé',
          contact: '<strong>Données de contact</strong> — adresse e-mail, numéro de téléphone, adresse postale',
          account: '<strong>Données de compte</strong> — nom d\'utilisateur, mot de passe haché, rôle attribué',
          attendance: '<strong>Données de présence &amp; d\'inscription</strong> — séances, groupes, inscriptions',
          financial: '<strong>Données financières</strong> — solde du portefeuille, recharges, références de frais',
          guardian: '<strong>Liens tuteur / parent</strong> — lorsque configurés pour les mineurs',
          technical: '<strong>Données techniques</strong> — horodatages de connexion, IP dans les journaux serveur, JWT dans localStorage',
          noBiometric: 'Nous ne collectons <strong>pas</strong> de données biométriques ni de numéros complets de cartes bancaires.',
          payments: 'Les paiements en ligne sont traités par la passerelle choisie par l\'Académie (<strong>Razorpay</strong>, <strong>Stripe</strong>, <strong>PayPal</strong> et/ou <strong>Mollie</strong>).'
        },
        legalBasis: {
          title: '3. Pourquoi nous traitons vos données (base légale)',
          gdpr: {
            contract: '<strong>Art. 6(1)(b) contrat</strong> — inscriptions, présence, facturation portefeuille, services d\'entraînement.',
            legitimate: '<strong>Art. 6(1)(f) intérêts légitimes</strong> — sécurité, prévention de la fraude, journaux d\'audit.',
            obligation: '<strong>Art. 6(1)(c) obligation légale</strong> — lorsque l\'Académie doit conserver des documents financiers selon la loi fiscale locale.',
            consent: '<strong>Art. 6(1)(a) / Art. 8 consentement</strong> — lorsque requis pour les enfants (consentement parental via les fonctionnalités tuteur).'
          },
          dpdp: {
            contract: '<strong>Exécution d\'un contrat</strong> — inscriptions, présence, recharges portefeuille et déductions de frais.',
            legitimate: '<strong>Intérêts légitimes</strong> — journaux d\'audit, détection de fraude, fonctionnement sécurisé.',
            obligation: '<strong>Obligation légale</strong> — conservation des documents financiers comme requis par la loi indienne (Companies Act / GST, généralement 8 ans).'
          }
        },
        retention: {
          title: '4. Combien de temps nous conservons vos données',
          activeAccount: '<strong>Données de compte actif</strong> — tant que votre compte reste auprès de l\'Académie.',
          attendance: '<strong>Présence &amp; inscription</strong> — généralement 3 ans après la dernière séance (la politique de l\'Académie peut varier).',
          financialGdpr: '<strong>Documents financiers</strong> — comme requis par la loi fiscale locale (souvent 6 à 10 ans dans les États membres de l\'UE).',
          financialDpdp: '<strong>Documents financiers</strong> — comme requis par la loi fiscale locale (généralement 8 ans selon les règles GST / impôt sur le revenu indiennes).',
          auditLogs: '<strong>Journaux d\'audit</strong> — conservés environ 12 mois, puis supprimés.'
        },
        sharing: {
          title: '5. Avec qui nous partageons vos données',
          intro: 'Nous ne vendons pas de données personnelles. Le partage est limité à :',
          gateways: '<strong>Passerelles de paiement</strong> — Razorpay, Stripe, PayPal et/ou Mollie (configurées par l\'Académie).',
          messaging: '<strong>Messagerie</strong> — fournisseur SMTP e-mail ; WhatsApp via WATI/Meta lorsque activé par l\'Académie.',
          hosting: '<strong>Hébergement</strong> — le serveur où cette instance auto-hébergée fonctionne (choisi par l\'Académie ; les académies UE devraient héberger dans l\'UE).',
          licence: '<strong>Validation de licence</strong> — empreinte d\'installation / vérifications de licence peuvent contacter le relais de licence d\'Axeron (voir DPA fournisseur).',
          staff: '<strong>Personnel de l\'Académie</strong> — accès limité par rôle uniquement.'
        },
        cookies: {
          title: '6. Cookies et stockage local',
          intro: 'Stockage essentiel uniquement :',
          jwt: '<strong>JWT</strong> (localStorage) — connexion.',
          consentFlag: '<strong>Indicateur de consentement aux cookies</strong> (localStorage).',
          gatewaySession: '<strong>ID de session passerelle en attente</strong> (sessionStorage) pendant les redirections de paiement.',
          noAds: 'Pas de cookies publicitaires ou d\'analyse tiers.'
        },
        rights: {
          gdprTitle: '7. Vos droits en vertu du RGPD / UK GDPR',
          gdprP1: 'Selon votre localisation, vous pouvez disposer de droits d\'accès, de rectification, d\'effacement, de limitation, de portabilité et d\'opposition, ainsi que de droits liés à la prise de décision automatisée le cas échéant.',
          gdprP2: 'Contactez l\'administrateur de l\'Académie pour exercer vos droits. Les Académies doivent répondre sans retard injustifié et dans un délai d\'un mois (prolongeable conformément à la loi).',
          gdprP3: 'Vous pouvez déposer une plainte auprès de votre autorité de contrôle locale (par exemple CNIL, BfDI, AP, ICO au Royaume-Uni).',
          dpdpTitle: '7. Vos droits en vertu du DPDP Act, 2023',
          dpdpP1: 'En tant que personne concernée en vertu du Digital Personal Data Protection Act, 2023 (Inde), vous pouvez demander l\'accès, la correction, l\'effacement (sous réserve de conservation légale) et les droits connexes via l\'administrateur de l\'Académie (délai de réponse cible : 30 jours).',
          dpdpP2: 'Les plaintes peuvent être adressées au Data Protection Board of India une fois pleinement opérationnel, ou à l\'administrateur de l\'Académie.'
        },
        security: {
          title: '8. Sécurité des données',
          https: 'HTTPS/TLS en transit (lorsque déployé avec TLS).',
          bcrypt: 'Mots de passe hachés avec bcrypt.',
          roles: 'Accès restreint par rôle.',
          encryption: 'Secrets de passerelle chiffrés au repos lorsque <code>PAYMENT_SECRETS_ENCRYPTION_KEY</code> est défini.'
        },
        changes: {
          title: '9. Modifications',
          body: 'Nous pouvons mettre à jour cette page ; la date &quot;Dernière mise à jour&quot; changera. L\'utilisation continue après une mise à jour signifie que vous acceptez la politique révisée.'
        },
        footer: {
          line1: '{{name}} · Politique de confidentialité · Reflète les pratiques de l\'Académie exploitant cette instance.',
          line2: 'Pack juridique fournisseur : voir <code>legal/PRIVACY-POLICY.txt</code> et <codeEu>legal/PRIVACY-POLICY-EU.txt</codeEu> (examen juridique requis avant signature client).'
        }
      },
      terms: {
        agreement: {
          title: '1. Accord',
          body: 'Ces Conditions régissent l\'utilisation de <strong>{{name}}</strong>, logiciel de gestion d\'académie sous licence d\'Axeron Technologies et exploité par votre académie (l\'&quot;Académie&quot;). En vous connectant, vous acceptez ces conditions et la <privacyLink>Politique de confidentialité</privacyLink>.'
        },
        licence: {
          title: '2. Licence et usage acceptable',
          body: 'L\'Académie accorde aux utilisateurs autorisés (admins, responsables, entraîneurs, élèves et tuteurs) l\'accès à la plateforme uniquement pour la gestion de l\'académie. Vous ne devez pas tenter de contourner les limites de licence, partager vos identifiants de manière non sécurisée ou abuser des passerelles de paiement.'
        },
        fees: {
          title: '3. Frais et paiements',
          body1: 'Les portefeuilles élèves et frais d\'adhésion sont facturés par l\'Académie, et non par Axeron Technologies. Les passerelles en ligne (Razorpay, Stripe, PayPal, Mollie) sont configurées par l\'Académie sous ses propres comptes marchands.',
          euLicence: 'Les frais de licence facturés par Axeron Technologies aux académies européennes sont indiqués dans la devise indiquée sur la commande, hors TVA applicable sauf mention contraire sur la facture.',
          nonEuLicence: 'Les frais de licence logicielle facturés par Axeron Technologies sont indiqués dans la devise convenue sur le bon de commande, hors taxes sauf mention contraire.'
        },
        support: {
          title: '4. Support',
          body: 'Le support est fourni par l\'administrateur de l\'Académie pour l\'usage quotidien, et par Axeron Technologies pour la licence et les défauts produit conformément à l\'EULA / accord de support des Académies.',
          euHours: ' Les réponses de support pour les clients européens visent les heures ouvrables UE (CET/CEST) lorsque possible.',
          indiaHours: ' Les heures de support pour les clients indiens sont généralement lun–sam 10:00–18:00 IST sauf accord contraire.'
        },
        liability: {
          title: '5. Responsabilité',
          body: 'Le logiciel est fourni tel que décrit dans le Contrat de licence utilisateur final entre Axeron Technologies et l\'Académie. L\'Académie est responsable de la configuration, de l\'accès utilisateur et de la conformité aux lois locales (y compris la protection des données).'
        },
        governingLaw: {
          title: '6. Droit applicable',
          euBody: 'Pour les clients académie européens, les conditions commerciales peuvent être soumises aux règles de consommation et commerciales UE/EEE telles que définies dans la commande signée / addendum EULA. Le règlement des litiges est celui spécifié dans cet accord. Documents juridiques fournisseur sous <code>legal/</code> à faire examiner par un avocat avant signature.',
          indiaBody: 'Sauf accord signé contraire, les accords de licence fournisseur pour les clients indiens sont régis par le droit indien (Gujarat), comme indiqué dans l\'EULA et les Conditions maintenus par Axeron Technologies.'
        },
        footer: '{{name}} · Conditions d\'utilisation · Voir aussi l\'EULA fournisseur dans le pack de licence logicielle.'
      },
      legal: {
        backToLogin: '← Retour à la connexion',
        termsTitle: 'Conditions d\'utilisation',
        privacyTitle: 'Politique de confidentialité',
        lastUpdated: 'Dernière mise à jour : juillet 2026 · S\'applique à : {{name}}'
      },
      notifications: {
        categories: {
          general: 'Général',
          billing: 'Facturation',
          attendance: 'Présence',
          privacy: 'Confidentialité',
          chess: 'Échecs',
          chessChallenge: 'Défi',
          chessGame: 'Partie',
          chessFairPlay: 'Fair-play',
          chessCorrespondence: 'Correspondance',
        },
        content: {
          new: 'Nouveau',
          open: 'Ouvrir',
          fallbackTitle: 'Notification',
          lowWallet: {
            title: 'Solde portefeuille bas',
            body: '{{studentName}} est à {{balance}} (seuil {{threshold}}). Rechargez ou encaisez.',
            action: 'Recharger ce portefeuille'
          },
          lowWalletDigest: {
            title: '{{count}} élèves avec portefeuille bas',
            body: '{{count}} élèves sont sous le seuil de portefeuille. Ouvrez Paiements dus pour examiner et encaisser.',
            action: 'Ouvrir paiements dus'
          },
          absence: {
            title: 'Absence enregistrée',
            body: '{{studentName}} a été marqué absent pour la séance du {{sessionInfo}}.',
            action: 'Ouvrir la présence'
          },
          dsar: {
            title: 'Demande d’accès aux données (DSAR)',
            body: '{{name}} (utilisateur #{{userId}}, {{username}}) a demandé une copie de ses données personnelles. Exportez via Rapports → Export de données.',
            action: 'Exporter leurs données'
          },
          chessGeneric: {
            CHESS_CHALLENGE: 'Défi d’échecs',
            CHESS_GAME: 'Partie d’échecs',
            CHESS_FAIR_PLAY: 'Fair-play',
            CHESS_CORR: 'Échecs par correspondance',
            CHESS: 'Échecs',
            bodyFallback: 'Ouvrez la section Jeu pour plus de détails.'
          }
        }
      }
    }
  },
  nl: {
    translation: {
      brand: {
        tagline: 'Beheren. Monitoren. Groeien.'
      },
      nav: {
        branches: 'Vestigingen',
        levels: 'Niveaus',
        batches: 'Groepen',
        students: 'Leerlingen',
        coaches: 'Coaches',
        managers: 'Beheerders',
        enrollments: 'Inschrijvingen',
        sessions: 'Sessies',
        calendar: 'Kalender',
        reports: 'Rapporten',
        wallet: 'Mijn portemonnee',
        faq: 'FAQ',
        dashboard: 'Overzicht',
        help: 'Hulp',
        finances: 'Financiën',
        manage: 'Beheren',
        people: 'Personen',
        academySetup: 'Academie-inrichting',
        operations: 'Operaties',
        system: 'Systeem',
        feesBilling: 'Tarieven & facturatie',
        myAccount: 'Mijn account',
        settings: 'Instellingen',
        discounts: 'Kortingen',
        sessionsSummary: 'Sessieoverzicht',
        monthlyStatements: 'Maandelijkse afschriften',
        reportSettings: 'Rapportinstellingen',
        atRiskStudents: 'Leerlingen met risico',
        walletTopup: 'Portemonnee opwaarderen',
        paymentEnforcement: 'Betalingsnaleving',
        paymentMethods: 'Betaalmethoden',
        regionalSettings: 'Regionale instellingen',
        productionReadiness: 'EU-productiegereed',
        playingPlatform: 'Speelplatform',
        playingHub: 'Speelhub',
        fairPlay: 'Fair play',
        playNow: 'Tegen computer',
        liveGames: 'Live partijen',
        myGames: 'Mijn partijen',
        challenges: 'Uitdagingen',
        leaderboard: 'Ranglijst',
        classroom: 'Klaslokaal',
        classroomOverview: 'Overzicht',
        classroomLive: 'Live lessen',
        classroomZoom: 'Zoom-verbinding',
        classroomSettings: 'Klaslokaalinstellingen',
        emailSettings: 'E-mailinstellingen',
        whatsapp: 'WhatsApp',
        aiCoachSettings: 'Ask AI Coach',
        branding: 'Huisstijl',
        license: 'Licentie',
        holidays: 'Vakanties',
        notifications: 'Meldingen',
        auditLogs: 'Auditlogboeken',
        mySessions: 'Mijn sessies',
        myEnrollments: 'Mijn inschrijvingen',
        sessionStatement: 'Sessieafschrift',
        parentPortal: 'Ouderportaal',
        home: 'Home',
        myChild: 'Mijn kind',
        overview: 'Overzicht',
        attendance: 'Aanwezigheid',
        more: 'Meer'
      },
      classroom: {
        hub: {
          title: 'Klaslokaal',
          subtitleStaff: 'Geef online lessen met Zoom — start hier, host of join via Live.',
          subtitleLearner: 'Doe hier mee met de live online lessen van uw academie.',
          zoomFirstBanner: 'Klaslokaal is Zoom-first. Videovergaderingen gebruiken het Zoom-account van de academie; ChessDesk maakt de meeting aan en opent die voor coaches en leerlingen.',
          liveTitle: 'Live lessen',
          liveDescStaff: 'Bekijk online sessies van vandaag en Start / Host / Deelnemen zodra Zoom verbonden is.',
          liveDescLearner: 'Bekijk aankomende online lessen en doe mee wanneer uw coach de meeting start.',
          zoomTitle: 'Zoom-verbinding',
          zoomDesc: 'Koppel Zoom zodat klasvideo in ChessDesk werkt.',
          zoomDescConnected: 'Zoom is verbonden. Beheer credentials of verbreek hier.',
          settingsTitle: 'Klaslokaalinstellingen',
          settingsDesc: 'Standaarden voor wachtruimte, dempen bij binnenkomst, camera en startmeldingen.',
          open: 'Openen',
          zoomConnectedBanner: 'Zoom is verbonden voor deze academie. Open Live lessen om te starten of deel te nemen.',
          zoomNotConnectedBanner: 'Zoom is nog niet verbonden. Koppel het Zoom-account van de academie om Start / Host / Deelnemen te activeren.',
          externalModeBanner: 'Klaslokaal gebruikt externe meetinglinks. Plak de URL op de sessie en gebruik Start / Deelnemen via Live of Sessies.',
          statusLoadFailed: 'Klaslokaalstatus kon niet worden geladen.',
          zoomStatusUnknown: 'Zoom-verbinding kon niet worden gecontroleerd. Vernieuw of open Live lessen.',
        },
        joinAction: {
          stillWaiting: "Nog wachten — je coach is nog niet gestart.",
          notStartedYet: 'Nog niet gestart',
          notStartedHint: 'Je coach heeft deze online les nog niet gestart. Tik om opnieuw te controleren.',
          checking: 'Controleren…',
          checkFailed: 'Kon klaslokaalstatus niet controleren. Probeer opnieuw of open Live lessen.',
        },
        live: {
          title: 'Live lessen',
          subtitleStaff: 'Start of host Zoom voor online sessies die aan u zijn toegewezen.',
          subtitleAdmin: 'Start of host Zoom voor online sessies van de hele academie.',
          subtitleLearner: 'Doe mee wanneer uw coach het klaslokaal opent.',
          zoomPendingHint: 'Zoom is nog niet verbonden. U kunt sessies openen; Start legt uit wat ontbreekt tot een admin Zoom verbindt.',
          zoomPendingHintLearner: 'Zoom is nog niet verbonden. Deelnemen verschijnt hier wanneer uw coach een online les start.',
          connectZoomCta: 'Zoom verbinden',
          todayHeading: 'Vandaag & aankomend',
          todayHint: 'Online sessies met een Zoom-link verschijnen hier.',
          todayHintLive: 'Geschikte online sessies in de komende 48 uur. Start maakt een meeting in ChessDesk; Host opent als host; Deelnemen als deelnemer.',
          todayHintLearner: 'Geschikte online sessies in de komende 48 uur. Deelnemen opent de deelnemerslink wanneer uw coach de les start.',
          openSessions: 'Sessies openen',
          refresh: 'Vernieuwen',
          emptyTitle: 'Nog geen live klaslokaal',
          emptyStaff: 'Geen geschikte online sessies in de komende 48 uur. Gebruik Sessies openen om te plannen. Start werkt ook onder Sessies zodra Zoom verbonden is.',
          emptyLearner: 'Wanneer uw coach een online les start, verschijnt hier Deelnemen. Bekijk ook Sessies voor uw rooster.',
          emptyStaffConnected: 'Geen online sessies in dit venster. Plan onder Sessies en kom terug om te Starten of Hostten.',
          emptyLearnerConnected: 'Geen live les nu. Wanneer uw coach er een start, verschijnt hier Deelnemen.',
          chipStart: 'Start',
          chipJoin: 'Deelnemen',
          chipHost: 'Host',
          statusLoadFailed: 'Kon klaslokaalstatus niet laden. Probeer te vernieuwen.',
          zoomConnectedHint: 'Zoom is verbonden. Gebruik Start / Host als host, of Deelnemen als er een link is.',
          start: 'Start',
          startZoom: 'Zoom starten',
          host: 'Host',
          restart: 'Opnieuw starten',
          join: 'Deelnemen',
          joinSession: 'Deelnemen aan sessie',
          starting: 'Openen…',
          startFailed: 'Kon Zoom-klaslokaal niet starten.',
          openInZoomFailed: 'Geen Zoom-hostlink beschikbaar. Probeer Host, of vraag een admin Zoom opnieuw te verbinden.',
          meetingReady: 'Meeting klaar',
          meetingNotCreated: 'Meeting nog niet aangemaakt',
          end: "Les beëindigen",
          ending: "Beëindigen…",
          endFailed: "Kon de les niet beëindigen.",
          meetingStatusCreating: 'Wordt aangemaakt…',
          meetingStatusStarted: 'Gestart',
          meetingStatusFailed: 'Mislukt — probeer Start opnieuw',
          meetingStatusEnded: 'Beëindigd',
          meetingStatusUnknown: 'Status onbekend',
          unnamedSession: 'Sessie',
          selectChildTitle: 'Selecteer een kind',
          selectChildHint: 'Kies een kind in de kop van het ouderportaal om hun live lessen te zien.',
          noChildrenTitle: 'Geen gekoppelde kinderen',
          noChildrenHint: 'Er is nog geen goedgekeurd kind gekoppeld aan dit ouderaccount.',
          childrenLoadFailed: 'Gekoppelde kinderen konden niet worden geladen. Vernieuw de pagina of probeer opnieuw.',
          externalModeHint: 'Externe meetinglinks. Start opent de sessielink; Deelnemen gebruikt de deelnemers-URL indien beschikbaar.',
        },
        meeting: {
          title: 'Klaslokaalmeeting',
          subtitle: 'Videoles in ChessDesk',
          preparing: 'Zoom-klaslokaal voorbereiden…',
          preparingHint: 'De meeting wordt opgezet. Dit duurt meestal een paar seconden.',
          joinFailed: 'Kon niet deelnemen aan embedded klaslokaal.',
          notStarted: 'De host is het les nog niet gestart. Wacht tot je coach het klaslokaal opent.',
          back: 'Terug',
          backToLive: 'Live lessen',
          cancelled: "Deze sessie is geannuleerd.",
          ended: "Deze les is beëindigd.",
          startAsHost: 'Starten als host',
          redirectingExternal: 'Externe meeting openen…',
          openExternalNow: 'Meeting nu openen',
          askAdminZoomSetup: 'Vraag uw admin Zoom te configureren onder Klaslokaal → Zoom.',
          embedUnavailable: 'Embedded meetinggegevens niet beschikbaar.',
          hostCredentialsMissing: 'Hostgegevens ontbreken. Controleer Klaslokaal → Instellingen → Zoom-host e-mail.',
          hostOpenFailed: 'Kon de klas niet openen. Probeer het zo opnieuw.',
          hostTokenInvalid: 'Kon de klas niet openen. Probeer het zo opnieuw.',
          hostTokenInvalidStaff: 'Kon de klas niet openen. Probeer het zo opnieuw.',
          noExternalLink: 'Geen externe meetinglink voor deze sessie.',
          invalidSession: 'Ongeldige sessie.',
          leave: 'Meeting verlaten',
          retry: 'Opnieuw proberen',
          openInZoom: 'Openen in Zoom-app',
          inMeetingHint: 'Video draait in ChessDesk. Verlaat wanneer u klaar bent.',
          connectingHint: 'Verbinden…',
          connectedHint: 'Live in ChessDesk',
          livePill: 'Live',
          zoomToolbarHint: 'Gebruik de bedieningsbalk in de video om microfoon en camera in te schakelen.',
          cameraOffTitle: 'Camera staat uit',
          cameraOffHint: 'Tik onderaan op het camerapictogram om jezelf aan de klas te tonen.',
          waitingRoomAdmit: 'Toelaten',
          waitingRoomAdmitAll: 'Iedereen toelaten',
          waitingRoomView: 'Deelnemers',
          waitingRoomDismiss: 'Wachtruimtemelding sluiten',
          waitingRoomOne: '{{name}} is in de wachtruimte.',
          waitingRoomMany: '{{count}} deelnemers zijn in de wachtruimte: {{names}}.',
          waitingRoomManyMore: '{{count}} deelnemers zijn in de wachtruimte ({{names}}, en meer).',
          waitingForHostPill: 'Wachten op host',
          learnerWaitingHint: 'Je wacht tot de host je toelaat. Houd deze pagina open.',
          waitingForHostAutoRetry: 'We proberen automatisch opnieuw wanneer je coach de les start.',
          leaveHostConfirm: 'Verlaten zonder de les te beëindigen? De Zoom-meeting blijft live tot je Les beëindigen kiest.',
          endHostConfirm: 'Les voor iedereen beëindigen? Studenten worden uit de Zoom-meeting verwijderd.',
          leaving: 'Meeting verlaten…',
          joinTimedOut: 'Het klaslokaal opent te langzaam. Probeer opnieuw of open in Zoom-app.',
          hostConflictWarning: 'Er is mogelijk al een andere host in de meeting. Slechts één persoon moet Toelaten bedienen.',
          fixZoomSetup: 'Zoom Meeting SDK instellen →',
          sdkLoadFailed: 'Videoklaslokaal kon niet worden geladen. Probeer opnieuw of open in Zoom-app.',
          signatureInvalid: 'Zoom weigerde de meeting-handtekening. Plak onder Klaslokaal → Zoom de Development Client-ID en Secret voor embedded video.',
        },
        settings: {
          title: 'Klaslokaalinstellingen',
          subtitle: 'Standaard meetingopties wanneer ChessDesk een Zoom-klaslokaal maakt.',
          infoBanner: 'Deze voorkeuren worden lokaal als concept opgeslagen tot klaslokaalinstellingen op de server beschikbaar zijn.',
          infoBannerLive: 'Deze standaarden gelden wanneer ChessDesk een Zoom-meeting voor een sessie maakt.',
          defaultProvider: 'Standaard videoprovider',
          defaultProviderNote: 'Zoom is de primaire provider. Externe link is een fallback voor eigen meeting-URL’s.',
          providerExternalLink: 'Externe meetinglink',
          hostUserEmail: 'Zoom-host-e-mail',
          hostUserEmailNote: 'Optioneel. Zoom-account-e-mail om meetings te maken. Leeg = e-mail van de sessiecoach.',
          hostEmailInvalid: 'Voer een geldig Zoom-host-e-mailadres in (of laat leeg voor coach-e-mail).',
          waitingRoom: 'Wachtruimte inschakelen',
          waitingRoomNote: 'Deelnemers wachten tot de host hen toelaat.',
          muteOnEntry: 'Deelnemers dempen bij binnenkomst',
          allowStudentCameraDefault: 'Leerlingcamera standaard toestaan',
          allowStudentCameraNote: 'Hosts kunnen dit per meeting in Zoom wijzigen.',
          notifyOnStart: 'Leerlingen melden wanneer de les start',
          notifyOnStartNote: 'Indien ingeschakeld ontvangen ingeschreven leerlingen en gekoppelde ouders een in-app-melding met een Deelnemen-link wanneer een coach het klaslokaal start.',
          saveDraft: 'Concept opslaan',
          save: 'Instellingen opslaan',
          saving: 'Opslaan…',
          saveSuccess: 'Klaslokaalinstellingen opgeslagen.',
          saveFailed: 'Kon klaslokaalinstellingen niet opslaan.',
          loadFailed: 'Kon klaslokaalinstellingen niet laden.',
          retryLoad: 'Opnieuw proberen',
          draftSaved: 'Concept opgeslagen in deze browser.',
          draftSaveFailed: 'Kon concept niet opslaan in deze browser.',
          gotoZoom: 'Zoom-verbinding'
        },
        zoom: {
          title: 'Zoom-verbinding',
          subtitle: 'Koppel het Zoom-account van de academie zodat ChessDesk meetings kan maken en openen.',
          infoBanner: 'Maak een Server-to-Server OAuth-app in de Zoom Marketplace. Plak Account-ID en clientgegevens, en verbind. Vereiste scopes: meeting:write:meeting:admin, meeting:read:meeting:admin, user:read:user:admin en user:read:token:admin (voor embedded hostvideo). Maak ook een General-app met Meeting SDK Embed voor Development Client-ID + Secret. Voeg http://localhost:3000 toe aan de allow-list voor lokaal testen.',
          statusConnected: 'Verbonden',
          statusConnectedHint: 'ChessDesk kan meetings maken en embedded video hosten met dit Zoom-account.',
          statusNotConnected: 'Niet verbonden',
          statusNotConnectedHint: 'Een concept opslaan verbindt Zoom niet. OAuth / API moet nog op de server worden aangesloten.',
          statusNotConnectedHintLive: 'Voer credentials in en klik op Verbinden met Zoom. ChessDesk controleert ze bij Zoom en slaat ze veilig op de server op.',
          serverToServerHeading: 'Server-to-Server OAuth',
          serverToServerHint: 'Aanbevolen voor Zoom-accounts van de academie. Bewaar het clientgeheim niet in de browser in productie — dit concept-UI is alleen voor setup.',
          serverToServerHintLive: 'Aanbevolen voor Zoom-accounts van de academie. Het clientgeheim wordt op de server versleuteld en nooit meer in de browser getoond.',
          accountId: 'Account-ID',
          accountIdNote: 'Vanuit Zoom Marketplace → uw app → App Credentials.',
          clientId: 'Client-ID',
          clientSecret: 'Clientgeheim',
          clientSecretNote: 'Wordt niet in het lokale concept opgeslagen. Alleen invoeren wanneer de API-verbinding klaar is.',
          clientSecretConfiguredNote: 'Er is al een clientgeheim opgeslagen. Laat leeg om te behouden, of voer een nieuw geheim in.',
          clientSecretConnectNote: 'Vereist om te verbinden. Versleuteld opgeslagen op de server — na opslaan niet meer zichtbaar.',
          saveDraft: 'Concept opslaan',
          saveCredentials: 'Credentials opslaan',
          clearDraft: 'Concept wissen',
          connectOAuth: 'Verbinden met Zoom',
          connectDisabledTitle: 'Backend-verbinding nog niet beschikbaar',
          connectDisabledHint: '„Verbinden met Zoom” blijft uitgeschakeld tot de server credentials kan uitwisselen en tokens veilig opslaan.',
          connectHintLive: 'Verbinden wisselt credentials met Zoom uit en markeert de academie alleen als verbonden bij succes.',
          connectNeedsFields: 'Voer Account-ID, Client-ID en clientgeheim in, en klik op Verbinden met Zoom.',
          draftSaved: 'Concept opgeslagen in deze browser. Zoom is nog steeds niet verbonden.',
          draftSavedServer: 'Credentials opgeslagen op de server. Zoom is pas verbonden na Verbinden met Zoom.',
          draftSavedConnected: 'Credentials bijgewerkt. Verbindingsstatus blijft gelijk tot u opnieuw verbindt.',
          draftSaveFailed: 'Kon Zoom-instellingen niet opslaan.',
          draftCleared: 'Concept gewist.',
          loadFailed: 'Kon Zoom-instellingen niet laden.',
          validationRequired: 'Account-ID en Client-ID zijn verplicht.',
          secretRequired: 'Clientgeheim is vereist om te verbinden (of sla het eerst op).',
          connectSuccess: 'Succesvol verbonden met Zoom.',
          connectFailed: 'Kon niet verbinden met Zoom. Controleer credentials en of de app is geactiveerd.',
          disconnect: 'Verbreken',
          disconnected: 'Zoom verbroken. Credentials blijven bewaard om opnieuw te verbinden.',
          disconnectFailed: 'Kon Zoom niet verbreken.',
          webhookHeading: 'Gebeurtenisabonnementen (webhooks)',
          meetingSdkHeading: 'Ingesloten video (Meeting SDK)',
          meetingSdkHint: 'Optioneel voor in-app-video. Tot de Zoom-app is gepubliceerd: plak Client-ID en Secret onder App Credentials → Development.',
          meetingSdkDevRequired: 'Embedded deelnemen vereist Development Meeting SDK-credentials hier.',
          meetingSdkClientId: 'Meeting SDK Client-ID (ontwikkeling)',
          meetingSdkClientIdNote: 'Vanuit dezelfde Zoom-app → App Credentials → tabblad Development.',
          meetingSdkClientSecret: 'Meeting SDK Clientgeheim (ontwikkeling)',
          meetingSdkClientSecretNote: 'Development-clientgeheim — alleen om embedded tokens te ondertekenen.',
          meetingSdkClientSecretConfiguredNote: 'Er is een Meeting SDK-geheim opgeslagen. Laat leeg om te behouden.',
          webhookHint: 'In Zoom Marketplace → Feature → Event Subscriptions de URL toevoegen en abonneren op meeting.started, meeting.ended en meeting.deleted.',
          webhookSecret: 'Webhook-geheimtoken',
          webhookSecretNote: 'Vanuit Zoom Event Subscriptions. Vereist om meeting-lifecycle-webhooks te verifiëren.',
          webhookSecretConfiguredNote: 'Er is een webhook-geheim opgeslagen. Laat leeg om te behouden.',
          clearCredentials: 'Credentials wissen',
          clearConfirm: 'Opgeslagen Zoom Account-ID, Client-ID en clientgeheim voor deze academie verwijderen?',
          cleared: 'Zoom-credentials gewist.',
          clearFailed: 'Kon Zoom-credentials niet wissen.',
          clearing: 'Wissen…',
          saving: 'Opslaan…',
          connecting: 'Verbinden…',
          disconnecting: 'Verbreken…',
          gotoSettings: 'Klaslokaalinstellingen →'
        }
      },
      playing: {
        hubTitle: 'Speelplatform',
        hubSubtitle: 'Speel schaak, daag clubgenoten uit en klim op het academieranglijst.',
        playTitle: 'Schaakbord',
        playSubtitle: 'Doe je zet',
        loadFailed: 'Speelplatformgegevens konden niet worden geladen.',
        moveFailed: 'Illegale of geweigerde zet.',
        resignFailed: 'Opgeven lukte niet.',
        yourRating: 'Jouw rating',
        record: 'W {{w}} · R {{d}} · V {{l}}',
        leaderboard: 'Ranglijst',
        leaderboardSubtitle: 'Topspelers in deze academie op interne rating.',
        viewLeaderboard: 'Ranglijst bekijken',
        playComputer: 'Speel tegen de computer',
        playComputerDesc: 'Oefen tegen een ingebouwde engine. Hogere niveaus spelen sterkere tactiek.',
        computerLevel: 'Moeilijkheid',
        ratedGame: 'Gewaardeerde partij',
        casualGame: 'Vriendschappelijke partij',
        playAsWhite: 'Speel met wit',
        playAsBlack: 'Speel met zwart',
        challenges: 'Inkomende uitdagingen',
        challengePlayer: 'Daag een speler uit',
        challengeSubtitle: 'Stuur een partijuitnodiging naar een ander academielid.',
        noChallenges: 'Geen openstaande uitdagingen.',
        accept: 'Accepteren',
        decline: 'Weigeren',
        myGames: 'Mijn partijen',
        myGamesSubtitle: 'Recente partijen die je op dit platform speelde.',
        noGames: 'Nog geen partijen — start er een vanuit de Speelhub.',
        players: 'Spelers',
        player: 'Speler',
        correspondence: 'Tijdmodus',
        liveClock: 'Live-klok',
        correspondenceMode: 'Correspondentie ({{n}} dagen per zet)',
        daysPerMove: '{{n}} dag(en) per zet',
        correspondenceHint: 'Elke speler heeft {{n}} dagen voor een zet. Geen aflopende klok.',
        moveDeadline: 'Zet vóór {{when}}',
        type: 'Type',
        statusLabel: 'Status',
        open: 'Openen',
        gameInfo: 'Partij',
        check: 'Schaak!',
        resign: 'Opgeven',
        backHub: 'Terug naar Speelhub',
        noMovesYet: 'Nog geen zetten.',
        rating: 'Rating',
        games: 'Partijen',
        noRatingsYet: 'Nog geen gewaardeerde partijen.',
        create: 'Maken',
        join: 'Meedoen',
        score: 'Score',
        searchPlayers: 'Spelers zoeken',
        challenge: 'Uitdagen',
        challengeSent: 'Uitdaging verzonden.',
        challengeFailed: 'Uitdaging kon niet worden verzonden.',
        reason: 'Uitslag',
        liveGames: 'Live partijen',
        liveGamesSubtitle: 'Bekijk lopende academiepartijen in realtime.',
        viewLive: 'Nu kijken',
        spectate: 'Toekijken',
        spectateTitle: 'Toekijken',
        spectatingHint: 'Alleen-lezen weergave — je kunt geen stukken zetten.',
        noLiveGames: 'Momenteel geen live partijen.',
        noLiveGamesHint: 'Wanneer clubgenoten een partij starten, verschijnt die hier.',
        allSpeeds: 'Alle',
        noLiveGamesForSpeed: 'Geen live partijen in deze tempo-klasse',
        tryAnotherSpeedFilter: 'Probeer een andere tempo-klasse of Alle.',
        noOpenSeeksExpired: 'Open zoekopdrachten verlopen — maak een nieuwe of daag uit.',
        noChallengesExpired: 'Inkomende uitdagingen verlopen',
        noChallengesExpiredHint: 'Stuur een nieuwe uitdaging of wacht op de volgende.',
        challengeExpired: 'Uitdaging verlopen',
        openSharedTip: 'Tip openen',
        boardSettingsLoadFailed: 'Bordinstellingen konden niet worden geladen.',
        prefsSaveFailed: 'Voorkeuren konden niet met de server worden gesynchroniseerd.',
        prevPly: 'Vorige zet',
        nextPly: 'Volgende zet',
        coachPlyOf: 'Zet {{current}} / {{total}}',
        fairPlayShowAllLevels: 'Alle niveaus tonen',
        expiresIn: 'Verloopt over {{when}}',
        incomingChallenges: 'Inkomende uitdagingen',
        confirmTitle: 'Bevestigen',
        sendChallenge: 'Uitdaging sturen',
        inboxPartialLoadFailed: 'Sommige uitdaginglijsten konden niet worden geladen. Vernieuw.',
        bye: 'vrijstelling',
        format: 'Formaat',
        teamA: 'Naam ploeg A',
        teamB: 'Naam ploeg B',
        chooseTeam: 'Team kiezen',
        selectTeam: 'Selecteer een team…',
        team: 'Team',
        fairPlay: 'Fair play',
        fairPlaySubtitle: 'Controleer beëindigde menselijke partijen op ongebruikelijke bedenktijden en engine-achtig spel.',
        fairPlayReview: 'Fair play-controle',
        fairPlayMinLevel: 'Minimumniveau',
        fairPlayEmpty: 'Geen gemarkeerde partijen',
        fairPlayEmptyHint: 'Beëindigde partijen boven de drempel verschijnen hier.',
        fairPlayMedian: 'Mediaan',
        fairPlayFastMoves: 'Snelle zetten',
        fairPlayStreak: 'Snelle reeks',
        fairPlayCase: 'Zaak',
        fairPlayCases: 'Zaken',
        fairPlayNotes: 'Stafnotities',
        fairPlayAddNote: 'Notitie toevoegen',
        fairPlayNotePlaceholder: 'Observatie, bewijs, opvolging…',
        fairPlaySaveNote: 'Notitie opslaan',
        fairPlayOpenCase: 'Zaak openen / bijwerken',
        fairPlayBanPlayer: 'Van speelplatform uitsluiten',
        fairPlayBanReason: 'Banreden',
        fairPlayBanDays: 'Dagen (leeg = onbepaald)',
        fairPlayLiftBan: 'Ban opheffen',
        fairPlayActiveBans: 'Actieve bans',
        fairPlayCaseStatus: 'Zaakstatus',
        fairPlayResolution: 'Besluit',
        fairPlaySubject: 'Betrokkene',
        fairPlayModeration: 'Moderatie',
        fairPlayNoteFailed: 'Kon notitie niet opslaan.',
        fairPlayCaseFailed: 'Kon zaak niet bijwerken.',
        fairPlayBanFailed: 'Kon ban niet toepassen.',
        fairPlayReportFailed: 'Kon dit rapport niet bijwerken.',
        fairPlayAppealFailed: 'Kon dit beroep niet bijwerken.',
        fairPlayCaseStatusLabel: {
          OPEN: 'Open',
          WATCHING: 'In de gaten',
          ACTIONED: 'Actie ondernomen',
          CLOSED: 'Gesloten',
          DISMISSED: 'Afgewezen'
        },
        fairPlayResolutionLabel: {
          NONE: 'Geen',
          WARNING: 'Waarschuwing',
          BAN: 'Uitsluiting',
          DISMISSED: 'Afgewezen'
        },

        moveTiming: 'Zettijden',
        thinkTime: 'Bedenktijd',
        openBoard: 'Bord openen',
        runEngineReview: 'Engine-controle starten',
        engineReviewRunning: 'Stockfish bezig…',
        engineReviewFailed: 'Engine-controle mislukt.',
        engineReviewAutoPending: 'Automatische anti-cheat staat in de wachtrij…',
        engineReviewAutoRunning: 'Automatische anti-cheat analyseert elke zet…',
        engineReviewAutoDone: 'Automatische zet-voor-zet anti-cheat voltooid.',
        engineReviewAutoFailed: 'Automatische anti-cheat mislukt — je kunt hieronder opnieuw starten.',
        engineFlag: 'Vlag',

        engineMatchTop1: 'Top-1-match',
        engineMatchTop3: 'Top-3-match',
        avgCpLoss: 'Gem. CPL',
        engineLevel: 'Engine-vlag',
        engineMatch: 'Match',
        cpLoss: 'CPL',
        jumpToPly: 'Spring',
        thinkSparkTitle: 'Bedenktijdprofiel',
        thinkSparkHint: 'Amber/rood = ultrasnelle zetten na de opening.',
        analysisQuick: 'Snel (d10)',
        analysisDeep: 'Diep (d16)',
        analysisCloud: 'Cloud-analyse',
        analysisDepth: 'Analysediepte',
        clubReview: 'Clubreview',
        playAgain: 'Opnieuw spelen',
        prevMistake: 'Vorige fout',
        nextMistake: 'Volgende fout',
        reviewReady: 'Review klaar',
        retryClubReview: 'Clubreview opnieuw',
        clubReviewNoMoves: 'Geen zetten — clubreview niet beschikbaar. Gebruik Analyseren.',
        clubReviewTooShort: 'Partij te kort voor automatische clubreview. Probeer Snel of Cloud.',
        clubReviewFailed: 'Clubreview mislukt. Opnieuw proberen, of Snel / Cloud-analyse.',
        clubReviewPending: 'Clubreview bereidt zettenkwaliteit voor…',
        clubReviewSlow: 'Clubreview duurt langer dan gebruikelijk. Je kunt lokaal analyseren.',
        clubReviewUnavailable: 'Clubreview niet beschikbaar voor deze partij. Gebruik Snel of Cloud.',
        clubReviewRequestFailed: 'Kon clubreview niet starten.',
        cancelAnalysis: 'Annuleren',
        cloudAnalysisFailed: 'Cloud-analyse mislukt. Is server-Stockfish beschikbaar?',
        fairPlayFastSummary: 'W snel {{white}} · Z snel {{black}}',
        moveNumber: '#',
        san: 'SAN',
        noGamesMatchFilter: 'Geen partijen voor dit filter',
        noGamesMatchFilterHint: 'Probeer een ander filter, of ga terug naar Alles.',
        noGamesMatchSearch: 'Geen partijen voor deze zoekopdracht',
        noGamesMatchSearchHint: 'Probeer een andere naam of wis de zoekopdracht.',
        clearSearch: 'Zoekopdracht wissen',
        teamNamesRequired: 'Voer twee verschillende teamnamen in.',
        filterAll: 'Alles',
        yourMove: 'Jouw zet',
        correspondenceGames: 'Correspondentie',
        fairPlayLevel: {
          LOW: 'OK',
          WATCH: 'Let op',
          HIGH: 'Hoog'
        },
        customTime: 'Aangepast',
        minutes: 'Minuten',
        increment: 'Tijdverhoging (sec)',
        yourColor: 'Jouw kleur',
        colorRandom: 'Willekeurig',
        colorRandomSide: 'Willekeurige kant',
        colorWhite: 'Wit',
        colorBlack: 'Zwart',
        offerDraw: 'Remise aanbieden',
        acceptDraw: 'Remise accepteren',
        declineDraw: 'Remise weigeren',
        opponentOffersDraw: 'Tegenstander biedt remise aan',
        youOfferedDraw: 'Remise aangeboden — wachten op tegenstander',
        drawFailed: 'Remiseaanbod kon niet worden bijgewerkt.',
        abort: 'Partij afbreken',
        abortFailed: 'Deze partij kon niet worden afgebroken.',
        abortLeaveFailed: 'Wachtende uitdaging kon niet worden geannuleerd. Die kan tot verlopen zichtbaar blijven voor je tegenstander.',
        whiteToMove: 'Wit aan zet',
        blackToMove: 'Zwart aan zet',
        yourTurn: 'Jij bent aan zet',
        engineThinking: 'Engine denkt na…',
        moves: 'Zetten',
        resignConfirm: 'Deze partij opgeven?',
        playNow: 'Nu spelen',
        resume: 'Hervatten',
        hubRatingHint: 'Klim op de academieladder met gewaardeerde partijen.',
        challengeShortcut: 'Nodig een clubgenoot uit',
        myGamesShortcut: 'Doorgaan of bekijken',
        challengesHint: 'Wordt live bijgewerkt wanneer iemand je uitdaagt.',
        noChallengesHint: 'Daag een clubgenoot uit om een live partij te starten.',
        noGamesHint: 'Speel tegen de computer of daag iemand uit om deze lijst te vullen.',
        noRatingsHint: 'Speel een paar gewaardeerde partijen om op de ranglijst te komen.',
        noPlayersFound: 'Geen spelers gevonden',
        noPlayersFoundHint: 'Probeer een andere naam of gebruikersnaam.',
        searchPlayersPlaceholder: 'Zoek op naam of gebruikersnaam',
        levelEasy: 'Makkelijk',
        levelHard: 'Moeilijk',
        recordShort: 'W/R/V',
        waitingOpponent: 'Wachten op tegenstander…',
        premoveHint: 'Je kunt al voorbewegen terwijl je wacht.',
        moveDeadlineShort: 'Zet vóór {{when}}',
        waitingToJoinTitle: 'Wachten tot tegenstander meedoet',
        waitingToJoinHint: 'Wachten tot {{name}} meedoet… Klokken starten bij accepteren.',
        waitingToJoinInviteeHint: 'Accepteer om de partij tegen {{name}} te starten.',
        premoveArmed: 'Voorgezet klaar: {{from}}→{{to}} — tik Clear of de velden om te annuleren',
        clearPremove: 'Wis voorbeweging',
        banQuickLinkBlocked: 'Niet beschikbaar tijdens een ban',
        banConfirm: 'Deze speler van het speelplatform uitsluiten?',
        ratedConfirm: 'Dit is een rated partij en beïnvloedt je rating. Doorgaan?',
        challengeAcceptFailed: 'Uitdaging accepteren mislukt. Probeer opnieuw.',
        challengeDeclineFailed: 'Afwijzen mislukt. Probeer opnieuw.',
        sharedAnalysis: 'Gedeelde analyse van {{name}}',
        loading: 'Laden…',
        banBanner: 'Je bent uitgesloten van het speelplatform.',
        banBannerReason: 'Reden: {{reason}}',
        banBannerUntil: 'Tot {{when}}',
        banBannerIndefinite: 'Deze uitsluiting heeft geen einddatum.',
        timeControl: {
          label: 'Speeltempo',
          daysPerMove: '{{count}}d/zet'
        },
        countdown: {
          zeroSeconds: '0s',
          daysHours: '{{d}}d {{h}}u',
          days: '{{d}}d',
          hoursMinutes: '{{h}}u {{m}}m',
          hours: '{{h}}u',
          minutes: '{{m}}m',
          seconds: '{{s}}s'
        },
        shareAnalysisBtn: 'Analyse delen',
        shareAnalysisDone: 'Analyse gedeeld met toeschouwers',
        shareAnalysisFailed: 'Kon analyse niet delen.',
        bookMoves: 'Boek',
        bookMovesHint: 'Clubboek-suggesties (alleen tegen computer)',
        exploreOpening: 'Verkennen',
        pickOpening: 'Clubopening laden',
        pickOpeningPlaceholder: 'Kies een opening…',
        openingLoaded: '{{name}} geladen',
        openingLoadFailed: 'Kon die opening niet laden',
        liveNowPeek: 'Nu live',
        autoQueen: 'Altijd promoveren tot dame',
        showBookHints: 'Openingsboek-hints',
        myStudents: 'Mijn leerlingen',
        rosterLoadFailed: 'Kon je leerlingen niet laden.',
        noRosterStudents: 'Nog geen leerlingen op je rooster',
        typeToSearchOrRoster: 'Typ een naam of kies hierboven een leerling.',
        drillOn: 'Boekdrill aan',
        drillOff: 'Boekdrill',
        drillScore: '{{correct}} / {{attempts}} boekzetten',
        drillOutOfBook: 'Buiten het boek',
        drillCorrect: 'Boekzet!',
        drillWrong: 'Niet in het clubboek{{hint}}',
        pgnPackBusy: 'PGN wordt gemaakt…',
        pgnPackEmpty: 'Nog geen afgeronde partijen om te exporteren.',
        pgnPackFailed: 'Kon PGN-pakket niet downloaden.',
        accuracyChip: '{{pct}}% nauwkeurigheid',
        accuracy: 'Nauwkeurigheid',
        recentAccuracy: '{{pct}}% recente nauwkeurigheid',
        recentAccuracyGames: '{{n}} partijen',
        vsYouRecord: 'tegen jou {{w}}–{{d}}–{{l}}',
        openEvent: 'Event openen',
        recentOpponents: 'Recente tegenstanders',
        gameLoadedOnCoachBoard: 'Partij #{{id}} geladen',
        gameLoadFailed: 'Kon die partij niet op het coachbord laden.',
        noActiveBans: 'Geen actieve bans.',
        liftBanConfirm: 'Deze uitsluiting opheffen?',
        hub: 'Terug naar Speelhub',

        currentRound: 'Huidige ronde',
        allowSpectators: 'Toeschouwers toestaan',
        spectatorsOff: 'Geen toeschouwers',
        fairPlayCaseAutoOpened: 'Automatisch (engine)',
        showCoordinates: 'Coördinaten',
        leaderboardGames: 'Partijen',
        flipBoard: 'Bord omdraaien',
        copyPgn: 'PGN kopiëren',
        downloadPgn: 'PGN downloaden',
        copyGameLink: 'Partijlink kopiëren',
        copied: 'Gekopieerd!',
        copyFailed: 'PGN kon niet worden gekopieerd.',
        rematch: 'Revanche',
        rematchSent: 'Uitdaging verzonden',
        rematchSentHint: 'Revanche-uitdaging verzonden — wachten op je tegenstander.',
        rematchFailed: 'Revanche-uitdaging kon niet worden verzonden.',
        rematchInboxLoadFailed: 'Revanche-uitdagingen laden mislukt. Probeer opnieuw.',
        offerTakeback: 'Zetterugname',
        acceptTakeback: 'Zetterugname accepteren',
        declineTakeback: 'Zetterugname weigeren',
        takebackFailed: 'Zetterugname kon niet worden bijgewerkt.',
        opponentOffersTakeback: 'Tegenstander vraagt om zetterugname',
        youOfferedTakeback: 'Zetterugname aangeboden — wachten op tegenstander',
        typeMove: 'Typ een zet',
        typeMovePlaceholder: 'e2e4 of Nf3',
        playMove: 'Zet',
        chat: 'Chat',
        chatPlaceholder: 'Bericht aan tegenstander…',
        chatSend: 'Versturen',
        chatEmpty: 'Nog geen berichten.',
        chatReadOnly: 'Toekijken',
        chatLoadFailed: 'Chat kon niet worden geladen.',
        chatSendFailed: 'Bericht kon niet worden verzonden.',
        chatOffline: 'U bent offline — maak opnieuw verbinding om te chatten.',
        quickMatch: 'Snelle partij',
        quickMatchDesc: 'Plaats een zoekopdracht of join een open lobby met hetzelfde speeltempo.',
        findOpponent: 'Tegenstander zoeken',
        seekWaiting: 'Wachten op een tegenstander…',
        cancelSeek: 'Zoekopdracht annuleren',
        seekFailed: 'Zoekopdracht kon niet worden bijgewerkt.',
        cancelSeekFailed: 'Zoekopdracht kon niet worden geannuleerd.',
        acceptSeekFailed: 'Kon niet deelnemen aan deze zoekopdracht.',
        seekMatched: 'Tegenstander gevonden — bord wordt geopend…',
        outgoingChallenges: 'Uitgaande uitdagingen',
        outgoingChallengesHint: 'Uitdagingen die je hebt gestuurd en die nog wachten.',
        noOutgoingChallenges: 'Geen uitgaande uitdagingen.',
        noOutgoingChallengesHint: 'Wanneer je iemand uitdaagt, verschijnt die hier tot geaccepteerd of geannuleerd.',
        cancelChallenge: 'Annuleren',
        cancelChallengeFailed: 'Deze uitdaging kon niet worden geannuleerd.',
        challengeAccepted: 'Uitdaging geaccepteerd',
        challengeDeclined: 'Uitdaging geweigerd',
        challengeCancelled: 'Uitdaging geannuleerd',
        accuracy: 'Nauwkeurigheid',
        moveQuality: {
          best: 'Beste',
          brilliant: 'Briljant',
          good: 'Goed',
          inaccuracy: 'Onnauwkeurigheid',
          mistake: 'Fout',
          blunder: 'Blunder'
        },
        continueGame: 'Partij bezig',
        activeGames: 'Bezig',
        pastGames: 'Afgelopen',
        noPastGames: 'Nog geen afgelopen partijen.',
        review: 'Naspelen',
        cancel: 'Annuleren',
        noPairings: 'Nog geen paringen',
        noPairingsHint: 'Paringen verschijnen nadat de organisator een ronde start.',
        choosePromotion: 'Kies promotie',
        challengeSentHint: 'Wachten tot <profile>{{name}}</profile> accepteert op de Speelhub.',
        challengeAnother: 'Iemand anders uitdagen',
        typeToSearch: 'Zoek een speler',
        typeToSearchHint: 'Typ minstens 2 tekens om in de academie te zoeken.',
        levelDepth: 'Engine-diepte ~{{depth}}',
        abortConfirm: 'Deze partij afbreken?',
        soundOn: 'Geluid aan',
        soundOff: 'Geluid uit',
        reviewingPly: 'Zet {{ply}} / {{total}}',
        you: 'Jij',
        reviewMoves: 'Zetten naspelen',
        showResult: 'Uitslag',
        dismissResult: 'Sluiten',
        backToLive: 'Live',
        retry: 'Opnieuw',
        plyStart: 'Eerste zet',
        plyPrev: 'Vorige zet',
        plyNext: 'Volgende zet',
        plyEnd: 'Laatste zet',
        searchFailed: 'Spelerzoeken mislukt. Probeer opnieuw.',
        searchFailedHint: 'Controleer je verbinding en typ een andere zoekopdracht.',
        outsideTop: 'Buiten de huidige ranglijstpagina',
        reconnecting: 'Verbinding verbroken — opnieuw verbinden en synchroniseren…',
        timeoutFailed: 'Timeout kon niet worden geclaimd. Probeer te vernieuwen.',
        firstMoveYourTurn: 'Doe je eerste zet — {{seconds}}s (daarna starten de klokken)',
        firstMoveWaiting: 'Wachten op eerste zet — {{seconds}}s',
        engineFailed: 'Computerzet mislukt. Vernieuw als het bord vast lijkt te zitten.',
        challengeIncoming: 'Nieuwe uitdaging',
        analyze: 'Analyseren',
        reanalyze: 'Opnieuw analyseren',
        analyzing: 'Analyseren… {{pct}}%',
        hideAnalysis: 'Analyse verbergen',
        analysis: 'Engine-eval',
        bestMove: 'Beste zet',
        levelBand: {
          beginner: 'Beginner',
          intermediate: 'Gevorderd',
          club: 'Clubsterkte',
          strong: 'Sterk'
        },
        gameType: {
          VS_COMPUTER: 'vs computer',
          CASUAL: 'Vriendschappelijk',
          RATED: 'Gewaardeerd'
        },
        resultReason: {
          CHECKMATE: 'Schaakmat',
          RESIGN: 'Opgave',
          STALEMATE: 'Pat',
          AGREEMENT: 'Remise met overeenstemming',
          THREEFOLD: 'Drievoudige herhaling',
          FIFTY_MOVE: 'Vijftigzettenregel',
          INSUFFICIENT: 'Onvoldoende materiaal',
          TIMEOUT: 'Tijd verlopen',
          ABORT: 'Afgebroken',
          COMPUTER: 'Computer'
        },
        status: {
          ACTIVE: 'Bezig',
          WHITE_WON: 'Wit wint',
          BLACK_WON: 'Zwart wint',
          DRAW: 'Remise',
          ABORTED: 'Afgebroken',
          PENDING: 'Wacht op start'
        },

        acceptAppeal: 'Beroep accepteren',
        acceptRematch: 'Revanche accepteren',
        adjudicateConfirm: 'Wit wint voor deze pairing zetten?',
        adjudicateConfirmBlack: 'Zwart wint voor deze pairing zetten?',
        adjudicateConfirmDraw: 'Remise voor deze pairing zetten?',
        appealFailed: 'Beroep kon niet worden verzonden.',
        appealPending: 'Beroep in behandeling',
        appealPlaceholder: 'Leg uit waarom het verbod moet worden opgeheven…',
        appealRejectNotePrompt: 'Notitie bij afwijzing (optioneel)',
        appealRejectedNote: 'Beroep afgewezen',
        applyFen: 'FEN toepassen',
        applyPgn: 'PGN laden',
        animationSpeed: 'Animatie',
        animationSlow: 'Traag',
        animationNormal: 'Normaal',
        animationFast: 'Snel',
        banStatusUnknown: 'Banstatus niet beschikbaar',
        boardSettings: 'Bordinstellingen',
        boardSettingsHint: 'Gesynchroniseerde voorkeuren op elk bord',
        boardTheme: 'Bordthema',
        boardThemes: { classic: 'Klassiek', green: 'Groen', brown: 'Bruin', blue: 'Blauw' },
        openings: {
          'alekhine_defence': "Aljechin-verdediging",
          'benoni_benko_path': "Benoni / Benko path",
          'birds_opening': "Bird's Opening",
          'caro_kann_defence': "Caro-Kann-verdediging",
          'caro_kann_advance': "Caro-Kann — Advance",
          'caro_kann_classical': "Caro-Kann — Classical",
          'dutch_defence': "Dutch Defence",
          'english_opening': "English Opening",
          'english_1_e5': "English — 1…e5",
          'english_1_e6': "English — 1…e6",
          'english_anglo_indian': "English — Anglo-Indian",
          'english_symmetrical': "English — Symmetrical",
          'evans_gambit': "Evans-gambiet",
          'french_defence': "Franse verdediging",
          'french_advance': "French — Advance",
          'french_classical': "French — Classical",
          'french_exchange': "French — Exchange",
          'giuoco_piano': "Giuoco Piano",
          'giuoco_piano_open': "Giuoco Piano — Open",
          'giuoco_piano_c3': "Giuoco Piano — c3",
          'giuoco_piano_cxd4': "Giuoco Piano — cxd4",
          'giuoco_piano_d4': "Giuoco Piano — d4",
          'indian_defence': "Indian Defence",
          'indian_3_nf3': "Indian — 3.Nf3",
          'italian_game': "Italiaanse opening",
          'kings_gambit': "Koningsgambiet",
          'kings_indian_defence': "King's Indian Defence",
          'kings_knight_opening': "Koningspaardopening",
          'kings_knight_two_knights_path': "King's Knight — Two Knights path",
          'kings_pawn': "Koningspion",
          'london_system': "Londens systeem",
          'london_system_nc6': "Londens systeem — Nc6",
          'london_system_c5': "Londens systeem — c5",
          'london_system_main_line': "Londens systeem — main line",
          'modern_defence': "Moderne verdediging",
          'nimzo_indian_defence': "Nimzo-Indian Defence",
          'nimzo_indian_path': "Nimzo-Indian path",
          'nimzo_larsen_attack': "Nimzo–Larsen Attack",
          'open_game': "Open partij",
          'owens_defence': "Owen-verdediging",
          'petrov_defence': "Petrov-verdediging",
          'philidor_defence': "Philidor-verdediging",
          'pirc_defence': "Pirc-verdediging",
          'qgd': "Afgewezen damegambiet",
          'queens_gambit': "Damegambiet",
          'queens_gambit_accepted': "Damegambiet Accepted",
          'queens_gambit_declined': "Afgewezen damegambiet",
          'queens_gambit_declined_3_nf3': "Afgewezen damegambiet — 3.Nf3",
          'queens_gambit_declined_bg5': "Afgewezen damegambiet — Bg5",
          'queens_gambit_declined_london_hybrid': "Afgewezen damegambiet — London hybrid",
          'queens_indian_nimzo_path': "Queen's Indian / Nimzo path",
          'queens_pawn': "Damepion",
          'queens_pawn_game': "Damepionpartij",
          'ruy_lopez': "Spaanse opening",
          'ruy_lopez_morphy': "Spaanse opening — Morphy",
          'ruy_lopez_morphy_defence': "Spaanse opening — Morphy Defence",
          'ruy_lopez_morphy_bb3': "Spaanse opening — Morphy, Bb3",
          'ruy_lopez_morphy_closed': "Spaanse opening — Morphy, Closed",
          'ruy_lopez_morphy_re1': "Spaanse opening — Morphy, Re1",
          'ruy_lopez_morphy_b5': "Spaanse opening — Morphy, b5",
          'ruy_lopez_morphy_d6': "Spaanse opening — Morphy, d6",
          'r_ti_opening': "Réti Opening",
          'r_ti_1_nf6': "Réti — 1…Nf6",
          'r_ti_1_c5': "Réti — 1…c5",
          'r_ti_1_d5': "Réti — 1…d5",
          'scandinavian_defence': "Scandinavische verdediging",
          'scandinavian_2_nf6': "Scandinavian — 2…Nf6",
          'scandinavian_2_qxd5': "Scandinavian — 2…Qxd5",
          'sicilian_defence': "Siciliaanse verdediging",
          'sicilian_open': "Siciliaans open",
          'sicilian_2_nf3': "Sicilian — 2.Nf3",
          'sicilian_2_nc6': "Sicilian — 2…Nc6",
          'sicilian_2_d6': "Sicilian — 2…d6",
          'sicilian_2_e6': "Sicilian — 2…e6",
          'sicilian_classical_path': "Sicilian — Classical path",
          'sicilian_dragon': "Sicilian — Dragon",
          'sicilian_dragon_classical': "Sicilian — Dragon, Classical",
          'sicilian_dragon_yugoslav': "Sicilian — Dragon, Yugoslav",
          'sicilian_hyperaccelerated': "Sicilian — Hyperaccelerated",
          'sicilian_najdorf': "Sicilian — Najdorf",
          'sicilian_najdorf_english_attack': "Sicilian — Najdorf, English Attack",
          'slav_defence': "Slav Defence",
          'two_knights_defence': "Twee paardenverdediging",
          'van_geet_opening': "Van Geet Opening"
        },
        defaultTeamA: 'Ploeg A',
        defaultTeamB: 'Ploeg B',
        claimDraw: 'Remise claimen',
        claimDrawFailed: 'Remise claimen mislukt.',
        clearHighlights: 'Markeringen wissen',
        coachBoard: 'Coachbord',
        coachBoardArrowHint: 'Rechtsklik markeert; tweede rechtsklik tekent een pijl.',
        coachBoardClassroomHint: 'Voor audio/video Classroom live gebruiken. Dit bord is alleen voor stellingen en pijlen.',
        coachBoardSubtitle: 'Lesbord — zet een stelling, teken pijlen, open Classroom voor A/V',
        confirmMove: 'Zet bevestigen',
        copyFen: 'FEN kopiëren',
        copyFailed: 'Kopiëren mislukt',
        dismissReport: 'Afwijzen',
        favoriteOpenings: 'Favoriete openingen',
        fenCopied: 'FEN gekopieerd',
        gameChat: 'Partijchat',
        highContrast: 'Hoog contrast',
        hubPartialLoad: 'Sommige hubgegevens zijn niet geladen',
        invalidFen: 'Ongeldige FEN',
        invalidPgn: 'PGN kon niet worden gelezen',
        loadMoreGames: 'Meer laden ({{loaded}} van {{total}})',
        moveConfirmation: 'Zetten bevestigen',
        moveSending: 'Zet wordt verzonden…',
        myRatingLoadFailed: 'Rating kon niet worden geladen',
        noOpenAppeals: 'Geen open beroepen',
        noOpenReports: 'Geen open meldingen',
        noOpenSeeks: 'Geen open zoekopdrachten',
        noOpeningsYet: 'Nog geen openingen',
        openAppeals: 'Open beroepen',
        openClassroomLive: 'Classroom live openen',
        openGame: 'Partij openen',
        openReports: 'Open meldingen',
        peakRating: 'Piek',
        pgnLoaded: 'PGN geladen op het bord',
        pgnPaste: 'PGN plakken',
        pgnPasteHint: 'Volledige PGN of zettenlijst…',
        pieceSet: 'Stukkenstijl',
        pieceSetStandard: 'Standaard',
        pieceSetUnicode: 'Unicode',
        playerNotFound: 'Speler niet gevonden',
        playerProfile: 'Spelersprofiel',
        playerProfileSubtitle: 'Ratings per tempo en partijen',
        playingNow: 'Speelt nu',
        prefsSaved: 'Voorkeuren opgeslagen',
        ratingTrend: 'Recente ratingtrend',
        ratingsBySpeed: 'Ratings per tempo',
        recentGames: 'Recente partijen',
        rejectAppeal: 'Beroep afwijzen',
        rematchIncoming: 'Revanche-aanbod',
        rematchIncomingToast: 'Revanche-uitdaging',
        rematchSending: 'Verzenden…',
        reportDetails: 'Details (optioneel)',
        reportFailed: 'Melding kon niet worden verzonden.',
        reportPlayer: 'Speler melden',
        reportPrompt: 'Beschrijf kort waarom u meldt (optioneel):',
        reportReasonPrompt: 'Reden van melding',
        reportSent: 'Melding verzonden. Staff bekijkt dit.',
        reportedBy: 'Gemeld door',
        reportsAppealsLoadFailed: 'Meldingen/beroepen niet geladen',
        resetBoard: 'Reset',
        resolveReport: 'Afhandelen',
        searchGames: 'Partijen zoeken',
        searchGamesPlaceholder: 'Tegenstander of status…',
        shortcutEnds: 'Naar begin / einde',
        shortcutHelpTitle: 'Sneltoetsen',
        shortcutScrub: 'Door zetten bladeren',
        shortcutToggleHelp: 'Hulp tonen / verbergen',
        shortcuts: 'Sneltoetsen',
        sound: 'Geluid',
        speedClass: 'Tempo',
        speedClassLabel: 'Tempoklasse',
        submitAppeal: 'Beroep indienen',
        submitReport: 'Melding indienen',
        submitting: 'Verzenden…',
        themesLoadFailed: 'Thema\'s niet geladen',
        tourneyFilterActive: 'Open & lopend',
        tourneyFilterDraft: 'Concept',
        tourneyFilterCompleted: 'Voltooid',
        undoMove: 'Zet terug',
        reportReason: {
          assistance: 'Vermoedelijke engine / hulp',
          chat: 'Beledigende chat',
          stalling: 'Traineren / weigeren te spelen',
          other: 'Overig'
        },
},
      auth: {
        welcome: 'Welkom',
        signInTo: 'Meld u aan bij {{name}}',
        login: 'Aanmelden',
        username: 'Gebruikersnaam',
        password: 'Wachtwoord',
        forgot: 'Wachtwoord vergeten?',
        enterUsername: 'Voer uw gebruikersnaam in',
        enterPassword: 'Voer uw wachtwoord in',
        invalid: 'Ongeldige gebruikersnaam of wachtwoord',
        showPassword: 'Wachtwoord tonen',
        hidePassword: 'Wachtwoord verbergen',
        agreePrefix: 'Door u aan te melden gaat u akkoord met onze',
        agreeAnd: 'en',
        privacyPolicy: 'Privacybeleid',
        terms: 'Voorwaarden',
        tooManyAttempts: 'Te veel pogingen. Probeer het later opnieuw.',
        sessionExpired: 'Uw sessie is verlopen. Meld u opnieuw aan.',
        sessionInvalid: 'Uw sessie is niet meer geldig. Meld u opnieuw aan.',
        sessionInactive: 'Uw sessie is verlopen wegens inactiviteit. Meld u opnieuw aan.'
      },
      footer: {
        quickLinks: 'Snelle links',
        academyContact: 'Contact academie',
        licenseInfo: 'Licentie-informatie',
        privacyPolicy: 'Privacybeleid',
        termsOfService: 'Servicevoorwaarden',
        terms: 'Voorwaarden',
        forgotPassword: 'Wachtwoord vergeten',
        helpGettingStarted: 'Help & aan de slag',
        helpFaq: 'Hulp & FAQ',
        allRightsReserved: 'Alle rechten voorbehouden.',
        developedBy: 'Ontwikkeld en gelicentieerd door',
        chessManagementSystem: 'Schaakbeheersysteem',
        contactDetailsHint: 'Contactgegevens kunnen door uw academiebeheerder worden toegevoegd.',
        licensePending: 'De licentiestatus verschijnt hier wanneer beschikbaar.',
        daysRemaining: '{{count}} dag resterend',
        daysRemaining_other: '{{count}} dagen resterend',
        licenseActive: 'Licentie actief',
        built: 'Build {{version}} · {{tz}}',
        unknownVersion: 'onbekend'
      },
      common: {
        language: 'Taal',
        save: 'Opslaan',
        cancel: 'Annuleren',
        loading: 'Laden…',
        edit: 'Bewerken',
        archive: 'Archiveren',
        add: 'Toevoegen',
        all: 'Alles',
        showing: 'Tonen',
        of: 'van',
        to: 'tot',
        results: 'resultaten',
        rowsPerPage: 'Rijen per pagina:',
        downloadExcel: 'Excel downloaden',
        search: 'Zoeken',
        id: 'ID:',
        idColumn: 'ID',
        items: 'onderdelen',
        back: 'Terug',
        firstPage: 'Eerste pagina',
        previousPage: 'Vorige pagina',
        nextPage: 'Volgende pagina',
        lastPage: 'Laatste pagina',
        prev: 'Vorige',
        next: 'Volgende',
        pageOf: 'Pagina {{current}} van {{total}}',
        showingRange: '{{start}} tot {{end}} van {{total}} resultaten',
        noDataExport: 'Geen gegevens om te exporteren',
        exportFailed: 'Exporteren mislukt. Probeer het opnieuw.',
        downloadAsExcel: 'Downloaden als Excel (.xlsx)',
        active: 'Actief',
        archived: 'Gearchiveerd',
        inactive: 'Inactief',
        inactiveSuffix: ' (Inactief)',
        activate: 'Activeren',
        actions: 'Acties',
        status: 'Status',
        username: 'Gebruikersnaam',
        email: 'E-mail',
        phone: 'Telefoon',
        roles: 'Rollen',
        fullName: 'Volledige naam',
        name: 'Naam',
        description: 'Beschrijving',
        noDataFound: 'Geen gegevens gevonden',
        emptySearchHint: 'Probeer uw zoektermen aan te passen',
        noMatchSearch: 'Geen {{entity}} die overeenkomen met uw zoekopdracht',
        noFound: 'Geen {{entity}} gevonden',
        exporting: 'Exporteren…',
        showDetails: 'Details tonen',
        hideDetails: 'Details verbergen',
        system: 'Systeem',
        na: 'n.v.t.',
        retry: 'Opnieuw proberen',
        listLoadFailed: 'Deze lijst kon niet worden geladen. Vernieuw en probeer opnieuw.',
        optionsLoadFailed: 'Opties konden niet worden geladen. Vernieuw de pagina of probeer opnieuw.',
        unknownError: 'Onbekende fout',
        entries: 'items',
        apply: 'Toepassen',
        never: 'Nooit',
        code: 'Code',
        location: 'Locatie',
        place: 'Plaats',
        score: 'Score',
        dismiss: 'Sluiten',
        yes: 'Ja',
        no: 'Nee',
        done: 'Klaar.',
        close: 'Sluiten',
        gotIt: 'Begrepen',
        confirm: 'Bevestigen',
        popupInfo: 'Informatie',
        minutesCount: '{{count}} minuten',
        unknown: 'Onbekend',
        userFallback: 'Gebruiker #{{id}}',
        branchFallback: 'Vestiging #{{id}}',
        levelFallback: 'Niveau #{{id}}',
        popupSuccess: 'Geslaagd',
        popupWarning: 'Controleer dit',
        popupError: 'Aandacht vereist',
        pressEscCancel: 'Druk op Esc om te annuleren',
        pressEscClose: 'Druk op Esc om te sluiten',
        backendDown: 'De backendserver is offline — neem contact op met een beheerder of probeer het later opnieuw.',
        ariaClose: 'Sluiten',
        info: 'Info',
        learnMore: 'Meer informatie',
        remove: 'Verwijderen',
        assigned: 'Toegewezen'
      },
      parent: {
        childSwitcher: {
          loadingChildren: 'Kinderen laden…',
          viewingChild: 'Kind bekijken',
          switchChild: 'Van kind wisselen',
          selectChild: 'Selecteer een kind…'
        },
        chooseChildTitle: 'Kies een kind',
        chooseChildBody: 'Selecteer welke gekoppelde leerling u wilt bekijken, of ga terug naar de ouderhome.',
        backToParentHome: 'Terug naar ouderhome',
        parentHome: 'Ouderhome',
        viewingPrefix: 'Bekijken',
        readOnly: 'Alleen-lezen',
        roleLabel: 'Ouder'
      },
      header: {
        accountMenu: 'Accountmenu',
        openNav: 'Navigatiemenu openen',
        closeNav: 'Navigatiemenu sluiten',
        closeNavSidebar: 'Navigatie sluiten',
        myProfile: 'Mijn profiel',
        switchRole: 'Rol wisselen',
        logout: 'Uitloggen'
      },
      selectSearch: {
        select: 'Selecteren…',
        search: 'Zoeken…',
        loading: 'Laden…',
        noOptions: 'Geen opties gevonden',
        loadFailed: 'Opties laden mislukt — probeer opnieuw',
      },
      datePicker: {
        clearDate: 'Datum wissen',
        selectTime: 'Tijd selecteren',
        selectDateTime: 'Datum en tijd selecteren',
        selectTimeIntervals: 'Tijd selecteren (intervallen van 15 min)',
        selectTimeWithFormat: 'Tijd selecteren ({{format}}, intervallen van 15 min)',
        dateWithFormat: 'Datum ({{format}})',
        timeWithFormat: 'Tijd ({{format}})',
        noTimeOptions: 'Geen tijdopties beschikbaar',
        duration: 'Duur: {{hours}}u {{minutes}}m',
        exceeds3Hours: 'Langer dan 3 uur',
        hour12: '12-uurs',
        hour24: '24-uurs',
        clear: 'Wissen',
        done: 'Klaar'
      },
      notificationBell: {
        ariaLabel: 'Meldingen',
        ariaLabelUnread: 'Meldingen ({{count}} ongelezen)',
        title: 'Meldingen',
        unread: '{{count}} ongelezen',
        markAllRead: 'Alles als gelezen markeren',
        empty: 'Alles bijgewerkt!',
        viewAll: 'Alle meldingen bekijken',
        unreadHint: '{{count}} vragen om uw aandacht',
        caughtUpHint: 'Geen ongelezen meldingen',
        emptyHint: 'Wallet-, aanwezigheids- en privacyalerts verschijnen hier wanneer actie nodig is.'
      },
      publicLayout: {
        developedBy: 'Ontwikkeld en gelicentieerd door',
        licenseSupport: 'Voor licentieaankoop of ondersteuning',
        contactSupport: 'Contact opnemen met support'
      },
      search: {
        placeholder: 'Zoek leerlingen, coaches, groepen…',
        aria: 'Globaal zoeken',
        searching: 'Zoeken…',
        noResults: 'Geen resultaten voor “{{q}}”',
        students: 'Leerlingen',
        coaches: 'Coaches',
        batches: 'Groepen',
        apiUnavailable: 'Zoek-API niet beschikbaar. Herstart de backendcontainer.',
        failed: 'Zoeken mislukt. Probeer het opnieuw.'
      },
      days: {
        short: {
          '1': 'ma',
          '2': 'di',
          '3': 'wo',
          '4': 'do',
          '5': 'vr',
          '6': 'za',
          '7': 'zo'
        }
      },
      charges: {
        modes: {
          PER_SESSION: 'Per sessie (vast)',
          PER_SESSION_HOURLY: 'Per sessie (uurlijks)',
          PER_PERIOD: 'Periodeplan',
          RECURRING_SESSIONS: 'Per sessie (uurlijks)',
          MONTHLY: 'Periodeplan',
          YEARLY: 'Periodeplan'
        },
        intervals: {
          WEEKLY: 'Wekelijks (7 dagen)',
          MONTHLY: 'Maandelijks (30 dagen)',
          QUARTERLY: 'Per kwartaal (90 dagen)',
          SEMI_ANNUAL: 'Halfjaarlijks (180 dagen)',
          YEARLY: 'Jaarlijks (365 dagen)'
        },
        status: {
          PAID: 'Betaald',
          UNPAID: 'Onbetaald',
          ESCALATED: 'Geëscaleerd'
        }
      },
      accountAccess: {
        signInLink: {
          button: 'E-mail aanmeldlink',
          buttonTitle: 'Stuur een e-mail met een link om hun wachtwoord in te stellen of te resetten',
          buttonTitleNoEmail: 'Voeg een e-mailadres toe aan het profiel om een aanmeldlink te sturen',
          noEmailAlert: 'Deze {{role}} heeft geen e-mailadres. Voeg een e-mailadres toe voordat u een aanmeldlink stuurt.',
          listConfirm: 'Een aanmeldlink e-mailen naar deze {{role}}? Zij kunnen die gebruiken om hun wachtwoord in te stellen of te resetten.',
          editConfirm: 'Een nieuwe aanmeldlink e-mailen naar deze {{role}}?',
          success: 'Aanmeldlink succesvol gemaild.',
          failure: 'Kon aanmeldlink niet e-mailen.',
          createCheckbox: 'E-mail aanmeldlink (zij stellen hun eigen wachtwoord in)',
          createNote: 'Er wordt een eenmalige aanmeldlink gemaild zodat zij hun wachtwoord kunnen instellen.',
          bulkImportOption: 'E-mail aanmeldlink — leerlingen stellen hun eigen wachtwoord in'
        },
        passwordReset: {
          pageSubtitle: 'Wij e-mailen een wachtwoordresetlink als er een account bestaat',
          successMessage: 'Als er een account bestaat voor dat e-mailadres of die gebruikersnaam, is er een wachtwoordresetlink verzonden. Controleer uw inbox.',
          submitButton: 'E-mail wachtwoordresetlink',
          publicHelp: 'U ontvangt een wachtwoordresetlink op uw geregistreerde e-mailadres.'
        }
      },
      userActions: {
        edit: 'Bewerken',
        archive: 'Archiveren',
        activate: 'Activeren',
        removeFromList: 'Van lijst verwijderen',
        removePermanently: 'Definitief verwijderen',
        editTitle: '{{label}} bewerken',
        archiveTitle: '{{label}} archiveren',
        reactivateTitle: '{{label}} opnieuw activeren',
        removeFromListTitle: 'Van uw lijst verwijderen zonder de leerling te archiveren',
        removePermanentlyTitle: 'Van lijsten verwijderen en een licentieplek vrijmaken; geschiedenis blijft als (Inactief)',
        archiveConfirm: 'Deze {{label}} archiveren? Zij kunnen niet meer aanmelden. Hun dossier blijft bewaard en telt nog mee voor uw licentie totdat u hen definitief verwijdert (wanneer de capaciteit vol is).',
        permanentRemoveConfirm: 'Deze gearchiveerde {{label}} definitief van uw lijsten verwijderen? Hun aanwezigheid, betalingen en overige geschiedenis blijven in het systeem en worden getoond als "(Inactief)". Dit maakt één licentieplek vrij. Dit kan niet ongedaan worden gemaakt.',
        reactivateConfirm: 'Deze {{label}} opnieuw activeren? Zij kunnen zich weer aanmelden.',
        loadArchiveFailed: 'Archiveringsopties voor {{label}} laden mislukt.',
        archiveFailed: '{{label}} archiveren mislukt.',
        removeFailed: '{{label}} verwijderen mislukt.',
        activateFailed: '{{label}} activeren mislukt.',
        labels: {
          student: 'leerling',
          coach: 'coach',
          manager: 'manager',
          user: 'gebruiker'
        }
      },
      carryForward: {
        columnHelp: 'Doorschuiven bepaalt de facturatie wanneer een leerling afwezig is. Indien ingeschakeld, wordt het sessietarief niet afgeschreven en kan het tegoed later worden gebruikt. Indien uitgeschakeld, wordt het tarief nog steeds afgeschreven, ook als de leerling als afwezig is gemarkeerd.',
        modes: {
          ALLOWED: {
            label: 'Toegestaan voor alle leerlingen',
            desc: 'Afwezige leerlingen worden niet in rekening gebracht — sessietegoeden worden doorgeschoven voor later gebruik.'
          },
          NOT_ALLOWED: {
            label: 'Niet toegestaan voor enige leerling',
            desc: 'Afwezige leerlingen worden toch in rekening gebracht — sessietarieven worden van hun portemonnee afgeschreven.'
          },
          ENROLLMENT_LEVEL: {
            label: 'Beslissen op inschrijvingsniveau',
            desc: 'Kies doorschuiven individueel bij het inschrijven van elke leerling.'
          }
        },
        batchPolicyTitle: {
          ALLOWED: 'Groepsbeleid: doorschuiven ingeschakeld voor alle leerlingen. Als een leerling als afwezig is gemarkeerd, wordt hun sessietarief niet afgeschreven — het tegoed wordt doorgeschoven.',
          NOT_ALLOWED: 'Groepsbeleid: doorschuiven uitgeschakeld voor alle leerlingen. Als een leerling als afwezig is gemarkeerd, wordt hun sessietarief toch van hun portemonnee afgeschreven.',
          ENROLLMENT_LEVEL: 'Groepsbeleid: doorschuiven wordt per leerling bij inschrijving ingesteld. Elke leerling kan het ingeschakeld hebben (geen kosten bij afwezigheid) of uitgeschakeld (kosten blijven gelden).',
          default: 'Deze leerling volgt het doorschuifbeleid van de groep.'
        },
        batchPolicySummary: {
          ALLOWED: 'Als een leerling afwezig is, worden er geen kosten in rekening gebracht — het sessietegoed wordt doorgeschoven.',
          NOT_ALLOWED: 'Als een leerling afwezig is, worden er toch kosten in rekening gebracht — het sessietarief wordt van hun portemonnee afgeschreven.',
          ENROLLMENT_LEVEL: 'U kunt doorschuiven individueel kiezen voor elke leerling bij inschrijving.'
        },
        batchPolicyLabel: {
          ALLOWED: 'Doorschuiven ingeschakeld voor allen',
          NOT_ALLOWED: 'Doorschuiven uitgeschakeld voor allen',
          ENROLLMENT_LEVEL: 'Inschrijvingsniveau (leerling-specifiek)',
          unknown: 'Onbekend'
        },
        studentOverrideTitle: {
          enabled: 'Doorschuiven ingeschakeld voor deze leerling. Bij afwezigheid wordt het sessietarief niet afgeschreven — het tegoed wordt doorgeschoven.',
          disabled: 'Doorschuiven uitgeschakeld voor deze leerling. Bij afwezigheid wordt het sessietarief toch van hun portemonnee afgeschreven.'
        },
        studentOverrideShort: {
          enabled: 'Niet in rekening gebracht bij afwezigheid — tegoed wordt doorgeschoven',
          disabled: 'Toch in rekening gebracht bij afwezigheid — geen doorschuiven'
        },
        attendanceCell: {
          followsBatch: 'Volgt groep ({{mode}})',
          enabled: 'Ingeschakeld',
          disabled: 'Uitgeschakeld',
          unknown: 'Onbekend'
        }
      },
      branchModes: {
        OFFLINE: {
          label: 'Op locatie',
          description: 'Sessies op een fysieke locatie'
        },
        ONLINE: {
          label: 'Online',
          description: 'Alleen externe / virtuele sessies'
        },
        HYBRID: {
          label: 'Online + op locatie',
          description: 'Biedt zowel sessies op locatie als online'
        }
      },
      sessionModes: {
        ANY: 'Alle',
        ONLINE: 'Online',
        OFFLINE: 'Offline'
      },
      popup: {
        labels: {
          alert: 'Begrepen',
          confirm: 'Bevestigen',
          cancel: 'Annuleren'
        },
        titles: {
          confirmDestructive: 'Destructieve actie bevestigen',
          pleaseConfirm: 'Bevestig alstublieft',
          confirmAction: 'Actie bevestigen',
          success: 'Geslaagd',
          attention: 'Er is aandacht nodig',
          pleaseReview: 'Controleer dit',
          information: 'Informatie'
        },
        confirmLabels: {
          delete: 'Verwijderen',
          archive: 'Archiveren',
          activate: 'Activeren',
          execute: 'Uitvoeren',
          send: 'Verzenden',
          trigger: 'Activeren',
          remove: 'Verwijderen',
          continue: 'Doorgaan'
        }
      },
      unsavedChanges: {
        title: 'Niet-opgeslagen wijzigingen',
        message: 'U heeft wijzigingen die nog niet zijn opgeslagen. Sla ze op voordat u weggaat, verwerp ze, of blijf op deze pagina.',
        save: 'Opslaan en weggaan',
        discard: 'Wijzigingen verwerpen',
        stay: 'Blijven'
      },
      errors: {
        api: {
          fallback: 'Er is iets misgegaan.',
          upgradeRequired: 'Upgrade vereist',
          onlinePaymentsUpgrade: 'Online betaalgateways vereisen een Professional- of Enterprise-licentie. Neem contact op met uw academie voor een upgrade.',
          planLimitReached: 'Planlimiet bereikt',
          planLimitMessage: 'Uw huidige plan heeft de capaciteit bereikt. Upgrade uw plan of maak capaciteit vrij en probeer het opnieuw.',
          requestFailed: 'Verzoek mislukt'
        }
      },
      wallet: {
        txnReasons: {
          ADVANCE_PAYMENT: 'Vooruitbetaling',
          ONLINE_TOPUP: 'Online opwaardering',
          SESSION_CHARGE: 'Sessiekosten',
          SESSION_REVERSAL: 'Sessie-terugboeking',
          DISCOUNT: 'Kortingstegoed',
          REFUND: 'Terugbetaling',
          LEGACY_DEBIT: 'Afschrijving',
          default: 'Transactie'
        },
        dateLines: {
          session: 'Sessie',
          created: 'Aangemaakt',
          paidOn: 'Betaald op'
        }
      },
      regional: {
        presets: {
          india: 'India',
          india12: 'India (12-uurs)',
          uae: 'VAE',
          us: 'Verenigde Staten',
          uk: 'Verenigd Koninkrijk',
          eurozone: 'Eurozone (algemeen)',
          germany: 'Duitsland',
          france: 'Frankrijk',
          netherlands: 'Nederland',
          belgium: 'België',
          spain: 'Spanje'
        },
        paymentReference: {
          IN: 'bijv. Ontvangstbewijs #1234, UPI-ref: 123456789012',
          US: 'bijv. Ontvangstbewijs #1234, cheque #5678, of kaartautorisatiecode',
          CA: 'e.g. Receipt #1234, PAD reference, or card auth code',
          GB: 'bijv. Ontvangstbewijs #1234, bankreferentie of cheque-nummer',
          AE: 'bijv. Ontvangstbewijs #1234 of bankoverschrijvingsreferentie',
          EU: 'bijv. Ontvangstbewijs #1234 of SEPA- / bankoverschrijvingsreferentie',
          default: 'bijv. Ontvangstbewijs #1234'
        },
        bankRouting: {
          IN: 'IFSC-code',
          US: 'Routingnummer',
          CA: 'Transit / Institution Number',
          GB: 'Sort code',
          AE: 'SWIFT / IBAN',
          EU: 'IBAN',
          default: 'Bankcode'
        }
      },
      batches: {
        title: 'Groepen',
        subtitle: 'Beheer en organiseer de groepen van uw schaakacademie',
        add: 'Groep toevoegen',
        searchPlaceholder: 'Zoek groepen op naam, ID of niveau...',
        entityLabel: 'groepen',
        archiveConfirm: 'Deze groep archiveren?',
        archiveFailed: 'Groep archiveren mislukt.',
        empty: 'Geen groepen gevonden',
        emptyHint: 'Maak uw eerste groep aan om te beginnen',
        managerSubtitle: 'Beheer en organiseer trainingsgroepen',
        active: 'Actief',
        archived: 'Gearchiveerd',
        batchNumber: 'Groep #{{id}}',
        unknown: 'Onbekende groep',
        individualSessions: 'Individuele sessies',
        individualSessionsNamed: 'Individuele sessies - {{level}}',
        walkInNamed: 'Walk-in - Niveau #{{level}}',
        carryForward: 'Doorschuiven',
        perStudent: 'Per leerling',
        carryForwardBadge: {
          allowed: 'Doorschuiven toegestaan',
          notAllowed: 'Geen doorschuiven',
          tooltipAllowed: 'Ongebruikte tegoeden worden doorgeschoven naar de volgende factureringscyclus.',
          tooltipNotAllowed: 'Ongebruikte tegoeden vervallen aan het einde van de factureringscyclus.'
        },
        editTitle: 'Groep bewerken',
        sessionsTitle: 'Sessies bekijken',
        archiveTitle: 'Groep archiveren',
        level: 'Niveau',
        branch: 'Vestiging',
        schedule: 'Rooster',
        sessionDays: 'Sessiedagen',
        sessionTime: 'Sessietijd',
        charges: 'Tarieven',
        lockNotes: {
          branch: 'De vestiging ligt vast nadat de groep is aangemaakt (via niveau).',
          level: 'Het vaardigheidsniveau ligt vast nadat de groep is aangemaakt en kan niet worden gewijzigd.',
          carryForward: 'Het doorschuifbeleid ligt vast nadat de groep is aangemaakt.',
          chargesMode: 'De factureringsmodus kan niet worden gewijzigd zodra er inschrijvingen of sessies bestaan.',
          monthlyYearly: 'Periodebedrag en interval liggen vast nadat de groep is aangemaakt.',
          systemManaged: 'Deze groep wordt automatisch door het systeem beheerd en kan niet worden bewerkt.',
          sessionCharge: 'Geldt alleen voor toekomstige sessiefacturatie; eerdere kosten blijven ongewijzigd.'
        }
      },
      dashboard: {
        welcome: 'Welkom, {{name}}',
        quickActions: 'Snelle acties',
        defaultAdmin: 'Beheerder',
        defaultManager: 'Manager',
        defaultCoach: 'Coach',
        defaultStudent: 'Leerling'
      },
      table: {
        branch: 'Vestiging',
        level: 'Niveau',
        batch: 'Groep',
        coach: 'Coach',
        time: 'Tijd',
        when: 'Wanneer',
        start: 'Start',
        end: 'Einde',
        mode: 'Modus',
        studentName: 'Naam leerling',
        coachName: 'Naam coach',
        branchName: 'Naam vestiging',
        enrolledDate: 'Inschrijvingsdatum',
        payment: 'Betaling',
        balanceAfter: 'Saldo na',
        planType: 'Plantype',
        groupType: 'Groepstype',
        defaultRate: 'Standaardtarief',
        defaultCredits: 'Standaardtegoeden',
        levelRange: 'Niveaubereik',
        creditExpiry: 'Vervaldatum tegoed',
        attendance: 'Aanwezigheid',
        topics: 'Onderwerpen',
        balanceBefore: 'Saldo vóór',
        sessionFee: 'Sessietarief',
        dateTime: 'Datum & tijd',
        date: 'Datum',
        description: 'Beschrijving',
        carryForward: 'Doorschuiven',
        amount: 'Bedrag'
      },
      pages: {
        adminDashboard: {
          emailAlertTitle: 'Uitgaande e-mail is niet geconfigureerd.',
          emailAlertBody: 'Leerlingen en medewerkers ontvangen geen aanmeldlinks, herinneringen of afschriften totdat SMTP is ingesteld.',
          configureEmail: 'E-mail configureren',
          whatsappAlertTitle: 'WhatsApp (WATI) is niet geconfigureerd.',
          whatsappAlertBody: 'Koppel uw WATI-account om sessieherinneringen en portemonnee-meldingen via WhatsApp te versturen.',
          configureWhatsApp: 'WhatsApp configureren',
          subtitle: 'Beheer uw academie met volledige controle over gebruikers, vestigingen en systeeminstellingen.',
          totalStudents: 'Totaal leerlingen',
          activeBranches: 'Actieve vestigingen',
          activeCoaches: 'Actieve coaches',
          atRiskStudents: 'Leerlingen met risico',
          activeEnrollments: 'Actieve inschrijvingen',
          addStudent: 'Leerling toevoegen',
          addStudentDesc: 'Schrijf een nieuwe leerling in bij de academie',
          createBatch: 'Groep aanmaken',
          createBatchDesc: 'Stel een nieuwe trainingsgroep in',
          walletTopup: 'Portemonnee opwaarderen',
          walletTopupDesc: 'Saldo toevoegen aan de portemonnee van een leerling',
          manageCoaches: 'Coaches beheren',
          manageCoachesDesc: 'Coachingpersoneel bekijken en beheren',
          viewSessions: 'Sessies bekijken',
          viewSessionsDesc: 'Alle trainingssessies beheren',
          paymentReports: 'Betalingsrapporten',
          paymentReportsDesc: 'Financiële ontvangsten bekijken'
        },
        managerDashboard: {
          subtitle: 'Beheer de werkzaamheden efficiënt. Bekijk coaches, leerlingen en volg de prestaties van vestigingen.',
          coaches: 'Coaches',
          students: 'Leerlingen',
          manageBatches: 'Groepen beheren',
          manageBatchesDesc: 'Trainingsgroepen bekijken en organiseren',
          enrollments: 'Inschrijvingen',
          enrollmentsDesc: 'Leerlinginschrijvingen beheren',
          manageLevels: 'Niveaus beheren',
          manageLevelsDesc: 'Niveaus configureren',
          sessionStatement: 'Sessieafschrift',
          sessionStatementDesc: 'Gedetailleerde sessierapporten bekijken',
          batchPayments: 'Groepsbijwoning',
          batchPaymentsDesc: 'Groepsbijwoningsmatrix bekijken'
        },
        coachDashboard: {
          subtitle: 'Uw dag in één oogopslag. Bekijk snel sessies, markeer aanwezigheid en plan de komende week.',
          metricsFailed: 'Statistieken laden mislukt.',
          todayFailed: 'Sessies van vandaag laden mislukt.',
          upcomingFailed: 'Komende sessies laden mislukt.',
          sessionsToday: 'Sessies vandaag',
          studentsToday: 'Leerlingen vandaag',
          hoursThisWeek: 'Uren deze week',
          attendanceWtd: 'Aanwezigheid (WTD)',
          noShowsToday: 'No-shows vandaag',
          todaysSchedule: 'Rooster van vandaag',
          viewAllSessions: 'Alle sessies bekijken',
          noSessionsToday: 'Geen sessies gepland voor vandaag.',
          upcoming7Days: 'Komend (komende 7 dagen)',
          noUpcomingSessions: 'Geen komende sessies in de komende 7 dagen.',
          viewAttendance: 'Aanwezigheid bekijken',
          markAttendance: 'Aanwezigheid markeren'
        },
        studentDashboard: {
          childOverview: 'Overzicht van {{name}}',
          childFallback: 'Kind',
          childSubtitle: 'Aanwezigheidssamenvatting, komende sessies en portemonneesaldo voor uw gekoppelde leerling.',
          subtitle: 'Volg uw voortgang, bekijk komende sessies en beheer uw portemonneesaldo.',
          loadFailed: 'Dashboard laden mislukt.',
          activeEnrollments: 'Actieve inschrijvingen',
          attendanceRate: 'Aanwezigheidspercentage',
          upcoming7d: 'Komend (7 dagen)',
          lastAttended: 'Laatst aanwezig',
          upcomingSessions: 'Komende sessies',
          noUpcomingSessions: 'Geen komende sessies.',
          recentAttendance: 'Recente aanwezigheid',
          noAttendanceRecords: 'Geen aanwezigheidsregistraties.'
        },
        parentHome: {
          loading: 'Ouderportaal laden…',
          portalLabel: 'Ouderportaal',
          hello: 'Hallo, {{name}}',
          there: 'daar',
          approvedSubtitle: 'Blijf op de hoogte van de aanwezigheid, het rooster en de academiekosten van uw kind — alles op één plek.',
          pendingSubtitle: 'Uw voogdaccount wordt ingesteld.',
          accessPending: 'Toegang in behandeling',
          pendingDefault: 'Uw voogdkoppeling wacht op goedkeuring van de academie. U kunt zich opnieuw aanmelden zodra een beheerder uw verzoek heeft goedgekeurd.',
          noLinkedStudents: 'Nog geen gekoppelde leerlingen. Vraag de academie om u een voogduitnodiging te sturen.',
          yourChildren: 'Uw kinderen',
          currentlyViewing: 'Momenteel bekeken',
          selectChildPrompt: 'Selecteer hierboven een kind om hun samenvatting en snelle acties te zien.',
          quickActions: 'Snelle acties',
          more: 'Meer',
          otherLinks: 'Overige links',
          helpFaq: 'Hulp & FAQ',
          loadingChild: 'Kindgegevens laden…',
          loadFailed: 'Ouderportaal kon niet worden geladen.',
          pendingAccess: 'Uw voogdtoegang wacht op goedkeuring of is nog niet gekoppeld.',
          overview: 'Overzicht',
          overviewDesc: 'Aanwezigheid & aankomende sessies',
          calendar: 'Kalender',
          calendarDesc: 'Maandelijkse planning',
          wallet: 'Portemonnee',
          walletDesc: 'Saldo & betalingen',
          attendance: 'Aanwezigheid',
          attendanceDesc: 'Sessiegeschiedenis',
          enrollments: 'Inschrijvingen',
          sessions: 'Sessies',
          notifications: 'Meldingen',
          sessionStatement: 'Sessieoverzicht',
          statusPendingApproval: 'Wacht op goedkeuring',
          statusApproved: 'Actief',
          statusRejected: 'Afgewezen',
          statusRevoked: 'Ingetrokken',
          selected: 'Geselecteerd',
          guardian: 'Voogd'
        },
        managerStudents: {
          title: 'Studenten',
          subtitle: 'Zoek, bekijk en beheer studententoegang met een overzichtelijkere lijst.',
          addStudent: 'Student toevoegen',
          searchPlaceholder: 'Zoek studenten op naam, gebruikersnaam, e-mail, telefoon of ID...',
          entityLabel: 'studenten',
          noMatch: 'Geen studenten komen overeen met uw zoekopdracht',
          empty: 'Geen studenten gevonden',
          removeConfirm: '{{name}} uit uw lijst verwijderen?\n\nDit verwijdert alleen uw directe managerkoppeling. De student wordt niet gearchiveerd en kan nog zichtbaar zijn als deze is ingeschreven bij uw vestigingen.',
          removeFailed: 'Student kon niet uit de lijst worden verwijderd.',
          thisStudent: 'deze student',
          entitySingular: 'leerling'
        },
        managerCoaches: {
          title: 'Coaches',
          subtitle: 'Bekijk coaches binnen het bereik van uw vestiging',
          searchPlaceholder: 'Zoek coaches op naam, gebruikersnaam, e-mail, telefoon of ID...',
          entityLabel: 'trainers',
          noMatch: 'Geen coaches komen overeen met uw zoekcriteria',
          empty: 'Geen coaches gevonden in het systeem'
        },
        managerBranches: {
          title: 'Mijn vestigingen',
          subtitle: 'Vestigingen die u beheert',
          searchPlaceholder: 'Zoek vestigingen op naam, code, telefoon, modus of ID...',
          entityLabel: 'vestigingen',
          unauthorized: 'U bent niet gemachtigd om vestigingen te bekijken.',
          loadFailed: 'Vestigingen laden mislukt.',
          noMatch: 'Geen vestigingen komen overeen met uw zoekopdracht',
          empty: 'Er zijn nog geen vestigingen aan u toegewezen',
          emptyHint: 'Neem contact op met een beheerder om vestigingen aan uw account toe te wijzen'
        },
        levels: {
          title: 'Niveaus',
          subtitle: 'Schaakniveaus beheren',
          managerSubtitle: 'Schaakniveaus beheren',
          coachSubtitle: 'Schaakniveaus bekijken',
          addLevel: 'Niveau toevoegen',
          searchPlaceholder: 'Zoek niveaus op naam, vestiging, beschrijving of ID...',
          entityLabel: 'niveaus',
          loadFailed: 'Niveaus laden mislukt.',
          noMatch: 'Geen niveaus komen overeen met uw zoekopdracht',
          empty: 'Geen niveaus gevonden',
          archiveConfirm: 'Dit niveau archiveren?',
          archiveFailed: 'Niveau archiveren mislukt.',
          colLevelName: 'Niveaunaam',
          editLevel: 'Niveau bewerken',
          archiveLevel: 'Niveau archiveren',
          levelFallback: 'Niveau {{id}}',
          form: {
            levelName: 'Niveaunaam',
            branchForLevel: 'Vestiging voor dit niveau',
            branchFixedNote: 'De vestiging staat vast nadat het niveau is aangemaakt en kan niet worden gewijzigd.',
            loadBranchesFailed: 'Vestigingen laden mislukt',
            loadingBranches: 'Vestigingen laden…'
          }
        },
        feePlans: {
          title: 'Tariefplannen',
          subtitle: 'Vestigingsspecifieke tariefplannen en facturatieconfiguratie',
          loadFailed: 'Plannen laden mislukt.',
          empty: 'Geen tariefplannen gevonden.',
          allBranches: 'Alle vestigingen',
          addPlan: 'Plan toevoegen',
          selectBranch: 'Selecteer vestiging…',
          selectBranchHint: 'Selecteer een vestiging om plannen te bekijken.',
          noPlans: 'Geen plannen gevonden voor deze vestiging.',
          archiveConfirm: 'Dit plan archiveren? Het wordt verborgen bij selectie, maar bewaard voor de geschiedenis.',
          archiveFailed: 'Plan archiveren mislukt.',
          activateFailed: 'Plan activeren mislukt.',
          colPlan: 'Plannr.',
          colBranch: 'Vestiging',
          colType: 'Type',
          colGroup: 'Groep',
          colRate: 'Tarief',
          colCredits: 'Tegoeden',
          colLevelScope: 'Niveaubereik',
          archiveTitle: 'Plan archiveren',
          activateTitle: 'Plan activeren'
        },
        studentBulkImport: {
          title: 'Bulkimport studenten',
          subtitle: 'Upload een CSV-bestand om meerdere studenten tegelijk te importeren. Download de sjabloon, vul de studentgegevens in en upload om te beginnen.',
          downloadTemplate: 'Sjabloon downloaden',
          totalRows: 'Totaal rijen',
          imported: 'Geïmporteerd',
          skipped: 'Overgeslagen',
          failed: 'Mislukt',
          allSuccess: 'Alle {{count}} studenten zijn succesvol geïmporteerd!',
          completedWithIssues: 'Import voltooid met enkele problemen.',
          summaryIssues: '{{success}} geïmporteerd · {{skipped}} overgeslagen (duplicaten) · {{failed}} mislukt. Bekijk de tabel hieronder voor details.',
          detailedReport: 'Gedetailleerd importrapport',
          importAnother: 'Nog een bestand importeren',
          viewAllStudents: 'Alle studenten bekijken',
          colNum: '#',
          colName: 'Studentnaam',
          colUsername: 'Gebruikersnaam',
          colEmail: 'E-mail',
          colStatus: 'Status',
          colReason: 'Reden / opmerking',
          statusImported: '✓ Geïmporteerd',
          statusSkipped: '⊘ Overgeslagen',
          statusFailed: '✗ Mislukt',
          step1: 'Importinstellingen',
          passwordStrategy: 'Wachtwoordstrategie',
          defaultPasswordOption: 'Standaardwachtwoord instellen — studenten kunnen direct inloggen',
          defaultPassword: 'Standaardwachtwoord',
          passwordPlaceholder: 'Minimaal 6 tekens',
          passwordHint: 'Toegepast op alle rijen. Als een rij een eigen wachtwoordkolom heeft, heeft die voorrang.',
          step2: 'CSV-bestand uploaden',
          rowsFound: '{{count}} studentrij gevonden',
          rowsFound_other: '{{count}} studentrijen gevonden',
          replaceFile: 'Klik of sleep een nieuw bestand om te vervangen',
          dropHint: 'Sleep uw CSV-bestand hierheen, of klik om te bladeren',
          csvOnly: 'Alleen .csv-bestanden worden geaccepteerd',
          noTemplate: 'Heeft u geen sjabloon?',
          downloadHere: 'Download hier',
          step3: 'Voorbeeld & importeren',
          showingFirst: 'Eerste 5 van {{total}} rijen weergegeven',
          startImport: 'Import starten',
          importing: 'Importeren…',
          importFailed: 'Import mislukt. Probeer het opnieuw.',
          selectFileFirst: 'Upload eerst een CSV-bestand.',
          enterpriseSubtitle: 'Bulk-CSV-import is alleen beschikbaar met de Enterprise-licentie.',
          enterpriseFeature: 'Enterprise-functie',
          enterpriseBody: 'Bulkimport van studenten via CSV is een exclusieve Enterprise-functie. Uw huidige licentie omvat deze mogelijkheid niet.',
          enterpriseHint: 'Om studenten met uw huidige abonnement te importeren, gebruikt u het formulier <1>{{addStudentLink}}</1> om ze één voor één in te schrijven. Neem contact op met uw softwareleverancier om te upgraden naar een Enterprise-licentie.',
          addStudentLink: 'Student toevoegen',
          csvOnlyAlert: 'Selecteer een CSV-bestand (.csv).\n\nVoor Excel-bestanden: sla eerst op als "CSV (gescheiden door lijstscheidingsteken)".',
          noDataRowsAlert: 'Geen gegevensrijen gevonden in het bestand. Controleer het bestandsformaat.'
        },
        studentImport: {
          title: 'Studentimport vanuit Google Sheets',
          loadingSubtitle: 'Importconfiguratie laden…',
          subtitle: 'Importeert automatisch nieuwe studentinschrijvingen vanuit Google Sheets dagelijks om 02:00 uur (academie lokale tijd)',
          loadFailed: 'Importstatus laden mislukt',
          importStatus: 'Importstatus',
          spreadsheetId: 'Spreadsheet-ID',
          sheetName: 'Bladnaam',
          lastProcessedRow: 'Laatst verwerkte rij',
          totalImported: 'Totaal geïmporteerd',
          lastImportTime: 'Laatste importtijd',
          lastUpdated: 'Laatst bijgewerkt',
          lastError: 'Laatste fout',
          actions: 'Acties',
          triggerImport: 'Import nu starten',
          processing: 'Verwerken...',
          resetTracker: 'Tracker resetten',
          refreshStatus: 'Status vernieuwen',
          triggerDesc: 'Start het importproces handmatig om direct te controleren op nieuwe studenten. Alleen rijen na de laatst verwerkte rij worden geïmporteerd.',
          resetDesc: 'Gevaar: Dit reset de tracker naar rij 0. Gebruik dit alleen als u alle studenten opnieuw moet importeren of als er een probleem is.',
          refreshDesc: 'Laad de huidige importstatus opnieuw om de nieuwste informatie te zien.',
          confirmTrigger: 'Weet u zeker dat u de studentimport nu wilt starten?',
          confirmReset: '⚠️ WAARSCHUWING: Dit reset de tracker en importeert ALLE studenten opnieuw vanaf het begin. Weet u het zeker?',
          triggerSuccess: 'Import succesvol gestart! Vernieuw om de resultaten te zien.',
          resetSuccess: 'Tracker succesvol gereset!',
          triggerFailed: 'Import starten mislukt',
          resetFailed: 'Tracker resetten mislukt',
          configTitle: 'Configuratie',
          setupTitle: 'Installatiehandleiding',
          configDaily: 'Automatische import draait dagelijks om <strong>02:00 uur (academie lokale tijd)</strong>',
          configNewRows: 'Alleen nieuwe rijen (na de laatst verwerkte rij) worden geïmporteerd',
          configRoleEmail: 'Elke student wordt aangemaakt met de rol STUDENT en er wordt een uitnodigingsmail verzonden',
          configUsername: 'Gebruikersnaam wordt automatisch gegenereerd op basis van e-mail of naam',
          configMappings: 'Configureer kolomtoewijzingen in <code>application.yml</code>',
          configEnv: 'In-/uitschakelen via omgevingsvariabele <code>STUDENT_IMPORT_ENABLED</code>',
          setupStep1: 'Maak een Google Cloud-project aan en schakel de Google Sheets API in',
          setupStep2: 'Maak een serviceaccount aan en download de credentials-JSON',
          setupStep3: 'Deel uw Google Spreadsheet met het e-mailadres van het serviceaccount',
          setupStep4: 'Plaats het credentials-bestand op <code>/app/config/google-credentials.json</code>',
          setupStep5: 'Stel <code>STUDENT_IMPORT_ENABLED=true</code> in de omgeving in',
          setupStep6: 'Pas kolomtoewijzingen in application.yml aan als de structuur van uw spreadsheet afwijkt',
          bulkImportButton: 'Bulkimport',
          bulkImportTitle: 'Nieuwe studenten importeren vanuit Google Sheets',
          bulkImporting: 'Importeren…',
          bulkImportConfirm: 'Studenten importeren vanuit Google Sheets?\n\nNieuwe registraties worden opgehaald uit de gekoppelde Google Forms.\nAlleen rijen die sinds de laatste import zijn toegevoegd, worden verwerkt.',
          bulkImportTriggered: 'Import gestart. Vernieuw de lijst zo meteen om nieuwe studenten te zien.',
          bulkImportFailed: 'Studenten importeren mislukt. Zorg dat studentimport op de server is ingeschakeld.'
        },
        feePlanForm: {
          title: 'Tariefplan toevoegen',
          subtitle: 'Maak een nieuw plan voor een specifieke vestiging.',
          branchLabel: 'Vestiging voor tariefplan',
          codeLabel: 'Tariefplancode',
          typeLabel: 'Tariefplantype',
          groupTypeLabel: 'Studentengroeptype',
          defaultRate: 'Standaardtarief',
          defaultCredits: 'Standaard sessietegoeden',
          levelMin: 'Minimumniveau',
          levelMax: 'Maximumniveau',
          carryForward: 'Aanwezigheid vereist',
          carryForwardEnabled: 'Ingeschakeld: geen facturatie voor AFWEZIGE studenten.',
          carryForwardDisabled: 'Uitgeschakeld: facturatie geldt of de student AANWEZIG of AFWEZIG is.',
          creditExpiry: 'Vervaltijd tegoed (dagen)',
          notes: 'Notities tariefplan',
          active: 'Tariefplan actief',
          selectBranchAlert: 'Selecteer vestiging.',
          codeRequired: 'Code is verplicht.',
          createFailed: 'Plan aanmaken mislukt.',
          saving: 'Opslaan…',
          createPlan: 'Plan aanmaken'
        },
        enrollmentDetail: {
          title: 'Inschrijving #{{id}}',
          subtitle: 'Bekijk inschrijvingsdetails en facturatie.',
          loadingTitle: 'Inschrijvingsdetails',
          notFound: 'Niet gevonden',
          loadFailed: 'Inschrijving laden mislukt',
          activeExpired: 'Actief (verlopen)',
          renew: 'Vernieuwen',
          activateConfirm: 'Weet u zeker dat u inschrijving #{{id}} wilt activeren?',
          archiveFailed: 'Archiveren mislukt.',
          basicInfo: 'Basisgegevens',
          enrollmentPeriod: 'Inschrijvingsperiode',
          enrolledDate: 'Inschrijvingsdatum',
          openEnded: 'Open einde',
          pricingBilling: 'Prijzen & facturatie',
          customCharges: 'Aangepaste inschrijvingskosten weergegeven',
          batchCharges: 'Standaard batchkosten weergegeven',
          billingPeriod: 'Facturatieperiode',
          periodFee: 'Periodetarief',
          notConfigured: 'Niet geconfigureerd',
          hourlyRate: 'Uurtarief',
          perSessionCharge: 'Kosten per sessie',
          afterDiscount: 'Na {{pct}}% korting',
          enrollmentOverride: 'Inschrijvingsoverschrijving',
          carryForward: 'Doorschuiven',
          enabled: 'Ingeschakeld',
          disabled: 'Uitgeschakeld',
          followsBatch: 'Volgt batchbeleid',
          discountApplied: 'Korting toegepast',
          noDiscount: 'Geen korting toegepast',
          paymentReceiver: 'Betalingsontvanger',
          receiverType: 'Type ontvanger',
          receiverName: 'Naam ontvanger',
          additionalInfo: 'Aanvullende informatie',
          enrollmentType: 'Inschrijvingstype',
          enrolledBy: 'Ingeschreven door',
          createdAt: 'Aangemaakt op'
        },
        discountForm: {
          createTitle: 'Korting aanmaken',
          editTitle: 'Korting bewerken',
          createSubtitle: 'Voeg een nieuwe kortingscode toe voor inschrijvingen.',
          editSubtitle: 'Werk kortingsdetails en bereik bij.',
          loadFailed: 'Korting laden mislukt',
          codeLabel: 'Kortingscode',
          nameLabel: 'Kortingsnaam',
          descriptionLabel: 'Beschrijving',
          typeLabel: 'Kortingstype',
          valueLabel: 'Kortingswaarde',
          branchLabel: 'Vestiging (optioneel)',
          levelLabel: 'Niveau (optioneel)',
          batchLabel: 'Batch (optioneel)',
          activeLabel: 'Actief',
          allBranches: 'Alle vestigingen',
          selectBranch: 'Selecteer eerst een vestiging',
          selectLevel: 'Selecteer eerst een niveau',
          saveFailed: 'Korting opslaan mislukt',
          saving: 'Opslaan…',
          create: 'Korting aanmaken',
          update: 'Korting bijwerken',
          codeRequired: 'Kortingscode is verplicht',
          nameRequired: 'Naam is verplicht',
          valueRequired: 'Waarde is verplicht',
          optionalDescription: 'Optionele beschrijving',
          percentOption: 'Percentage (%)',
          fixedOption: 'Vast bedrag ({{currency}})',
          scopeTitle: 'Kortingsbereik (optioneel)',
          scopeHint: 'Laat alles leeg voor een globale korting, of beperk tot een specifieke vestiging, niveau of batch.',
          percentMaxAlert: 'Percentagekorting mag niet hoger zijn dan 100%',
          updateSuccess: 'Korting succesvol bijgewerkt',
          createSuccess: 'Korting succesvol aangemaakt'
        },
        userDetails: {
          title: 'Gebruikersgegevens',
          loadFailed: 'Gebruikersgegevens laden mislukt.',
          notFound: 'Gebruiker niet gevonden',
          editUser: 'Gebruiker bewerken',
          backToList: 'Terug naar lijst',
          personalInfo: 'Persoonlijke gegevens',
          contactInfo: 'Contactgegevens',
          accountInfo: 'Accountgegevens',
          address: 'Adres',
          noAddress: 'Geen adres geregistreerd',
          dateOfBirth: 'Geboortedatum',
          gender: 'Geslacht',
          memberSince: 'Lid sinds',
          lastLogin: 'Laatste login',
          walletBalance: 'Portemonneesaldo',
          activeEnrollments: 'Actieve inschrijvingen',
          totalSessions: 'Totaal sessies',
          attendanceRate: 'Aanwezigheidspercentage',
          guardians: 'Voogden',
          noGuardians: 'Geen voogden gekoppeld',
          roles: 'Rollen',
          username: 'Gebruikersnaam',
          userId: 'Gebruikers-ID',
          coachSummary: 'Coachoverzicht'
        },
        coachStudents: {
          title: 'Studenten',
          subtitle: 'Studenten op uw rooster — uit uw sessies en toewijzingen door beheerders',
          searchPlaceholder: 'Zoek studenten op naam, gebruikersnaam, e-mail, telefoon of ID...',
          entityLabel: 'studenten',
          loadFailed: 'Studenten laden mislukt.',
          empty: 'Geen studenten gevonden.',
          noMatch: 'Geen studenten komen overeen met uw zoekopdracht'
        },
        studentEnrollments: {
          title: 'Mijn inschrijvingen',
          subtitle: 'Bekijk uw actieve cursusinschrijvingen',
          loadFailed: 'Inschrijvingen laden mislukt.',
          searchPlaceholder: 'Zoek op niveau, batch of datum...',
          entityLabel: 'inschrijvingen',
          noMatch: 'Geen inschrijvingen komen overeen met uw zoekopdracht',
          empty: 'Nog geen inschrijvingen',
          payOnline: 'Online betalen'
        },
        studentSessions: {
          title: 'Mijn sessies',
          subtitle: 'Bekijk en zoek in uw geplande sessies en aanwezigheidsstatus.',
          loadFailed: 'Sessies laden mislukt.',
          searchPlaceholder: 'Zoeken… (niveau, batch, coach, status, aanwezigheid)',
          empty: 'Geen sessies gevonden.',
          noMatch: 'Geen sessies komen overeen met uw zoekopdracht.'
        },
        studentAttendance: {
          title: 'Mijn aanwezigheid',
          parentTitle: 'Aanwezigheid',
          subtitle: 'Volg de aanwezigheidsgeschiedenis van sessies over alle batches',
          loadFailed: 'Aanwezigheid laden mislukt.',
          searchPlaceholder: 'Zoek op status, niveau, batch, coach of tijd...',
          entityLabel: 'registraties',
          noMatch: 'Geen records komen overeen met uw zoekopdracht',
          empty: 'Geen aanwezigheidsrecords gevonden'
        },
        wallet: {
          title: 'Mijn portemonnee',
          childTitle: 'Portemonnee van kind',
          subtitle: 'Bekijk uw saldo, voeg tegoed toe en bekijk de transactiegeschiedenis.',
          childSubtitle: 'Bekijk saldo, vul online bij en bekijk de transactiegeschiedenis van uw kind.',
          balanceLabel: 'Uw portemonneesaldo',
          balanceHint: 'Het saldo wordt automatisch gebruikt om sessies te betalen.',
          onlineUnavailableTitle: 'Online betalingen niet beschikbaar',
          onlineUnavailableDefault: 'De licentie van uw academie omvat geen online betaalgateways. Neem contact op met de academie of betaal via een andere methode.',
          loadFailed: 'Portemonnee laden mislukt. Vernieuw de pagina.',
          enrollmentPaying: 'Betaling voor inschrijving #{{id}}. Het bedrag wordt bijgeschreven op uw portemonnee en gekoppeld aan deze inschrijving.',
          confirmingPayment: 'Uw betaling bevestigen…',
          addMoney: 'Geld toevoegen',
          addMoneyHint: 'Kies een betaalmethode en rond de betaling veilig af. Het bedrag verschijnt in uw portemonnee na bevestiging.',
          enterAmount: 'Voer bedrag in',
          securePayments: '🔒 Veilige betalingen via {{gateway}}',
          topupUnavailable: 'Online bijvullen is momenteel niet beschikbaar. Neem contact op met uw academie om tegoed aan uw portemonnee toe te voegen.',
          transactionHistory: 'Transactiegeschiedenis',
          noTransactions: 'Nog geen transacties.',
          downloadReceipt: 'Bon downloaden',
          invalidAmount: 'Voer een geldig bedrag in (minimaal {{symbol}}1).',
          selectPaymentMethod: 'Selecteer een betaalmethode.',
          invalidEnrollmentLink: 'Ongeldige inschrijvingslink. Open Online betalen opnieuw via Mijn inschrijvingen.',
          paymentConfirmFailed: 'Betalingsbevestiging mislukt. Neem contact op met de academie.',
          paymentStartFailed: 'Betaling kon niet worden gestart. Probeer het opnieuw.',
          paymentUpgradeRequired: 'Online betalingen vereisen een licentie-upgrade.',
          paymentVerifyFailed: 'Betalingsverificatie mislukt. Neem contact op met de academie.',
          receiptDownloadFailed: 'Bon kon niet worden gedownload. Probeer het opnieuw.',
          razorpayTitle: 'Portemonnee bijvullen',
          razorpayDesc: 'Voeg tegoed toe aan uw academieportemonnee',
          txnAdvanceTopup: 'Vooruitbijvulling',
          txnOnlineTopup: 'Online bijvulling',
          txnEnrollmentPayment: 'Inschrijvingsbetaling',
          txnSessionCharge: 'Sessieafschrijving',
          txnSessionReversal: 'Sessietergboeking',
          txnDiscount: 'Kortingstegoed',
          txnRefund: 'Terugbetaling',
          txnApplied: 'Toegepast op factuur',
          txnDefault: 'Transactie',
          payNow: 'Nu betalen',
          redirecting: 'Doorverwijzen…',
          ref: 'Ref'
        },
        sessionStatement: {
          eyebrow: 'Rapporten',
          title: 'Studentensessieoverzicht',
          subtitle: 'Volledige sessiegeschiedenis met aanwezigheid, tariefkosten, portemonneestortingen en -afschrijvingen, en lopend saldo — voor de geselecteerde student en periode.',
          embeddedSubtitle: 'Volledige sessiegeschiedenis met aanwezigheid, tariefkosten, portemonneestortingen & -afschrijvingen, en lopend saldo — voor de geselecteerde student en periode.',
          filters: 'Filters',
          selectStudent: 'Selecteer student',
          chooseStudent: 'Kies een student...',
          fromDate: 'Vanaf datum',
          toDate: 'Tot datum',
          generate: 'Overzicht genereren',
          generating: 'Laden...',
          selectStudentError: 'Selecteer een student',
          loadFailed: 'Sessieoverzicht laden mislukt',
          exportPdfFailed: 'PDF exporteren mislukt',
          student: 'Leerling',
          statementPeriod: 'Overzichtsperiode',
          openingBalance: 'Beginsaldo',
          availableAtStart: 'Beschikbaar bij start',
          walletCredits: 'Portemonneestortingen',
          transactionsCount: '{{count}} transacties',
          sessionCharges: 'Sessiekosten',
          sessionsPresentAbsent: '{{present}}A / {{absent}}Af sessies',
          closingBalance: 'Eindsaldo',
          currentBalance: 'Huidig saldo',
          walletTransactions: 'Portemonneetransacties',
          walletTransactionsHint: 'Bijvullingen en tegoeden toegevoegd aan uw portemonnee',
          sessionHistory: 'Sessiegeschiedenis',
          sessionHistoryHint: 'Gedetailleerd overzicht van alle sessies in deze periode',
          noSessionsPeriod: 'Geen sessies gevonden voor deze periode',
          discount: 'Korting: {{amount}}',
          emailLabel: 'Voer e-mailadres ontvanger in',
          present: 'Aanwezig',
          absent: 'Afwezig'
        },
        notifications: {
          title: 'WhatsApp-meldingen',
          subtitle: 'Kies welke academieberichten u via WhatsApp ontvangt. Uw telefoonnummer moet in uw profiel staan.',
          subtitleShort: 'Kies welke academieberichten u via WhatsApp ontvangt.',
          loadFailed: 'Meldingsvoorkeuren konden niet worden geladen.',
          saveFailed: 'Opslaan mislukt.',
          saved: 'Voorkeuren opgeslagen.',
          optInTitle: 'WhatsApp-berichten van mijn academie ontvangen',
          optInHint: 'Standaard ingeschakeld. Schakel uit om alle WhatsApp-meldingen te stoppen.',
          sessionReminders: 'Sessieherinneringen',
          absenceAlerts: 'Afwezigheidswaarschuwingen',
          lowBalance: 'Waarschuwingen laag portemonneesaldo',
          feeReminders: 'Herinneringen tarief / openstaand saldo',
          sessionCancelled: 'Meldingen geannuleerde sessie',
          holidayAnnouncements: 'Vakantieaankondigingen',
          tryAgain: 'Opnieuw proberen',
          unavailable: 'Niet beschikbaar',
          savePreferences: 'Voorkeuren opslaan',
          saving: 'Opslaan…'
        },
        parentNotifications: {
          title: 'WhatsApp-meldingen van kind',
          subtitle: 'Beheer WhatsApp-waarschuwingen die naar het telefoonnummer van uw kind op hun profiel worden gestuurd. Dit zijn de meldingsinstellingen van de student.',
          saved: 'Voorkeuren opgeslagen voor uw kind.',
          optInTitle: 'WhatsApp voor deze student inschakelen',
          optInHint: 'Waarschuwingen gaan naar het telefoonnummer op het profiel van uw kind.'
        },
        profile: {
          title: 'Mijn profiel',
          subtitle: 'Werk uw persoonlijke gegevens en adres bij',
          loadFailed: 'Profiel laden mislukt',
          requestDataTitle: 'Mijn gegevens opvragen',
          requestDataBody: 'Download een machineleesbare kopie van je profielgegevens, of vraag beheerders om een vollediger export (inzage / overdraagbaarheid).',
          downloadData: 'Mijn gegevens downloaden (JSON)',
          downloadDataDone: 'Je gegevenspakket is gedownload.',
          downloadDataFailed: 'Gegevenspakket kon niet worden gedownload.',
          requestData: 'Mijn gegevens opvragen',
          requestSent: 'Verzoek verzonden.',
          requestFailed: 'Verzoek kon niet worden verzonden. Probeer het later opnieuw.',
          changePassword: 'Wachtwoord wijzigen',
          currentPassword: 'Huidig wachtwoord',
          newPassword: 'Nieuw wachtwoord',
          confirmPassword: 'Nieuw wachtwoord bevestigen',
          passwordMinHint: 'Minimaal 8 tekens',
          profileUpdated: 'Profiel bijgewerkt',
          profileUpdateFailed: 'Profiel bijwerken mislukt',
          passwordChanged: 'Wachtwoord succesvol gewijzigd.',
          passwordChangeFailed: 'Wachtwoord wijzigen mislukt. Probeer het opnieuw.',
          enterCurrentPassword: 'Voer uw huidige wachtwoord in.',
          passwordMinLength: 'Nieuw wachtwoord moet minimaal 8 tekens bevatten.',
          passwordsMismatch: 'Nieuwe wachtwoorden komen niet overeen.',
          passwordSameAsCurrent: 'Nieuw wachtwoord moet verschillen van het huidige wachtwoord.',
          firstNameRequired: 'Voornaam is verplicht en mag letters, spaties, koppeltekens en apostroffen bevatten.',
          middleNameLetters: 'Tweede voornaam mag letters, spaties, koppeltekens en apostroffen bevatten.',
          firstNameLetters: 'Voornaam mag letters, spaties, koppeltekens en apostroffen bevatten — geen cijfers.',
          lastNameLetters: 'Achternaam mag letters, spaties, koppeltekens en apostroffen bevatten — geen cijfers.',
          lastNameRequired: 'Achternaam is verplicht en mag letters, spaties, koppeltekens en apostroffen bevatten.',
          invalidDob: 'Voer een geldige geboortedatum in het verleden in.',
          invalidAddress: 'Ongeldig adres.',
          invalidLatitude: 'Breedtegraad moet een geldig getal zijn.',
          invalidLongitude: 'Lengtegraad moet een geldig getal zijn.',
          saveChanges: 'Wijzigingen opslaan',
          saving: 'Opslaan…',
          sending: 'Verzenden…',
          updating: 'Bijwerken…',
          updatePassword: 'Wachtwoord bijwerken',
          retry: 'Opnieuw',
          preferredLanguage: 'Voorkeurstaal',
          preferredLanguageHint: 'Gebruikt voor de app-interface en voor e-mails en meldingen die naar u worden gestuurd.',
          lang_en: 'English',
          lang_de: 'Deutsch',
          lang_fr: 'Français',
          lang_nl: 'Nederlands'
        },
        students: {
          title: 'Studenten',
          subtitle: 'Zoek, bekijk en beheer studententoegang met een overzichtelijkere lijst.',
          add: 'Student toevoegen',
          bulkImport: 'Bulkimport',
          searchPlaceholder: 'Zoek studenten op naam, gebruikersnaam, e-mail, telefoon of ID...',
          entityLabel: 'studenten',
          exportFailed: 'Studenten exporteren mislukt. Probeer het opnieuw.',
          colStudentName: 'Studentnaam'
        },
        coaches: {
          title: 'Coaches',
          subtitle: 'Houd instructeursgegevens, uitnodigingen en toewijzingen georganiseerd in één moderne werkruimte.',
          add: 'Coach toevoegen',
          searchPlaceholder: 'Zoek coaches op naam, gebruikersnaam, e-mail, telefoon of ID...',
          entityLabel: 'trainers',
          colCoachName: 'Coachnaam',
          manageStudents: 'Studenten beheren',
          manageStudentsTitle: 'Toegewezen studenten voor individuele sessies beheren',
          manageAssignments: {
            title: 'Studenttoewijzingen beheren',
            coachLabel: 'Coach: {{name}}',
            infoBanner: 'Hier toegewezen studenten worden zichtbaar voor deze coach bij het registreren van individuele sessies. Zonder toewijzing ziet de coach een student niet in het sessiemenu — ook niet als de student actief is in het systeem.',
            assignedStudents: 'Toegewezen studenten ({{count}})',
            availableStudents: 'Beschikbare studenten ({{count}})',
            searchAssigned: 'Zoek toegewezen studenten...',
            searchAvailable: 'Zoek studenten...',
            noAssigned: 'Nog geen studenten toegewezen',
            noAssignedMatch: 'Geen toegewezen studenten komen overeen met uw zoekopdracht',
            noStudentsFound: 'Geen studenten gevonden',
            allAssigned: 'Alle studenten zijn toegewezen',
            assignButton: '{{count}} student(en) toewijzen',
            assignButton_other: '{{count}} studenten toewijzen',
            assigning: 'Toewijzen...',
            loadFailed: 'Gegevens laden mislukt',
            selectAtLeastOne: 'Selecteer ten minste één student',
            assignSuccess: '{{count}} student(en) succesvol toegewezen',
            assignSuccess_other: '{{count}} studenten succesvol toegewezen',
            assignFailed: 'Studenten toewijzen mislukt: {{message}}',
            removeConfirm: 'Deze studenttoewijzing verwijderen?',
            removeSuccess: 'Toewijzing succesvol verwijderd',
            removeFailed: 'Toewijzing verwijderen mislukt',
            assignedOn: 'Toegewezen: {{date}}'
          }
        },
        managers: {
          title: 'Beheerders',
          subtitle: 'Beheer beheerdersgebruikers met duidelijkere filters, betere hiërarchie en consistente acties.',
          add: 'Manager toevoegen',
          searchPlaceholder: 'Zoek managers op naam, gebruikersnaam, e-mail, telefoon of ID...',
          entityLabel: 'beheerders',
          colManagerName: 'Managernaam'
        },
        branches: {
          title: 'Vestigingen',
          subtitle: 'Beheer academievestigingen, archiveer oudere locaties en houd de bedrijfsvoering overzichtelijk.',
          add: 'Vestiging toevoegen',
          searchPlaceholder: 'Zoek vestigingen op naam, code, telefoon, manager of ID...',
          entityLabel: 'vestigingen',
          loadFailed: 'Vestigingen laden mislukt.',
          archiveConfirm: 'Deze vestiging archiveren? Dit deactiveert de vestiging, maar behoudt de gegevens.',
          archiveFailed: 'Vestiging archiveren mislukt.',
          activateFailed: 'Vestiging activeren mislukt.',
          managerFallback: 'Manager',
          colBranchName: 'Vestigingsnaam',
          colCode: 'Code',
          colMode: 'Modus',
          colManagedBy: 'Beheerd door',
          editTitle: 'Vestiging bewerken',
          archiveTitle: 'Vestiging archiveren',
          activateTitle: 'Vestiging activeren'
        },
        users: {
          title: 'Gebruikers',
          subtitle: 'Gebruikersaccounts en rechten beheren',
          add: 'Gebruiker toevoegen',
          entityLabel: 'gebruikers'
        },
        discounts: {
          title: 'Kortingenbeheer',
          subtitle: 'Kortingscodes en aanbiedingen beheren',
          create: 'Korting aanmaken',
          empty: 'Geen kortingen gevonden',
          emptyHint: 'Ga aan de slag door een nieuwe kortingscode aan te maken.',
          loadFailed: 'Kortingen laden mislukt',
          archiveConfirm: 'Weet u zeker dat u deze korting wilt archiveren?',
          activateConfirm: 'Weet u zeker dat u deze korting wilt activeren?',
          archiveSuccess: 'Korting succesvol gearchiveerd',
          activateSuccess: 'Korting succesvol geactiveerd',
          archiveFailed: 'Korting archiveren mislukt',
          activateFailed: 'Korting activeren mislukt',
          colDiscountName: 'Kortingsnaam',
          colValue: 'Waarde',
          colScope: 'Bereik',
          scopeBranch: 'Per vestiging',
          scopeLevel: 'Per niveau',
          scopeBatch: 'Per batch',
          scopeGlobal: 'Globaal',
          typePercent: 'Percentage',
          typeFixed: 'Vast bedrag',
          editTitle: 'Korting bewerken',
          archiveTitle: 'Korting archiveren',
          activateTitle: 'Korting activeren'
        },
        atRisk: {
          eyebrow: 'Rapporten',
          title: 'Risicoleerlingen',
          subtitle: 'Leerlingen waarvan de aanwezigheid onder uw drempel valt — neem contact op voordat ze afhaken.',
          daysLabel: 'Dagen terugkijken',
          daysSuffix: 'dagen',
          thresholdLabel: 'Minimale aanwezigheid (%)',
          thresholdSuffix: '% aanwezigheid',
          loadFailed: 'Risicoleerlingen laden mislukt. Probeer het opnieuw.',
          daysError: 'Dagen moeten tussen 1 en 365 liggen.',
          thresholdError: 'Drempel moet tussen 1 en 100 liggen.',
          riskCritical: 'Kritiek',
          riskHigh: 'Hoog',
          riskMedium: 'Gemiddeld',
          tierCritical: '{{count}} kritiek',
          tierHigh: '{{count}} hoog',
          tierMedium: '{{count}} gemiddeld',
          emptyTitle: 'Geen leerlingen in de risicogroep',
          emptyDescription: 'Alle actief ingeschreven leerlingen hebben meer dan {{threshold}}% aanwezigheid in de afgelopen {{days}} dagen.',
          colStudent: 'Leerling',
          colRisk: 'Risico',
          colAttendanceRate: 'Aanwezigheidspercentage',
          colSessions: 'Sessies',
          colLastSeen: 'Laatst gezien',
          colBatches: 'Groepen',
          profile: 'Profiel',
          emailAction: 'E-mail',
          footer: '{{count}} leerling(en) weergegeven met <{{threshold}}% aanwezigheid in de afgelopen {{days}} dagen. Leerlingen zonder sessies in het venster worden uitgesloten.',
          studentFallback: 'Leerling #{{id}}',
          coachFallback: 'Coach #{{id}}'
        },
        auditLogs: {
          title: 'Auditlogs',
          subtitle: 'Systeemactiviteiten bekijken en volgen',
          filterUser: 'Gebruiker',
          filterTable: 'Tabel',
          filterOperation: 'Bewerking',
          filterStartDate: 'Startdatum',
          filterEndDate: 'Einddatum',
          filterSearch: 'Zoeken',
          allUsers: 'Alle gebruikers',
          allTables: 'Alle tabellen',
          allOperations: 'Alle bewerkingen',
          searchPlaceholder: 'Zoeken in oude/nieuwe gegevens...',
          clearFilters: 'Filters wissen',
          exportCsv: 'Exporteren naar CSV',
          fetchFailed: 'Auditlogs ophalen mislukt',
          exportFailed: 'Auditlogs exporteren mislukt',
          empty: 'Geen auditlogs gevonden',
          colTable: 'Tabel',
          colOperation: 'Bewerking',
          colRecordId: 'Record-ID',
          colTimestamp: 'Tijdstempel',
          oldData: 'Oude gegevens:',
          newData: 'Nieuwe gegevens:',

          tables: {
            users: 'Gebruikers',
            sessions: 'Sessies',
            session: 'Sessie',
            batches: 'Groepen',
            batch: 'Groep',
            branches: 'Vestigingen',
            address: 'Adres',
            levels: 'Niveaus',
            enrollment: 'Inschrijving',
            attendance: 'Aanwezigheid',
            fee_plan: 'Tariefplan',
            discount_master: 'Korting',
            wallet_txn: 'Portemonneetransactie',
            billing_settings: 'Factureringsinstellingen',
            coach_student_assignment: 'Coach–leerlingkoppeling',
            session_participants: 'Sessiedeelnemers',
            password_reset_tokens: 'Wachtwoordresettokens',
            academy_holiday: 'Academievakantie',
            email_queue: 'E-mailwachtrij',
            whatsapp_queue: 'WhatsApp-wachtrij',
            auth: 'Authenticatie'
          },
          ops: {
            INSERT: 'Invoegen',
            UPDATE: 'Bijwerken',
            DELETE: 'Verwijderen',
            LOGIN: 'Inloggen',
            LOGOUT: 'Uitloggen',
            PASSWORD_SET: 'Wachtwoord ingesteld',
            PASSWORD_RESET: 'Wachtwoord gereset',
            BULK_UPDATE: 'Bulkupdate',
            ARCHIVE: 'Archiveren',
            ACTIVATE: 'Activeren',
            DEACTIVATE: 'Deactiveren'
          },
          showingEntries: '{{start}} tot {{end}} van {{total}} vermeldingen weergegeven'
        },
        walletTopup: {
          eyebrow: 'Tarieven & facturering',
          title: 'Wallet opwaarderen',
          subtitle: 'Voeg tegoed toe aan de wallet van een leerling. Dit tegoed kan worden gebruikt voor sessiekosten, met automatische afschrijving na elke sessie.',
          walletBadge: 'Wallet',
          selectStudent: 'Leerling selecteren',
          searchPlaceholder: 'Zoek leerling op naam...',
          noStudents: 'Geen leerlingen gevonden',
          change: 'Wijzigen',
          studentId: 'Leerling-ID: {{id}}',
          idLabel: 'ID: {{id}}',
          archivedWarning: 'Deze leerling is momenteel niet actief in het systeem. Wallet-opwaarderingen kunnen nog steeds voor hun account worden geregistreerd.',
          archivedAlertTitle: 'Gearchiveerde leerling',
          archivedAlertMessage: '{{name}} is momenteel niet actief in het systeem.\n\nDeze leerling is gearchiveerd. U kunt nog steeds een wallet-opwaardering registreren, maar zij kunnen niet inloggen of reguliere leerlingfuncties gebruiken totdat een beheerder hun account opnieuw activeert.',
          addFunds: 'Tegoed toevoegen',
          enterPaymentDetails: 'Voer hieronder de betalingsgegevens in',
          amount: 'Bedrag',
          paymentMode: 'Betaalwijze',
          paidOn: 'Betaald op',
          paidOnHint: 'Alleen vandaag of een eerdere datum',
          reference: 'Referentie / transactie-ID',
          referenceOptional: '(Optioneel)',
          paymentReceivedBy: 'Betaling ontvangen door',
          staffMember: 'Medewerker',
          otherPerson: 'Andere persoon',
          selectStaff: 'Medewerker selecteren',
          chooseStaff: '-- Kies medewerker --',
          receiverName: 'Naam ontvanger',
          receiverNamePlaceholder: 'Voer de volledige naam van de ontvanger in',
          clearForm: 'Formulier wissen',
          recordPayment: 'Betaling registreren',
          processing: 'Bezig met verwerken...',
          chooseStudent: 'Kies een leerling',
          amountRequired: 'Bedrag moet groter zijn dan 0',
          paidOnRequired: 'Kies de betaaldatum',
          paidOnFuture: 'Betaaldatum mag niet in de toekomst liggen',
          paidOnInvalid: 'Voer een geldige betaaldatum in',
          receiverTypeInvalid: 'Selecteer een geldig type betalingsontvanger (USER/OTHERS).',
          staffRequired: 'Selecteer de medewerker die de betaling heeft ontvangen.',
          receiverNameRequired: 'Voer de naam van de ontvanger in.',
          success: '✅ Wallet succesvol opgewaardeerd! {{amount}} bijgeschreven op de wallet van de leerling.',
          failed: 'Wallet-opwaardering registreren mislukt.'
        }
      },
      cookie: {
        body: 'Deze app gebruikt alleen essentiële cookies — om u ingelogd te houden en uw sessie te onthouden. Geen tracking, geen advertenties.',
        gotIt: 'Begrepen',
        privacy: 'Privacybeleid',
        terms: 'Voorwaarden',
        and: 'en',
        ariaLabel: 'Cookietoestemming'
      },
      faq: {
        title: 'Veelgestelde vragen',
        subtitle: '{{count}} vragen over alles wat u als {{role}} kunt doen.',
        findAnswers: 'Hulp',
        searchHeading: 'Antwoorden zoeken',
        searchHint: 'Zoek op trefwoord of blader hieronder per onderwerp.',
        searchPlaceholder: 'Vragen zoeken…',
        allSections: 'Alles',
        noResults: 'Geen resultaten gevonden',
        tryDifferent: 'Probeer een ander trefwoord of wis de zoekopdracht',
        noQuestionsMatch: 'Geen vragen komen overeen met uw zoekopdracht.',
        resultsFor: '{{count}} resultaat voor "{{query}}"',
        resultsFor_other: '{{count}} resultaten voor "{{query}}"',
        footerPrompt: 'Kunt u niet vinden wat u zoekt?',
        footerAction: 'Neem contact op met de beheerder van uw academie voor hulp.',
        roles: {
          ADMIN: 'Beheerder',
          MANAGER: 'Beheerder',
          COACH: 'Coach',
          STUDENT: 'Leerling',
          PARENT: 'Ouder'
        }
      },
      settings: {
        email: {
          title: 'E-mailinstellingen',
          loadingSubtitle: 'SMTP-configuratie laden…',
          subtitle: 'Configureer het SMTP-account dat uw academie gebruikt om inloglinks, herinneringen, overzichten en andere systeem-e-mails te versturen.',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          stepThree: 'Stap 3',
          smtpSection: 'SMTP-verbinding',
          smtpSectionDesc: 'Servergegevens waarmee uitgaande systeem-e-mail van uw academie wordt verzonden.',
          fromSection: 'Afzenderidentiteit',
          fromSectionDesc: 'Hoe ontvangers de Van- en Antwoord-aan-adressen op academie-e-mails zien.',
          testSection: 'Bezorging verifiëren',
          testSectionDesc: 'Stuur een testbericht om referenties en uitgaande bezorging te bevestigen voordat u live gaat.',
          statusLabel: 'Status',
          modeEnabled: 'Verzenden aan',
          modeConfigured: 'Geconfigureerd',
          modeIncomplete: 'Onvolledig',
          saveSettings: 'Instellingen opslaan',
          saveBeforeTest: 'Sla SMTP-instellingen op voordat u een test-e-mail verstuurt.',
          testCompleted: 'Test voltooid.',
          testFailed: 'Test-e-mail mislukt.',
          useStarttlsDesc: 'Versleutel de SMTP-sessie na verbinding. Aanbevolen voor poort 587.',
          lastTestPassedHint: 'Voor Gmail bevestigen we dat het bericht in de map Verzonden van de afzender staat. Ontvangers moeten nog steeds spam/promoties controleren.',
          lastTestFailedHint: 'Controleer de gegevens en probeer het opnieuw.',
          infoBanner: 'Dit is uw configuratie voor <strong>uitgaande verzending</strong>. Het openbare supportcontact voor leerlingen wordt beheerd onder <1>Branding</1>. Meldingsschakelaars staan onder <2>Meldingen</2>.',
          enableTitle: 'Uitgaande e-mail inschakelen',
          enableHint: 'Vereist geldige SMTP-gegevens hieronder.',
          smtpHost: 'SMTP-host',
          smtpPort: 'SMTP-poort',
          smtpUsername: 'SMTP-gebruikersnaam',
          smtpPassword: 'SMTP-wachtwoord / app-wachtwoord',
          passwordConfigured: '(al ingesteld — laat leeg om te behouden)',
          passwordRequired: 'Verplicht',
          fromEmail: 'Van-e-mailadres',
          fromName: 'Van-weergavenaam',
          fromNamePlaceholder: 'Standaard de academienaam',
          replyTo: 'Antwoord-aan-e-mail',
          replyToPlaceholder: 'Standaard het support-e-mailadres uit Branding',
          useStarttls: 'STARTTLS gebruiken (aanbevolen voor poort 587)',
          enableWarning: 'Voer SMTP-host, van-e-mailadres en wachtwoord in voordat u uitgaande e-mail inschakelt.',
          testRecipient: 'Test-e-mail versturen naar',
          sendTest: 'Test versturen',
          verifying: 'Bezig met verifiëren… (max. 45s)',
          lastTestPassed: 'Laatste test geslaagd',
          lastTestFailed: 'Laatste test mislukt',
          loadFailed: 'E-mailinstellingen laden mislukt.',
          saveSuccess: 'E-mailinstellingen opgeslagen.',
          saveFailed: 'E-mailinstellingen opslaan mislukt.'
        },
        whatsapp: {
          title: 'WhatsApp (WATI)',
          loadingSubtitle: 'Instellingen laden…',
          subtitle: 'Koppel het WATI-account van uw academie om sessieherinneringen, walletmeldingen en andere operationele berichten via WhatsApp te versturen. Berichtkosten worden door WATI/Meta aan uw academie gefactureerd.',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          stepThree: 'Stap 3',
          connectionSection: 'Verbinding',
          connectionSectionDesc: 'Licentiestatus, hoofdschakelaar en WATI-gegevens voor uitgaande WhatsApp.',
          templatesSection: 'Berichtsjablonen',
          templatesSectionDesc: 'Exacte sjabloonnamen zoals goedgekeurd in uw WATI-account.',
          localeTemplateNote: 'Voor regionale locales de/fr/nl stuurt de app "{{example}}" (basisnaam + _{{lang}}). Registreer passende taalsjablonen in WATI.',
          recentLogDesc: 'Laatste uitgaande WhatsApp-pogingen voor deze academie.',
          statusLabel: 'Status',
          modeUnlicensed: 'Niet gelicentieerd',
          modeEnabled: 'Verzenden aan',
          modeOff: 'Uit',
          saveSettings: 'Instellingen opslaan',
          notLicensed: 'WhatsApp is niet inbegrepen in uw huidige ChessDesk-licentie. Neem contact op met Axeron Technologies om te upgraden (Professional of Enterprise, of schakel <strong>WhatsApp Enabled</strong> in op de rij van uw licentieblad).',
          enableTitle: 'WhatsApp inschakelen',
          enableHint: 'Hoofdschakelaar van de academie (vereist licentie + WATI-gegevens of mockmodus).',
          mockMode: 'Mockmodus',
          mockModeDesc: 'Berichten lokaal loggen zonder WATI aan te roepen (aanbevolen voor ontwikkeling/testen)',
          apiBaseUrl: 'WATI API-basis-URL',
          apiBaseUrlNote: 'Vanuit WATI-dashboard → API Docs',
          channelNumber: 'Zakelijk kanaalnummer',
          channelNumberNote: 'Uw WATI WhatsApp-nummer (vereist voor live verzending).',
          bearerToken: 'WATI Bearer-token',
          tokenPlaceholder: 'Plak bearer-token uit WATI API Docs',
          templatesTitle: 'Goedgekeurde sjabloonnamen in WATI',
          templateSessionReminder: 'Sessieherinnering (leerlingen)',
          templateAbsenceAlert: 'Afwezigheidsmelding (leerlingen)',
          templateLowBalance: 'Lage wallet (leerlingen)',
          templateFeeReminder: 'Tarief / openstaand (leerlingen)',
          templatePendingSessions: 'Openstaande sessies (coaches)',
          templateLowBalanceAdmin: 'Lage-walletmelding (beheerders)',
          templateSessionCancelled: 'Sessie geannuleerd (leerlingen)',
          templateHolidayAnnouncement: 'Vakantieaankondiging (alle medewerkers & leerlingen)',
          templateCorrespondenceDeadline: 'Correspondentie-deadline (speelplatform)',
          templateCorrespondenceYourMove: 'Correspondentie — jouw zet (speelplatform)',
          enableWarning: 'Voer WATI-gegevens en zakelijk kanaalnummer in, of schakel mockmodus in voordat u activeert.',
          recentLog: 'Recent berichtenlogboek',
          noMessages: 'Nog geen WhatsApp-berichten in de wachtrij.',
          when: 'Wanneer',
          phone: 'Telefoon',
          type: 'Type',
          status: 'Status',
          unavailable: {
            notAvailableTitle: 'WhatsApp-meldingen zijn niet beschikbaar',
            notAvailableBody: 'Uw academie heeft WhatsApp-berichten op ChessDesk niet ingeschakeld. {{phoneHint}} Neem contact op met het academiekantoor als u sessie- of saldoupdates nodig heeft.',
            notSetupTitle: 'WhatsApp is nog niet ingesteld',
            notSetupBody: 'Uw academie heeft WhatsApp-meldingen nog niet ingeschakeld. Zodra zij WhatsApp koppelen, kunt u hier kiezen welke meldingen u ontvangt. {{phoneHint}}',
            phoneHintParent: 'Wanneer WhatsApp beschikbaar is, gaan meldingen naar het telefoonnummer op het profiel van uw kind.',
            phoneHintStudent: 'Wanneer WhatsApp beschikbaar is, gaan meldingen naar het telefoonnummer op uw profiel.'
          },
          loadFailed: 'WhatsApp-instellingen laden mislukt.',
          saveSuccess: 'WhatsApp-instellingen opgeslagen.',
          saveFailed: 'Instellingen opslaan mislukt.',
          testTitle: 'Testbericht versturen',
          testDesc: 'Lost de sessieherinneringssjabloon op voor de academielocale (inclusief _de/_fr/_nl) en stuurt via WATI of mockmodus.',
          testPhone: 'Ontvangernummer (E.164)',
          sendTest: 'Test versturen',
          testing: 'Testen…',
          testFailed: 'WhatsApp-test mislukt.'
        },
        chessCoach: {
          title: 'Ask AI Coach',
          loadingSubtitle: 'Ask AI Coach-instellingen laden…',
          subtitle: 'Schakel de Ask AI Coach-chatbot in en vul een OpenAI-compatibele API-sleutel in (OpenAI, Groq, OpenRouter, enz.).',
          infoBanner: 'Als Ask AI Coach is ingeschakeld en geconfigureerd, verschijnt het voor leerlingen, ouders en staf in Playing. Engine-analyse blijft op uw server; de API-sleutel wordt alleen gebruikt voor uitleg in natuurlijke taal.',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          connectionSection: 'Inschakelen & verbinding',
          connectionSectionDesc: 'Hoofdschakelaar en inloggegevens voor het taalmodel achter Ask AI Coach.',
          limitsSection: 'Limieten & fair play',
          limitsSectionDesc: 'Snelheidslimieten, antwoordlengte en beleid voor live gewaardeerde partijen.',
          statusLabel: 'Status',
          modeOperational: 'Live',
          modeEnabledIncomplete: 'Aan — API-sleutel ontbreekt',
          modeConfigured: 'Geconfigureerd (uit)',
          modeIncomplete: 'Niet ingesteld',
          enableTitle: 'Ask AI Coach inschakelen',
          enableHint: 'Toont de coach-chat voor academiegebruikers wanneer een API-sleutel is opgeslagen.',
          enableWarning: 'Plak een API-sleutel voordat u Ask AI Coach inschakelt.',
          baseUrl: 'API-basis-URL',
          baseUrlHint: 'OpenAI-compatibele chat-completions-basis (zonder slash aan het eind). Groq: https://api.groq.com/openai/v1',
          model: 'Model',
          modelHint: 'Model-id van de provider, bijv. gpt-4o-mini of llama-3.3-70b-versatile',
          apiKey: 'API-sleutel',
          apiKeyPlaceholder: 'Plak API-sleutel van de provider',
          apiKeyConfigured: '(al ingesteld — leeg laten om te behouden)',
          apiKeyHint: 'Wordt in de academiedatabase opgeslagen en na opslaan niet meer getoond.',
          maxTokens: 'Max. antwoordtokens',
          rateLimit: 'Vragen per gebruiker / minuut',
          maxHistory: 'Geschiedenisbeurten naar het model',
          timeoutMs: 'Timeout (ms)',
          liveRatedIdeasOnly: 'Alleen ideeën in live gewaardeerde partijen',
          liveRatedIdeasOnlyDesc: 'In actieve gewaardeerde menselijke partijen alleen plannen bespreken — nooit een concreet beste zet voorstellen.',
          saveSettings: 'Instellingen opslaan',
          loadFailed: 'Ask AI Coach-instellingen laden mislukt.',
          saveSuccess: 'Ask AI Coach-instellingen opgeslagen.',
          saveFailed: 'Ask AI Coach-instellingen opslaan mislukt.'
        },
        production: {
          title: 'EU-productiegereedheid',
          loadingSubtitle: 'Go-live-vereisten controleren…',
          subtitle: 'Checklist voor HTTPS-webhooks, SMTP, WhatsApp-taalsjablonen, tijdzone en bevestiging van juridische review van EU-concepten.',
          loadFailed: 'Productiegereedheid laden mislukt.',
          readyBanner: 'Geen in-app blockers — rond live Mollie/Stripe-keys en Meta-sjablonen buiten ChessDesk af.',
          notReadyBanner: 'Los onderstaande blockers op vóór EU go-live.',
          readyHint: 'Dit scherm kan geen merchantaccounts of advocatensignaturen maken; het volgt wat ChessDesk kan verifiëren.',
          checklistTitle: 'Checklist',
          open: 'Openen',
          checkHttps: 'Publieke HTTPS-academie-URL (FRONTEND_BASE_URL)',
          checkMollieWebhook: 'Mollie-webhook-URL geconfigureerd',
          checkSmtp: 'SMTP ingeschakeld en operationeel',
          smtpLastTestFailed: 'Laatste SMTP-test mislukt',
          checkWhatsapp: 'WhatsApp (indien gelicentieerd)',
          whatsappSuffix: 'Localesuffix {{suffix}} — registreer bijpassende Meta-sjablonen',
          whatsappEnglish: 'Engelse basissjabloonnamen',
          checkLegal: 'Juridische review bevestigd',
          checkTimezone: 'Academietijdzone (regionale instellingen)',
          timezoneDetail: '{{academy}}',
          webhooksTitle: 'Webhook-URL\'s om te registreren',
          webhooksDesc: 'Kopieer naar Stripe-, PayPal-, Mollie- en Razorpay-dashboards.',
          templatesTitle: 'Opgeloste WhatsApp-sjabloonnamen',
          templatesDesc: 'Registreer deze exacte namen in Meta/WATI voor de academielocale.',
          legalTitle: 'EU-juridisch pakket & bevestiging',
          legalDesc: 'Concepten zijn klaar voor counsel, niet advocaat-finaal. Bevestig alleen na review door een advocaat in de relevante jurisdictie.',
          docPrivacyEu: 'Privacy (EU)',
          docDpaEu: 'DPA-bijlage (EU)',
          docPrivacy: 'Privacy',
          docDpa: 'DPA',
          docTerms: 'Voorwaarden',
          docEula: 'EULA',
          docLoading: 'Laden…',
          docLoadFailed: 'Document laden mislukt.',
          confirmLabel: 'Ik bevestig dat counsel het huidige EU-juridische pakket voor deze academie heeft beoordeeld, en dat we ondertekende kopieën bewaren.',
          confirmRequired: 'Vink eerst het bevestigingsvakje aan.',
          notesLabel: 'Notities (optioneel)',
          notesPlaceholder: 'bijv. kantoor, reviewdatum',
          ackButton: 'Counsel-bevestiging opslaan',
          ackSuccess: 'Counsel-bevestiging opgeslagen.',
          ackFailed: 'Bevestiging opslaan mislukt.',
          alreadyAcked: 'Bevestigd door {{by}} op {{at}} (versie {{version}}).'
        },
        notifications: {
          title: 'Meldingsvoorkeuren',
          loadingSubtitle: 'Standaardmeldingen van de academie laden…',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          stepThree: 'Stap 3',
          stepFour: 'Stap 4',
          saveSettings: 'Instellingen opslaan',
          subtitle: 'Academiebrede standaardwaarden voor e-mail, WhatsApp (WATI) en in-app-meldingen. Wijzigingen gelden voor geautomatiseerde berichten die door het systeem worden verstuurd. Voor Europese clubs staat voorbeeldtekst DE/FR in docs/notification-template-packs-DE-FR.md (plakken in SMTP/WATI-sjablonen).',
          savePreferences: 'Voorkeuren opslaan',
          saveSuccess: 'Voorkeuren succesvol opgeslagen.',
          saveFailed: 'Voorkeuren konden niet worden opgeslagen. Probeer het opnieuw.',
          emailPanel: 'E-mail',
          emailDesc: 'SMTP-berichten naar leerlingen, coaches en medewerkers bij gebeurtenissen.',
          emailFooterOn: 'Schakel de hoofdschakelaar uit om alle uitgaande e-mails te pauzeren zonder individuele opties te wijzigen.',
          emailFooterOff: 'Configureer uitgaande SMTP onder Systeem → E-mailinstellingen voordat u e-mailmeldingen inschakelt.',
          emailNotConfigured: 'Uitgaande e-mail is nog niet geconfigureerd. Ga eerst naar <strong>Systeem → E-mailinstellingen</strong>.',
          whatsappPanel: 'WhatsApp',
          whatsappNotLicensed: 'WhatsApp is niet inbegrepen in uw licentie. Schakel het in op uw licentieblad of upgrade uw abonnement.',
          whatsappDesc: 'Gebruikt WATI-gegevens uit Systeem → WhatsApp. Leerlingen kunnen per berichttype afmelden in hun portaal.',
          notLicensedBadge: 'Niet gelicentieerd',
          whatsappFooter: 'Configureer API-sleutels onder Systeem → WhatsApp.',
          whatsappMaster: 'WhatsApp-meldingen (hoofd)',
          studentsSection: 'Leerlingen',
          staffSection: 'Medewerkers & academie',
          inappPanel: 'In-app',
          inappDesc: 'Belmeldingen in het dashboard voor beheerders, managers en coaches.',
          walletPanel: 'Walletmeldingen',
          walletDesc: 'Gebruikt voor e-mail- en WhatsApp-waarschuwingen bij laag saldo wanneer die kanalen zijn ingeschakeld.',
          lowWalletThreshold: 'Drempel laag walletsaldo',
          lowWalletHelper: 'Melden wanneer het walletsaldo van een leerling onder dit bedrag komt.',
          walletTip: '<strong>Tip:</strong> Leerling-specifieke afmeldingen staan bij de meldingsinstellingen van elke leerling. Deze pagina bepaalt wat de academie standaard mag versturen.',
          emailMaster: 'E-mailmeldingen (hoofd)',
          emailFeeReminders: 'E-mails met tariefherinneringen',
          emailSessionRemindersStudent: 'Sessieherinneringen voor leerlingen',
          emailSessionRemindersCoach: 'Herinneringen openstaande sessies voor coaches',
          emailAbsenceAlerts: 'E-mails met afwezigheidsmeldingen',
          emailSessionCancelled: 'E-mails bij geannuleerde sessie',
          emailOnlineTopupAdmin: 'Online betaling ontvangen (beheerdersmelding)',
          inappMaster: 'In-app-meldingen (hoofd)',
          inappFeeAlerts: 'In-app tariefmeldingen',
          inappPendingSessions: 'In-app meldingen openstaande sessies',
          inappAtRiskStudents: 'In-app meldingen risicoleerlingen',
          waSessionReminders: 'Sessieherinneringen',
          waAbsenceAlerts: 'Afwezigheidsmeldingen',
          waLowBalance: 'Waarschuwingen laag walletsaldo',
          waFeeReminders: 'Herinneringen tarief / openstaand',
          waSessionCancelled: 'Berichten geannuleerde sessie',
          waPendingSessions: 'Openstaande sessies (coaches)',
          waLowBalanceAdmin: 'Lage-walletmeldingen (beheerders)',
          waHolidayAnnouncements: 'Vakantieaankondigingen',
          waCorrespondenceDeadline: 'Correspondentie-deadline (speelplatform)',
          waCorrespondenceYourMove: 'Correspondentie — jouw zet (speelplatform)'
        },
        billing: {
          title: 'Betalingshandhaving',
          subtitle: 'Gedrag van aanwezigheid en tariefinning beheren',
          loadFailed: 'Instellingen voor betalingshandhaving laden mislukt.',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          stepThree: 'Stap 3',
          modeLabel: 'Huidige modus',
          modeStrict: 'Strikt — eerst betalen',
          modeGrace: 'Uitstel toegestaan',
          modeFlexible: 'Flexibel',
          attendanceSection: 'Aanwezigheid & openstaande tarieven',
          attendanceSectionDesc: 'Bepaal of leerlingen mogen deelnemen wanneer periodetarieven onbetaald zijn, en hoe lang uitstel duurt.',
          requirePayment: 'Betaling vereisen vóór aanwezigheid',
          requirePaymentDesc: 'Medewerkers kunnen niet inschrijven zonder betaling; de wallet moet bij inschrijving worden belast.',
          allowUnpaidPeriod: 'Aanwezigheid toestaan terwijl periodetarief onbetaald is',
          allowUnpaidPeriodDesc: 'Leerlingen mogen deelnemen nadat een periodelast mislukt, tot de uitstelgrenzen zijn bereikt.',
          graceSection: 'Uitstelgrenzen',
          graceSectionDesc: 'Laat leeg voor geen limiet. De eerst bereikte limiet blokkeert verdere aanwezigheid.',
          graceCalendarDays: 'Uitstel kalenderdagen (optioneel)',
          graceSessions: 'Uitstel sessies (optioneel)',
          graceOptionalNote: 'Leeg = geen limiet voor deze regel',
          walletSection: 'Wallet & inning',
          walletSectionDesc: 'Hoe sessie-/periodebelastingen zich gedragen bij laag saldo, en hoe lang automatische inning opnieuw probeert.',
          allowNegativeWallet: 'Negatief walletsaldo toestaan bij belasten',
          allowNegativeWalletDesc: 'Belastingen worden nog geboekt bij saldo nul — de leerling draagt een schuld.',
          collectionWindow: 'Venster voor opnieuw innen (kalenderdagen)',
          collectionWindowDesc: 'Hoeveel dagen het systeem blijft proberen onbetaalde periodetarieven te innen (1–14).',
          remindersSection: 'Herinneringen',
          remindersSectionDesc: 'Automatische berichten wanneer inschrijvingen bijna aflopen.',
          expiryReminders: 'Herinneringen voor aflopende inschrijving versturen',
          expiryRemindersDesc: 'Leerlingen (en gekoppelde voogden) melden vóór einddatums van inschrijvingen.',
          saveSettings: 'Instellingen opslaan',
          saveSuccess: 'Instellingen opgeslagen.',
          saveFailed: 'Instellingen opslaan mislukt.'
        },
        regional: {
          title: 'Regionale instellingen',
          subtitle: 'Configureer landinstelling, valuta en hoe datums en tijden verschijnen in de app, e-mails en PDF\'s. Betaalgateways worden beheerd onder Systeem → Betaalmethoden.',
          quickPreset: 'Snelle voorinstelling',
          custom: 'Aangepast',
          sampleAmount: 'Voorbeeldbedrag:',
          sampleDate: 'Voorbeelddatum:',
          sampleTime: 'Voorbeeldtijd:',
          timezoneLabel: 'Tijdzone:',
          cardLocale: 'Landinstelling & tijdzone',
          cardCountry: 'Land & telefoon',
          cardCurrency: 'Valuta',
          cardTax: 'Belasting (alleen weergave)',
          cardDateTime: 'Datum en tijd',
          timezone: 'Tijdzone',
          timezoneNote: 'Gebruikt voor tijdstempels bij PDF-export en regionale standaardwaarden.',
          localeTag: 'Landinstellingstag (BCP 47)',
          localeTagNote: 'Bepaalt getalgroepering en datumvolgorde in de webapp.',
          countryCode: 'Landcode (ISO)',
          countryName: 'Landnaam',
          phoneDialCode: 'Telefoonlandnummer',
          phoneFormatHint: 'Hint telefoonformaat',
          countryNote: 'Wijzigen van land werkt bij welke betaalmethoden beschikbaar zijn (UPI alleen voor India, enz.).',
          currencyNote: 'Alle walletbedragen en sessiekosten gebruiken deze valuta. Zie docs/CURRENCY-DESIGN.md.',
          currencyCode: 'Valutacode (ISO 4217)',
          currencySymbol: 'Valutasymbool',
          decimalPlaces: 'Decimalen',
          symbolPosition: 'Symboolpositie',
          symbolBefore: 'Voor bedrag ({{example}})',
          symbolAfter: 'Na bedrag ({{example}})',
          taxNote: 'ChessDesk toont belastinglabels op regionale instellingen en bonnen, maar genereert geen wettelijke belastingfacturen.',
          taxLabel: 'Belastinglabel',
          taxRate: 'Belastingtarief (%)',
          taxId: 'Belasting-ID',
          taxIdLabel: 'Label belasting-ID-veld',
          taxIncluded: 'Belasting inbegrepen in weergegeven prijzen',
          taxIncludedYes: 'Ja (inclusief)',
          taxIncludedNo: 'Nee (exclusief)',
          timeFormat: 'Tijdformaat (webapp)',
          timeFormatNote: 'Sessies, aanwezigheid, agenda en tijdkiezers.',
          time24: '24-uurs (14:30)',
          time12: '12-uurs (02:30 PM)',
          datetimeFormat: 'Datum- & tijdformaat (e-mails & PDF\'s)',
          datetimeFormatNote: 'Java-patroon voor rapporten, herinneringen en bonnen. Wordt bijgewerkt wanneer u het tijdformaat wijzigt.',
          saveSettings: 'Instellingen opslaan',
          saving: 'Opslaan...',
          loadFailed: 'Regionale instellingen laden mislukt.',
          saveSuccess: 'Regionale instellingen succesvol opgeslagen.',
          saveFailed: 'Regionale instellingen opslaan mislukt.',
          placeholders: {
            localeTag: 'nl-NL',
            countryCode: 'NL',
            countryName: 'Nederland',
            phoneDialCode: '+31',
            phoneFormatHint: '+31 6 XXXXXXXX',
            currencyCode: '',
            currencySymbol: '',
            taxLabel: 'btw',
            taxIdLabel: 'btw-nummer',
            taxRegistration: 'Uw academie {{label}}'
          }
        },
        branding: {
          title: 'Aangepaste branding',
          subtitle: 'Stel de naam, slogan en het logo van uw academie in. Wijzigingen zijn meteen actief — opnieuw bouwen is niet nodig.',
          loadingSubtitle: 'Brandinginstellingen laden…',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          stepThree: 'Stap 3',
          stepFour: 'Stap 4',
          sectionIdentityDesc: 'Weergegeven in de kop, op het inlogscherm en in de voettekst. Laat leeg om build-time standaardwaarden te gebruiken.',
          sectionLogoDesc: 'Upload een vierkant logo voor kopteksten, inlogschermen en PDF-exports.',
          appNameNote: 'Weergegeven in de kop, op het inlogscherm en in de voettekst. Laat leeg om de build-time standaard ({{default}}) te gebruiken.',
          taglineNote: 'Korte regel onder de appnaam in de kop. Laat leeg om de build-time standaard te gebruiken.',
          saveSettings: 'Instellingen opslaan',
          saveSuccess: 'Brandinginstellingen succesvol opgeslagen.',
          saveFailed: 'Brandinginstellingen opslaan mislukt.',
          enterpriseSubtitle: 'Runtime-wijzigingen van logo en academienaam zijn alleen beschikbaar op de Enterprise-licentie.',
          enterpriseFeature: 'Enterprise-functie',
          enterpriseNote: 'Huidige niet-Enterprise-licenties blijven de standaard build-time branding uit de deployconfiguratie gebruiken.',
          loadFailed: 'Huidige brandinginstellingen laden mislukt',
          appNameTagline: 'Appnaam & slogan',
          appName: 'App- / academienaam',
          tagline: 'Slogan',
          saveText: 'Tekst opslaan',
          logoImage: 'Logoafbeelding',
          clickChooseLogo: 'Klik om een logo te kiezen',
          logoHint: 'PNG, JPG, SVG, WebP · max. 2 MB · aanbevolen 256 × 256 px',
          removeLogo: 'Aangepast logo verwijderen',
          removing: 'Verwijderen…',
          uploadLogo: 'Logo uploaden',
          uploading: 'Uploaden…',
          logoActive: 'Er is momenteel een aangepast logo actief.',
          logoDefault: 'Geen aangepast logo ingesteld — het standaardlogo uit de build wordt gebruikt.',
          academyContact: 'Academiecontact',
          contactNote: 'Weergegeven in de inlogvoettekst en op de helppagina voor leerlingen, ouders en medewerkers.',
          supportEmail: 'Support-e-mail',
          supportPhone: 'Supporttelefoon',
          saveContact: 'Contact opslaan',
          livePreview: 'Live voorbeeld',
          previewNote: 'Zo verschijnt de kop voor gebruikers.',
          loginPreview: 'Voorbeeld inlogscherm',
          instantNote: '⚡ Direct & blijvend',
          instantDesc: 'Wijzigingen worden in de database opgeslagen en door alle gebruikers opgepikt bij hun volgende paginalading — opnieuw starten of bouwen is niet nodig.',
          removeLogoConfirm: 'Aangepast logo verwijderen? Het systeemstandaardlogo wordt gebruikt.',
          logoUploadSuccess: 'Logo succesvol geüpload.',
          logoUploadFailed: 'Logo uploaden mislukt',
          logoRemoved: 'Aangepast logo verwijderd. Standaardlogo is nu actief.',
          logoRemoveFailed: 'Logo verwijderen mislukt',
          contactUpdated: 'Contactgegevens van de academie bijgewerkt.',
          contactSaveFailed: 'Contactgegevens opslaan mislukt',
          textUpdated: 'Brandingtekst succesvol bijgewerkt.',
          textSaveFailed: 'Brandingtekst opslaan mislukt',
          validImageFile: 'Selecteer een geldig afbeeldingsbestand (PNG, JPG, SVG, enz.)',
          logoTooLargeAlert: 'Logo moet kleiner zijn dan 2 MB'
        },
        license: {
          title: 'Licentie',
          subtitle: 'Uw abonnementscapaciteit, huidig gebruik en een overzicht van alle ondersteunde ChessDesk-licentieniveaus. Limieten worden ingesteld door uw softwareleverancier (licentiesleutel en Google Sheet). Neem contact op met support om te upgraden.',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          stepThree: 'Stap 3',
          stepFour: 'Stap 4',
          yourLicense: 'Uw licentie',
          yourLicenseDesc: 'Editie, vervaldatum en sleutel uitgegeven door uw softwareleverancier.',
          standardPlan: '{{edition}} · standaardlimieten {{caps}}',
          loadFailed: 'Licentie-informatie laden mislukt.',
          edition: 'Editie',
          expiry: 'Vervaldatum',
          licenseKey: 'Licentiesleutel',
          notSpecified: 'Niet opgegeven',
          unlimited: 'Onbeperkt',
          daysRemaining: '{{count}} dag resterend',
          daysRemaining_other: '{{count}} dagen resterend',
          slotsRemaining: '{{count}} plek resterend',
          slotsRemaining_other: '{{count}} plekken resterend',
          atLimit: 'Op limiet',
          nearLimit: 'Bijna limiet',
          capacityTitle: 'Uw capaciteit (alle leerlingen, coaches & vestigingen)',
          capacityNote: 'Leerlingen en coaches tellen mee terwijl ze actief of gearchiveerd zijn. Wanneer u op de abonnementslimiet zit, kunnen gearchiveerde leerlingen/coaches <strong>definitief worden verwijderd</strong> om een plek vrij te maken (geschiedenis blijft bewaard en gelabeld als Inactief). Vestigingen tellen altijd mee.',
          students: 'Leerlingen',
          branches: 'Vestigingen',
          coaches: 'Coaches',
          featuresTitle: 'Functies in uw abonnement',
          featureCore: 'Walletfacturering, aanwezigheid, sessies en rapporten',
          featureBranding: 'Aangepast logo en academienaam:',
          featureWhatsapp: 'WhatsApp (WATI)-integratie:',
          featureBulkImport: 'Bulkimport leerlingen via CSV:',
          plansTitle: 'Ondersteunde abonnementen & functies',
          plansDesc: 'Referentie voor alle ChessDesk-licentieniveaus. Uw actieve limieten hierboven kunnen afwijken als uw leverancier aangepaste limieten op het licentieblad heeft ingesteld.',
          yourPlan: 'Uw abonnement',
          capacityLimits: 'Capaciteitslimieten (standaard)',
          tierCaps: 'tot {{students}} leerlingen, {{branches}} vestigingen, {{coaches}} coaches',
          plans: { starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' },
          featureGroups: { core: 'Kernplatform', multiBranch: 'Meerdere vestigingen & betalingen', enterprise: 'Enterprise' },
          features: {
            attendance: 'Aanwezigheid, sessies en sessiekalender',
            billing: 'Facturatie, facturen en leerlingwallet',
            reports: 'Rapporten en Excel-export',
            portal: 'Leerling- en ouderportaal',
            email: 'E-mailmeldingen',
            atRisk: 'Detectie van risicoleerlingen',
            multiBranch: 'Beheer van meerdere vestigingen',
            managerRole: 'Managerrol per vestiging',
            crossBranch: 'Rapportage over vestigingen',
            onlinePayments: 'Online betalingsgateways (Razorpay, Stripe, PayPal)',
            whatsapp: 'WhatsApp-meldingen (WATI)',
            bulkImport: 'Bulk CSV-import van leerlingen',
            customBranding: 'Aangepast logo en academienaam in de app'
          },
          activeStudents: 'Actieve leerlingen',
          included: 'Inbegrepen',
          enterpriseOnly: 'Alleen Enterprise',
          proEnterprise: 'Professional / Enterprise (of bladmarkering)'
        },
        payment: {
          title: 'Online betaalgateways',
          subtitle: 'Schakel een of meer gateways in voor wallet-opwaarderingen door leerlingen. Elke academie gebruikt eigen merchantgegevens.',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          stepThree: 'Stap 3',
          gatewaysSection: 'Online betaalgateways',
          gatewaysSectionDesc: 'Configureer merchantgegevens voor wallet-opwaarderingen door leerlingen. Alleen gateways die beschikbaar zijn voor het land van uw academie worden getoond.',
          noGatewaysForCountry: 'Er zijn geen online gateways beschikbaar voor het huidige academieland.',
          saveSettings: 'Instellingen opslaan',
          enabled: 'Ingeschakeld',
          loadFailed: 'Betaalinstellingen laden mislukt.',
          saveSuccess: 'Instellingen betaalgateway opgeslagen.',
          saveFailed: 'Instellingen opslaan mislukt.',
          manualMethods: 'Handmatige betaalmethoden',
          manualMethodsDesc: 'Gebruikt wanneer medewerkers wallet-opwaarderingen registreren. Methoden die in uw land niet worden gebruikt, worden automatisch verborgen.',
          recentActivity: 'Recente betaalactiviteit',
          recentActivityDesc: 'Laatste 50 gatewaygebeurtenissen — nuttig bij het debuggen van webhooks of mislukte opwaarderingen.',
          noEvents: 'Nog geen gatewaygebeurtenissen.',
          when: 'Wanneer',
          gateway: 'Gateway',
          event: 'Gebeurtenis',
          status: 'Status',
          student: 'Leerling',
          detail: 'Detail',
          deploymentNote: 'Deployopmerking',
          licenseRequired: 'Online betaalgateways vereisen een <strong>Professional</strong>- of <strong>Enterprise</strong>-licentie{{edition}}. U kunt hier de instellingen bekijken, maar leerlingen kunnen pas online betalen nadat u upgradet.',
          licenseEditionSuffix: ' (huidig: {{edition}})',
          httpsRequired: 'Stripe en PayPal hebben een openbare HTTPS-site-URL nodig. Huidige basis: <code class="font-mono text-xs">{{url}}</code>. Stel <code class="font-mono">FRONTEND_BASE_URL=https://…</code> in productie in.',
          localhostHttpsHint: 'Ontwikkelings-URL {{url}} is lokaal prima. Voor live Mollie/Stripe stelt u FRONTEND_BASE_URL in op uw openbare https://-academiedomein en registreert u de webhook-URL\'s op elke gatewaykaart.',
          academyCurrency: 'Academievaluta: <strong>{{currency}}</strong> — alleen betaalmethoden voor <strong>{{country}}</strong> worden getoond.',
          yourCountry: 'uw land',
          gatewayActive: '{{name}} is actief. Leerlingen kunnen deze kiezen bij het opwaarderen van hun wallet.',
          gatewayInactive: '{{name}} staat uit. Leerlingen zien deze optie niet.',
          secretKeepPlaceholder: '(al ingesteld — laat leeg om te behouden)',
          secretSetHelper: '✓ Geheim is ingesteld. Laat leeg om de huidige waarde te behouden.',
          secretNotSetHelper: '⚠ Geheim nog niet ingesteld — vereist om betalingen te accepteren.',
          paypalSandbox: 'PayPal-sandboxmodus',
          paypalSandboxHint: 'Gebruik de sandbox-API om te testen met sandboxreferenties van developer.paypal.com.',
          testConnection: 'Verbinding testen',
          testing: 'Testen…',
          connected: 'Verbonden.',
          connectionFailed: 'Verbinding mislukt.',
          connectionTestFailed: 'Verbindingstest mislukt.',
          webhookUrlTitle: 'Webhook-URL (registreer in het {{name}}-dashboard):',
          subscribeTo: 'Abonneer op:',
          keysFrom: 'Sleutels van',
          currencyUnsupported: '{{name}} ondersteunt de academievaluta {{currency}} niet. Wijzig de regionale instellingen of kies een andere gateway.',
          httpsGatewayHint: '{{name}} heeft een openbare HTTPS-URL nodig (stel FRONTEND_BASE_URL in). Localhost is alleen voor ontwikkeling in orde.',
          resolveWarnings: 'Los de waarschuwingen hierboven op voordat {{name}} livebetalingen kan accepteren.',
          secretRequiredWhenEnabled: '⚠ {{label}} is verplicht wanneer {{name}} is ingeschakeld.',
          webhookOptionalHint: 'Optioneel — activeert een ondertekende webhook-back-up als de browser niet terugkeert.',
          manualMethodDefault: 'Medewerkers kunnen met deze methode portemonnee-opwaarderingen registreren.',
          deploymentHttps: 'Stripe en PayPal vereisen een openbare HTTPS-URL. Uw site moet <code class="font-mono">/api/</code> doorsturen naar de backend zodat webhooks <code class="font-mono">{{webhookBase}}/api/webhooks/…</code> bereiken',
          deploymentFrontendUrl: 'Stel <code class="font-mono">FRONTEND_BASE_URL</code> in uw omgeving in voor correcte retour-URL\'s van betalingen.'
        },
        report: {
          title: 'Rapportinstellingen',
          subtitle: 'Kies welke rapporten beschikbaar zijn in het Rapportencentrum en configureer rapportspecifieke invoervelden.',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          stepThree: 'Stap 3',
          availableReports: 'Welke rapporten beschikbaar zijn',
          availableReportsDesc: 'Schakel een rapport uit om het te verbergen in het Rapportencentrum en te blokkeren in de API. Sessierapportlinks voor studenten en ouders volgen dezelfde schakelaar.',
          enabledCountLabel: 'Ingeschakeld',
          statusOn: 'Aan',
          statusOff: 'Uit',
          visibleTo: 'Zichtbaar voor',
          appliesTo: 'Van toepassing op',
          groups: {
            finance: 'Tarieven & geld',
            attendance: 'Aanwezigheid & sessies',
            export: 'Gegevensexport'
          },
          coachRevenue: 'Coachcommissiesplit',
          coachRevenueDesc: 'Academieaandeel per sessietype. De coach ontvangt het restant.',
          batchAcademyShare: 'Groepssessies — academieaandeel (%)',
          individualAcademyShare: 'Individuele sessies — academieaandeel (%)',
          splitNote: 'Verdeling: Coach {{coach}}% / Academie {{academy}}%',
          batchAttendance: 'Opties groepsbijwoning',
          batchAttendanceDesc: 'Bepaalt wat coaches en managers zien in de groepsbijwoningsmatrix. Beheerders zien altijd sessiekosten.',
          showCharges: 'Toon sessiekosten aan coaches en managers',
          showChargesDesc: 'Indien ingeschakeld, zien coaches en managers tarieven per sessie en periodetotalen in groepsgewijze aanwezigheidsrapporten, Excel-exports en PDF\'s.',
          saveSettings: 'Instellingen opslaan',
          loadFailed: 'Rapportinstellingen laden mislukt.',
          saveSuccess: 'Rapportinstellingen succesvol opgeslagen.',
          saveFailed: 'Rapportinstellingen opslaan mislukt.',
          percentError: 'Voer een percentage tussen 0 en 100 in.',
          percentRange: 'Moet tussen 0 en 100 liggen.'
        },
        holidays: {
          title: 'Vakantiebeheer',
          subtitle: 'Blokkeer academiebrede of vestigingsspecifieke gesloten dagen. Sessies kunnen niet op vakantiedagen worden gepland.',
          loadingSubtitle: 'Vakanties laden…',
          stepOne: 'Stap 1',
          stepTwo: 'Stap 2',
          saveSuccessTitle: 'Vakantie opgeslagen',
          saveSuccessWithEmails: 'De vakantie is opgeslagen en er worden notificatie-e-mails verzonden naar alle relevante medewerkers.',
          saveSuccessWithoutEmails: 'De vakantie is opgeslagen zonder notificatie-e-mails te verzenden.',
          saveHolidayFailed: 'Vakantie kon niet worden opgeslagen.',
          deleteTitle: 'Vakantie verwijderen?',
          deletePastTitle: 'Verstreken vakantie verwijderen?',
          deleteMessage: '"{{name}}" wordt verwijderd. Op die data kunnen opnieuw sessies worden gepland. Als medewerkers al waren geïnformeerd, laat het hen indien nodig handmatig weten.',
          deletePastMessage: '"{{name}}" is al afgelopen. Verwijderen werkt alleen deze lijst en de kalender bij. Eerdere sessies en aanwezigheid worden niet gewijzigd.',
          deleteHint: 'Vakantie verwijderen en plannen op deze data toestaan',
          deletePastHint: 'Afgelopen vakantie uit de lijst verwijderen',
          thisHoliday: 'deze vakantie',
          addNew: 'Nieuwe vakantie toevoegen',
          addNewDesc: 'Definieer een gesloten periode voor de hele academie of één vestiging.',
          holidayName: 'Vakantienaam',
          startDate: 'Startdatum',
          endDate: 'Einddatum',
          branch: 'Vestiging voor vakantie',
          branchHelper: 'Kies alleen een vestiging als deze vakantie uitsluitend voor die locatie geldt.',
          allBranches: 'Alle vestigingen (academiebreed)',
          notes: 'Vakantieopmerkingen',
          addHoliday: 'Vakantie toevoegen',
          configured: 'Geconfigureerde vakanties',
          total: '{{count}} totaal',
          upcomingPast: '· {{upcoming}} aankomend · {{past}} verstreken',
          colName: 'Vakantienaam',
          colFrom: 'Van',
          colTo: 'Tot',
          colBranch: 'Vestiging',
          colActions: 'Acties',
          noHolidays: 'Nog geen vakanties geconfigureerd.',
          allBranchesBadge: 'Alle vestigingen',
          past: 'Verstreken',
          delete: 'Verwijderen',
          loadFailed: 'Vakanties laden mislukt.',
          nameRequired: 'Vakantienaam is verplicht.',
          datesRequired: 'Startdatum en einddatum zijn verplicht.',
          validDatesFormat: 'Voer geldige data in het formaat {{format}} in.',
          previewFailed: 'E-mailvoorbeeld kon niet worden voorbereid.',
          deleteFailed: 'Vakantie kon niet worden verwijderd.',
          endDateInvalid: 'Einddatum moet op of na de startdatum liggen.',
          notesPlaceholder: 'Optionele interne notitie',
          namePlaceholder: 'bijv. Zomerstop, Diwali-vakantie',
          emailPreview: {
            title: 'Vakantienotificatie-e-mail controleren',
            subtitle: 'Bekijk de e-mail hieronder en kies of u {{count}} relevante ontvanger wilt informeren (medewerkers en studenten waar van toepassing).',
            subtitle_other: 'Bekijk de e-mail hieronder en kies of u {{count}} relevante ontvangers wilt informeren (medewerkers en studenten waar van toepassing).',
            subjectLabel: 'Onderwerp',
            previewLabel: 'E-mailvoorbeeld',
            previewHelper: 'Elke ontvanger krijgt een persoonlijke begroeting; het voorbeeld toont een voorbeeldlay-out.',
            recipientsLabel: 'Ontvangers ({{count}})',
            noRecipients: 'Er zijn geen actieve beheerders, managers, coaches of studenten met e-mailadressen gevonden. U kunt de vakantie nog steeds opslaan zonder notificaties te verzenden.',
            roleBlockTitle: '{{role}} ({{count}})',
            roleSummaryItem: '{{count}} {{role}}',
            roleSummaryItem_other: '{{count}} {{role}}',
            studentBulkNotice: '{{count}} student met e-mailadres ontvangt dit bericht.',
            studentBulkNotice_other: '{{count}} studenten met e-mailadressen ontvangen dit bericht.',
            moreStudentsHidden: '…en nog {{count}} student hier niet getoond.',
            moreStudentsHidden_other: '…en nog {{count}} studenten hier niet getoond.',
            saveWithoutEmails: 'Opslaan zonder e-mails',
            saveAndSend: 'Opslaan en e-mails verzenden',
            roles: {
              Admin: 'Beheerder',
              Manager: 'Beheerder',
              Coach: 'Coach',
              Student: 'Leerling'
            }
          }
        }
      },
      forms: {
        common: {
          firstName: 'Voornaam',
          middleName: 'Tussennaam',
          lastName: 'Achternaam',
          dateOfBirth: 'Geboortedatum',
          gender: 'Geslacht',
          male: 'Man',
          female: 'Vrouw',
          other: 'Anders',
          loginUsername: 'Gebruikersnaam',
          emailAddress: 'E-mailadres',
          phoneNumber: 'Telefoonnummer',
          accountActive: 'Account actief',
          loginPassword: 'Wachtwoord',
          confirmLoginPassword: 'Wachtwoord bevestigen',
          roles: 'Rollen',
          select: 'Selecteren…',
          cancel: 'Annuleren',
          save: 'Opslaan',
          saving: 'Opslaan…',
          create: 'Aanmaken',
          update: 'Bijwerken',
          none: '— Geen —'
        },
        placeholders: {
          firstName: 'Voornaam',
          middleName: 'Tussennaam',
          lastName: 'Achternaam',
          firstNameRequired: 'Voornaam *',
          lastNameRequired: 'Achternaam *',
          username: 'Gebruikersnaam',
          email: 'E-mail',
          password: 'Wachtwoord',
          confirmPassword: 'Wachtwoord bevestigen'
        },
        validation: {
          firstNameRequired: 'Voornaam is verplicht en mag letters, spaties, koppeltekens en apostroffen bevatten.',
          middleNameLetters: 'Tussennaam mag letters, spaties, koppeltekens en apostroffen bevatten.',
          firstNameLetters: 'Voornaam mag letters, spaties, koppeltekens en apostroffen bevatten — geen cijfers.',
          lastNameLetters: 'Achternaam mag letters, spaties, koppeltekens en apostroffen bevatten — geen cijfers.',
          lastNameRequired: 'Achternaam is verplicht en mag letters, spaties, koppeltekens en apostroffen bevatten.',
          invalidDob: 'Voer een geldige geboortedatum in het verleden in.',
          usernameRequired: 'Gebruikersnaam is verplicht.',
          emailInvalid: 'Voer een geldig e-mailadres in.',
          passwordMin: 'Wachtwoord moet minstens 6 tekens bevatten.',
          passwordsMismatch: 'Wachtwoord en bevestiging komen niet overeen.',
          rolesRequired: 'Selecteer minstens één rol.',
          invalidAddress: 'Ongeldig adres.',
          invalidLatitude: 'Breedtegraad moet een geldig getal zijn.',
          invalidLongitude: 'Lengtegraad moet een geldig getal zijn.'
        },
        phone: {
          countryCode: 'Landcode',
          searchCountry: 'Land zoeken',
          noMatches: 'Geen resultaten',
          phoneNumber: 'Telefoonnummer'
        },
        address: {
          title: 'Adres',
          addressLine1: 'Adresregel 1',
          addressLine2: 'Adresregel 2',
          addressLine2Placeholder: 'Appartement / Verdieping / Gebouw (optioneel)',
          country: 'Land',
          provinceRegion: 'Provincie / Regio',
          cityMunicipality: 'Plaats / Gemeente',
          postcode: 'Postcode',
          latitude: 'Breedtegraad',
          longitude: 'Lengtegraad',
          optional: '(optioneel)',
          selectCountry: 'Land selecteren',
          selectCountryFirst: 'Selecteer eerst een land',
          selectProvince: 'Provincie / Regio selecteren',
          noRegions: 'Geen regio\'s voor dit land',
          selectProvinceFirst: 'Selecteer eerst een provincie / regio',
          loadingCities: 'Plaatsen laden...',
          searchCity: 'Plaats zoeken',
          saved: '(opgeslagen)',
          noCityList: 'Geen plaatsenlijst voor deze regio — u kunt de plaatsnaam typen.',
          fillHint: 'Als u een adresveld invult, vul dan minstens <strong>Land</strong> en <strong>Plaats</strong> in. Postcode, provincie en lat/lng zijn optioneel.',
          countryCityRequired: 'Als u een adres invult, vul dan minstens land en plaats in.',
          streetPlaceholder: 'Straatadres',
          egExample: 'bijv. {{example}}'
        },
        student: {
          addTitle: 'Student toevoegen',
          editTitle: 'Student bewerken',
          subtitle: 'Leg studentgegevens, toegang en adres vast in één overzichtelijke workflow.',
          fideSection: 'FIDE / rating (optioneel)',
          fideId: 'FIDE-ID',
          eloRating: 'Elo-rating',
          fideTitle: 'FIDE-titel',
          createButton: 'Student aanmaken',
          updateButton: 'Student bijwerken',
          managerRolesNote: 'Managers kunnen alleen studentaccounts aanmaken of bewerken.',
          updated: 'Student bijgewerkt',
          created: 'Student aangemaakt',
          updateFailed: 'Student bijwerken mislukt.',
          saveFailed: 'Student opslaan mislukt.',
          loadArchiveFailed: 'Archiefopties laden mislukt.',
          enrollmentsEnded: 'Inschrijvingen beëindigd. Het studentaccount blijft actief.',
          archived: 'Student gearchiveerd.',
          archiveFailed: 'Student archiveren mislukt.',
          activeDesc: 'Gearchiveerde studenten blijven in de geschiedenis en kunnen later opnieuw worden geactiveerd.',
          activeDescArchive: 'Schakel uit om deze student te archiveren. Bij actieve inschrijvingen kiest u hoe die eindigen.'
        },
        coach: {
          addTitle: 'Coach toevoegen',
          editTitle: 'Coach bewerken',
          subtitle: 'Beheer coachidentiteit, logintoegang en profielgegevens met dezelfde verzorgde workflow als elders in het product.',
          createButton: 'Coach aanmaken',
          updateButton: 'Coach bijwerken',
          activeDesc: 'Gebruik dit om de coach te archiveren of opnieuw te activeren zonder de geschiedenis te verliezen.',
          updated: 'Coach bijgewerkt',
          created: 'Coach aangemaakt',
          updateFailed: 'Coach bijwerken mislukt.',
          saveFailed: 'Coach opslaan mislukt.'
        },
        manager: {
          addTitle: 'Manager toevoegen',
          editTitle: 'Manager bewerken',
          subtitle: 'Houd de managerinrichting overzichtelijk en consistent met de verbeterde productbrede formuliervaring.',
          createButton: 'Manager aanmaken',
          updateButton: 'Manager bijwerken',
          updated: 'Manager bijgewerkt',
          created: 'Manager aangemaakt',
          saveFailed: 'Manager opslaan mislukt.',
          activeDesc: 'Managers kunnen veilig worden gearchiveerd zonder hun gegevens te verwijderen.'
        },
        user: {
          addTitle: 'Gebruiker aanmaken',
          editTitle: 'Gebruiker bewerken',
          subtitle: 'Beheer accountgegevens, rollen en toegang van gebruikers.',
          emailSignInLink: 'Inloglink per e-mail (zij stellen hun eigen wachtwoord in)',
          createButton: 'Gebruiker aanmaken',
          updateButton: 'Gebruiker bijwerken',
          loadFailed: 'Gebruiker laden mislukt.',
          saveFailed: 'Gebruiker opslaan mislukt. Controleer de invoer.'
        },
        branch: {
          addTitle: 'Vestiging toevoegen',
          editTitle: 'Vestiging bewerken',
          subtitle: 'Maak of werk vestigingsgegevens bij met duidelijkere secties, betere spatiëring en een premiumer UI.',
          branchName: 'Vestigingsnaam',
          branchCode: 'Vestigingscode',
          branchMode: 'Vestigingsmodus',
          managerOptional: 'Manager (optioneel)',
          branchActive: 'Vestiging actief',
          branchActiveDesc: 'Schakel deze vestiging uit om te archiveren met behoud van alle gerelateerde records.',
          branchAddress: 'Vestigingsadres',
          onlineNote: 'Fysiek adres is verborgen voor uitsluitend online-vestigingen.',
          createButton: 'Vestiging aanmaken',
          updateButton: 'Vestiging bijwerken',
          nameRequired: 'Vestigingsnaam is verplicht.',
          codeRequired: 'Vestigingscode is verplicht.',
          modeRequired: 'Modus is verplicht.',
          updateSuccess: 'Vestiging succesvol bijgewerkt',
          addSuccess: 'Vestiging succesvol toegevoegd',
          namePlaceholder: 'bijv. Hoofdvestiging',
          uniqueCodePlaceholder: 'Unieke code',
          loadManagersFailed: 'Managers laden mislukt',
          updateFailed: 'Vestiging bijwerken mislukt.',
          saveFailed: 'Vestiging opslaan mislukt.'
        },
        batch: {
          addTitle: 'Groep aanmaken',
          editTitle: 'Groep bewerken',
          subtitle: 'Configureer groepsrooster, tarieven en sessie-instellingen.',
          branch: 'Vestiging voor groep',
          level: 'Niveau',
          coach: 'Toegewezen coach',
          name: 'Groepsnaam',
          sessionMode: 'Groepssessiemodus',
          defaultStart: 'Standaardtijd start (UU:mm)',
          defaultEnd: 'Standaardtijd einde (UU:mm)',
          selectStartTime: 'Starttijd selecteren',
          selectEndTime: 'Eindtijd selecteren',
          billingConfig: 'Factureringsconfiguratie',
          chargesMode: 'Factureringsmodus',
          sessionCharge: 'Sessietarief ({{currency}})',
          periodLength: 'Standaardperiodelengte',
          periodFee: 'Periodetarief ({{currency}})',
          carryForward: 'Overdrachtsbeleid',
          selectBranchFirst: 'Selecteer eerst een vestiging',
          selectEllipsis: 'Selecteren…',
          notePerSession: 'Per sessie apart in rekening brengen',
          notePerSessionHourly: 'Uurtarief × sessieduur',
          notePerPeriod: 'Vast tarief voor een inschrijvingsperiode met datumbereik',
          periodNoCarryForward: 'Sessie-overdracht geldt niet voor groepen met periodiefacturering. Ongebruikte sessies worden niet overgedragen tussen factureringsperioden.',
          validation: {
            selectBranch: 'Selecteer een vestiging',
            selectLevel: 'Selecteer een niveau',
            selectCoach: 'Selecteer een coach',
            selectSessionMode: 'Selecteer een sessiemodus',
            enterName: 'Voer een naam voor de groep in',
            selectSessionDay: 'Selecteer minstens één sessiedag',
            selectDefaultStart: 'Selecteer de standaardstarttijd',
            selectDefaultEnd: 'Selecteer de standaardeindtijd',
            endAfterStart: 'Standaardeindtijd moet na de standaardstarttijd liggen',
            saveFailed: 'Groep opslaan mislukt'
          }
        },
        level: {
          addTitle: 'Niveau toevoegen',
          editTitle: 'Niveau bewerken',
          subtitle: 'Definieer niveau-naam en weergavevolgorde voor groepen en inschrijvingen.',
          saveFailed: 'Opslaan mislukt'
        }
      },
      enrollments: {
        title: 'Inschrijvingen',
        subtitle: 'Beheer studentinschrijvingen, periodetarieven en verlengingen',
        newEnrollment: 'Nieuwe inschrijving',
        editEnrollment: 'Inschrijving bewerken',
        formSubtitle: 'Schrijf een student in voor een groep met tariefplan en factureringsopties.',
        validation: {
          loadEnrollmentFailed: 'Inschrijvingsgegevens laden mislukt',
          loadFailed: 'Gegevens laden mislukt',
          selectAtLeastOneStudent: 'Selecteer minstens één student',
          carryForwardRequired: 'Overdrachtsbeslissing is verplicht voor deze groep',
          endDateRequired: 'Einddatum is verplicht voor inschrijvingen met periodiefacturering',
          saveFailed: 'Inschrijving opslaan mislukt'
        },
        entityLabel: 'inschrijvingen',
        searchPlaceholder: 'Zoek inschrijvingen op ID, studentnaam, groep of niveau...',
        noMatch: 'Geen inschrijvingen komen overeen met uw zoekopdracht',
        empty: 'Geen inschrijvingen gevonden',
        emptyHint: 'Probeer uw zoektermen aan te passen',
        filters: {
          all: 'Alle',
          active: 'Actief',
          archived: 'Gearchiveerd'
        },
        columns: {
          id: 'ID',
          student: 'Leerling',
          level: 'Niveau',
          batch: 'Groep',
          billing: 'Facturering',
          period: 'Periode',
          payment: 'Betaling',
          expiry: 'Vervaldag',
          status: 'Status',
          actions: 'Acties'
        },
        periodOngoing: 'lopend',
        expiresToday: 'Vervalt vandaag',
        daysLeft: 'Nog {{count}}d',
        expired: 'Verlopen',
        notApplicable: 'n.v.t.',
        renew: 'Verlengen',
        renewTitle: 'Inschrijving voor volgende periode aanmaken',
        view: 'Bekijken',
        viewTitle: 'Inschrijvingsdetails bekijken',
        edit: 'Bewerken',
        editTitle: 'Inschrijving bewerken',
        archive: 'Archiveren',
        activate: 'Activeren',
        reactivateConfirm: 'Inschrijving #{{id}} opnieuw activeren? Alleen mogelijk als vandaag binnen de inschrijvingsperiode valt.',
        archiveFailed: 'Archiveren mislukt.',
        cannotArchive: 'U kunt deze inschrijving niet archiveren.',
        crossBranchWarning: 'Waarschuwing kruisvestiging',
        crossBranchEnrollment: 'Inschrijving #{{id}}',
        crossBranchDesc: '<strong>{{name}}</strong> is ook actief ingeschreven in vestigingen die <span class="text-orange-600 font-medium">niet door u worden beheerd</span>:',
        continueArchive: 'Doorgaan met archiveren',
        statusActive: 'Actief',
        statusArchived: 'Gearchiveerd',
        form: {
          student: 'Leerling',
          selectStudents: 'Studenten selecteren ({{count}} geselecteerd)',
          searchStudents: 'Typ om studenten te zoeken...',
          selectAll: 'Alles selecteren ({{count}})',
          clear: 'Wissen',
          level: 'Niveau',
          trainingBatch: 'Trainingsgroep',
          select: 'Selecteren...',
          startDate: 'Startdatum inschrijving',
          startDateHint: 'Datum waarop de student officieel bij deze groep aansluit',
          endDate: 'Einddatum inschrijving',
          optional: '(Optioneel)',
          endDatePeriodHint: 'Standaard uit groepsplan ({{interval}}); u mag aanpassen.',
          endDateOpenHint: 'Laat leeg voor een open einde inschrijving',
          carryForwardPolicy: 'Beleid sessie-overdracht',
          batchPolicy: 'Groepsbeleid:',
          setCarryForward: 'Overdracht voor deze student instellen:',
          enableCarryForward: 'Overdracht inschakelen',
          disableCarryForward: 'Overdracht uitschakelen',
          carryForwardRequired: 'Deze groep vereist dat u het overdrachtsbeleid per student expliciet instelt.',
          carryForwardBatchLevel: 'Overdracht wordt op groepsniveau beheerd',
          overrideBilling: 'Factureringstarieven overschrijven (optioneel)',
          hide: 'Verbergen',
          show: 'Tonen',
          billingMode: 'Factureringsmodus inschrijving',
          billingPeriod: 'Factureringsperiode',
          discountOff: '{{value}} korting',
          periodFee: 'Periodetarief',
          perSessionCharge: 'Tarief per sessie',
          hourlyRate: 'Uurtarief',
          periodFeeAmount: 'Bedrag periodetarief',
          useBatchDefault: 'Groepsstandaard gebruiken',
          notConfigured: 'Niet geconfigureerd',
          pricingSummary: 'Prijsoverzicht',
          batchDefaultPricing: 'Standaardprijzen groep',
          chargesMode: 'Tariefmodus:',
          perSession: 'Per sessie:',
          periodPlan: 'Periodeplan:',
          applyDiscount: 'Korting toepassen',
          applyDiscountCustom: 'Korting toepassen (wordt toegepast op aangepaste tarieven)',
          noDiscount: 'Geen korting toegepast',
          discountApplied: '{{value}}% korting wordt toegepast',
          discountAppliedCustom: ' op uw aangepaste prijzen',
          customChargesApplied: 'Aangepaste tarieven toegepast',
          customChargesDesc: 'Aangepaste prijzen voor deze student (overschrijft groepsstandaarden)',
          customChargesDiscount: '✓ {{value}}% "{{name}}"-korting wordt bovenop de aangepaste tarieven toegepast',
          finalCharges: 'Eindtarieven voor deze student',
          pricingModel: 'Prijsmodel:',
          perHour: 'Per uur:',
          updateEnrollment: 'Inschrijving bijwerken',
          createEnrollment: 'Inschrijving aanmaken',
          saving: 'Inschrijving opslaan…',
          enrolling: 'Inschrijven {{current}} van {{total}}',
          preparing: 'Voorbereiden…',
          redirecting: 'Doorverwijzen naar inschrijvingslijst...',
          successCount: '✅ {{count}} student succesvol ingeschreven',
          successCount_other: '✅ {{count}} studenten succesvol ingeschreven',
          failedCount: '❌ {{count}} student kon niet worden ingeschreven',
          failedCount_other: '❌ {{count}} studenten konden niet worden ingeschreven',
          renewBanner: 'Verlengen vanaf inschrijving #{{id}}. Datums staan standaard op de volgende periode — pas indien nodig aan.'
        },
        archiveModal: {
          title: 'Inschrijving archiveren',
          description: 'Stel de einddatum in voor {{name}} (inschrijving #{{id}}).',
          enrolledOn: ' Ingeschreven op {{date}}.',
          endDate: 'Einddatum',
          archiving: 'Archiveren…'
        },
        paymentModal: {
          title: 'Periodetarief & portemonnee',
          enrolling: '{{name}} inschrijven',
          review: 'Controleer de betaling voordat u de inschrijving afrondt',
          periodFee: 'Periodetarief:',
          plan: 'Plan:',
          walletBalance: 'Portemonneesaldo:',
          insufficient: '(onvoldoende voor volledig tarief)',
          amountNeeded: 'Benodigd bedrag:',
          topUp: 'Portemonnee opwaarderen',
          refreshing: 'Vernieuwen…',
          refreshBalance: 'Saldo vernieuwen',
          paymentRequired: 'Uw academie vereist betaling vóór aanwezigheid. Breng de portemonnee nu in rekening of waardeer eerst op.',
          unpaidPriorTitle: 'Periodetarief vorige periode nog onbetaald',
          unpaidPriorDesc: 'Inschrijving #{{id}} ({{period}}) heeft een openstaand saldo van {{amount}}.',
          unpaidPriorAck: 'Ik begrijp dit en wil doorgaan met deze nieuwe inschrijving.',
          insufficientHelp: 'De portemonnee dekt het volledige periodetarief niet. Kies hoe u de inning wilt afhandelen. We proberen automatisch opnieuw volgens een dagschema en wanneer sessies zijn afgerond.',
          collectLegend: 'Hoe moeten we het tarief innen?',
          chargeNow: 'Portemonnee nu belasten',
          collectViaSchedule: 'Innen over de komende dagen',
          enrollUnpaid: 'Inschrijven zonder nu te belasten',
          chargeNowHelp: 'Het periodetarief wordt meteen van de portemonnee van de student afgeschreven.',
          chargeNowInsufficient: 'De portemonnee wordt onmiddellijk belast. Waardeer eerst op, of schakel een negatief portemonneesaldo in bij de factureringsinstellingen.',
          collectViaScheduleHelp: 'We proberen het tarief tot enkele dagen dagelijks te innen, en opnieuw wanneer sessies zijn afgerond. De student mag deelnemen volgens de respijtregels van uw academie.',
          enrollUnpaidHelp: 'De inschrijving wordt aangemaakt zonder directe afschrijving. Inning volgt de geplande herhaalpogingen van uw academie.',
          confirmEnroll: 'Bevestigen en inschrijven'
        },
        lockReasons: {
          student: 'Deze inschrijving hoort bij deze student en kan niet naar iemand anders worden overgezet.',
          level: 'Het niveau is vastgelegd toen deze student zich aansloot en kan hier niet worden gewijzigd. Om naar een ander niveau te gaan, beëindig deze inschrijving en maak een nieuwe aan.',
          batch: 'De trainingsgroep is vastgelegd toen deze student zich aansloot en kan hier niet worden gewijzigd. Om naar een andere groep te gaan, beëindig deze inschrijving en maak een nieuwe aan.',
          startDate: 'De startdatum is opgeslagen bij het aanmaken van deze inschrijving en kan daarna niet meer worden gewijzigd.',
          endDateEditable: 'Stel in of werk bij wanneer deze student moet stoppen met deze groep. Reeds gehouden of gefactureerde sessies worden niet beïnvloed.',
          carryForward: 'Deze overdrachtskeuze is bij inschrijving gemaakt en kan hier niet worden gewijzigd.',
          carryForwardBatch: 'Overdracht voor deze groep is hetzelfde voor alle studenten en wordt beheerd in de groepsinstellingen.',
          billingMode: 'Het factureringstype voor deze inschrijving kan na inschrijving niet meer worden gewijzigd.',
          billingAmount: 'Dit tariefbedrag kan niet worden gewijzigd op een bestaande inschrijving.',
          customRateEditable: 'Werkt alleen het aangepaste tarief voor toekomstige sessies bij. Reeds afgenomen kosten blijven zoals ze waren.',
          customRateLocked: 'Aangepaste prijzen kunnen alleen bij de eerste inschrijving worden ingesteld, niet bij bewerken.',
          discount: 'De korting op deze inschrijving kan na inschrijving niet meer worden gewijzigd.',
          systemManaged: 'Deze inschrijving is automatisch aangemaakt voor een eenmalige sessie en kan niet worden bewerkt.'
        }
      },
      sessions: {
        title: 'Sessies',
        subtitle: 'Filter op niveau en groep. Markeer aanwezigheid, bewerk of maak sessies.',
        addWalkIn: 'Walk-in-sessie toevoegen',
        addWalkInTitle: 'Een eenmalige sessie voor een walk-in-student aanmaken',
        addIndividual: 'Individuele sessie toevoegen',
        addIndividualTitle: 'Individuele één-op-één-sessie aanmaken',
        addBatch: 'Groepssessies toevoegen',
        addBatchTitle: 'Groepssessies toevoegen',
        selectBatchTitle: 'Selecteer een groep om een sessie aan te maken',
        showingOnDate: 'Sessies tonen op',
        selectLevelBatch: ' — selecteer een niveau en groep om sessies te laden',
        clearDateFilter: 'Datumfilter wissen',
        filterLevel: 'Filteren op niveau',
        filterBatch: 'Filteren op groep',
        selectLevel: 'Niveau selecteren…',
        selectBatch: 'Groep selecteren…',
        selectLevelFirst: 'Selecteer eerst een niveau',
        sessionStatus: 'Sessiestatus',
        allStatuses: 'Alle statussen',
        sortBy: 'Sorteren op',
        sortDateDesc: 'Datum (nieuwste eerst)',
        sortDateAsc: 'Datum (oudste eerst)',
        sortStatus: 'Status (In behandeling → Voltooid)',
        sortIdDesc: 'Sessie-ID (hoog naar laag)',
        sortIdAsc: 'Sessie-ID (laag naar hoog)',
        searchSessions: 'Sessies zoeken',
        searchPlaceholder: 'Sessies zoeken...',
        showingResults: '{{count}} resultaat weergegeven',
        showingResults_other: '{{count}} resultaten weergegeven',
        statusFilter: 'Status:',
        clearFilters: 'Filters wissen',
        selectLevelBatchEmpty: 'Selecteer een niveau en groep',
        selectLevelBatchHint: 'Kies uit de filters hierboven om sessies te bekijken',
        noSessions: 'Geen sessies gevonden',
        noSessionsHint: 'Maak uw eerste sessie voor deze groep',
        sessionNumber: 'Sessie #{{id}}',
        walkIn: 'Inloop',
        level: 'Niveau',
        batch: 'Groep',
        coach: 'Coach',
        notAssigned: 'Niet toegewezen',
        statusControl: 'Statusbeheer',
        statusPending: 'In behandeling',
        statusCompleted: 'Voltooid',
        statusCancelled: 'Geannuleerd',
        completedLocked: 'Voltooide sessies kunnen niet worden gewijzigd',
        cancelledLocked: 'Geannuleerde sessies kunnen niet worden gewijzigd',
        saveStatus: 'Status opslaan',
        save: 'Opslaan',
        saving: 'Opslaan…',
        viewAttendance: 'Aanwezigheid bekijken',
        viewAttendanceCancelled: 'Aanwezigheid van geannuleerde sessie bekijken',
        viewAttendanceCompleted: 'Ingevulde aanwezigheid van deze voltooide sessie bekijken',
        updateNotes: 'Sessienotities bijwerken',
        updateNotesTitle: 'Sessienotities/onderwerp bijwerken',
        reverse: 'Terugdraaien',
        reversing: 'Terugdraaien...',
        reverseTitle: 'Deze voltooide sessie terugdraaien en studenten crediteren',
        markAttendance: 'Aanwezigheid markeren',
        markAttendanceTitle: 'Aanwezigheid voor deze sessie markeren',
        viewAttendanceSession: 'Aanwezigheid voor deze sessie bekijken',
        attendanceCompletedLocked: 'Aanwezigheid kan niet worden gemarkeerd voor voltooide sessies',
        attendanceCancelledLocked: 'Aanwezigheid kan niet worden gemarkeerd voor geannuleerde sessies',
        edit: 'Bewerken',
        editTitle: 'Sessiedetails bewerken',
        pagination: '{{start}} tot {{end}} van {{total}} sessies weergegeven',
        rowsPerPage: 'Rijen per pagina:',
        loadFailed: 'Sessies laden mislukt.',
        loadDateFailed: 'Sessies voor geselecteerde datum laden mislukt.',
        markBeforeComplete: 'Markeer de aanwezigheid voordat u de sessie als VOLTOOID markeert.',
        completeSuccess: 'Sessie gemarkeerd als VOLTOOID. Studentfacturering is verwerkt.',
        completeFailed: 'Sessie afronden en facturering verwerken mislukt:\n\n{{message}}\n\nZorg dat studenten voldoende portemonneesaldo hebben en probeer het opnieuw.',
        statusUpdateFailed: 'Sessiestatus bijwerken mislukt: {{message}}',
        reversalReasonRequired: 'Reden voor terugdraaien is verplicht.',
        reversalConfirm: 'Het ongedaan maken van deze sessie zal:\\n- Bedragen terugboeken naar de portemonnees van leerlingen\\n- De sessie markeren als CANCELLED\\n\\nWeet u zeker dat u wilt doorgaan?',
        reversalSuccess: 'Sessie succesvol ongedaan gemaakt.',
        reversalFailed: 'Sessie ongedaan maken mislukt: {{message}}',
        walkInCreated: 'Walk-in sessie succesvol aangemaakt!\\nSessie-ID: {{sessionId}}\\nInschrijving-ID: {{enrollmentId}}',
        individualCreated: 'Individuele sessie succesvol aangemaakt!\\nSessie-ID: {{sessionId}}\\nGroep: {{batch}}\\nLeerling: {{student}}\\nKosten: {{charge}}',
        reversalModal: {
          title: 'Sessie #{{id}} ongedaan maken',
          willDo: 'Deze actie zal:',
          creditWallets: 'Bedragen terugboeken naar de portemonnees van alle leerlingen',
          markCancelled: 'De sessie markeren als CANCELLED',
          compensatingTxns: 'Compenserende portemonneetransacties aanmaken',
          recordReason: 'De reden van terugboeking vastleggen voor audit',
          reasonLabel: 'Reden van terugboeking',
          reasonPlaceholder: 'bijv. Sessie geannuleerd wegens onbeschikbaarheid van de coach...',
          confirm: 'Terugboeking bevestigen'
        },
        form: {
          editTitle: 'Sessie bewerken',
          addTitle: 'Groepssessies toevoegen',
          editSubtitle: 'Sessiedetails bijwerken',
          addSubtitle: 'Terugkerende sessies plannen voor uw groep',
          close: 'Sluiten',
          trainingBatch: 'Trainingsgroep',
          selectBatch: 'Selecteer groep...',
          sessionCoach: 'Sessiecoach',
          selectCoach: 'Selecteer coach...',
          autoSelected: 'Automatisch geselecteerd',
          sessionTiming: 'Sessietijden',
          startTime: 'Begintijd sessie',
          endTime: 'Eindtijd sessie',
          selectStartTime: 'Selecteer begintijd',
          selectEndTime: 'Selecteer eindtijd',
          sessionDate: 'Sessiedatum',
          dateRange: 'Datumbereik',
          date: 'Datum ({{format}})',
          from: 'Van ({{format}})',
          to: 'Tot ({{format}})',
          selectDays: 'Selecteer dagen voor herhaling',
          selected: 'Geselecteerd:',
          update: 'Bijwerken',
          create: 'Aanmaken',
          updating: 'Bijwerken...',
          creating: 'Aanmaken...',
          fillRequired: 'Vul alle verplichte velden in.',
          endAfterStart: 'Eindtijd moet na begintijd liggen',
          selectDay: 'Selecteer ten minste één dag.',
          invalidDateRange: 'Ongeldig datumbereik.',
          invalidDate: 'Ongeldige datum.',
          updated: 'Sessie succesvol bijgewerkt.',
          created: '{{count}} sessie(s) aangemaakt.',
          updateFailed: 'Sessie bijwerken mislukt.',
          createFailed: 'Sessies aanmaken mislukt.',
          longSessionWarning: '⚠️ Waarschuwing lange sessieduur\\n\\nSessiedatum(s): {{dates}}\\nBegintijd: {{start}}\\nEindtijd: {{end}}\\nDuur: {{duration}}\\n\\nDeze sessieduur bedraagt meer dan 3 uur. Wilt u doorgaan?',
          hour: '{{count}} uur',
          hour_other: '{{count}} uur',
          minute: '{{count}} minuut',
          minute_other: '{{count}} minuten'
        },
        walkInModal: {
          title: 'Walk-in sessie toevoegen',
          info: 'Walk-in sessie:',
          infoBody: 'Maakt een eenmalige sessie aan voor een leerling die niet regelmatig is ingeschreven. Het systeem maakt indien nodig automatisch een inschrijving en groep aan.',
          students: 'Leerlingen',
          searchStudents: 'Zoek en selecteer leerlingen...',
          noStudentsFound: 'Geen leerlingen gevonden',
          allStudentsSelected: 'Alle leerlingen geselecteerd',
          searchHint: 'Typ om te zoeken en één of meer leerlingen te selecteren',
          sessionDetails: 'Sessiedetails',
          selectLevel: 'Selecteer niveau...',
          sessionCoach: 'Sessiecoach',
          selectCoach: 'Selecteer coach...',
          coachAutoNote: 'Automatisch geselecteerd (coaches kunnen alleen sessies voor zichzelf aanmaken)',
          timing: 'Tijden',
          sessionDate: 'Sessiedatum',
          startTime: 'Begintijd',
          endTime: 'Eindtijd',
          selectTime: 'Selecteer tijd',
          sessionCharge: 'Sessiekosten ({{symbol}})',
          enterAmount: 'Voer bedrag in',
          chargeNote: 'Dit is het totale bedrag voor de gehele sessieduur, geen uurtarief.',
          creating: 'Aanmaken...',
          create: 'Walk-in sessie aanmaken',
          fillRequired: 'Vul alle verplichte velden in en selecteer ten minste één leerling',
          endAfterStart: 'Eindtijd moet na begintijd liggen',
          createFailed: 'Walk-in sessie aanmaken mislukt',
          longSessionTitle: 'Waarschuwing lange sessieduur',
          longSessionProceed: 'Deze sessieduur bedraagt meer dan 3 uur. Wilt u doorgaan?',
          sessionDateLabel: 'Sessiedatum:',
          startTimeLabel: 'Begintijd:',
          endTimeLabel: 'Eindtijd:',
          durationLabel: 'Duur:',
          userFallback: 'Gebruiker #{{id}}',
          idLabel: 'ID: #{{id}}'
        },
        individualModal: {
          title: 'Individuele sessie toevoegen',
          autoActions: 'Automatische acties:',
          autoPresent: 'Aanwezigheid wordt automatisch gemarkeerd als PRESENT',
          autoCharge: 'Sessiekosten worden onmiddellijk van de portemonnee van de leerling afgeschreven',
          autoCompleted: 'Sessiestatus wordt ingesteld op COMPLETED (voor boekhoudkundige doeleinden)',
          sessionDetails: 'Sessiedetails',
          selectLevel: 'Selecteer niveau',
          studentName: 'Naam leerling',
          selectStudent: 'Selecteer leerling',
          sessionCoach: 'Sessiecoach',
          selectCoach: 'Selecteer coach',
          timing: 'Tijden',
          sessionDate: 'Sessiedatum',
          startTime: 'Begintijd',
          endTime: 'Eindtijd',
          selectTime: 'Selecteer tijd',
          sessionTopic: 'Sessieonderwerp',
          topicPlaceholder: 'bijv. Schaakopeningstheorie, Eindspeltraining',
          sessionCharge: 'Sessiekosten ({{symbol}})',
          chargePlaceholder: 'bijv. 600',
          chargeNote: 'Het getoonde standaardtarief is het uurtarief. Het wordt automatisch aangepast op basis van de sessieduur. U kunt dit bedrag handmatig wijzigen.',
          creating: 'Aanmaken...',
          create: 'Individuele sessie aanmaken',
          selectLevelError: 'Selecteer een niveau',
          selectStudentError: 'Selecteer een leerling',
          selectCoachError: 'Selecteer een coach',
          selectDateError: 'Selecteer de sessiedatum',
          selectStartError: 'Selecteer de begintijd',
          selectEndError: 'Selecteer de eindtijd',
          topicRequired: 'Voer een sessieonderwerp in',
          chargeRequired: 'Voer een geldig bedrag in',
          endAfterStart: 'Eindtijd moet na begintijd liggen',
          invalidDate: 'Voer een geldige sessiedatum in.',
          createFailed: 'Individuele sessie aanmaken mislukt',
          longSessionTitle: 'Waarschuwing lange sessieduur',
          longSessionProceed: 'Deze sessieduur bedraagt meer dan 3 uur. Wilt u doorgaan?',
          sessionDateLabel: 'Sessiedatum:',
          startTimeLabel: 'Begintijd:',
          endTimeLabel: 'Eindtijd:',
          durationLabel: 'Duur:'
        },
        updateNotesModal: {
          title: 'Sessienotities bijwerken',
          subtitle: 'Sessie #{{sessionId}} - {{batchName}}',
          sessionNotes: 'Sessienotities',
          placeholder: 'Voer sessienotities in...',
          charCount: '{{count}} / 2000 tekens',
          saving: 'Opslaan...',
          saveNotes: 'Notities opslaan',
          updateFailed: 'Sessienotities bijwerken mislukt. Probeer het opnieuw.'
        }
      },
      attendance: {
        markTitle: 'Aanwezigheid markeren',
        viewTitle: 'Sessieaanwezigheid',
        viewLoading: 'Aanwezigheidsgegevens laden…',
        coach: 'Coach',
        status: 'Status',
        sessionNotes: 'Sessienotities',
        sessionNotesRequired: 'Sessienotities',
        sessionNotesPlaceholder: 'Voeg notities toe voor deze sessie voordat u de aanwezigheid markeert',
        sessionNotesHint: 'Verplicht vóór het opslaan of afronden van de sessie',
        searchStudents: 'Zoek leerlingen… (naam of #id)',
        warning: 'Waarschuwing',
        success: 'Geslaagd',
        markAllPresent: 'Alles aanwezig markeren',
        markAllAbsent: 'Alles afwezig markeren',
        saveAttendance: 'Aanwezigheid opslaan',
        saving: 'Opslaan…',
        completeSession: 'Sessie afronden',
        completing: 'Afronden…',
        completeTitle: 'Sla aanwezigheid op en markeer deze sessie als COMPLETED.',
        alreadyCompleted: 'Sessie is al gemarkeerd als COMPLETED.',
        cancelledCannotComplete: 'Geannuleerde sessies kunnen niet worden afgerond.',
        loadingStudents: 'Leerlingen laden…',
        checkInOutInfo: 'Incheck- en uitchecktijden',
        checkInOutHint: 'Deze tijden zijn optioneel. Vul ze alleen in wanneer de aanwezigheidstijd van een leerling afwijkt van de geplande sessietijd.',
        student: 'Leerling',
        attendanceStatus: 'Aanwezigheidsstatus',
        checkIn: 'Inchecktijd',
        checkOut: 'Uitchecktijd',
        notMarked: 'Niet gemarkeerd',
        present: 'Aanwezig',
        absent: 'Afwezig',
        notApplicable: 'Niet van toepassing',
        selectCheckIn: 'Selecteer inchecktijd',
        selectCheckOut: 'Selecteer uitchecktijd',
        noStudentsMatch: 'Geen leerlingen komen overeen met "{{query}}".',
        walkInStudents: 'Walk-in leerlingen ({{count}})',
        addWalkIn: '+ Walk-in leerling toevoegen',
        duplicateWalkIns: '{{count}} walk-in record overlapt met ingeschreven groepsleerlingen en wordt hieronder verborgen om verwarring te voorkomen.',
        duplicateWalkIns_other: '{{count}} walk-in records overlappen met ingeschreven groepsleerlingen en worden hieronder verborgen om verwarring te voorkomen.',
        removeDuplicate: 'Duplicaat verwijderen: {{name}}',
        noWalkIns: 'Geen walk-in leerlingen voor deze sessie',
        walkInHint: 'Walk-in leerlingen worden automatisch als aanwezig gemarkeerd en bij afronding van de sessie in rekening gebracht',
        notes: 'Notities',
        actions: 'Acties',
        remove: 'Verwijderen',
        sessionStart: 'Sessie begin',
        sessionEnd: 'Sessie einde',
        walkInAdded: 'Walk-in leerling succesvol toegevoegd!',
        walkInRemoved: 'Walk-in leerling succesvol verwijderd!',
        removeWalkInConfirm: 'Deze walk-in leerling verwijderen? Er worden geen kosten voor deze sessie in rekening gebracht.',
        removeWalkInFailed: 'Walk-in leerling verwijderen mislukt',
        notesRequiredSave: 'Sessienotities zijn verplicht vóór het opslaan van de aanwezigheid.',
        notesRequiredComplete: 'Sessienotities zijn verplicht vóór het afronden van de sessie.',
        markBeforeComplete: 'Markeer de aanwezigheid vóór het afronden van de sessie.',
        savedAck: 'Aanwezigheid opgeslagen om {{time}}. U kunt deze bijwerken totdat de sessie is gemarkeerd als COMPLETED.',
        completeConfirm: 'Deze sessie markeren als COMPLETED? Eenmaal afgerond kan dit niet meer worden gewijzigd.',
        completeSuccess: 'Sessie succesvol gemarkeerd als COMPLETED!',
        completeFailed: 'Sessie afronden mislukt: {{message}}',
        saveFailed: 'Aanwezigheid opslaan mislukt.',
        loadFailed: 'Aanwezigheidsgegevens laden mislukt.',
        sessionIdMissing: 'Sessie-ID ontbreekt in route/state.',
        batchUnknown: 'Groep is onbekend voor deze sessie. Alleen leerlingen met bestaande markeringen worden getoond.',
        batchStudents: 'Groepsleerlingen',
        walkInLabel: 'Walk-in leerlingen',
        type: 'Type',
        walkInType: 'Inloop',
        carryForward: 'Doorschuiven',
        carryForwardColumnHelp: 'Doorschuiven bepaalt de facturering wanneer een leerling afwezig is. Indien ingeschakeld, wordt het sessiebedrag niet afgeschreven en kan het tegoed later worden gebruikt. Indien uitgeschakeld, wordt het bedrag toch afgeschreven, ook als de leerling als afwezig is gemarkeerd.',
        sessionCancelled: 'Sessie geannuleerd',
        cancelledDesc: 'Deze sessie is geannuleerd/ongedaan gemaakt. Alle factureringskosten zijn terugbetaald.',
        cancelledHistorical: ' De hieronder getoonde aanwezigheidsgegevens zijn historisch en niet langer actief.',
        missingSession: 'Sessie-informatie ontbreekt.',
        loadDetailsFailed: 'Aanwezigheidsdetails laden mislukt.',
        addWalkInStudent: {
          title: 'Walk-in leerling toevoegen',
          selectStudent: 'Selecteer leerling',
          searchPlaceholder: 'Zoeken op naam of e-mail...',
          alreadyOnSession: 'Overeenkomende leerlingen staan al op deze sessie en kunnen niet als walk-in worden toegevoegd.',
          noMatch: 'Geen andere leerlingen komen overeen met uw zoekopdracht',
          allOnSession: 'Alle beschikbare leerlingen staan al op deze sessie',
          noStudents: 'Geen leerlingen gevonden',
          change: 'Wijzigen',
          checkIn: 'Inchecktijd (optioneel)',
          checkInHint: 'Laat leeg om de begintijd van de sessie te gebruiken',
          checkOut: 'Uitchecktijd (optioneel)',
          checkOutHint: 'Laat leeg om de eindtijd van de sessie te gebruiken',
          notes: 'Notities (optioneel)',
          notesPlaceholder: 'Eventuele aanvullende notities...',
          adding: 'Toevoegen...',
          add: 'Walk-in leerling toevoegen',
          selectRequired: 'Selecteer een leerling',
          alreadyOnRoster: 'Deze leerling staat al op de sessielijst of walk-inlijst',
          loadFailed: 'Leerlingen laden mislukt',
          addFailed: 'Walk-in leerling toevoegen mislukt',
          noteTitle: 'Let op:',
          noteBody: 'Walk-in leerlingen worden automatisch het sessietarief in rekening gebracht wanneer de sessie is afgerond. Het bedrag wordt van hun portemonnee afgeschreven. Leerlingen die al in deze groep zijn ingeschreven, worden hier niet getoond.'
        }
      },
      reports: {
        eyebrow: 'Rapporten',
        title: 'Rapporten',
        subtitle: 'Bekijk en exporteer gedetailleerde rapporten voor uw academie',
        loading: 'Rapporten laden…',
        noReportsEnabled: 'Er zijn geen rapporten ingeschakeld',
        noReportsHint: 'Uw academiebeheerder heeft alle rapporten uitgeschakeld. Neem contact op met een beheerder om rapporten in te schakelen onder Rapporten → Rapportinstellingen.',
        tabs: {
          'coach-revenue': 'Coachomzet & commissie',
          'session-statement': 'Sessieoverzicht leerling',
          'batch-attendance': 'Aanwezigheid per groep',
          'academy-revenue': 'Omzetoverzicht academie',
          'student-attendance': 'Aanwezigheidsoverzicht leerling',
          'payments-due': 'Openstaande betalingen',
          'data-export': 'Gegevensexport',
          'sessions-summary': 'Sessieoverzicht',
          'monthly-statements': 'Maandelijks overzicht beheer'
        },
        tabsManager: {
          'session-statement': 'Sessieoverzicht'
        },
        descriptions: {
          'coach-revenue': 'Omzet per sessie met splitsing academie- vs. coachcommissie.',
          'session-statement': 'Kostenoverzicht per leerling met betalingen, sessiekosten en PDF-export.',
          'batch-attendance': 'Matrix van leerlingen vs. sessies voor een groep in een datumbereik.',
          'academy-revenue': 'Bruto-omzet per maand en vestiging met commissie-uitsplitsing.',
          'student-attendance': 'Aanwezigheidspercentages en aantallen per leerling voor een periode.',
          'payments-due': 'Momentopname van leerlingen met achterstallig of laag portemonneesaldo waarbij contributie geïnd moet worden.',
          'data-export': 'CSV-exports voor leerlingenlijst, voogden en portemonneetransacties.',
          'sessions-summary': 'Sessiedatum/-tijd, niveau, groep, coach en onderwerp voor een leerling of coach over een datumbereik.',
          'monthly-statements': 'Voorbeeld, verzenden en volgen van maandelijkse sessieoverzicht-e-mails met kostenschattingen.'
        },
        dataExport: {
          title: 'Gegevensexport',
          subtitle: 'Download academiegegevens als CSV-bestanden. Alle exports bevatten een UTF-8 BOM voor correcte weergave in Excel.',
          preparing: 'Voorbereiden…',
          from: 'Van',
          to: 'Tot',
          studentRoster: 'Leerlingenlijst',
          studentRosterDesc: 'Alle leerlingen met contactgegevens, geboortedatum, optionele FIDE/Elo-velden, actieve inschrijvingsgroepen en huidig portemonneesaldo.',
          downloadStudents: 'students.csv downloaden',
          guardianLinks: 'Voogdkoppelingen',
          guardianLinksDesc: 'Leerling–voogdrelaties en koppelingsstatus voor DSAR- / oudertoegangsverzoeken.',
          downloadGuardians: 'guardians.csv downloaden',
          walletTransactions: 'Portemonneetransacties',
          walletTransactionsDesc: 'Alle portemonneebijschrijvingen en -afschrijvingen voor de geselecteerde periode — inclusief leerlingnaam, bedrag, reden, betaalmethode en referentie.',
          downloadTransactions: 'transactions.csv downloaden',
          footer: 'CSV-bestanden openen rechtstreeks in Microsoft Excel, Google Sheets en LibreOffice Calc. Voor grote academies kan de export enkele seconden duren.',
          invalidDateRange: 'Selecteer een geldig datumbereik.',
          exportStudentsFailed: 'Leerlingenlijst exporteren mislukt. Probeer het opnieuw.',
          exportGuardiansFailed: 'Voogdkoppelingen exporteren mislukt. Probeer het opnieuw.',
          exportTxnsFailed: 'Transacties exporteren mislukt. Probeer het opnieuw.',
          dsarBannerTitle: 'Gegevensverzoek — handel hier',
          dsarBannerBody: '{{name}} (gebruiker #{{userId}}, {{username}}) vroeg een kopie van persoonsgegevens. Download de exports hieronder en open indien nodig het profiel om de identiteit te controleren.',
          dsarUnknownName: 'Deze gebruiker',
          dsarOpenProfile: 'Profiel openen',
          dsarOpenWallet: 'Wallet-opwaardering openen',
          studentFilter: 'Leerling',
          studentFilterHint: 'Alle CSV-downloads hieronder gebruiken deze selectie. Kies één leerling voor een DSAR-pakket, of Alle leerlingen voor academybrede exports.',
          chooseStudent: 'Zoek leerlingen…',
          allStudents: 'Alle leerlingen',
          studentFallback: 'Leerling #{{id}}',
          scopedToStudent: 'Exportscope: {{student}}',
          scopedToAll: 'Exportscope: alle leerlingen',
          dsarLocked: 'DSAR-focus',
          clearStudentFilter: 'Leerlingfilter wissen',
          studentRosterDescOne: 'Contactgegevens, geboortedatum, optionele FIDE/Elo-velden, actieve groepen en walletsaldo alleen voor de geselecteerde leerling.',
          guardianLinksDescOne: 'Voogdrelaties en status alleen voor de geselecteerde leerling.',
          walletTransactionsDescOne: 'Portemonneebijschrijvingen en -afschrijvingen alleen voor de geselecteerde leerling en periode.',
          downloadOneStudent: 'Deze leerling.csv downloaden',
          downloadOneGuardians: 'guardians.csv voor deze leerling',
          downloadOneTransactions: 'transactions.csv voor deze leerling',
          walletStudentFilter: 'Leerling',
          walletExportingFor: 'Transacties exporteren voor {{student}}',
          studentRequiredForTxns: 'Selecteer een leerling voordat u portemonneetransacties downloadt.',
          studentsLoadFailed: 'Leerlingen laden mislukt. Controleer of de backend draait en vernieuw.',
          studentsEmpty: 'Geen actieve leerlingen gevonden.'
        },
        common: {
          filters: 'Filters',
          fromDate: 'Van-datum',
          toDate: 'Tot-datum',
          from: 'Van',
          to: 'Tot',
          filterByBranch: 'Filteren op vestiging',
          allBranches: 'Alle vestigingen',
          filterByCoach: 'Filteren op coach',
          allCoaches: 'Alle coaches',
          filterByLevel: 'Filteren op niveau',
          selectLevel: 'Selecteer niveau',
          allLevels: 'Alle niveaus',
          filterByBatch: 'Filteren op groep',
          selectBatch: 'Selecteer groep',
          allBatches: 'Alle groepen',
          search: 'Zoeken',
          generate: 'Genereren',
          generateReport: 'Rapport genereren',
          loadingReport: 'Rapportgegevens laden…',
          failedToLoad: 'Rapport laden mislukt.',
          selectDateRange: 'Selecteer een datumbereik',
          invalidDateFormat: 'Ongeldig datumformaat. Gebruik {{format}}',
          invalidDateRange: 'Selecteer een geldig datumbereik.',
          selectFromToBatch: 'Selecteer van-datum, tot-datum en groep',
          enterEmail: 'Voer e-mailadres in',
          enterEmailAlert: 'Voer een e-mailadres in',
          emailSent: 'Rapport succesvol per e-mail verzonden',
          emailFailed: 'E-mail verzenden mislukt',
          exportPdfFailed: 'PDF exporteren mislukt',
          close: 'Sluiten',
          present: 'Aanwezig',
          absent: 'Afwezig',
          upcoming: 'Aankomend',
          ongoing: 'Bezig',
          past: 'Verstreken',
          walkIn: 'Walk-in',
          student: 'Leerling',
          studentId: 'Leerling-ID',
          batch: 'Groep',
          level: 'Niveau',
          branch: 'Vestiging',
          coach: 'Coach',
          sessions: 'Sessies',
          status: 'Status',
          email: 'E-mail',
          phone: 'Telefoon',
          amount: 'Bedrag',
          plan: 'Abonnement',
          endDate: 'Einddatum',
          grandTotal: 'Eindtotaal',
          periodTotalAllPages: 'Periodetotaal (alle pagina\'s)',
          unknown: 'Onbekend',
          pageSize: 'Paginagrootte:',
          first: 'Eerste',
          previous: 'Vorige',
          next: 'Volgende',
          last: 'Laatste',
          showingSessionsRange: '{{start}} - {{end}} van {{total}} sessies weergegeven',
          showingSessionsPage: '{{count}} op deze pagina · {{total}} totaal · Pagina {{page}} van {{pages}}',
          pageOf: 'Pagina {{page}} van {{pages}}',
          records: '{{count}} records',
          downloadPageExcel: 'Pagina Excel downloaden',
          exportFullHint: 'Gebruik hieronder Export PDF/CSV voor het volledige rapport (alle pagina\'s), overeenkomend met het overzicht op het scherm.',
          individualSession: 'Individuele sessie',
          individual: 'Individueel',
          sessionNotes: 'Sessienotities',
          dateTime: 'Datum / tijd',
          sessionDateTime: 'Sessiedatum & -tijd',
          sessionTopic: 'Sessieonderwerp',
          topic: 'Onderwerp',
          gross: 'Bruto',
          grossRevenue: 'Bruto-omzet',
          academy: 'Academie',
          academyShare: 'Aandeel academie',
          academyEarnings: 'Inkomsten academie',
          coachEarnings: 'Inkomsten coach',
          attendanceRate: 'Aanwezigheidspercentage',
          viewAttendance: 'Aanwezigheidsdetails bekijken',
          clickViewAttendance: 'Klik om aanwezigheidsdetails te bekijken',
          sessionAttendance: 'Sessieaanwezigheid',
          sessionDetails: 'Sessiedetails',
          dateAndTime: 'Datum & tijd',
          batchAndBranch: 'Groep & vestiging',
          students: 'Leerlingen',
          avg: 'Gem.',
          average: 'gemiddelde',
          commissionSplit: 'Commissiesplitsing',
          totalSessionCharges: 'Totale sessiekosten',
          charges: 'Kosten',
          totals: 'Totalen',
          errorPrefix: 'Fout: {{message}}',
          noServerResponse: 'Geen reactie van de server. Controleer uw verbinding.',
          presentCount: 'Aanwezig ({{count}}):',
          absentCount: 'Afwezig ({{count}}):',
          attendancePct: 'Aanwezigheid %',
          month: 'Maand',
          type: 'Type',
          startTime: 'Begintijd',
          endTime: 'Eindtijd',
          sessionId: 'Sessie-ID',
          date: 'Datum',
          studentsFound: '{{count}} leerling gevonden',
          studentsFound_other: '{{count}} leerlingen gevonden',
          never: 'Nooit',
          amountDue: 'Openstaand bedrag',
          lastTopup: 'Laatste opwaardering',
          batchesCol: 'Groepen',
          balance: 'Saldo',
          inScope: 'Binnen bereik',
          windowDays: 'Venster (dagen)',
          overdue: 'ACHTERSTALLIG',
          lowBalance: 'LAAG SALDO',
          expired: 'VERLOPEN',
          expiringSoon: 'BINNENKORT VERLOPEN',
          byStudent: 'Op leerling',
          byCoach: 'Op coach',
          choosePerson: 'Kies een {{mode}}…',
          allLevelsLabel: 'ALLE niveaus',
          allBatchesLabel: 'ALLE groepen',
          noSessionsFilter: 'Geen sessies gevonden voor dit filter.',
          selectPerson: 'Selecteer een {{mode}}.',
          selectValidDates: 'Geef een geldige van- en tot-datum op.',
          confirm: 'Bevestigen'
        },
        exportBar: {
          sendReportViaEmail: 'Rapport per e-mail verzenden',
          exportPdf: 'PDF exporteren',
          generatingPdf: 'PDF genereren…',
          exportCsv: 'CSV exporteren',
          generatingCsv: 'CSV genereren…',
          sending: 'Verzenden…',
          sent: 'Verzonden!',
          sendEmail: 'E-mail verzenden',
          exportPdfFailed: 'PDF exporteren mislukt. Probeer het opnieuw.',
          exportCsvFailed: 'CSV exporteren mislukt. Probeer het opnieuw.',
          enterEmail: 'Voer een e-mailadres in',
          enterValidEmail: 'Voer een geldig e-mailadres in',
          sendEmailFailed: 'E-mail verzenden mislukt. Probeer het opnieuw.',
          generateFirstHint: 'Genereer eerst een rapport om exportopties in te schakelen.'
        },
        coachRevenue: {
          title: 'Coachomzet & commissie',
          subtitle: 'Volgt inkomsten per sessie, commissiepercentages en uitbetalingstotalen per coach — filterbaar op datumbereik, vestiging en coach.',
          totalSessions: 'Totaal sessies',
          sessionMix: '{{individual}} individueel, {{batch}} groep',
          noSessionsFound: 'Geen sessies gevonden',
          noSessionsHint: 'Geen sessies gevonden voor de geselecteerde filters en datumbereik',
          coachWiseEarnings: 'Inkomsten per coach',
          dayWiseEarnings: 'Inkomsten per dag',
          daysCount: '{{count}} dagen',
          daysScrollHint: '{{count}} dagen · scroll voor meer'
        },
        sessionAttendance: {
          subtitle: 'Aanwezigheidsmatrix leerling-per-sessie voor een geselecteerde groep — toont aanwezig/afwezig-status',
          subtitleCharges: ' en sessiekosten per sessie',
          subtitleEnd: ' voor elke leerling.'
        },
        studentAttendance: {
          subtitleCoach: 'Aanwezigheidsoverzicht voor uw sessies en leerlingen — filter op niveau of groep die u coacht.',
          subtitleAdmin: 'Aanwezigheidsoverzicht op leerlingniveau over groepen — totaal sessies, aanwezig/afwezig-aantallen en aanwezigheidspercentage, met filtering op vestiging, niveau, groep en coach.',
          totalStudents: 'Totaal leerlingen',
          avgAttendance: 'Gem. aanwezigheid',
          totalPresent: 'Totaal aanwezig',
          totalAbsent: 'Totaal afwezig',
          detailsTitle: 'Aanwezigheidsdetails leerling',
          lowAttendanceLegend: 'Gemarkeerde rijen geven aanwezigheid onder 50% aan',
          empty: 'Geen leerlingrecords gevonden voor de geselecteerde filters.'
        },
        academyRevenue: {
          subtitle: 'Bruto-omzet per maand en vestiging met uitsplitsing academie- vs. coachcommissie voor een datumbereik.',
          monthlyBreakdown: 'Maandelijkse uitsplitsing',
          branchBreakdown: 'Uitsplitsing per vestiging',
          month: 'Maand',
          sessions: 'Sessies',
          grossRevenue: 'Bruto-omzet',
          academyCommission: 'Academiecommissie',
          academyRevenue: 'Academie-omzet',
          coachPayout: 'Uitbetaling coach',
          walletTopUps: 'Portemonnee-opwaarderingen',
          walletBalanceHeld: 'Aangehouden portemonneesaldo',
          empty: 'Geen omzetgegevens gevonden voor de geselecteerde filters.'
        },
        paymentsDue: {
          subtitle: 'Momentopname van leerlingen met een portemonneesaldo onder de geconfigureerde drempel voor laag saldo (risico) of een negatief saldo (achterstallig). Geen datumbereik nodig — weerspiegelt de huidige staat van alle portemonneerekeningen.',
          paymentStatus: 'Betalingsstatus',
          statusAll: 'Alles (achterstallig + laag saldo)',
          statusOverdue: 'Alleen achterstallig (negatief saldo)',
          statusLowBalance: 'Alleen risico (niet achterstallig)',
          thresholdOverride: 'Drempel laag saldo overschrijven',
          thresholdPlaceholder: 'Laat leeg om de systeemstandaard te gebruiken',
          thresholdHint: 'Systeemstandaard: {{amount}}',
          overdueStudents: 'Achterstallige leerlingen',
          overdueSub: 'Negatief portemonneesaldo',
          totalAmountDue: 'Totaal openstaand bedrag',
          totalDueSub: 'Som verschuldigd door achterstallige leerlingen',
          belowThreshold: 'Onder drempel',
          belowSub: '{{pct}}% van leerlingen binnen bereik',
          studentsInScope: 'Leerlingen binnen bereik',
          empty: 'Geen leerlingen komen overeen met de geselecteerde filters — alle portemonnees liggen boven de drempel.',
          sessionsSince: 'Sessies sinds',
          ratePerSession: 'Tarief/sessie'
        },
        sessionsSummary: {
          title: 'Sessieoverzicht',
          subtitle: 'Bekijk sessiedatum/-tijd, niveau, groep, coach en onderwerp voor een leerling of coach over een datumbereik.',
          loadFailed: 'Sessieoverzicht laden mislukt.'
        },
        monthlyStatements: {
          title: 'Maandelijks overzicht beheer',
          subtitle: 'Voorbeeld, verzenden en volgen van sessieoverzicht-e-mails. Kies de overzichtsperiode en een aparte periode voor kostenschatting; reeds betaalde sessies worden uitgesloten van de schatting.',
          periodSettings: 'Periode-instellingen',
          statementAndEstimate: 'Overzicht & kostenschatting',
          periodDesc: 'Kies welke activiteit op het overzicht komt, en daarna het venster voor komende kostenschattingen.',
          statementLabel: 'Overzicht:',
          statementPeriod: 'Overzichtsperiode',
          statementPeriodDesc: 'Activiteit, portemonneekosten en aanwezigheid.',
          estimatePeriod: 'Periode kostenschatting',
          estimatePeriodDesc: 'Komende sessies die worden gebruikt om kosten op het overzicht te schatten.',
          thisMonth: 'Deze maand',
          lastMonth: 'Vorige maand',
          includeEstimate: 'Komende kostenschatting opnemen',
          estimateAuto: 'Schatting automatisch vullen vanaf einde overzicht',
          estimateFrom: 'Schatting van',
          estimateTo: 'Schatting tot',
          loadPreview: 'Voorbeeld laden',
          loading: 'Laden…',
          loadFailed: 'Gegevens laden mislukt.',
          totalStudents: 'Totaal leerlingen',
          sent: 'Verzonden',
          pending: 'In behandeling',
          failed: 'Mislukt',
          notSent: 'Niet verzonden',
          searchStudents: 'Zoek leerlingen…',
          sendAll: 'Alles verzenden',
          refreshQueue: 'Wachtrij vernieuwen',
          studentCol: 'Leerling',
          emailCol: 'E-mail',
          walletBalance: 'Portemonneesaldo',
          estimateFees: 'Geschatte kosten',
          queueStatus: 'Wachtrijstatus',
          actions: 'Acties',
          previewPdf: 'PDF-voorbeeld',
          downloadPdf: 'PDF downloaden',
          send: 'Verzenden',
          cancelEmail: 'Annuleren',
          retry: 'Opnieuw proberen',
          noStudents: 'Geen leerlingen gevonden voor deze periode.',
          allowPopups: 'Sta pop-ups toe om de PDF te bekijken.',
          accessDenied: 'Toegang geweigerd — zorg dat u bent ingelogd als ADMIN.',
          pdfLoadFailed: 'PDF laden mislukt.',
          pdfDownloadFailed: 'PDF downloaden mislukt.',
          pdfError: 'PDF-fout: {{message}}',
          confirmSendAllTitle: 'Overzichten verzenden voor {{range}}',
          confirmSendAllMsg: 'Dit plaatst e-mails in de wachtrij voor {{count}} leerling{{suffix}}.{{skipNote}}',
          skipNote: ' {{count}} leerling{{suffix}} met status SENT/PENDING worden automatisch overgeslagen.',
          sendAllLabel: 'Alles verzenden',
          queuedAll: 'Overzichten in de wachtrij geplaatst voor alle leerlingen.',
          sendAllFailed: 'Alles verzenden mislukt.',
          confirmSendOneTitle: 'Overzicht verzenden voor {{name}}',
          confirmSendOneMsg: 'Een overzicht-e-mail voor {{name}} over {{range}} in de wachtrij plaatsen?',
          queued: 'In wachtrij ✓',
          queueFailed: 'In wachtrij plaatsen mislukt.',
          cancelTitle: 'E-mail annuleren',
          cancelMsg: 'Deze openstaande e-mail annuleren? De leerling ontvangt het overzicht niet, tenzij u het opnieuw verzendt.',
          cancelLabel: 'E-mail annuleren',
          cancelled: 'Geannuleerd ✓',
          cancelFailed: 'Annuleren mislukt.',
          retryQueued: 'Opnieuw proberen in wachtrij ✓',
          retryFailed: 'Opnieuw proberen mislukt.',
          statusSent: 'Verzonden',
          statusPending: 'In behandeling',
          statusProcessing: 'Wordt verwerkt',
          statusFailed: 'Mislukt',
          statusCancelled: 'Geannuleerd',
          months: {
            '1': 'januari',
            '2': 'februari',
            '3': 'maart',
            '4': 'april',
            '5': 'mei',
            '6': 'juni',
            '7': 'juli',
            '8': 'augustus',
            '9': 'september',
            '10': 'oktober',
            '11': 'november',
            '12': 'december'
          },
          queueing: 'In wachtrij plaatsen…',
          sendAllWithCount: '🚀 Alles verzenden ({{count}})',
          includeFeeEstimateTitle: 'Kostenraming en stortingsherinnering opnemen',
          includeFeeEstimateDesc: 'Vink uit om alleen een activiteitsoverzicht te verzenden. Leerlingen met voldoende portemonneesaldo slaan de betalingsherinnering nog steeds over wanneer dit is aangevinkt.',
          resetDefault: 'Standaard herstellen',
          resetDefaultTitle: 'Raming herstellen naar het standaardbereik op basis van de einddatum van het overzicht',
          then: 'daarna',
          autoRange: 'Auto: {{range}} ({{count}} dag{{daySuffix}})',
          customEstimateRange: 'Aangepast ramingsbereik',
          estimateInvalid: 'De kostenramingsperiode vereist geldige van-/tot-datums.',
          estimateLabel: 'Raming:',
          studentsWithActivity: '{{count}} leerling{{suffix}} met betalings-/inschrijvingsactiviteit',
          clearSearch: 'Zoekopdracht wissen',
          noEmailOnFile: 'Geen e-mailadres bekend',
          sendStatementEmail: 'Overzicht per e-mail verzenden',
          resendTooltip: 'Leerling heeft dit al ontvangen. Wordt opnieuw verzonden.',
          resend: 'Opnieuw verzenden',
          charges: 'Kosten',
          closingBalance: 'Eindsaldo',
          upcomingEst: 'Komende ram.',
          emailStatus: 'E-mailstatus',
          notSentStatus: 'Niet verzonden',
          archived: 'Gearchiveerd',
          noStudentsActivity: 'Geen leerlingen met betalings- of inschrijvingsactiviteit gevonden voor {{range}}.',
          noStudentsMatch: 'Geen leerlingen komen overeen met "{{term}}".',
          refresh: 'Vernieuwen',
          refreshTitle: 'Gegevens vernieuwen',
          showingStudents: '{{filtered}} van {{total}} leerlingen weergegeven',
          depositDue: 'Storting verschuldigd',
          reportOnly: 'Alleen rapport',
          prepaid: 'Vooruitbetaald',
          balanceCredit: '{{amount}} tegoed',
          balanceDue: '{{amount}} verschuldigd',
          amountDue: 'Verschuldigd {{amount}}',
          estimateHidden: 'Gesch. {{amount}} verborgen',
          estimateHiddenEmail: 'Gesch. {{amount}} verborgen in e-mail',
          sessionCountShort: '{{count}} sessies',
          confirmSendAllMsg_one: 'Dit plaatst e-mails in de wachtrij voor {{count}} leerling.{{skipNote}}',
          confirmSendAllMsg_other: 'Dit plaatst e-mails in de wachtrij voor {{count}} leerlingen.{{skipNote}}',
          skipNote_one: ' {{count}} leerling met status SENT/PENDING wordt automatisch overgeslagen.',
          skipNote_other: ' {{count}} leerlingen met status SENT/PENDING worden automatisch overgeslagen.',
          studentsWithActivity_one: '{{count}} leerling met betalings-/inschrijvingsactiviteit',
          studentsWithActivity_other: '{{count}} leerlingen met betalings-/inschrijvingsactiviteit',
          autoRange_one: 'Auto: {{range}} ({{count}} dag)',
          autoRange_other: 'Auto: {{range}} ({{count}} dagen)',
          paymentReminder: 'Betalingsherinnering'
        }
      },
      calendar: {
        eyebrow: 'Beheren',
        title: 'Sessiekalender',
        subtitleStaff: 'Alle geplande sessies in één oogopslag',
        subtitleStudent: 'Uw ingeschreven sessies en aanwezigheid',
        subtitleParent: 'De ingeschreven sessies en aanwezigheid van uw kind',
        subtitleCoach: 'Uw sessies — filter op een leerling uit uw rooster om alleen uw sessies voor die leerling te zien',
        loadingSubtitle: 'Uw ingeschreven sessies en aanwezigheid',
        allSessions: 'Alle sessies',
        myBranches: 'Mijn vestigingen',
        mySessions: 'Mijn sessies',
        pending: '{{count}} in behandeling',
        completed: '{{count}} afgerond',
        today: 'Vandaag',
        month: 'Maand',
        week: 'Week',
        viewing: 'Weergave',
        searchName: 'Naam zoeken…',
        coaches: 'Coaches',
        students: 'Leerlingen',
        myRosterStudents: 'Leerlingen in mijn rooster',
        noRosterStudents: 'Nog geen leerlingen in het rooster',
        noResults: 'Geen resultaten',
        student: 'leerling',
        coach: 'coach',
        batches: 'Groepen:',
        showAll: 'Alles tonen',
        batchFilterHintAll: 'Klik op een groep om alleen die sessies te tonen · klik op meer groepen om te combineren',
        batchFilterHintSelected: '{{count}} groep geselecteerd · klik op een andere om toe te voegen · klik op geselecteerd om te verwijderen',
        batchFilterHintSelected_other: '{{count}} groepen geselecteerd · klik op een andere om toe te voegen · klik op geselecteerd om te verwijderen',
        legendPending: 'In behandeling',
        legendCompleted: 'Afgerond',
        legendCancelled: 'Geannuleerd',
        legendBatchColors: 'Kleuren = groep',
        legendHoliday: '🏖 = academievakantie',
        legendHover: 'Beweeg over een sessie voor details',
        legendPresent: '✓ Aanwezig',
        legendAbsent: '✗ Afwezig',
        legendClickDay: 'Klik op een dag voor de volledige lijst',
        legendClickAttendance: 'Klik om aanwezigheid te openen',
        shortcuts: 'Sneltoetsen: ← → navigeren · T vandaag',
        shortcutsStaff: ' · Klik op een dag om de sessielijst te openen',
        loadFailed: 'Sessies laden mislukt. Probeer het opnieuw.',
        noSessionsMonth: 'Geen sessies gepland in {{month}} {{year}}',
        noSessionsHint: 'Probeer een andere maand of controleer uw groepsfilters',
        holiday: 'Vakantie',
        months: {
          '1': 'januari',
          '2': 'februari',
          '3': 'maart',
          '4': 'april',
          '5': 'mei',
          '6': 'juni',
          '7': 'juli',
          '8': 'augustus',
          '9': 'september',
          '10': 'oktober',
          '11': 'november',
          '12': 'december'
        },
        session: 'Sessie',
        level: 'Niveau',
        sessionCoach: 'Sessiecoach',
        time: 'Tijd',
        sessionStatus: 'Sessiestatus',
        attendance: 'Aanwezigheid',
        sessionNotes: 'Sessienotities',
        present: 'Aanwezig',
        absent: 'Afwezig',
        marked: 'Gemarkeerd',
        notMarked: 'Niet gemarkeerd',
        more: '+{{count}} meer',
        showLess: 'Minder tonen',
        sessionsCount: '{{count}} sessie',
        sessionsCount_other: '{{count}} sessies',
        sessAbbr: '{{count}} sessies',
        noSessionsDay: 'Geen sessies op deze dag.',
        prevYear: 'Vorig jaar',
        nextYear: 'Volgend jaar',
        prev: 'Vorige',
        next: 'Volgende',
        close: 'Sluiten'
      },
      notificationsPage: {
        title: 'Meldingen',
        subtitle: 'Waarschuwingen en herinneringen uit uw academiewerkruimte.',
        markAllRead: 'Alles als gelezen markeren',
        empty: 'Alles bijgewerkt — nog geen meldingen.',
        pageOf: 'Pagina {{page}} van {{totalPages}} · {{total}} in totaal',
        previous: 'Vorige',
        next: 'Volgende',
        justNow: 'zojuist',
        minutesAgo: '{{count}}m geleden',
        hoursAgo: '{{count}}u geleden',
        daysAgo: '{{count}}d geleden',
        subtitleValue: 'Actiegerichte meldingen over wallets, aanwezigheid en privacyverzoeken — open een item om naar het juiste scherm te gaan.',
        unreadBanner: 'U heeft {{count}} ongelezen melding(en). Open er een om te bekijken en te handelen.',
        emptyHint: 'Wanneer leerlingen onder de walletsdrempel komen, afwezigheid wordt gemarkeerd of een DSAR wordt aangevraagd, verschijnen alerts hier met een directe link.'
      },
      form: {
        sessionDays: 'Sessiedagen',
        selectOneDay: 'Selecteer minstens één dag'
      },
      authPages: {
        forgotTitle: 'Wachtwoord vergeten',
        emailOrUsername: 'E-mail of gebruikersnaam',
        emailOrUsernamePlaceholder: 'u@voorbeeld.com of uw gebruikersnaam',
        sending: 'Verzenden…',
        backToSignIn: 'Terug naar inloggen',
        setPasswordTitle: 'Stel uw wachtwoord in',
        missingToken: 'Token ontbreekt in de URL. Gebruik de nieuwste inloglink uit uw e-mail.',
        newPassword: 'Nieuw wachtwoord',
        confirmPassword: 'Wachtwoord bevestigen',
        passwordRules: 'Minimaal 8 tekens, inclusief 1 hoofdletter, 1 cijfer en 1 speciaal teken (@ $ ! % * ? &).',
        setPasswordButton: 'Wachtwoord instellen',
        saving: 'Opslaan...',
        invalidToken: 'Deze inloglink is ongeldig. Token ontbreekt in de URL.',
        passwordInvalid: 'Het wachtwoord moet minstens 8 tekens bevatten, inclusief één hoofdletter, één cijfer en één speciaal teken.',
        passwordsMismatch: 'Wachtwoorden komen niet overeen.',
        passwordSetSuccess: 'Wachtwoord succesvol ingesteld. U kunt nu inloggen.',
        linkInvalidExpired: 'Link is ongeldig of verlopen.'
      },
      roleSelect: {
        title: 'Selecteer uw rol',
        noRoles: 'Geen rollen gevonden op uw account.',
        contactAdmin: 'Neem contact op met een beheerder.',
        switchHint: 'U kunt uw rol op elk moment wijzigen via het menu in de koptekst.'
      },
      setupWizard: {
        welcomeTitle: 'Welkom bij de app',
        welcomeSubtitle: 'Laten we uw academie in enkele snelle stappen instellen.',
        stepOf: 'Stap {{current}} van {{total}}',
        footerHint: 'Alle instellingen kunt u later wijzigen via het beheerderspaneel.',
        continue: 'Doorgaan →',
        back: '← Terug',
        saving: 'Opslaan…',
        finishing: 'Afronden…',
        finishSetup: 'Installatie voltooien →',
        enterDashboard: 'Naar dashboard →',
        optional: '(optioneel)',
        saved: 'Opgeslagen',
        skipped: 'Overgeslagen',
        configured: 'Geconfigureerd',
        notConfiguredEmail: 'Niet geconfigureerd — Systeem → E-mailinstellingen',
        notConfiguredWhatsApp: 'Niet geconfigureerd — Systeem → WhatsApp',
        saveError: 'Er is iets misgegaan. Probeer het opnieuw.',
        regionalSaveError: 'Regionale instellingen zijn niet opgeslagen. Probeer het opnieuw.',
        emailSaveError: 'E-mailinstellingen konden niet worden opgeslagen.',
        whatsappSaveError: 'WhatsApp-instellingen konden niet worden opgeslagen.',
        step1: {
          title: 'Geef uw academie een naam',
          description: 'Dit verschijnt in de koptekst, op de inlogpagina, in e-mails en in alle PDF-exports.',
          logo: 'Academielogo',
          clickUpload: 'Klik om logo te uploaden',
          logoFormats: 'PNG, JPG, SVG · Max. 2 MB',
          clickChange: 'Klik om te wijzigen',
          logoPreviewAlt: 'Logovoorbeeld',
          academyName: 'Academienaam',
          tagline: 'Slogan',
          nameRequired: 'Academienaam is verplicht.',
          logoTooLarge: 'Logo mag maximaal 2 MB zijn.',
          namePlaceholder: 'bijv. Kingside Chess School',
          taglinePlaceholder: 'bijv. Denk dieper, speel sterker'
        },
        step2: {
          title: 'Contactgegevens',
          description: 'Dit openbare supportcontact wordt getoond aan leerlingen en ouders. Dit is niet hetzelfde als uw uitgaande SMTP-afzendadres (geconfigureerd in de volgende optionele stap).',
          supportPhone: 'Supporttelefoonnummer',
          supportEmail: 'Support-e-mail',
          phonePlaceholder: 'Supporttelefoonnummer',
          emailPlaceholder: 'bijv. support@mijnacademie.nl',
          phoneHint: 'Wordt getoond op facturen, in e-mails en in het leerlingenportaal.',
          emailHint: 'Leerlingen kunnen dit adres gebruiken voor hulp.',
          phoneRequired: 'Telefoonnummer is verplicht.',
          emailRequired: 'E-mailadres is verplicht.',
          emailInvalid: 'Voer een geldig e-mailadres in.'
        },
        step3: {
          title: 'Regio en betalingen',
          description: 'Kies het land en de valuta van uw academie. Online gateways (Razorpay, Stripe, PayPal) configureert u later onder Systeem → Betaalmethoden.'
        },
        step4: {
          title: 'Communicatie koppelen',
          description: 'Optioneel — configureer nu uitgaande e-mail en WhatsApp, of sla over en stel ze later in via het beheerderspaneel.',
          skipHint: 'Overgeslagen onderdelen kunt u later altijd configureren onder Systeem → E-mailinstellingen en Systeem → WhatsApp.',
          emailSection: 'Uitgaande e-mail (SMTP)',
          emailSectionHint: 'Gebruikt voor inloglinks, herinneringen, overzichten en waarschuwingen.',
          whatsappSection: 'WhatsApp (WATI)',
          whatsappSectionHint: 'Sessieherinneringen, portemonnee-waarschuwingen en operationele berichten.',
          whatsappNotLicensed: 'WhatsApp is niet inbegrepen in uw licentie. U kunt dit overslaan en later upgraden.',
          saveEmail: 'E-mailinstellingen opslaan',
          saveWhatsApp: 'WhatsApp-instellingen opslaan',
          skipForNow: 'Voorlopig overslaan',
          smtpHost: 'SMTP-host',
          smtpPort: 'SMTP-poort',
          smtpUsername: 'SMTP-gebruikersnaam',
          smtpPassword: 'SMTP-wachtwoord',
          fromEmail: 'Afzender-e-mailadres',
          alreadySet: '(al ingesteld)',
          requiredToSave: 'Vereist om op te slaan',
          watiBaseUrl: 'WATI API-basis-URL',
          channelNumber: 'Zakelijk kanaalnummer',
          watiToken: 'WATI-bearertoken',
          pasteToken: 'Token plakken'
        },
        step5: {
          title: 'Alles is gereed!',
          ready: '{{name}} is klaar voor gebruik. U kunt deze gegevens op elk moment bijwerken via Instellingen → Branding.',
          academyLabel: 'Academie',
          supportLabel: 'Support',
          communicationsLabel: 'Communicatie',
          outboundEmail: 'Uitgaande e-mail:',
          whatsapp: 'WhatsApp:'
        }
      },
      publicHelp: {
        backToLogin: '← Terug naar inloggen',
        title: 'Help en aan de slag',
        subtitle: 'Snelle antwoorden voordat u inlogt bij {{name}}.',
        signingIn: 'Inloggen',
        signingInBody: 'Gebruik de gebruikersnaam en het wachtwoord die u van de beheerder van uw academie heeft ontvangen. Als u een inlog-e-mail heeft ontvangen, open dan eerst de link in dat bericht om uw wachtwoord in te stellen en keer daarna hier terug om in te loggen.',
        forgotPassword: 'Wachtwoord vergeten?',
        forgotPasswordBody: 'Gebruik de link <1>Wachtwoord vergeten?</1> op de inlogpagina. U ontvangt een link om uw wachtwoord te resetten op uw geregistreerde e-mailadres.',
        forgotPasswordLink: 'Wachtwoord vergeten?',
        contactAcademy: 'Neem contact op met uw academie',
        contactAcademyBody: 'Voor accounttoegang of academiespecifieke vragen kunt u rechtstreeks contact opnemen:',
        email: 'E-mail:',
        phone: 'Telefoon:',
        privacy: 'Privacy',
        privacyBody: 'Lees onze <1>Privacyverklaring</1> om te begrijpen hoe uw gegevens op dit platform worden verwerkt.',
        privacyLink: 'Privacyverklaring'
      },
      licenseInvalid: {
        heading: 'Softwarelicentie inactief',
        body: 'De toegang tot het aanwezigheidssysteem is opgeschort omdat de softwarelicentie niet langer geldig is. Uw gegevens blijven volledig veilig en intact.',
        restoreAccess: 'Toegang herstellen',
        softwareProvider: 'Softwareleverancier',
        tryAgain: 'Opnieuw proberen',
        contactSupport: 'Contact opnemen met support',
        footer: '{{name}} — Aanwezigheidsbeheersysteem'
      },
      guardianPublic: {
        inviteTitle: 'Uitnodiging voor voogd',
        invalidLink: 'Deze uitnodigingslink voor voogden is ongeldig of verlopen.',
        createAccount: 'Voogdaccount aanmaken',
        linkingFor: ' — u koppelt als voogd voor ',
        confirmLink: 'Voogdkoppeling bevestigen',
        linkAccountFor: ' — koppel uw account als voogd voor ',
        invitedEmail: 'Uitgenodigd e-mailadres: {{email}}',
        accountEmail: 'Account-e-mail: {{email}}',
        relationship: 'Uw relatie tot de leerling',
        relationships: {
          MOTHER: 'Moeder',
          FATHER: 'Vader',
          GUARDIAN: 'Voogd',
          GRANDPARENT: 'Grootouder',
          OTHER: 'Overig'
        },
        passwordNote: '8+ tekens, hoofdletter, cijfer en speciaal teken',
        confirmPassword: 'Wachtwoord bevestigen',
        submitRegistration: 'Registratie indienen',
        submitting: 'Indienen…',
        acceptRequest: 'Accepteren en toegang aanvragen',
        decline: 'Weigeren',
        nameRequired: 'Voornaam, achternaam en gebruikersnaam zijn verplicht.',
        passwordInvalid: 'Het wachtwoord moet minstens 8 tekens bevatten, inclusief één hoofdletter, één cijfer en één speciaal teken.',
        passwordsMismatch: 'Wachtwoorden komen niet overeen.',
        registrationSubmitted: 'Registratie ingediend. Meld u aan nadat uw academie de toegang als voogd heeft goedgekeurd.',
        registrationFailed: 'Registratie mislukt. De link is mogelijk ongeldig of verlopen.',
        requestSubmitted: 'Aanvraag ingediend. Meld u aan nadat uw academie de toegang als voogd heeft goedgekeurd.',
        invitationDeclined: 'Uitnodiging geweigerd.',
        processFailed: 'Deze uitnodiging kon niet worden verwerkt.'
      },
      managerForms: {
        editBatch: 'Groep bewerken',
        createBatch: 'Groep aanmaken',
        batchSubtitle: 'Configureer groepsgegevens, planning en factureringsinstellingen.',
        editLevel: 'Niveau bewerken',
        addLevel: 'Niveau toevoegen',
        levelSubtitle: 'Definieer niveau naam en weergavevolgorde voor de groepen van uw vestiging.',
        saveFailed: 'Opslaan mislukt'
      },
      walletStaff: {
        topupTitle: 'Portemonnee opwaarderen',
        referenceOptional: 'Referentie (optioneel)',
        clear: 'Wissen',
        topup: 'Opwaarderen',
        saving: 'Opslaan…',
        missingStudent: 'Leerling ontbreekt',
        invalidAmount: 'Voer een geldig bedrag in',
        credited: 'Portemonnee bijgeschreven: {{amount}}.',
        topupFailed: 'Opwaarderen mislukt.',
        refundTitle: 'Portemonneeterugbetaling verwerken',
        refundImportant: 'Belangrijk:',
        refundWarning: 'Hiermee wordt het terugbetalingsbedrag van de portemonnee van de leerling afgeschreven. Maximaal terug te betalen:',
        missingStudentInfo: 'Leerlinggegevens ontbreken',
        invalidAmountPositive: 'Voer een geldig bedrag groter dan 0 in',
        cannotRefundMore: 'Kan niet meer terugbetalen dan het huidige saldo: {{amount}}',
        reasonRequired: 'Geef een reden voor de terugbetaling op',
        refundSuccess: 'Terugbetaling van {{amount}} succesvol verwerkt.',
        refundFailed: 'Terugbetaling mislukt',
        refundAmount: 'Terugbetalingsbedrag',
        paymentMode: 'Betaalwijze',
        maxPlaceholder: 'Max.: {{amount}}',
        referenceTxn: 'Referentie-/transactienummer',
        referencePlaceholder: 'Transactie-ID, cheque nr., enz.',
        gatewayRefund: 'Ook terugbetalen via de payment gateway (Razorpay/Stripe/PayPal). Vink uit voor alleen een portemonnee-aanpassing wanneer de gateway al handmatig is terugbetaald.',
        reasonForRefund: 'Reden voor terugbetaling',
        reasonPlaceholder: 'Bijv. Leerling stopt, te veel betaald, enz.',
        processing: 'Verwerken…',
        processRefund: 'Terugbetaling verwerken',
        balanceLabel: 'Portemonneesaldo',
        refresh: 'Vernieuwen',
        refund: 'Terugbetalen',
        transactions: 'Portemonneetransacties',
        noTransactions: 'Nog geen transacties.',
        showAllTransactions: 'Alle portemonneetransacties tonen',
        moreCount: ' ({{count}} meer)',
        loadFailedPortal: 'Uw portemonnee kon niet worden geladen. Vernieuw de pagina.',
        loadFailedStaff: 'De portemonnee van deze leerling kon niet worden geladen. Mogelijk valt de leerling buiten het bereik van uw vestiging.',
        grossCharge: 'Brutokosten:',
        discountApplied: 'Korting toegepast:',
        reversalCredit: 'Terugboekingskrediet:',
        netCharge: 'Nettokosten:',
        reversed: '(Teruggeboekt)',
        refunded: 'TERUGBETAALD',
        sessionReversed: '(Sessie teruggeboekt)',
        modifiedOn: 'Gewijzigd op {{date}}',
        original: 'Origineel:',
        modify: 'Wijzigen',
        revert: 'Terugdraaien',
        balanceShort: 'Saldo:',
        sessionBatch: 'Sessie #{{sessionId}} (Groep #{{batchId}})',
        sessionOnly: 'Sessie #{{sessionId}}',
        modifyTxn: 'Transactie wijzigen',
        currentAmount: 'Huidig bedrag',
        newAmount: 'Nieuw bedrag *',
        newAmountPlaceholder: 'Voer nieuw bedrag in',
        reasonOptional: 'Reden (optioneel)',
        modificationReasonPlaceholder: 'Voer reden voor wijziging in',
        saveChanges: 'Wijzigingen opslaan',
        revertTxn: 'Transactie terugdraaien',
        revertWarningTitle: 'Waarschuwing: deze actie kan niet ongedaan worden gemaakt!',
        revertWarningBody: 'Hiermee wordt de transactie permanent verwijderd en het portemonneesaldo met {{amount}} verlaagd.',
        txnDetails: 'Transactiegegevens',
        amountLabel: 'Bedrag:',
        typeLabel: 'Type:',
        reasonLabel: 'Reden:',
        dateLabel: 'Datum:',
        revertReasonOptional: 'Reden voor terugdraaien (optioneel)',
        revertReasonPlaceholder: 'Voer de reden in voor het terugdraaien van deze transactie',
        confirmRevert: 'Terugdraaien bevestigen',
        validAmountRequired: 'Voer een geldig positief bedrag in',
        modifyFailed: 'Transactie wijzigen mislukt',
        revertFailed: 'Transactie terugdraaien mislukt'
      },
      guardiansSection: {
        title: 'Voogden',
        intro: 'Nodig maximaal {{max}} ouders of voogden uit per e-mail. Zij kiezen hun relatie wanneer zij accepteren.',
        canApprove: ' U kunt openstaande verzoeken goedkeuren of afwijzen.',
        contactAcademy: ' Om een goedgekeurde voogdkoppeling te verwijderen, neemt u contact op met uw academie.',
        loading: 'Voogden laden…',
        loadFailed: 'Voogden laden mislukt.',
        enterEmail: 'Voer het e-mailadres van de voogd in.',
        cancelInviteConfirm: 'Deze uitnodiging voor voogd annuleren?',
        cancelInviteFailed: 'Uitnodiging kon niet worden geannuleerd.',
        approveFailed: 'Voogd kon niet worden goedgekeurd.',
        revokeConfirm: 'Deze voogdkoppeling verwijderen? Na intrekking verliezen zij de toegang.',
        revokeFailed: 'Voogd kon niet worden ingetrokken.',
        rejectConfirm: 'Dit voogdverzoek afwijzen?',
        rejectFailed: 'Voogd kon niet worden afgewezen.',
        emptyTitle: 'Nog geen voogden gekoppeld',
        emptyBody: 'Nodig een ouder of voogd uit per e-mail. Zij registreren of bevestigen hun account, waarna uw academie de toegang moet goedkeuren.',
        linkedTitle: 'Gekoppelde voogden',
        pendingTitle: 'Openstaande uitnodigingen',
        approve: 'Goedkeuren',
        reject: 'Afwijzen',
        remove: 'Verwijderen',
        emailPlaceholder: 'E-mailadres van voogd',
        expires: ' · verloopt {{date}}',
        expired: ' · verlopen',
        limitReached: 'Limiet voor voogden bereikt ({{used}}/{{max}}).',
        limitRevokeHint: ' Annuleer een openstaande uitnodiging of verwijder een koppeling voordat u een nieuwe uitnodigt.',
        limitCancelHint: ' Annuleer een openstaande uitnodiging voordat u een nieuwe uitnodigt.',
        status: {
          PENDING_APPROVAL: 'Wacht op goedkeuring',
          APPROVED: 'Goedgekeurd',
          REJECTED: 'Afgewezen',
          REVOKED: 'Ingetrokken'
        },
        inviteType: {
          NEW_REGISTRATION: 'Nieuw account',
          EXISTING_USER_LINK: 'Bestaand account koppelen'
        },
        inviteButton: 'Voogd uitnodigen',
        resendButton: 'Uitnodiging voor voogd opnieuw verzenden',
        sending: 'Verzenden…',
        inviteFailure: 'Uitnodiging voor voogd kon niet worden verzonden.',
        resendFailure: 'Uitnodiging voor voogd kon niet opnieuw worden verzonden.'
      },
      archiveStudent: {
        titleWithEnrollments: 'Leerlingaccount archiveren',
        titleSimple: 'Leerling archiveren',
        hasEnrollments: '{{name}} heeft {{count}} actieve inschrijving.',
        hasEnrollments_other: '{{name}} heeft {{count}} actieve inschrijvingen.',
        chooseProceed: 'Kies hoe u wilt doorgaan.',
        archiveSimple: '{{name}} archiveren? Zij kunnen niet meer inloggen. Hun geschiedenis blijft bewaard.',
        thisStudent: 'Deze leerling',
        thisStudentLower: 'deze leerling',
        loadingEnrollments: 'Inschrijvingsgegevens laden…',
        activeEnrollments: 'Actieve inschrijvingen',
        ongoing: 'lopend',
        whatHappens: 'Wat moet er gebeuren?',
        endEnrollmentsOnly: 'Alleen inschrijvingen beëindigen',
        endEnrollmentsOnlyDesc: 'Beëindig groepslidmaatschap op de onderstaande datum. Account blijft actief — leerling kan nog inloggen.',
        endAndArchive: 'Inschrijvingen beëindigen en account archiveren',
        recommended: '(aanbevolen)',
        endAndArchiveDesc: 'Gebruikelijk wanneer een leerling vertrekt. Beëindigt inschrijvingen en trekt portaaltoegang in.',
        archiveOnly: 'Alleen account archiveren',
        archiveOnlyDesc: 'Trek inloggen in, maar laat inschrijvingen actief. Aanwezigheid en facturering lopen door tot elke inschrijving eindigt.',
        endDateLabel: 'Einddatum voor alle vermelde inschrijvingen',
        archiveOnlyConfirm: 'Ik begrijp dat inschrijvingen actief blijven, aanwezigheid nog kan worden gemarkeerd en de leerling niet kan inloggen tot reactivering.',
        processing: 'Verwerken…',
        continue: 'Doorgaan',
        archive: 'Archiveren'
      },
      privacy: {
        metaLastUpdated: 'Laatst bijgewerkt: juli 2026 · Van toepassing op: {{name}}',
        metaGdprFraming: ' · GDPR- / UK GDPR-kader',
        metaDpdpFraming: ' · DPDP Act 2023-kader',
        whoWeAre: {
          title: '1. Wie wij zijn',
          p1: 'Deze applicatie (&quot;<strong>{{name}}</strong>&quot;) wordt beheerd door uw schaakacademie (de &quot;Academie&quot;, &quot;wij&quot;, &quot;ons&quot;). De Academie treedt op als <strong>verwerkingsverantwoordelijke</strong> voor persoonsgegevens die via dit platform worden verwerkt. Axeron Technologies (softwareleverancier) treedt doorgaans alleen op als <strong>verwerker</strong> waar zij gelicentieerde software, remote support of licentievalidatie levert — zie de verwerkersovereenkomst met uw Academie.',
          p2: 'Voor privacyvragen neemt u eerst contact op met de beheerder van de Academie.'
        },
        whatWeCollect: {
          title: '2. Welke persoonsgegevens wij verzamelen',
          intro: 'Wij verzamelen en verwerken de volgende categorieën persoonsgegevens:',
          identity: '<strong>Identiteitsgegevens</strong> — voornaam, achternaam, geboortedatum (leerlingen); optioneel FIDE-id / Elo indien ingeschakeld',
          contact: '<strong>Contactgegevens</strong> — e-mailadres, telefoonnummer, huisadres',
          account: '<strong>Accountgegevens</strong> — gebruikersnaam, gehasht wachtwoord, toegewezen rol',
          attendance: '<strong>Aanwezigheids- en inschrijvingsgegevens</strong> — sessies, groepen, inschrijvingen',
          financial: '<strong>Financiële gegevens</strong> — portemonneesaldo, opwaarderingen, kostenreferenties',
          guardian: '<strong>Voogd-/ouderkoppelingen</strong> — waar geconfigureerd voor minderjarigen',
          technical: '<strong>Technische gegevens</strong> — inlogtijdstempels, IP in serverlogs, JWT in localStorage',
          noBiometric: 'Wij verzamelen <strong>geen</strong> biometrische gegevens of volledige betaalkaartnummers.',
          payments: 'Online betalingen worden verwerkt via de door de Academie gekozen gateway (<strong>Razorpay</strong>, <strong>Stripe</strong>, <strong>PayPal</strong> en/of <strong>Mollie</strong>).'
        },
        legalBasis: {
          title: '3. Waarom wij uw gegevens verwerken (rechtsgrond)',
          gdpr: {
            contract: '<strong>Art. 6(1)(b) overeenkomst</strong> — inschrijvingen, aanwezigheid, portemonneefacturering, coachingdiensten.',
            legitimate: '<strong>Art. 6(1)(f) gerechtvaardigde belangen</strong> — beveiliging, fraudepreventie, auditlogs.',
            obligation: '<strong>Art. 6(1)(c) wettelijke verplichting</strong> — waar de Academie financiële administratie moet bewaren volgens lokale belastingwetgeving.',
            consent: '<strong>Art. 6(1)(a) / Art. 8 toestemming</strong> — waar vereist voor kinderen (ouderlijke toestemming via voogdfuncties).'
          },
          dpdp: {
            contract: '<strong>Uitvoering van een overeenkomst</strong> — inschrijvingen, aanwezigheid, portemonnee-opwaarderingen en kostenaftrek.',
            legitimate: '<strong>Gerechtvaardigde belangen</strong> — auditlogs, fraudedetectie, veilige werking.',
            obligation: '<strong>Wettelijke verplichting</strong> — bewaren van financiële administratie zoals vereist onder de Indiase Companies Act / GST (doorgaans 8 jaar).'
          }
        },
        retention: {
          title: '4. Hoe lang wij uw gegevens bewaren',
          activeAccount: '<strong>Actieve accountgegevens</strong> — zolang uw account bij de Academie blijft.',
          attendance: '<strong>Aanwezigheid en inschrijving</strong> — doorgaans 3 jaar na de laatste sessie (Academiebeleid kan afwijken).',
          financialGdpr: '<strong>Financiële administratie</strong> — zoals vereist door lokale belastingwetgeving (vaak 6–10 jaar in EU-lidstaten).',
          financialDpdp: '<strong>Financiële administratie</strong> — zoals vereist door lokale belastingwetgeving (doorgaans 8 jaar onder Indiase GST-/inkomstenbelastingregels).',
          auditLogs: '<strong>Auditlogs</strong> — ongeveer 12 maanden bewaard, daarna gewist.'
        },
        sharing: {
          title: '5. Met wie wij uw gegevens delen',
          intro: 'Wij verkopen geen persoonsgegevens. Delen is beperkt tot:',
          gateways: '<strong>Payment gateways</strong> — Razorpay, Stripe, PayPal en/of Mollie (geconfigureerd door de Academie).',
          messaging: '<strong>Berichten</strong> — e-mail-SMTP-provider; WhatsApp via WATI/Meta wanneer ingeschakeld door de Academie.',
          hosting: '<strong>Hosting</strong> — de server waarop deze self-hosted instantie draait (gekozen door de Academie; EU-academies dienen in de EU te hosten).',
          licence: '<strong>Licentievalidatie</strong> — installatievingerafdruk / licentiecontroles kunnen contact maken met de licentierelay van Axeron (zie DPA van de leverancier).',
          staff: '<strong>Academiepersoneel</strong> — alleen toegang op basis van rol.'
        },
        cookies: {
          title: '6. Cookies en lokale opslag',
          intro: 'Alleen essentiële opslag:',
          jwt: '<strong>JWT</strong> (localStorage) — sessie.',
          consentFlag: '<strong>Cookie-toestemmingsvlag</strong> (localStorage).',
          gatewaySession: '<strong>Tijdelijke gateway-sessie-id</strong> (sessionStorage) tijdens betalingsdoorverwijzingen.',
          noAds: 'Geen advertentie- of analytische cookies van derden.'
        },
        rights: {
          gdprTitle: '7. Uw rechten onder GDPR / UK GDPR',
          gdprP1: 'Afhankelijk van uw locatie kunt u rechten hebben op inzage, rectificatie, wissing, beperking, overdraagbaarheid en bezwaar, en rechten met betrekking tot geautomatiseerde besluitvorming waar van toepassing.',
          gdprP2: 'Neem contact op met de beheerder van de Academie om rechten uit te oefenen. Academies dienen zonder onnodige vertraging en binnen één maand te reageren (verlengbaar voor zover de wet dat toestaat).',
          gdprP3: 'U kunt een klacht indienen bij uw lokale toezichthouder (bijvoorbeeld CNIL, BfDI, AP, ICO in het VK).',
          dpdpTitle: '7. Uw rechten onder de DPDP Act, 2023',
          dpdpP1: 'Als data principal onder de Digital Personal Data Protection Act, 2023 (India) kunt u via de beheerder van de Academie inzage, correctie, wissing (onder voorbehoud van wettelijke bewaarplicht) en gerelateerde rechten aanvragen (reactietermijn: 30 dagen).',
          dpdpP2: 'Klachten kunnen worden gericht aan de Data Protection Board of India zodra deze volledig operationeel is, of aan de beheerder van de Academie.'
        },
        security: {
          title: '8. Gegevensbeveiliging',
          https: 'HTTPS/TLS tijdens verzending (bij implementatie met TLS).',
          bcrypt: 'Wachtwoorden gehasht met bcrypt.',
          roles: 'Rolbeperkte toegang.',
          encryption: 'Gateway-geheimen versleuteld in rust wanneer <code>PAYMENT_SECRETS_ENCRYPTION_KEY</code> is ingesteld.'
        },
        changes: {
          title: '9. Wijzigingen',
          body: 'Wij kunnen deze pagina bijwerken; de datum &quot;Laatst bijgewerkt&quot; verandert dan. Voortgezet gebruik na een update betekent dat u het herziene beleid accepteert.'
        },
        footer: {
          line1: '{{name}} · Privacyverklaring · Weerspiegelt de praktijken van de Academie die deze instantie beheert.',
          line2: 'Juridisch pakket van de leverancier: zie <code>legal/PRIVACY-POLICY.txt</code> en <codeEu>legal/PRIVACY-POLICY-EU.txt</codeEu> (juridische toetsing vereist vóór ondertekening door de klant).'
        }
      },
      terms: {
        agreement: {
          title: '1. Overeenkomst',
          body: 'Deze voorwaarden regelen het gebruik van <strong>{{name}}</strong>, software voor academiebeheer gelicentieerd door Axeron Technologies en beheerd door uw academie (de &quot;Academie&quot;). Door in te loggen gaat u akkoord met deze voorwaarden en de <privacyLink>Privacyverklaring</privacyLink>.'
        },
        licence: {
          title: '2. Licentie en aanvaardbaar gebruik',
          body: 'De Academie verleent geautoriseerde gebruikers (beheerders, managers, coaches, leerlingen en voogden) toegang tot het platform uitsluitend voor academiebeheer. U mag niet proberen licentielimieten te omzeilen, inloggegevens onveilig te delen of payment gateways misbruiken.'
        },
        fees: {
          title: '3. Kosten en betalingen',
          body1: 'Leerlingenportemonnees en lidmaatschapskosten worden in rekening gebracht door de Academie, niet door Axeron Technologies. Online gateways (Razorpay, Stripe, PayPal, Mollie) worden door de Academie geconfigureerd onder eigen merchant accounts.',
          euLicence: 'Licentiekosten die Axeron Technologies aan Europese academies in rekening brengt, worden geciteerd in de valuta vermeld op de bestelling, exclusief toepasselijke btw tenzij anders vermeld op de factuur.',
          nonEuLicence: 'Softwarelicentiekosten die Axeron Technologies in rekening brengt, worden geciteerd in de valuta die op het bestelformulier is overeengekomen, exclusief belasting tenzij anders vermeld.'
        },
        support: {
          title: '4. Support',
          body: 'Support wordt geleverd door de beheerder van de Academie voor dagelijks gebruik, en door Axeron Technologies voor licenties en productgebreken onder de EULA / supportovereenkomst van de Academies.',
          euHours: ' Supportreacties voor Europese klanten zijn waar mogelijk gericht op EU-kantooruren (CET/CEST).',
          indiaHours: ' Supporturen voor Indiase klanten zijn doorgaans ma–za 10:00–18:00 IST, tenzij anders overeengekomen.'
        },
        liability: {
          title: '5. Aansprakelijkheid',
          body: 'De software wordt geleverd zoals beschreven in de End User License Agreement tussen Axeron Technologies en de Academie. De Academie is verantwoordelijk voor configuratie, gebruikerstoegang en naleving van lokale wetgeving (inclusief gegevensbescherming).'
        },
        governingLaw: {
          title: '6. Toepasselijk recht',
          euBody: 'Voor Europese academieklanten kunnen commerciële voorwaarden onderworpen zijn aan EU/EER-consumenten- en handelsregels zoals vastgelegd in de ondertekende bestelling / EULA-addendum. Geschillenbeslechting is zoals gespecificeerd in die overeenkomst. Juridische documenten van de leverancier onder <code>legal/</code> zijn gemarkeerd voor juridische toetsing vóór ondertekening.',
          indiaBody: 'Tenzij een ondertekende overeenkomst anders bepaalt, worden leverancierslicentieovereenkomsten voor Indiase klanten beheerst door het recht van India (Gujarat), zoals vermeld in de EULA en voorwaarden van Axeron Technologies.'
        },
        footer: '{{name}} · Gebruiksvoorwaarden · Zie ook de EULA van de leverancier in het softwarelicentiepakket.'
      },
      legal: {
        backToLogin: '← Terug naar inloggen',
        termsTitle: 'Gebruiksvoorwaarden',
        privacyTitle: 'Privacyverklaring',
        lastUpdated: 'Laatst bijgewerkt: juli 2026 · Van toepassing op: {{name}}'
      },
      userStatuses: {
        labels: {
          coach: 'coach',
          manager: 'beheerder'
        }
      },
      notifications: {
        categories: {
          general: 'Algemeen',
          billing: 'Facturatie',
          attendance: 'Aanwezigheid',
          privacy: 'Privacy',
          chess: 'Schaken',
          chessChallenge: 'Uitdaging',
          chessGame: 'Partij',
          chessFairPlay: 'Fair play',
          chessCorrespondence: 'Correspondentie',
        },
        content: {
          new: 'Nieuw',
          open: 'Openen',
          fallbackTitle: 'Melding',
          lowWallet: {
            title: 'Laag walletsaldo',
            body: '{{studentName}} staat op {{balance}} (drempel {{threshold}}). Waardeer op of vorder betaling.',
            action: 'Deze wallet opwaarderen'
          },
          lowWalletDigest: {
            title: '{{count}} leerlingen met lage wallet',
            body: '{{count}} leerlingen zitten onder de walletsdrempel. Open Openstaande betalingen om te controleren en te innen.',
            action: 'Openstaande betalingen openen'
          },
          absence: {
            title: 'Afwezigheid geregistreerd',
            body: '{{studentName}} is als afwezig gemarkeerd voor de sessie op {{sessionInfo}}.',
            action: 'Aanwezigheid openen'
          },
          dsar: {
            title: 'Gegevensverzoek (DSAR)',
            body: '{{name}} (gebruiker #{{userId}}, {{username}}) vroeg een kopie van persoonsgegevens. Exporteer via Rapporten → Gegevensexport.',
            action: 'Gegevens exporteren'
          },
          chessGeneric: {
            CHESS_CHALLENGE: 'Schaakuitdaging',
            CHESS_GAME: 'Schaakpartij',
            CHESS_FAIR_PLAY: 'Fair play',
            CHESS_CORR: 'Correspondentieschaak',
            CHESS: 'Schaken',
            bodyFallback: 'Open het Speel-gedeelte voor details.'
          }
        }
      }
    }
  }
};

const saved = typeof window !== 'undefined' ? localStorage.getItem('uiLanguage') : null;

function deepMerge(target, src) {
  for (const [k, v] of Object.entries(src || {})) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      if (!target[k] || typeof target[k] !== 'object') target[k] = {};
      deepMerge(target[k], v);
    } else {
      // Overlay wins — used to fill missing keys and correct EN leftovers in de/fr/nl.
      target[k] = v;
    }
  }
  return target;
}
for (const [lang, map] of Object.entries(missingLocales)) {
  const tre = {};
  for (const [path, val] of Object.entries(map)) {
    const parts = path.split('.');
    let cur = tre;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = cur[parts[i]] || {};
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = val;
  }
  if (resources[lang]?.translation) deepMerge(resources[lang].translation, tre);
}

i18n.use(initReactI18next).init({
  resources,
  lng: saved || 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

// Keep calendar month/weekday names aligned with the Header language selector
// (regional localeTag still controls numeric date order / currency grouping).
syncUiLanguage(i18n.language || saved || 'en');
i18n.on('languageChanged', (lng) => syncUiLanguage(lng));

export default i18n;
